(() => {
    // The query string the page was opened with, before any routing below
    // rewrites the address. Sent with a registration so one that came from an
    // ad can be told from one that did not (utils/adAttribution.js).
    const LANDING_QUERY = window.location.search;

    // ── State ─────────────────────────────────────────────
    const state = {
        user: null,
        presentationId: null,
        presentationTopic: '',
        presentationExamBoard: '',
        presentationSubject: '',
        presentationLevel: '',
        selectedTheme: 'classic',
        slideCount: 'auto',
        slides: new Map(),
        planSlides: [],       // mutable ordered array during approval
        planTitle: '',
        planEventSource: null,
        genEventSource: null,
        activeSlideId: null,
        activeSlidePosition: null,
        aiEditBuffer: '',
        pptMode: 'direct',
        dragActive: false,
        selectedEl: null,
        signupLocked: false,
        signupUnlocked: false,
        signupCode: '',
        presenting: false,
        presentPosition: 1,
        presentStep: 0,
        // Companions ticked at submit, honoured when the deck finishes.
        pendingCompanions: { worksheet: false, quiz: false },
        packEventSource: null,
        // users.prefs_json, fetched once at boot. Starts as the same defaults
        // the server would send so nothing has to wait on the round trip or
        // branch on "not loaded yet".
        prefs: { a11y: { layout: null, tint: null, textScale: 1, reduceMotion: false }, whatsNew: null, senPresets: [] },
    };

    // ── DOM refs ──────────────────────────────────────────
    const $ = id => document.getElementById(id);

    const appEl             = $('app');
    const landingView       = $('landing-view');
    const authModal         = $('auth-modal');
    const authView          = $('auth-view');
    const generatorView     = $('generator-view');
    const presListView      = $('presentations-list-view');
    const studentProfileView = $('student-profile-view');
    const studentMarkingView = $('student-marking-view');

    const loginForm         = $('login-form');
    const registerForm      = $('register-form');
    const signupGateForm    = $('signup-gate-form');
    const forgotForm        = $('forgot-form');
    const forgotSent        = $('forgot-sent');
    const loginTab          = $('tab-login');
    const registerTab       = $('tab-register');
    const authError         = $('auth-error');
    const baeduLoginBtn     = $('baedu-login-btn');
    const authBaeduDivider  = $('auth-baedu-divider');

    const navUserEmail      = $('nav-user-email');
    const btnLogout         = $('btn-logout');
    const navCreateWrap     = $('nav-create-wrap');
    const navStudentsBtn    = $('nav-students');
    const navTutoringBtn    = $('nav-tutoring');
    const tutoringView      = $('tutoring-view');
    // Declared up here, not beside the tutoring code: showPresList clears it,
    // and that can run before the tutoring section further down has executed.
    let pendingTutoringLink = null;
    const studentsView      = $('students-view');
    const lessonsView       = $('lessons-view');
    const btnCreate         = $('btn-create');
    const createMenu        = $('create-menu');
    const logo              = document.querySelector('#nav .logo');

    const generateForm      = $('generate-form');
    const topicInput        = $('topic');
    const inputContent      = $('input-content');
    const generateBtn       = $('generate-btn');
    const generateError     = $('generate-error');
    const packOptions       = $('pack-options');
    const packDock          = $('pack-dock');
    const packDockList      = $('pack-dock-list');
    const themePicker       = $('theme-picker');
    const editorThemePicker = $('editor-theme-picker');

    const progressBar       = $('progress-bar');
    const progressText      = $('progress-text');
    const progressIcon      = $('progress-icon');
    // Status label swaps in place (transitions.dev text-states-swap): the old
    // line exits up with blur, the new one rises back in.
    function setProgressText(next) {
        if (!progressText) return;
        if (progressText.textContent === next) return;
        const dur = parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--text-swap-dur')
        ) || 150;
        clearTimeout(progressText._swapTimer);
        progressText.classList.add('is-exit');
        progressText._swapTimer = setTimeout(() => {
            progressText.textContent = next;
            progressText.classList.remove('is-exit');
            progressText.classList.add('is-enter-start');
            void progressText.offsetHeight; // reflow so the return animates
            progressText.classList.remove('is-enter-start');
        }, dur);
    }
    // Spinner ↔ check in the same slot.
    function setProgressDone(done) {
        progressIcon?.setAttribute('data-state', done ? 'b' : 'a');
    }

    const planView          = $('plan-view');
    const planTitleEl       = $('plan-title');
    const planSubtitle      = $('plan-subtitle');
    const planSlidesEl      = $('plan-slides');
    const btnApprovePlan    = $('btn-approve-plan');
    const btnRejectPlan     = $('btn-reject-plan');

    const slideDeck         = null; // removed – replaced by ppt-editor

    const pptEditor         = $('ppt-editor');
    const pptChromeTitle    = $('ppt-chrome-title');
    const pptBtnPresent     = $('ppt-btn-present');
    const presentOverlay    = $('present-overlay');
    const presentStage      = $('present-stage');
    const presentPrev       = $('present-prev');
    const presentNext       = $('present-next');
    const presentExit       = $('present-exit');
    const presentCounter    = $('present-counter');
    const presentNotes      = $('present-notes');
    const slideNotesBox     = $('slide-notes');
    const slideNotesInput   = $('slide-notes-input');
    // Teacher notes by slide id - see rememberNotes. Declared up here, with the
    // refs, because opening a deck can run before the notes code below does.
    const slideNotes        = new Map();
    let notesSaveTimer = null;
    let notesSlideId = null;
    const presentCredits    = $('present-credits');
    const slidePanel        = $('slide-panel');
    const slidePanelWrap    = $('slide-panel-wrap');
    // The thumbnail rail slides into the editor region whenever the editor
    // itself appears. Mirroring `hidden` here catches every show/hide path
    // rather than only the ones that go through a named function.
    if (pptEditor && slidePanelWrap) {
        const syncSlidePanel = () =>
            slidePanelWrap.setAttribute('data-open', String(!pptEditor.hasAttribute('hidden')));
        new MutationObserver(syncSlidePanel).observe(pptEditor, { attributes: true, attributeFilter: ['hidden'] });
        syncSlidePanel();
    }
    const stageCanvas       = $('stage-canvas');
    const stageSlideInner   = $('stage-slide-inner');
    if (stageSlideInner) MathRender.installLiveEditing(stageSlideInner);
    const stageInfo         = $('stage-info');
    const stageError        = $('stage-error');
    const pptDirectBar      = $('ppt-direct-bar');
    const pptBtnUndo        = $('ppt-btn-undo');
    const pptBtnRedo        = $('ppt-btn-redo');
    const pptColorPicker    = $('text-color-picker');

    const generateCard      = $('generate-card');

    const presList          = $('pres-list');
    const presListError     = $('pres-list-error');
    const presListSuccessEl = $('pres-list-success');
    const presLoadMoreBtn   = $('pres-load-more');

    const pptBtnExportBaedu = $('ppt-btn-export-baedu');

    // ── Helpers ───────────────────────────────────────────
    // Surfaces tagged .t-dropdown / .t-modal (transitions.dev) grow in on
    // show and scale back out on hide; `hidden` still owns display, so it
    // is only dropped once the closing tween has finished. Anything
    // untagged keeps the plain attribute flip.
    // The animated surface is either the element itself or the card it
    // wraps – a full-screen modal container must not scale, only its card.
    function tTarget(el) {
        if (el.classList.contains('t-modal') || el.classList.contains('t-dropdown')) return el;
        return el.querySelector(':scope > .t-modal, :scope > .t-dropdown');
    }
    function tCloseMs(target) {
        const name = target.classList.contains('t-modal') ? '--modal-close-dur' : '--dropdown-close-dur';
        const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
        return Number.isFinite(v) ? v : 150;
    }
    // A surface mid-close is on its way out – treat it as already shut so a
    // click during the tween re-opens rather than toggling it closed.
    function isShown(el) {
        if (!el || el.hasAttribute('hidden')) return false;
        const target = tTarget(el);
        return !target || !target.classList.contains('is-closing');
    }
    /**
     * Keeps the Pupils nav link lit while any of its views is on screen: the
     * page itself, and the one class, lesson or pupil opened from it.
     *
     * Driven from show/hide rather than set by each page, because the app has
     * fifteen page functions and only a few of them know about Pupils – the
     * rest would each have to remember to turn the highlight off on their way
     * past, and one that forgot would leave the nav lying.
     */
    function syncStudentsNav() {
        // Views by id rather than by their consts: classesView is declared far
        // below, and show() runs before that line is reached.
        const on = (...ids) => ids.some(id => isShown($(id)));
        const lit = [
            [navStudentsBtn, on('students-view', 'student-profile-view', 'student-marking-view',
                                'lessons-view', 'classes-view')],
            [navTutoringBtn, on('tutoring-view')],
        ];
        for (const [btn, current] of lit) {
            if (!btn) continue;
            if (current) btn.setAttribute('aria-current', 'page');
            else btn.removeAttribute('aria-current');
        }
        syncPupilsLabels();
    }

    /**
     * What the Pupils page is called.
     *
     * "Results" for a tutoring company, and its lessons section "Workbooks".
     * Its pupils are its clients, under Tutoring, and its lessons are the ones
     * it has booked; this page holds neither – only work handed in under a name
     * somebody typed. Same page, same sections, named for what is on it.
     *
     * Called from syncStudentsNav because whether an organisation tutors can
     * arrive after the first page is drawn (see syncTutoringNav), and that is
     * the one place every page change and that late answer both pass through.
     */
    function syncPupilsLabels() {
        const label = tutoringOrg() ? 'Results' : 'Pupils';
        document.querySelectorAll('[data-pupils-label]').forEach(el => {
            if (el.textContent !== label) el.textContent = label;
        });
        const lessons = $('lsn-list-heading');
        const lessonsLabel = tutoringOrg() ? 'Workbooks' : 'Lessons';
        if (lessons && lessons.textContent !== lessonsLabel) lessons.textContent = lessonsLabel;
    }

    /**
     * A tutoring company's pupils are on courses, not in classes, and work is
     * set to a lesson. So it has no Classes section on Pupils and no "Set to a
     * class" anywhere: both would be somewhere to look for something that is
     * not there.
     */
    function tutoringOrg() {
        return state.user?.org?.tutoring === true;
    }

    function show(...els) {
        els.forEach(el => {
            if (!el) return;
            const target = tTarget(el);
            el.removeAttribute('hidden');
            if (!target) return;
            clearTimeout(el._tCloseTimer);
            target.classList.remove('is-open', 'is-closing');
            void target.offsetWidth; // paint the pre-open state before opening
            target.classList.add('is-open');
        });
        syncStudentsNav();
    }
    function hide(...els) {
        els.forEach(el => {
            if (!el) return;
            const target = tTarget(el);
            if (!target || !isShown(el)) {
                if (target) target.classList.remove('is-open', 'is-closing');
                el.setAttribute('hidden', '');
                return;
            }
            target.classList.remove('is-open');
            target.classList.add('is-closing');
            clearTimeout(el._tCloseTimer);
            el._tCloseTimer = setTimeout(() => {
                target.classList.remove('is-closing');
                el.setAttribute('hidden', '');
                syncStudentsNav();
            }, tCloseMs(target));
        });
        syncStudentsNav();
    }

    // transitions.dev card-resize. CSS alone can't tween to/from an
    // intrinsic (auto) height, so this measures the container before and
    // after `mutate` runs and drives the tween between two explicit pixel
    // heights. Used wherever swapping content changes a container's
    // natural size (settings pane switch, auth modal tab switch).
    const reduceMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    function resizeTransition(container, mutate) {
        if (!container) { mutate(); return; }
        if (reduceMotionQuery.matches) { mutate(); return; }
        clearTimeout(container._resizeTimer);
        // A still-lingering inline transition from an interrupted previous
        // call would animate the snap-back to `before` below – clear it so
        // that jump is always instant.
        container.style.transition = '';
        // Whatever height is currently on screen – including mid-tween if a
        // previous resize hasn't settled yet – is the correct start point.
        const before = container.getBoundingClientRect().height;
        mutate();
        container.style.height = 'auto';
        const after = container.getBoundingClientRect().height;
        container.style.height = `${before}px`;
        container.classList.add('t-resize');
        // `.t-resize`'s `transition` shorthand only wins the cascade if no
        // later-declared class on the same element also sets `transition` –
        // `.t-modal` and `.t-input` both do, and a shorthand collision
        // replaces the whole property instead of merging it, so the height
        // tween silently never ran. Setting it inline guarantees it always
        // applies no matter what other classes are on the element.
        container.style.transition = 'height var(--resize-dur) var(--resize-ease)';
        void container.offsetHeight; // reflow so the height change below tweens
        container.style.height = `${after}px`;

        container._resizeTimer = setTimeout(() => {
            // Release back to intrinsic sizing – these containers never carry
            // an author-set inline height outside of this mechanism, so the
            // rest state is always '', never a value captured mid-sequence.
            container.style.height = '';
            container.style.transition = '';
            container.classList.remove('t-resize');
        }, motionMs('--resize-dur', 300) + 30);
    }

    function setError(el, msg) {
        if (!el) return;
        el.textContent = msg || '';
        msg ? show(el) : hide(el);
        if (msg) shakeInvalid(el); else clearInvalid(el);
    }

    // transitions.dev error-state-shake. The card owns the border and the
    // message, so it carries both .is-error classes; .is-shaking stays
    // orthogonal so the shake can replay without flickering the error state.
    function motionMs(name, fallback) {
        const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
        return Number.isFinite(v) ? v : fallback;
    }
    function clearInvalid(errEl) {
        const wrap = errEl?.closest('.t-input-wrap');
        if (!wrap) return;
        wrap.classList.remove('is-error');
        wrap.querySelector('.t-input')?.classList.remove('is-error');
        if (wrap.classList.contains('t-input')) wrap.classList.remove('is-error');
    }
    function shakeInvalid(errEl) {
        const wrap = errEl?.closest('.t-input-wrap');
        if (!wrap) return;
        const input = wrap.classList.contains('t-input') ? wrap : wrap.querySelector('.t-input');
        if (!input) return;

        wrap.classList.add('is-error');
        input.classList.add('is-error');

        input.classList.remove('is-shaking');
        void input.offsetWidth; // reflow so the shake replays
        input.classList.add('is-shaking');

        const shakeMs = motionMs('--shake-dur-a', 80) * 2 + motionMs('--shake-dur-b', 60) * 2;
        setTimeout(() => input.classList.remove('is-shaking'), shakeMs + 20);
        // No timed revert. The message is the only place the reason is given,
        // and three seconds is not long enough to read one and act on it; it
        // also only faded, so the box it sat in stayed behind as a gap.
    }
    // Typing is already a correction – take the message away, box and all.
    document.addEventListener('input', (e) => {
        const errEl = e.target.closest?.('.t-input-wrap.is-error')?.querySelector('.t-error-msg');
        if (errEl) setError(errEl, '');
    });

    // ── "Did you mean…?" ──────────────────────────────────
    // The server questions an address one slip from a big provider's – gmial
    // for gmail – and says which it thinks was meant. That is put as a choice,
    // not shown as an error: it is a guess.
    //
    // "Yes" corrects the field and stops there, so the address that makes the
    // account is one they have seen in the box and pressed the button on.
    // "No" has nothing left to look at, and sends the form.
    //
    // The address last kept as typed. Sent back with the next attempt, which
    // is what tells the server it has already asked.
    let questionedEmail = '';
    function askAboutEmail(slot, form, { questioned, suggestion }) {
        if (!slot || !form) return;
        const answer = (label, cls, onPick) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = cls;
            btn.textContent = label;
            btn.addEventListener('click', () => { hide(slot); onPick(); });
            return btn;
        };
        const line = document.createElement('p');
        const meant = document.createElement('strong');
        meant.textContent = suggestion;
        line.append('Did you mean ', meant, '?');
        const actions = document.createElement('div');
        actions.className = 'email-ask-actions';
        actions.append(
            answer('Yes, change it', 'email-ask-yes', () => {
                form.email.value = suggestion;
                form.querySelector('button[type=submit]')?.focus();
            }),
            answer('No, it’s right', 'email-ask-no', () => { questionedEmail = questioned; form.requestSubmit(); }),
        );
        slot.replaceChildren(line, actions);
        show(slot);
        actions.firstChild.focus();
    }

    // ── "Check your address" bar ──────────────────────────
    // On the library until the link in the confirm email has been pressed. It
    // is the only place a teacher is shown the address they typed, and so the
    // only place they can notice it is wrong before a password reset is sent
    // to nobody.
    const emailConfirm = $('email-confirm');
    const emailFixForm = $('email-fix-form');
    // Per tab, not for good: it has not stopped being true, and next visit is
    // a fair time to say so again.
    const emailConfirmHiddenKey = () => `email-confirm-hidden:${state.user?.id || ''}`;
    function emailConfirmDismissed() {
        try { return sessionStorage.getItem(emailConfirmHiddenKey()) === '1'; } catch { return false; }
    }
    function syncEmailConfirm() {
        if (!emailConfirm) return;
        if (!state.user?.emailUnconfirmed || emailConfirmDismissed()) return hide(emailConfirm);
        $('email-confirm-address').textContent = state.user.email || '';
        show(emailConfirm);
    }
    function emailConfirmStatus(text) {
        const el = $('email-confirm-status');
        if (el) el.textContent = text || '';
    }

    $('email-confirm-dismiss')?.addEventListener('click', () => {
        try { sessionStorage.setItem(emailConfirmHiddenKey(), '1'); } catch { /* hidden for this page, then */ }
        hide(emailConfirm);
    });

    $('email-confirm-resend')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        btn.disabled = true;
        emailConfirmStatus('');
        try {
            const res = await fetch('/api/auth/email/resend', { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            emailConfirmStatus(res.ok ? 'Sent again – give it a minute, and check your junk folder.' : (data.error || 'Could not send it.'));
        } catch {
            emailConfirmStatus('Network error – not sent.');
        } finally {
            btn.disabled = false;
        }
    });

    $('email-confirm-fix')?.addEventListener('click', () => {
        if (!emailFixForm) return;
        setError($('email-fix-error'), '');
        hide($('email-fix-ask'));
        emailFixForm.email.value = state.user?.email || '';
        emailFixForm.password.value = '';
        show(emailFixForm);
        emailFixForm.email.focus();
        emailFixForm.email.select();
    });
    $('email-fix-cancel')?.addEventListener('click', () => hide(emailFixForm));

    emailFixForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const errEl = $('email-fix-error');
        const btn = emailFixForm.querySelector('button[type=submit]');
        setError(errEl, '');
        hide($('email-fix-ask'));
        btn.disabled = true;
        try {
            const res = await fetch('/api/auth/email/fix', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailFixForm.email.value.trim(), password: emailFixForm.password.value, emailChecked: questionedEmail }),
            });
            const data = await res.json().catch(() => ({}));
            if (data.questioned) return askAboutEmail($('email-fix-ask'), emailFixForm, data);
            if (!res.ok) { emailFixForm.password.value = ''; return setError(errEl, data.error || 'Could not change your address'); }
            state.user.email = data.email;
            if (navUserEmail) navUserEmail.textContent = data.email;
            hide(emailFixForm);
            syncEmailConfirm();
            emailConfirmStatus(data.sent ? 'Changed, and a new email is on its way.' : 'Changed. The email did not send – try “Send it again” in a minute.');
        } catch {
            setError(errEl, 'Network error');
        } finally {
            btn.disabled = false;
        }
    });

    // transitions.dev toast – one host, transient confirmations only.
    const toastEl = $('toast');
    let toastTimer = null;
    function showToast(text, ms = 2000) {
        if (!toastEl || !text) return;
        clearTimeout(toastTimer);
        toastEl.textContent = text;
        toastEl.hidden = false;
        void toastEl.offsetWidth; // paint the resting state before rising
        toastEl.classList.add('is-open');
        toastTimer = setTimeout(() => {
            toastEl.classList.remove('is-open');
            const closeMs = parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue('--toast-close')
            ) || 250;
            toastTimer = setTimeout(() => { toastEl.hidden = true; }, closeMs);
        }, ms);
    }

    // ── Input clear with dissolve (transitions.dev) ───────
    // Per-frame JS is unavoidable here: the per-word streak envelope can't
    // be expressed as static keyframes. Timing is read from CSS on every
    // call so tuning the variables applies without a reload.
    const clearMotion = (() => {
        const root = document.documentElement;
        const num = (name, fb) => {
            const v = parseFloat(getComputedStyle(root).getPropertyValue(name));
            return Number.isFinite(v) ? v : fb;
        };
        // Minimal cubic-bezier sampler so the JS easing matches the CSS.
        function bezier(str) {
            const m = String(str).match(/cubic-bezier\(([-\d.]+),([-\d.]+),([-\d.]+),([-\d.]+)\)/);
            if (!m) return (t) => t;
            const [x1, y1, x2, y2] = m.slice(1).map(parseFloat);
            const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
            const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
            return (t) => {
                if (t <= 0) return 0;
                if (t >= 1) return 1;
                let u = t;
                for (let i = 0; i < 8; i++) {
                    const dx = ((ax * u + bx) * u + cx) * u - t;
                    const d = (3 * ax * u + 2 * bx) * u + cx;
                    if (Math.abs(dx) < 1e-6 || d === 0) break;
                    u -= dx / d;
                }
                return ((ay * u + by) * u + cy) * u;
            };
        }
        const canvas = document.createElement('canvas').getContext('2d');

        function buildGlow(wrap, input, text) {
            canvas.font = getComputedStyle(input).font;
            const isDark = root.getAttribute('data-theme') === 'dark';
            const rgb = isDark ? '255,255,255' : '0,0,0';
            const w = wrap.clientWidth || 280;
            const padLeft = parseFloat(getComputedStyle(input).paddingLeft) || 12;
            const spread = num('--glow-spread', 1.5);
            const layers = [];
            let x = 0;
            text.split(/(\s+)/).forEach((seg) => {
                const segW = canvas.measureText(seg).width;
                if (seg.trim()) {
                    const cx = padLeft + x + segW / 2;
                    const hw = Math.max(segW * 0.45, 8) * spread;
                    [[0, 0.8, 7, 0.22], [hw * 0.45, 0.55, 8, 0.18],
                     [-hw * 0.4, 0.65, 6, 0.16], [hw * 0.15, 0.9, 5, 0.14]]
                        .forEach(([dx, rwm, rh, a]) => {
                            const lx = (((cx + dx) / w) * 100).toFixed(2);
                            layers.push(
                                `radial-gradient(ellipse ${Math.max(hw * rwm, 2).toFixed(1)}px ${rh}px at ${lx}% 100%, rgba(${rgb},${a}), transparent)`
                            );
                        });
                }
                x += segW;
            });
            return layers.join(', ');
        }

        // Keeps .has-value in sync so the mirror owns the glyphs.
        function sync(wrap) {
            const input = wrap?.querySelector('input');
            const mirror = wrap?.querySelector('.t-clear-mirror');
            if (!input || !mirror) return;
            const has = input.value.length > 0;
            wrap.classList.toggle('has-value', has);
            if (has) mirror.textContent = input.value.replace(/ /g, '\u00a0');
        }

        function run(wrap, onDone) {
            const input = wrap?.querySelector('input');
            const mirror = wrap?.querySelector('.t-clear-mirror');
            const phold = wrap?.querySelector('.t-clear-placeholder');
            const glow = wrap?.querySelector('.t-clear-glow');
            if (!wrap || !input || !mirror || !phold || !glow || wrap._clearing || !input.value) {
                onDone?.();
                return;
            }
            wrap._clearing = true;
            const keepFocus = document.activeElement === input;
            mirror.textContent = input.value.replace(/ /g, '\u00a0');

            const total  = num('--clear-dur', 1000);
            const outDur = num('--clear-out-dur', 400);
            const inDur  = num('--clear-in-dur', 400);
            const outFly = num('--clear-out-fly', 12);
            const inFly  = num('--clear-in-fly', 12);
            const blur   = num('--clear-blur', 2);
            const delay  = num('--glow-delay', 50);
            const peakAt = num('--glow-peak-at', 0.15);
            const gOp    = num('--glow-opacity', 0.42);
            const easeOut = bezier(getComputedStyle(root).getPropertyValue('--clear-out-ease'));
            const easeIn  = bezier(getComputedStyle(root).getPropertyValue('--clear-in-ease'));

            // Clear the real value first; the mirror carries the old glyphs
            // through the fly-out.
            onDone?.();
            wrap.classList.remove('has-value');
            wrap.classList.add('is-clearing');
            glow.style.background = buildGlow(wrap, input, mirror.textContent);
            glow.style.opacity = '0';

            const t0 = performance.now();
            (function tick(now) {
                const el = now - t0;
                const eo = easeOut(Math.min(1, el / outDur));
                mirror.style.transform = `translateY(${(eo * outFly).toFixed(1)}px)`;
                mirror.style.opacity = (1 - eo).toFixed(3);
                mirror.style.filter = `blur(${(eo * blur).toFixed(1)}px)`;

                const ei = easeIn(Math.min(1, el / inDur));
                phold.style.transform = `translateY(${(-inFly + ei * inFly).toFixed(1)}px)`;
                phold.style.opacity = (0.9 + ei * 0.1).toFixed(3);
                phold.style.filter = `blur(${(blur - ei * blur).toFixed(1)}px)`;

                let g = 0;
                if (el > delay) {
                    const gp = Math.min(1, (el - delay) / Math.max(1, total - delay));
                    g = gp < peakAt ? gp / peakAt : 1 - (gp - peakAt) / (1 - peakAt);
                }
                glow.style.opacity = (g * gOp).toFixed(3);

                if (el < total) {
                    requestAnimationFrame(tick);
                } else {
                    wrap.classList.remove('is-clearing');
                    [mirror, phold].forEach((n) => { n.style.cssText = ''; });
                    mirror.textContent = '';
                    glow.style.opacity = '0';
                    glow.style.background = '';
                    wrap._clearing = false;
                    if (keepFocus) requestAnimationFrame(() => input.focus({ preventScroll: true }));
                }
            })(performance.now());
        }

        return { run, sync };
    })();

    function esc(str) {
        if (typeof str !== 'string') return '';
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    const SHORT_DATE = { day: 'numeric', month: 'short', year: 'numeric' };
    const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', SHORT_DATE) : '—');

    async function apiFetch(path, opts = {}) {
        // A multipart body must write its OWN Content-Type, because the header
        // carries the boundary that separates the parts and only the browser
        // knows it. Forcing application/type on a FormData body sent the file
        // up labelled as JSON, express.json() then tried to parse the multipart
        // payload, and what surfaced was a JSON syntax error quoting the
        // boundary itself: `Unexpected token '-', "------geck"...`.
        //
        // Blob and URLSearchParams bodies have the same rule, so the test is
        // "did the caller hand us something the browser types for itself".
        const body = opts.body;
        const selfTyped = typeof FormData !== 'undefined' && body instanceof FormData
            || typeof Blob !== 'undefined' && body instanceof Blob
            || typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams;
        const res = await fetch(path, {
            headers: {
                ...(selfTyped ? {} : { 'Content-Type': 'application/json' }),
                ...(opts.headers || {}),
            },
            ...opts,
        });
        if (res.status === 401) { showAuth(); throw new Error('Not authenticated'); }
        if (res.status === 402) {
            try { showQuotaModal(await res.clone().json()); } catch { showQuotaModal({}); }
        }
        return res;
    }

    // ── Upload source material ────────────────────────────
    //
    // Every "paste content / notes" box has an upload control beside it. The
    // server turns photos, PDFs, Word and PowerPoint files into text
    // (routes/sources.js). That text is NOT put in the box: it is kept here,
    // shown as a removable file, and joined to whatever the teacher typed when
    // they generate. A box full of a transcribed worksheet is not something a
    // teacher asked to read, and it made a file look like pasted text.
    //
    // A teacher who attaches a file has also told us what the topic is, so an
    // empty topic is filled in from it (the box's data-topic names the field).
    const SOURCE_TEXT_LIMIT = 50000;
    const sourceFiles = new Map();   // textarea id -> [{ name, text }]
    const sourceRenderers = new Map();

    // What a generation should send for this box: what was typed, then each file.
    function sourceTextFor(targetId, limit = SOURCE_TEXT_LIMIT) {
        const files = sourceFiles.get(targetId) || [];
        const typed = ($(targetId)?.value || '').trim();
        const parts = files.map((f) => (files.length > 1 ? `--- ${f.name} ---\n${f.text}` : f.text));
        return [typed, ...parts].filter(Boolean).join('\n\n').slice(0, limit);
    }

    function clearSourceFiles(targetId) {
        sourceFiles.delete(targetId);
        sourceRenderers.get(targetId)?.();
    }

    // The box's extra panels ("Paste notes", "From a presentation") open from
    // the buttons along its bottom edge. Named by the box's target and the
    // panel, so code that fills one in can open it too.
    function setSourcePanel(targetId, name, open) {
        const box = document.querySelector(`.src-drop[data-target="${targetId}"]`);
        const panel = box?.querySelector(`[data-src-panel="${name}"]`);
        if (!panel) return;
        panel.hidden = !open;
        box.querySelector(`[data-src-toggle="${name}"]`)?.setAttribute('aria-expanded', String(!!open));
    }

    // The kinds the server reads (utils/sourceExtract.js), checked here too so a
    // wrong file is refused instantly rather than after an upload.
    const SOURCE_EXT = /\.(pdf|docx|pptx|jpe?g|png|webp|gif)$/i;
    // Mirrors the server: documents up to 50 MB, photos up to 10.
    const SOURCE_MAX_BYTES = 50 * 1024 * 1024;
    const SOURCE_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
    const sourceIsImage = (f) => /^image\//.test(f.type) || /\.(jpe?g|png|webp|gif)$/i.test(f.name);

    // A file that misses the box must not make the browser leave the page to
    // open it, which is what an unhandled drop does and looks like the upload
    // silently failing.
    ['dragover', 'drop'].forEach((type) => window.addEventListener(type, (e) => {
        if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault();
    }));

    function initSourceUploads() {
        document.querySelectorAll('.src-drop').forEach((box) => {
            const targetId = box.dataset.target;
            const target = $(targetId);
            const topicEl = box.dataset.topic ? $(box.dataset.topic) : null;
            const input = box.querySelector('.src-drop-input');
            const pick = box.querySelector('.src-drop-pick');
            const status = box.querySelector('.src-upload-status');
            if (!target || !input) return;
            const limit = Number(box.dataset.limit) || SOURCE_TEXT_LIMIT;

            const say = (msg, tone) => { status.textContent = msg; status.dataset.tone = tone || ''; };

            // The same files are listed wherever the composer shows them: under
            // the drop zone, and again on the topic step, so a teacher who has
            // moved on can still see what is being used and take a file off.
            const render = () => {
                const files = sourceFiles.get(targetId) || [];
                box.classList.toggle('has-files', files.length > 0);
                                document.querySelectorAll(`[data-src-files="${targetId}"]`).forEach((list) => {
                    list.innerHTML = '';
                    files.forEach((f, i) => {
                        const li = document.createElement('li');
                        li.className = 'src-drop-file';
                        const name = document.createElement('span');
                        name.className = 'src-drop-file-name';
                        name.textContent = f.name;
                        const rm = document.createElement('button');
                        rm.type = 'button';
                        rm.className = 'src-drop-file-remove';
                        rm.setAttribute('aria-label', `Remove ${f.name}`);
                        rm.textContent = '×';
                        rm.addEventListener('click', () => {
                            files.splice(i, 1);
                            if (!files.length) sourceFiles.delete(targetId);
                            render();
                            say('');
                        });
                        li.append(name, rm);
                        list.appendChild(li);
                    });
                });
            };
            sourceRenderers.set(targetId, () => { render(); say(''); });

            async function upload(picked) {
                if (!picked.length) return;
                const bad = picked.find((f) => !SOURCE_EXT.test(f.name) && !/^image\//.test(f.type));
                if (bad) { say(`${bad.name} is not a file we can read. Use a PDF, Word, PowerPoint or a photo.`, 'error'); return; }
                const big = picked.find((f) => f.size > (sourceIsImage(f) ? SOURCE_MAX_IMAGE_BYTES : SOURCE_MAX_BYTES));
                if (big) { say(`${big.name} is too big. Photos can be up to 10 MB and documents up to 50 MB.`, 'error'); return; }
                if (picked.length > 5) { say('Add up to 5 files at a time.', 'error'); return; }

                const body = new FormData();
                picked.forEach((f) => body.append('files', f));
                const wantTopic = !!topicEl && !topicEl.value.trim();
                if (wantTopic) body.append('wantTopic', '1');
                box.classList.add('is-busy');
                say(picked.length > 1 ? `Reading ${picked.length} files…` : `Reading ${picked[0].name}…`);
                try {
                    const res = await apiFetch('/api/sources/extract', { method: 'POST', body });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) { say(data.error || 'Upload failed. Try again.', 'error'); return; }

                    const files = sourceFiles.get(targetId) || [];
                    data.files.forEach((f) => files.push({ name: f.name, text: f.text }));
                    sourceFiles.set(targetId, files);
                    render();

                    const parts = [];
                    // Only fill a topic the teacher has not typed, and only if
                    // they have not started typing while the file was reading.
                    if (data.topic && topicEl && !topicEl.value.trim()) {
                        topicEl.value = data.topic;
                        topicEl.dispatchEvent(new Event('input', { bubbles: true }));
                        parts.push('Topic named from your file.');
                    } else {
                        parts.push(data.files.length === 1 ? 'Got it.' : 'Got them.');
                    }
                    if (sourceTextFor(targetId, Infinity).length > limit) parts.push('It is long, so the end will be left out.');
                    parts.push(...data.files.map((f) => f.note).filter(Boolean));
                    const problems = (data.errors || []).map((e) => `${e.name}: ${e.error}`);
                    parts.push(...problems);
                    say(parts.join(' '), problems.length ? 'error' : 'ok');
                } catch (e) {
                    if (e.message !== 'Not authenticated') say('Upload failed. Check your connection and try again.', 'error');
                } finally {
                    box.classList.remove('is-busy');
                }
            }

            pick?.addEventListener('click', () => input.click());
            // A "nothing to build from" error is answered by anything done in
            // the box, so it should not sit there once the teacher has acted.
            const errorEl = $({ 'input-content': 'generate-error', 'ws-context': 'worksheet-error', 'quiz-context': 'quiz-error', 'bk-source-input': 'booklet-error' }[targetId]);
            if (errorEl) {
                ['input', 'change'].forEach((type) => box.addEventListener(type, () => setError(errorEl, '')));
            }
            // The specification checklist is only offered where a topic list is
            // held for the chosen board, subject and level. Its group is shown
            // and hidden elsewhere (topic-picker.js), so the button follows it,
            // and carries the picker's own count so ticks show while it is shut.
            box.querySelectorAll('[data-src-when]').forEach((btn) => {
                const group = $(btn.dataset.srcWhen);
                if (!group) return;
                const countEl = group.querySelector('output');
                const badge = btn.querySelector('.src-box-count');
                const follow = () => {
                    btn.hidden = group.hidden;
                    if (group.hidden) setSourcePanel(targetId, btn.dataset.srcToggle, false);
                    const ticked = group.querySelectorAll('input[type="checkbox"]:checked').length;
                    if (badge) badge.textContent = ticked && countEl ? countEl.textContent.replace(/ selected$/, '') : '';
                    btn.classList.toggle('has-value', !!ticked);
                };
                new MutationObserver(follow).observe(group, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true, characterData: true });
                group.addEventListener('change', follow);
                follow();
            });
            box.querySelectorAll('[data-src-toggle]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const name = btn.dataset.srcToggle;
                    const opening = btn.getAttribute('aria-expanded') !== 'true';
                    setSourcePanel(targetId, name, opening);
                    if (opening) box.querySelector(`[data-src-panel="${name}"]`)?.querySelector('textarea, select, input')?.focus();
                });
            });
            input.addEventListener('change', () => {
                const picked = Array.from(input.files || []);
                input.value = '';
                upload(picked);
            });

            // Dropping anywhere on the zone works; counting enters and leaves
            // keeps the highlight steady as the pointer crosses its children.
            let depth = 0;
            const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
            box.addEventListener('dragenter', (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth++; box.classList.add('is-over'); });
            box.addEventListener('dragover', (e) => { if (hasFiles(e)) e.preventDefault(); });
            box.addEventListener('dragleave', () => { depth = Math.max(0, depth - 1); if (!depth) box.classList.remove('is-over'); });
            box.addEventListener('drop', (e) => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                depth = 0;
                box.classList.remove('is-over');
                upload(Array.from(e.dataTransfer.files));
            });
        });
    }
    initSourceUploads();

    // ── What your documents carry ─────────────────────────
    //
    // A paying school's crest and name, or the free tier's credit line, or
    // nothing – decided by the server (see utils/resourceMark.js). The server
    // stamps its own exports without asking; this is the same answer for the
    // two documents the browser builds itself, the print popup and the preview
    // iframe, so what a teacher sees before printing is what comes out of the
    // printer.
    //
    // Fetched once and remembered, including a null answer: it cannot change
    // while the page is open, and the preview is opened from a button that
    // should not wait on the network twice.
    let resourceMarkPromise = null;

    function loadResourceMark() {
        if (!resourceMarkPromise) {
            resourceMarkPromise = apiFetch('/api/settings/resource-mark')
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => (d && d.mark) || null)
                // Not worth failing a print over. Whatever went wrong, the
                // document still wants to open.
                .catch(() => null);
        }
        return resourceMarkPromise;
    }

    /** Called when the logo or the switch changes, so the next print is right. */
    function forgetResourceMark() { resourceMarkPromise = null; }

    function closeEventSources() {
        if (state.planEventSource) { state.planEventSource.close(); state.planEventSource = null; }
        if (state.genEventSource)  { state.genEventSource.close();  state.genEventSource  = null; }
        if (state.packEventSource) { state.packEventSource.close(); state.packEventSource = null; }
    }

    function applyTheme(theme) {
        if (slidePanel) slidePanel.dataset.theme = theme;
        if (stageSlideInner) stageSlideInner.dataset.theme = theme;
        // Also apply to all thumb inners
        slidePanel?.querySelectorAll('.slide-thumb-inner').forEach(el => el.dataset.theme = theme);
    }

    // The theme a new deck gets for its level until the teacher picks one.
    // Mirrors defaultThemeFor in routes/presentations.js: the composer used to
    // send "classic" whatever was chosen, so the server's level default never
    // applied and a Year 1 deck opened in the A-Level look.
    function defaultThemeForLevel(level) {
        const key = String(level || '').trim().toLowerCase();
        if (/^(year [12]|years 1-2|ks1|key stage 1|p[12]|early level)$/.test(key)) return 'infant';
        if (/^(year [3-6]|years \d-\d|ks2|key stage 2|primary|p[3-7]|(first|second) level|11\+)$/.test(key)) return 'primary';
        return 'classic';
    }

    function followLevelTheme() {
        if (state.themeChosen) return;
        state.selectedTheme = defaultThemeForLevel($('level')?.value);
        syncThemePickers(state.selectedTheme);
    }

    // Keep both theme pickers (pre-creation generator form + in-editor switcher) in sync
    function syncThemePickers(theme) {
        [themePicker, editorThemePicker].forEach(picker => {
            picker?.querySelectorAll('.theme-btn').forEach(b => {
                b.classList.toggle('active', b.dataset.theme === theme);
            });
        });
    }

    // ── Auth ──────────────────────────────────────────────
    const worksheetView    = $('worksheet-view');    // forward-declare for use in showAuth/showPresList
    const bookletView      = $('booklet-view');
    const quizView         = $('quiz-view');
    const quizResultsView  = $('quiz-results-view');
    const billingView      = $('billing-view');
    const orgView          = $('org-view');
    const joinView         = $('join-view');

    function showAuth() {
        state.user = null;
        window.Tutoring?.reset();
        syncAdminBtn();
        // The org alert bar lives outside #app so it can span the viewport,
        // which also means hiding #app doesn't take it with it – a school's
        // "Payment failed" strip would otherwise sit over the signed-out
        // landing page for whoever used this browser next.
        hide(appEl, authModal, $('org-alert-bar'));
        show(landingView);
    }

    function updateBaeduLoginBtn(tab) {
        if (!baeduLoginBtn) return;
        const params = new URLSearchParams();
        if (state.signupCode) params.set('code', state.signupCode);
        // From "forgot your password", signing in with BA Ed doubles as proof
        // of identity, so the server lets them pick a new password on arrival.
        if (tab === 'forgot') params.set('intent', 'reset');
        const qs = params.toString();
        baeduLoginBtn.href = '/api/auth/baedu/login' + (qs ? `?${qs}` : '');
        baeduLoginBtn.lastChild.textContent = tab === 'forgot'
            ? ' Use BlueAcorn Education instead'
            : ' Continue with BlueAcorn Education';
    }

    function showAuthModal(tab = 'login') {
        state.pendingAuthTab = tab;
        show(authView);
        hide($('auth-email-ask'));
        // "Continue with BlueAcorn Education" is switched off for now: the
        // button and its divider stay in the page, hidden on every tab.
        hide(authBaeduDivider, baeduLoginBtn);
        if (loginForm) loginForm.password.value = '';
        if (registerForm) registerForm.password.value = '';

        if (tab === 'register') {
            registerTab?.classList.add('active');
            loginTab?.classList.remove('active');
        } else {
            loginTab?.classList.add('active');
            registerTab?.classList.remove('active');
        }

        if (state.signupLocked && !state.signupUnlocked) {
            hide(loginForm, registerForm, forgotForm, forgotSent, authBaeduDivider, baeduLoginBtn);
            show(signupGateForm);
        } else if (tab === 'register') {
            hide(loginForm, signupGateForm, forgotForm, forgotSent);
            show(registerForm);
        } else if (tab === 'forgot') {
            hide(loginForm, registerForm, signupGateForm, forgotSent);
            show(forgotForm);
        } else if (tab === 'forgot-sent') {
            hide(loginForm, registerForm, signupGateForm, forgotForm);
            show(forgotSent);
        } else {
            hide(registerForm, signupGateForm, forgotForm, forgotSent);
            show(loginForm);
        }
        // The two forgot screens share the BA Ed alternative.
        updateBaeduLoginBtn(tab.startsWith('forgot') ? 'forgot' : tab);
        setError(authError, '');
        show(authModal);
        document.body.style.overflow = 'hidden';
    }

    function hideAuthModal() {
        hide(authModal);
        document.body.style.overflow = '';
        if (loginForm) loginForm.password.value = '';
        if (registerForm) registerForm.password.value = '';
    }

    function clearLanding() {
        hidePackSide();
        hide(landingView, authModal);
        document.body.style.overflow = '';
        show(appEl);
    }

    function showGenerator() {
        clearLanding();
        hide(authView, presListView, bookletView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(generatorView, generateCard);
        show(btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        resetGeneratorView();
    }

    // The lesson series lives in the create area beside the presentation
    // composer, as its own card from the first step (see the markup).
    // `folder` is set when it was started from the new-folder card: the
    // series fills that folder, and its name is the obvious first topic.
    function showSeriesPage({ folder = null, scheme = null, push = true } = {}) {
        clearLanding();
        hide(authView, presListView, bookletView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(generatorView);
        show(btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        resetGeneratorView();
        hide(generateCard);
        show($('series-card'));
        setError($('series-error'), '');
        state.seriesFolder = folder;
        const note = $('sr-folder-note');
        if (note) {
            note.hidden = !folder;
            note.textContent = folder ? `into “${folder.name}”` : '';
        }
        if (folder && srTopicInput && !srTopicInput.value.trim()) srTopicInput.value = folder.name;
        srWizard?.setStep(1, { focus: false });
        if (scheme) showScheme(scheme, { push });
        window.scrollTo({ top: 0 });
    }

    function resetGenerateBtn() {
        if (!generateBtn) return;
        generateBtn.disabled = false;
        generateBtn.textContent = 'Generate presentation';
    }

    // The "also make" ticks are a standing preference, not a per-deck choice –
    // a teacher who always wants the set shouldn't re-tick every time.
    const COMPANION_PREF_KEY = 'packCompanions';

    function companionPrefs() {
        let saved = {};
        try { saved = JSON.parse(localStorage.getItem(COMPANION_PREF_KEY)) || {}; } catch {}
        return { worksheet: !!saved.worksheet, quiz: !!saved.quiz };
    }

    function restoreCompanionPrefs() {
        const prefs = companionPrefs();
        const ws = $('pack-want-worksheet');
        const qz = $('pack-want-quiz');
        if (ws) ws.checked = prefs.worksheet;
        if (qz) qz.checked = prefs.quiz;
    }

    packOptions?.addEventListener('change', () => {
        localStorage.setItem(COMPANION_PREF_KEY, JSON.stringify({
            worksheet: !!$('pack-want-worksheet')?.checked,
            quiz: !!$('pack-want-quiz')?.checked,
        }));
    });

    function showPresList(tab) {
        // Back in the library: whatever "Prepare" was for, it is not this.
        pendingTutoringLink = null;
        clearLanding();
        hide(authView, bookletView, generatorView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(presListView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        const greetEl = $('dash-greeting');
        if (greetEl) {
            const h = new Date().getHours();
            const greeting = h < 5 ? 'Working late' : h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
            const name = (state.user?.email || '').split('@')[0].split(/[._-]/)[0];
            greetEl.textContent = name
                ? `${greeting}, ${name.charAt(0).toUpperCase() + name.slice(1)}`
                : greeting;
        }
        if (window.location.pathname !== '/') history.pushState({}, '', '/');
        loadPresentations();
        loadWorksheets();
        loadQuizzes();
        loadAllFolders();
        loadSchemes();
        maybeShowWhatsNew();
        loadMarketplaceCount();
        // Not for the pane – for the count on the Shared tab, which is the only
        // hint that a colleague has put something up since you last looked.
        loadOrgLibrary();
        // SlideRenderer needs these before it can draw a custom-template slide,
        // so they're fetched once on sign-in rather than per deck.
        loadCustomTemplates();
        setDashTab(tab || 'presentations');
    }

    // How many lessons are actually on the Marketplace – the thing that turns
    // the band from a destination into a reason to go there. Fetched once per
    // session and written into copy that already reads correctly without it, so
    // a failed or slow request costs nothing and never shows a flash of "0".
    let mktCountLoaded = false;
    async function loadMarketplaceCount() {
        if (mktCountLoaded) return;
        const el = $('mkt-band-sub');
        if (!el) return;
        mktCountLoaded = true;
        try {
            // limit=1 because only the count is wanted here; the response is
            // the same one the landing strip caches.
            const res = await fetch('/marketplace/top.json?limit=1');
            if (!res.ok) return;
            const { total } = await res.json();
            if (!total) return;
            el.textContent = `${total.toLocaleString()} finished lesson${total === 1 ? '' : 's'} from other teachers, `
                + 'ready to take and edit.';
        } catch { /* the band's written copy stands on its own */ }
    }

    function resetGeneratorView() {
        state.presentationId = null;
        state.slides.clear();
        state.planSlides = [];
        state.activeSlideId = null;
        state.activeSlidePosition = null;
        state.aiEditBuffer = '';
        state.dragActive = false;
        closeEventSources();
        if (slidePanel) slidePanel.innerHTML = '';
        if (stageSlideInner) stageSlideInner.innerHTML = '';
        if (planSlidesEl) planSlidesEl.innerHTML = '';
        hide(progressBar, planView, pptEditor, sendModal, $('series-card'), $('scheme-view'), $('gen-series-back'), $('gen-loading'));
        state.scheme = null;
        state.pendingSeriesLesson = null;
        state.replanId = null;
        state.resumedPres = null;
        show(generateCard);
        setError(generateError, '');
        setError(stageError, '');
        resetGenerateBtn();
        // Before syncAllWsTiles, not after: this used to clear the board at the
        // very end, so the tiles were synced against the old value and the
        // board tile stayed lit over an empty select. Level and subject were
        // never cleared at all.
        resetCurriculumFields('exam-board', 'level', subjectCombobox);
        // A new deck starts on its level's theme again, not the last deck's.
        state.themeChosen = false;
        followLevelTheme();
        refreshIgcseHints();
        // Set in code, so no change event fired and the topic picker would
        // still be showing the last deck's list.
        refreshGenTopics();
        genWizard?.setStep(1, { focus: false });
        hidePackDock();
        restoreCompanionPrefs();
        applyTheme(state.selectedTheme);
    }

    const BAEDU_ERROR_MESSAGES = {
        already_linked: 'That BlueAcorn Education account is already linked to a different BA Productivity account. Sign in to that account to unlink it first, or use a different BlueAcorn Education account.',
        account_exists: 'A BA Productivity account already exists with this email. Sign in with your email and password, then link BlueAcorn Education from Settings.',
        login_disabled: 'Signing in with BlueAcorn Education has been turned off for this account. Sign in with your email and password instead.',
        userinfo_failed: 'BlueAcorn Education did not return your account details. Please try again.',
        token_exchange_failed: 'BlueAcorn Education rejected the sign-in request. Please try again.',
        state_mismatch: 'The request expired or was tampered with. Please try again.',
        missing_params: 'The request was incomplete. Please try again.',
        access_denied: 'You declined the BlueAcorn Education authorization request.',
        signup_locked: 'Access is invite-only right now. Enter the access code first, then try again.',
        wrong_account: 'That’s a different BlueAcorn Education account. Sign in with the school account this login belongs to.',
        not_applicable: 'This account can already be signed into with a password.',
    };
    function baeduErrorMessage(reason) {
        return BAEDU_ERROR_MESSAGES[reason] || 'Could not connect to BlueAcorn Education. Please try again.';
    }

    // ── Scripts only a signed-in teacher needs ────────────────
    //
    // KaTeX and the tutoring pages are a quarter of a megabyte that the landing
    // page has no use for, so index.html no longer loads them ahead of this
    // file. They are fetched here instead, once, when there is someone signed
    // in to need them.
    const lateScripts = new Map();
    function loadLateScript(src) {
        if (!lateScripts.has(src)) {
            lateScripts.set(src, new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = src;
                s.onload = () => resolve();
                // Forgotten on failure, so the next thing to want it tries again.
                s.onerror = () => { lateScripts.delete(src); reject(new Error(`Could not load ${src}`)); };
                document.head.appendChild(s);
            }));
        }
        return lateScripts.get(src);
    }
    const loadKatex = () => (window.katex ? Promise.resolve() : loadLateScript('/vendor/katex/katex.min.js'));
    // tutoring.js and its stylesheet; the places list first, as the ask bar
    // matches against it as it draws, and Home's widgets, which it is built from.
    const loadTutoring = () => {
        if (window.Tutoring) return Promise.resolve();
        loadLateStyle('/css/tutoring.css');
        return Promise.all([loadLateScript('/js/tutoring-places.js'), loadLateScript('/js/home-widgets.js')])
            .then(() => loadLateScript('/js/tutoring.js'));
    };
    function loadLateStyle(href) {
        if (document.querySelector(`link[href="${href}"]`)) return;
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = href;
        document.head.appendChild(l);
    }

    /**
     * Waited on by every path that signs someone in, before it shows them
     * anything: the renderers fall back to printing the raw $…$ when KaTeX is
     * missing, and would do so for whatever was drawn before it arrived. Never
     * rejects – maths as plain text is a better failure than no app.
     */
    /**
     * Where the site opens for someone signed in: the tutoring area when their
     * organisation can use it (an agency in its free week or paying), the
     * library otherwise.
     */
    function showHome() {
        if (!state.user?.org?.tutoringOpen) return showPresList();
        history.replaceState({}, '', '/tutoring');
        showTutoringPage('/tutoring', false);
    }

    function loadSignedInScripts() {
        return Promise.all([
            loadKatex(),
            state.user?.org?.tutoring ? loadTutoring() : null,
        ]).catch(() => {});
    }

    // Nearly always signed in already when the server sent the app showing –
    // it hides it for a visitor (routes/home.js) – so start now rather than
    // after /me has answered. Harmless when wrong: checkAuth waits regardless.
    if (appEl && !appEl.hidden) loadKatex().catch(() => {});

    async function checkAuth() {
        try {
            const loginParams = new URLSearchParams(window.location.search);
            // Arrived on an emailed reset link. Handled before /me, because the
            // whole point is that they can't sign in.
            if (window.location.pathname === '/reset-password') {
                await showResetPassword(loginParams.get('token') || '');
                return;
            }
            // An emailed invitation – an organisation seat (?token=) or an
            // individual Pro account (?invite=). Both are handled before /me:
            // the person holding one usually has no account at all yet, and
            // bouncing them to the sign-in wall would lose the token.
            if (window.location.pathname === '/join') {
                const inviteToken = loginParams.get('invite');
                if (inviteToken) {
                    await showUserJoin(inviteToken);
                    return;
                }
                await showJoin(loginParams.get('token') || '');
                return;
            }
            if (loginParams.has('baedu_login_error')) {
                window.history.replaceState({}, '', '/');
            }
            const res = await fetch('/api/auth/me');
            if (!res.ok) {
                showAuth();
                if (loginParams.has('baedu_login_error')) {
                    showAuthModal('login');
                    setError(authError, baeduErrorMessage(loginParams.get('baedu_login_error')));
                }
                answerEarlyLandingTap();
                return;
            }
            state.user = await res.json();
            await loadSignedInScripts();
            syncAdminBtn();
            loadPrefs();

            // A billing problem is worth seeing on every page, not only on the
            // one visit to /org that would otherwise surface it. showOrg()
            // refreshes this same bar from the same data when it runs, so
            // there is one source for it either way.
            if (state.user.org) {
                fetch('/api/org').then((r) => r.json()).then((d) => {
                    if (d && d.org) renderOrgAlertBar(d.org.billingAlert, d.isOrgAdmin);
                }).catch(() => {});
            }

            // Handle redirect back from Stripe Checkout / billing portal
            const params = new URLSearchParams(window.location.search);
            if (params.has('billing')) {
                // Buying seats comes back to /org, not /billing. Sending them
                // to the personal plan page after paying for a team is both
                // wrong and alarming – it shows no sign of what they just did.
                if (window.location.pathname === '/org') {
                    window.history.replaceState({}, '', '/org');
                    // Straight off the back of checkout, so ask Stripe rather
                    // than waiting on the webhook – the whole page hangs off a
                    // seat count only the webhook writes, and this is exactly
                    // the moment it will not have arrived yet.
                    await showOrg(true);
                    if (params.get('billing') === 'success') {
                        window.fbq?.('track', 'Subscribe');
                        window.ttq?.track('Subscribe');
                    }
                    return;
                }
                window.history.replaceState({}, '', '/billing');
                if (params.get('billing') === 'switch-yearly-return') {
                    // Returning from a wallet payment redirect (e.g. Revolut
                    // Pay) – confirm the invoice actually got paid before
                    // treating the switch as complete.
                    await apiFetch('/api/billing/switch-yearly/finalize', { method: 'POST' }).catch(() => {});
                }
                if (params.get('billing') === 'success') {
                    window.fbq?.('track', 'Subscribe');
                    window.ttq?.track('Subscribe');
                }
                showBilling(true); // sync from Stripe – webhook may not have landed yet
                return;
            }

            // Signed in via BlueAcorn Education from "forgot your password" –
            // that sign-in was the proof, so go straight to choosing a new one.
            if (params.has('password_reset')) {
                window.history.replaceState({}, '', '/');
                showPresList();
                openPasswordModal('reset', 'change');
                return;
            }

            // Handle redirect back from re-authenticating to set a password.
            // ?from=billing means they started on the billing page, so put them
            // back there with the dialog open rather than in Settings.
            if (params.has('baedu_verify') || params.has('baedu_verify_error')) {
                const fromBilling = params.get('from') === 'billing';
                const vErr = params.get('baedu_verify_error');
                window.history.replaceState({}, '', fromBilling ? '/billing' : '/');

                if (fromBilling) {
                    await showBilling();
                    if (vErr) setError(billingError, baeduErrorMessage(vErr));
                    else openPasswordModal('billing');
                    return;
                }

                showPresList();
                settingsPasswordOpen = !vErr;
                await showSettings(null, 'account');
                if (vErr) {
                    const slot = $('set-password-slot');
                    if (slot) slot.innerHTML = `<p class="msg msg-error">${esc(baeduErrorMessage(vErr))}</p>`;
                }
                return;
            }

            // Handle redirect back from linking BlueAcorn Education in Settings
            if (params.has('baedu_link') || params.has('baedu_link_error')) {
                window.history.replaceState({}, '', '/');
                showPresList();
                showSettings(params.has('baedu_link_error') ? (params.get('baedu_link_error') || 'unknown_error') : null);
                return;
            }

            // Handle handoff from the "Presentation Clipper" browser extension
            if (params.has('prefill_topic')) {
                window.history.replaceState({}, '', '/');
                const prefillTopic = (params.get('prefill_topic') || '').slice(0, 500);
                const prefillContent = params.get('prefill_content') || '';
                const prefillTarget = params.get('prefill_target') || 'presentation';

                if (prefillTarget === 'worksheet') {
                    showWorksheetPage(prefillTopic, 'list', '', '', '', '', prefillContent);
                } else if (prefillTarget === 'quiz') {
                    showQuizPage(prefillTopic, 'list', '', '', '', '', prefillContent);
                } else {
                    showGenerator();
                    if (topicInput) {
                        topicInput.value = prefillTopic;
                        // Assigning .value fires nothing, and the subject
                        // auto-detect listens for input.
                        topicInput.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                    if (prefillContent && inputContent) {
                        inputContent.value = prefillContent;
                        setGenContextOpen(true);
                    }
                    genWizard?.setStep(1, { focus: false });
                    topicInput?.focus();
                }
                return;
            }

            // Restore view from URL on page load / refresh
            const pathname = window.location.pathname;
            const presMatch = pathname.match(/^\/p\/([^/]+)$/);
            const wsMatch   = pathname.match(/^\/worksheets\/([^/]+)$/);
            const bkMatch   = pathname.match(/^\/booklets\/([^/]+)$/);
            const quizMatch = pathname.match(/^\/quizzes\/([^/]+)$/);
            const quizResultsMatch = pathname.match(/^\/quizzes\/([^/]+)\/responses$/);
            const markMatch = pathname.match(/^\/marking\/([^/]+)$/);
            const seriesMatch = pathname.match(/^\/series\/([^/]+)$/);
            const studentMatch = pathname.match(/^\/students\/([^/]+)$/);
            const lessonMatch = pathname.match(/^\/lessons\/([^/]+)$/);
            const classMatch = pathname.match(/^\/classes\/([^/]+)$/);
            const assignmentMatch = pathname.match(/^\/assignments\/([^/]+)$/);
            const studentMarkMatch = pathname.match(/^\/students\/([^/]+)\/responses\/([^/]+)$/);
            if (presMatch) {
                // Straight to the deck. Showing the library first meant a
                // refresh sat on the library while six lists loaded, with the
                // one request that mattered queued behind them. The library
                // loads when it is gone back to; it is only shown here if the
                // deck cannot be opened. Custom templates are the exception:
                // a slide made from one cannot be drawn without them.
                loadCustomTemplates();
                if (!await openPresentation(presMatch[1])) showPresList();
            } else if (bkMatch) {
                await openBooklet(bkMatch[1]);
            } else if (wsMatch) {
                await openWorksheet(wsMatch[1]);
            } else if (quizResultsMatch) {
                await showQuizResults(quizResultsMatch[1], { push: false });
            } else if (quizMatch) {
                await openQuiz(quizMatch[1]);
            } else if (markMatch) {
                await openMarkingSet(markMatch[1]);
            } else if (seriesMatch) {
                if (!await openScheme(seriesMatch[1], { push: false })) showPresList();
            } else if (pathname === '/marking') {
                showMarkingPage();
            } else if (studentMarkMatch) {
                await showResponseMarking(studentMarkMatch[1], studentMarkMatch[2], false);
            } else if (studentMatch) {
                await showStudentProfile(studentMatch[1]);
            } else if (lessonMatch) {
                await openLessonDetail(lessonMatch[1], false);
            } else if (classMatch) {
                await openClass(classMatch[1], false);
            } else if (assignmentMatch) {
                await openAssignmentByLink(assignmentMatch[1]);
            } else if (pathname === '/students') {
                showStudentsPage(false);
            } else if (pathname === '/lessons' || pathname === '/classes') {
                // Pages of their own once; sections of Pupils now. Old links
                // and bookmarks land where the list went.
                history.replaceState({}, '', '/students');
                showStudentsPage(false);
            } else if (pathname === '/booklets') {
                showBookletPage();
            } else if (pathname === '/worksheets') {
                showWorksheetPage('', 'list');
            } else if (pathname === '/quizzes') {
                showQuizPage('', 'list');
            } else if (pathname === '/tutoring' || pathname.startsWith('/tutoring/')) {
                showTutoringPage(pathname, false);
            } else if (pathname === '/billing') {
                showBilling();
            } else if (pathname === '/org') {
                if (state.user?.org) showOrg();
                else { history.replaceState({}, '', '/'); showPresList(); }
            } else if (pathname === '/') {
                // The library is at / too: refreshing it, or coming back to
                // it, stays on it. Only arriving at the site opens tutoring.
                const nav = performance.getEntriesByType?.('navigation')?.[0];
                if (nav && nav.type !== 'navigate') showPresList();
                else showHome();
            } else {
                showPresList();
            }
        } catch { showAuth(); answerEarlyLandingTap(); }
    }

    // A tap on a landing button from before this script arrived, which the
    // inline script above #landing-view kept. Called once the visitor is known
    // to be signed out and showAuth() has run – not sooner, because showAuth()
    // closes the auth dialog, and would close the one this opens.
    function answerEarlyLandingTap() {
        window.landingReady = true;
        const action = window.earlyLandingAction;
        window.earlyLandingAction = null;
        document.querySelectorAll('[data-landing-action].is-waiting').forEach(b => b.classList.remove('is-waiting'));
        if (action && authModal?.hidden) showAuthModal(action === 'signin' ? 'login' : 'register');
    }

    // Landing CTA buttons
    document.querySelectorAll('[data-landing-action="signin"]').forEach(b =>
        b.addEventListener('click', () => showAuthModal('login'))
    );
    document.querySelectorAll('[data-landing-action="register"]').forEach(b =>
        b.addEventListener('click', () => showAuthModal('register'))
    );

    $('auth-modal-close')?.addEventListener('click', hideAuthModal);
    $('auth-modal')?.addEventListener('click', e => {
        if (e.target.classList.contains('auth-modal-backdrop')) hideAuthModal();
    });

    // Auth tabs
    loginTab?.addEventListener('click', () => showAuthModal('login'));
    registerTab?.addEventListener('click', () => showAuthModal('register'));

    signupGateForm?.addEventListener('submit', async (e) => {
        e.preventDefault(); setError(authError, '');
        const code = signupGateForm.code.value;
        try {
            const res = await fetch('/api/auth/signup-code', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code }),
            });
            const data = await res.json();
            if (!res.ok) return setError(authError, data.error || 'Incorrect code');
            state.signupUnlocked = true;
            state.signupCode = code;
            signupGateForm.reset();
            showAuthModal(state.pendingAuthTab || 'login');
        } catch { setError(authError, 'Network error'); }
    });

    // Where to go once signed in, when we were sent here from somewhere.
    //
    // The public Marketplace links here as its "More ways to sign in" escape
    // hatch, and without this that link was a one-way door: someone reading a
    // lesson who needed the school sign-in route landed back on the library,
    // having lost the listing that persuaded them to sign in at all.
    //
    // Only same-origin paths, and never a protocol-relative one – "//evil.com"
    // starts with a slash and is an absolute URL to another host, which is the
    // whole open-redirect trick.
    function nextTarget() {
        const raw = new URLSearchParams(location.search).get('next');
        if (!raw || raw[0] !== '/' || raw[1] === '/' || raw[1] === '\\') return null;
        return raw;
    }

    // Returns true when it has navigated away, so the caller stops setting up a
    // view the browser is about to leave.
    function goToNext() {
        const next = nextTarget();
        if (next) { window.location.href = next; return true; }
        return false;
    }

    loginForm?.addEventListener('submit', async (e) => {
        e.preventDefault(); setError(authError, '');
        const email = loginForm.email.value.trim();
        const password = loginForm.password.value;
        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, code: state.signupCode }),
            });
            const data = await res.json();
            if (!res.ok) { loginForm.password.value = ''; return setError(authError, data.error || 'Login failed'); }
            // Login answers with less than /me: without the organisation's
            // details there is nothing to say where to open.
            state.user = await fetch('/api/auth/me').then((r) => (r.ok ? r.json() : data)).catch(() => data);
            syncAdminBtn();
            await loadSignedInScripts();
            loadPrefs();
            // Signed in on the way to accepting an invitation (signInToAccept
            // in showJoin / showUserJoin): back to it, now as whoever they
            // signed in as.
            if (window.location.pathname === '/join') {
                const params = new URLSearchParams(window.location.search);
                if (params.get('invite')) return void showUserJoin(params.get('invite'));
                return void showJoin(params.get('token') || '');
            }
            if (goToNext()) return;
            showHome();
        } catch { setError(authError, 'Network error'); }
    });

    // ── Forgot password ───────────────────────────────────
    $('forgot-link')?.addEventListener('click', () => {
        const typed = loginForm?.email?.value.trim();
        showAuthModal('forgot');
        if (typed && forgotForm) forgotForm.email.value = typed;
        forgotForm?.email?.focus();
    });
    $('forgot-back')?.addEventListener('click', () => showAuthModal('login'));
    $('forgot-sent-back')?.addEventListener('click', () => showAuthModal('login'));

    forgotForm?.addEventListener('submit', async (e) => {
        e.preventDefault(); setError(authError, '');
        const btn = forgotForm.querySelector('button[type=submit]');
        const email = forgotForm.email.value.trim();
        btn.disabled = true;
        btn.textContent = 'Sending…';
        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) return setError(authError, data.error || 'Could not send the reset email');
            // Deliberately the same answer whether or not that address exists.
            showAuthModal('forgot-sent');
        } catch {
            setError(authError, 'Network error');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Email me a reset link';
        }
    });

    registerForm?.addEventListener('submit', async (e) => {
        e.preventDefault(); setError(authError, '');
        hide($('auth-email-ask'));
        const email = registerForm.email.value.trim();
        const password = registerForm.password.value;
        const firstName = registerForm.firstName?.value.trim() || '';
        const lastName = registerForm.lastName?.value.trim() || '';
        try {
            const res = await fetch('/api/auth/register', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, firstName, lastName, code: state.signupCode, landing: LANDING_QUERY, emailChecked: questionedEmail }),
            });
            const data = await res.json();
            // Asked about the address, not refused: the password stays, so
            // either answer is one press and not a form to fill in again.
            if (data.questioned) return askAboutEmail($('auth-email-ask'), registerForm, data);
            if (!res.ok) { registerForm.password.value = ''; return setError(authError, data.error || 'Registration failed'); }
            state.user = data; syncAdminBtn();
            await loadSignedInScripts();
            window.fbq?.('track', 'CompleteRegistration');
            window.ttq?.track('CompleteRegistration');
            // Nothing is news to an account made a moment ago.
            savePrefs({ whatsNew: WHATS_NEW.id }).catch(() => {});
            // A referral discount is the one thing worth interrupting the
            // return journey for – it is the only moment they are told about
            // it, and a redirect would take the modal with it.
            if (data.referral) { showPresList(); return showReferralModal(data.referral); }
            if (goToNext()) return;
            showPresList();
        } catch { setError(authError, 'Network error'); }
    });

    btnLogout?.addEventListener('click', async () => {
        hide(userMenu); btnUser?.setAttribute('aria-expanded', 'false');
        await fetch('/api/auth/logout', { method: 'POST' }); showAuth();
    });

    logo?.addEventListener('click', () => { if (state.user) showPresList(); });

    // Only one nav dropdown open at a time
    function closeNavMenus() {
        if (createMenu) hide(createMenu);
        document.querySelectorAll('.ba-apps-menu').forEach(m => hide(m));
        document.querySelectorAll('.ba-apps-btn').forEach(b => b.setAttribute('aria-expanded', 'false'));
        if (userMenu) { hide(userMenu); btnUser?.setAttribute('aria-expanded', 'false'); }
    }

    // Create dropdown
    btnCreate?.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = isShown(createMenu);
        closeNavMenus();
        if (!wasOpen && createMenu) show(createMenu);
    });
    createMenu?.addEventListener('click', (e) => {
        const item = e.target.closest('.nav-create-item');
        if (!item) return;
        hide(createMenu);
        if (item.dataset.action === 'presentation') showGenerator();
        else if (item.dataset.action === 'series') showSeriesPage();
        else if (item.dataset.action === 'worksheet') showWorksheetPage('', 'list');
        else if (item.dataset.action === 'quiz') showQuizPage('', 'list');
        else if (item.dataset.action === 'booklet') showBookletPage();
        // showBookletPage does the plan check itself, so every other way in
        // (the library's empty state, a /booklets URL) is covered by the same
        // one place rather than by a check per entry point.
    });
    document.addEventListener('click', (e) => {
        if (isShown(createMenu) && !navCreateWrap?.contains(e.target)) {
            hide(createMenu);
        }
    });

    // Blue Acorn app switcher (one instance in landing nav, one in app nav)
    document.querySelectorAll('.ba-apps').forEach((baApps) => {
        const btn  = baApps.querySelector('.ba-apps-btn');
        const menu = baApps.querySelector('.ba-apps-menu');
        if (!btn || !menu) return;
        const close = () => { hide(menu); btn.setAttribute('aria-expanded', 'false'); };
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const wasOpen = isShown(menu);
            closeNavMenus();
            if (!wasOpen) { show(menu); btn.setAttribute('aria-expanded', 'true'); }
        });
        document.addEventListener('click', (e) => {
            if (isShown(menu) && !baApps.contains(e.target)) close();
        });
    });

    // ── Account avatar menu ───────────────────────────────
    const btnUser       = $('btn-user');
    const userMenu      = $('user-menu');
    const avatarInitial = $('nav-avatar-initial');
    // Below 720px the bar drops Pupils and Tutoring and the account menu carries
    // them instead. Mirrored at open time rather than at each of the fifteen
    // show() call sites, so the two copies cannot disagree about who may see them.
    function syncNavMenuOnly() {
        const mS = $('btn-menu-students');
        if (mS) {
            mS.hidden = !navStudentsBtn || navStudentsBtn.hidden;
            const src = $('nav-students-badge');
            const dst = $('btn-menu-students-badge');
            if (src && dst) {
                dst.textContent = src.textContent;
                dst.hidden = src.hidden;
            }
        }
        for (const [menuId, navBtn] of [
            ['btn-menu-students', navStudentsBtn],
            ['btn-menu-tutoring', navTutoringBtn],
        ]) {
            const item = $(menuId);
            if (!item) continue;
            if (navBtn?.hasAttribute('aria-current')) item.setAttribute('aria-current', 'page');
            else item.removeAttribute('aria-current');
        }
        const mT = $('btn-menu-tutoring');
        if (mT) mT.hidden = !navTutoringBtn || navTutoringBtn.hidden;
    }

    btnUser?.addEventListener('click', (e) => {
        e.stopPropagation();
        const wasOpen = isShown(userMenu);
        closeNavMenus();
        if (!wasOpen) {
            syncNavMenuOnly();
            show(userMenu); btnUser.setAttribute('aria-expanded', 'true');
        }
    });
    document.addEventListener('click', (e) => {
        if (isShown(userMenu) && !e.target.closest('#nav-user')) {
            hide(userMenu); btnUser?.setAttribute('aria-expanded', 'false');
        }
    });
    // Keep the avatar initial in sync with the signed-in email
    if (navUserEmail && avatarInitial) {
        new MutationObserver(() => {
            avatarInitial.textContent = (navUserEmail.textContent || '').trim().charAt(0) || '?';
        }).observe(navUserEmail, { childList: true, characterData: true, subtree: true });
    }

    // ── Billing, quota & admin ────────────────────────────
    const btnSettings  = $('btn-settings');
    const btnBilling   = $('btn-billing');
    const btnUpgrade   = $('btn-upgrade');
    const btnAdmin     = $('btn-admin');
    const btnOrg       = $('btn-org');
    const orgBody      = $('org-body');
    const orgError     = $('org-error');
    // The organisation panel has two homes: /org's own card, and the tutoring
    // area's Admin tab, which gets the same panel without the seats (an agency
    // doesn't buy by the seat). orgPanel is whichever is on screen, and both
    // hosts carry the panel's delegated listeners.
    const orgEmbed = document.createElement('div');
    orgEmbed.innerHTML = '<div class="error" role="alert" hidden></div><div></div>';
    const orgPagePanel  = { body: orgBody, error: orgError, embedded: false };
    const orgEmbedPanel = { body: orgEmbed.lastElementChild, error: orgEmbed.firstElementChild, embedded: true };
    let orgPanel = orgPagePanel;
    const onOrgPanel = (evt, fn) => [orgBody, orgEmbedPanel.body].forEach((host) => host?.addEventListener(evt, fn));
    const billingBody  = $('billing-body');
    const billingError = $('billing-error');
    const quotaModal   = $('quota-modal');
    const quotaMessage = $('quota-message');

    const KIND_LABELS = { presentation: 'Presentations', worksheet: 'Worksheets', quiz: 'Quizzes', marking: 'Marking', edit: 'Edits', bucket: 'Generations', booklet: 'Booklets' };

    /**
     * The organisation's crest in the site header.
     *
     * Only when there is one to draw: an organisation that has not uploaded a
     * logo gets nothing rather than its name in text, because the divider and a
     * second piece of wordmark next to ours is clutter, and the org page is one
     * click away in the account menu either way.
     *
     * The image is fetched from /api/org/logo like the org page's preview, so
     * the browser caches it as an ordinary image instead of it riding along in
     * every /api/auth/me response as base64. `src` is only ever set when we
     * know there is a logo, so this never fires a request that 404s.
     */
    function syncOrgLockup() {
        syncTutoringNav();

        const wrap = $('nav-org');
        const img = $('nav-org-logo');
        if (!wrap || !img) return;

        const org = state.user?.org;
        if (!org || !org.hasLogo) { hide(wrap); return; }

        const name = org.name || 'your organisation';
        // Only assign src when it changes: re-assigning the same URL restarts
        // the request, and syncAdminBtn runs on every plan change and view.
        // Re-assigned only when it changes: setting the same URL restarts the
        // request, and this runs on every plan change and view switch. `v`
        // moves when an admin uploads a new crest, which is what gets the new
        // one on screen without waiting out the endpoint's own cache.
        const src = `/api/org/logo?v=${encodeURIComponent(org.logoVersion || org.id)}`;
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
        img.alt = name;
        wrap.title = name;
        wrap.setAttribute('aria-label', `${name} – your organisation`);
        // A crest that fails to load must not leave a stray divider behind.
        img.onerror = () => hide(wrap);
        show(wrap);
    }

    $('nav-org')?.addEventListener('click', () => showOrg());

    function syncAdminBtn() {
        state.user?.isAdmin ? show(btnAdmin) : hide(btnAdmin);
        // The organisation entry only exists for people who are in one, which
        // is most easily read off /api/auth/me – the membership rides along on
        // a query the server was making anyway.
        // …and whose organisation has a page: an agency's is in Tutoring.
        state.user?.org && !state.user.org.agencyPlan ? show(btnOrg) : hide(btnOrg);
        // The mirror of the line above: bringing your school in is only an
        // invitation for someone whose school isn't here yet.
        const btnPilot = $('btn-pilot');
        if (btnPilot) state.user?.org ? hide(btnPilot) : show(btnPilot);
        syncOrgLockup();
        // Same membership decides the library's Shared tab. Hidden rather than
        // disabled for everyone else: a solo teacher has no organisation to be
        // told about.
        const sharedTab = $('dash-tab-shared');
        if (sharedTab) state.user?.org ? show(sharedTab) : hide(sharedTab);

        // The Pro badge on Booklet is shown to everyone who does not have it,
        // so the create menu says so before the click rather than after.
        document.querySelectorAll('[data-pro-badge]').forEach((el) => {
            state.user && state.user.plan !== 'pro' ? show(el) : hide(el);
        });

        syncEmailConfirm();

        if (!btnUpgrade) return;
        if (!state.user) { hide(btnUpgrade); return; }
        const isPro = state.user.plan === 'pro';
        const proBadge = $('nav-avatar-pro');
        if (proBadge) proBadge.hidden = !isPro;
        $('btn-user')?.setAttribute('aria-label', isPro ? 'Account (Pro)' : 'Account');
        if (isPro) hide(btnUpgrade);
        else show(btnUpgrade);
    }

    // ── Accessibility preferences ─────────────────────────
    //
    // One fetch at sign-in, then everything that cares reads state.prefs. The
    // export pickers, the app's own type size and the motion override all hang
    // off this, so it is deliberately not gated on the settings modal opening.
    //
    // A failed fetch is silent and leaves the defaults in place. Losing a
    // preference is a worse day for the teacher than an error banner would
    // help with, but it is not a reason to block the app from loading.
    async function loadPrefs() {
        state.prefsLoaded = false;
        try {
            const res = await apiFetch('/api/settings/prefs');
            const data = await res.json();
            if (data?.prefs) { state.prefs = data.prefs; state.prefsLoaded = true; }
        } catch { /* defaults stand */ }
        applyPrefs();
        refreshA11yPickers();
        maybeShowWhatsNew();
    }

    /**
     * Push the on-screen preferences onto <html>, where the CSS reads them.
     *
     * Attributes rather than inline styles so the rules stay in the stylesheet
     * and so the motion override can hook the same declarations the
     * prefers-reduced-motion media queries already use.
     */
    function applyPrefs() {
        const a11y = state.prefs?.a11y || {};
        const root = document.documentElement;
        // 1 is the default and writing it would put an attribute on every page
        // for every user; only deviations get one.
        if (a11y.textScale && a11y.textScale !== 1) {
            root.style.setProperty('--text-scale', String(a11y.textScale));
            root.setAttribute('data-text-scale', String(a11y.textScale));
        } else {
            root.style.removeProperty('--text-scale');
            root.removeAttribute('data-text-scale');
        }
        root.toggleAttribute('data-reduce-motion', !!a11y.reduceMotion);
    }

    /**
     * Save a patch and adopt whatever the server says it stored.
     *
     * The response is the merged, validated object rather than the patch, so a
     * value the server clamped or rejected is reflected in the UI immediately
     * instead of the pane showing something that was never saved.
     */
    async function savePrefs(patch) {
        const res = await apiFetch('/api/settings/prefs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not save that.');
        state.prefs = data.prefs;
        applyPrefs();
        refreshA11yPickers();
        return state.prefs;
    }

    // ── Referral welcome modal ────────────────────────────────

    const referralModal = $('referral-modal');

    function showReferralModal(offer) {
        if (!referralModal || !offer) return;
        const headline = $('referral-modal-headline');
        const term = $('referral-modal-term');
        const eyebrow = $('referral-modal-eyebrow');
        if (headline) headline.textContent = offer.headline || '';
        if (term) term.textContent = offer.term || '';
        // Said here or nowhere: this modal is the one time a referred user is
        // told what they have, and it must not read as a discount on whichever
        // plan they later pick.
        const note = $('referral-modal-note');
        if (note && offer.monthlyOnly) {
            note.textContent = 'It’s already on your account, ready for the monthly plan. '
                + 'Nothing to enter – it comes off automatically if and when you upgrade to Pro.';
        }
        // Partners are named where we have a name for them – "Sarah's newsletter
        // sent you" reads as a favour, "a partner link" reads as machinery.
        if (eyebrow) {
            eyebrow.textContent = offer.partner
                ? `${offer.partner} sent you here`
                : 'You came in through a partner link';
        }
        show(referralModal);
        document.body.style.overflow = 'hidden';
        // Focus the dialog itself, not the CTA inside it: it announces the
        // dialog and puts Tab at the top of it, without painting a focus ring
        // on a button for someone who arrived here by mouse.
        referralModal.querySelector('.ref-card')?.focus();
    }

    function hideReferralModal() {
        hide(referralModal);
        document.body.style.overflow = '';
    }

    referralModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-referral-close]')) hideReferralModal();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isShown(referralModal)) hideReferralModal();
    });
    $('referral-modal-billing')?.addEventListener('click', () => { hideReferralModal(); showBilling(); });

    // ── What's new ────────────────────────────────────────
    // One announcement at a time. To announce something else, change the id
    // and the words here: every account that hasn't seen that id is shown it
    // once, in the library, and it stays under "What's new" in the account menu.
    const WHATS_NEW = {
        id: '2026-10-word-and-layout',
        title: 'Two new things for your worksheets',
        items: [
            {
                icon: '<rect x="4" y="4" width="16" height="5" rx="1.5"/><rect x="4" y="12" width="7" height="8" rx="1.5"/><rect x="14" y="12" width="6" height="8" rx="1.5"/>',
                name: 'Say how it should be laid out',
                text: 'Press this icon at the bottom of the topic box and describe the sheet, e.g. gap-fill notes for each part of your PowerPoint, then an activity on each.',
            },
            {
                icon: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="m8.5 12 1.2 5 1.8-4 1.8 4 1.2-5"/>',
                name: 'Download as Word',
                text: 'Choose <b>Word</b> in <b>Print / Download</b> to edit the sheet yourself. Add <b>With answers</b> for the answer key.',
            },
        ],
        // The button under the list: where to go to try it.
        cta: 'Make a worksheet',
        open: () => showWorksheetPage('', 'list'),
    };

    const whatsNewModal = $('whatsnew-modal');

    function showWhatsNew() {
        if (!whatsNewModal) return;
        $('whatsnew-title').textContent = WHATS_NEW.title;
        $('whatsnew-try').textContent = WHATS_NEW.cta;
        $('whatsnew-list').innerHTML = WHATS_NEW.items.map(item => `
            <li>
                <span class="wn-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${item.icon}</svg></span>
                <span class="wn-text"><b class="wn-name">${item.name}</b>${item.text}</span>
            </li>`).join('');
        show(whatsNewModal);
        document.body.style.overflow = 'hidden';
        whatsNewModal.querySelector('.wn-card')?.focus();
        // Remembered on being shown, not on being closed: someone who shuts the
        // tab with it open has still seen it.
        if (state.prefs.whatsNew !== WHATS_NEW.id) {
            state.prefs.whatsNew = WHATS_NEW.id;
            savePrefs({ whatsNew: WHATS_NEW.id }).catch(() => {});
        }
    }

    function hideWhatsNew() {
        hide(whatsNewModal);
        document.body.style.overflow = '';
    }

    // Only in the library, only once the account's own answer is in, and never
    // on top of a dialog that is already saying something.
    function maybeShowWhatsNew() {
        if (!state.user || !state.prefsLoaded || state.prefs.whatsNew === WHATS_NEW.id) return;
        if (!isShown(presListView) || isShown(referralModal) || isShown(quotaModal)) return;
        showWhatsNew();
    }

    whatsNewModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-whatsnew-close]')) hideWhatsNew();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isShown(whatsNewModal)) hideWhatsNew();
    });
    $('whatsnew-try')?.addEventListener('click', () => { hideWhatsNew(); WHATS_NEW.open(); });
    $('btn-whatsnew')?.addEventListener('click', () => { hide(userMenu); showWhatsNew(); });

    function showQuotaModal(body = {}) {
        if (!quotaModal) return;
        // A sign-in's reply doesn't say what kind of organisation this is, and
        // the answer decides what this dialog offers: ask once, then come back.
        const org = state.user?.org;
        if (org && org.agencyPlan === undefined) {
            org.agencyPlan = false;
            fetch('/api/org').then((r) => r.json()).then((d) => { org.agencyPlan = !!(d && d.agencyPlan); })
                .catch(() => {}).then(() => showQuotaModal(body));
            return;
        }

        // Two different reasons land here: a monthly allowance is spent, or the
        // feature is Pro-only. Telling someone they've "hit their limit" for a
        // feature they've never used would just be wrong.
        const featureGate = body.error === 'pro_required';
        const kind = KIND_LABELS[body.kind]?.toLowerCase() || 'generations';

        const titleEl = $('quota-title');
        if (titleEl) titleEl.textContent = featureGate ? 'That one’s on Pro' : 'You’re on a roll';

        if (quotaMessage) {
            quotaMessage.textContent = body.message
                || (body.limit
                    ? `You've used all ${body.limit} ${kind} included in the ${body.plan || 'free'} plan this month.`
                    : `You've hit your plan's monthly limit for ${kind}.`);
        }

        const meter = quotaModal.querySelector('.quota-meter');
        if (meter) meter.hidden = featureGate;   // nothing has been used up
        const meterLabel = $('quota-meter-label');
        const meterCount = $('quota-meter-count');
        if (meterLabel) {
            meterLabel.textContent = body.error === 'bucket_exhausted'
                ? 'Organisation bucket'
                : body.error === 'bucket_kind_capped'
                    ? `${KIND_LABELS[body.kind] || 'This kind'} (bucket cap)`
                    : (KIND_LABELS[body.kind] || 'Monthly allowance');
        }
        if (meterCount) meterCount.textContent = body.limit ? `${body.limit} / ${body.limit} used` : 'Limit reached';

        // A bucket-exhausted or kind-capped account is already Pro through its
        // organisation. Pro of their own takes over once the bucket can't
        // cover them (middleware/quota.js, bucketOrPersonal), so the server
        // marks the wall `canBuyPro` for anyone without it and the upsell
        // stays. Someone who already has their own Pro never sees this wall
        // for the bucket; theirs is the ordinary monthly one.
        const isBucketWall = body.error === 'bucket_exhausted' || body.error === 'bucket_kind_capped';
        const noUpsell = isBucketWall && !body.canBuyPro;
        const perks = quotaModal.querySelector('.quota-perks');
        const plans = quotaModal.querySelector('.quota-plans');
        const upgradeBtn = $('quota-upgrade');
        if (perks) perks.hidden = noUpsell;
        if (plans) plans.hidden = noUpsell;
        if (upgradeBtn) upgradeBtn.hidden = noUpsell;

        // Someone in a tutoring agency whose plan isn't running: its free week
        // is over, or its subscription has stopped. A personal Pro plan is not
        // what they are missing, so it isn't offered: the agency's own plan
        // is, to whoever can start it.
        // Only in the Tutoring area, where the agency is what's on screen. In
        // Productivity a wall is a personal one: the answer there is Pro, which
        // a personal subscription grants whatever the agency's state
        // (seatPlanFor takes whichever of the two gives access).
        const inTutoring = !!tutoringView && !tutoringView.hidden;
        const agencyWall = inTutoring && !!org?.agencyPlan && !isBucketWall;
        // The one Productivity exception: the admin of an agency that is paying
        // (a bucket only exists while its plan is live) whose month's resources
        // have run out can buy more, from the agency's admin page.
        const topUpWall = isBucketWall && !!org?.agencyPlan && org.role === 'admin';
        const card = quotaModal.querySelector('.quota-card');
        quotaModal.dataset.agency = agencyWall ? '1' : '';
        card?.classList.toggle('is-agency', agencyWall);
        quotaModal.querySelector('.quota-agency')?.remove();
        quotaModal.dataset.topup = topUpWall ? '1' : '';
        if (upgradeBtn) upgradeBtn.textContent = isBucketWall ? 'Get Pro of your own' : 'Upgrade to Pro';
        if (topUpWall && upgradeBtn) {
            // The admin's answer is the agency's top-up, not a plan of their own.
            if (perks) perks.hidden = true;
            if (plans) plans.hidden = true;
            upgradeBtn.hidden = false;
            upgradeBtn.textContent = 'Top up resources';
            // The server's wording is for members; this reader is the admin.
            if (quotaMessage) quotaMessage.textContent = quotaMessage.textContent.replace('Ask an admin to top it up.', 'You can top it up now.');
        }
        if (agencyWall) {
            const admin = org.role === 'admin';
            const name = org.name || 'your agency';
            if (titleEl) titleEl.textContent = `Keep ${name} running`;
            if (quotaMessage) {
                quotaMessage.textContent = admin
                    ? 'Your free week is over. Add a card and everyone carries on where they left off.'
                    : 'The free week is over. Ask an admin to add a card; everything you’ve made is still here.';
            }
            if (meter) meter.hidden = true;
            if (perks) perks.hidden = true;
            if (plans) plans.hidden = true;
            if (upgradeBtn) { upgradeBtn.hidden = !admin; upgradeBtn.textContent = 'Add a card and carry on'; }

            // What the plan is, in the agency's own prices. Those are an
            // admin's to see (/api/org/agency), so a tutor gets the same list
            // without them; nothing is quoted that the server didn't send.
            const gbp = (pence) => `£${(pence / 100).toFixed(2).replace(/\.00$/, '')}`;
            const draw = (d) => {
                const r = d && d.rates;
                // A plan that was paying and has stopped isn't a free week ending.
                if (d && d.access && d.access.state === 'lapsed' && quotaMessage) {
                    quotaMessage.textContent = 'Your plan has stopped. Add a card and everyone carries on where they left off.';
                }
                const free = d && d.ladders && d.ladders.hours[0].pence === 0 ? d.ladders.hours[0].upTo : 0;
                const line = (text) => `<li><span class="quota-perk-check">&#10003;</span><span>${text}</span></li>`;
                const box = document.createElement('div');
                box.className = 'quota-agency';
                box.innerHTML = `
                    ${r ? `<p class="quota-agency-price"><b>${gbp(r.base)}</b> a month<small>then only what you choose to add</small></p>` : ''}
                    <ul class="quota-perks">
                        ${line('<b>The whole office</b>: enquiries, scheduling, reports, invoices and tutor pay')}
                        ${line(free ? `<b>${free} classroom hours on us</b>, every month` : '<b>A classroom of your own</b>, built in')}
                        ${line(`<b>Resources in one click</b> for every lesson${r ? `, ${gbp(r.g1)} for 500` : ''}`)}
                        ${line('<b>Your brand</b>, on your own web address')}
                    </ul>`;
                quotaModal.querySelector('.quota-agency')?.remove();
                upgradeBtn?.before(box);
            };
            draw(null);
            if (admin) fetch('/api/org/agency').then((res) => (res.ok ? res.json() : null)).then((d) => { if (d && quotaModal.dataset.agency) draw(d); }).catch(() => {});
        }

        show(quotaModal);
        document.body.style.overflow = 'hidden';
    }
    function hideQuotaModal() {
        hide(quotaModal);
        document.body.style.overflow = '';
    }
    quotaModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-quota-close]')) hideQuotaModal();
    });
    let quotaInterval = 'year';
    const quotaPlans = quotaModal ? Array.from(quotaModal.querySelectorAll('.quota-plan')) : [];
    quotaPlans.forEach(btn => btn.addEventListener('click', () => {
        quotaInterval = btn.dataset.interval;
        quotaPlans.forEach(b => {
            const selected = b === btn;
            b.classList.toggle('is-selected', selected);
            b.setAttribute('aria-checked', String(selected));
        });
    }));
    $('quota-upgrade')?.addEventListener('click', () => {
        // For an agency (see showQuotaModal) the plan to start is its own, and
        // a paying agency's top-up is bought on the same admin page.
        if (quotaModal.dataset.agency || quotaModal.dataset.topup) {
            hideQuotaModal();
            return showTutoringPage('/tutoring/admin');
        }
        startCheckout(quotaInterval);
    });

    // ── Settings modal ─────────────────────────────────────
    const settingsModal = $('settings-modal');
    const settingsShell = settingsModal?.querySelector('.set-shell');
    const settingsBody  = $('settings-body');
    const settingsNav   = settingsModal ? Array.from(settingsModal.querySelectorAll('.set-nav-item')) : [];
    const settingsTitle = $('set-pane-title');

    const PANE_TITLES = { account: 'Account', accessibility: 'Accessibility', connections: 'Connections' };
    let settingsPane = 'account';
    let settingsData = null;
    let settingsLinkError = null;
    let settingsLastFocus = null;
    // Set when we return from the BA Ed re-auth, so the Account pane opens
    // straight into the password form instead of making them click again.
    let settingsPasswordOpen = false;

    async function showSettings(linkError, pane) {
        if (!settingsModal) return;
        settingsLastFocus = document.activeElement;
        settingsLinkError = linkError || null;
        settingsData = null;
        show(settingsModal);
        document.body.style.overflow = 'hidden';
        syncSettingsIdentity();
        // An OAuth error is about the connection, so open where it happened.
        selectSettingsPane(pane || (linkError ? 'connections' : settingsPane));
        settingsBody.innerHTML = '<p class="set-loading">Loading…</p>';
        try {
            const res = await apiFetch('/api/settings/baedu');
            settingsData = await res.json();
            renderSettingsPane();
        } catch {
            settingsBody.innerHTML = '<p class="msg msg-error">Could not load your settings. Close this and try again.</p>';
        }
    }

    function hideSettings() {
        hide(settingsModal);
        document.body.style.overflow = '';
        settingsLinkError = null;
        settingsLastFocus?.focus?.();
    }

    function syncSettingsIdentity() {
        const email = state.user?.email || '';
        const avatar = $('set-id-avatar');
        const emailEl = $('set-id-email');
        if (avatar) avatar.textContent = (email[0] || '?').toUpperCase();
        if (emailEl) { emailEl.textContent = email; emailEl.title = email; }
    }

    function selectSettingsPane(pane) {
        const changingPane = pane !== settingsPane;
        settingsPane = pane;
        settingsNav.forEach(btn => {
            const active = btn.dataset.pane === pane;
            btn.classList.toggle('is-active', active);
            active ? btn.setAttribute('aria-current', 'true') : btn.removeAttribute('aria-current');
        });
        if (settingsTitle) settingsTitle.textContent = PANE_TITLES[pane] || 'Settings';
        if (!settingsData) return;
        // transitions.dev card-resize: Account and Connections differ in
        // height, so the shell tweens between them on a user-driven switch.
        // The very first render (data just finished loading) isn't a pane
        // switch, so it renders straight in without the resize dance.
        changingPane
            ? resizeTransition(settingsShell, renderSettingsPane)
            : renderSettingsPane();
    }

    settingsNav.forEach(btn => btn.addEventListener('click', () => selectSettingsPane(btn.dataset.pane)));

    function renderSettingsPane() {
        if (!settingsBody || !settingsData) return;
        if (settingsPane === 'connections') return renderConnectionsPane(settingsData);
        if (settingsPane === 'accessibility') return renderAccessibilityPane();
        renderAccountPane(settingsData);
    }

    // Account – how you get in, and what you're on.
    function renderAccountPane(data) {
        const isPro = state.user?.plan === 'pro';
        const baeduState = !data.linked
            ? { pill: 'Not linked', on: false, sub: 'Link it in Connections to sign in with one click.' }
            : data.loginEnabled
                ? { pill: 'On', on: true, sub: 'You can sign in from the BlueAcorn Education button.' }
                : { pill: 'Off', on: false, sub: 'Linked for exports, but not for signing in.' };

        settingsBody.innerHTML = `
            <div class="set-view">
                <div class="set-group">
                    <p class="set-group-title">How you sign in</p>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">Email and password</div>
                            <div class="set-row-sub">${data.hasPassword
                                ? esc(state.user?.email || 'Your account email')
                                : data.canSetPassword
                                    ? 'Right now this account can only be reached through your school. Add a password and it stays yours.'
                                    : 'No password set on this account.'}</div>
                        </div>
                        ${data.canSetPassword
                            ? '<button id="set-password-btn" class="set-linkbtn" type="button">Set a password</button>'
                            : data.hasPassword
                                ? `<div class="set-row-actions"><span class="set-pill is-on">On</span><button id="change-password-btn" class="set-linkbtn" type="button">Change password</button></div>`
                                : '<span class="set-pill">Not set</span>'}
                    </div>
                    <div id="set-password-slot"></div>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">BlueAcorn Education</div>
                            <div class="set-row-sub">${baeduState.sub}</div>
                        </div>
                        <span class="set-pill ${baeduState.on ? 'is-on' : ''}">${baeduState.pill}</span>
                    </div>
                </div>
                <div class="set-group">
                    <p class="set-group-title">Marketplace</p>
                    <div class="set-row set-row-stack">
                        <div class="set-row-main">
                            <div class="set-row-label">Your name</div>
                            <div class="set-row-sub">Shown against anything you publish to the Marketplace, and credited to you in the library of every teacher who uses it. Your email is never shown.</div>
                        </div>
                        <div class="set-name-row">
                            <input type="text" id="set-first-name" class="set-name-input" maxlength="40"
                                   autocomplete="given-name" aria-label="First name" placeholder="First name"
                                   value="${escAttr(data.firstName || '')}">
                            <input type="text" id="set-last-name" class="set-name-input" maxlength="40"
                                   autocomplete="family-name" aria-label="Last name" placeholder="Last name"
                                   value="${escAttr(data.lastName || '')}">
                            <button id="set-name-save" class="set-linkbtn" type="button">Save</button>
                        </div>
                        <p id="set-name-msg" class="set-name-msg" hidden></p>
                    </div>
                    <div class="set-row set-row-stack">
                        <div class="set-row-main">
                            <div class="set-row-label">Your teaching profile</div>
                            <div class="set-row-sub">Shown on your public Marketplace page. A teacher deciding whether to use your lesson is deciding whether to trust you, and this is what they have to go on. Both are optional and public.</div>
                        </div>
                        <div class="set-profile-fields">
                            <input type="text" id="set-teaches" class="set-name-input" maxlength="120"
                                   aria-label="What you teach" placeholder="e.g. Head of Chemistry, 11-18"
                                   value="${escAttr(data.teaches || '')}">
                            <textarea id="set-bio" class="set-bio-input" maxlength="280" rows="3"
                                      aria-label="About you"
                                      placeholder="A line or two about you and how you teach.">${esc(data.bio || '')}</textarea>
                            <div class="set-profile-foot">
                                <span id="set-bio-count" class="set-bio-count"></span>
                                <button id="set-profile-save" class="set-linkbtn" type="button">Save</button>
                            </div>
                        </div>
                        <p id="set-profile-msg" class="set-name-msg" hidden></p>
                    </div>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">Email me when my lessons are used</div>
                            <div class="set-row-sub">A weekly note when other teachers take a copy of something you shared. Nothing is sent in a week where nobody did.</div>
                        </div>
                        <label class="toggle-switch t-toggle" data-on="${data.emailUsageDigest ? 'true' : 'false'}">
                            <input type="checkbox" id="set-usage-digest" ${data.emailUsageDigest ? 'checked' : ''}>
                            <span class="toggle-track"></span>
                            <span class="toggle-thumb t-toggle-thumb"></span>
                        </label>
                    </div>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">Email me tips and reminders</div>
                            <div class="set-row-sub">An occasional idea for what to make next. Emails about your account, like password resets, are always sent.</div>
                        </div>
                        <label class="toggle-switch t-toggle" data-on="${data.emailTips ? 'true' : 'false'}">
                            <input type="checkbox" id="set-email-tips" ${data.emailTips ? 'checked' : ''}>
                            <span class="toggle-track"></span>
                            <span class="toggle-thumb t-toggle-thumb"></span>
                        </label>
                    </div>
                    <div id="set-email-msg"></div>
                </div>
                <div class="set-group">
                    <p class="set-group-title">Plan</p>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">${isPro ? 'Pro' : 'Free'}</div>
                            <div class="set-row-sub">${isPro
                                ? 'Unlimited presentations, worksheets and quizzes.'
                                : 'A monthly allowance of presentations, worksheets and quizzes.'}</div>
                        </div>
                        <button id="set-plan-btn" class="set-linkbtn" type="button">${isPro ? 'Manage plan' : 'Upgrade'}</button>
                    </div>
                </div>
            </div>
        `;
        $('set-plan-btn')?.addEventListener('click', () => { hideSettings(); showBilling(); });
        wireDisplayNameRow(data);
        wireProfileRow(data);
        wireEmailSwitch('set-usage-digest', 'usageDigest', 'emailUsageDigest');
        wireEmailSwitch('set-email-tips', 'tips', 'emailTips');
        $('set-password-btn')?.addEventListener('click', () => openPasswordFlow('settings', data.passwordVerified));
        $('change-password-btn')?.addEventListener('click', openChangePasswordModal);
        if (settingsPasswordOpen && data.canSetPassword && data.passwordVerified) openPasswordModal('settings');
        settingsPasswordOpen = false;
    }

    // The same switch as the unsubscribe link in the email – both write one
    // column – so someone who unsubscribed by accident can turn it back on here.
    // `key` is the switch's name in the POST body, `dataKey` in the settings data.
    function wireEmailSwitch(id, key, dataKey) {
        const toggle = $(id);
        if (!toggle) return;
        toggle.addEventListener('change', async (e) => {
            const enabled = e.target.checked;
            // .is-init only after the first flip, so the overshoot keyframes
            // never play on load.
            const sw = e.target.closest('.t-toggle');
            if (sw) { sw.classList.add('is-init'); sw.dataset.on = String(enabled); }
            e.target.disabled = true;
            try {
                const res = await apiFetch('/api/settings/email', {
                    method: 'POST', body: JSON.stringify({ [key]: enabled }),
                });
                if (!res.ok) throw new Error();
                if (settingsData) settingsData[dataKey] = enabled;
            } catch {
                e.target.checked = !enabled;
                if (sw) sw.dataset.on = String(!enabled);
                const msgEl = $('set-email-msg');
                if (msgEl) msgEl.innerHTML = '<p class="msg msg-error" style="margin-top:10px;">Could not change this setting. Try again.</p>';
            } finally {
                e.target.disabled = false;
            }
        });
    }

    function wireDisplayNameRow(data) {
        const firstEl = $('set-first-name');
        const lastEl = $('set-last-name');
        const saveBtn = $('set-name-save');
        const msg = $('set-name-msg');
        if (!firstEl || !lastEl || !saveBtn) return;

        const say = (text, isError) => {
            if (!msg) return;
            msg.textContent = text;
            msg.classList.toggle('is-error', !!isError);
            msg.hidden = !text;
        };

        const save = async () => {
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving…';
            say('');
            try {
                const res = await apiFetch('/api/settings/name', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        firstName: firstEl.value.trim(),
                        lastName: lastEl.value.trim(),
                    }),
                });
                const body = await res.json();
                if (!res.ok) { say(body.error || 'Could not save that name', true); return; }
                // Keep the pane's copy in step, so reopening settings without a
                // refetch doesn't show the old name.
                data.firstName = body.firstName;
                data.lastName = body.lastName;
                say(`Your listings will show “${body.firstName} ${body.lastName}”.`);
            } catch {
                say('Network error', true);
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        };

        saveBtn.addEventListener('click', save);
        [firstEl, lastEl].forEach(el => el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
        }));
    }

    // The two public fields on an author's Marketplace page.
    //
    // Saved together and moderated server-side, so unlike the name row this can
    // come back with a refusal – the message area is the only place that is
    // reported, and it deliberately never echoes the checker's own wording.
    function wireProfileRow(data) {
        const teachesEl = $('set-teaches');
        const bioEl = $('set-bio');
        const saveBtn = $('set-profile-save');
        const msg = $('set-profile-msg');
        const count = $('set-bio-count');
        if (!teachesEl || !bioEl || !saveBtn) return;

        const say = (text, isError) => {
            if (!msg) return;
            msg.textContent = text;
            msg.classList.toggle('is-error', !!isError);
            msg.hidden = !text;
        };

        // Only once there is something to count, rather than greeting an empty
        // field with "0 / 280".
        const syncCount = () => {
            if (!count) return;
            const len = bioEl.value.length;
            count.textContent = len ? `${len} / 280` : '';
        };
        bioEl.addEventListener('input', syncCount);
        syncCount();

        saveBtn.addEventListener('click', async () => {
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving…';
            say('');
            try {
                const res = await apiFetch('/api/settings/profile', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ teaches: teachesEl.value, bio: bioEl.value }),
                });
                const body = await res.json();
                if (!res.ok) { say(body.error || 'Could not save that', true); return; }
                // Keep the pane's copy in step, so reopening settings without a
                // refetch does not show the old profile.
                data.teaches = body.teaches || '';
                data.bio = body.bio || '';
                say(body.teaches || body.bio
                    ? 'Saved. This is on your public Marketplace page.'
                    : 'Cleared. Your Marketplace page now shows just your name.');
            } catch {
                say('Network error', true);
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        });
    }

    // ── Choose a new password (emailed reset link) ────────────
    // Its own dialog rather than the set-a-password one: this runs signed out,
    // is authorised by the token in the URL, and signs them in on success.
    async function showResetPassword(token) {
        const modal = $('reset-modal');
        if (!modal) return;
        showAuth();
        show(modal);
        document.body.style.overflow = 'hidden';

        const form = $('reset-form');
        const dead = $('reset-dead');
        // The dead panel carries its own heading, so the card header would
        // only repeat a title that no longer describes the screen.
        // Being throttled is not the same as a dead link – saying so would
        // send someone off to request a replacement they don't need.
        const showDead = (opts = {}) => {
            form.hidden = true;
            $('reset-head').hidden = true;
            dead.hidden = false;
            if (opts.title) $('reset-dead-title').textContent = opts.title;
            if (opts.copy) $('reset-dead-copy').textContent = opts.copy;
            if (opts.hideAction) $('reset-restart').hidden = true;
        };
        const showThrottled = () => showDead({
            title: 'Too many attempts',
            copy: 'Wait a few minutes, then open your link again.',
            hideAction: true,
        });

        $('reset-restart')?.addEventListener('click', () => {
            hide(modal);
            document.body.style.overflow = '';
            window.history.replaceState({}, '', '/');
            showAuthModal('forgot');
        });

        try {
            const res = await fetch(`/api/auth/reset-password?token=${encodeURIComponent(token)}`);
            if (res.status === 429) return showThrottled();
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.valid) return showDead();
            $('reset-email').textContent = data.email || 'your account';
        } catch {
            return showDead();
        }
        $('reset-input')?.focus();

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const msg = $('reset-msg');
            const btn = $('reset-save');
            const fail = (m) => { msg.innerHTML = `<p class="msg msg-error">${esc(m)}</p>`; };
            const password = $('reset-input').value;
            const confirm = $('reset-confirm').value;

            if (password.length < 8) return fail('Password must be at least 8 characters.');
            if (password !== confirm) return fail('Those two passwords don’t match.');

            msg.innerHTML = '';
            btn.disabled = true;
            btn.textContent = 'Saving…';
            try {
                const res = await fetch('/api/auth/reset-password', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, password }),
                });
                const data = await res.json().catch(() => ({}));
                if (res.status === 429) {
                    throw new Error('Too many attempts. Wait a few minutes and try again – your link is still good.');
                }
                if (!res.ok) throw new Error(data.error || 'Could not set your password.');
                // The token was single-use and is now spent, so drop it from
                // the URL before doing anything else.
                window.history.replaceState({}, '', '/');
                hide(modal);
                document.body.style.overflow = '';
                state.user = data;
                syncAdminBtn();
                await loadSignedInScripts();
                showPresList();
            } catch (err) {
                fail(err.message || 'Could not set your password.');
                btn.disabled = false;
                btn.textContent = 'Save and sign in';
            }
        });
    }

    // ── Set a password (school accounts claiming themselves) ──
    // Two entry points – the billing panel and Settings – share one dialog.
    // `origin` decides only where the user is put back afterwards.
    let pwWired = false;
    let pwOrigin = 'settings';

    // The server won't accept a password without a fresh BA Ed re-authentication,
    // so send them through that first unless it's already been done.
    function openPasswordFlow(origin, verified) {
        if (verified) return openPasswordModal(origin);
        window.location.href = '/api/settings/password/verify'
            + (origin === 'billing' ? '?from=billing' : '');
    }

    // mode 'set' – a school account taking its first password.
    // mode 'change' – recovering one they'd forgotten, proven via BA Ed.
    function openPasswordModal(origin, mode = 'set') {
        const modal = $('password-modal');
        if (!modal) return;
        pwOrigin = origin || 'settings';

        if (!pwWired) {
            pwWired = true;
            modal.addEventListener('click', (e) => {
                if (e.target.closest('[data-pw-close]')) closePasswordModal();
            });
            $('pw-form')?.addEventListener('submit', submitPassword);
        }

        const changing = mode === 'change';
        const email = state.user?.email || 'your email';
        $('pw-title').textContent = changing ? 'Choose a new password' : 'Set a password';
        $('pw-lede').innerHTML = changing
            ? `You&rsquo;ve confirmed it&rsquo;s you with BlueAcorn Education. Pick a new password for <strong>${esc(email)}</strong>.`
            : `You&rsquo;ve confirmed it&rsquo;s you. Pick a password and you&rsquo;ll be able to sign in as <strong>${esc(email)}</strong> whatever happens to your school account.`;
        $('pw-form')?.reset();
        setPwMessage('');
        const saveBtn = $('pw-save');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = changing ? 'Save password' : 'Set password'; }

        show(modal);
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', pwKeydown);
        $('pw-input')?.focus();
    }

    function closePasswordModal() {
        const modal = $('password-modal');
        if (!isShown(modal)) return;
        hide(modal);
        document.removeEventListener('keydown', pwKeydown);
        // Settings is itself a modal – only release the page scroll if the
        // password dialog was the last thing open.
        if (!isShown($('settings-modal'))) document.body.style.overflow = '';
    }

    function pwKeydown(e) {
        if (e.key === 'Escape') closePasswordModal();
    }

    function setPwMessage(text) {
        const el = $('pw-msg');
        if (el) el.innerHTML = text ? `<p class="msg msg-error">${esc(text)}</p>` : '';
    }

    async function submitPassword(e) {
        e.preventDefault();
        const saveBtn = $('pw-save');
        const password = $('pw-input')?.value || '';
        const confirm = $('pw-confirm')?.value || '';

        if (password.length < 8) return setPwMessage('Password must be at least 8 characters.');
        if (password !== confirm) return setPwMessage('Those two passwords don’t match.');

        setPwMessage('');
        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
        try {
            const res = await apiFetch('/api/settings/password', {
                method: 'POST', body: JSON.stringify({ password }),
            });
            const out = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (out.needsVerify) {
                    // Verification aged out while the dialog sat open.
                    closePasswordModal();
                    openPasswordFlow(pwOrigin, false);
                    return;
                }
                throw new Error(out.error || 'Could not set your password.');
            }
            closePasswordModal();
            // The account may no longer be school-only, so the surface they
            // came from re-reads its state: billing swaps the panel for the
            // real upgrade options, Settings flips the sign-in rows. A password
            // recovery has no such surface – they're already in the app.
            if (pwOrigin === 'billing') await showBilling();
            else if (pwOrigin === 'settings') await showSettings(null, 'account');
        } catch (err) {
            setPwMessage(err.message || 'Could not set your password.');
            saveBtn.disabled = false;
            saveBtn.textContent = 'Set password';
        }
    }

    // ── Change password (signed-in, has an existing password) ──
    let cpWired = false;

    function openChangePasswordModal() {
        const modal = $('change-password-modal');
        if (!modal) return;
        if (!cpWired) {
            cpWired = true;
            modal.addEventListener('click', (e) => {
                if (e.target.closest('[data-cp-close]')) closeChangePasswordModal();
            });
            $('cp-form')?.addEventListener('submit', submitChangePassword);
        }
        $('cp-form')?.reset();
        setCpMessage('');
        const saveBtn = $('cp-save');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Change password'; }
        show(modal);
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', cpKeydown);
        $('cp-current')?.focus();
    }

    function closeChangePasswordModal() {
        const modal = $('change-password-modal');
        if (!isShown(modal)) return;
        hide(modal);
        document.removeEventListener('keydown', cpKeydown);
        if (!isShown($('settings-modal'))) document.body.style.overflow = '';
    }

    function cpKeydown(e) {
        if (e.key === 'Escape') closeChangePasswordModal();
    }

    function setCpMessage(text) {
        const el = $('cp-msg');
        if (el) el.innerHTML = text ? `<p class="msg msg-error">${esc(text)}</p>` : '';
    }

    async function submitChangePassword(e) {
        e.preventDefault();
        const saveBtn = $('cp-save');
        const current = $('cp-current')?.value || '';
        const password = $('cp-new')?.value || '';
        const confirm = $('cp-confirm')?.value || '';

        if (password.length < 8) return setCpMessage('Password must be at least 8 characters.');
        if (password !== confirm) return setCpMessage('Passwords don’t match.');

        setCpMessage('');
        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
        try {
            const res = await fetch('/api/settings/password', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentPassword: current, newPassword: password }),
            });
            const out = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(out.error || 'Could not change your password.');
            closeChangePasswordModal();
            if (isShown($('settings-modal'))) await showSettings(null, 'account');
        } catch (err) {
            setCpMessage(err.message || 'Could not change your password.');
            saveBtn.disabled = false;
            saveBtn.textContent = 'Change password';
        }
    }

    // Accessibility – the defaults a teacher sets once instead of re-picking
    // them from the export menu on every download.
    //
    // Everything here saves on change rather than behind a Save button. These
    // are preferences, not a form: the effect of each one is visible the
    // moment it lands, and a pending unsaved state is only a way to lose it.
    const TEXT_SCALE_STEPS = [
        { value: 1,    label: 'Normal' },
        { value: 1.1,  label: 'Large (110%)' },
        { value: 1.25, label: 'Larger (125%)' },
        { value: 1.5,  label: 'Largest (150%)' },
    ];

    function renderAccessibilityPane() {
        const a11y = state.prefs?.a11y || {};
        const layoutOpts = (window.A11yProfiles?.TEXT_LAYOUTS || [])
            .map(l => `<option value="${escAttr(l.id || '')}"${(a11y.layout || '') === (l.id || '') ? ' selected' : ''}>${esc(l.label)}</option>`)
            .join('');
        const tintOpts = (window.A11yProfiles?.TINTS || [])
            .map(t => `<option value="${escAttr(t.id || '')}"${(a11y.tint || '') === (t.id || '') ? ' selected' : ''}>${esc(t.label)}</option>`)
            .join('');
        const scaleOpts = TEXT_SCALE_STEPS
            .map(s => `<option value="${s.value}"${Number(a11y.textScale) === s.value ? ' selected' : ''}>${esc(s.label)}</option>`)
            .join('');

        settingsBody.innerHTML = `
            <div class="set-view">
                <div class="set-group">
                    <p class="set-group-title">Printed worksheets and quizzes</p>
                    <div class="set-row set-row-stack">
                        <div class="set-row-main">
                            <div class="set-row-label">Default layout</div>
                            <div class="set-row-sub">How every worksheet and quiz you print or download is set out, unless you change it for one document in the export menu.</div>
                        </div>
                        <select id="set-a11y-layout" class="set-select" aria-describedby="set-a11y-layout-note">${layoutOpts}</select>
                        <p id="set-a11y-layout-note" class="set-row-sub set-a11y-note"></p>
                    </div>
                    <div class="set-row set-row-stack">
                        <div class="set-row-main">
                            <div class="set-row-label">Default paper colour</div>
                            <div class="set-row-sub">A pale background behind the text, printed in. The equivalent of the coloured overlay some pupils are given, without needing the overlay.</div>
                        </div>
                        <select id="set-a11y-tint" class="set-select">${tintOpts}</select>
                    </div>
                    <p id="set-a11y-print-msg" class="set-name-msg" hidden></p>
                </div>
                <div class="set-group">
                    <p class="set-group-title">This app, on your screen</p>
                    <div class="set-row set-row-stack">
                        <div class="set-row-main">
                            <div class="set-row-label">Text size</div>
                            <div class="set-row-sub">Scales the text throughout BA Productivity. Your browser's own zoom still works on top of this.</div>
                        </div>
                        <select id="set-a11y-scale" class="set-select">${scaleOpts}</select>
                    </div>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">Reduce motion</div>
                            <div class="set-row-sub">Turns off the sliding and fading. Already on automatically if your computer is set to reduce motion — this is for a shared machine whose settings you cannot change.</div>
                        </div>
                        <label class="toggle-switch t-toggle" data-on="${a11y.reduceMotion ? 'true' : 'false'}">
                            <input type="checkbox" id="set-a11y-motion" ${a11y.reduceMotion ? 'checked' : ''}>
                            <span class="toggle-track"></span>
                            <span class="toggle-thumb t-toggle-thumb"></span>
                        </label>
                    </div>
                    <p id="set-a11y-screen-msg" class="set-name-msg" hidden></p>
                </div>
                <div class="set-group">
                    <p class="set-group-title">Your pupils</p>
                    <div class="set-row">
                        <div class="set-row-main">
                            <div class="set-row-label">Live quizzes</div>
                            <div class="set-row-sub">Pupils get their own text and colour controls on the quiz page, and their choice is remembered on that device. Nothing for you to set up, and no account for them to make.</div>
                        </div>
                        <a class="set-linkbtn" href="/accessibility" target="_blank" rel="noopener">What's supported</a>
                    </div>
                </div>
            </div>
        `;
        wireAccessibilityPane();
    }

    function wireAccessibilityPane() {
        const layoutEl = $('set-a11y-layout');
        const tintEl   = $('set-a11y-tint');
        const scaleEl  = $('set-a11y-scale');
        const motionEl = $('set-a11y-motion');
        const noteEl   = $('set-a11y-layout-note');

        const syncNote = () => {
            if (!noteEl) return;
            const hit = (window.A11yProfiles?.TEXT_LAYOUTS || [])
                .find(l => (l.id || '') === (layoutEl?.value || ''));
            noteEl.textContent = hit ? hit.blurb : '';
        };
        syncNote();

        // Each control owns the message line in its own group, so a failure
        // says which setting did not save rather than "something went wrong"
        // somewhere on the pane.
        const say = (id, text, isError) => {
            const msg = $(id);
            if (!msg) return;
            msg.textContent = text;
            msg.classList.toggle('is-error', !!isError);
            msg.hidden = !text;
        };

        // The control is disabled while the write is in flight and reverts to
        // the stored value if it fails, so what is on screen is never a
        // setting that is not actually saved.
        const commit = async (el, msgId, patch, revert) => {
            el.disabled = true;
            say(msgId, '');
            try {
                await savePrefs(patch);
                say(msgId, 'Saved');
                setTimeout(() => say(msgId, ''), 1600);
            } catch {
                revert();
                say(msgId, 'Could not save that. Try again.', true);
            } finally {
                el.disabled = false;
            }
        };

        layoutEl?.addEventListener('change', () => {
            syncNote();
            commit(layoutEl, 'set-a11y-print-msg',
                { a11y: { layout: layoutEl.value || null } },
                () => { layoutEl.value = state.prefs?.a11y?.layout || ''; syncNote(); });
        });

        tintEl?.addEventListener('change', () => {
            commit(tintEl, 'set-a11y-print-msg',
                { a11y: { tint: tintEl.value || null } },
                () => { tintEl.value = state.prefs?.a11y?.tint || ''; });
        });

        scaleEl?.addEventListener('change', () => {
            commit(scaleEl, 'set-a11y-screen-msg',
                { a11y: { textScale: Number(scaleEl.value) } },
                () => { scaleEl.value = String(state.prefs?.a11y?.textScale ?? 1); });
        });

        motionEl?.addEventListener('change', () => {
            const on = motionEl.checked;
            const sw = motionEl.closest('.t-toggle');
            // .is-init only after the first flip, so the overshoot keyframes
            // never play on load – same as the usage-digest toggle.
            if (sw) { sw.classList.add('is-init'); sw.dataset.on = String(on); }
            commit(motionEl, 'set-a11y-screen-msg',
                { a11y: { reduceMotion: on } },
                () => {
                    motionEl.checked = !on;
                    if (sw) sw.dataset.on = String(!on);
                });
        });
    }

    // Connections – the bridge between the two BlueAcorn apps.
    function renderConnectionsPane(data) {
        const bridgeState = !data.linked ? 'unlinked' : data.needsReauth ? 'attention' : 'linked';
        const seal = bridgeState === 'attention' ? '!' : '✓';
        const status = {
            unlinked:  'Not connected',
            attention: 'Connected, needs permission',
            linked:    `Connected as ${state.user?.email || 'your account'}`,
        }[bridgeState];

        const alert = settingsLinkError
            ? `<div class="set-alert">${esc(baeduErrorMessage(settingsLinkError))}</div>`
            : bridgeState === 'attention'
                ? '<div class="set-alert">BlueAcorn Education needs a few more permissions before it can receive your presentations, worksheets and quizzes.</div>'
                : '';

        const bridge = `
            <div class="set-bridge" data-state="${bridgeState}">
                <div class="set-bridge-app">
                    <div class="set-bridge-tile is-prod">BA</div>
                    <div class="set-bridge-name">Productivity</div>
                </div>
                <div class="set-wire">
                    <span class="set-wire-live"></span>
                    <span class="set-wire-seal">${seal}</span>
                </div>
                <div class="set-bridge-app">
                    <div class="set-bridge-tile is-edu">BA</div>
                    <div class="set-bridge-name">Education</div>
                </div>
            </div>
            <p class="set-bridge-status">${esc(status)}</p>
        `;

        const unlinkBtn = `<button id="settings-unlink-btn" class="set-quiet-btn" ${data.hasPassword
            ? ''
            : 'disabled title="Set a password first, or unlinking will lock you out of this account."'}>Unlink BlueAcorn Education</button>`;

        if (!data.linked) {
            settingsBody.innerHTML = `
                <div class="set-view">
                    ${alert}
                    ${bridge}
                    <p class="set-note">Link the two and you can sign in with BlueAcorn Education, and send lessons straight across without reconnecting each time.</p>
                    <button id="settings-link-btn" class="btn btn-baedu" style="width:100%;">Connect BlueAcorn Education</button>
                </div>
            `;
            $('settings-link-btn')?.addEventListener('click', () => {
                window.location.href = '/api/settings/baedu/link';
            });
            return;
        }

        if (data.needsReauth) {
            settingsBody.innerHTML = `
                <div class="set-view">
                    ${alert}
                    ${bridge}
                    <button id="settings-reconnect-btn" class="btn btn-baedu" style="width:100%;">Grant access</button>
                    <div id="settings-msg"></div>
                    ${unlinkBtn}
                </div>
            `;
            $('settings-reconnect-btn')?.addEventListener('click', () => {
                window.location.href = '/api/settings/baedu/link';
            });
            $('settings-unlink-btn')?.addEventListener('click', doUnlinkBaedu);
            return;
        }

        settingsBody.innerHTML = `
            <div class="set-view">
                ${alert}
                ${bridge}
                <div class="set-row">
                    <div class="set-row-main">
                        <div class="set-row-label">Sign in with BlueAcorn Education</div>
                        <div class="set-row-sub">${data.hasPassword
                            ? 'Turn this off to require your email and password.'
                            : 'Set a password before turning this off, or you’ll be locked out.'}</div>
                    </div>
                    <label class="toggle-switch t-toggle" data-on="${data.loginEnabled ? 'true' : 'false'}">
                        <input type="checkbox" id="settings-login-toggle" ${data.loginEnabled ? 'checked' : ''} ${data.hasPassword ? '' : 'disabled'}>
                        <span class="toggle-track"></span>
                        <span class="toggle-thumb t-toggle-thumb"></span>
                    </label>
                </div>
                <div id="settings-msg"></div>
                ${unlinkBtn}
            </div>
        `;
        $('settings-login-toggle')?.addEventListener('change', async (e) => {
            const enabled = e.target.checked;
            // .is-init only after the first flip, so the overshoot keyframes
            // never play on load.
            const sw = e.target.closest('.t-toggle');
            if (sw) { sw.classList.add('is-init'); sw.dataset.on = String(enabled); }
            e.target.disabled = true;
            try {
                const res = await apiFetch('/api/settings/baedu/login-enabled', {
                    method: 'POST', body: JSON.stringify({ enabled }),
                });
                if (!res.ok) throw new Error();
                if (settingsData) settingsData.loginEnabled = enabled;
            } catch {
                e.target.checked = !enabled;
                const msgEl = $('settings-msg');
                if (msgEl) msgEl.innerHTML = '<p class="msg msg-error" style="margin-top:10px;">Could not change this setting. Try again.</p>';
            } finally {
                e.target.disabled = false;
            }
        });
        $('settings-unlink-btn')?.addEventListener('click', doUnlinkBaedu);
    }

    async function doUnlinkBaedu() {
        const ok = await showConfirm({
            title: 'Unlink BlueAcorn Education?',
            message: 'You will no longer be able to sign in with BlueAcorn Education, and any export flows will need to be reconnected.',
            confirmLabel: 'Unlink',
            danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch('/api/settings/baedu/unlink', { method: 'POST' });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                const msgEl = $('settings-msg');
                if (msgEl) msgEl.innerHTML = `<p class="msg msg-error" style="margin-top:10px;">${esc(data.error || 'Could not unlink account.')}</p>`;
                return;
            }
        } catch {
            const msgEl = $('settings-msg');
            if (msgEl) msgEl.innerHTML = '<p class="msg msg-error" style="margin-top:10px;">Could not unlink account. Please try again.</p>';
            return;
        }
        showSettings();
    }

    btnSettings?.addEventListener('click', () => { hide(userMenu); showSettings(); });
    $('settings-close')?.addEventListener('click', hideSettings);
    $('set-signout')?.addEventListener('click', () => { hideSettings(); btnLogout?.click(); });
    settingsModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-settings-close]')) hideSettings();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !isShown(settingsModal)) return;
        e.preventDefault();
        hideSettings();
    });
    // Keep tabbing inside the dialog while it is open.
    settingsModal?.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const focusable = settingsModal.querySelectorAll(
            'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    async function startCheckout(interval) {
        try {
            const res = await apiFetch('/api/billing/checkout', {
                method: 'POST', body: JSON.stringify({ interval }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Checkout failed');
            window.ttq?.track('InitiateCheckout', {
                contents: [{ content_id: `pro-${interval}`, content_type: 'product', content_name: 'Pro' }],
            });
            window.location.href = data.url;
        } catch (err) {
            setError(billingError, err.message || 'Could not start checkout');
        }
    }

    async function openPortal() {
        try {
            const res = await apiFetch('/api/billing/portal', { method: 'POST' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Portal unavailable');
            window.location.href = data.url;
        } catch (err) {
            setError(billingError, err.message || 'Could not open billing portal');
        }
    }

    // "2026-07" is the key the quota middleware counts against – never show it raw.
    function formatPeriod(period) {
        const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
        if (!m) return String(period || '');
        const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
        return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }

    /**
     * One section of the billing page.
     *
     * The page used to be a single flat run of near-identical bars under tiny
     * uppercase headings: a monthly allowance that resets, a standing storage
     * total that does not, and money being earned, all drawn the same way and
     * separated by almost nothing. The heading here is a real heading, and the
     * meta on the right carries the one fact that section is actually about – so
     * the three read as three different things before any bar is looked at.
     */
    function billSection(title, meta, body) {
        return `
            <section class="bill-sec">
                <div class="bill-sec-head">
                    <h2 class="bill-sec-title">${esc(title)}</h2>
                    ${meta ? `<span class="bill-sec-meta">${meta}</span>` : ''}
                </div>
                ${body}
            </section>`;
    }

    // One allowance. Sits in a grid rather than spanning the page, so the count
    // is beside its label instead of a screen-width apart, and five of them take
    // three rows instead of five.
    //
    // The note says only what is wrong with THIS allowance – when everything
    // resets is stated once, in the section header, rather than repeated on
    // every warning row as it used to be. `fullNote` defaults to the personal
    // Pro plan's monthly framing; bucket bars pass their own (an empty pool is
    // topped up by an admin, not waited out).
    function quotaItem(kind, used, limit, fullNote = 'All used for this month') {
        const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : (used > 0 ? 100 : 0);
        const cls = limit == null ? '' : pct >= 100 ? 'full' : pct >= 80 ? 'warn' : '';
        const count = limit == null
            ? `${used.toLocaleString()} <span class="usage-sep">&middot;</span> Unlimited`
            : `${used.toLocaleString()} <span class="usage-sep">/</span> ${limit.toLocaleString()}`;
        const note = cls === 'full'
            ? `<p class="usage-note usage-note-alert">${esc(fullNote)}</p>`
            : cls === 'warn'
                ? '<p class="usage-note">Nearly out</p>'
                : '';
        return `
            <div class="quota-item">
                <div class="usage-row-top">
                    <span class="usage-label">${esc(KIND_LABELS[kind] || kind)}</span>
                    <span class="usage-count">${count}</span>
                </div>
                <div class="usage-track"><div class="usage-fill ${cls}" style="width:${limit == null ? 100 : pct}%"></div></div>
                ${note}
            </div>`;
    }

    /**
     * The organisation bucket: one shared pool, with a ceiling on the priciest
     * kinds *inside* it.
     *
     * The nesting is the whole point, and it is doing the explaining. Drawn
     * flat – the total and the capped kinds as three equal bars in one grid –
     * it reads as three separate allowances, so a member works out they have
     * 20 generations AND 5 presentations AND 5 edits, which is not what they
     * have at all. The caps come out of the total above them.
     *
     * That is why the copy here is only a label. An earlier version spelled
     * the relationship out in a sentence ("counted inside that total, not on
     * top of it…") because the flat layout gave the reader no other clue; a
     * sentence that argues against a misreading has to plant the misreading
     * first. With the total full width and stated first, and the limits
     * indented beneath it, "Limits within the bucket" is the whole story.
     *
     * Shared by the billing page and the organisation page: the same thing,
     * described the same way, from the same numbers.
     */
    function bucketPanel(bucket, orgName) {
        const capped = (bucket.kinds || []).filter((k) => k.cap != null);
        return `
            <div class="bucket-block">
                <div class="bucket-total">
                    ${quotaItem('bucket', bucket.used, bucket.size, 'Bucket is empty – ask an admin to top it up')}
                </div>
                ${capped.length ? `
                <div class="bucket-subs">
                    <p class="bucket-subs-head">Limits within the bucket</p>
                    <div class="quota-grid">
                        ${capped.map((k) => quotaItem(k.kind, k.used, k.cap, 'Limit reached for this cycle')).join('')}
                    </div>
                </div>` : ''}
                <p class="org-note bucket-foot">Everyone at ${esc(orgName)} draws from this same bucket${bucket.resetsOn ? `,
                   and it refills on its own every month &ndash; next on ${esc(shortDate(bucket.resetsOn))}` : ''}.</p>
            </div>`;
    }

    // Trims trailing zeroes so a whole cap reads "2 GB", not "2.0 GB".
    function trimNum(n, decimals) {
        return String(Number(n.toFixed(decimals)));
    }

    function formatBytes(bytes) {
        if (!bytes) return '0 MB';
        const mb = bytes / (1024 * 1024);
        if (mb < 1) return `${trimNum(Math.max(0.1, mb), 1)} MB`;
        // Round before the unit test, so 1023.6 MB rolls up to 1 GB rather than
        // rendering as "1024 MB" next to a limit already shown in GB.
        if (Math.round(mb) < 1024) return `${mb < 10 ? trimNum(mb, 1) : Math.round(mb)} MB`;
        const gb = mb / 1024;
        return `${gb < 10 ? trimNum(gb, 1) : Math.round(gb)} GB`;
    }

    // Image storage is a running total across every presentation, not a monthly
    // counter, so it gets its own row rather than sitting in the usage list.
    // What sharing on the Marketplace has earned. Shown on the billing page
    // because that is where money is, and because the number it is counting –
    // other teachers taking your lessons – is otherwise invisible to the person
    // who did the sharing.
    //
    // Hidden entirely for someone who has never had a lesson taken and has never
    // earned anything: an empty progress bar towards a reward is a worse pitch
    // than not mentioning it, and the dashboard band is already asking them to
    // publish.
    //
    // Credit already given is enough to show it, even with no current uses. A
    // listing being deleted takes its clone rows with it, so an author can hold
    // credit while their count reads zero – and the one thing worse than no
    // explanation of a discount is a discount with no explanation.
    //
    // Nothing here says "earn". This is not payment for work and must not read
    // as though it were: teachers are not selling us lessons, and a wage framing
    // would be both wrong about what happens and worse at causing it. The
    // product does the giving – "we take £1 off" – and the reason is that other
    // people found the work useful.
    function marketplaceCreditPanel(m) {
        if (!m || (!m.uses && !m.creditsEarned && !m.creditsPending)) return '';
        const perCredit = m.usesPerCredit;
        const pct = Math.min(100, Math.round((m.usesIntoCurrent / perCredit) * 100));
        const given = m.creditsEarned + m.creditsPending;
        // Stripe's balance is the honest "what comes off next time" – it has had
        // spent credit taken out of it. Falls back to the total given when there
        // is no customer yet, which is true and is all we know.
        const waiting = m.creditPence === null || m.creditPence === undefined
            ? null : m.creditPence / 100;
        // The next hundred, so the row reads as progress towards a real number
        // rather than as a percentage of nothing.
        const nextAt = (Math.floor(m.uses / perCredit) + 1) * perCredit;
        const tail = waiting !== null && waiting > 0
            ? ` <strong>&pound;${waiting.toFixed(2)} comes off your next bill.</strong>`
            : given
                ? ` That&rsquo;s &pound;${given} off so far.`
                : '';

        // Same shape as the meters above – the page has one – but green, because
        // this is the only bar here filling towards something you get rather
        // than a limit you run out of.
        return billSection('Thanks for sharing',
            given ? `&pound;${given} off so far` : '',
            `
            <div class="usage-row-top">
                <span class="usage-label">Taken by other teachers</span>
                <span class="usage-count">${m.uses.toLocaleString()} <span class="usage-sep">/</span> ${nextAt.toLocaleString()}</span>
            </div>
            <div class="usage-track"><div class="usage-fill earn" style="width:${pct}%"></div></div>
            <p class="usage-note">${m.usesToNext.toLocaleString()} more and we&rsquo;ll take &pound;1 off your next bill.${tail}</p>`);
    }

    function storageBar(storage) {
        const used = Number(storage.usedBytes) || 0;
        const limit = Number(storage.limitBytes) || 0;
        const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
        const cls = pct >= 100 ? 'full' : pct >= 80 ? 'warn' : '';
        // The bar colour alone can't carry the state – say in words what is
        // happening, especially once uploads have actually stopped working.
        const note = cls === 'full'
            ? 'You&rsquo;re out of space, so new uploads will fail. Deleting an image frees it again.'
            : cls === 'warn'
                ? 'You&rsquo;re close to the limit. Deleting an image frees the space again.'
                : 'Doesn&rsquo;t reset monthly. Deleting an image frees its space.';
        const body = `
            <div class="usage-track"><div class="usage-fill ${cls}" style="width:${pct}%"></div></div>
            <p class="usage-note${cls === 'full' ? ' usage-note-alert' : ''}">${note}</p>
            ${storageBreakdown(storage.presentations || [], used, cls)}`;
        // The headline number goes in the section header, so the bar underneath
        // needs no label row of its own – there is only one thing being measured.
        return billSection('Image storage',
            `${esc(formatBytes(used))} <span class="usage-sep">/</span> ${esc(formatBytes(limit))}`,
            body);
    }

    // Heaviest presentations first, so it's obvious where the space went.
    //
    // Folded away by default: it is four or more rows of detail under a bar that
    // most people never need to act on. It opens itself once the bar is amber or
    // red, which is exactly when "where did it all go" becomes the question.
    function storageBreakdown(list, totalUsed, state = '') {
        if (!list.length) return '';
        // Bars are a share of everything stored, not of the biggest row. Scaling
        // to the top row would peg it at 100% and make it look like the whole
        // quota even when the unlisted remainder is larger.
        const rows = list.map((p) => {
            const bytes = Number(p.usedBytes) || 0;
            const share = totalUsed ? (bytes / totalUsed) * 100 : 0;
            const width = share > 0 ? Math.max(2, Math.round(share)) : 0;
            const images = `${p.imageCount} image${p.imageCount === 1 ? '' : 's'}`;
            // Booklets store uploaded figures against the same limit, so they
            // appear in this list too and must link to themselves rather than
            // to a presentation that does not exist.
            const booklet = p.kind === 'booklet';
            const href = booklet
                ? `/booklets/${encodeURIComponent(p.id)}`
                : `/p/${encodeURIComponent(p.id)}`;
            const meta = booklet ? `${images} · Booklet` : images;
            return `
                <a class="storage-item" href="${href}"${booklet ? '' : ` data-pres-id="${escAttr(p.id)}"`}>
                    <span class="storage-item-main">
                        <span class="storage-item-title">${esc(p.title)}</span>
                        <span class="storage-item-meta">${esc(meta)}</span>
                    </span>
                    <span class="storage-item-bar" aria-hidden="true"><span class="storage-item-fill" style="width:${width}%"></span></span>
                    <span class="storage-item-size">${esc(formatBytes(bytes))}</span>
                </a>`;
        }).join('');
        const listedBytes = list.reduce((sum, p) => sum + (Number(p.usedBytes) || 0), 0);
        const rest = Math.max(0, totalUsed - listedBytes);
        const restWidth = totalUsed ? Math.max(2, Math.round((rest / totalUsed) * 100)) : 0;
        return `
            <details class="storage-details"${state ? ' open' : ''}>
                <summary class="storage-summary">Where it&rsquo;s going</summary>
                <div class="storage-list">${rows}</div>
                ${rest > 0 ? `
                <div class="storage-item storage-item-rest">
                    <span class="storage-item-main">
                        <span class="storage-item-title">Everything else</span>
                        <span class="storage-item-meta">Your other presentations and booklets</span>
                    </span>
                    <span class="storage-item-bar" aria-hidden="true"><span class="storage-item-fill" style="width:${restWidth}%"></span></span>
                    <span class="storage-item-size">${esc(formatBytes(rest))}</span>
                </div>` : ''}
            </details>`;
    }

    // List prices, so a referral discount can be shown against them. These are
    // the same figures hardcoded in the panel markup below and in the quota
    // modal; the Stripe prices themselves remain the source of truth.
    const BASE_PRICE_GBP = { month: 5.99, year: 59.88 };
    // Organisations are priced separately from personal Pro, per seat. The
    // source of truth is utils/orgPricing.js; this mirrors its standard price
    // for the self-serve quote. An org with a negotiated price gets its own
    // figures on org.price from /api/org instead.
    const ORG_PRICE_GBP = { month: 7.99, year: 79.90 };
    // Mirrors STRIPE_MAX_SEATS / ENQUIRY_EMAIL in utils/org.js – the server
    // refuses anything past the cap either way; this is so the form says so
    // before they've typed a card in.
    const STRIPE_MAX_SEATS = 20;
    const ENQUIRY_EMAIL = 'enquire@blueacorn.org';

    /**
     * What the first payment comes to once a referral coupon is applied.
     * Returns null when we can't work it out – a coupon in another currency
     * can't be subtracted from a sterling price, and quoting a number we aren't
     * sure of is worse than quoting none.
     */
    function discountedGbp(base, offer) {
        if (!offer) return null;
        if (offer.percentOff) return Math.round(base * (1 - offer.percentOff / 100) * 100) / 100;
        if (offer.amountOffCents && String(offer.currency).toLowerCase() === 'gbp') {
            return Math.max(0, Math.round((base - offer.amountOffCents / 100) * 100) / 100);
        }
        return null;
    }

    async function showBilling(sync = false) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(billingView, btnLogout, navCreateWrap, navStudentsBtn);
        if (window.location.pathname !== '/billing') history.pushState({ billing: true }, '', '/billing');
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        setError(billingError, '');
        if (billingBody) billingBody.innerHTML = '<p class="billing-loading" role="status">Loading&hellip;</p>';
        try {
            const res = await apiFetch(`/api/billing${sync ? '?sync=1' : ''}`);
            const b = await res.json();
            if (!res.ok) throw new Error(b.error || 'Failed to load billing info');
            window.fbq?.('track', 'ViewContent');
            window.ttq?.track('ViewContent');

            const isPro = b.plan === 'pro';
            const isEduAccount = !!b.isEduAccount;
            // Paid and active in Stripe, but a deferred-start grant that
            // hasn't reached its access date yet – billed like a Pro
            // customer, capped like a Free one until then.
            const proPending = !!b.proPending;
            const hasActiveSubscription = !!b.hasActiveSubscription;
            if (state.user) { state.user.plan = b.plan; syncAdminBtn(); }
            const renew = b.currentPeriodEnd
                ? new Date(b.currentPeriodEnd).toLocaleDateString('en-GB')
                : null;
            const accessDate = b.proAccessFrom
                ? new Date(b.proAccessFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                : null;
            const bars = Object.entries(b.limits)
                .map(([kind, limit]) => quotaItem(kind, b.usage[kind] || 0, limit))
                .join('');

            // Built from the server's real Pro limits so the pitch can never
            // drift from the bars underneath it, or quietly omit a whole quota.
            const proBlurb = (() => {
                const pl = b.proLimits;
                if (!pl) return 'Higher monthly limits on everything, plus priority generation.';
                // KIND_LABELS are column headings ("Marking", "Edits"); this
                // sentence needs countable nouns.
                const units = {
                    presentation: 'presentations', worksheet: 'worksheets', quiz: 'quizzes',
                    marking: 'marked answers', edit: 'AI edits', booklet: 'booklets',
                };
                const order = ['presentation', 'worksheet', 'quiz', 'booklet', 'marking', 'edit'];
                const parts = order
                    .filter((k) => pl[k])
                    .map((k) => `${pl[k].toLocaleString()} ${units[k] || (KIND_LABELS[k] || k).toLowerCase()}`);
                const last = parts.pop();
                return esc(`${parts.join(', ')} and ${last} every month, plus priority generation.`);
            })();

            // A referral discount, if they came in through a partner link that
            // carries one. It is applied by Stripe at checkout either way – this
            // just stops the panel quoting a price they will not be charged.
            const offer = hasActiveSubscription ? null : b.referralOffer;
            const offerMonth = discountedGbp(BASE_PRICE_GBP.month, offer);
            // A monthly-only offer (any coupon Stripe counts in months) is
            // refused by the yearly checkout, so the yearly tile has to quote
            // the full price – striking it through would promise a discount
            // the customer would then not be given.
            const offerYear = offer && offer.monthlyOnly ? null : discountedGbp(BASE_PRICE_GBP.year, offer);
            // A discount that runs out has to say so next to the price, or the
            // number above it is a promise we don't keep on the second invoice.
            const forever = offer && offer.duration === 'forever';
            // A free trial doesn't move either price – it defers the first
            // invoice – so the tiles keep quoting the real figure and say when
            // it is actually charged.
            const trialDays = (offer && offer.trialDays) || 0;
            const trialPhrase = !trialDays ? ''
                : trialDays === 7 ? 'the first week'
                : trialDays % 7 === 0 ? `the first ${trialDays / 7} weeks`
                : trialDays === 1 ? 'the first day'
                : `the first ${trialDays} days`;
            // In the price slot the length has to sit right next to the word
            // Free, in days rather than weeks: 'Yearly / Free' one line under a
            // '2 months free' tag reads like a free year, and the tiles are
            // three across with no room to spell it out twice.
            const trialShort = `${trialDays} day${trialDays === 1 ? '' : 's'}`;
            // A trial defers the price rather than changing it, so the note
            // below carries the real one – the price slot has already said what
            // today costs.
            const monthNote = trialDays
                ? `Then &pound;${(offerMonth ?? BASE_PRICE_GBP.month).toFixed(2)} / mo &ndash; cancel anytime`
                : offerMonth === null || forever
                    ? 'Flexible &ndash; cancel anytime'
                    : `Then &pound;${BASE_PRICE_GBP.month.toFixed(2)} &ndash; cancel anytime`;
            const yearNote = trialDays
                ? `Then &pound;${(offerYear ?? BASE_PRICE_GBP.year).toFixed(2)} billed once a year`
                : offerYear === null
                    ? '&pound;59.88 billed once a year'
                    : `&pound;${offerYear.toFixed(2)} for your first year${forever ? '' : `, then &pound;${BASE_PRICE_GBP.year.toFixed(2)}`}`;

            // What each tile puts where the price goes. A discount strikes the
            // old number through and shows the new one; a trial gets the same
            // treatment, because £0.00 today is exactly what it is – without
            // it, a free week renders as full price with a footnote and reads
            // as no offer at all.
            const priceLine = (base, discounted, suffix) => trialDays
                ? `<s>&pound;${base.toFixed(2)}</s> <span class="quota-plan-free">Free</span><small class="quota-plan-for">for ${trialShort}</small>`
                : `${discounted === null ? '' : `<s>&pound;${base.toFixed(2)}</s> `}&pound;${(discounted ?? base).toFixed(2)}<small>&nbsp;/ ${suffix}</small>`;

            const isTrialing = b.status === 'trialing';
            // The facts under the plan's name. Who is paying is in the name
            // itself now ("Pro, through …"), and "upgrade for higher limits"
            // went once the picker started sitting directly underneath it.
            const metaParts = [];
            if (proPending && accessDate) metaParts.push(`Pro access starts ${esc(accessDate)}`);
            if (isTrialing && accessDate) metaParts.push(`First charge ${esc(accessDate)}`);
            else if (hasActiveSubscription && b.billingInterval) metaParts.push(`Billed ${b.billingInterval === 'year' ? 'annually' : 'monthly'}`);
            if (isPro && renew) {
                metaParts.push(`${b.manualGrant ? 'Expires' : b.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${esc(renew)}`);
            }
            if (b.manualGrant && !hasActiveSubscription && !b.orgSeat) metaParts.push('Upgrade afterwards to keep Pro limits');

            // Someone paying for their own Pro subscription on top of a seat
            // that already covers them. Placed above the plan card: it is the
            // single most actionable thing on the whole page for them – there
            // is a charge to actually stop below.
            const duplicateSeatBanner = (b.orgSeat && b.orgSeat.duplicate) ? `
                <div class="referral-banner">
                    <span class="referral-banner-tag">Paying twice</span>
                    <span class="referral-banner-text">
                        <strong>You&rsquo;re also paying for Pro yourself</strong>
                        <small>Cancel your own subscription below and you lose nothing &ndash; your seat covers you.</small>
                    </span>
                </div>` : '';

            // Someone paying for their own Pro has Pro, whatever else covers
            // them; and an agency that buys no resources gives them none, so
            // "Pro, through" would promise what isn't there.
            const noResources = !!(b.orgSeat && b.orgBucket && b.orgBucket.size === 0);
            const planTitle = isEduAccount ? 'School account'
                : b.orgSeat && b.ownPro ? 'Pro plan'
                : noResources ? 'No resources included'
                : b.orgSeat ? `Pro, through ${esc(b.orgSeat.name)}`
                : proPending ? 'Pro &middot; reserved'
                : `${esc(b.planLabel)} plan`;
            const planChips = [
                isEduAccount ? '<span class="bill-chip">No password set</span>' : '',
                b.cancelAtPeriodEnd ? '<span class="bill-chip bill-chip-warn">Cancellation scheduled</span>' : '',
            ].join('');

            // A bucket member can buy Pro of their own once the bucket can't
            // cover them – used up with no top-up left, or never bought (an
            // agency can pay for no resources at all). It takes over from the
            // bucket then (middleware/quota.js, bucketOrPersonal). While the
            // bucket has room there's nothing to buy, so nothing is offered.
            const bucket = b.orgBucket;
            const bucketOut = !!(b.orgSeat && bucket && bucket.runOut && !b.ownPro && !hasActiveSubscription);
            const orgName = esc(b.orgSeat?.name || 'Your organisation');
            const bucketOutLine = bucket && bucket.size === 0
                ? `${orgName} doesn&rsquo;t pay for resources.`
                : `${orgName} has used this month&rsquo;s resources${bucket?.resetsOn ? ` &ndash; the bucket refills on ${esc(shortDate(bucket.resetsOn))}` : ''}.`;

            const yearlySwitch = hasActiveSubscription && !isTrialing && b.billingInterval === 'month' ? `
                <div class="switch-yearly-row">
                    <div class="switch-yearly-info">
                        <div class="switch-yearly-head">
                            <strong>Switch to yearly</strong>
                            <span class="switch-yearly-badge">2 months free</span>
                        </div>
                        <p>Unused time this month is credited automatically.</p>
                    </div>
                    <div class="switch-yearly-cta">
                        <span class="switch-yearly-price">&pound;59.88<small>&nbsp;/ year</small></span>
                        <button class="btn btn-primary btn-sm" id="btn-switch-yearly" type="button">Switch</button>
                    </div>
                </div>` : '';

            const upgradePanel = `
                <div class="upgrade-panel">
                    ${offer ? `
                    <div class="referral-banner">
                        <span class="referral-banner-tag">${esc(offer.headline)}</span>
                        <span class="referral-banner-text">
                            <strong>${offer.partner
                                ? `${esc(offer.partner)} got you ${trialDays ? 'a free trial' : 'a discount'}`
                                : trialDays ? 'Your first payment is on us' : 'You have a discount'}</strong>
                            <small>${trialDays
                                ? `Start today and nothing is charged for ${trialPhrase}. Cancel before it ends and you pay nothing.`
                                : `Applied automatically ${esc(offer.term)} on the ${offer.monthlyOnly ? 'monthly plan' : 'plan you pick'} &ndash; no code to enter.`}</small>
                        </span>
                    </div>` : ''}
                    ${bucketOut && !noResources ? `<p class="bill-out">${bucketOutLine}</p>` : ''}
                    <div class="upgrade-head">
                        <h2>${bucketOut ? 'Get Pro of your own' : 'Upgrade to Pro'}</h2>
                        <p>${proBlurb}${bucketOut && !noResources ? ' The shared bucket is still used first whenever it has some.' : ''}</p>
                    </div>
                    <div class="quota-plans billing-plan-select" role="radiogroup" aria-label="Billing period" id="billing-plan-group">
                        <button class="quota-plan" data-interval="month" role="radio" aria-checked="false" type="button">
                            <span class="quota-plan-name">Monthly</span>
                            <span class="quota-plan-price">${priceLine(BASE_PRICE_GBP.month, offerMonth, 'mo')}</span>
                            <span class="quota-plan-note">${monthNote}</span>
                        </button>
                        <button class="quota-plan is-selected" data-interval="year" role="radio" aria-checked="true" type="button">
                            <span class="quota-plan-tag">2 months free</span>
                            <span class="quota-plan-name">Yearly</span>
                            <span class="quota-plan-price">${priceLine(BASE_PRICE_GBP.year / 12, offerYear === null ? null : offerYear / 12, 'mo')}</span>
                            <span class="quota-plan-note">${yearNote}</span>
                        </button>
                    </div>
                    <button class="quota-cta" id="billing-plan-cta" type="button">${trialDays ? 'Start free trial' : 'Upgrade yearly'}</button>
                </div>`;

            // What sits under the plan's name, inside the same card. It used to
            // be a second box restating the plan ("Pro" in one, "Pro, through
            // Brightside" in the next); one card now says it once and then
            // carries whatever there is to do about it.
            const seatNote = b.orgSeat ? `
                <p class="bill-plan-note">${noResources
                    ? b.ownPro
                        ? `${orgName} doesn&rsquo;t pay for resources, so everything comes from your own Pro.`
                        : `${orgName} pays for your Tutoring work, not for resources.`
                    : bucket
                    ? b.ownPro
                        ? 'They pay for a shared bucket of generations, used first. Your own Pro takes over when it runs out.'
                        : 'They pay for a shared bucket of generations.'
                    : 'They pay for your seat.'} Everything you make stays yours and private &ndash; nobody else in the organisation can see it.</p>` : '';
            const planBody = b.orgSeat ? seatNote + (bucketOut ? upgradePanel : yearlySwitch)
                : isPro ? yearlySwitch
                : proPending ? `
                <p class="bill-plan-note">${isTrialing
                    ? `Nothing&rsquo;s been charged yet &ndash; your card is only billed on ${esc(accessDate || '1 September')}, then Pro switches on automatically.`
                    : `You&rsquo;re all paid up &ndash; ${b.billingInterval === 'year' ? 'billed annually' : 'billed monthly'} from today. Full Pro limits switch on automatically; there&rsquo;s nothing else to do.`}</p>`
                : isEduAccount ? ''
                : upgradePanel;

            // A school account has nothing to pay with until it has a password,
            // so its panel stands on its own under the plan card.
            const eduPanel = isEduAccount ? `
                <section class="edu-panel">
                    <p class="edu-eyebrow">Ways into this account</p>
                    <h2 class="edu-head">Right now, only your school</h2>
                    <p class="edu-copy">With no password of its own, there&rsquo;s nothing here to attach a card to.</p>
                    <div class="edu-keys">
                        <div class="edu-key">
                            <span class="edu-key-mark is-held"></span>
                            <span class="edu-key-text">
                                <span class="edu-key-label">BlueAcorn Education</span>
                                <span class="edu-key-sub">Managed by your school</span>
                            </span>
                            <span class="edu-key-pill">In use</span>
                        </div>
                        <button class="edu-key edu-key-open" id="btn-edu-set-password" type="button">
                            <span class="edu-key-mark is-open"></span>
                            <span class="edu-key-text">
                                <span class="edu-key-label">A password of your own</span>
                                <span class="edu-key-sub">Not set yet &ndash; takes about a minute</span>
                            </span>
                            <span class="edu-key-go">Set it</span>
                        </button>
                    </div>
                    <div class="edu-foot">
                        <div class="edu-foot-text">
                            <span class="edu-foot-line">Then Pro, whenever you want it</span>
                            <span class="edu-foot-sub">Everything already in the account stays exactly as it is.</span>
                        </div>
                        <div class="edu-price" title="&pound;4.99/mo when billed yearly">
                            <span class="edu-price-amt">
                                <span class="edu-price-strike">&pound;5.99</span>
                                <span class="edu-price-reveal">&pound;4.99</span>
                            </span>
                            <span class="edu-price-per">/mo</span>
                            <span class="edu-price-yearly-tag">billed yearly</span>
                        </div>
                    </div>
                </section>` : '';

            billingBody.innerHTML = `
                ${tutoringBillingNote()}
                ${duplicateSeatBanner}
                <section class="bill-plan">
                    <div class="bill-plan-top">
                        <div class="bill-plan-info">
                            <p class="bill-plan-eyebrow">Your plan</p>
                            <div class="bill-plan-line">
                                <h2 class="bill-plan-name${(isPro || proPending) && !(noResources && !b.ownPro) ? ' is-pro' : ''}">${planTitle}</h2>
                                ${planChips}
                            </div>
                            ${metaParts.length ? `<p class="bill-plan-meta">${metaParts.join(' &middot; ')}</p>` : ''}
                        </div>
                        ${hasActiveSubscription ? '<div class="billing-actions"></div>' : ''}
                    </div>
                    ${planBody.trim() ? `<div class="bill-plan-body">${planBody}</div>` : ''}
                </section>
                ${eduPanel}
                ${b.orgBucket && noResources ? ''
                    : b.orgBucket
                    ? billSection(
                        'Organisation bucket',
                        `${b.orgBucket.available.toLocaleString()} left${b.orgBucket.resetsOn ? ` · refills ${esc(shortDate(b.orgBucket.resetsOn))}` : ''}`,
                        bucketPanel(b.orgBucket, b.orgSeat?.name || 'your organisation')
                    )
                    : billSection(
                        'This month',
                        b.periodResetsOn ? `Resets in ${esc(formatPeriod(b.periodResetsOn))}` : esc(formatPeriod(b.period)),
                        `<div class="quota-grid">${bars}</div>`
                    )}
                ${b.orgBucket && b.ownPro
                    ? billSection(noResources ? 'This month' : 'Your own Pro',
                        noResources ? (b.periodResetsOn ? `Resets in ${esc(formatPeriod(b.periodResetsOn))}` : '') : 'Used once the bucket runs out',
                        `<div class="quota-grid">${bars}</div>`)
                    : ''}
                ${b.storage ? storageBar(b.storage) : ''}
                ${marketplaceCreditPanel(b.marketplace)}
                ${orgBuyPanel(b)}
                ${schoolsPanel(b)}`;

            // Real hrefs, so Cmd/Ctrl/middle-click open a new tab the way they
            // should – only take over the plain left click for the SPA route.
            billingBody.querySelectorAll('a.storage-item').forEach((item) => {
                item.addEventListener('click', (e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    openPresentation(item.dataset.presId);
                });
            });

            const actions = billingBody.querySelector('.billing-actions');
            if (hasActiveSubscription) {
                const manage = document.createElement('button');
                // Ghost renders as bare muted text – the only account action a
                // Pro user has here needs to look like a control.
                manage.className = 'btn btn-secondary';
                manage.textContent = 'Manage billing';
                manage.addEventListener('click', openPortal);
                actions.appendChild(manage);
                const switchBtn = billingBody.querySelector('#btn-switch-yearly');
                if (switchBtn) switchBtn.addEventListener('click', async () => {
                    const ok = await showConfirm({
                        title: 'Switch to yearly billing?',
                        message: 'You’ll be billed £59.88 / year – two months free. Unused time on your monthly plan is credited toward the first year.',
                        confirmLabel: 'Switch to yearly',
                        cancelLabel: 'Keep monthly'
                    });
                    if (!ok) return;
                    switchBtn.disabled = true;
                    switchBtn.textContent = 'Switching…';
                    try {
                        const res = await apiFetch('/api/billing/switch-yearly', { method: 'POST' });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.error || 'Failed to switch billing');

                        if (data.requires_action && data.mode === 'redirect') {
                            // Wallet payment method (e.g. Revolut Pay) – Stripe's
                            // hosted invoice page doesn't reliably support these
                            // (it can error on the return_url), so we drive the
                            // redirect ourselves and come back to return_url.
                            const stripe = await loadStripeJs(data.publishable_key);
                            const { error } = await stripe.confirmPayment({
                                clientSecret: data.client_secret,
                                confirmParams: { return_url: data.return_url },
                            });
                            // A successful redirect navigates away and never
                            // reaches here – only failures do.
                            if (error) {
                                await apiFetch('/api/billing/switch-yearly/cancel', { method: 'POST' }).catch(() => {});
                                throw new Error(error.message || 'Payment wasn’t completed – you’re still on monthly billing. No charge was made.');
                            }
                            return;
                        }
                        if (data.requires_action) {
                            // Bank wants 3DS verification – run the challenge here.
                            switchBtn.textContent = 'Verifying…';
                            let verified = false;
                            try {
                                const stripe = await loadStripeJs(data.publishable_key);
                                const result = await stripe.confirmCardPayment(data.client_secret);
                                verified = !!(result.paymentIntent && result.paymentIntent.status === 'succeeded');
                            } catch (_) { /* fall through – treated as not verified */ }

                            if (!verified) {
                                // User closed / failed the challenge – revert server-side.
                                await apiFetch('/api/billing/switch-yearly/cancel', { method: 'POST' }).catch(() => {});
                                throw new Error('Card verification wasn’t completed – you’re still on monthly billing. No charge was made.');
                            }
                            const fin = await apiFetch('/api/billing/switch-yearly/finalize', { method: 'POST' });
                            const finData = await fin.json();
                            if (!fin.ok) throw new Error(finData.error || 'Failed to switch billing');
                        }

                        await showBilling(true);
                    } catch (err) {
                        setError(billingError, err.message || 'Failed to switch billing');
                        switchBtn.disabled = false;
                        switchBtn.textContent = 'Switch to yearly';
                    }
                });
            } else if (isEduAccount) {
                billingBody.querySelector('#btn-edu-set-password')
                    ?.addEventListener('click', () => openPasswordFlow('billing', b.passwordVerified));
            } else {
                const planBtns = Array.from(billingBody.querySelectorAll('#billing-plan-group .quota-plan'));
                const planCta = billingBody.querySelector('#billing-plan-cta');
                // 'Upgrade' is the wrong verb when nothing is charged today.
                const PLAN_CTA_LABELS = trialDays
                    ? { month: 'Start free trial', year: 'Start free trial' }
                    : { month: 'Upgrade monthly', year: 'Upgrade yearly' };
                let planInterval = 'year';
                planBtns.forEach(btn => btn.addEventListener('click', () => {
                    planInterval = btn.dataset.interval;
                    planBtns.forEach(b => {
                        const selected = b === btn;
                        b.classList.toggle('is-selected', selected);
                        b.setAttribute('aria-checked', String(selected));
                    });
                    if (planCta) planCta.textContent = PLAN_CTA_LABELS[planInterval] || 'Upgrade to Pro';
                }));
                planCta?.addEventListener('click', () => startCheckout(planInterval));
            }
        } catch (err) {
            if (billingBody) billingBody.innerHTML = '';
            setError(billingError, err.message || 'Failed to load billing info');
        }
    }

    // ── Buying seats for a school or team ─────────────────────
    // The way in for a tutoring company or a head of department paying by
    // card. Deliberately at the bottom of the billing page rather than beside
    // the personal plan: almost everyone here is buying one seat for
    // themselves, and putting a seat count next to that only makes the simple
    // choice look complicated.

    /**
     * Where a school comes in: a free pilot for the teacher with no budget, and
     * a call for the buyer past what the seat form sells.
     *
     * These were two panels, amber and purple, each with an eyebrow, a heading,
     * a paragraph and its own buttons, stacked under the personal plan. Two
     * pitches at full volume at the foot of a settings page read as neither,
     * and made a page about one person's plan look like a sales page. One quiet
     * card, two rows, the same two asks.
     *
     * The pilot asks for a link forwarded rather than a bill, because the
     * person most likely to bring us a school is a classroom teacher with no
     * authority to sign anything. Card checkout stops at STRIPE_MAX_SEATS, and
     * past that the product is a shared pool on an invoice, so that row is a
     * signpost: the detail belongs in the reply to their email.
     *
     * Hidden for anyone already inside an organisation – they have brought
     * their school in, and this would be asking them to do it twice. A
     * tutoring agency counts, even one with no plan of its own yet.
     */
    function schoolsPanel(b) {
        if (b.orgSeat || b.orgBucket || b.isEduAccount || state.user?.org?.tutoring) return '';
        return `
            <section class="bill-schools">
                <h2 class="bill-schools-title">For your school</h2>
                <div class="bill-schools-row">
                    <div class="bill-schools-text">
                        <strong>Free pilot for a term</strong>
                        <span>Every teacher at your school, no card or purchase order.</span>
                    </div>
                    <button type="button" class="btn btn-secondary btn-sm" data-pilot-open="billing">Tell us about your school</button>
                </div>
                <div class="bill-schools-row">
                    <div class="bill-schools-text">
                        <strong>More than ${STRIPE_MAX_SEATS} teachers?</strong>
                        <span>One shared pool for everyone, invoiced on a PO.</span>
                    </div>
                    <div class="bill-schools-ctas">
                        <button type="button" class="btn btn-secondary btn-sm" data-cal-link="iceniean/quick-chat" data-cal-namespace="quick-chat" data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'>Book a call</button>
                        <a class="btn btn-ghost btn-sm" href="mailto:${ENQUIRY_EMAIL}?subject=Pricing%20for%20our%20school">Email us</a>
                    </div>
                </div>
            </section>`;
    }

    /**
     * For anyone in a tutoring agency: this page is their own Productivity
     * plan, and the agency's money is somewhere else.
     *
     * Without it, an agency admin looking for the plan they pay for, or the
     * invoices they send families, lands here from "Plan & billing" in the
     * account menu and finds a personal Pro upsell. A tutor looking for what
     * they're owed finds the same. Each is told where theirs is, by the names
     * the Tutoring sidebar uses, with nothing pointing at a page they can't open.
     */
    function tutoringBillingNote() {
        const org = state.user?.org;
        if (!org?.tutoring) return '';
        const admin = org.role === 'admin';
        const name = esc(org.name || 'your agency');
        const links = admin
            ? [['/tutoring/admin', 'Agency plan'], ['/tutoring/invoices', 'Invoices'], ['/tutoring/pay-runs', 'Tutor pay']]
            : [['/tutoring/pay', 'My pay']];
        return `
            <aside class="bill-tut">
                <span class="bill-tut-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></span>
                <div class="bill-tut-text">
                    <strong>${admin ? `Billing for ${name} is in Tutoring` : 'Your tutoring pay is in Tutoring'}</strong>
                    <span>${admin
                        ? 'This page only covers your own Productivity plan.'
                        : `This page only covers your own Productivity plan. Pay and statements from ${name} are under My pay.`}</span>
                </div>
                <div class="bill-tut-links">
                    ${links.map(([href, label]) => `<a class="btn btn-secondary btn-sm" href="${href}" data-tut-go="${href}">${label}</a>`).join('')}
                </div>
            </aside>`;
    }

    /** Self-serve seat buying is switched off; flip to false to bring the form back. */
    const ORG_BUY_HIDDEN = true;

    function orgBuyPanel(b) {
        // On someone's paid seat already: nothing here is theirs to buy.
        // Edu-only accounts can't hold a card at all.
        if (b.orgSeat || b.isEduAccount) return '';

        // Started buying seats and closed the tab. The form for finishing lives
        // on the organisation page, next to the rest of the state – two copies
        // of it, in two places, is how the two drift apart.
        if (b.orgAwaitingPayment) {
            return `
                <section class="org-buy-panel">
                    <p class="edu-eyebrow">One step left</p>
                    <h2 class="edu-head">${esc(b.orgAwaitingPayment.name)} isn&rsquo;t switched on yet</h2>
                    <p class="org-note">You started setting it up but nothing has been paid for, so there are no seats yet.
                       It takes a moment to finish.</p>
                    <button class="btn btn-primary" id="org-goto-setup">Finish setting up</button>
                </section>`;
        }

        // Hidden deliberately. The awaiting-payment branch above still shows,
        // so anyone who started buying seats can still finish.
        if (ORG_BUY_HIDDEN) return '';

        const initial = orgBuyQuote(5, 'year');
        return `
            <section class="org-buy-panel">
                <div class="org-buy-top">
                    <div>
                        <p class="edu-eyebrow">More than one of you?</p>
                        <h2 class="edu-head">Seats for your school or team</h2>
                    </div>
                    <span class="org-buy-badge">2 months free yearly</span>
                </div>
                <p class="edu-copy">One bill for everyone, and every seat is a full Pro account. Invite people
                   by email or paste in a staff list &ndash; what each of them makes stays private to them.</p>
                <ul class="org-buy-points">
                    <li>One invoice, however many people</li>
                    <li>Full Pro limits on every seat</li>
                    <li>Add or remove seats whenever you like</li>
                </ul>
                <form class="org-buy-form">
                    <div class="org-buy-fields">
                        <label>Organisation name
                            <input name="orgName" type="text" placeholder="Your organisation’s name" maxlength="200" required>
                        </label>
                        <label>Seats
                            <input name="seats" type="number" min="2" max="${STRIPE_MAX_SEATS}" value="5" required>
                        </label>
                        <label>Billing
                            <select name="interval">
                                <option value="year">Yearly &ndash; 2 months free</option>
                                <option value="month">Monthly</option>
                            </select>
                        </label>
                    </div>
                    <div class="org-buy-foot">
                        <div class="org-buy-total">
                            <span class="org-buy-total-amt">${initial.amount}</span>
                            <span class="org-buy-total-per">${initial.per}</span>
                            <span class="org-buy-total-note">${initial.note}</span>
                        </div>
                        <button type="submit" class="btn btn-primary">Continue to payment</button>
                    </div>
                </form>
                <p class="org-note">More than ${STRIPE_MAX_SEATS} people, or buying on a purchase order?
                   There&rsquo;s a better fit just below.</p>
                <div class="error org-buy-error" role="alert" hidden></div>
            </section>`;
    }

    /**
     * What a given seat count actually comes to.
     *
     * The panel used to quote a per-seat price and leave the buyer to multiply
     * it themselves, which is the one sum they care about and the one they
     * shouldn't have to do. Yearly is quoted as the annual figure with the
     * monthly equivalent underneath, because that is the number they'll compare
     * against the monthly option.
     */
    function orgBuyQuote(seats, interval) {
        const n = Math.max(Number(seats) || 0, 0);
        const yearly = interval === 'year';
        const total = n * (yearly ? ORG_PRICE_GBP.year : ORG_PRICE_GBP.month);
        // Past the card ceiling there is no price to quote: the answer is a
        // conversation and an invoice, so the total is replaced by the address
        // to write to rather than a number they can't act on. `overCap` is what
        // stops the form being submitted (see refreshOrgBuyQuote).
        if (n > STRIPE_MAX_SEATS) {
            return {
                overCap: true,
                amount: `${n} seats`,
                per: '',
                note: `That's past what we sell by card. Email <a href="mailto:${ENQUIRY_EMAIL}">${ENQUIRY_EMAIL}</a>
                       and we'll invoice your school or trust, and help you get everyone set up.`,
            };
        }
        return {
            overCap: false,
            amount: `£${total.toFixed(2)}`,
            per: yearly ? '/ year' : '/ month',
            note: n
                ? yearly
                    ? `${n} seat${n === 1 ? '' : 's'} &ndash; works out at £${(total / 12).toFixed(2)} a month`
                    : `${n} seat${n === 1 ? '' : 's'} at £${ORG_PRICE_GBP.month.toFixed(2)} each`
                : 'Choose how many seats you need',
        };
    }

    /** Repaints the quote in place as the seat count or interval changes. */
    function refreshOrgBuyQuote(form) {
        const quote = orgBuyQuote(form.elements.seats.value, form.elements.interval.value);
        // The button is disabled on both forms, but only the billing-page one
        // carries a total to repaint – the org setup form has no quote block.
        const submit = form.querySelector('button[type="submit"]');
        if (submit) {
            submit.disabled = quote.overCap;
            submit.title = quote.overCap ? `Card checkout tops out at ${STRIPE_MAX_SEATS} seats` : '';
        }
        const total = form.querySelector('.org-buy-total');
        if (!total) return;
        total.classList.toggle('org-buy-total-enquire', quote.overCap);
        total.querySelector('.org-buy-total-amt').textContent = quote.amount;
        total.querySelector('.org-buy-total-per').textContent = quote.per;
        total.querySelector('.org-buy-total-note').innerHTML = quote.note;
    }

    // Delegated for both events: typing in the number field fires `input`,
    // changing the select fires `change`, and the panel is rebuilt on every
    // render so a directly bound listener would need re-binding each time.
    // Bound on both hosts, because the same form appears on the billing page
    // and (to finish a half-done checkout) on the organisation page – the
    // seat ceiling has to disable the button in either place.
    ['input', 'change'].forEach((evt) => [billingBody, orgBody].forEach((host) => host?.addEventListener(evt, (e) => {
        const form = e.target.closest('.org-buy-form');
        if (form) refreshOrgBuyQuote(form);
    })));

    /**
     * Sends an .org-buy-form to checkout. Shared, because the same form appears
     * on the billing page (buying for the first time) and on the organisation
     * page (finishing a checkout that was closed part-way), and fields are read
     * by name rather than id so neither copy can collide with the other.
     */
    async function startOrgCheckout(form, errorEl) {
        const btn = form.querySelector('button[type="submit"]');
        if (btn?.disabled) return;
        setError(errorEl, '');
        btn.disabled = true;
        try {
            const res = await apiFetch('/api/org/checkout', {
                method: 'POST',
                body: JSON.stringify({
                    name: form.elements.orgName.value.trim(),
                    seats: Number(form.elements.seats.value),
                    interval: form.elements.interval.value,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Could not start checkout');
            window.ttq?.track('InitiateCheckout', {
                contents: [{
                    content_id: `org-${form.elements.interval.value}`, content_type: 'product', content_name: 'Organisation',
                }],
            });
            window.location.href = data.url;
        } catch (err) {
            btn.disabled = false;
            if (err.message !== 'Not authenticated') setError(errorEl, err.message || 'Could not start checkout');
        }
    }

    // Delegated on the billing body, which is rebuilt on every render.
    billingBody?.addEventListener('submit', (e) => {
        const form = e.target.closest('.org-buy-form');
        if (!form) return;
        e.preventDefault();
        startOrgCheckout(form, billingBody.querySelector('.org-buy-error'));
    });

    billingBody?.addEventListener('click', (e) => {
        if (e.target.closest('#org-goto-setup')) showOrg();
        // Real hrefs for a new tab; a plain click stays in the app.
        const tut = e.target.closest('a[data-tut-go]');
        if (tut && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) {
            e.preventDefault();
            showTutoringPage(tut.dataset.tutGo);
        }
    });

    // ── Your organisation ─────────────────────────────────────
    // Seats, and who is sitting in them. Everyone in the organisation can read
    // this; the controls only render for an admin, and the server refuses them
    // regardless of what the page decides to draw.

    /**
     * A tutoring agency on the agency plan has no organisation page: its
     * people, logo and plan are the tutoring area's Admin tab, and nothing
     * here applies to it (it doesn't buy seats). Whoever arrives is taken
     * there, or to the tutoring area's front page if they aren't an admin.
     */
    function leaveOrgForTutoring() {
        const path = state.user.org.role === 'admin' ? '/tutoring/admin' : '/tutoring';
        history.replaceState({}, '', path);
        showTutoringPage(path, false);
    }

    async function showOrg(sync = false) {
        if (!state.user?.org) return;
        if (state.user.org.agencyPlan) return leaveOrgForTutoring();
        // The panel's controls are found by id, so only one copy may exist.
        orgPanel = orgPagePanel;
        orgEmbedPanel.body.innerHTML = '';
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView, billingView, markingView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(orgView, btnLogout, navCreateWrap, navStudentsBtn);
        if (window.location.pathname !== '/org') history.pushState({ org: true }, '', '/org');
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        setError(orgError, '');
        if (orgBody) orgBody.innerHTML = '<p class="billing-loading" role="status">Loading&hellip;</p>';
        try {
            const res = await apiFetch(`/api/org${sync ? '?sync=1' : ''}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load your organisation');

            // A sign-in's reply doesn't say what kind of organisation this is;
            // this one always does. Remembered, so the menu stops offering it.
            if (data.agencyPlan) {
                state.user.org.agencyPlan = true;
                state.user.org.role = data.isOrgAdmin ? 'admin' : state.user.org.role;
                if (orgBody) orgBody.innerHTML = '';
                syncAdminBtn();
                return leaveOrgForTutoring();
            }

            // "Not switched on" is the one conclusion here worth double-checking
            // against Stripe before saying it out loud: the seat count comes
            // from a webhook, and a webhook that is late, lost or (in
            // development) not being listened for would have us tell someone who
            // has just paid that they haven't. One retry, then believe it.
            if (data.org.awaitingPayment && !sync) return showOrg(true);

            renderOrg(data);
        } catch (err) {
            if (orgBody) orgBody.innerHTML = '';
            if (err.message !== 'Not authenticated') setError(orgError, err.message || 'Failed to load your organisation');
        }
    }

    /**
     * The same panel inside the tutoring area's Admin tab (handed to
     * Tutoring.mount as its orgPanel hook). `host` is the tab's container; a
     * reload after a change leaves it out and redraws in place.
     */
    async function showOrgEmbedded(host) {
        orgPanel = orgEmbedPanel;
        if (orgBody) orgBody.innerHTML = '';
        if (host) host.replaceChildren(orgEmbed);
        setError(orgPanel.error, '');
        orgPanel.body.innerHTML = '<p class="billing-loading" role="status">Loading&hellip;</p>';
        try {
            const res = await apiFetch('/api/org');
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load your organisation');
            renderOrg(data);
        } catch (err) {
            orgPanel.body.innerHTML = '';
            if (err.message !== 'Not authenticated') setError(orgPanel.error, err.message || 'Failed to load your organisation');
        }
    }

    /** Redraw the panel wherever it is showing. */
    const reloadOrg = (sync) => (orgPanel.embedded ? showOrgEmbedded() : showOrg(sync));

    // Heading + body + optional action per reason orgBillingAlert can name.
    // Kept as data rather than branching HTML, so the one thing that varies
    // (the wording) is the only thing written down.
    // One line each. This is a strip across the top of every page, not a panel
    // – a paragraph in it wraps to three lines and stops reading as a notice.
    // The bold lead says what happened, the rest says what it costs or what to
    // do, and anything longer belongs on the organisation page.
    const ORG_ALERT_COPY = {
        revoked: {
            heading: 'Access switched off',
            body: () => 'Everyone here is on the free plan. Get in touch to turn it back on.',
        },
        expired: {
            heading: 'Access has run out',
            body: (a) => `It ended on ${esc(shortDate(a.accessUntil))} and everyone here is on the free plan. Get in touch to renew.`,
        },
        expiring: {
            heading: 'Renewal due',
            body: (a) => `Your seats stop working in ${a.daysLeft} day${a.daysLeft === 1 ? '' : 's'}, on ${esc(shortDate(a.accessUntil))}. Get in touch to renew.`,
        },
        past_due: {
            heading: 'Payment failed',
            body: () => 'Update your card before access stops for everyone here.',
            action: 'Manage billing',
        },
        cancelled: {
            heading: 'Subscription ended',
            body: () => 'Everyone here is on the free plan. Start it again from your organisation page.',
        },
        cancelling: {
            heading: 'Seats cancel soon',
            body: (a) => `Everyone here drops to the free plan on ${esc(shortDate(a.currentPeriodEnd))}.`,
            action: 'Manage billing',
        },
    };

    const orgAlertBar = $('org-alert-bar');

    /**
     * Shows or hides the site-wide strip from an organisation's billingAlert.
     * Admin-only – an ordinary member has no control to act on any of this,
     * and being told their school's card failed is alarming without being
     * useful.
     */
    function renderOrgAlertBar(alert, isOrgAdmin) {
        if (!orgAlertBar) return;
        const copy = alert && isOrgAdmin && ORG_ALERT_COPY[alert.reason];
        if (!copy) return hide(orgAlertBar);

        orgAlertBar.classList.remove('org-alert-bar-warning', 'org-alert-bar-critical');
        orgAlertBar.classList.add(`org-alert-bar-${alert.level}`);
        $('org-alert-bar-text').innerHTML = `<strong>${esc(copy.heading)}.</strong> ${copy.body(alert)}`;
        const btn = $('org-alert-bar-action');
        if (btn) {
            if (copy.action) { btn.textContent = copy.action; show(btn); }
            else hide(btn);
        }
        show(orgAlertBar);
    }

    $('org-alert-bar-action')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        btn.disabled = true;
        try {
            const res = await apiFetch('/api/org/portal', { method: 'POST' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Could not open billing');
            window.location.href = data.url;
        } catch {
            btn.disabled = false;
        }
    });

    // Read by the org-library delegated click handler, and by loadOrgLibrary's
    // render, so a shared-item card knows whether to offer a remove button
    // without threading isOrgAdmin through every call.
    let orgIsAdmin = false;

    /**
     * Who at the organisation made what, this calendar month.
     *
     * The page's answer to "what are we actually getting for this?", which
     * until now it could not answer at all: a seat org's per-user counters are
     * each keyed on that member's own subscription anniversary, and a bucket
     * org's pool had no user dimension whatsoever. Both now write one shared
     * monthly row per person (utils/orgUsage.js), and this draws it.
     *
     * The month is a CALENDAR month and says so, because it is deliberately
     * not the bucket cycle shown further up the same page. Two totals that
     * nearly agree are worse than two that plainly measure different things.
     *
     * An admin gets everyone, sorted busiest first with the noughts at the
     * bottom where they are the point rather than an embarrassment. Anyone else
     * gets their own line and the total it sits in.
     */
    function activityPanel(activity, isOrgAdmin) {
        if (!activity || !activity.people) return '';

        const monthLabel = (key) => {
            const [y, m] = String(key).split('-').map(Number);
            if (!y || !m) return 'this month';
            return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        };
        const month = monthLabel(activity.period);

        // Nobody has made anything yet: a table of noughts explains nothing a
        // sentence cannot, and reads as a broken page in a brand-new org.
        if (!activity.total) {
            return `
                <div class="org-section">
                    <h2 class="org-section-head">Activity</h2>
                    <p class="org-note">Nothing has been made ${activity.period === monthKeyNow() ? 'yet this month' : `in ${esc(month)}`}.
                       ${isOrgAdmin ? 'As soon as anyone generates something it shows up here, by person.' : ''}</p>
                </div>`;
        }

        const rows = activity.people.map((p) => `
            <tr${p.total === 0 ? ' class="org-activity-idle"' : ''}>
                <td>${esc(p.name)}${p.former ? ' <span class="admin-tag">left</span>' : ''}
                    ${isOrgAdmin && p.email ? `<br><small class="admin-stat-label">${esc(p.email)}</small>` : ''}</td>
                ${activity.kinds.map((k) => `<td class="org-activity-num">${p.counts[k] || 0}</td>`).join('')}
                <td class="org-activity-num"><strong>${p.total}</strong></td>
                <td class="org-activity-share">
                    <span class="org-activity-bar" style="--share:${p.share}%" aria-hidden="true"></span>
                    <span class="org-activity-pct">${p.share}%</span>
                </td>
            </tr>`).join('');

        return `
            <div class="org-section">
                <h2 class="org-section-head">Activity
                    ${isOrgAdmin ? `<a class="org-section-link" href="/api/org/activity.csv?period=${encodeURIComponent(activity.period)}" download>Download as CSV</a>` : ''}
                </h2>
                <p class="org-note">${esc(month)} &middot; ${activity.total.toLocaleString()} generations across the organisation.
                   ${isOrgAdmin && activity.inactive
                        ? `${activity.inactive} ${activity.inactive === 1 ? 'person hasn&rsquo;t' : 'people haven&rsquo;t'} made anything this month.`
                        : ''}</p>
                <div class="admin-users">
                    <table class="admin-table org-activity-table">
                        <thead>
                            <tr>
                                <th>${isOrgAdmin ? 'Person' : 'You'}</th>
                                ${activity.kinds.map((k) => `<th class="org-activity-num">${esc(KIND_LABELS[k] || k)}</th>`).join('')}
                                <th class="org-activity-num">Total</th>
                                <th>Share</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                    </table>
                </div>
                <p class="org-note">Counted by calendar month, so it won&rsquo;t line up exactly with a billing cycle.
                   ${isOrgAdmin ? 'Failed generations aren&rsquo;t counted &ndash; they aren&rsquo;t charged for either.' : ''}</p>
            </div>`;
    }

    /** This calendar month as the server keys it – see utils/orgUsage.js. */
    function monthKeyNow() {
        const d = new Date();
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    }

    /**
     * The organisation panel as the tutoring area's Admin tab draws it, in that
     * area's own parts (cards that lift, pills, quiet buttons): who is here and
     * who has been asked, with the invitation box above them; then what has
     * been made this month. The logo is on tutoring's Branding page.
     *
     * The controls keep the ids and classes the handlers further down listen
     * for, so inviting, removing and leaving are the same code as on /org. Nothing about seats is drawn: a tutoring agency doesn't buy them.
     */
    function renderOrgEmbedded(data) {
        const { org, members, invites, isOrgAdmin, activity } = data;
        const bucketHere = org.billingMode === 'bucket' && !data.agencyPlan;
        const admins = members.filter((m) => m.role === 'admin').length;
        const onlyAdmin = isOrgAdmin && admins <= 1;
        const TONES = ['blue', 'green', 'purple', 'amber', 'teal', 'indigo'];
        const initials = (name) => {
            const words = String(name || '').split(/[\s@.]+/).filter(Boolean);
            return ((words[0] || '?')[0] + (words.length > 1 ? words[1][0] : '')).toUpperCase();
        };

        const person = (m, i) => {
            const name = [m.firstName, m.lastName].filter(Boolean).join(' ');
            // The last admin can't be made a member or removed: the server
            // refuses both, so neither is offered.
            const fixed = m.role === 'admin' && admins <= 1;
            return `
                <li class="tut-person">
                    <span class="tut-person-face tone-${TONES[i % TONES.length]}" aria-hidden="true">${esc(initials(name || m.email))}</span>
                    <span class="tut-person-who"><b>${esc(name || m.email)}</b>${name ? `<small>${esc(m.email)}</small>` : ''}</span>
                    <span class="tut-person-tags">${m.role === 'admin' ? '<span class="tut-pill tone-amber">Admin</span>' : ''}${m.payingSeparately ? '<span class="tut-pill tone-slate">Pays separately</span>' : ''}</span>
                    <span class="tut-person-when">Joined ${esc(shortDate(m.joinedAt))}</span>
                    ${isOrgAdmin && !fixed ? `<span class="tut-person-acts">
                        <button class="btn btn-ghost btn-sm org-role" data-user="${esc(m.userId)}" data-role="${m.role === 'admin' ? 'member' : 'admin'}">${m.role === 'admin' ? 'Make member' : 'Make admin'}</button>
                        <button class="btn btn-ghost btn-sm org-remove" data-user="${esc(m.userId)}" data-name="${escAttr(m.email)}">Remove</button>
                    </span>` : ''}
                </li>`;
        };
        const invited = (i) => `
                <li class="tut-person is-invited">
                    <span class="tut-person-face" aria-hidden="true">@</span>
                    <span class="tut-person-who"><b>${esc(i.email)}</b><small>Invited ${esc(shortDate(i.createdAt))}, expires ${esc(shortDate(i.expiresAt))}</small></span>
                    <span class="tut-person-tags"><span class="tut-pill tone-slate">Invited</span></span>
                    <span class="tut-person-when"></span>
                    <span class="tut-person-acts"><button class="btn btn-ghost btn-sm org-revoke" data-invite="${esc(i.id)}">Revoke</button></span>
                </li>`;

        const month = (() => {
            const [y, m] = String((activity && activity.period) || '').split('-').map(Number);
            return y && m ? new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'This month';
        })();
        const made = (p) => (activity.kinds || []).filter((k) => p.counts[k]).map((k) => `${p.counts[k]} ${(KIND_LABELS[k] || k).toLowerCase()}`).join(', ');
        const activityRows = activity && activity.total ? activity.people.map((p) => `
                <li${p.total ? '' : ' class="is-idle"'}>
                    <span class="tut-person-who"><b>${esc(p.name)}${p.former ? ' <span class="tut-pill tone-slate">Left</span>' : ''}</b><small>${esc(made(p) || 'Nothing yet')}</small></span>
                    <span class="tut-activity-bar" style="--share:${Number(p.share) || 0}%" aria-hidden="true"></span>
                    <b class="tut-activity-n">${p.total}</b>
                </li>`).join('') : '';

        orgPanel.body.innerHTML = `
            <div class="tut-plan-head">
                <h2>People <span class="tut-people-sum">${members.length} here${invites.length ? `, ${invites.length} invited` : ''}</span></h2>
                ${isOrgAdmin ? '<a class="btn btn-secondary btn-sm" href="/api/org/members.csv" download>Download CSV</a>' : ''}
            </div>
            ${bucketHere && org.bucket.exhausted ? '<div class="tut-warn"><p><strong>The bucket is empty for this billing period.</strong> Generations are paused until it&rsquo;s topped up.</p></div>' : ''}

            <div class="tut-card tut-people">
                ${isOrgAdmin ? `
                <form id="org-invite-form" class="tut-invite">
                    <input class="tut-input" id="org-invite-email" type="email" placeholder="tutor@agency.co.uk" aria-label="Email address to invite" required>
                    <button type="submit" class="btn btn-primary">Send invitation</button>
                </form>
                <details class="org-bulk tut-invite-bulk">
                    <summary>Invite a whole list</summary>
                    <textarea id="org-bulk-text" rows="4" aria-label="Addresses to invite, one per line" placeholder="a.smith@agency.co.uk&#10;b.jones@agency.co.uk"></textarea>
                    <div class="org-bulk-actions">
                        <input id="org-bulk-file" type="file" accept=".csv,text/csv,text/plain">
                        <button class="btn btn-secondary btn-sm" id="org-bulk-send">Send them all</button>
                    </div>
                </details>
                <div id="org-bulk-report"></div>` : ''}
                <ul class="tut-people-list">
                    ${members.map(person).join('')}
                    ${isOrgAdmin ? invites.map(invited).join('') : ''}
                </ul>
                ${onlyAdmin
                    ? '<p class="tut-plan-small">You&rsquo;re the only admin, so you can&rsquo;t leave yet. Make someone else an admin first.</p>'
                    : `<p class="tut-plan-small">Leaving removes you from ${esc(org.name)}. Everything you&rsquo;ve made stays yours.
                        <button class="tut-linkbtn" id="org-leave" type="button">Leave ${esc(org.name)}</button></p>`}
            </div>

            <div class="tut-people-more">
                <div class="tut-card">
                    <h3 class="tut-card-title">Made this month
                        ${isOrgAdmin && activity && activity.total ? `<a class="tut-linkbtn" href="/api/org/activity.csv?period=${encodeURIComponent(activity.period)}" download>CSV</a>` : ''}</h3>
                    ${activityRows
                        ? `<p class="tut-activity-total"><b>${activity.total.toLocaleString('en-GB')}</b> in ${esc(month)}</p><ul class="tut-activity">${activityRows}</ul>`
                        : '<p class="tut-hint">Nothing yet. It shows up here, by person, as soon as anyone makes something.</p>'}
                    <p class="tut-plan-small tut-logo-foot">Resources colleagues share sit in your library, under Shared.
                        <button class="tut-linkbtn" id="org-open-library" type="button">Open it</button></p>
                </div>
            </div>

            ${bucketHere ? `
            <div class="tut-card">
                <h3 class="tut-card-title">Bucket</h3>
                ${bucketPanel(org.bucket, org.name)}
                ${isOrgAdmin && org.paysByCard ? '<p class="tut-chips"><button class="btn btn-secondary btn-sm" id="org-portal">Manage billing</button></p>' : ''}
            </div>` : ''}`;
    }

    function renderOrg(data) {
        const { org, members, invites, isOrgAdmin, activity } = data;
        orgIsAdmin = isOrgAdmin;
        const seats = org.seats;
        const onInvoice = org.billingMode === 'invoice';
        const onBucket = org.billingMode === 'bucket';

        // The persistent top-of-page strip, not anything embedded in this
        // card: a failed card or a lapsing purchase order is not something
        // that should need a visit to /org to be noticed.
        renderOrgAlertBar(org.billingAlert, isOrgAdmin);

        // The tutoring area's Admin tab draws the same people, invitations,
        // activity and logo its own way, and leaves the seats behind here.
        if (orgPanel.embedded) return renderOrgEmbedded(data);

        const bucketHere = onBucket;
        // A card organisation whose subscription has ended: its admin starts
        // it again here (POST /api/org/restart) rather than in the billing
        // portal, which has nothing left in it to manage.
        const ended = org.paysByCard && org.hasStripeSubscription && org.plan !== 'pro';
        // What it had, priced as it would be bought again (POST /api/org/restart
        // works the quantity out the same way).
        const restartUnits = onBucket ? Math.max(1, Math.round((org.bucket?.size || 500) / 500)) : Math.max(seats.purchased || 0, members.length, 2);
        const restartWhat = onBucket
            ? `${(restartUnits * 500).toLocaleString('en-GB')} resources a month, shared by everyone`
            : `${restartUnits} seats`;
        const restartPrice = (pence) => `£${((pence * restartUnits) / 100).toFixed(2).replace(/\.00$/, '')}`;
        const restartForm = !isOrgAdmin ? `
                <div class="org-restart"><p><strong>Not paid for at the moment.</strong> Everyone here is on the free plan until an admin starts it again.</p></div>` : `
                <div class="org-restart">
                    <p><strong>Not paid for at the moment.</strong> Everyone here is on the free plan until it starts again.</p>
                    <form class="org-restart-form" id="org-restart-form">
                        <select name="interval" aria-label="Billing">
                            <option value="month">${org.price ? `${restartPrice(org.price.monthPence)} a month` : 'Monthly'}</option>
                            <option value="year">${org.price ? `${restartPrice(org.price.yearPence)} a year, 2 months free` : 'Yearly, 2 months free'}</option>
                        </select>
                        <button type="submit" class="btn btn-primary btn-sm">Start again</button>
                    </form>
                    <p class="org-restart-what">${restartWhat}, paid by card.</p>
                </div>`;

        $('org-name').textContent = org.name;
        $('org-sub').textContent = isOrgAdmin
            ? 'Seats, and who is sitting in them.'
            : `You have a seat with ${org.name}.`;

        // Created but never paid for – a checkout that was closed part-way.
        // Rendered as the half-finished setup it actually is rather than as an
        // error: the normal page here would be an invite form against nought
        // seats and a table containing one person who has no seat either.
        //
        // The steps borrow the school-account panel's held/open marks. It is
        // the same idea (what you have, what you don't yet) and the sibling
        // screen a new customer is most likely to have just seen.
        if (org.awaitingPayment) {
            $('org-sub').textContent = 'Nearly there.';
            orgPanel.body.innerHTML = `
                <section class="org-setup">
                    <p class="edu-eyebrow">One step left</p>
                    <h2 class="edu-head">${esc(org.name)} isn&rsquo;t switched on yet</h2>
                    <p class="edu-copy">${isOrgAdmin
                        ? 'Nothing has been charged, so there are no seats to hand out &ndash; and yours isn&rsquo;t active either. Choose how many you need and you can start inviting people straight away.'
                        : `Whoever set ${esc(org.name)} up hasn&rsquo;t finished paying for it, so there are no seats yet. Nothing you&rsquo;ve made is affected &ndash; you&rsquo;re on the free plan until it&rsquo;s sorted.`}</p>

                    <ol class="edu-keys org-steps">
                        <li class="edu-key">
                            <span class="edu-key-mark is-held" aria-hidden="true"></span>
                            <span class="edu-key-text">
                                <span class="edu-key-label">Organisation created</span>
                                <span class="edu-key-sub">${esc(org.name)}</span>
                            </span>
                            <span class="edu-key-pill">Done</span>
                        </li>
                        <li class="edu-key ${isOrgAdmin ? 'is-now' : ''}">
                            <span class="edu-key-mark is-open" aria-hidden="true"></span>
                            <span class="edu-key-text">
                                <span class="edu-key-label">Pay for your seats</span>
                                <span class="edu-key-sub">${org.price && org.price.custom
                                    ? `&pound;${(org.price.monthPence / 100).toFixed(2)} per seat a month, or &pound;${(org.price.yearPence / 100).toFixed(2)} a year`
                                    : `&pound;${ORG_PRICE_GBP.month.toFixed(2)} per seat a month, or two months free paid yearly`}</span>
                            </span>
                            ${isOrgAdmin ? '<span class="edu-key-pill org-step-pill-now">Next</span>' : ''}
                        </li>
                        <li class="edu-key">
                            <span class="edu-key-mark is-open" aria-hidden="true"></span>
                            <span class="edu-key-text">
                                <span class="edu-key-label">Invite your people</span>
                                <span class="edu-key-sub">One at a time, or paste in a whole staff list</span>
                            </span>
                        </li>
                    </ol>

                    ${isOrgAdmin ? `
                    <form class="org-buy-form org-setup-form">
                        <label>Organisation name
                            <input name="orgName" type="text" maxlength="200" value="${escAttr(org.name)}" required>
                        </label>
                        <label>Seats
                            <input name="seats" type="number" min="2" max="${STRIPE_MAX_SEATS}" value="${Math.min(seats.purchased || 5, STRIPE_MAX_SEATS)}" required>
                        </label>
                        <label>Billing
                            <select name="interval">
                                <option value="year">Yearly &ndash; 2 months free</option>
                                <option value="month">Monthly</option>
                            </select>
                        </label>
                        <button type="submit" class="btn btn-primary">Continue to payment</button>
                    </form>
                    <p class="org-note">You can change the name or the number of seats here &ndash; nothing is fixed until you pay.
                       More than ${STRIPE_MAX_SEATS} seats, or need a purchase order? Email
                       <a href="mailto:${ENQUIRY_EMAIL}">${ENQUIRY_EMAIL}</a> and we&rsquo;ll invoice you.</p>
                    <p class="org-note">Already paid and still seeing this?
                       <button type="button" class="org-recheck" id="org-recheck">Check again</button> &ndash;
                       card payments take a moment to come back to us.</p>` : ''}
                </section>`;
            return;
        }

        // The server refuses to let the last admin go, for the same reason it
        // refuses to let one be removed. Saying so here is better than offering
        // a button whose only outcome is that message.
        const onlyAdmin = isOrgAdmin && members.filter((m) => m.role === 'admin').length <= 1;

        const memberRows = members.map((m) => `
            <tr>
                <td>${esc([m.firstName, m.lastName].filter(Boolean).join(' ') || m.email)}<br><small class="admin-stat-label">${esc(m.email)}</small></td>
                <td>${m.role === 'admin' ? '<span class="admin-tag">admin</span>' : 'Member'}</td>
                <td>${shortDate(m.joinedAt)}</td>
                <td>${m.payingSeparately ? '<span class="admin-tag">paying separately</span>' : ''}</td>
                ${isOrgAdmin ? `<td class="org-row-actions">
                    <button class="btn btn-sm org-role" data-user="${esc(m.userId)}" data-role="${m.role === 'admin' ? 'member' : 'admin'}">${m.role === 'admin' ? 'Make member' : 'Make admin'}</button>
                    <button class="btn btn-sm org-remove" data-user="${esc(m.userId)}" data-name="${escAttr(m.email)}">Remove</button>
                </td>` : ''}
            </tr>`).join('');

        const inviteRows = invites.map((i) => `
            <tr>
                <td>${esc(i.email)}</td>
                <td>Invited ${shortDate(i.createdAt)}</td>
                <td>Expires ${shortDate(i.expiresAt)}</td>
                <td class="org-row-actions"><button class="btn btn-sm org-revoke" data-invite="${esc(i.id)}">Revoke</button></td>
            </tr>`).join('');

        orgPanel.body.innerHTML = `
            <div class="admin-summary">
                <div class="admin-stat"><span class="admin-stat-num">${seats.members}</span><span class="admin-stat-label">People</span></div>
                <div class="admin-stat"><span class="admin-stat-num">${seats.pendingInvites}</span><span class="admin-stat-label">Invited</span></div>
                ${bucketHere ? `
                <div class="admin-stat"><span class="admin-stat-num">${org.bucket.available.toLocaleString()}</span><span class="admin-stat-label">Left in bucket</span></div>
                ` : onBucket ? '' : `
                <div class="admin-stat"><span class="admin-stat-num">${seats.purchased}</span><span class="admin-stat-label">Seats paid for</span></div>
                <div class="admin-stat"><span class="admin-stat-num">${seats.available}</span><span class="admin-stat-label">Free</span></div>
                `}
            </div>

            ${bucketHere && org.bucket.exhausted ? `
            <p class="admin-capped-note">The bucket is empty for this billing period &ndash; generations are paused until it&rsquo;s topped up.</p>` : ''}

            ${seats.overSubscribed ? `
            <p class="admin-capped-note">There are more people here than seats you&rsquo;re paying for. Nobody has lost access &ndash;
               but invitations are paused until the numbers line up again.</p>` : ''}

            ${data.duplicatePaidSeat ? `
            <div class="referral-banner">
                <span class="referral-banner-tag">Paying twice</span>
                <span class="referral-banner-text">
                    <strong>${esc(org.name)} is paying for your seat</strong>
                    <small>You&rsquo;re also paying for Pro yourself. Cancel your own subscription in Plan &amp; billing and you lose nothing.</small>
                </span>
            </div>` : ''}

            ${bucketHere ? `
            <div class="org-section">
                <h2 class="org-section-head">Bucket</h2>
                ${ended ? restartForm : bucketPanel(org.bucket, org.name)}
                ${isOrgAdmin && !ended ? `
                ${org.paysByCard ? `
                <p class="org-note">Paid by card${org.currentPeriodEnd ? `, ${org.cancelAtPeriodEnd ? 'ending' : 'renewing'} on ${esc(shortDate(org.currentPeriodEnd))}` : ''}.
                   The bucket refills on your billing date. Headcount is unlimited &ndash; add as many people as you like,
                   they all draw from this same bucket. Email us to change its size.</p>
                <div class="org-seat-row"><button class="btn btn-sm" id="org-portal">Manage billing</button></div>` : `
                <p class="org-note">Running ${org.accessUntil ? `to ${esc(shortDate(org.accessUntil))}` : 'for now'}.
                   Headcount is unlimited &ndash; add as many people as you like, they all draw from this same bucket.
                   Email us to top it up or change its size.</p>`}` : ''}
            </div>` : ''}

            ${isOrgAdmin && !onBucket ? `
            <div class="org-section">
                <h2 class="org-section-head">Seats</h2>
                ${onInvoice ? `
                    <p class="org-note">Your seats are on an invoice&nbsp;&ndash; ${seats.purchased} of them,
                       ${org.accessUntil ? `running to ${esc(shortDate(org.accessUntil))}` : 'with no end date set'}.
                       Email us to change the number and we&rsquo;ll sort it out.</p>
                ` : ended ? restartForm : `
                    <div class="org-seat-row">
                        <label for="org-seat-count">Seats</label>
                        <input id="org-seat-count" type="number" min="2" max="${STRIPE_MAX_SEATS}" value="${seats.purchased}">
                        <button class="btn btn-primary btn-sm" id="org-seat-save">Update</button>
                        <button class="btn btn-sm" id="org-portal">Manage billing</button>
                    </div>
                    <p class="org-note">More seats are billed straight away. Fewer are credited against your next invoice
                       &ndash; you can&rsquo;t go below the ${seats.used} you&rsquo;re using.</p>
                    <p class="org-note">Need more than ${STRIPE_MAX_SEATS} seats? Email
                       <a href="mailto:${ENQUIRY_EMAIL}">${ENQUIRY_EMAIL}</a> and we&rsquo;ll invoice your school or trust.</p>
                `}
            </div>` : ''}

            ${activityPanel(activity, isOrgAdmin)}

            ${isOrgAdmin ? `
            <div class="org-section">
                <h2 class="org-section-head">Invite people</h2>
                <form id="org-invite-form" class="org-invite-form">
                    <input id="org-invite-email" type="email" placeholder="teacher@school.uk" required>
                    <button type="submit" class="btn btn-primary">Send invitation</button>
                </form>
                <details class="org-bulk">
                    <summary>Invite a whole list at once</summary>
                    <p class="org-note">A CSV or a pasted column of addresses &ndash; one per line. Names alongside them are fine.
                       Nobody gets an account until they accept.</p>
                    <textarea id="org-bulk-text" rows="5" placeholder="a.smith@school.uk&#10;b.jones@school.uk"></textarea>
                    <div class="org-bulk-actions">
                        <input id="org-bulk-file" type="file" accept=".csv,text/csv,text/plain">
                        <button class="btn btn-primary btn-sm" id="org-bulk-send">Send them all</button>
                    </div>
                </details>
                <div id="org-bulk-report"></div>
            </div>` : ''}

            ${brandingPanel(org, isOrgAdmin)}

            <!-- The shelf itself lives in the dashboard's Shared tab. What is
                 left here is the signpost, because /org is where an admin looks
                 for anything the organisation has in common. -->
            <div class="org-section">
                <h2 class="org-section-head">Shared library</h2>
                <p class="org-note">Worksheets, quizzes and presentations colleagues have shared sit in your library,
                   under <strong>Shared</strong>. Share your own from its menu on any finished item.</p>
                <button class="btn btn-secondary btn-sm" id="org-open-library">Open the shared library</button>
            </div>

            <div class="org-section">
                <h2 class="org-section-head">People
                    ${isOrgAdmin ? '<a class="org-section-link" href="/api/org/members.csv" download>Download as CSV</a>' : ''}
                </h2>
                <div class="admin-users">
                    <table class="admin-table">
                        <thead><tr><th>Name</th><th>Role</th><th>Joined</th><th></th>${isOrgAdmin ? '<th></th>' : ''}</tr></thead>
                        <tbody>${memberRows}</tbody>
                    </table>
                </div>
            </div>

            ${isOrgAdmin && invites.length ? `
            <div class="org-section">
                <h2 class="org-section-head">Waiting to accept</h2>
                <div class="admin-users">
                    <table class="admin-table"><tbody>${inviteRows}</tbody></table>
                </div>
            </div>` : ''}

            <div class="org-section org-leave">
                ${onlyAdmin ? `
                <p class="org-note">You&rsquo;re the only admin of ${esc(org.name)}, so you can&rsquo;t leave it just yet
                   &ndash; an organisation with nobody to administer it can only be sorted out by us.
                   Make someone else an admin above and the option appears here.</p>` : `
                <p class="org-note">Leaving gives your seat back to ${esc(org.name)}. Everything you&rsquo;ve made stays yours
                   &ndash; you drop back to the free plan, or to your own subscription if you have one.</p>
                <button class="btn btn-secondary btn-sm" id="org-leave">Leave ${esc(org.name)}</button>`}
            </div>`;
    }

    /**
     * The organisation's mark on what its teachers make.
     *
     * Shown to members as well as admins, minus the controls: a teacher who
     * finds their school's crest on a worksheet should be able to see here
     * where it came from rather than wonder whether they did it by accident.
     *
     * Deliberately short of prose. What the logo does is visible the moment
     * anyone prints something, and a screen that explains it in four paragraphs
     * is a screen nobody reads the one line on that matters – the switch.
     *
     * The preview is <img src="/api/org/logo"> rather than the data URI the
     * documents use: it is an ordinary image in an ordinary page, and there is
     * no reason to push half a megabyte of base64 through the org JSON to draw
     * it. `t` busts the endpoint's own one-minute cache after an upload.
     */
    function brandingPanel(org, isOrgAdmin) {
        const b = org.branding || { hasLogo: false, showOnResources: true };
        // Reachable: a failed card or a purchase order that has run out. Seats
        // read as free until it is sorted and a free account carries our name
        // instead – better said here than discovered on a worksheet.
        const lapsed = org.plan !== 'pro';
        return `
            <div class="org-section org-brand-section">
                <h2 class="org-section-head">Your logo on resources</h2>

                ${lapsed ? `<p class="admin-capped-note">While billing is sorted out, seats count as free accounts
                   &ndash; so resources carry our name, not ${esc(org.name)}&rsquo;s.</p>` : ''}

                <div class="org-brand-row">
                    <div class="org-brand-preview ${b.hasLogo ? '' : 'is-empty'}" id="org-brand-preview">
                        ${b.hasLogo
                            ? `<img src="/api/org/logo?t=${Date.now()}" alt="${escAttr(org.name)}">`
                            : '<span>No logo yet</span>'}
                    </div>
                    ${isOrgAdmin ? `
                    <div class="org-brand-controls">
                        <input type="file" id="org-logo-file" accept="image/png,image/jpeg,image/webp,image/svg+xml" class="sr-only">
                        <button class="btn btn-secondary btn-sm" id="org-logo-pick">${b.hasLogo ? 'Replace' : 'Upload a logo'}</button>
                        ${b.hasLogo ? '<button class="btn btn-sm" id="org-logo-remove">Remove</button>' : ''}
                        <p class="org-note">PNG, JPEG, WebP or SVG &middot; up to 512KB</p>
                    </div>` : ''}
                </div>

                ${isOrgAdmin ? `
                <label class="org-brand-toggle">
                    <input type="checkbox" id="org-brand-on" ${b.showOnResources ? 'checked' : ''}>
                    <span>Show ${esc(org.name)} on worksheets, booklets and slides</span>
                </label>` : `
                <p class="org-note">${b.showOnResources
                    ? `Resources carry ${esc(org.name)}.`
                    : 'Switched off by your admin.'}</p>`}

                <div class="error org-brand-error" role="alert" hidden></div>
            </div>`;
    }

    // The three branding controls. Delegated off orgBody like every other
    // control on this page, because renderOrg replaces the whole thing.
    onOrgPanel('click', (e) => {
        if (e.target.closest('#org-logo-pick')) $('org-logo-file')?.click();
        if (e.target.closest('#org-logo-remove')) removeOrgLogo();
    });

    onOrgPanel('change', (e) => {
        if (e.target.id === 'org-logo-file') {
            const file = e.target.files?.[0];
            // Cleared before the upload, not after: a rejected file left in the
            // input fires no `change` when it is picked again, so the second
            // attempt at the same file would do nothing at all.
            e.target.value = '';
            uploadOrgLogo(file);
        }
        if (e.target.id === 'org-brand-on') setOrgBrandingOn(e.target.checked);
    });

    /**
     * Fold a branding change back into the signed-in user, so the header
     * follows it without a reload.
     *
     * state.user.org came from /api/auth/me at sign-in and would otherwise go
     * on saying there is no logo until the next page load – which is the one
     * moment an admin is definitely looking at the header to see whether it
     * worked.
     */
    function applyBrandingChange(branding) {
        if (!branding || !state.user?.org) return;
        state.user.org.hasLogo = !!branding.hasLogo;
        // Any new value busts the <img> cache; the timestamp is only ever
        // compared against itself.
        state.user.org.logoVersion = String(Date.now());
        syncOrgLockup();
    }

    function brandError(message) {
        const box = orgPanel.body?.querySelector('.org-brand-error');
        if (!box) return;
        box.textContent = message || '';
        box.hidden = !message;
    }

    async function uploadOrgLogo(file) {
        if (!file) return;
        brandError('');
        const body = new FormData();
        body.append('logo', file);
        try {
            // Raw fetch rather than apiFetch, which this predates: apiFetch
            // used to force Content-Type: application/json and break a
            // multipart body. It no longer does, so this could move over and
            // pick up the 401/402 handling it currently goes without.
            const res = await fetch('/api/org/logo', { method: 'POST', body });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Could not upload that logo');
            forgetResourceMark();
            applyBrandingChange(data.branding);
            reloadOrg();
        } catch (err) {
            brandError(err.message);
        }
    }

    async function removeOrgLogo() {
        brandError('');
        try {
            const res = await apiFetch('/api/org/logo', { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Could not remove the logo');
            forgetResourceMark();
            applyBrandingChange(data.branding);
            reloadOrg();
        } catch (err) {
            brandError(err.message);
        }
    }

    async function setOrgBrandingOn(on) {
        brandError('');
        try {
            const res = await apiFetch('/api/org/branding', {
                method: 'PATCH',
                body: JSON.stringify({ showOnResources: !!on }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Could not save that');
            forgetResourceMark();
        } catch (err) {
            brandError(err.message);
            // Put the switch back where the server still has it, rather than
            // leaving it showing a state nothing agrees with.
            const box = $('org-brand-on');
            if (box) box.checked = !on;
        }
    }

    // ── Shared library (dashboard "Shared" tab) ────────────
    //
    // The shelf of what colleagues have shared into the organisation. It is a
    // tab of your library rather than a section of /org because taking a
    // colleague's worksheet is the same act as opening one of your own – the
    // only difference is whose name is on it.
    //
    // Everything is fetched once and filtered in the browser: a school's shelf
    // is tens of items, not thousands, and searching a list that small over the
    // network would be slower to type against than to scroll.

    const orgLib = {
        items: [],
        orgName: '',
        loaded: false,
        loading: false,
        kind: 'all',
        person: 'all',
        query: '',
    };

    const ORGLIB_KIND_NOUNS = { presentation: 'Presentation', worksheet: 'Worksheet', quiz: 'Quiz', booklet: 'Booklet' };
    // Which tab of your own library the copy has just landed in.
    const ORGLIB_KIND_TABS = { presentation: 'Presentations', worksheet: 'Worksheets', quiz: 'Quizzes', booklet: 'Booklets' };

    function orgLibUsesLabel(n) {
        if (!n) return 'Not taken yet';
        return `Taken by ${n} colleague${n === 1 ? '' : 's'}`;
    }

    /** Initials for the little coloured disc against each shared item. */
    function orgLibInitials(name) {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    function orgLibIsNew(createdAt) {
        if (!createdAt) return false;
        return Date.now() - new Date(createdAt).getTime() < 7 * 24 * 60 * 60 * 1000;
    }

    async function loadOrgLibrary(force = false) {
        if (!state.user?.org) return;
        if (orgLib.loading || (orgLib.loaded && !force)) return;
        orgLib.loading = true;
        const list = $('olib-list');
        if (list && !orgLib.loaded) list.innerHTML = listSkeletonHTML();
        try {
            const res = await apiFetch('/api/org/library');
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load');
            orgLib.items = data.items || [];
            orgLib.orgName = data.org?.name || '';
            orgIsAdmin = !!data.org?.isAdmin;
            orgLib.loaded = true;
            renderOrgLibrary();
        } catch (err) {
            if (err.message !== 'Not authenticated' && list) {
                list.innerHTML = '<p class="org-note">Couldn’t load the shared library. Try again in a moment.</p>';
            }
        } finally {
            orgLib.loading = false;
        }
    }

    function orgLibFiltered() {
        const q = orgLib.query.trim().toLowerCase();
        return orgLib.items.filter((it) => {
            if (orgLib.kind !== 'all' && it.itemType !== orgLib.kind) return false;
            if (orgLib.person !== 'all' && it.sharedBy !== orgLib.person) return false;
            if (!q) return true;
            return [it.title, it.subject, it.level, it.examBoard, it.sharedByName]
                .filter(Boolean).join(' ').toLowerCase().includes(q);
        });
    }

    function renderOrgLibrary() {
        const list = $('olib-list');
        if (!list) return;

        renderOrgLibraryChrome();
        const items = orgLibFiltered();

        // Nothing on the shelf at all, versus nothing matching what was typed:
        // the first needs telling how to put something on it, the second only
        // needs the filters cleared.
        if (!orgLib.items.length) {
            list.innerHTML = `
                <div class="olib-empty">
                    <p class="olib-empty-title">Nothing shared yet</p>
                    <p class="olib-empty-sub">Choose <strong>Share to org library</strong> from any finished item&rsquo;s menu and colleagues can take a copy.</p>
                    <button type="button" class="btn btn-primary btn-sm olib-empty-cta" data-tab-jump="presentations">Go to your presentations</button>
                </div>`;
            return;
        }
        if (!items.length) {
            list.innerHTML = `
                <div class="olib-empty">
                    <p class="olib-empty-title">Nothing matches</p>
                    <p class="olib-empty-sub">${orgLib.items.length} item${orgLib.items.length === 1 ? '' : 's'} shared here, none of them this.</p>
                    <button type="button" class="btn btn-secondary btn-sm" id="olib-clear">Clear the filters</button>
                </div>`;
            return;
        }

        list.innerHTML = `<div class="olib-grid">${items.map(orgLibCardHTML).join('')}</div>`;
    }

    function orgLibCardHTML(it) {
        const facets = [it.subject, it.level, it.examBoard].filter(Boolean);
        const canRemove = orgIsAdmin || it.sharedBy === state.user?.id;
        const mine = it.sharedBy === state.user?.id;
        return `
        <article class="olib-card olib-${esc(it.itemType)}" data-id="${escAttr(it.id)}">
            <header class="olib-card-top">
                <span class="olib-card-icon" aria-hidden="true">${LIST_ICONS[it.itemType] || ''}</span>
                <span class="olib-card-kind">${esc(ORGLIB_KIND_NOUNS[it.itemType] || 'Item')}</span>
                <!-- At most one flag. "New" is about deciding whether to take
                     it, which is settled once you have – and both of them plus
                     the type would not fit across a card this wide anyway. -->
                ${it.alreadyAdded
                    ? '<span class="olib-flag olib-flag-have" title="You have already added this to your library">Added</span>'
                    : (orgLibIsNew(it.createdAt) ? '<span class="olib-flag">New</span>' : '')}
                ${canRemove ? `<button type="button" class="pres-delete-btn olib-remove" data-id="${escAttr(it.id)}" title="${mine ? 'Unshare this' : 'Remove from the shared library'}" aria-label="Remove from the shared library">${TRASH_ICON}</button>` : ''}
            </header>

            <h3 class="olib-card-title">${esc(it.title || 'Untitled')}</h3>
            ${facets.length ? `<p class="olib-card-facets">${facets.map((f) => `<span class="olib-facet">${esc(f)}</span>`).join('')}</p>` : ''}

            <footer class="olib-card-foot">
                <span class="olib-avatar ${mine ? 'olib-avatar-me' : ''}" aria-hidden="true">${esc(orgLibInitials(it.sharedByName))}</span>
                <span class="olib-card-by">
                    <span class="olib-card-who">${mine ? 'Shared by you' : `${esc(it.sharedByName || 'A colleague')}`}</span>
                    <!-- One line, and it is the count: the date is what the
                         New flag already says, and on a 270px card the two
                         together wrapped onto a second line every time. -->
                    <span class="olib-card-uses" title="Shared ${escAttr(shortDate(it.createdAt))}">${esc(orgLibUsesLabel(it.cloneCount))}</span>
                </span>
                <button type="button" class="btn btn-sm ${it.alreadyAdded ? 'btn-ghost' : 'btn-primary'} olib-add" data-id="${escAttr(it.id)}">
                    ${it.alreadyAdded ? 'Take another copy' : 'Add to my library'}
                </button>
            </footer>
        </article>`;
    }

    /** The heading, counts, type filter and the row of colleagues above the grid. */
    function renderOrgLibraryChrome() {
        const items = orgLib.items;
        const sharers = [...new Map(items.map((it) => [it.sharedBy, it.sharedByName])).entries()];
        const taken = items.filter((it) => it.alreadyAdded).length;

        const title = $('olib-hero-title');
        if (title) title.textContent = orgLib.orgName ? `Shared at ${orgLib.orgName}` : 'Shared with you';

        const stats = $('olib-stats');
        if (stats) {
            if (!items.length) hide(stats);
            else {
                const bits = [
                    `${items.length} item${items.length === 1 ? '' : 's'} from ${sharers.length} colleague${sharers.length === 1 ? '' : 's'}`,
                    taken ? `${taken} already in your library` : '',
                ].filter(Boolean);
                stats.textContent = bits.join(' · ');
                show(stats);
            }
        }

        const toolbar = $('olib-toolbar');
        if (toolbar) items.length ? show(toolbar) : hide(toolbar);

        // Each type button carries its own count, and one with nothing behind
        // it is disabled rather than hidden: a row that changes width as
        // colleagues share things is harder to aim at than a greyed button.
        $('olib-kinds')?.querySelectorAll('.mkt-mode').forEach((btn) => {
            const kind = btn.dataset.kind;
            const n = kind === 'all' ? items.length : items.filter((it) => it.itemType === kind).length;
            btn.classList.toggle('active', kind === orgLib.kind);
            btn.disabled = !n;
            let count = btn.querySelector('.olib-kind-count');
            if (!count) {
                count = document.createElement('span');
                count.className = 'olib-kind-count';
                btn.appendChild(count);
            }
            count.textContent = n;
        });

        const people = $('olib-people');
        if (people) {
            if (sharers.length < 3) hide(people);
            else {
                people.innerHTML = `
                    <button type="button" class="olib-person ${orgLib.person === 'all' ? 'active' : ''}" data-person="all">
                        <span class="olib-person-name">Everyone</span>
                    </button>
                    ${sharers.map(([id, name]) => `
                    <button type="button" class="olib-person ${orgLib.person === id ? 'active' : ''}" data-person="${escAttr(id)}">
                        <span class="olib-avatar olib-avatar-sm ${id === state.user?.id ? 'olib-avatar-me' : ''}" aria-hidden="true">${esc(orgLibInitials(name))}</span>
                        <span class="olib-person-name">${esc(id === state.user?.id ? 'You' : (name || 'A colleague'))}</span>
                        <span class="olib-person-count">${items.filter((it) => it.sharedBy === id).length}</span>
                    </button>`).join('')}`;
                show(people);
            }
        }

        // The tab itself carries the count, so a colleague sharing something
        // is visible from whichever tab you happen to be looking at.
        const badge = $('orglib-tab-count');
        if (badge) {
            badge.textContent = items.length;
            items.length ? show(badge) : hide(badge);
        }
    }

    const orgLibPane = $('orglib-pane');

    $('olib-search')?.addEventListener('input', (e) => {
        orgLib.query = e.target.value;
        renderOrgLibrary();
    });

    $('olib-kinds')?.addEventListener('click', (e) => {
        const btn = e.target.closest('.mkt-mode');
        if (!btn) return;
        orgLib.kind = btn.dataset.kind;
        renderOrgLibrary();
    });

    $('olib-people')?.addEventListener('click', (e) => {
        const btn = e.target.closest('.olib-person');
        if (!btn) return;
        orgLib.person = btn.dataset.person;
        renderOrgLibrary();
    });

    orgLibPane?.addEventListener('click', async (e) => {
        const add    = e.target.closest('.olib-add');
        const remove = e.target.closest('.olib-remove');
        const clear  = e.target.closest('#olib-clear');
        const jump   = e.target.closest('[data-tab-jump]');

        if (clear) {
            orgLib.kind = 'all';
            orgLib.person = 'all';
            orgLib.query = '';
            const search = $('olib-search');
            if (search) search.value = '';
            return renderOrgLibrary();
        }

        if (jump) return setDashTab(jump.dataset.tabJump);

        if (add) {
            const item = orgLib.items.find((i) => i.id === add.dataset.id);
            add.disabled = true;
            add.textContent = 'Adding…';
            try {
                const r = await apiFetch(`/api/org/library/${add.dataset.id}/clone`, { method: 'POST' });
                const data = await r.json();
                if (!r.ok) {
                    showToast(data.error || 'Couldn’t add that');
                    add.disabled = false;
                    add.textContent = item?.alreadyAdded ? 'Take another copy' : 'Add to my library';
                    return;
                }
                // Bumping the local copy rather than refetching keeps the card
                // where it is under the cursor; the shelf is re-fetched next
                // time the tab is opened anyway.
                if (item) {
                    if (!item.alreadyAdded) item.cloneCount += 1;
                    item.alreadyAdded = true;
                }
                showToast(`Copied into ${ORGLIB_KIND_TABS[data.itemType] || 'your library'}`);
                if (data.itemType === 'presentation') loadPresentations();
                if (data.itemType === 'worksheet') loadWorksheets();
                if (data.itemType === 'quiz') loadQuizzes();
                if (data.itemType === 'booklet') loadBooklets();
                renderOrgLibrary();
            } catch {
                showToast('Network error');
                add.disabled = false;
                add.textContent = 'Add to my library';
            }
            return;
        }

        if (remove) {
            const item = orgLib.items.find((i) => i.id === remove.dataset.id);
            const ok = await showConfirm({
                title: 'Stop sharing this?',
                message: 'Colleagues will no longer see it in the shared library. Anyone who already took a copy keeps theirs, and your own original is untouched.',
                confirmLabel: 'Remove',
                danger: true,
            });
            if (!ok) return;
            try {
                const r = await apiFetch(`/api/org/library/${remove.dataset.id}`, { method: 'DELETE' });
                if (!r.ok) return showToast('Couldn’t remove that');
                orgLib.items = orgLib.items.filter((i) => i !== item);
                renderOrgLibrary();
            } catch { showToast('Network error'); }
        }
    });

    /** Renders a bulk run line by line – the line numbers are the whole point. */
    function renderBulkReport(data) {
        const el = $('org-bulk-report');
        if (!el) return;
        const problems = data.results.filter((r) => r.status === 'skipped');
        el.innerHTML = `
            <p class="org-note"><strong>${data.invited} invited.</strong>
               ${problems.length ? `${problems.length} skipped.` : 'Nothing skipped.'}
               ${orgPanel.embedded ? '' : `${data.seatsRemaining} seat${data.seatsRemaining === 1 ? '' : 's'} left.`}</p>
            ${problems.length ? `
            <div class="admin-users">
                <table class="admin-table">
                    <thead><tr><th>Line</th><th>Address</th><th>Why not</th></tr></thead>
                    <tbody>${problems.map((r) => `
                        <tr><td>${r.line}</td><td>${esc(r.email || '—')}</td><td>${esc(r.detail || r.reason)}</td></tr>`).join('')}
                    </tbody>
                </table>
            </div>
            <p class="org-note">Fix those lines${orgPanel.embedded ? '' : ' (or add seats)'} and send the same list again &ndash;
               anyone already invited is skipped, so nothing is sent twice.</p>` : ''}`;
    }

    // One delegated handler: the panel is re-rendered after every change, so
    // per-row listeners would have to be re-bound each time.
    onOrgPanel('click', async (e) => {
        const remove = e.target.closest('.org-remove');
        const role   = e.target.closest('.org-role');
        const revoke = e.target.closest('.org-revoke');
        // Same destination as the seats card's own button – the alert banner
        // just gets there without the admin having to hunt for it.
        const portal = e.target.closest('#org-portal') || e.target.closest('#org-alert-portal');
        const seats  = e.target.closest('#org-seat-save');
        const leave  = e.target.closest('#org-leave');
        // "I've paid, why does this still say I haven't." Forces the Stripe
        // check rather than leaving them to guess how long to wait.
        const recheck = e.target.closest('#org-recheck');
        if (recheck) {
            recheck.disabled = true;
            recheck.textContent = 'Checking…';
            return void reloadOrg(true);
        }
        // The signpost to the shelf, which lives on the dashboard now.
        if (e.target.closest('#org-open-library')) return void showPresList('shared');
        if (!remove && !role && !revoke && !portal && !seats && !leave) return;

        setError(orgPanel.error, '');
        try {
            if (remove) {
                const ok = await showConfirm({
                    title: 'Remove them from your organisation?',
                    message: `${remove.dataset.name} keeps everything they've made, but drops back to the free plan.${orgPanel.embedded ? '' : ' The seat frees up straight away.'}`,
                    confirmLabel: 'Remove',
                    danger: true,
                });
                if (!ok) return;
                const res = await apiFetch(`/api/org/members/${encodeURIComponent(remove.dataset.user)}`, { method: 'DELETE' });
                if (!res.ok) throw new Error((await res.json()).error || 'Could not remove them');
            } else if (role) {
                const res = await apiFetch(`/api/org/members/${encodeURIComponent(role.dataset.user)}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ role: role.dataset.role }),
                });
                if (!res.ok) throw new Error((await res.json()).error || 'Could not change their role');
            } else if (revoke) {
                const res = await apiFetch(`/api/org/invites/${encodeURIComponent(revoke.dataset.invite)}`, { method: 'DELETE' });
                if (!res.ok) throw new Error((await res.json()).error || 'Could not revoke that invitation');
            } else if (leave) {
                const ok = await showConfirm({
                    title: 'Leave this organisation?',
                    message: 'Everything you’ve made stays yours, but you’ll drop back to the free plan unless you have your own subscription. Someone will have to invite you again to come back.',
                    confirmLabel: 'Leave',
                    danger: true,
                });
                if (!ok) return;
                const res = await apiFetch('/api/org/members/me', { method: 'DELETE' });
                if (!res.ok) throw new Error((await res.json()).error || 'Could not leave');
                // The nav item and the alert bar both hang off state.user.org,
                // and there is no org page left to re-render – so clear it here
                // rather than re-fetching a page this account can no longer see.
                showToast('You’ve left the organisation');
                state.user.org = null;
                syncAdminBtn();
                hide($('org-alert-bar'));
                return void showPresList();
            } else if (portal) {
                const res = await apiFetch('/api/org/portal', { method: 'POST' });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Could not open billing');
                window.location.href = data.url;
                return;
            } else if (seats) {
                const newSeats = Number($('org-seat-count').value);
                seats.disabled = true;

                // Priced first, confirmed second: the seat count alone doesn't
                // say what it costs. An organisation that started on a 100%-off
                // promo has nothing to credit back when it adds seats, so an
                // increase can land at full undiscounted price with no offset
                // – correct Stripe proration, but not something to find out
                // from a card statement. Showing the real number here is the
                // only fix that works regardless of the exact discount.
                let preview;
                try {
                    const pRes = await apiFetch('/api/org/seats/preview', {
                        method: 'POST',
                        body: JSON.stringify({ seats: newSeats }),
                    });
                    preview = await pRes.json();
                    if (!pRes.ok) throw new Error(preview.error || 'Could not price that change');
                } catch (err) {
                    seats.disabled = false;
                    if (err.message !== 'Not authenticated') setError(orgPanel.error, err.message || 'Could not price that change');
                    return;
                }

                if (preview.amountDueCents > 0) {
                    const ok = await showConfirm({
                        title: 'Charge your card now?',
                        message: `Moving to ${newSeats} seats charges ${money(preview.amountDueCents, preview.currency)} today.`,
                        confirmLabel: `Pay ${money(preview.amountDueCents, preview.currency)}`,
                    });
                    if (!ok) { seats.disabled = false; return; }
                }

                const res = await apiFetch('/api/org/seats', {
                    method: 'POST',
                    body: JSON.stringify({ seats: newSeats }),
                });
                const data = await res.json();
                seats.disabled = false;
                if (!res.ok) throw new Error(data.error || 'Could not change your seats');
                // A seat increase can need 3DS. The yearly switch already owns
                // that dance; rather than half-implement it here, hand the
                // customer to the billing portal, where the invoice is payable.
                if (data.requires_action) {
                    setError(orgPanel.error, 'Your bank needs to confirm that payment. Open “Manage billing” and pay the outstanding invoice – your seats change the moment it clears.');
                    return;
                }
                showToast('Seats updated');
            }
            reloadOrg();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(orgPanel.error, err.message || 'Something went wrong');
        }
    });

    onOrgPanel('submit', async (e) => {
        // Finishing a checkout that was closed part-way. Same form and same
        // handler as the billing page's, so there is one definition of what
        // buying seats does.
        const buy = e.target.closest('.org-buy-form');
        if (buy) {
            e.preventDefault();
            return startOrgCheckout(buy, orgPanel.error);
        }
        // Starting an ended subscription again: straight to Stripe's checkout.
        const restart = e.target.closest('#org-restart-form');
        if (restart) {
            e.preventDefault();
            const btn = restart.querySelector('button[type="submit"]');
            if (btn.disabled) return;
            btn.disabled = true;
            setError(orgPanel.error, '');
            try {
                const res = await apiFetch('/api/org/restart', { method: 'POST', body: JSON.stringify({ interval: restart.elements.interval.value }) });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.error || 'Could not start checkout');
                window.location.href = data.url;
            } catch (err) {
                btn.disabled = false;
                if (err.message !== 'Not authenticated') setError(orgPanel.error, err.message || 'Could not start checkout');
            }
            return;
        }

        const form = e.target.closest('#org-invite-form');
        if (!form) return;
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        if (btn?.disabled) return;
        setError(orgPanel.error, '');
        btn.disabled = true;
        try {
            const res = await apiFetch('/api/org/invites', {
                method: 'POST',
                body: JSON.stringify({ email: $('org-invite-email').value.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Could not send that invitation');
            showToast('Invitation sent');
            reloadOrg();
        } catch (err) {
            btn.disabled = false;
            if (err.message !== 'Not authenticated') setError(orgPanel.error, err.message || 'Could not send that invitation');
        }
    });

    onOrgPanel('change', (e) => {
        const file = e.target.closest('#org-bulk-file');
        if (!file || !file.files?.[0]) return;
        // Read it here and post the text: a staff list is a few kilobytes, and
        // this keeps the endpoint a plain JSON one with no upload handling.
        const reader = new FileReader();
        reader.onload = () => { $('org-bulk-text').value = String(reader.result || ''); };
        reader.readAsText(file.files[0]);
    });

    onOrgPanel('click', async (e) => {
        const send = e.target.closest('#org-bulk-send');
        if (!send) return;
        e.preventDefault();
        const csv = $('org-bulk-text').value.trim();
        if (!csv) return setError(orgPanel.error, 'Paste a list, or choose a file');
        setError(orgPanel.error, '');
        send.disabled = true;
        try {
            const res = await apiFetch('/api/org/invites/bulk', { method: 'POST', body: JSON.stringify({ csv }) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Could not send those invitations');
            // Re-render first, then paint the report into the fresh panel: the
            // seat counts above it have just changed.
            await reloadOrg();
            renderBulkReport(data);
        } catch (err) {
            send.disabled = false;
            if (err.message !== 'Not authenticated') setError(orgPanel.error, err.message || 'Could not send those invitations');
        }
    });

    btnOrg?.addEventListener('click', () => { hide(userMenu); showOrg(); });
    // The dropdown has to close first, or it sits over the dialog it opened.
    $('btn-pilot')?.addEventListener('click', () => {
        hide(userMenu);
        window.openPilotModal?.('app');
    });

    // ── Accepting an invitation (/join?token=…) ───────────────
    // Reachable with no account and no session at all, so it renders its own
    // card rather than anything inside the signed-in shell.

    // A dead end, laid out as one: an icon that says what kind of problem this
    // is, then what to do about it, then a single way out. Shared by the org
    // seat invitation and the individual one.
    function joinState({ tone, icon, title, text, hint, button, id }) {
        return `
            <div class="join-state">
                <span class="join-state-icon tone-${esc(tone)}" aria-hidden="true">${icon}</span>
                <h1 class="join-state-title">${title}</h1>
                <p class="join-state-text">${text}</p>
                ${hint ? `<p class="join-state-hint">${hint}</p>` : ''}
                <button class="btn btn-primary join-state-btn" id="${id}">${button}</button>
            </div>`;
    }

    // Unknown, expired and already-accepted tokens all look the same from the
    // client – an accepted invite is deleted – so one state covers both kinds
    // of invitation.
    function joinExpiredState() {
        return {
            tone: 'amber',
            icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
            title: 'This invitation has expired',
            text: 'Invitations last two weeks. Ask the person who invited you to send a new one &ndash; it only takes them a moment.',
            hint: 'Already accepted it? You don&rsquo;t need the link any more &ndash; just sign in as usual.',
            button: 'Go to BA Productivity',
            id: 'join-home',
        };
    }

    // The card for an individual invitation to a Pro account. The same order as
    // the email that brought them here (userInviteHtml in utils/mailer.js): a
    // tile, what they get, which address it is for, and one thing to do next.
    function userJoinInviteHtml(info, wrongAccount) {
        const words = [info.firstName, info.lastName].filter(Boolean)
            .join(' ').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
        const initials = (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '').slice(0, 2)).toUpperCase() || 'BA';
        const until = info.accessUntil
            ? new Date(info.accessUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
            : '';
        const sub = until
            ? `Your account comes with <strong>Pro access until ${esc(until)}</strong> &ndash; already paid for, so there&rsquo;s no card needed.`
            : 'Your account comes with Pro access, already paid for.';
        return `
            <div class="join-invite">
                <span class="join-invite-mark" aria-hidden="true">${esc(initials)}</span>
                <p class="join-invite-eyebrow">You&rsquo;re invited</p>
                <h1 class="join-state-title">BA Productivity</h1>
                <p class="join-state-text">${sub}</p>
                <p class="join-invite-for">Invitation for <strong>${esc(info.email)}</strong></p>
                <div id="join-error" class="error" role="alert" hidden></div>
                ${wrongAccount ? `
                    <p class="join-state-hint">You&rsquo;re signed in as <strong>${esc(info.signedInAs)}</strong>, which isn&rsquo;t the address this was sent to.</p>
                    <button class="btn btn-primary join-state-btn" id="join-signin">Switch account to accept</button>
                ` : info.accountExists ? `
                    <button class="btn btn-primary join-state-btn" id="join-accept">${info.signedInAs ? 'Accept invitation' : 'Sign in to accept'}</button>
                ` : `
                    <form id="join-form" class="join-form">
                        <label>Choose a password <input id="join-password" type="password" minlength="8" required autocomplete="new-password"></label>
                        <button type="submit" class="btn btn-primary join-state-btn">Create account and accept</button>
                    </form>`}
            </div>`;
    }

    /**
     * An invitation that can be accepted: whose organisation, what accepting
     * costs (nothing), which address it is for, and the one thing to do next.
     * The same order as the email that brought them here (orgInviteHtml in
     * utils/mailer.js), down to the organisation's initials in a tile.
     */
    function joinInviteHtml(info, wrongAccount) {
        const words = String(info.orgName || '').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
        const initials = (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '').slice(0, 2)).toUpperCase();
        const admin = info.role === 'admin';
        const sub = info.freeWeek
            ? (admin ? 'Your free week starts when you accept. No card needed.' : 'There&rsquo;s nothing to pay and no card needed.')
            : 'Your place is already paid for, so there&rsquo;s no card needed.';
        return `
            <div class="join-invite">
                <span class="join-invite-mark" aria-hidden="true">${esc(initials)}</span>
                <p class="join-invite-eyebrow">You&rsquo;re invited to ${admin ? 'run' : 'join'}</p>
                <h1 class="join-state-title">${esc(info.orgName)}</h1>
                <p class="join-state-text">${sub}</p>
                <p class="join-invite-for">Invitation for <strong>${esc(info.email)}</strong></p>
                <div id="join-error" class="error" role="alert" hidden></div>
                ${wrongAccount ? `
                    <p class="join-state-hint">You&rsquo;re signed in as <strong>${esc(info.signedInAs)}</strong>, which isn&rsquo;t the address this was sent to.</p>
                    <button class="btn btn-primary join-state-btn" id="join-signin">Switch account to accept</button>
                ` : info.accountExists ? `
                    <button class="btn btn-primary join-state-btn" id="join-accept">${info.signedInAs ? 'Accept invitation' : 'Sign in to accept'}</button>
                ` : `
                    <form id="join-form" class="join-form">
                        <div class="join-form-names">
                            <label>First name <input id="join-first" type="text" maxlength="40" autocomplete="given-name"></label>
                            <label>Last name <input id="join-last" type="text" maxlength="40" autocomplete="family-name"></label>
                        </div>
                        <label>Choose a password <input id="join-password" type="password" minlength="8" required autocomplete="new-password"></label>
                        <button type="submit" class="btn btn-primary join-state-btn">Create account and accept</button>
                    </form>`}
            </div>`;
    }

    async function showJoin(token) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(joinView);
        const body = $('join-body');
        body.innerHTML = '<p class="billing-loading" role="status">Checking your invitation&hellip;</p>';

        let info;
        try {
            const res = await fetch(`/api/org-invite/${encodeURIComponent(token)}`);
            info = await res.json();
        } catch {
            body.innerHTML = joinState({
                tone: 'blue',
                icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg>',
                title: 'We couldn’t check that link',
                text: 'Something went wrong on our side. Your invitation is probably fine &ndash; try again in a moment.',
                button: 'Try again',
                id: 'join-retry',
            });
            $('join-retry').addEventListener('click', () => showJoin(token));
            return;
        }

        // Unknown, expired and already-accepted tokens all look the same from
        // here – an accepted invite is deleted – so the copy covers both.
        if (!info.valid) {
            body.innerHTML = joinState(joinExpiredState());
            $('join-home').addEventListener('click', () => { window.location.href = '/'; });
            return;
        }

        // Signed in as someone else: say whose invitation it is rather than
        // silently attaching it to whoever happens to be logged in.
        const wrongAccount = info.accountExists && info.signedInAs && info.signedInAs.toLowerCase() !== info.email;

        body.innerHTML = joinInviteHtml(info, wrongAccount);

        $('join-home')?.addEventListener('click', () => { history.replaceState({}, '', '/'); window.location.reload(); });
        // Signing in on the way to accepting: the sign-in form, with the
        // invited address already in it. The page's address stays
        // /join?token=…, which is what brings them back here once they are in
        // (see the login form's handler). `signOutFirst` is for someone signed
        // in as the wrong person: left signed in, the server would go on
        // seeing the old account whatever the page showed.
        const signInToAccept = async (signOutFirst) => {
            history.replaceState({}, '', `/join?token=${encodeURIComponent(token)}`);
            if (signOutFirst) await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
            showAuth();
            showAuthModal('login');
            if (loginForm) {
                loginForm.email.value = info.email;
                loginForm.password.focus();
            }
        };
        $('join-signin')?.addEventListener('click', () => signInToAccept(true));

        const accept = async (payload) => {
            setError($('join-error'), '');
            try {
                const res = await apiFetch(`/api/org-invite/${encodeURIComponent(token)}/accept`, {
                    method: 'POST',
                    body: JSON.stringify(payload || {}),
                });
                const data = await res.json();
                if (!res.ok) {
                    if (data.requiresSignIn) {
                        setError($('join-error'), data.error);
                        return signInToAccept(false);
                    }
                    throw new Error(data.error || 'Could not accept that invitation');
                }
                state.user = data;
                syncAdminBtn();
                await loadSignedInScripts();
                history.replaceState({}, '', '/');
                showPresList();
                showToast(info.freeWeek
                    ? `You’re in – welcome to ${data.org.name}`
                    : `You’re in – ${data.org.name} is covering your Pro plan`, 4000);
            } catch (err) {
                if (err.message !== 'Not authenticated') setError($('join-error'), err.message || 'Could not accept that invitation');
            }
        };

        $('join-accept')?.addEventListener('click', () => (info.signedInAs ? accept() : signInToAccept(false)));
        $('join-form')?.addEventListener('submit', (e) => {
            e.preventDefault();
            accept({
                password: $('join-password').value,
                firstName: $('join-first').value.trim(),
                lastName: $('join-last').value.trim(),
            });
        });
    }

    /**
     * Accepting an individual invitation (/join?invite=…) – the admin panel's
     * People-screen invite, which carries Pro access.
     * Same shape as showJoin, but there is no organisation and the name comes
     * from the invitation, so a new account only has to choose a password.
     */
    async function showUserJoin(token) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(joinView);
        const body = $('join-body');
        body.innerHTML = '<p class="billing-loading" role="status">Checking your invitation&hellip;</p>';

        let info;
        try {
            const res = await fetch(`/api/user-invite/${encodeURIComponent(token)}`);
            info = await res.json();
        } catch {
            body.innerHTML = joinState({
                tone: 'blue',
                icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg>',
                title: 'We couldn’t check that link',
                text: 'Something went wrong on our side. Your invitation is probably fine &ndash; try again in a moment.',
                button: 'Try again',
                id: 'join-retry',
            });
            $('join-retry').addEventListener('click', () => showUserJoin(token));
            return;
        }

        if (!info.valid) {
            body.innerHTML = joinState(joinExpiredState());
            $('join-home').addEventListener('click', () => { window.location.href = '/'; });
            return;
        }

        const wrongAccount = info.accountExists && info.signedInAs && info.signedInAs.toLowerCase() !== info.email;
        body.innerHTML = userJoinInviteHtml(info, wrongAccount);

        // Signs in on the way to accepting, with the invited address already in
        // the form. The address stays /join?invite=…, which is what brings them
        // back here once they are in (see the login form's handler).
        const signInToAccept = async (signOutFirst) => {
            history.replaceState({}, '', `/join?invite=${encodeURIComponent(token)}`);
            if (signOutFirst) await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
            showAuth();
            showAuthModal('login');
            if (loginForm) {
                loginForm.email.value = info.email;
                loginForm.password.focus();
            }
        };
        $('join-signin')?.addEventListener('click', () => signInToAccept(true));

        const accept = async (payload) => {
            setError($('join-error'), '');
            try {
                const res = await apiFetch(`/api/user-invite/${encodeURIComponent(token)}/accept`, {
                    method: 'POST',
                    body: JSON.stringify(payload || {}),
                });
                const data = await res.json();
                if (!res.ok) {
                    if (data.requiresSignIn) {
                        setError($('join-error'), data.error);
                        return signInToAccept(false);
                    }
                    throw new Error(data.error || 'Could not accept that invitation');
                }
                state.user = data;
                syncAdminBtn();
                await loadSignedInScripts();
                history.replaceState({}, '', '/');
                showPresList();
                const until = info.accessUntil
                    ? new Date(info.accessUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                    : '';
                showToast(until
                    ? `You’re in – your Pro access runs until ${until}`
                    : 'You’re in – welcome to BA Productivity', 4000);
            } catch (err) {
                if (err.message !== 'Not authenticated') setError($('join-error'), err.message || 'Could not accept that invitation');
            }
        };

        $('join-accept')?.addEventListener('click', () => (info.signedInAs ? accept() : signInToAccept(false)));
        $('join-form')?.addEventListener('submit', (e) => {
            e.preventDefault();
            accept({ password: $('join-password').value });
        });
    }


    btnBilling?.addEventListener('click', () => { hide(userMenu); showBilling(); });
    btnUpgrade?.addEventListener('click', () => showBilling());
    // The console is a page of its own now (public/admin.html), so this is a
    // real navigation rather than a view swap. Nothing about it is decided
    // here: the page asks /api/admin whether this account may see it.
    btnAdmin?.addEventListener('click', () => { window.location.href = '/admin'; });

    // ── Quick-access panel ────────────────────────────────
    document.querySelector('.quick-access')?.addEventListener('click', (e) => {
        const card = e.target.closest('.qa-card');
        if (!card) return;
        if (card.dataset.action === 'presentation') showGenerator();
        else if (card.dataset.action === 'worksheet') showWorksheetPage('', 'list');
        else if (card.dataset.action === 'quiz') showQuizPage('', 'list');
        else if (card.dataset.action === 'marking') showMarkingPage();
        else if (card.dataset.action === 'booklet') showBookletPage();
        // Same call the create menu makes: showBookletPage does its own plan
        // check, so both doors in are gated by the one place rather than by a
        // check per entry point.
    });

    // ── Theme picker ──────────────────────────────────────
    themePicker?.addEventListener('click', (e) => {
        const btn = e.target.closest('.theme-btn');
        if (!btn) return;
        state.selectedTheme = btn.dataset.theme;
        // A choice made here stands, whatever the level is changed to after.
        state.themeChosen = true;
        applyTheme(state.selectedTheme);
        syncThemePickers(state.selectedTheme);
    });

    // In-editor theme switcher – changes the theme of an already-created presentation
    editorThemePicker?.addEventListener('click', async (e) => {
        const btn = e.target.closest('.theme-btn');
        if (!btn) return;
        const theme = btn.dataset.theme;
        state.selectedTheme = theme;
        applyTheme(theme);
        syncThemePickers(theme);

        if (state.presentationId) {
            try {
                await apiFetch(`/api/presentations/${state.presentationId}/theme`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ theme }),
                });
            } catch (err) {
                if (err.message !== 'Not authenticated') setError(stageError, 'Could not save theme change');
            }
        }
    });

    // ── Slide count ───────────────────────────────────────
    // A row of ceilings, 6 to 30 and then No limit. Every option is a maximum
    // the planner may stop short of, never a count it has to reach: an exact
    // count made it pad a twelve-slide topic out to thirty. Each plan starts on
    // its highest option – No limit on Pro, 10 on Free, which is what the
    // server holds a free Auto deck to anyway – so the only reason to change it
    // is a shorter deck.
    const slideCountOptions = $('slide-count-options');
    const SLIDE_AUTO = 'auto';
    const SLIDE_FREE_CAP = 10;    // Free plans cannot take a deck over this
    const SLIDE_FIT_SCALE = 32;   // the fit bar's right-hand end, in slides
    // { key, estimate, topics } from /length-estimate, for the fit bar.
    let slideCountEstimate = null;
    const genWizardHints = [
        'A topic, your own files, or both.',
        'Generating uses one of your monthly presentation allowances.',
    ];

    // Unknown until /api/auth/me answers; treated as Pro until then so a paying
    // user never sees the Pro marking flash on.
    const slideCountIsFree = () => !!state.user && state.user.plan !== 'pro';
    const slideCountGate = () => ({
        error: 'pro_required',
        message: `Pro presentations go up to 40 slides. On the free plan you can generate up to ${SLIDE_FREE_CAP}.`,
    });
    // On Free, Auto and "up to 10" are the same deck, so either lights the 10.
    function slideCountActiveCap() {
        if (state.slideCount === SLIDE_AUTO) return slideCountIsFree() ? String(SLIDE_FREE_CAP) : SLIDE_AUTO;
        return String(state.slideCount);
    }
    const slideCapLocked = (cap) => slideCountIsFree() && (cap === SLIDE_AUTO || Number(cap) > SLIDE_FREE_CAP);

    function syncSlideCountOptions() {
        const active = slideCountActiveCap();
        slideCountOptions?.querySelectorAll('.ws-length-opt').forEach((b) => {
            const on = b.dataset.cap === active;
            const locked = slideCapLocked(b.dataset.cap);
            b.classList.toggle('active', on);
            b.classList.toggle('pro-only', locked);
            b.setAttribute('aria-checked', String(on));
            b.tabIndex = on ? 0 : -1;
            const tag = b.querySelector('.ws-length-pro');
            if (locked && !tag) b.insertAdjacentHTML('afterbegin', '<span class="ws-length-pro" aria-hidden="true">PRO</span>');
            else if (!locked && tag) tag.remove();
        });
        syncSlideCountFit();
    }

    // The fit bar: green for what the topic is likely to need, the blue mark
    // for the chosen ceiling. A ceiling below the need cuts the green short.
    function syncSlideCountFit() {
        const fit = $('slide-count-fit');
        if (!fit) return;
        const need = slideCountEstimate?.estimate;
        fit.hidden = !need;
        if (!need) return;
        const active = slideCountActiveCap();
        const cap = active === SLIDE_AUTO ? null : Number(active);
        const pct = (n) => `${Math.min(100, (n / SLIDE_FIT_SCALE) * 100).toFixed(2)}%`;
        $('slide-count-need').style.width = pct(cap ? Math.min(need, cap) : need);
        $('slide-count-cap').style.left = cap ? `calc(${pct(cap)} - 1px)` : 'calc(100% - 2px)';
        const topics = slideCountEstimate.topics;
        $('slide-count-fit-label').textContent = topics > 1 ? `${topics} topics need` : 'Topic needs';
        $('slide-count-fit-value').textContent = `~${need}`;
    }

    // Asked again only when what the estimate depends on has changed. Runs
    // from the observer below, never at load: the topic picker is declared
    // further down this file.
    let slideCountEstimateSeq = 0;
    async function refreshSlideCountEstimate() {
        const params = new URLSearchParams({
            examBoard: $('exam-board')?.value || '',
            subject: $('subject')?.value || '',
            level: $('level')?.value || '',
            tier: $('tier')?.value || '',
        });
        const { topics } = topicPartsFor(topicInput, genTopicPicker);
        if (topics?.length) params.set('topics', topics.join(','));
        const key = params.toString();
        if (slideCountEstimate?.key === key) return;
        const seq = ++slideCountEstimateSeq;
        try {
            const res = await apiFetch(`/api/presentations/length-estimate?${key}`);
            if (!res.ok || seq !== slideCountEstimateSeq) return;
            const data = await res.json();
            slideCountEstimate = { key, estimate: data.estimate, topics: data.topics };
        } catch { return; /* the bar stays hidden */ }
        syncSlideCountFit();
        updateSlideCountHelp();
    }

    function slideCountHelpText() {
        const active = slideCountActiveCap();
        const two = ' A deck that comes out over 20 slides uses two of your monthly presentations.';
        if (active === SLIDE_AUTO) return `The AI uses as many slides as the topic needs.${two}`;
        const n = Number(active);
        const need = slideCountEstimate?.estimate;
        return `The AI uses as many as it needs, up to ${n}.`
            + (need && n < need
                ? (slideCountEstimate.topics > 1 ? ' Each topic gets a shorter section to fit.' : ' It may cut some practice to fit.')
                : '')
            + (n > 20 ? two : '');
    }

    function slideCountAllowanceText() {
        // A ceiling over 20 may or may not produce a deck over 20; the plan
        // decides, and the charge follows what it decides.
        const active = slideCountActiveCap();
        return active === SLIDE_AUTO || Number(active) > 20
            ? 'Generating uses one of your monthly presentation allowances, or two if the deck comes out over 20 slides.'
            : 'Generating uses one of your monthly presentation allowances.';
    }

    function updateSlideCountHelp() {
        const helpText = $('slide-count-help-text');
        const counter = $('slide-count-counter');
        if (helpText) helpText.textContent = slideCountHelpText();
        // A free account is told where longer decks are, and offered the way there.
        const upgrade = $('slide-count-upgrade');
        if (upgrade) upgrade.hidden = !slideCountIsFree();
        const allowanceText = slideCountAllowanceText();
        genWizardHints[1] = allowanceText;
        const wizardHint = document.querySelector('#generate-form [data-wiz-hint]');
        if (wizardHint) wizardHint.textContent = allowanceText;
        if (counter) {
            const active = slideCountActiveCap();
            counter.textContent = active === SLIDE_AUTO ? 'No limit' : `${active} slides`;
        }
    }

    function setSlideCount(cap) {
        state.slideCount = cap;
        syncSlideCountOptions();
        updateSlideCountHelp();
    }

    $('slide-count-upgrade')?.addEventListener('click', () => showQuotaModal(slideCountGate()));
    slideCountOptions?.addEventListener('click', (e) => {
        const btn = e.target.closest('.ws-length-opt');
        if (!btn) return;
        // A Pro length on the free plan opens the upgrade dialog, the same one
        // as the link, and leaves the choice where it was.
        if (slideCapLocked(btn.dataset.cap)) return showQuotaModal(slideCountGate());
        setSlideCount(btn.dataset.cap);
    });
    // Arrow keys move along the row, as in any radio group.
    slideCountOptions?.addEventListener('keydown', (e) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        const open = [...slideCountOptions.querySelectorAll('.ws-length-opt')].filter((b) => !slideCapLocked(b.dataset.cap));
        const at = open.findIndex((b) => b.dataset.cap === slideCountActiveCap());
        const next = open[Math.max(0, Math.min(open.length - 1, at + step))];
        if (next) { setSlideCount(next.dataset.cap); next.focus(); }
    });
    syncSlideCountOptions();
    updateSlideCountHelp();
    // The plan and the topics arrive after this runs, from more than one
    // place, so the row redraws its Pro marking and asks for the estimate
    // whenever it comes into view instead.
    if (slideCountOptions && 'IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            syncSlideCountOptions();
            updateSlideCountHelp();
            refreshSlideCountEstimate();
        }).observe(slideCountOptions);
    }

    // ── Generate form ─────────────────────────────────────
    // The panel's height is driven by the shared .t-acc grid track, which reads
    // data-open – a class alone leaves it shut.
    function setGenContextOpen(open) { setSourcePanel('input-content', 'notes', open); }

    // ── Composer steps ────────────────────────────────────
    // What it is about → look and length.
    //
    // The first step holds everything that decides the content: the topic, the
    // teacher's own material, the curriculum, and the specification's topic
    // list where one is held. The curriculum is a row of three under the box
    // rather than a step of its own, because it is optional and was the
    // tallest screen in the flow; the checklist opens from the box, so ticking
    // topics is one more way of saying what the deck is about, beside typing
    // it or attaching it.
    const genWizard = initComposerWizard({
        formId: 'generate-form',
        submitId: 'generate-btn',
        nextLabels: ['Next: look and length →'],
        hints: genWizardHints,
        isReady: () => {
            if (!topicLineFor(topicInput, genTopicPicker)) {
                setError(generateError, 'Type a topic, add a file, or tick topics from the specification');
                topicInput?.focus();
                return false;
            }
            setError(generateError, '');
            return true;
        },
    });

    // ── Subject combobox ──────────────────────────────────
    // Moved to /js/subjects.js so the server can hold the same list: the publish
    // endpoint is a public API, and a client posting straight to it must not be
    // able to reintroduce the free-text drift the pickers exist to prevent.
    //
    // Defaulted rather than destructured straight off the global. This runs at
    // setup, so if that script ever fails to load, destructuring `undefined`
    // would throw here and take the whole app down – over a subject list. The
    // empty lists degrade to pickers with nothing in them, which is bad but is
    // not a white screen, and the console says why.
    if (!window.Subjects) console.error('[app] /js/subjects.js did not load – subject pickers will be empty');
    const Vocab = window.Subjects || { SUBJECT_GROUPS: [], LEVELS: [], BOARDS: [] };
    const SUBJECT_GROUPS = Vocab.SUBJECT_GROUPS;
    const MKT_LEVELS = Vocab.LEVELS;
    const MKT_BOARDS = Vocab.BOARDS;

    function initSubjectCombobox({ inputId, hiddenId, dropId, clearId, badgeId }) {
        const inputEl  = $(inputId);
        const hiddenEl = $(hiddenId);
        const dropEl   = $(dropId);
        const clearBtn = clearId ? $(clearId) : null;
        const badge    = badgeId ? $(badgeId) : null;
        if (!inputEl || !hiddenEl || !dropEl) return {};

        let hlIndex = -1;
        let autoDetected = false;
        // Narrows the list to the subjects a selection can actually be built
        // for — set by the National Curriculum coupling, null the rest of the
        // time. It filters what is OFFERED, never what is stored: the hidden
        // input stays the value of record, so a subject filtered out from
        // under a restored draft is still readable.
        let allowFilter = null;
        let emptyMessage = 'No subjects match';

        function renderOptions(query) {
            const q = query.toLowerCase().trim();
            dropEl.innerHTML = '';
            hlIndex = -1;
            let anyMatch = false;

            SUBJECT_GROUPS.forEach(({ group, subjects }) => {
                const allowed = allowFilter ? subjects.filter(allowFilter) : subjects;
                const matches = q ? allowed.filter(s => s.toLowerCase().includes(q)) : allowed;
                if (!matches.length) return;
                anyMatch = true;

                const gh = document.createElement('div');
                gh.className = 'sc-group';
                gh.textContent = group;
                dropEl.appendChild(gh);

                matches.forEach(subject => {
                    const opt = document.createElement('div');
                    opt.className = 'sc-option' + (hiddenEl.value === subject ? ' sc-option--selected' : '');
                    opt.dataset.val = subject;
                    if (q) {
                        const i = subject.toLowerCase().indexOf(q);
                        opt.innerHTML = esc(subject.slice(0, i)) + '<mark>' + esc(subject.slice(i, i + q.length)) + '</mark>' + esc(subject.slice(i + q.length));
                    } else {
                        opt.textContent = subject;
                    }
                    opt.addEventListener('mousedown', e => { e.preventDefault(); pickSubject(subject, false); });
                    dropEl.appendChild(opt);
                });
            });

            if (!anyMatch) {
                const empty = document.createElement('div');
                empty.className = 'sc-empty';
                empty.textContent = q ? 'No subjects match' : emptyMessage;
                dropEl.appendChild(empty);
            }
        }

        function openDrop() {
            show(dropEl);
            renderOptions(inputEl.value);
        }

        function closeDrop() {
            hide(dropEl);
            inputEl.value = hiddenEl.value;
            hlIndex = -1;
        }

        function pickSubject(value, isAuto) {
            hiddenEl.value = value;
            inputEl.value  = value;
            hide(dropEl);
            autoDetected = isAuto;
            if (badge) badge.dataset.open = String(isAuto);
            if (clearBtn) clearBtn.hidden = !value;
            clearMotion.sync(inputEl.closest('.t-clear'));
            // The hidden input is the field everything else reads, but assigning
            // .value fires nothing — anything that needs to react to the subject
            // changing (the spec-coverage indicator) has no other signal.
            hiddenEl.dispatchEvent(new Event('change', { bubbles: true }));
        }

        function moveHl(dir) {
            const opts = dropEl.querySelectorAll('.sc-option');
            if (!opts.length) return;
            opts[hlIndex]?.classList.remove('sc-option--hl');
            hlIndex = Math.max(0, Math.min(hlIndex + dir, opts.length - 1));
            opts[hlIndex].classList.add('sc-option--hl');
            opts[hlIndex].scrollIntoView({ block: 'nearest' });
        }

        inputEl.addEventListener('focus', openDrop);
        inputEl.addEventListener('blur', () => setTimeout(closeDrop, 180));
        inputEl.addEventListener('input', () => renderOptions(inputEl.value));
        inputEl.addEventListener('keydown', e => {
            if (dropEl.hidden) { openDrop(); return; }
            if (e.key === 'ArrowDown')  { e.preventDefault(); moveHl(+1); }
            else if (e.key === 'ArrowUp')   { e.preventDefault(); moveHl(-1); }
            else if (e.key === 'Enter') {
                e.preventDefault();
                const hl = dropEl.querySelectorAll('.sc-option')[hlIndex];
                if (hl) pickSubject(hl.dataset.val, false);
            } else if (e.key === 'Escape') { e.preventDefault(); closeDrop(); inputEl.blur(); }
        });

        // Pointerdown would blur the input before the click lands; keeping
        // focus is what lets the dissolve hand the caret straight back.
        const keepClearFocus = (e) => { if (document.activeElement === inputEl) e.preventDefault(); };
        clearBtn?.addEventListener('pointerdown', keepClearFocus);
        clearBtn?.addEventListener('mousedown', keepClearFocus);
        clearBtn?.addEventListener('click', () => {
            clearMotion.run(inputEl.closest('.t-clear'), () => {
                pickSubject('', false);
                inputEl.focus();
            });
        });
        inputEl.addEventListener('input', () => clearMotion.sync(inputEl.closest('.t-clear')));

        return {
            setAuto: (value) => pickSubject(value, true),
            set:     (value) => pickSubject(value, false),
            getValue: () => hiddenEl.value,
            isAutoDetected: () => autoDetected,
            /**
             * Restrict which subjects are offered. `fn` is a predicate over
             * subject names, or null to offer everything again. `emptyText`
             * is what an empty list says — "No subjects match" is wrong when
             * the list is empty because of the filter rather than the query.
             * Returns whether the CURRENT value survives the filter, so the
             * caller can decide whether to clear it.
             */
            setFilter(fn, emptyText) {
                allowFilter = fn || null;
                emptyMessage = emptyText || 'No subjects match';
                if (!dropEl.hidden) renderOptions(inputEl.value);
                return !hiddenEl.value || !fn || fn(hiddenEl.value);
            },
        };
    }

    // boardId → the subject combobox for that composer, read by the National
    // Curriculum narrowing further down. Declared HERE, above the comboboxes
    // that fill it, rather than beside the code that reads it: `const` is not
    // hoisted into scope, so registering into it before this line throws
    // ReferenceError and takes the whole script down with it.
    const NC_SUBJECT_COMBOS = {};

    const subjectCombobox = initSubjectCombobox({
        inputId: 'subject-input', hiddenId: 'subject',
        dropId: 'sc-dropdown', clearId: 'subject-clear', badgeId: 'subject-auto-badge',
    });
    NC_SUBJECT_COMBOS['exam-board'] = subjectCombobox;
    // The badge is what tells a teacher the subject was filled in for them
    // rather than by them – without it, an auto-detected subject looks like
    // something they typed and forgot, and a wrong guess goes unchallenged.
    const wsSubjectCombobox = initSubjectCombobox({
        inputId: 'ws-subject-input', hiddenId: 'ws-subject',
        dropId: 'ws-sc-dropdown', clearId: 'ws-subject-clear',
        badgeId: 'ws-subject-auto-badge',
    });
    NC_SUBJECT_COMBOS['ws-exam-board'] = wsSubjectCombobox;
    const quizSubjectCombobox = initSubjectCombobox({
        inputId: 'quiz-subject-input', hiddenId: 'quiz-subject',
        dropId: 'quiz-sc-dropdown', clearId: 'quiz-subject-clear',
        badgeId: 'quiz-subject-auto-badge',
    });
    NC_SUBJECT_COMBOS['quiz-exam-board'] = quizSubjectCombobox;
    const srSubjectCombobox = initSubjectCombobox({
        inputId: 'sr-subject-input', hiddenId: 'sr-subject',
        dropId: 'sr-sc-dropdown', clearId: 'sr-subject-clear',
        badgeId: 'sr-subject-auto-badge',
    });
    NC_SUBJECT_COMBOS['sr-exam-board'] = srSubjectCombobox;
    const mkSubjectCombobox = initSubjectCombobox({
        inputId: 'mk-subject-input', hiddenId: 'mk-subject',
        dropId: 'mk-sc-dropdown', clearId: 'mk-subject-clear', badgeId: 'mk-subject-auto-badge',
    });
    // The publish dialog's subject picker. Same control and same list as the
    // composer's, so a deck published from the library is filed under a name
    // the catalogue already knows.
    const mktpubSubjectCombobox = initSubjectCombobox({
        inputId: 'mktpub-subject-input', hiddenId: 'mktpub-subject',
        dropId: 'mktpub-sc-dropdown', clearId: 'mktpub-subject-clear',
    });
    // ── Composer tiles over selects ───────────────────────
    //
    // Board, level, difficulty and length are drawn as tiles, but the <select>
    // each was built from stays in the DOM as the value the rest of the app
    // reads, saves and restores. Nothing else had to learn about the new
    // controls, and a value set in code still shows up in the UI because the
    // sync runs both ways.
    //
    // A host declares which select it mirrors and which artwork set it wants:
    //   <div class="ws-tiles" data-tiles-for="ws-level" data-tiles-art="level">
    // so the four composers share one table instead of one per field id.
    const WS_TILE_GLYPH = {
        globe:   '<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17"/><path d="M12 3.5c2.3 2.4 2.3 14.6 0 17c-2.3-2.4-2.3-14.6 0-17z"/>',
        rosette: '<circle cx="12" cy="9" r="5.2"/><path d="M8.7 13.4L7 20.5l5-2.4 5 2.4-1.7-7.1"/>',
        cap:     '<path d="M12 4.5l8.5 3.9-8.5 3.9-8.5-3.9z"/><path d="M7 10.6v3.8c0 1.4 2.2 2.6 5 2.6s5-1.2 5-2.6v-3.8"/>',
        // The National Curriculum is a document, not an awarding body — it gets
        // an open book rather than the boards' rosette.
        book:    '<path d="M12 6.7C10.4 5.1 7.9 4.6 5.3 4.6v13.2c2.6 0 5.1.5 6.7 2.1 1.6-1.6 4.1-2.1 6.7-2.1V4.6c-2.6 0-5.1.5-6.7 2.1z"/><path d="M12 6.7v13.2"/>',
        // Difficulty reads as a rising signal: same glyph, more bars.
        bars1:   '<path d="M6 17.5v-4"/><path d="M12 17.5v-4" opacity=".25"/><path d="M18 17.5v-4" opacity=".25"/>',
        bars2:   '<path d="M6 17.5v-4"/><path d="M12 17.5v-7.5"/><path d="M18 17.5v-4" opacity=".25"/>',
        bars3:   '<path d="M6 17.5v-4"/><path d="M12 17.5v-7.5"/><path d="M18 17.5v-11"/>',
        // Length reads as a filling page.
        page1:   '<rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M8.5 8h7"/>',
        page2:   '<rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M8.5 8h7M8.5 12h7"/>',
        page3:   '<rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M8.5 8h7M8.5 12h7M8.5 16h7"/>',
    };
    // The glyphs are decorative – every tile still carries its name in text –
    // but a board you recognise by its colour is one you stop reading eight
    // labels to find. Tones are the app's own accent family, not any board's
    // real branding.
    const WS_TILE_ART = {
        board: {
            '':                        ['globe',   'tone-slate'],
            'National Curriculum':     ['book',    'tone-teal'],
            'Curriculum for Excellence': ['book',  'tone-indigo'],
            'AQA':                     ['rosette', 'tone-blue'],
            'Edexcel':                 ['rosette', 'tone-purple'],
            'OCR':                     ['rosette', 'tone-amber'],
            'WJEC':                    ['rosette', 'tone-green'],
            'CCEA':                    ['rosette', 'tone-red'],
            'Cambridge International': ['rosette', 'tone-teal'],
            'IB':                      ['rosette', 'tone-indigo'],
            'ISEB':                    ['rosette', 'tone-red'],
            'ISEB Pre-Tests':          ['rosette', 'tone-red'],
            'SEAG':                    ['rosette', 'tone-purple'],
        },
        level: {
            '':         ['globe', 'tone-slate'],
            '11+':      ['cap',   'tone-red'],
            'GCSE':     ['cap',   'tone-green'],
            'IGCSE':    ['cap',   'tone-green'],
            'A-Level':  ['cap',   'tone-purple'],
            'IB SL':    ['cap',   'tone-blue'],
            'IB HL':    ['cap',   'tone-blue'],
            'BTEC L2':  ['cap',   'tone-amber'],
            'BTEC L3':  ['cap',   'tone-amber'],
            // Primary years share one tone so they read as a family beside
            // the qualification tiles; KS3 is its own bridge stage.
            'Year 1':   ['cap', 'tone-teal'],
            'Year 2':   ['cap', 'tone-teal'],
            'Year 3':   ['cap', 'tone-teal'],
            'Year 4':   ['cap', 'tone-teal'],
            'Year 5':   ['cap', 'tone-teal'],
            'Year 6':   ['cap', 'tone-teal'],
            'KS3':      ['cap', 'tone-indigo'],
            // Scotland's primary years, one family of their own.
            'P1':       ['cap', 'tone-blue'],
            'P2':       ['cap', 'tone-blue'],
            'P3':       ['cap', 'tone-blue'],
            'P4':       ['cap', 'tone-blue'],
            'P5':       ['cap', 'tone-blue'],
            'P6':       ['cap', 'tone-blue'],
            'P7':       ['cap', 'tone-blue'],
        },
        difficulty: {
            foundation: ['bars1', 'tone-green'],
            standard:   ['bars2', 'tone-blue'],
            higher:     ['bars3', 'tone-purple'],
        },
        length: {
            short:    ['page1', 'tone-slate'],
            standard: ['page2', 'tone-slate'],
            long:     ['page3', 'tone-slate'],
        },
    };

    function wsTileArt(artKind, value) {
        const art = WS_TILE_ART[artKind]?.[value];
        if (!art) return null;
        const [glyph, tone] = art;
        const span = document.createElement('span');
        span.className = `ws-tile-glyph ${tone}`;
        span.setAttribute('aria-hidden', 'true');
        // Constant markup from the table above – no user input reaches this.
        span.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${WS_TILE_GLYPH[glyph]}</svg>`;
        return span;
    }

    function buildWsTiles(host) {
        const selectId = host.dataset.tilesFor;
        const select = $(selectId);
        if (!select) return;
        const seg = host.classList.contains('ws-seg');
        host.innerHTML = '';

        const makeTile = (opt) => {
            // "Standard (~20 questions)" → a name and a quieter second line,
            // so the tile reads at a glance and still carries the detail.
            const m = opt.text.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
            const name = m ? m[1] : opt.text;
            const sub  = m ? m[2] : '';
            const label = document.createElement('label');
            label.className = seg ? 'ws-seg-opt' : 'ws-tile';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = `tiles-${selectId}`;
            input.value = opt.value;
            input.checked = opt.value === select.value;
            label.dataset.tileValue = opt.value;
            const nameEl = document.createElement('span');
            nameEl.className = 'ws-tile-name';
            nameEl.textContent = name;
            const glyph = wsTileArt(host.dataset.tilesArt, opt.value);
            label.append(input);
            if (glyph) label.append(glyph);
            label.append(nameEl);
            if (sub && host.dataset.segSub) {
                const subEl = document.createElement('span');
                subEl.className = 'ws-tile-sub';
                subEl.textContent = sub;
                label.append(subEl);
            }
            input.addEventListener('change', () => {
                select.value = input.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
            });
            return label;
        };

        // Walk children, not select.options: options is flat and drops
        // <optgroup> labels, which is how the primary years got added. A wall
        // of fifteen ungrouped level tiles reads as noise; the group headings
        // are what keep it scannable.
        Array.from(select.children).forEach(node => {
            if (node.tagName === 'OPTGROUP') {
                const groupName = node.label || '';
                // A collapsible group folds into one chip until it is wanted.
                // Seven year tiles beside seven exam levels is a wall that
                // makes the common case harder to read for the sake of the
                // uncommon one; the chip keeps the group one click away and
                // says how much is behind it.
                const collapsible = !seg && node.hasAttribute('data-collapsible');
                if (!seg) {
                    const head = document.createElement(collapsible ? 'button' : 'div');
                    head.className = collapsible ? 'ws-tiles-toggle' : 'ws-tiles-group';
                    // Named so the heading can be hidden with its tiles when a
                    // filter empties the group — a heading over nothing reads
                    // as a rendering fault.
                    head.dataset.tileGroup = groupName;
                    if (collapsible) {
                        head.type = 'button';
                        head.dataset.tileToggle = groupName;
                        head.setAttribute('aria-expanded', 'false');
                        head.innerHTML = '<span class="ws-tiles-toggle-chev" aria-hidden="true"></span>';
                        const txt = document.createElement('span');
                        txt.textContent = `${groupName} (${node.children.length})`;
                        head.append(txt);
                        head.addEventListener('click', () => {
                            setGroupExpanded(host, groupName, head.getAttribute('aria-expanded') !== 'true');
                        });
                    } else {
                        // Deliberately not aria-hidden: the radios stay flat to
                        // assistive tech, so this heading is the only grouping
                        // cue a screen reader gets.
                        head.textContent = groupName;
                    }
                    host.append(head);
                }
                Array.from(node.children).forEach(opt => {
                    const tile = makeTile(opt);
                    tile.dataset.tileGroup = groupName;
                    if (collapsible) tile.dataset.tileCollapsible = '1';
                    host.append(tile);
                });
            } else if (node.tagName === 'OPTION') {
                host.append(makeTile(node));
            }
        });
        // Collapsed unless the value already in the select lives inside the
        // group — a restored draft must never open on a picker that appears
        // to have nothing selected.
        Array.from(select.querySelectorAll('optgroup[data-collapsible]')).forEach(g => {
            const holdsValue = Array.from(g.children).some(o => o.value === select.value);
            setGroupExpanded(host, g.label || '', holdsValue);
        });
        // Programmatic writes to the select do not fire change, so the restore
        // paths call syncAllWsTiles() directly; this covers user-driven ones.
        select.addEventListener('change', () => syncWsTiles(host));
    }

    // Declared above its callers: `const` is not hoisted into scope, and the
    // group labels it escapes contain an ampersand.
    const cssEscape = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'));

    /**
     * Open or fold a collapsible tile group.
     *
     * Expansion is a display state, never a filter: `hidden` on a folded tile
     * is set here and cleared here, while the National Curriculum narrowing
     * uses a separate flag on the same tiles. Two writers of one `hidden`
     * attribute would fight, so applyTileVisibility() below is the only place
     * that actually resolves them.
     */
    function setGroupExpanded(host, groupName, expanded) {
        const toggle = host.querySelector(`.ws-tiles-toggle[data-tile-toggle="${cssEscape(groupName)}"]`);
        if (toggle) toggle.setAttribute('aria-expanded', String(!!expanded));
        host.dataset[`expanded${groupName.replace(/[^A-Za-z0-9]/g, '')}`] = String(!!expanded);
        applyTileVisibility(host);
    }

    /**
     * The one place a tile's `hidden` is decided.
     *
     * Two independent reasons hide a tile — its group is folded, or the chosen
     * board cannot serve it — and each was previously written straight to
     * `hidden`, so whichever ran last won and the other silently lost. Both are
     * now flags read here.
     */
    function applyTileVisibility(host) {
        host.querySelectorAll('.ws-tile').forEach(tile => {
            const group = tile.dataset.tileGroup || '';
            const folded = tile.dataset.tileCollapsible
                && host.dataset[`expanded${group.replace(/[^A-Za-z0-9]/g, '')}`] !== 'true';
            tile.hidden = !!folded || tile.dataset.tileFiltered === '1';
        });
        // A chip goes when there is nothing behind it to unfold, and also when
        // its group is the only thing left on screen: offering to fold away
        // every remaining option is a control whose one use is a dead end.
        host.querySelectorAll('.ws-tiles-toggle, .ws-tiles-group').forEach(head => {
            const group = head.dataset.tileGroup || '';
            const all = Array.from(host.querySelectorAll('.ws-tile'));
            const mine = all.filter(t => (t.dataset.tileGroup || '') === group);
            const othersLeft = all.some(t => (t.dataset.tileGroup || '') !== group
                && t.dataset.tileFiltered !== '1' && t.dataset.tileValue !== '');
            head.hidden = mine.length > 0
                && (mine.every(t => t.dataset.tileFiltered === '1') || !othersLeft);
        });
    }

    // ── Curriculum pickers ────────────────────────────────
    //
    // On the composers' first step, board and level are each one button that
    // shows the current choice and opens the tiles beneath it. The tiles and
    // the select behind them are untouched: this is only where they live, so
    // an optional question takes one row instead of a screen.
    function syncCurriculumPick(selectId) {
        const select = $(selectId);
        const btn = document.querySelector(`.cur-pick[data-cur-for="${selectId}"]`);
        if (!select || !btn) return;
        const opt = select.options[select.selectedIndex];
        const valueEl = btn.querySelector('.cur-pick-value');
        if (valueEl) valueEl.textContent = select.value ? (opt?.textContent || select.value) : (btn.dataset.curEmpty || 'Any');
        btn.classList.toggle('has-value', !!select.value);
    }

    function setCurriculumPanel(selectId, open) {
        const btn = document.querySelector(`.cur-pick[data-cur-for="${selectId}"]`);
        const panel = document.querySelector(`[data-cur-panel="${selectId}"]`);
        if (!btn || !panel) return;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', String(!!open));
    }

    document.querySelectorAll('.cur-pick[data-cur-for]').forEach((btn) => {
        const selectId = btn.dataset.curFor;
        btn.addEventListener('click', () => {
            const opening = btn.getAttribute('aria-expanded') !== 'true';
            // One open at a time: they share the space under the row.
            btn.closest('.cur')?.querySelectorAll('.cur-pick[data-cur-for]').forEach((other) => {
                setCurriculumPanel(other.dataset.curFor, other === btn && opening);
            });
        });
        syncCurriculumPick(selectId);
        // A pick is the end of the question, so the tiles fold away again.
        $(selectId)?.addEventListener('change', () => {
            syncCurriculumPick(selectId);
            setCurriculumPanel(selectId, false);
        });
    });

    function syncWsTiles(host) {
        const select = $(host.dataset.tilesFor);
        if (!select) return;
        syncCurriculumPick(host.dataset.tilesFor);
        host.querySelectorAll('input[type="radio"]').forEach(r => {
            r.checked = r.value === select.value;
        });
        // Also re-derives the primary board/level coupling, so restore paths
        // that write both selects and sync tiles land with the right tiles
        // disabled without firing any change events.
        refreshBoardTileCoupling();
    }

    const wsTileHosts = Array.from(document.querySelectorAll('[data-tiles-for]'));
    wsTileHosts.forEach(buildWsTiles);
    const syncAllWsTiles = () => wsTileHosts.forEach(syncWsTiles);

    // ── Primary board/level coupling ──────────────────────
    //
    // At Years 1–6 the National Curriculum is not one option among many — it
    // is the specification the level is defined by. So picking a primary year
    // sets the board select to it (and fires change, so coverage, hints and
    // tiles all follow), and while a primary level is set every other board
    // tile is disabled with a note saying why.
    //
    // The inverse is deliberately NOT rewritten: the teacher who somehow has
    // AQA selected at Year 3 keeps their selection visible-but-blocked rather
    // than having it silently swapped under them. The hidden <select> stays
    // the value of record either way — buildWsTiles and every restore path
    // read it, so it must never be removed or hidden.
    const NC_BOARD_VALUE = 'National Curriculum';
    const CFE_BOARD_VALUE = 'Curriculum for Excellence';
    const PRIMARY_LEVEL_VALUES = new Set(['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6']);
    const CFE_LEVEL_VALUES = new Set(['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7']);
    // Marking is out of scope: mk-exam-board/mk-level stay uncoupled.
    const COUPLED_PAIRS = [
        ['exam-board', 'level'],
        ['bk-exam-board', 'bk-level'],
        ['ws-exam-board', 'ws-level'],
        ['quiz-exam-board', 'quiz-level'],
        ['sr-exam-board', 'sr-level'],
    ];
    const isPrimaryLevelValue = (v) => PRIMARY_LEVEL_VALUES.has(v) || v === 'KS3' || CFE_LEVEL_VALUES.has(v);
    // The curriculum a level is defined by, or null for an exam level. Years
    // 1–6 and KS3 are England's, P1–P7 Scotland's, and neither is offered
    // under the other: a P3 lookup against the National Curriculum finds
    // nothing, and the reverse is just as empty.
    const curriculumForLevel = (v) => CFE_LEVEL_VALUES.has(v) ? CFE_BOARD_VALUE
        : (PRIMARY_LEVEL_VALUES.has(v) || v === 'KS3') ? NC_BOARD_VALUE : null;
    const isCurriculumBoard = (b) => b === NC_BOARD_VALUE || b === CFE_BOARD_VALUE;

    // The 11+ is the reverse of a curriculum level: one level, set by three
    // exam boards, none of which sets anything else. Picking 11+ under AQA
    // clears the board; picking ISEB under GCSE clears the level. The level
    // cannot pick the board for the teacher the way Year 3 does, because
    // there are three to choose from.
    const ELEVEN_PLUS_LEVEL = '11+';
    const ELEVEN_PLUS_BOARDS = new Set(['ISEB', 'ISEB Pre-Tests', 'SEAG']);
    const isElevenPlusBoard = (b) => ELEVEN_PLUS_BOARDS.has(b);

    // ── What the National Curriculum can actually build ───
    //
    // Picking it as the board narrows the other two fields to what exists:
    // the levels it covers (Years 1–6 and KS3 — it has nothing to say about
    // GCSE or A-Level), and the subjects held at the chosen level. Offering
    // AQA's level list under the National Curriculum invites a selection that
    // can only fail at generate time, three steps later.
    //
    // Narrowing hides tiles and filters the subject dropdown. It never removes
    // <option>s: buildWsTiles and every restore path read the select as the
    // value of record, so the options must stay in the DOM.
    //
    // Mirrors LEVEL_FALLBACKS in utils/specPoints.js — a subject is offerable
    // at Year 3 if it is held at Year 3, at Years 3-4, or at KS2. Keep the two
    // in step: this list going stale shows up as a subject the picker offers
    // and the server then has no statements for.
    const NC_PICKABLE_LEVELS = {
        'National Curriculum': ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'KS3'],
        'Curriculum for Excellence': ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'],
        // Not curricula, but narrowed the same way: ISEB holds three subjects
        // at 11+ and offering the other sixty invites a dead end.
        'ISEB': ['11+'],
        'ISEB Pre-Tests': ['11+'],
        'SEAG': ['11+'],
    };
    const NC_LEVEL_FALLBACKS = {
        'Year 1': ['KS1'], 'Year 2': ['KS1'],
        'Year 3': ['Years 3-4', 'KS2'], 'Year 4': ['Years 3-4', 'KS2'],
        'Year 5': ['Years 5-6', 'KS2'], 'Year 6': ['Years 5-6', 'KS2'],
        'KS3': [],
        'P1': ['Early Level'],
        'P2': ['First Level'], 'P3': ['First Level'], 'P4': ['First Level'],
        'P5': ['Second Level'], 'P6': ['Second Level'], 'P7': ['Second Level'],
        '11+': [],
    };
    let ncCombosPromise = null;
    let ncSubjectsByLevel = null;   // `${board}|${level}` → Set(subject); null until loaded
    let ncSubjectsAny = null;       // board → Set(subject), for "no level picked yet"

    function loadNcCombos() {
        if (ncCombosPromise) return ncCombosPromise;
        ncCombosPromise = apiFetch('/api/spec-coverage/all')
            .then(r => r.json())
            .then(data => {
                const held = new Map();   // board → subject → Set(level)
                (data.combos || []).forEach(c => {
                    if (!NC_PICKABLE_LEVELS[c.board]) return;
                    if (!held.has(c.board)) held.set(c.board, new Map());
                    const bySubject = held.get(c.board);
                    if (!bySubject.has(c.subject)) bySubject.set(c.subject, new Set());
                    bySubject.get(c.subject).add(c.level);
                });
                ncSubjectsByLevel = new Map();
                ncSubjectsAny = new Map();
                for (const [board, levels] of Object.entries(NC_PICKABLE_LEVELS)) {
                    const any = new Set();
                    ncSubjectsAny.set(board, any);
                    for (const level of levels) {
                        const wanted = [level, ...(NC_LEVEL_FALLBACKS[level] || [])];
                        const set = new Set();
                        (held.get(board) || new Map()).forEach((heldLevels, subject) => {
                            if (wanted.some(w => heldLevels.has(w))) { set.add(subject); any.add(subject); }
                        });
                        ncSubjectsByLevel.set(`${board}|${level}`, set);
                    }
                }
                return ncSubjectsByLevel;
            })
            // A failed fetch must not lock the teacher out of the subject
            // picker: leaving the filter off offers too much, which is
            // recoverable, where offering nothing is not.
            .catch(() => { ncSubjectsByLevel = null; ncSubjectsAny = null; return null; });
        return ncCombosPromise;
    }

    /**
     * Show only the levels the chosen board can actually teach.
     *
     * Both directions, because both are true: the National Curriculum says
     * nothing about GCSE, and AQA publishes no Year 3 specification. Filtering
     * the exam levels away only when the National Curriculum was picked left
     * the primary years cluttering every GCSE selection for no reason.
     *
     * With no board chosen the list stays complete — that is the browse state,
     * and the years are folded behind their chip anyway.
     */
    function applyNcLevelVisibility(levelId, board) {
        const host = document.querySelector(`[data-tiles-for="${levelId}"]`);
        if (!host) return;
        const curriculum = isCurriculumBoard(board);
        const elevenPlus = isElevenPlusBoard(board);
        const examBoard = !!board && !curriculum && !elevenPlus;
        host.querySelectorAll('.ws-tile').forEach(tile => {
            const v = tile.dataset.tileValue;
            // The blank "Any level" option always stays: it is the unset
            // state, not a level we would have to hold data for.
            const drop = v !== '' && (curriculum ? curriculumForLevel(v) !== board
                : elevenPlus ? v !== ELEVEN_PLUS_LEVEL
                : examBoard && (isPrimaryLevelValue(v) || v === ELEVEN_PLUS_LEVEL));
            tile.dataset.tileFiltered = drop ? '1' : '0';
        });
        // Under the National Curriculum the years are the whole list, so
        // leaving them folded behind a chip would hide every option there is.
        // Read off the select rather than naming the group here: a label typed
        // in two places is a rename away from silently folding the only tiles
        // the picker has left.
        const levelSel = $(levelId);
        const groups = Array.from(levelSel?.querySelectorAll('optgroup[data-collapsible]') || []);
        if (!groups.length) { applyTileVisibility(host); return; }
        groups.forEach(g => {
            // Open when the group is all there is, or when the current value
            // lives inside it; folded otherwise. Deriving it rather than
            // leaving it latched is what stops the wall coming back — pick
            // GCSE after browsing the years and the years fold away again.
            const holdsValue = Array.from(g.children).some(o => o.value === levelSel.value);
            // Only the group holding this curriculum's own levels opens: under
            // Curriculum for Excellence the English years stay folded.
            const servesBoard = curriculum && Array.from(g.children).some(o => curriculumForLevel(o.value) === board);
            setGroupExpanded(host, g.label || '', servesBoard || holdsValue);
        });
    }

    /**
     * At 11+ only the three 11+ boards are offered. The blank "None / General"
     * tile stays, as "Any level" does on the other side: it is the unset
     * state, and the way back out. Every other level shows every board but
     * the 11+ ones, which set nothing else.
     */
    function applyElevenPlusBoardVisibility(boardId, level) {
        const host = document.querySelector(`[data-tiles-for="${boardId}"]`);
        if (!host) return;
        const elevenPlus = level === ELEVEN_PLUS_LEVEL;
        host.querySelectorAll('.ws-tile').forEach(tile => {
            const v = tile.dataset.tileValue;
            const drop = v !== '' && !!level && isElevenPlusBoard(v) !== elevenPlus;
            tile.dataset.tileFiltered = drop ? '1' : '0';
        });
        applyTileVisibility(host);
    }

    /** Narrow the subject picker to what is held at this board and level. */
    function applyNcSubjectFilter(boardId, levelId) {
        const combo = NC_SUBJECT_COMBOS[boardId];
        if (!combo || !combo.setFilter) return;
        const boardSel = $(boardId);
        const levelSel = $(levelId);
        if (!boardSel || !levelSel) return;

        const board = boardSel.value;
        // 11+ with no board yet still narrows, to what any 11+ board holds:
        // nothing else exists at that level.
        const elevenPlusAny = !board && levelSel.value === ELEVEN_PLUS_LEVEL;
        if (!NC_PICKABLE_LEVELS[board] && !elevenPlusAny) { combo.setFilter(null); return; }

        loadNcCombos().then(() => {
            if (!ncSubjectsAny) return;   // fetch failed; leave the list open
            if ($(boardId)?.value !== board) return;   // changed while in flight
            const level = $(levelId)?.value;
            if (elevenPlusAny && level !== ELEVEN_PLUS_LEVEL) return;
            const allowed = elevenPlusAny
                ? new Set([...ELEVEN_PLUS_BOARDS].flatMap(b => [...(ncSubjectsByLevel.get(`${b}|${ELEVEN_PLUS_LEVEL}`) || [])]))
                : (level && ncSubjectsByLevel.get(`${board}|${level}`)) || ncSubjectsAny.get(board) || new Set();
            const kept = combo.setFilter(
                (s) => allowed.has(s),
                elevenPlusAny
                    ? 'No 11+ subjects held yet.'
                    : level
                    ? `No ${board} subjects held for ${level} yet.`
                    : 'Pick a level to see the subjects held for it.');
            // A subject that survived the narrowing stays. One that did not is
            // cleared rather than left selected-but-unofferable, which would
            // generate against a board/subject pair we hold nothing for.
            if (!kept) combo.set('');
        });
    }

    function ensureCouplingNote(host) {
        let note = host.nextElementSibling;
        if (note && note.classList.contains('ws-tiles-note')) return note;
        note = document.createElement('div');
        note.className = 'ws-tiles-note';
        note.hidden = true;
        host.after(note);
        return note;
    }

    /** Re-reads every coupled pair and applies disabled/note state. Cheap:
     *  four pairs, eight selects, no layout work. */
    function refreshBoardTileCoupling() {
        for (const [boardId, levelId] of COUPLED_PAIRS) {
            const boardSel = $(boardId);
            const levelSel = $(levelId);
            if (!boardSel || !levelSel) continue;
            const host = document.querySelector(`[data-tiles-for="${boardId}"]`);
            if (!host) continue;

            const board = boardSel.value;
            // Done before the clear below so the tile the value is about to
            // lose is already gone rather than blinking out afterwards.
            applyNcLevelVisibility(levelId, board);
            applyElevenPlusBoardVisibility(boardId, levelSel.value);

            // Board and level are one choice in two fields, and either half can
            // be changed first. Whichever is picked wins, and the other is
            // dropped if it cannot stand with it.
            //
            // Nothing is disabled to prevent this. Greying out every exam board
            // while a primary year was set made the pair a trap: the way out
            // was to set the level back to "Any level" first, which is not
            // something the screen said anywhere. Letting the pick through and
            // clearing what it contradicts costs a selection the teacher was
            // abandoning anyway, and always moves them forward.
            //
            // The change fired here re-enters this function for every pair, so
            // this one is skipped rather than returned from — a return would
            // leave the other three composers unrefreshed on the outer pass.
            // A curriculum board clashes with any level that is not its own —
            // exam levels and the other country's years alike.
            const levelBoard = levelSel.value ? curriculumForLevel(levelSel.value) : null;
            // An 11+ board clashes with every level but 11+, and 11+ with
            // every board but the three that set it.
            const elevenPlusClash = !!board && isElevenPlusBoard(board) !== (levelSel.value === ELEVEN_PLUS_LEVEL);
            const clash = levelSel.value && ((isCurriculumBoard(board) ? levelBoard !== board : !!board && !!levelBoard) || elevenPlusClash);
            if (clash) {
                levelSel.value = '';
                levelSel.dispatchEvent(new Event('change', { bubbles: true }));
                continue;
            }
            applyNcSubjectFilter(boardId, levelId);

            // Never disabled — see above. The note is now purely informative:
            // it says why the board row has only one real answer at this level,
            // rather than announcing a block.
            host.querySelectorAll('input[type="radio"]').forEach(r => { r.disabled = false; });
            const note = ensureCouplingNote(host);
            if (levelBoard) {
                note.textContent = `The ${levelBoard} is the specification at this level. Picking an exam board here clears the level.`;
                note.hidden = false;
            } else if (levelSel.value === ELEVEN_PLUS_LEVEL) {
                note.textContent = '11+ is set by ISEB Common Entrance, the ISEB Pre-Tests and SEAG. Picking another board here clears the level.';
                note.hidden = false;
            } else {
                note.hidden = true;
            }
        }
    }

    /**
     * Clear a composer's curriculum step — exam board, level and subject.
     *
     * A new document starts blank. Nothing else clears these three: they are
     * two <select>s and a combobox, so left alone they carry the last
     * document's curriculum into the next one, and Firefox restores them
     * across a reload on top of that. The teacher who made an A-Level
     * Chemistry deck then starts a Year 3 maths one against OCR A-Level
     * without being told.
     *
     * Order matters: values are cleared BEFORE the tiles are synced, or the
     * tiles keep showing the old selection against an already-empty select.
     */
    function resetCurriculumFields(boardId, levelId, combo) {
        const board = $(boardId);
        const level = $(levelId);
        if (board) board.value = '';
        if (level) level.value = '';
        combo?.set?.('');
        syncAllWsTiles();
        refreshBoardTileCoupling();
    }

    function wireBoardLevelCoupling([boardId, levelId]) {
        const boardSel = $(boardId);
        const levelSel = $(levelId);
        if (!boardSel || !levelSel) return;
        // Picking a primary level makes its curriculum the board: Years 1–6
        // the National Curriculum, P1–P7 Curriculum for Excellence.
        // Setting the value directly would leave every other listener (tiles,
        // coverage, IGCSE hints) stale, so fire change like a user would.
        levelSel.addEventListener('change', () => {
            const levelBoard = curriculumForLevel(levelSel.value);
            if (levelBoard && boardSel.value !== levelBoard) {
                boardSel.value = levelBoard;
                boardSel.dispatchEvent(new Event('change', { bubbles: true }));
            }
            // 11+ under a board that does not set it: the level is the newer
            // choice, so the board goes — not the level, which is what the
            // clash check below would otherwise drop.
            if (levelSel.value === ELEVEN_PLUS_LEVEL && boardSel.value && !isElevenPlusBoard(boardSel.value)) {
                boardSel.value = '';
                boardSel.dispatchEvent(new Event('change', { bubbles: true }));
            }
            refreshBoardTileCoupling();
        });
        boardSel.addEventListener('change', refreshBoardTileCoupling);
    }

    COUPLED_PAIRS.forEach(wireBoardLevelCoupling);
    refreshBoardTileCoupling();

    // ── IGCSE nudge ───────────────────────────────────────
    //
    // Picking an international board and then GCSE is nearly always a slip:
    // internationally the 14–16 course is the IGCSE, and the spec we would
    // plan from is filed under that level. The nudge is worth two different
    // strengths, because the two cases are not equally certain:
    //
    //   Cambridge International awards no GCSE at all – every CIE spec we
    //   hold is IGCSE or A-Level – so there we can say so plainly.
    //
    //   AQA and Edexcel award both, and a UK teacher picking AQA GCSE is
    //   entirely right. There the nudge only asks the question.
    //
    // Boards with no IGCSE at all (OCR, WJEC, Eduqas, CCEA) never nudge.
    const IGCSE_BOARDS = {
        'Cambridge International': {
            state: 'firm',
            text: (b) => `${b} does not award GCSE – its 14–16 course is the IGCSE, and that is where its specifications sit.`,
        },
        'AQA':     { state: 'soft', text: (b) => `Teaching this outside the UK? ${b} runs an International GCSE as well, with its own specification.` },
        'Edexcel': { state: 'soft', text: (b) => `Teaching this outside the UK? ${b} runs an International GCSE as well, with its own specification.` },
    };

    function renderIgcseHint(hintEl) {
        const board = $(hintEl.dataset.hintBoard);
        const level = $(hintEl.dataset.hintLevel);
        if (!board || !level) return;
        const rule = IGCSE_BOARDS[board.value];
        const applies = !!rule && level.value === 'GCSE' && hintEl.dataset.dismissed !== board.value;
        if (!applies) { hintEl.hidden = true; hintEl.innerHTML = ''; return; }

        hintEl.dataset.state = rule.state;
        hintEl.innerHTML = '';
        const icon = document.createElement('span');
        icon.className = 'ws-hint-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5.5"/><path d="M12 7.6h.01"/></svg>';
        const body = document.createElement('div');
        const text = document.createElement('p');
        text.className = 'ws-hint-text';
        text.textContent = rule.text(board.value);
        const actions = document.createElement('div');
        actions.className = 'ws-hint-actions';

        const swap = document.createElement('button');
        swap.type = 'button';
        swap.className = 'btn btn-primary btn-sm';
        swap.textContent = 'Switch to IGCSE';
        swap.addEventListener('click', () => {
            level.value = 'IGCSE';
            level.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const keep = document.createElement('button');
        keep.type = 'button';
        keep.className = 'btn btn-ghost btn-sm';
        keep.textContent = 'Keep GCSE';
        // Dismissal is remembered against the board that raised it, so it does
        // not come back while they finish this sheet, but a different board
        // still gets to ask.
        keep.addEventListener('click', () => {
            hintEl.dataset.dismissed = board.value;
            renderIgcseHint(hintEl);
        });

        actions.append(swap, keep);
        body.append(text, actions);
        hintEl.append(icon, body);
        hintEl.hidden = false;
    }

    document.querySelectorAll('[data-level-hint]').forEach(hintEl => {
        const board = $(hintEl.dataset.hintBoard);
        const level = $(hintEl.dataset.hintLevel);
        // A new board is a new question, so an earlier "keep GCSE" is spent.
        board?.addEventListener('change', () => {
            delete hintEl.dataset.dismissed;
            renderIgcseHint(hintEl);
        });
        level?.addEventListener('change', () => renderIgcseHint(hintEl));
        renderIgcseHint(hintEl);
    });

    /** Re-checks every composer's nudge – for the restore paths, which set the
     *  board and level in code and so fire no change events. */
    function refreshIgcseHints() {
        document.querySelectorAll('[data-level-hint]').forEach(renderIgcseHint);
    }

    // ── Composer wizard ───────────────────────────────────
    //
    // One step of a creation form on screen at a time, with a progress bar and
    // a Back / Next footer. Every composer in the app runs on this: the steps
    // differ, the mechanics do not.
    //
    // `isReady(target)` is the caller's gate – it returns false to refuse a
    // move (and is where that form shows its own error). Only the submit
    // button on the last step generates anything; surfacing it earlier pushes
    // people past steps that exist to show them what they could have had.
    function initComposerWizard({ formId, nextLabels = [], hints = [], submitId, isReady }) {
        const form = $(formId);
        if (!form) return null;
        const panels = Array.from(form.querySelectorAll('.ws-step-panel'));
        if (!panels.length) return null;
        const back   = form.querySelector('[data-wiz-back]');
        const next   = form.querySelector('[data-wiz-next]');
        const hintEl = form.querySelector('[data-wiz-hint]');
        const numEl  = form.querySelector('[data-wiz-num]');
        const totEl  = form.querySelector('[data-wiz-total]');
        const barEl  = form.querySelector('[data-wiz-bar]');
        const submit = submitId ? $(submitId) : null;
        const count  = panels.length;
        let step = 1;

        // The total is written from the panels rather than typed into the
        // markup, so splitting a step cannot leave "of 3" behind.
        if (totEl) totEl.textContent = String(count);
        barEl?.setAttribute('aria-valuemax', String(count));
        panels.forEach((p, i) => {
            p.setAttribute('aria-label', `Step ${i + 1} of ${count}: ${p.dataset.stepName || ''}`.trim());
        });

        function syncChrome() {
            const last = step >= count;
            if (numEl) numEl.textContent = String(step);
            if (barEl) {
                barEl.style.setProperty('--ws-progress', `${(step / count) * 100}%`);
                barEl.setAttribute('aria-valuenow', String(step));
            }
            if (back) back.hidden = step === 1;
            if (next) {
                next.hidden = last;
                next.textContent = nextLabels[step - 1] || 'Next →';
            }
            if (hintEl) hintEl.textContent = hints[step - 1] || '';
            if (submit) submit.hidden = !last;
        }

        function setStep(target, opts = {}) {
            step = Math.min(Math.max(target, 1), count);
            panels.forEach(p => { p.hidden = Number(p.dataset.step) !== step; });
            syncChrome();
            // Focus the panel itself, not its first field: it carries the
            // step's name, so a screen reader announces which step this is
            // instead of dropping the user onto an unexplained control.
            if (opts.focus !== false) {
                panels.find(p => Number(p.dataset.step) === step)?.focus({ preventScroll: true });
            }
            // The card changes height between steps; keep its head in view
            // rather than leaving the teacher mid-panel.
            const top = form.getBoundingClientRect().top;
            if (top < 0) form.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }

        const go = (target) => {
            if (target > step && isReady && !isReady(target)) return;
            setStep(target);
        };
        next?.addEventListener('click', () => go(step + 1));
        back?.addEventListener('click', () => setStep(step - 1));

        // On a real <form>, Enter in any field triggers implicit submission –
        // which on step 1 would generate while skipping the two steps that
        // exist to be seen. Enter advances instead, and only the last step is
        // allowed to submit at all.
        if (form.tagName === 'FORM') {
            form.addEventListener('submit', (e) => {
                if (step >= count) return;
                e.preventDefault();
                e.stopImmediatePropagation();
                go(step + 1);
            }, true);
        }

        setStep(1, { focus: false });

        return {
            setStep,
            next: () => go(step + 1),
            last: () => setStep(count),
            get step() { return step; },
            get count() { return count; },
        };
    }

    // ── Spec-coverage indicator ───────────────────────────
    // Tells the teacher, before they generate, what grounding we hold for this
    // board/subject/level: 'verified' means extracted from the board's own
    // published specification, 'aligned' means matched to the National
    // Curriculum programme of study (authored against it, not extracted
    // word-for-word), 'generic' means command-word guidance only.
    const specCoverageEl = $('spec-coverage');
    let specCoverageSeq = 0;

    // How wide each NC key-stage document actually is, for the viaKeyStage
    // wording. Mirrors the spans in utils/specPoints.js.
    const NC_LEVEL_SPANS = {
        'KS1': 'Years 1–2', 'KS2': 'Years 3–6', 'KS3': 'Years 7–9',
        'Years 3-4': 'Years 3–4', 'Years 5-6': 'Years 5–6',
        'Early Level': 'nursery to P1', 'First Level': 'P2–P4', 'Second Level': 'P5–P7',
    };

    /** The "set by level, not by year" line, worded for the curriculum it is about. */
    function curriculumSpanLine(info, written = 'Content') {
        const span = NC_LEVEL_SPANS[info.level] || info.level;
        return info.board === CFE_BOARD_VALUE
            ? `Curriculum for Excellence sets ${info.subject} by level, not by year group: these ${info.statements} benchmarks cover ${span}. ${written} will be written for ${info.requestedLevel}.`
            : `The National Curriculum sets ${info.subject} by key stage, not by year: these ${info.statements} objectives cover ${span}. ${written} will be written for ${info.requestedLevel}.`;
    }

    function renderSpecCoverage(info) {
        if (!specCoverageEl) return;
        // Keyed on state, not verified: an NC alignment is real grounding but
        // not a verification claim, so hiding it would hide the very thing
        // that tells the teacher primary content is grounded.
        if (!info || !info.state || info.state === 'generic') { specCoverageEl.hidden = true; return; }

        const codeSuffix = info.specCode ? ` (${info.specCode})` : '';
        specCoverageEl.dataset.state = info.state;
        specCoverageEl.innerHTML = '';
        const icon = document.createElement('span');
        icon.className = 'spec-coverage-icon';
        icon.textContent = '✓';
        const body = document.createElement('div');
        const title = document.createElement('div');
        title.className = 'spec-coverage-title';

        if (info.state === 'aligned') {
            // No code suffix here. A board's spec code is printed on the front
            // of the specification and teachers quote it — "AQA 8461" is worth
            // showing. "DfE-2013-Maths" is our own filing name, means nothing
            // outside this repo, and reads as noise after the subject.
            // The 11+ boards are exams, not curricula: their documents are
            // authored too, but against a specification.
            const examSpec = !isCurriculumBoard(info.board);
            title.textContent = examSpec
                ? `Aligned to the ${info.board} ${info.subject} ${info.level} specification`
                : info.board === CFE_BOARD_VALUE
                ? `Aligned to the ${info.board} ${info.subject} Benchmarks for ${info.level}`
                : `Aligned to the ${info.board} ${info.subject} programme of study for ${info.level}`;
            const detail = document.createElement('div');
            detail.className = 'spec-coverage-detail';
            // Says what will happen, like the verified branch below, rather
            // than explaining how the data was produced. "Aligned to" already
            // carries the honest claim — the exam-board banner is the one that
            // says "quoted verbatim" — and a teacher seeing only this banner
            // cannot act on the difference between our two provenances. That
            // distinction lives in `provenance` and in utils/specPoints.js,
            // for whoever maintains this.
            detail.textContent = info.viaKeyStage
                ? curriculumSpanLine(info)
                : examSpec
                ? `Slides will be planned against the ${info.statements} points summarised from the published specification.`
                : `Slides will be planned against the ${info.statements} objectives in the ${info.level} programme of study.`;
            body.append(title, detail);
            specCoverageEl.append(icon, body);
            specCoverageEl.hidden = false;
            return;
        }

        title.textContent = `Matched to the ${info.board} ${info.subject} ${info.level || ''}`.trim() + ` specification${codeSuffix}`;
        const detail = document.createElement('div');
        detail.className = 'spec-coverage-detail';
        detail.textContent = `Slides will be planned against the ${info.statements} specification statements for this course, taken word-for-word from the published specification.`;
        body.append(title, detail);
        specCoverageEl.append(icon, body);
        specCoverageEl.hidden = false;
    }

    async function refreshSpecCoverage() {
        if (!specCoverageEl) return;
        const board = $('exam-board')?.value || '';
        const subject = $('subject')?.value || '';
        const level = $('level')?.value || '';
        if (!board || !subject || !level) { specCoverageEl.hidden = true; return; }

        // Guard against out-of-order responses: the three fields are often set
        // in quick succession (subject auto-detect fires while the board is
        // being picked), and a slow earlier request must not overwrite a newer.
        const seq = ++specCoverageSeq;
        try {
            const qs = new URLSearchParams({ board, subject, level });
            const tier = $('tier')?.value || '';
            if (tier) qs.set('tier', tier);
            const res = await apiFetch(`/api/spec-coverage?${qs}`);
            const info = await res.json();
            if (seq !== specCoverageSeq) return;
            renderSpecCoverage(res.ok ? info : null);
        } catch {
            if (seq === specCoverageSeq) specCoverageEl.hidden = true;
        }
    }

    $('exam-board')?.addEventListener('change', refreshSpecCoverage);
    $('level')?.addEventListener('change', refreshSpecCoverage);
    $('level')?.addEventListener('change', followLevelTheme);
    $('subject')?.addEventListener('change', refreshSpecCoverage);
    // Combined or Triple changes which specification the deck is read from.
    $('tier')?.addEventListener('change', refreshSpecCoverage);

    // ── Exam-style mode (worksheet composer) ───────────────
    // Tells the teacher, before they generate, whether we hold a real exam
    // board's question SHAPE for this board/subject/level, and lets them
    // compose the whole worksheet from it instead of custom question types.
    //
    // The choice lives in a pair of cards in step 3 (Your own mix / Exam-style);
    // the banner below the board fields in step 2 is the evidence for it, not
    // the control. Availability is only ever set from the server answer, so a
    // stale "yes" cannot survive a board change.
    const wsExamStyleEl   = $('ws-exam-style-coverage');
    const wsTypesGroup    = $('ws-types-group');
    const wsModeExamCard  = $('ws-mode-exam-card');
    const wsModeExamRadio = $('ws-exam-style-toggle');
    const wsModeCustom    = $('ws-mode-custom');
    const wsDescribeEl    = $('ws-describe');
    const wsDescribeNote  = $('ws-describe-note');
    const wsLayoutBtn     = document.querySelector('[data-target="ws-context"] [data-src-toggle="layout"]');
    const wsModeBadge     = $('ws-mode-badge');
    const wsModeStatus    = $('ws-mode-status');
    let wsExamStyleSeq = 0;
    let wsExamStyleModeOn = false;

    // The layout described on step 1, or ''. When there is one it decides the
    // question types, so the tiles give way to a line saying so.
    const wsDescription = () => (wsDescribeEl?.value || '').trim();

    // ── Question recipe ───────────────────────────────────
    // How many of each type, which is also the sheet's length: the server
    // builds exactly these counts (typeCounts). Presets fill the recipe; the
    // exam preset swaps it for one count of multi-part questions, 1-8. A sheet
    // of WS_COUNT_DOUBLE_FROM or more spends two allowances (the server's
    // QUESTION_COUNT_DOUBLE_FROM).
    const WSQ_TYPES = ['multiple_choice', 'true_false', 'fill_blank', 'matching', 'short_answer', 'extended_response'];
    const WSQ_NAMES = {
        multiple_choice: 'Multiple choice', true_false: 'True or false', fill_blank: 'Fill the blank',
        matching: 'Matching', short_answer: 'Short answer', extended_response: 'Extended response',
    };
    const WSQ_TONES = {
        multiple_choice: 'tone-blue', true_false: 'tone-green', fill_blank: 'tone-amber',
        matching: 'tone-purple', short_answer: 'tone-teal', extended_response: 'tone-indigo',
    };
    const WSQ_PRESETS = {
        quick: { multiple_choice: 5, true_false: 5 },
        revision: { multiple_choice: 5, true_false: 5, fill_blank: 5, short_answer: 5 },
        written: { short_answer: 6, extended_response: 2 },
    };
    const WS_COUNT_MAX = 40;
    const WS_COUNT_DOUBLE_FROM = 26;
    const WS_EXAM_RANGE = { min: 1, max: 8 };
    const wsRecipe = Object.fromEntries(WSQ_TYPES.map((t) => [t, WSQ_PRESETS.revision[t] || 0]));
    let wsExamCount = 4;
    const wsRecipeTotal = () => WSQ_TYPES.reduce((n, t) => n + wsRecipe[t], 0);
    const wsRecipeEl = $('ws-type-checks');

    function setWsRecipe(counts) {
        WSQ_TYPES.forEach((t) => { wsRecipe[t] = counts[t] || 0; });
        renderWsRecipe();
    }

    function renderWsRecipe() {
        const total = wsRecipeTotal();
        wsRecipeEl?.querySelectorAll('.wsq-row[data-type]').forEach((row) => {
            const n = wsRecipe[row.dataset.type];
            row.classList.toggle('is-off', n === 0);
            row.querySelector('[data-count]').textContent = String(n);
            row.querySelector('[data-step="-1"]').disabled = n === 0;
            row.querySelector('[data-step="1"]').disabled = total >= WS_COUNT_MAX;
        });
        const totalEl = $('wsq-total');
        if (totalEl) totalEl.textContent = `${total} question${total === 1 ? '' : 's'}`;
        const note = $('wsq-total-note');
        if (note) {
            note.textContent = total === 0 ? 'Add at least one question.'
                : total >= WS_COUNT_MAX ? `${WS_COUNT_MAX} is the most a sheet takes.`
                    : total >= WS_COUNT_DOUBLE_FROM ? 'Uses two of your monthly worksheets.'
                        : `Up to ${WS_COUNT_MAX} per sheet.`;
        }
        const examStepper = $('wsq-exam-stepper');
        if (examStepper) {
            examStepper.querySelector('[data-count]').textContent = String(wsExamCount);
            examStepper.querySelector('[data-step="-1"]').disabled = wsExamCount <= WS_EXAM_RANGE.min;
            examStepper.querySelector('[data-step="1"]').disabled = wsExamCount >= WS_EXAM_RANGE.max;
        }
        // A preset reads as chosen while the recipe is exactly it, and stops
        // the moment a count is changed: the recipe is then the teacher's own.
        document.querySelectorAll('.wsq-preset[data-preset]').forEach((btn) => {
            const preset = WSQ_PRESETS[btn.dataset.preset];
            const same = WSQ_TYPES.every((t) => (preset[t] || 0) === wsRecipe[t]);
            btn.classList.toggle('is-on', !wsExamStyleModeOn && same);
            btn.setAttribute('aria-pressed', String(!wsExamStyleModeOn && same));
        });
    }

    wsRecipeEl?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        const row = btn?.closest('.wsq-row[data-type]');
        if (!btn || !row) return;
        const t = row.dataset.type;
        const room = WS_COUNT_MAX - wsRecipeTotal();
        wsRecipe[t] = Math.max(0, wsRecipe[t] + Math.min(Number(btn.dataset.step), room));
        renderWsRecipe();
    });
    $('wsq-exam-stepper')?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        wsExamCount = Math.max(WS_EXAM_RANGE.min, Math.min(WS_EXAM_RANGE.max, wsExamCount + Number(btn.dataset.step)));
        renderWsRecipe();
    });
    document.querySelectorAll('.wsq-preset[data-preset]').forEach((btn) => btn.addEventListener('click', () => {
        setWsRecipe(WSQ_PRESETS[btn.dataset.preset]);
        if (wsExamStyleModeOn) setWsExamStyleMode(false);
    }));

    renderWsRecipe();

    /** How many questions a generated sheet holds, counted the way the server counts them. */
    const wsGeneratedCount = (data) => WSQ_TYPES
        .reduce((n, t) => n + (Array.isArray(data?.[t]) ? data[t].length : 0), 0);

    function syncWsMode() {
        wsExamStyleModeOn = !!wsModeExamRadio?.checked;
        const described = !!wsDescription();
        // The types are replaced by the board's shapes in exam mode and by the
        // description when there is one, so they are hidden rather than left
        // on screen doing nothing. The supports apply in every mode: on an
        // exam-style sheet the server adds them after the questions are made.
        if (wsTypesGroup) wsTypesGroup.hidden = wsExamStyleModeOn || described;
        // Exam practice swaps the recipe for its own count of multi-part
        // questions; a described sheet takes its length from the description.
        const wsExamGroup = $('ws-exam-group');
        if (wsExamGroup) wsExamGroup.hidden = !wsExamStyleModeOn;
        const wsPresetsGroup = $('wsq-presets-group');
        if (wsPresetsGroup) wsPresetsGroup.hidden = wsExamStyleModeOn || described;
        renderWsRecipe();
        // Lit, like Paste notes with notes in it, so a layout written and then
        // folded away is still seen to be there.
        wsLayoutBtn?.classList.toggle('has-value', described);
        if (wsDescribeNote) {
            wsDescribeNote.hidden = !described;
            wsDescribeNote.innerHTML = wsExamStyleModeOn
                ? 'Exam-style questions follow the board&rsquo;s own question shapes, so the layout you described in step 1 is not used.'
                : '<b>Your description in step 1 sets the layout</b>, so it picks the question types too. Clear it to choose them here.';
        }
    }

    function setWsExamStyleMode(on) {
        if (wsModeExamRadio) wsModeExamRadio.checked = on;
        if (!on && wsModeCustom) wsModeCustom.checked = true;
        syncWsMode();
    }

    [wsModeExamRadio, wsModeCustom].forEach(el => el?.addEventListener('change', syncWsMode));
    wsDescribeEl?.addEventListener('input', syncWsMode);

    /** Availability of exam-style generation, and the card state that shows it. */
    function setWsExamStyleAvailable(info) {
        const ok = !!info;
        if (wsModeExamCard) wsModeExamCard.dataset.available = String(ok);
        if (wsModeExamRadio) wsModeExamRadio.disabled = !ok;
        if (wsModeBadge) wsModeBadge.hidden = !ok;
        if (wsModeStatus) {
            wsModeStatus.textContent = ok
                ? `${info.patternCount} ${info.board} ${info.subject}${info.level ? ' ' + info.level : ''} question shape${info.patternCount === 1 ? '' : 's'}.`
                : wsExamStyleIncomplete()
                    ? 'Pick a board, subject and level in step 1.'
                    // Primary has no exam and never will hold question shapes,
                    // so the "yet" of the ordinary unavailable message would
                    // promise something that cannot exist.
                    : noExamAtThisLevel()
                        ? 'Exam-style questions come from real exam papers – there is no exam at this level.'
                        : 'We don’t hold question shapes for that board and subject yet.';
        }
        // Losing availability must not leave the sheet generating in a mode
        // the server would reject.
        if (!ok && wsExamStyleModeOn) setWsExamStyleMode(false);
    }

    function noExamAtThisLevel() {
        return isCurriculumBoard($('ws-exam-board')?.value)
            || isPrimaryLevelValue($('ws-level')?.value || '');
    }

    function wsExamStyleIncomplete() {
        return !($('ws-exam-board')?.value && $('ws-subject')?.value && $('ws-level')?.value);
    }

    function renderWsExamStyleCoverage(info) {
        setWsExamStyleAvailable(info && info.available ? info : null);
        if (!wsExamStyleEl) return;
        if (!info || !info.available) { wsExamStyleEl.hidden = true; return; }

        wsExamStyleEl.dataset.state = 'verified';
        wsExamStyleEl.innerHTML = '';
        const icon = document.createElement('span');
        icon.className = 'spec-coverage-icon';
        icon.textContent = '✓';
        const body = document.createElement('div');
        const title = document.createElement('div');
        title.className = 'spec-coverage-title';
        title.textContent = `Exam-style questions available for ${info.board} ${info.subject} ${info.level || ''}`.trim();
        const detail = document.createElement('div');
        detail.className = 'spec-coverage-detail';
        detail.textContent = `We hold ${info.patternCount} real exam-board-shaped multi-part question${info.patternCount === 1 ? '' : 's'} for this combination. Choose “Exam-style questions” in step 3 to build the whole sheet from them.`;
        body.append(title, detail);
        wsExamStyleEl.append(icon, body);
        wsExamStyleEl.hidden = false;
    }

    async function refreshWsExamStyleCoverage() {
        if (!wsExamStyleEl) return;
        const board = $('ws-exam-board')?.value || '';
        const subject = $('ws-subject')?.value || '';
        const level = $('ws-level')?.value || '';
        if (!board || !subject || !level) { wsExamStyleEl.hidden = true; setWsExamStyleAvailable(null); return; }

        const seq = ++wsExamStyleSeq;
        try {
            const qs = new URLSearchParams({ board, subject, level });
            const res = await apiFetch(`/api/question-patterns?${qs}`);
            const info = await res.json();
            if (seq !== wsExamStyleSeq) return;
            renderWsExamStyleCoverage(res.ok ? info : null);
        } catch {
            if (seq === wsExamStyleSeq) { wsExamStyleEl.hidden = true; setWsExamStyleAvailable(null); }
        }
    }
    setWsExamStyleAvailable(null);

    $('ws-exam-board')?.addEventListener('change', refreshWsExamStyleCoverage);
    $('ws-level')?.addEventListener('change', refreshWsExamStyleCoverage);
    $('ws-subject')?.addEventListener('change', refreshWsExamStyleCoverage);

    // ── Topic pickers (presentation, worksheet, quiz) ─────
    //
    // The same tick list the booklet composer offers, shown under the topic
    // box where we hold an authored list for this board/subject/level. The
    // two add up rather than compete: ticks say which parts of the
    // specification to cover, and anything typed in the box is covered as
    // well. The box only ever holds what the teacher typed — the ticks are
    // shown by the list itself, not echoed into it.
    //
    // They meet only when the resource is made (topicLineFor below): the
    // stored topic is the ticks' summary followed by the typed line, which is
    // what the library card reads back. The typed line also travels on its
    // own as `topicNote`, and the ids as `topics`, because the line is only a
    // summary: the server expands the ids into every objective under every
    // ticked topic, in the board's own vocabulary, and names the typed line
    // as extra coverage (see utils/specTopics.js briefFromTopics).
    function initFieldTopicPicker(prefix, fieldId, subWithList) {
        const field = $(fieldId);
        const group = $(prefix + '-topics-group');
        const list  = $(prefix + '-topics');
        if (!field || !group || !list || !window.TopicPicker) return null;

        // The step's own sub-heading changes with whether a list is offered,
        // the way the booklet composer's does.
        const panel  = field.closest('.ws-step-panel');
        const subEl  = panel?.querySelector('.ws-hero-sub');
        const subFree = subEl?.textContent || '';
        // With a list on offer the box is no longer the only way to say what
        // to cover, so its label says so and ticks alone satisfy the form.
        const label = panel?.querySelector('label[for="' + fieldId + '"]');
        const labelFree = label?.innerHTML || '';
        const wasRequired = field.required;

        return window.TopicPicker.create({
            group, list,
            countEl: $(prefix + '-topics-count'),
            clearBtn: $(prefix + '-topics-clear'),
            apiFetch,
            onChange: (picker) => {
                if (subEl) subEl.textContent = picker.catalogue ? subWithList : subFree;
                if (label) {
                    label.innerHTML = picker.catalogue
                        ? labelFree + ' <span class="ws-optional">(optional if you tick topics below)</span>'
                        : labelFree;
                }
                if (wasRequired) field.required = !(picker.catalogue && picker.count());
            },
        });
    }

    // A topic column holds 500 characters, so a longer line is cut here
    // rather than rejected by the server.
    const TOPIC_LINE_MAX = 500;

    /** The ticks and the typed line together, as the one topic line stored. */
    function topicLineFor(field, picker) {
        const typed = field?.value.trim() || '';
        const ticked = picker?.catalogue && picker.count() ? picker.summary(TOPIC_LINE_MAX) : '';
        const line = ticked && typed ? ticked + ' — ' + typed : (ticked || typed);
        return line.length > TOPIC_LINE_MAX ? line.slice(0, TOPIC_LINE_MAX - 1).trimEnd() + '…' : line;
    }

    /** The ticked ids and the typed line beside them, for a generation request. */
    function topicPartsFor(field, picker) {
        if (!picker?.catalogue || !picker.count()) return { topics: undefined, topicNote: undefined };
        return { topics: picker.ids(), topicNote: field?.value.trim() || undefined };
    }

    const TOPICS_SUB = 'Tick topics from the specification, write your own, or both. Ticked topics are written against the board’s own words.';
    const genTopicPicker  = initFieldTopicPicker('gen', 'topic', TOPICS_SUB);
    const wsTopicPicker   = initFieldTopicPicker('ws', 'worksheet-topic', TOPICS_SUB);

    // The composers (the first step of a presentation, worksheet, quiz and
    // booklet; see the markup): one box to write in, with the specification
    // button, the course chip and the material buttons along its foot. Each
    // names its own course fields and topic list in data-course and
    // data-topics-group.
    document.querySelectorAll('.src-composer').forEach(function initComposer(box) {
        const targetId = box.dataset.target;
        const [boardId, subjectId, levelId] = (box.dataset.course || '').split(',');
        const group = $(box.dataset.topicsGroup);
        const hint  = box.querySelector('.cur-hint');
        const topic = box.dataset.topic ? $(box.dataset.topic) : null;
        const specBtn = box.querySelector('[data-spec-btn]');
        const specCount = specBtn?.querySelector('.src-icon-count');
        const chip  = box.querySelector('.src-course-chip');
        const chipLabel = chip?.querySelector('.src-course-label');
        const topicsPanel = box.querySelector('[data-src-panel="topics"]');
        const placeholder = topic?.placeholder || '';
        const val = (id) => ($(id)?.value || '').trim();

        // A topic stored as one line (worksheet, quiz): the box wraps like a
        // message, but Enter adds nothing and pasted line breaks become spaces.
        if (topic && topic.hasAttribute('data-one-line')) {
            topic.addEventListener('keydown', (e) => { if (e.key === 'Enter') e.preventDefault(); });
            topic.addEventListener('input', () => {
                if (/[\r\n]/.test(topic.value)) topic.value = topic.value.replace(/\s*[\r\n]+\s*/g, ' ');
            });
        }

        function setTopicsOpen(open) {
            if (topicsPanel) topicsPanel.hidden = !open;
            specBtn?.setAttribute('aria-expanded', String(open));
        }

        // With no checklist yet, the button explains what unlocks one by
        // opening the course picker, rather than opening an empty panel.
        specBtn?.addEventListener('click', () => {
            if (!group || group.hidden) {
                setSourcePanel(targetId, 'course', true);
                if (hint) hint.hidden = false;
                return;
            }
            setTopicsOpen(topicsPanel?.hidden);
        });

        if (group) {
            let listed = !group.hidden;
            const syncSpec = () => {
                const nowListed = !group.hidden;
                const incomplete = !(val(boardId) && val(subjectId) && val(levelId));
                // A full course with no list held for it has nothing to unlock.
                if (hint) hint.hidden = nowListed || !incomplete;
                if (topic) topic.placeholder = nowListed ? 'Tick topics from the specification below, or type one' : placeholder;
                // A checklist arriving is the moment the course was chosen, so
                // it opens; one going away closes with it.
                if (nowListed !== listed) setTopicsOpen(nowListed);
                listed = nowListed;
                const ticked = group.querySelectorAll('input[type="checkbox"]:checked').length;
                if (specCount) specCount.textContent = ticked ? String(ticked) : '';
                specBtn?.classList.toggle('has-value', !!ticked);
            };
            new MutationObserver(syncSpec).observe(group, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });
            group.addEventListener('change', syncSpec);
            [boardId, subjectId, levelId].forEach((id) => $(id)?.addEventListener('change', syncSpec));
            syncSpec();
        }

        // The chip names the course. Board, level and subject are set from
        // script as often as by hand (the subject is guessed from the topic,
        // restores fill them in), which fires no event to rely on, so it is
        // re-read on a short timer, as the tier picker does.
        let lastLabel = null;
        const syncChip = () => {
            const parts = [val(subjectId), val(boardId), val(levelId)].filter(Boolean);
            const label = parts.length ? parts.join(' · ') : 'Choose a course';
            if (label === lastLabel) return;
            lastLabel = label;
            if (chipLabel) chipLabel.textContent = label;
            chip?.classList.toggle('is-empty', !parts.length);
        };
        syncChip();
        setInterval(syncChip, 500);
    });
    const quizTopicPicker = initFieldTopicPicker('quiz', 'quiz-topic', TOPICS_SUB);

    function loadTopicPicker(picker, boardId, subjectId, levelId, tierId) {
        picker?.load($(boardId)?.value, $(subjectId)?.value, $(levelId)?.value, $(tierId)?.value || '');
    }
    const refreshGenTopics  = () => loadTopicPicker(genTopicPicker, 'exam-board', 'subject', 'level', 'tier');
    const refreshWsTopics   = () => loadTopicPicker(wsTopicPicker, 'ws-exam-board', 'ws-subject', 'ws-level', 'ws-tier');
    const refreshQuizTopics = () => loadTopicPicker(quizTopicPicker, 'quiz-exam-board', 'quiz-subject', 'quiz-level', 'quiz-tier');

    // The subject combobox commits to the HIDDEN input and fires change there,
    // not on the visible one the teacher types into (see initSubjectCombobox).
    // The course field too: Combined and Triple, Foundation and Higher, each
    // teach a different part of the same list, so choosing one redraws it.
    ['exam-board', 'subject', 'level', 'tier'].forEach((id) => $(id)?.addEventListener('change', refreshGenTopics));
    ['ws-exam-board', 'ws-subject', 'ws-level', 'ws-tier'].forEach((id) => $(id)?.addEventListener('change', refreshWsTopics));
    ['quiz-exam-board', 'quiz-subject', 'quiz-level', 'quiz-tier'].forEach((id) => $(id)?.addEventListener('change', refreshQuizTopics));

    // ── Lesson series ─────────────────────────────────────
    // Its own composer (#series-card): what the series covers and how many
    // lessons. Drafting returns an outline (routes/schemes.js), edited in
    // #scheme-view and saved as it changes. Each lesson is then made through
    // the presentation composer, with its own settings, and filed in the
    // series' folder.
    const seriesCard      = $('series-card');
    const seriesForm      = $('series-form');
    const seriesBtn       = $('series-btn');
    const seriesError     = $('series-error');
    const srTopicInput    = $('sr-topic');
    const srTopicPicker   = initFieldTopicPicker('sr', 'sr-topic', TOPICS_SUB);
    const refreshSrTopics = () => loadTopicPicker(srTopicPicker, 'sr-exam-board', 'sr-subject', 'sr-level', 'sr-tier');
    ['sr-exam-board', 'sr-subject', 'sr-level', 'sr-tier'].forEach((id) => $(id)?.addEventListener('change', refreshSrTopics));

    const srWizard = initComposerWizard({
        formId: 'series-form',
        submitId: 'series-btn',
        hints: ['Drafting the outline is free. Each lesson uses a presentation allowance when you make it.'],
    });

    function pickInGroup(group, btn, selector) {
        group.querySelectorAll(selector).forEach((b) => {
            const on = b === btn;
            b.classList.toggle('active', on);
            if (b.hasAttribute('role')) b.setAttribute('aria-checked', String(on));
        });
    }

    function seriesCountBy() {
        return seriesForm?.querySelector('.sr-by-btn.active')?.dataset.by || 'lessons';
    }

    function seriesLessonCount() {
        const n = (id) => parseInt($(id)?.value, 10) || 0;
        const total = seriesCountBy() === 'weeks' ? n('sr-weeks') * n('sr-per-week') : n('sr-lessons');
        return Math.min(Math.max(total, 2), 40);
    }

    seriesForm?.querySelector('.sr-by')?.addEventListener('click', (e) => {
        const btn = e.target.closest('.sr-by-btn');
        if (!btn) return;
        pickInGroup(seriesForm.querySelector('.sr-by'), btn, '.sr-by-btn');
        seriesForm.querySelectorAll('[data-by-row]').forEach((row) => { row.hidden = row.dataset.byRow !== btn.dataset.by; });
    });
    ['sr-weeks', 'sr-per-week'].forEach((id) => $(id)?.addEventListener('input', () => {
        const total = $('sr-total');
        if (total) total.textContent = String(seriesLessonCount());
    }));

    function resetSeriesBtn() {
        if (!seriesBtn) return;
        seriesBtn.disabled = false;
        seriesBtn.textContent = 'Draft the lessons';
    }

    seriesForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        setError(seriesError, '');
        const topic = topicLineFor(srTopicInput, srTopicPicker);
        if (!topic) {
            srTopicInput?.focus();
            return setError(seriesError, 'Type what the series covers, add a file, or tick topics from the specification');
        }

        seriesBtn.disabled = true;
        seriesBtn.textContent = 'Drafting…';
        hide(seriesCard);
        showGenLoading('Drafting the lessons', topic);
        try {
            const res = await apiFetch('/api/schemes', {
                method: 'POST',
                body: JSON.stringify({
                    topic,
                    inputContent: sourceTextFor('sr-input-content'),
                    examBoard: $('sr-exam-board')?.value || '',
                    subject: $('sr-subject')?.value || '',
                    level: $('sr-level')?.value || '',
                    tier: $('sr-tier')?.value || '',
                    ...topicPartsFor(srTopicInput, srTopicPicker),
                    lessonCount: seriesLessonCount(),
                    folderId: state.seriesFolder?.id || undefined,
                }),
            });
            const data = await res.json();
            hideGenLoading();
            resetSeriesBtn();
            if (!res.ok) {
                show(seriesCard);
                return setError(seriesError, data.error || 'Could not draft the series');
            }
            rememberScheme(data);
            showScheme(data);
        } catch (err) {
            hideGenLoading();
            show(seriesCard);
            resetSeriesBtn();
            if (err.message !== 'Not authenticated') setError(seriesError, 'Network error');
        }
    });

    // ── Lesson series: the outline ────────────────────────
    const schemeView      = $('scheme-view');
    const schemeLessonsEl = $('scheme-lessons');
    const schemeTitleEl   = $('scheme-title');
    const schemeError     = $('scheme-error');
    const schemeSaveState = $('scheme-save-state');

    const SCHEME_LOCK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
    const SCHEME_DONE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
    const SCHEME_UP_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6"/></svg>';
    const SCHEME_DOWN_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M12 5v14M6 13l6 6 6-6"/></svg>';
    const SCHEME_UNLOCK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 7.5-2"/></svg>';

    function showScheme(scheme, { push = true } = {}) {
        state.scheme = scheme;
        const path = `/series/${scheme.id}`;
        if (push && window.location.pathname !== path) history.pushState({ schemeId: scheme.id }, '', path);
        setError(schemeError, '');
        schemeTitleEl.value = scheme.title || '';
        $('scheme-feedback-text').value = '';
        schemeSaveState.textContent = '';
        hide(seriesCard);
        show(schemeView);
        renderScheme();
        window.scrollTo({ top: 0 });
    }

    // A whole topic is shown by its name; a single point by the point, since
    // its topic's name would claim more than the lesson covers.
    function schemeTopicLabel(id) {
        const item = state.scheme.topics.find((t) => t.id === id);
        if (!item) return id;
        const cut = item.text.indexOf(': ');
        if (cut < 0) return item.text;
        return /\.p\d+$/.test(id) ? item.text.slice(cut + 2) : item.text.slice(0, cut);
    }

    function renderScheme() {
        const { lessons, topics } = state.scheme;
        const course = [state.scheme.examBoard, state.scheme.level, state.scheme.subject].filter(Boolean).join(' ');
        $('scheme-subtitle').textContent = `${lessons.length} lesson${lessons.length === 1 ? '' : 's'}` + (course ? ` · ${course}` : '');
        const made = lessons.filter((l) => l.presentationId).length;
        $('scheme-progress').textContent = `${made} of ${lessons.length} made`;

        schemeLessonsEl.innerHTML = '';
        lessons.forEach((lesson, i) => schemeLessonsEl.appendChild(buildSchemeRow(lesson, i)));
        schemeLessonsEl.querySelectorAll('.scheme-lesson-objective').forEach(fitObjective);

        const covered = new Set(lessons.flatMap((l) => l.topicIds));
        const missing = topics.filter((t) => !covered.has(t.id));
        const warn = $('scheme-uncovered');
        warn.hidden = !missing.length;
        warn.textContent = missing.length
            ? `Not in any lesson yet: ${missing.map((t) => schemeTopicLabel(t.id)).join(', ')}`
            : '';
    }

    function buildSchemeRow(lesson, index) {
        const li = document.createElement('li');
        li.className = 'scheme-lesson' + (lesson.presentationId ? ' is-made' : '');
        li.dataset.index = index;
        const action = lesson.presentationId
            ? `<button type="button" class="scheme-go is-made" data-open>${lesson.madeStatus === 'done' ? 'Open' : 'Carry on'}</button>`
            : '<button type="button" class="scheme-go" data-make>Make lesson <span aria-hidden="true">→</span></button>';
        li.innerHTML = `
            <span class="scheme-lesson-num" title="Drag to reorder">${lesson.presentationId ? SCHEME_DONE_SVG : index + 1}</span>
            <div class="scheme-lesson-body">
                <div class="scheme-lesson-titlerow">
                    <input class="scheme-lesson-title" type="text" maxlength="200" value="${esc(lesson.title)}" aria-label="Lesson ${index + 1} title">
                    ${lesson.locked ? `<span class="scheme-lesson-kept" title="Kept when the outline is redone">${SCHEME_LOCK_SVG}</span>` : ''}
                </div>
                <textarea class="scheme-lesson-objective" rows="1" maxlength="400" placeholder="What pupils will be able to do" aria-label="Lesson ${index + 1} objective">${esc(lesson.objective)}</textarea>
            </div>
            <div class="scheme-lesson-side">
                ${action}
                <button type="button" class="scheme-lesson-more" data-more title="More" aria-label="More for lesson ${index + 1}" aria-haspopup="menu" aria-expanded="false">${DOTS_ICON}</button>
            </div>
        `;
        return li;
    }

    // field-sizing does this in Chrome; elsewhere a one-row box would cut the
    // objective to its first line.
    function fitObjective(el) {
        el.style.height = 'auto';
        // Nothing to measure while the outline is hidden; it is fitted again
        // when drawn on screen.
        if (el.scrollHeight) el.style.height = `${el.scrollHeight}px`;
    }

    // A narrower window wraps the objectives onto more lines.
    window.addEventListener('resize', () => {
        if (schemeView && !schemeView.hidden) schemeLessonsEl.querySelectorAll('.scheme-lesson-objective').forEach(fitObjective);
    });

    function moveSchemeLesson(from, to) {
        const lessons = state.scheme.lessons;
        if (to < 0 || to >= lessons.length) return;
        const [moved] = lessons.splice(from, 1);
        lessons.splice(to, 0, moved);
        renderScheme();
        queueSchemeSave();
    }

    function schemeLessonMenu(btn, at) {
        const lessons = state.scheme.lessons;
        const items = [];
        if (at.index > 0) items.push({ label: 'Move up', icon: SCHEME_UP_SVG, onClick: () => moveSchemeLesson(at.index, at.index - 1) });
        if (at.index < lessons.length - 1) items.push({ label: 'Move down', icon: SCHEME_DOWN_SVG, onClick: () => moveSchemeLesson(at.index, at.index + 1) });
        // A made lesson is always kept; the server holds it fixed on a redo.
        if (!at.lesson.presentationId) {
            items.push({
                label: at.lesson.locked ? 'Let Redo change this' : 'Keep when redoing',
                icon: at.lesson.locked ? SCHEME_UNLOCK_SVG : SCHEME_LOCK_SVG,
                onClick: () => { at.lesson.locked = !at.lesson.locked; renderScheme(); queueSchemeSave(); },
            });
        }
        if (lessons.length > 1) {
            items.push({
                label: at.lesson.presentationId ? 'Remove from series' : 'Delete lesson',
                icon: TRASH_ICON,
                danger: true,
                onClick: () => { lessons.splice(at.index, 1); renderScheme(); queueSchemeSave(); },
            });
        }
        openCardMenu(btn, items);
    }

    function schemeLessonAt(el) {
        const li = el.closest('.scheme-lesson');
        return li ? { li, index: Number(li.dataset.index), lesson: state.scheme.lessons[Number(li.dataset.index)] } : null;
    }

    // Typing edits the lesson in place and saves; it never re-renders, which
    // would take the caret out of the box being typed in.
    schemeLessonsEl?.addEventListener('input', (e) => {
        const at = schemeLessonAt(e.target);
        if (!at) return;
        if (e.target.classList.contains('scheme-lesson-title')) at.lesson.title = e.target.value;
        else if (e.target.classList.contains('scheme-lesson-objective')) {
            at.lesson.objective = e.target.value;
            fitObjective(e.target);
        } else return;
        queueSchemeSave();
    });

    schemeLessonsEl?.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        const at = btn && schemeLessonAt(btn);
        if (!at) return;
        if (btn.hasAttribute('data-make')) return makeSchemeLesson(at.lesson);
        if (btn.hasAttribute('data-open')) {
            saveSchemeNow();
            showPresList();
            return openPresentation(at.lesson.presentationId);
        }
        if (btn.hasAttribute('data-more')) {
            e.stopPropagation();
            schemeLessonMenu(btn, at);
        }
    });

    // Drag by the number: the row itself holds text boxes, and a draggable
    // ancestor stops a click-drag from selecting the text in them.
    let schemeDragFrom = null;
    schemeLessonsEl?.addEventListener('pointerdown', (e) => {
        const handle = e.target.closest('.scheme-lesson-num');
        if (handle) handle.closest('.scheme-lesson').draggable = true;
    });
    // A press on the handle that never became a drag leaves the row
    // draggable, which would stop text being selected in its boxes.
    schemeLessonsEl?.addEventListener('pointerup', () => {
        if (schemeDragFrom !== null) return;
        schemeLessonsEl.querySelectorAll('.scheme-lesson[draggable="true"]').forEach((li) => { li.draggable = false; });
    });
    schemeLessonsEl?.addEventListener('dragstart', (e) => {
        const at = schemeLessonAt(e.target);
        if (!at) return;
        schemeDragFrom = at.index;
        at.li.classList.add('is-dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(at.index));
    });
    function clearSchemeDropMarks() {
        schemeLessonsEl.querySelectorAll('.is-drop-before, .is-drop-after')
            .forEach((el) => el.classList.remove('is-drop-before', 'is-drop-after'));
    }
    function schemeDropAfter(li, e) {
        const r = li.getBoundingClientRect();
        return e.clientY > r.top + r.height / 2;
    }
    schemeLessonsEl?.addEventListener('dragover', (e) => {
        if (schemeDragFrom === null) return;
        const at = schemeLessonAt(e.target);
        if (!at) return;
        e.preventDefault();
        clearSchemeDropMarks();
        at.li.classList.add(schemeDropAfter(at.li, e) ? 'is-drop-after' : 'is-drop-before');
    });
    schemeLessonsEl?.addEventListener('drop', (e) => {
        if (schemeDragFrom === null) return;
        const at = schemeLessonAt(e.target);
        if (!at) return;
        e.preventDefault();
        let to = at.index + (schemeDropAfter(at.li, e) ? 1 : 0);
        if (to > schemeDragFrom) to -= 1;
        const [moved] = state.scheme.lessons.splice(schemeDragFrom, 1);
        state.scheme.lessons.splice(to, 0, moved);
        schemeDragFrom = null;
        renderScheme();
        queueSchemeSave();
    });
    schemeLessonsEl?.addEventListener('dragend', () => {
        schemeDragFrom = null;
        clearSchemeDropMarks();
        schemeLessonsEl.querySelectorAll('.scheme-lesson').forEach((li) => {
            li.classList.remove('is-dragging');
            li.draggable = false;
        });
    });

    $('scheme-add')?.addEventListener('click', () => {
        state.scheme.lessons.push({ id: Math.random().toString(16).slice(2, 14), title: 'New lesson', objective: '', topicIds: [], notes: '', locked: false });
        renderScheme();
        queueSchemeSave();
        const inputs = schemeLessonsEl.querySelectorAll('.scheme-lesson-title');
        inputs[inputs.length - 1]?.select();
    });

    schemeTitleEl?.addEventListener('input', () => {
        state.scheme.title = schemeTitleEl.value;
        queueSchemeSave();
    });

    // Saves are debounced and chained: the last edit always lands last, and
    // a redraft can wait for any save still in flight.
    let schemeSaveTimer = null;
    let schemeSaving = Promise.resolve();
    function queueSchemeSave() {
        clearTimeout(schemeSaveTimer);
        schemeSaveState.textContent = 'Unsaved changes';
        schemeSaveTimer = setTimeout(saveSchemeNow, 700);
    }
    function saveSchemeNow() {
        clearTimeout(schemeSaveTimer);
        const scheme = state.scheme;
        if (!scheme) return schemeSaving;
        const body = JSON.stringify({ title: scheme.title, lessons: scheme.lessons });
        schemeSaving = schemeSaving.then(async () => {
            schemeSaveState.textContent = 'Saving…';
            try {
                const res = await apiFetch(`/api/schemes/${scheme.id}`, { method: 'PUT', body });
                const data = await res.json().catch(() => ({}));
                schemeSaveState.textContent = res.ok ? 'Saved' : '';
                if (!res.ok) setError(schemeError, data.error || 'Could not save the outline');
            } catch (err) {
                schemeSaveState.textContent = '';
                if (err.message !== 'Not authenticated') setError(schemeError, 'Could not save the outline');
            }
        });
        return schemeSaving;
    }

    $('scheme-ask')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!state.scheme) return;
        const btn = $('scheme-redraft');
        const feedback = $('scheme-feedback-text').value.trim();
        clearTimeout(schemeSaveTimer);
        setError(schemeError, '');
        btn.disabled = true;
        btn.textContent = 'Redoing…';
        schemeView.classList.add('is-busy');
        try {
            await schemeSaving;
            const res = await apiFetch(`/api/schemes/${state.scheme.id}/redraft`, {
                method: 'POST',
                body: JSON.stringify({ title: state.scheme.title, lessons: state.scheme.lessons, feedback }),
            });
            const data = await res.json();
            if (!res.ok) return setError(schemeError, data.error || 'Could not redo the outline');
            state.scheme = data;
            schemeTitleEl.value = data.title || '';
            $('scheme-feedback-text').value = '';
            schemeSaveState.textContent = 'Saved';
            renderScheme();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(schemeError, 'Network error');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Redo outline';
            schemeView.classList.remove('is-busy');
        }
    });

    function openSchemeFolder(scheme) {
        saveSchemeNow();
        showPresList('presentations');
        if (scheme?.folderId) openFolder('presentation', scheme.folderId);
    }

    $('scheme-back')?.addEventListener('click', () => openSchemeFolder(state.scheme));

    // ── Lesson series: making one lesson ──────────────────
    // The presentation composer, filled in from the lesson's brief (its title
    // as the topic; objective, spec content and its neighbours as notes), so
    // the teacher chooses this lesson's own settings. The deck is linked to
    // the lesson as soon as it exists, which also files it in the folder.
    async function makeSchemeLesson(lesson) {
        const scheme = state.scheme;
        await saveSchemeNow();
        let brief;
        try {
            const res = await apiFetch(`/api/schemes/${scheme.id}/lessons/${lesson.id}/brief`);
            brief = await res.json();
            if (!res.ok) return setError(schemeError, brief.error || 'Could not open that lesson');
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(schemeError, 'Network error');
            return;
        }
        await prepareFromSession('presentation', {
            topic: brief.topic, examBoard: brief.examBoard, subject: brief.subject, level: brief.level, context: brief.context,
        });
        // The tier only exists once subject and level have drawn its options,
        // which tierPicker.js does on its own short timer.
        if (brief.tier) {
            setTimeout(() => {
                const tier = $('tier');
                if (tier && [...tier.options].some((o) => o.value === brief.tier)) {
                    tier.value = brief.tier;
                    tier.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }, 500);
        }
        // Topic, notes and course all come from the series; what is left to
        // choose is how this lesson looks. Back still reaches the first step.
        genWizard?.last();
        state.pendingSeriesLesson = { schemeId: scheme.id, lessonId: lesson.id };
        const back = $('gen-series-back');
        if (back) {
            back.textContent = `← Lesson ${brief.position} of ${brief.total} in ${scheme.title}`;
            back.title = 'Back to the series';
            back.hidden = false;
        }
    }

    async function linkSeriesLesson(presentationId) {
        const p = state.pendingSeriesLesson;
        if (!p) return;
        try {
            await apiFetch(`/api/schemes/${p.schemeId}/lessons/${p.lessonId}/link`, {
                method: 'POST', body: JSON.stringify({ presentationId }),
            });
        } catch { /* the deck still exists; it just is not ticked off in the outline */ }
    }

    /** Opens a series' outline; false when it could not be loaded. */
    async function openScheme(id, { push = true } = {}) {
        try {
            const res = await apiFetch(`/api/schemes/${encodeURIComponent(id)}`);
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Could not open the series'); return false; }
            showSeriesPage({ scheme: data, push });
            return true;
        } catch (err) {
            if (err.message !== 'Not authenticated') showToast('Network error');
            return false;
        }
    }

    $('gen-series-back')?.addEventListener('click', () => {
        const p = state.pendingSeriesLesson;
        if (p) openScheme(p.schemeId);
    });

    // Which presentations folders hold a series, so a folder's header can
    // offer to open it rather than start another.
    // On state rather than a local: the library header reads it, and can be
    // drawn before this part of the file has run.
    state.schemeByFolder = new Map();
    function rememberScheme(s) {
        if (s?.folderId) state.schemeByFolder.set(s.folderId, s.id);
    }
    async function loadSchemes() {
        try {
            const res = await apiFetch('/api/schemes');
            if (!res.ok) return;
            const { items } = await res.json();
            const byFolder = new Map();
            // Newest first, so where a folder somehow holds two the newest wins.
            (items || []).slice().reverse().forEach((s) => { if (s.folder_id) byFolder.set(s.folder_id, s.id); });
            state.schemeByFolder = byFolder;
            renderLibHead();
        } catch { /* the button just offers a new series */ }
    }

    // The dialog body scrolls, so a dropdown opening near the bottom of it is
    // clipped rather than escaping the card. Bringing the field into view first
    // puts the options in the space below it.
    $('mktpub-subject-input')?.addEventListener('focus', () => {
        $('mktpub-sc-wrap')?.closest('.mktpub-meta-grid')
            ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    // Level and board are short enough to be plain selects. Populated from the
    // shared list rather than written into the HTML, so adding a level is one
    // edit in one file.
    //
    // Both keep a blank first option: the fields are optional, and a listing
    // with no exam board is a normal thing – forcing a choice would mean
    // teachers picking one at random, which is worse than an empty column.
    function fillFacetSelect(el, values, blankLabel) {
        if (!el) return;
        el.innerHTML = '';
        const blank = document.createElement('option');
        blank.value = '';
        blank.textContent = blankLabel;
        el.appendChild(blank);
        for (const v of values) {
            const opt = document.createElement('option');
            opt.value = v;
            opt.textContent = v;
            el.appendChild(opt);
        }
    }
    fillFacetSelect($('mktpub-level'), MKT_LEVELS, 'Not specified');
    fillFacetSelect($('mktpub-board'), MKT_BOARDS, 'Not specified');

    // Selects a value, carrying an off-list one from an older listing rather
    // than dropping it. Rebuilds the options each time so a value kept for one
    // listing does not linger into the next.
    function setFacetSelect(el, value, values, blankLabel) {
        if (!el) return;
        fillFacetSelect(el, values, blankLabel);
        const wanted = String(value || '');
        if (wanted && !values.includes(wanted)) {
            const opt = document.createElement('option');
            opt.value = wanted;
            opt.textContent = `${wanted} (not on the list)`;
            el.appendChild(opt);
        }
        el.value = wanted;
    }

    // ── Auto-detect subject ───────────────────────────────
    function wireSubjectAutoDetect(topicEl, combobox) {
        let timer = null;
        topicEl?.addEventListener('input', () => {
            clearTimeout(timer);
            const topic = topicEl.value.trim();
            // Short enough to still catch one-word topics ("Vectors", "Osmosis").
            if (topic.length < 4) return;
            // A subject the teacher chose is theirs to keep. One we guessed is
            // only as good as the topic it was guessed from, so it is guessed
            // again when the topic changes – otherwise the first deck's subject
            // sticks to every topic typed after it.
            const mayFill = () => !combobox.getValue?.() || combobox.isAutoDetected?.();
            if (!mayFill()) return;
            timer = setTimeout(async () => {
                try {
                    const res = await apiFetch('/api/presentations/detect-subject', {
                        method: 'POST',
                        body: JSON.stringify({ topic }),
                    });
                    if (!res.ok) {
                        console.warn('[subject] detect-subject failed:', res.status);
                        return;
                    }
                    const { subject } = await res.json();
                    // The answer is for the topic as it was sent: drop it if the
                    // box has moved on or a subject was picked in the meantime.
                    if (topicEl.value.trim() !== topic || !mayFill()) return;
                    if (subject && subject !== combobox.getValue?.()) combobox.setAuto?.(subject);
                } catch (e) {
                    console.warn('[subject] detect-subject failed:', e && e.message);
                }
            }, 800);
        });
    }

    wireSubjectAutoDetect(topicInput, subjectCombobox);
    wireSubjectAutoDetect($('worksheet-topic'), wsSubjectCombobox);
    wireSubjectAutoDetect($('quiz-topic'), quizSubjectCombobox);
    wireSubjectAutoDetect($('sr-topic'), srSubjectCombobox);
    // The question is this form's topic – long enough to classify from.
    wireSubjectAutoDetect($('mk-question'), mkSubjectCombobox);

    generateForm?.addEventListener('submit', async (e) => {
        e.preventDefault(); setError(generateError, '');
        const topic = topicLineFor(topicInput, genTopicPicker);
        if (!topic) return setError(generateError, 'Please enter a topic or tick one from the list');

        // Captured now so later edits to the form can't change what gets built.
        state.pendingCompanions = {
            worksheet: !!$('pack-want-worksheet')?.checked,
            quiz: !!$('pack-want-quiz')?.checked,
        };

        // Read now: by the time the deck is done the composer is long gone.
        state.pendingStarterIds = genStarterSources();

        generateBtn.disabled = true;
        generateBtn.textContent = 'Planning…';
        if (slidePanel) slidePanel.innerHTML = '';
        if (stageSlideInner) stageSlideInner.innerHTML = '';
        state.slides.clear();
        state.planSlides = [];
        state.activeSlideId = null;
        hide(planView, pptEditor, generateCard);
        closeEventSources();
        showGenLoading('Planning your presentation', topic);

        const replanId = state.replanId;
        try {
            const res = await apiFetch(replanId ? `/api/presentations/${replanId}/replan` : '/api/presentations', {
                method: replanId ? 'PUT' : 'POST',
                body: JSON.stringify({
                    topic,
                    inputContent: sourceTextFor('input-content'),
                    theme: state.selectedTheme,
                    examBoard: $('exam-board')?.value || '',
                    subject: $('subject')?.value || '',
                    level: $('level')?.value || '',
                    tier: $('tier')?.value || '',
                    // Ids into data/spec-topics, not spec codes – see
                    // utils/specTopics.js – and the typed line beside them.
                    // Both absent unless a list was offered and something in
                    // it was ticked.
                    ...topicPartsFor(topicInput, genTopicPicker),
                    templateIds: selectedGenTemplateIds(),
                    supportLadder: $('gen-support-ladder')?.checked !== false,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                resetGenerateBtn();
                hideGenLoading();
                show(generateCard);
                return setError(generateError, data.error || 'Failed to create presentation');
            }
            state.presentationId = data.id;
            state.replanId = null;
            try { closeSeam(); } catch { /* not set up yet */ }
            // A replanned deck is already linked to wherever it came from.
            if (!replanId) {
                linkToTutoringSession('presentation', data.id);
                linkSeriesLesson(data.id);
            }
            warmExportPipeline();
            startPlanStream(data.id);
        } catch (err) {
            resetGenerateBtn();
            hideGenLoading();
            show(generateCard);
            if (err.message !== 'Not authenticated') setError(generateError, 'Network error');
        }
    });

    // The scaffolded-practice switch draws its state from data-on, like every
    // other toggle here; the checkbox underneath is what the create call reads.
    $('gen-support-ladder')?.addEventListener('change', (e) => {
        const sw = e.target.closest('.t-toggle');
        if (sw) { sw.classList.add('is-init'); sw.dataset.on = String(e.target.checked); }
    });

    // ── Waiting for a plan or an outline ──────────────────
    // Shown in the place the plan (or the series outline) will appear, so the
    // page has something the shape of what is coming instead of one line of
    // status.
    function showGenLoading(title, sub = '') {
        $('gen-loading-title').textContent = title;
        $('gen-loading-sub').textContent = sub;
        show($('gen-loading'));
        window.scrollTo({ top: 0 });
    }
    function hideGenLoading() { hide($('gen-loading')); }

    // ── Plan stream ───────────────────────────────────────
    const TEMPLATE_LABELS = {
        title: 'Title', 'title-image': 'Title (Image)', content: 'Content', 'two-column': 'Two Column',
        'comparison-diagram': 'Comparison + Diagram', 'process-flow': 'Process Flow',
        quote: 'Quote', chapter: 'Chapter', 'image-text': 'Image + Text',
        timeline: 'Timeline', definition: 'Definition',
        'term-grid': 'Term Grid', concept: 'Concept',
        'sentence-starters': 'Sentence Starters', 'structured-prompt': 'Structured Prompt',
        'worked-example': 'Worked Example', 'talk-task': 'Talk Task',
        'true-false': 'True or False', 'odd-one-out': 'Odd One Out', 'spot-the-mistake': 'Spot the Mistake',
        'multiple-choice': 'Multiple Choice', 'word-cards': 'Word Cards', 'maths-picture': 'Maths Picture',
    };

    function startPlanStream(presId) {
        closeEventSources();
        const es = new EventSource('/api/presentations/' + presId + '/plan-stream?slideCount=' + state.slideCount);
        state.planEventSource = es;

        es.addEventListener('plan_done', (e) => {
            es.close(); state.planEventSource = null;
            hideGenLoading();
            const plan = JSON.parse(e.data);
            showPlanApproval(plan);
        });

        es.addEventListener('quota_exceeded', (e) => {
            es.close(); state.planEventSource = null;
            hideGenLoading();
            show(generateCard);
            resetGenerateBtn();
            try { showQuotaModal(JSON.parse(e.data)); } catch { showQuotaModal({}); }
        });

        es.addEventListener('error', (e) => {
            es.close(); state.planEventSource = null;
            hideGenLoading();
            show(generateCard);
            resetGenerateBtn();
            try {
                const data = JSON.parse(e.data);
                setError(generateError, data.message || 'Planning failed');
            } catch { setError(generateError, 'Planning failed'); }
        });
    }

    // ── Plan approval with reorder + notes ────────────────
    function showPlanApproval(plan) {
        state.planSlides = plan.slides.map(s => ({ ...s, notes: s.notes || '' }));
        state.planTitle = plan.title;
        planTitleEl.textContent = plan.title;
        updatePlanSubtitle();
        setError($('plan-error'), '');
        planSlidesEl.classList.remove('is-settled');
        renderPlanCards();
        show(planView);
        window.scrollTo({ top: 0 });
        btnApprovePlan.disabled = false;
        btnApprovePlan.textContent = 'Generate presentation';
    }

    function updatePlanSubtitle() {
        const n = state.planSlides.length;
        planSubtitle.textContent = `${n} slide${n === 1 ? '' : 's'} · move, delete or add a note to any slide`;
    }

    function renderPlanCards() {
        planSlidesEl.innerHTML = '';
        state.planSlides.forEach((slide, index) => {
            planSlidesEl.appendChild(buildPlanCard(slide, index));
        });
    }

    // One row per slide, in the order they will be presented: number, title,
    // what kind of slide it is, and its points as a line. Moving and notes
    // sit at the row's end and show on hover, or when there is a note.
    function buildPlanCard(slide, index) {
        const total = state.planSlides.length;
        const li = document.createElement('li');
        li.className = 'plan-card' + (slide.notes ? ' has-note' : '');
        li.dataset.index = index;
        const points = (slide.key_points || []).filter(Boolean);
        li.innerHTML = `
            <span class="plan-card-num">${index + 1}</span>
            <div class="plan-card-body">
                <div class="plan-card-titleline">
                    <span class="plan-card-slide-title">${esc(slide.slide_title)}</span>
                    <span class="plan-card-kind">${esc(TEMPLATE_LABELS[slide.template] || slide.template || '')}</span>
                </div>
                ${points.length ? `<p class="plan-card-points">${points.map(esc).join(' · ')}</p>` : ''}
                <div class="plan-card-notes" ${slide.notes ? '' : 'hidden'}>
                    <textarea rows="2" placeholder="Instructions for this slide, e.g. focus on the 2021 data" aria-label="Note for slide ${index + 1}">${esc(slide.notes)}</textarea>
                </div>
            </div>
            <div class="plan-card-tools">
                <button type="button" class="plan-card-tool plan-card-notes-toggle">${slide.notes ? 'Edit note' : 'Add note'}</button>
                <button type="button" class="plan-card-tool plan-card-move move-up" title="Move up" aria-label="Move slide ${index + 1} up" ${index === 0 ? 'disabled' : ''}>${SCHEME_UP_SVG}</button>
                <button type="button" class="plan-card-tool plan-card-move move-down" title="Move down" aria-label="Move slide ${index + 1} down" ${index === total - 1 ? 'disabled' : ''}>${SCHEME_DOWN_SVG}</button>
                <button type="button" class="plan-card-tool plan-card-move plan-card-delete" title="Delete slide" aria-label="Delete slide ${index + 1}" ${total <= 1 ? 'disabled' : ''}>${TRASH_ICON}</button>
            </div>
        `;

        const move = (to) => {
            syncNotes();
            [state.planSlides[index], state.planSlides[to]] = [state.planSlides[to], state.planSlides[index]];
            planSlidesEl.classList.add('is-settled');
            renderPlanCards();
            planSlidesEl.querySelectorAll('.plan-card')[to]?.classList.add('is-moved');
        };
        li.querySelector('.move-up')?.addEventListener('click', () => { if (index > 0) move(index - 1); });
        li.querySelector('.move-down')?.addEventListener('click', () => { if (index < total - 1) move(index + 1); });
        li.querySelector('.plan-card-delete')?.addEventListener('click', () => {
            if (state.planSlides.length <= 1) return;
            syncNotes();
            state.planSlides.splice(index, 1);
            planSlidesEl.classList.add('is-settled');
            renderPlanCards();
            updatePlanSubtitle();
        });

        const notesWrap = li.querySelector('.plan-card-notes');
        li.querySelector('.plan-card-notes-toggle')?.addEventListener('click', () => {
            notesWrap.hidden = !notesWrap.hidden;
            if (!notesWrap.hidden) notesWrap.querySelector('textarea')?.focus();
        });
        notesWrap.querySelector('textarea')?.addEventListener('input', (e) => {
            state.planSlides[index].notes = e.target.value;
            li.classList.toggle('has-note', !!e.target.value.trim());
        });

        return li;
    }

    // Read any notes the user typed before re-render or approve
    function syncNotes() {
        planSlidesEl.querySelectorAll('.plan-card').forEach((card) => {
            const i = parseInt(card.dataset.index);
            const ta = card.querySelector('.plan-card-notes textarea');
            if (ta && state.planSlides[i] !== undefined) {
                state.planSlides[i].notes = ta.value;
            }
        });
    }

    // "Ask for changes": the plan as the teacher has it now – reordered,
    // trimmed, noted – goes back with what they want different, and the
    // revised plan replaces it here and on the server.
    $('plan-ask')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = $('plan-feedback-text');
        const feedback = input.value.trim();
        if (!state.presentationId || !feedback) { input.focus(); return; }
        syncNotes();
        const btn = $('plan-redraft');
        setError($('plan-error'), '');
        btn.disabled = true;
        btn.textContent = 'Redoing…';
        btnApprovePlan.disabled = true;
        planView.classList.add('is-busy');
        try {
            const res = await apiFetch(`/api/presentations/${state.presentationId}/plan/revise`, {
                method: 'POST',
                body: JSON.stringify({ slides: state.planSlides, feedback }),
            });
            const data = await res.json();
            if (!res.ok) return setError($('plan-error'), data.error || 'Could not change the plan');
            input.value = '';
            showPlanApproval({ title: state.planTitle || data.title, slides: data.slides });
        } catch (err) {
            if (err.message !== 'Not authenticated') setError($('plan-error'), 'Network error');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Redo plan';
            btnApprovePlan.disabled = false;
            planView.classList.remove('is-busy');
        }
    });

    btnApprovePlan?.addEventListener('click', async () => {
        if (!state.presentationId) return;
        syncNotes();
        btnApprovePlan.disabled = true;
        btnApprovePlan.textContent = 'Generating…';

        try {
            const res = await apiFetch(`/api/presentations/${state.presentationId}/approve`, {
                method: 'POST',
                body: JSON.stringify({
                    plan: { title: state.planTitle, slides: state.planSlides },
                }),
            });
            if (!res.ok) {
                const data = await res.json();
                btnApprovePlan.disabled = false; btnApprovePlan.textContent = 'Generate presentation';
                return setError($('plan-error'), data.error || 'Approval failed');
            }
            hide(planView);
            setProgressText('Generating slides…');
            show(progressBar);
            startGenerateStream(state.presentationId);
        } catch (err) {
            btnApprovePlan.disabled = false; btnApprovePlan.textContent = 'Generate presentation';
            if (err.message !== 'Not authenticated') setError($('plan-error'), 'Network error');
        }
    });

    // Back to the form with this deck's settings, on the step that holds
    // them. Submitting then plans the same deck again (state.replanId) rather
    // than making another and leaving this one planned in the library.
    btnRejectPlan?.addEventListener('click', async () => {
        const id = state.presentationId;
        const resumed = state.resumedPres;
        hide(planView);
        // Opened from the library, so the form is empty: fill it from the row.
        if (resumed) await fillComposerFrom(resumed);
        show(generateCard);
        resetGenerateBtn();
        state.presentationId = null;
        state.replanId = id;
        genWizard?.last();
        window.scrollTo({ top: 0 });
    });

    async function fillComposerFrom(pres) {
        const topicIds = parseJson(pres.topics_json) || [];
        await prepareFromSession('presentation', {
            topic: pres.topic || '', examBoard: pres.exam_board || '', subject: pres.subject || '', level: pres.level || '',
            context: pres.input_content || '', topicIds,
        });
        // With ticks the box holds only what was typed beside them.
        if (topicIds.length && topicInput) topicInput.value = pres.topic_note || '';
        hide(planView, generateCard);
        state.selectedTheme = pres.theme || 'classic';
        syncThemePickers(state.selectedTheme);
        const ladder = $('gen-support-ladder');
        if (ladder) {
            ladder.checked = pres.support_ladder !== 0;
            const sw = ladder.closest('.t-toggle');
            if (sw) sw.dataset.on = String(ladder.checked);
        }
        if (pres.tier) {
            setTimeout(() => {
                const tier = $('tier');
                if (tier && [...tier.options].some((o) => o.value === pres.tier)) {
                    tier.value = pres.tier;
                    tier.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }, 500);
        }
    }

    // ── Generate stream ───────────────────────────────────
    function startGenerateStream(presId) {
        closeEventSources();
        hide(generateCard);
        if (pptChromeTitle) pptChromeTitle.textContent = state.planTitle || state.presentationTopic || 'Untitled presentation';
        show(pptEditor);
        applyTheme(state.selectedTheme);
        switchPptMode('direct');

        const es = new EventSource('/api/presentations/' + presId + '/stream');
        state.genEventSource = es;

        es.addEventListener('slide_start', (e) => {
            const { slideId, position, title } = JSON.parse(e.data);
            setProgressText(`Generating slide ${position}: ${title}…`);
            addSlideThumb(position, slideId);
        });

        es.addEventListener('slide_done', (e) => {
            const { slideId, position, html: generatedHtml, contents_json, elements_json, json_schema_version, speaker_notes } = JSON.parse(e.data);
            rememberNotes(slideId, speaker_notes);
            if (slideId === state.activeSlideId) showSlideNotes(slideId);
            let html = generatedHtml;
            let contents = parseJson(contents_json);
            let elements = parseJson(elements_json);
            // The server sizes text boxes from an estimate; measured here, a box
            // it made a line short grows (and the slide is saved that way)
            // rather than its text running over what is underneath.
            let refitted = false;
            if (json_schema_version === 1 && contents && Array.isArray(elements)) {
                const fitted = fitToText({ contents, elements });
                if (fitted.elements !== elements) {
                    elements = fitted.elements;
                    html = SlideModelRenderer.buildHtml(contents, elements);
                    refitted = true;
                }
            }
            state.slides.set(position, {
                id: slideId,
                html,
                contents_json: contents,
                elements_json: elements,
                json_schema_version,
            });
            if (refitted) saveSlideById(slideId);
            updateThumb(position, slideId, html);
            // Auto-select first completed slide
            if (!state.activeSlideId) selectSlide(slideId, html, position);
        });

        es.addEventListener('slide_error', (e) => {
            const { slideId, message } = JSON.parse(e.data);
            markThumbError(slideId, message);
        });

        es.addEventListener('quota_exceeded', (e) => {
            es.close(); state.genEventSource = null;
            hide(progressBar);
            hide(pptEditor);
            resetGenerateBtn();
            try { showQuotaModal(JSON.parse(e.data)); } catch { showQuotaModal({}); }
        });

        es.addEventListener('presentation_done', () => {
            // Land on the check before the bar leaves, so the deck finishing
            // reads as a completed step rather than the bar just vanishing.
            setProgressDone(true);
            setProgressText('Deck ready');
            setTimeout(() => hide(progressBar), 600);
            resetGenerateBtn();
            if (stageInfo) stageInfo.textContent = `Slide ${state.activeSlidePosition} / ${state.slides.size}`;
            es.close(); state.genEventSource = null;
            // The deck is saved and on screen; any companions build alongside it.
            startCompanionsStream(presId);
            // And the starter, if one was asked for in the composer.
            addPendingStarter(presId);
        });

        es.addEventListener('error', (e) => {
            try { const d = JSON.parse(e.data); setError(generateError, d.message || 'Generation error'); } catch {}
            hide(progressBar);
            resetGenerateBtn();
            es.close(); state.genEventSource = null;
        });
    }

    // ── Lesson pack companions ────────────────────────────
    // Runs after the deck is done: the worksheet and exit ticket are generated
    // and saved server-side, so closing the tab doesn't lose them. Each is
    // independent – one failing or hitting the quota never blocks the other.

    // How boards describe where a mark sits inside its level.
    const PLACEMENT_LABELS = {
        'borderline': 'bottom of the band',
        'just-enough': 'just enough for the band',
        'slight-inconsistency': 'meets the band, a little inconsistently',
        'consistent': 'top of the band',
    };

    const PACK_ARTIFACTS = {
        worksheet: { label: 'Worksheet',       open: (id) => openWorksheet(id) },
        quiz:      { label: 'Exit-ticket quiz', open: (id) => openQuiz(id) },
    };

    function hidePackDock() {
        if (state.packEventSource) {
            state.packEventSource.close();
            state.packEventSource = null;
        }
        if (packDockList) packDockList.innerHTML = '';
        hide(packDock);
    }

    function packRow(kind) {
        return packDockList?.querySelector(`[data-artifact="${kind}"]`) || null;
    }

    function setPackRow(kind, status, { id = null, message = '' } = {}) {
        const row = packRow(kind);
        if (!row) return;
        row.dataset.status = status;

        const statusEl = row.querySelector('.pack-row-status');
        const actionEl = row.querySelector('.pack-row-action');
        if (!statusEl || !actionEl) return;

        const text = {
            queued:     'Waiting…',
            generating: 'Writing questions…',
            done:       'Ready',
            blocked:    'Monthly limit reached',
            failed:     message || 'Could not generate',
        }[status] || '';
        statusEl.textContent = text;

        actionEl.innerHTML = '';
        if (status === 'done' && id) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pack-row-btn';
            btn.textContent = 'Open';
            // Opening navigates away from the editor, so the dock goes with it.
            btn.addEventListener('click', () => { hidePackDock(); PACK_ARTIFACTS[kind]?.open(id); });
            actionEl.appendChild(btn);
        } else if (status === 'blocked') {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pack-row-btn pack-row-btn-upgrade';
            btn.textContent = 'Upgrade';
            btn.addEventListener('click', () => showQuotaModal(row._quota || {}));
            actionEl.appendChild(btn);
        }
    }

    function buildPackDock(kinds) {
        if (!packDock || !packDockList) return;
        packDockList.innerHTML = '';
        for (const kind of kinds) {
            const li = document.createElement('li');
            li.className = 'pack-row';
            li.dataset.artifact = kind;
            li.dataset.status = 'queued';
            li.innerHTML =
                '<span class="pack-row-spin" aria-hidden="true"></span>' +
                `<span class="pack-row-body"><span class="pack-row-label">${PACK_ARTIFACTS[kind].label}</span>` +
                '<span class="pack-row-status">Waiting…</span></span>' +
                '<span class="pack-row-action"></span>';
            packDockList.appendChild(li);
        }
        show(packDock);
    }

    function startCompanionsStream(presId) {
        const { worksheet: wantWorksheet, quiz: wantQuiz } = state.pendingCompanions;
        if (!wantWorksheet && !wantQuiz) return;

        const kinds = [];
        if (wantWorksheet) kinds.push('worksheet');
        if (wantQuiz) kinds.push('quiz');
        buildPackDock(kinds);

        const params = new URLSearchParams({
            worksheet: wantWorksheet ? '1' : '0',
            quiz: wantQuiz ? '1' : '0',
        });
        const es = new EventSource(`/api/packs/${presId}/companions/stream?${params}`);
        state.packEventSource = es;

        es.addEventListener('stage', (e) => {
            const { artifact } = JSON.parse(e.data);
            setPackRow(artifact, 'generating');
        });

        es.addEventListener('artifact_saved', (e) => {
            const { artifact, id } = JSON.parse(e.data);
            setPackRow(artifact, 'done', { id });
        });

        es.addEventListener('artifact_error', (e) => {
            const { artifact, message } = JSON.parse(e.data);
            setPackRow(artifact, 'failed', { message });
        });

        // The deck already generated, so this is the moment Pro is worth most:
        // show the block in place rather than throwing up a modal over the work.
        es.addEventListener('quota_exceeded', (e) => {
            let data = {};
            try { data = JSON.parse(e.data); } catch {}
            const row = packRow(data.artifact);
            if (row) row._quota = data;
            setPackRow(data.artifact, 'blocked');
        });

        es.addEventListener('done', () => {
            es.close(); state.packEventSource = null;
            const head = packDock?.querySelector('.pack-dock-title');
            if (head) head.textContent = 'Lesson pack ready';
        });

        es.addEventListener('error', (e) => {
            try {
                const d = JSON.parse(e.data);
                for (const kind of kinds) {
                    if (packRow(kind)?.dataset.status !== 'done') {
                        setPackRow(kind, 'failed', { message: d.message });
                    }
                }
            } catch { /* transport-level error – EventSource retries on its own */ }
            es.close(); state.packEventSource = null;
        });
    }

    $('pack-dock-close')?.addEventListener('click', hidePackDock);

    // ── Pack side marker ──────────────────────────────────
    // When the open resource belongs to a lesson pack, a few dots sit against
    // the edge of the window. At rest that is all they are; hovering or
    // focusing one names it. Nothing appears for a resource that stands alone.

    const packSide = $('pack-side');

    function hidePackSide() {
        if (!packSide) return;
        packSide.innerHTML = '';
        hide(packSide);
    }

    /**
     * @param {string} presentationId the deck the pack hangs off
     * @param {{type: 'presentation'|'worksheet'|'quiz', id?: string}} current
     */
    async function renderPackSide(presentationId, current) {
        hidePackSide();
        if (!packSide || !presentationId) return;

        let pack;
        try {
            const res = await apiFetch(`/api/packs/${presentationId}`);
            if (!res.ok) return;
            pack = await res.json();
        } catch { return; }

        const qs = (n) => `${n} question${n === 1 ? '' : 's'}`;
        const members = [
            {
                kind: 'presentation',
                name: pack.presentation.title || pack.presentation.topic || 'Presentation',
                meta: pack.presentation.slide_count
                    ? `${pack.presentation.slide_count} slide${pack.presentation.slide_count === 1 ? '' : 's'}`
                    : 'Slides',
                current: current.type === 'presentation',
                open: () => openPresentation(pack.presentation.id),
            },
            ...pack.worksheets.map(w => ({
                kind: 'worksheet',
                name: w.title || 'Worksheet',
                meta: qs(w.question_count),
                current: current.type === 'worksheet' && w.id === current.id,
                open: () => openWorksheet(w.id),
            })),
            ...pack.quizzes.map(z => ({
                kind: 'quiz',
                name: z.title || 'Quiz',
                meta: qs(z.question_count),
                current: current.type === 'quiz' && z.id === current.id,
                open: () => openQuiz(z.id),
            })),
        ];

        // One item is not a pack – nothing to point at.
        if (members.length < 2) return;

        for (const m of members) {
            const el = document.createElement(m.current ? 'div' : 'button');
            el.className = 'pks-dot';
            el.dataset.kind = m.kind;
            if (m.current) {
                el.dataset.current = '1';
                el.setAttribute('aria-current', 'true');
            } else {
                el.type = 'button';
                el.addEventListener('click', m.open);
            }
            const name = document.createElement('span');
            name.className = 'pks-name';
            name.textContent = m.name;
            const meta = document.createElement('span');
            meta.className = 'pks-meta';
            meta.textContent = m.current ? 'Open now' : m.meta;
            const tip = document.createElement('span');
            tip.className = 'pks-tip';
            tip.append(name, meta);
            el.appendChild(tip);
            el.setAttribute('aria-label', `${m.name} – ${m.meta}`);
            packSide.appendChild(el);
        }
        show(packSide);
    }

    // ── PPT editor ────────────────────────────────────────

    const stageOverlay = $('stage-overlay');
    const selBox       = $('sel-box');
    const snapLineV    = $('snap-line-v');
    const snapLineH    = $('snap-line-h');
    const slideCtxMenu = $('slide-ctx-menu');

    function scaleStage() {
        if (!stageCanvas || !stageSlideInner) return;
        const w = stageCanvas.offsetWidth;
        if (!w) return;
        const scale = w / 960;
        stageSlideInner.style.transform = `scale(${scale})`;
        if (stageOverlay) stageOverlay.style.transform = `scale(${scale})`;
        stageCanvas.style.height = (540 * scale) + 'px';
    }

    // ── Present mode (fullscreen slideshow) ────────────────
    function orderedSlideList() {
        return [...state.slides.entries()].sort((a, b) => a[0] - b[0]).map(([, s]) => s);
    }

    function scalePresentStage() {
        if (!presentStage) return;
        // visualViewport tracks the actually-visible area on mobile (shrinks
        // while the browser's address bar/toolbar is showing); window.inner*
        // stays at the static layout size and would scale the slide too
        // large, cutting off its bottom behind the browser chrome.
        const vw = window.visualViewport?.width  || window.innerWidth;
        const vh = window.visualViewport?.height || window.innerHeight;
        const scale = Math.min(vw / 960, vh / 540);
        presentStage.style.transform = `scale(${scale})`;
    }

    // Parts of a slide that arrive one click at a time: the steps of a worked
    // example, the answers on an answer slide. The renderers number them with
    // data-reveal; a slide with none of them behaves exactly as it always did.
    function presentRevealSteps() {
        if (!presentStage) return [];
        const steps = [...presentStage.querySelectorAll('[data-reveal]')]
            .map(el => Number(el.dataset.reveal))
            .filter(n => n > 0);
        return [...new Set(steps)].sort((a, b) => a - b);
    }

    function applyPresentReveal() {
        if (!presentStage) return;
        presentStage.querySelectorAll('[data-reveal]').forEach((el) => {
            el.classList.toggle('is-unrevealed', Number(el.dataset.reveal) > state.presentStep);
        });
        // The arrows stay live while there is still something on this slide to
        // show or take back, even on the first and last slides of the deck.
        const steps = presentRevealSteps();
        const total = orderedSlideList().length;
        const hasHidden = steps.some(n => n > state.presentStep);
        const hasShown = steps.some(n => n <= state.presentStep);
        if (presentPrev) presentPrev.disabled = state.presentPosition === 1 && !hasShown;
        if (presentNext) presentNext.disabled = state.presentPosition === total && !hasHidden;
    }

    // `revealed` is for arriving backwards: a slide the class has already been
    // through comes back as they left it, not blanked for a second run.
    function renderPresentSlide(pos, { revealed = false } = {}) {
        const slides = orderedSlideList();
        if (!slides.length || !presentStage) return;
        const clamped = Math.min(Math.max(pos, 1), slides.length);
        state.presentPosition = clamped;
        SlideRenderer.renderSlideHtml(presentStage, slides[clamped - 1].html);
        if (presentNotes) presentNotes.textContent = slideNotes.get(slides[clamped - 1].id) || 'No notes for this slide.';
        state.presentStep = revealed ? Infinity : 0;
        applyPresentReveal();
        // Photos are on screen fullscreen here, so the credit has to be too –
        // in the overlay chrome alongside the counter, never on the slide.
        renderUnsplashCredits(presentStage, presentCredits, false);
        if (presentCounter) presentCounter.textContent = `${clamped} / ${slides.length}`;
    }

    function goPresentSlide(delta) {
        // Within the slide first: forward brings in the next hidden part, back
        // takes the last one away. Only when there is nothing left to do here
        // does the deck move.
        const steps = presentRevealSteps();
        if (delta > 0) {
            const next = steps.find(n => n > state.presentStep);
            if (next) { state.presentStep = next; applyPresentReveal(); return; }
        } else {
            const shown = steps.filter(n => n <= state.presentStep);
            if (shown.length) {
                state.presentStep = shown.length > 1 ? shown[shown.length - 2] : 0;
                applyPresentReveal();
                return;
            }
        }

        const before = state.presentPosition;
        const target = Math.min(Math.max(before + delta, 1), orderedSlideList().length);
        // Re-rendering the slide already on screen would reset what has been
        // revealed on it, so an arrow held at either end of the deck does nothing.
        if (target === before) return;
        renderPresentSlide(target, { revealed: delta < 0 });
        // Counts moves that landed somewhere new, so holding down an arrow at
        // the last slide doesn't read as having taught forty of them.
        if (state.presentPosition !== before) presentSlidesSeen += 1;
    }

    // How much presenting counts as having taught the lesson. Opening the
    // slideshow to check a typo and closing it again is not a moment to ask
    // anyone for anything, so a run has to reach a few slides or a minute
    // before the prompt on the way out is warranted.
    const PRESENTED_ENOUGH_SLIDES = 3;
    const PRESENTED_ENOUGH_MS = 60000;
    let presentStartedAt = 0;
    let presentSlidesSeen = 0;

    function startPresentMode() {
        if (!state.slides.size || !presentOverlay) return;
        endCrop(false);
        // Whatever was changed a moment ago is part of what gets presented.
        if (autoSavePending) { clearTimeout(autoSaveTimer); savePptDirect(); }
        presentStartedAt = Date.now();
        presentSlidesSeen = 1;
        state.presenting = true;
        window.ImageBankPanel?.close();
        if (presentStage) presentStage.dataset.theme = state.selectedTheme;
        if (presentNotes) presentNotes.hidden = true;
        show(presentOverlay);
        scalePresentStage();
        renderPresentSlide(state.activeSlidePosition || 1);
        window.addEventListener('resize', scalePresentStage);
        // Mobile address-bar show/hide changes visualViewport without always
        // firing window's resize event, so the slide would stay mis-scaled.
        window.visualViewport?.addEventListener('resize', scalePresentStage);
        // Fullscreen may be blocked (e.g. iframe without allow="fullscreen") –
        // the overlay itself still covers the viewport as a fallback either way.
        presentOverlay.requestFullscreen?.().catch(() => {});
    }

    function exitPresentMode() {
        if (!state.presenting) return;
        state.presenting = false;
        hide(presentOverlay);
        window.removeEventListener('resize', scalePresentStage);
        window.visualViewport?.removeEventListener('resize', scalePresentStage);
        if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});

        // The best moment there is to ask: they have just taught the thing, so
        // they know whether it was any good. Delayed a beat so it arrives after
        // the editor is back rather than on top of the overlay closing.
        const taught = presentSlidesSeen >= PRESENTED_ENOUGH_SLIDES
            || (presentStartedAt && Date.now() - presentStartedAt >= PRESENTED_ENOUGH_MS);
        if (taught) {
            setTimeout(() => showShareNudge({
                title: 'You just taught this',
                sub: 'Share it so the next teacher doesn’t have to build it.',
            }), 900);
        }
        presentStartedAt = 0;
        presentSlidesSeen = 0;
    }

    pptBtnPresent?.addEventListener('click', startPresentMode);
    presentExit?.addEventListener('click', exitPresentMode);
    presentPrev?.addEventListener('click', () => goPresentSlide(-1));
    presentNext?.addEventListener('click', () => goPresentSlide(1));

    // Click anywhere on the stage (not the nav controls) to advance
    presentOverlay?.addEventListener('click', (e) => {
        if (e.target.closest('.present-nav')) return;
        goPresentSlide(1);
    });

    // Swipe left/right to change slides on touch devices – tapping alone
    // only goes forward, and there's no keyboard on a phone.
    let touchStartX = null, touchStartY = null, touchSwiped = false;
    presentOverlay?.addEventListener('touchstart', (e) => {
        if (!state.presenting || e.target.closest('.present-nav')) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchSwiped = false;
    }, { passive: true });
    presentOverlay?.addEventListener('touchmove', (e) => {
        if (touchStartX === null) return;
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;
        if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) touchSwiped = true;
    }, { passive: true });
    presentOverlay?.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (touchSwiped) {
            e.preventDefault(); // suppress the synthetic click so it doesn't also advance
            goPresentSlide(dx < 0 ? 1 : -1);
        }
    }, { passive: false });

    document.addEventListener('keydown', (e) => {
        if (!state.presenting) return;
        if (e.key === 'Escape') { exitPresentMode(); return; }
        // N shows the teacher's notes. Off by default, and off again on every
        // start, because on a single screen the class can read them too.
        if ((e.key === 'n' || e.key === 'N') && !e.metaKey && !e.ctrlKey && presentNotes) { presentNotes.hidden = !presentNotes.hidden; return; }
        if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); goPresentSlide(1); }
        else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(e.key)) { e.preventDefault(); goPresentSlide(-1); }
    });

    // Browser-native fullscreen exit (Esc, F11, etc.) bypasses our button/keydown
    // handlers entirely – sync state so the overlay doesn't stay stuck open.
    document.addEventListener('fullscreenchange', () => {
        if (!document.fullscreenElement && state.presenting) exitPresentMode();
    });

    // Convert an element's screen rect to 960×540 CSS coords
    function getElBoundsInSlide(el) {
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return null;
        const sr = slideEl.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        const s  = 960 / sr.width;
        return {
            left:   (er.left   - sr.left) * s,
            top:    (er.top    - sr.top)  * s,
            width:  er.width  * s,
            height: er.height * s,
            right:  (er.right  - sr.left) * s,
            bottom: (er.bottom - sr.top)  * s,
        };
    }

    // ── Cropped pictures ──────────────────────────────────
    // A crop is a mask, not a new image: the <img> keeps the whole picture
    // stretched over its box (object-fit: fill) and a clip-path hides the
    // edges. Nothing is re-uploaded, the crop can be reopened and changed, and
    // because the mask is in percentages it stretches with the picture.
    //
    // The cost is that a cropped picture's box is bigger than what shows. The
    // element's own geometry (drag, model, exports) stays in terms of the box;
    // anything drawn FOR THE TEACHER - the selection frame, its handles, the
    // snap lines - uses visibleBoundsOf instead.

    /** { t, r, b, l } as fractions of the box, plus any corner rounding; null when not cropped. */
    function cropInsetsOf(el) {
        const m = /^inset\((.*?)(?:\s+round\s+(.+))?\)$/.exec((el?.style?.clipPath || '').trim());
        if (!m) return null;
        const v = m[1].trim().split(/\s+/).map(x => (parseFloat(x) || 0) / 100);
        // The browser hands the four values back in CSS shorthand.
        const t = v[0] || 0;
        const r = v.length > 1 ? v[1] : t;
        const b = v.length > 2 ? v[2] : t;
        const l = v.length > 3 ? v[3] : r;
        return { t, r, b, l, round: m[2] || '' };
    }

    function setCropInsets(el, ins) {
        const pct = n => (Math.max(0, n) * 100).toFixed(3).replace(/\.?0+$/, '') + '%';
        const none = !ins || [ins.t, ins.r, ins.b, ins.l].every(n => n < 0.0005);
        if (none && !ins?.round) { el.style.removeProperty('clip-path'); return; }
        el.style.clipPath = `inset(${pct(ins.t)} ${pct(ins.r)} ${pct(ins.b)} ${pct(ins.l)}${ins.round ? ' round ' + ins.round : ''})`;
    }

    /** The part of an element that actually shows, in slide coordinates. */
    function visibleBoundsOf(el) {
        const b = getElBoundsInSlide(el);
        const ins = b && cropInsetsOf(el);
        // A picture fitted inside its box ("contain") leaves empty margins.
        // The frame goes round the picture, not round the space reserved for it.
        if (b && !ins && el.tagName === 'IMG' && el.naturalWidth && el.naturalHeight
            && getComputedStyle(el).objectFit === 'contain') {
            const k = Math.min(b.width / el.naturalWidth, b.height / el.naturalHeight);
            const width = el.naturalWidth * k, height = el.naturalHeight * k;
            const left = b.left + (b.width - width) / 2, top = b.top + (b.height - height) / 2;
            return { left, top, width, height, right: left + width, bottom: top + height };
        }
        if (!ins) return b;
        const left = b.left + ins.l * b.width;
        const top = b.top + ins.t * b.height;
        const width = b.width * (1 - ins.l - ins.r);
        const height = b.height * (1 - ins.t - ins.b);
        return { left, top, width, height, right: left + width, bottom: top + height };
    }

    // Takes a flow element out of its template's layout so it can be given a
    // box of its own. `freezeSiblings` pins the ones beside it where they are
    // first, or they would close up into the gap it leaves.
    function promoteToAbsolute(m, bounds, origin, freezeSiblings) {
        if (isAbsoluteEl(m) || !m.parentElement) return;
        if (freezeSiblings) {
            Array.from(m.parentElement.children).forEach(sibling => {
                if (sibling === m || isAbsoluteEl(sibling)) return;
                const sb = getElBoundsInSlide(sibling);
                if (!sb) return;
                sibling.style.position = 'absolute';
                // The bounds measured above are the border box. A template's own
                // margin (the question's 0.3rem, the answer box's 0.6rem) is added
                // on top of left/top once the element is absolute, so every
                // frozen sibling used to jump by its margin on the first resize.
                sibling.style.margin   = '0';
                sibling.style.left     = (sb.left - origin.left) + 'px';
                sibling.style.top      = (sb.top  - origin.top)  + 'px';
                sibling.style.width    = sb.width  + 'px';
                sibling.style.height   = sb.height + 'px';
            });
        }
        m.style.position = 'absolute';
        m.style.margin   = '0';
        m.style.left     = (bounds.left - origin.left) + 'px';
        m.style.top      = (bounds.top  - origin.top)  + 'px';
        m.style.width    = bounds.width  + 'px';
        m.style.height   = bounds.height + 'px';
        m.style.minWidth  = '';
        m.style.minHeight = '';
        m.style.transform = '';
        if (m.style.display === 'inline-block') m.style.display = 'block';
    }

    /**
     * Makes a picture's box and its picture the same thing.
     *
     * Templates and the Add Image button draw pictures "cover" (fill the box,
     * trim the overflow) or "contain" (fit inside, leave margins). Both mean
     * the box is not the picture, so resizing the box used to trim more or
     * less of it instead of stretching it. This rewrites the same appearance
     * explicitly - the box becomes the whole picture, and whatever "cover" was
     * trimming becomes a crop - after which a resize stretches what is seen
     * and a crop has real edges to work from. Nothing moves on screen.
     */
    function makeImageExplicit(img) {
        if (img.tagName !== 'IMG' || !isAbsoluteEl(img)) return;
        const fit = getComputedStyle(img).objectFit;
        const nw = img.naturalWidth, nh = img.naturalHeight;
        if ((fit === 'cover' || fit === 'contain') && nw && nh && !cropInsetsOf(img)) {
            const b = getElBoundsInSlide(img);
            const origin = positioningOriginInSlide(img);
            if (b && b.width > 0 && b.height > 0) {
                const scale = fit === 'cover'
                    ? Math.max(b.width / nw, b.height / nh)
                    : Math.min(b.width / nw, b.height / nh);
                const dw = nw * scale, dh = nh * scale;
                const radius = getComputedStyle(img).borderRadius;
                img.style.left   = (b.left + (b.width - dw) / 2 - origin.left) + 'px';
                img.style.top    = (b.top + (b.height - dh) / 2 - origin.top) + 'px';
                img.style.width  = dw + 'px';
                img.style.height = dh + 'px';
                if (fit === 'cover') {
                    const x = Math.max(0, (dw - b.width) / 2 / dw);
                    const y = Math.max(0, (dh - b.height) / 2 / dh);
                    // The rounding moves onto the mask, or the corners the
                    // box used to round would be cut square.
                    setCropInsets(img, { t: y, r: x, b: y, l: x, round: radius && !/^0(px|%)?$/.test(radius) ? radius : '' });
                }
            }
        }
        img.style.objectFit = 'fill';
        // A model picture's fit is stored, and re-applied from this attribute
        // every time the slide is redrawn (applyImageFit).
        if (img.hasAttribute('data-object-fit')) img.dataset.objectFit = 'fill';
    }

    // ── Crop mode ─────────────────────────────────────────
    let cropState = null;
    const CROP_MIN = 16;

    function canCrop(el) {
        return !!el && el.tagName === 'IMG' && isResizableEl(el) && !isDesigningTemplate()
            && state.pptMode === 'direct';
    }

    function startCrop(img) {
        if (cropState || !canCrop(img) || !stageOverlay) return;
        if (!img.naturalWidth) { showToast('This picture has not loaded yet'); return; }
        const snap = getSlideHtml();
        // The whole picture is shown while cropping, which is not a state to
        // save: autosave waits until the crop is applied or abandoned.
        pauseAutoSave();
        setSelectedEl(null);
        clearMultiSelect();

        const b0 = getElBoundsInSlide(img);
        promoteToAbsolute(img, b0, positioningOriginInSlide(img), true);
        makeImageExplicit(img);
        const full = getElBoundsInSlide(img);
        const ins = cropInsetsOf(img) || { t: 0, r: 0, b: 0, l: 0, round: '' };
        const rect = visibleBoundsOf(img);
        const clipBefore = img.style.clipPath;
        img.style.clipPath = '';

        const layer = document.createElement('div');
        layer.id = 'crop-layer';
        layer.innerHTML = `
            <div class="crop-rect">
                ${['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(d => `<span class="crop-h crop-h-${d}" data-dir="${d}"></span>`).join('')}
            </div>
            <div class="crop-bar">
                <button type="button" class="btn btn-primary btn-sm" data-crop="apply">Crop</button>
                <button type="button" class="btn btn-secondary btn-sm" data-crop="reset">Whole picture</button>
                <button type="button" class="btn btn-secondary btn-sm" data-crop="cancel">Cancel</button>
            </div>`;
        stageOverlay.appendChild(layer);
        cropState = { img, snap, full, rect: { ...rect }, round: ins.round, clipBefore, layer };
        drawCrop();

        layer.addEventListener('mousedown', onCropMouseDown);
        layer.addEventListener('click', (e) => {
            const act = e.target.closest('[data-crop]')?.dataset.crop;
            if (act === 'apply') endCrop(true);
            else if (act === 'cancel') endCrop(false);
            else if (act === 'reset') { cropState.rect = { ...cropState.full }; drawCrop(); }
        });
    }

    function drawCrop() {
        const { rect, layer } = cropState;
        const box = layer.querySelector('.crop-rect');
        box.style.left = rect.left + 'px';
        box.style.top = rect.top + 'px';
        box.style.width = (rect.right - rect.left) + 'px';
        box.style.height = (rect.bottom - rect.top) + 'px';
        // Under the crop where there is room on the slide, above it otherwise.
        const bar = layer.querySelector('.crop-bar');
        const below = rect.bottom + 10;
        bar.style.top = (below + 40 <= 540 ? below : Math.max(4, rect.top - 44)) + 'px';
        bar.style.left = Math.max(4, Math.min(rect.left, 960 - 300)) + 'px';
    }

    function onCropMouseDown(e) {
        if (!cropState || e.button !== 0 || e.target.closest('.crop-bar')) return;
        const dir = e.target.closest('.crop-h')?.dataset.dir || (e.target.closest('.crop-rect') ? 'move' : '');
        if (!dir) return;
        e.preventDefault();
        const { full } = cropState;
        const start = { ...cropState.rect };
        const startX = e.clientX, startY = e.clientY;
        const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

        function onMove(mv) {
            const scale = stageCanvas.offsetWidth / 960;
            const dx = (mv.clientX - startX) / scale;
            const dy = (mv.clientY - startY) / scale;
            const r = { ...start };
            if (dir === 'move') {
                const mx = clamp(dx, full.left - start.left, full.right - start.right);
                const my = clamp(dy, full.top - start.top, full.bottom - start.bottom);
                r.left += mx; r.right += mx; r.top += my; r.bottom += my;
            } else {
                if (dir.includes('w')) r.left = clamp(start.left + dx, full.left, start.right - CROP_MIN);
                if (dir.includes('e')) r.right = clamp(start.right + dx, start.left + CROP_MIN, full.right);
                if (dir.includes('n')) r.top = clamp(start.top + dy, full.top, start.bottom - CROP_MIN);
                if (dir.includes('s')) r.bottom = clamp(start.bottom + dy, start.top + CROP_MIN, full.bottom);
            }
            cropState.rect = r;
            drawCrop();
        }
        function onUp() {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        }
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }

    // `apply` false puts the mask back as it was. Called with false by anything
    // about to replace or save the stage, so a half-made crop is never stored.
    function endCrop(apply) {
        if (!cropState) return;
        const { img, snap, full, rect, round, clipBefore, layer } = cropState;
        cropState = null;
        layer.remove();
        resumeAutoSave();
        if (apply && full.width > 0 && full.height > 0) {
            setCropInsets(img, {
                l: (rect.left - full.left) / full.width,
                r: (full.right - rect.right) / full.width,
                t: (rect.top - full.top) / full.height,
                b: (full.bottom - rect.bottom) / full.height,
                round,
            });
            commitHistory(snap);
            // After the pause above has lifted, so this one is not swallowed.
            setTimeout(scheduleSave, 300);
        } else {
            img.style.clipPath = clipBefore;
        }
        if (img.isConnected && state.dragActive) setSelectedEl(img);
    }

    function removeCrop(img) {
        if (!cropInsetsOf(img)) return;
        const snap = getSlideHtml();
        const round = cropInsetsOf(img).round;
        setCropInsets(img, { t: 0, r: 0, b: 0, l: 0, round });
        commitHistory(snap);
        setSelectedEl(img);
    }

    document.addEventListener('keydown', (e) => {
        if (!cropState) return;
        if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); endCrop(true); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); endCrop(false); }
    }, true);

    // A model group (shared data-group-id) is one object until a member is
    // clicked a second time, as in PowerPoint: state.soloEl is then that
    // member, which moves and resizes alone while staying in the group –
    // a term-grid picture can be nudged without dragging its card and text.
    state.soloEl = null;

    function groupMembersOf(el) {
        const gid = el?.dataset?.groupId;
        if (!gid || state.soloEl === el) return [el];
        return Array.from(stageSlideInner.querySelectorAll(`[data-group-id="${CSS.escape(gid)}"]`));
    }

    function getGroupBounds(el) {
        // What shows, not the box: a cropped picture's box runs past its edges.
        const members = groupMembersOf(el);
        if (members.length < 2) return visibleBoundsOf(el);
        return unionBounds(members) || visibleBoundsOf(el);
    }

    function unionBounds(members) {
        let bounds = null;
        members.forEach(member => {
            const b = visibleBoundsOf(member);
            if (!b) return;
            if (!bounds) {
                bounds = { ...b };
                return;
            }
            bounds.left = Math.min(bounds.left, b.left);
            bounds.top = Math.min(bounds.top, b.top);
            bounds.right = Math.max(bounds.right, b.right);
            bounds.bottom = Math.max(bounds.bottom, b.bottom);
            bounds.width = bounds.right - bounds.left;
            bounds.height = bounds.bottom - bounds.top;
        });
        return bounds;
    }

    /**
     * Where an absolutely-positioned element's left/top are measured FROM,
     * in slide coordinates.
     *
     * All the editor's geometry is done in slide coordinates, but CSS resolves
     * left/top against the nearest positioned ancestor. For a direct child of
     * .slide those are the same thing; for an element inside a group they are
     * not, and writing slide coordinates would jump it by the group's offset.
     */
    function positioningOriginInSlide(el) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        const container = el?.parentElement;
        if (!container || container === slideEl) return { left: 0, top: 0 };

        const cs = getComputedStyle(container);
        if (cs.position === 'static') return { left: 0, top: 0 };  // not a containing block

        const b = getElBoundsInSlide(container);
        if (!b) return { left: 0, top: 0 };
        // The containing block is the padding box, so the border sits outside it.
        return {
            left: b.left + (parseFloat(cs.borderLeftWidth) || 0),
            top: b.top + (parseFloat(cs.borderTopWidth) || 0),
        };
    }

    /** Elements whose size can be edited: anything with a positioned parent. */
    function isResizableEl(el) {
        return isDirectSlideChild(el)
            || !!el?.parentElement?.classList?.contains('slide__group');
    }

    // ── Entering a group ──────────────────────────────────
    // A group behaves as one object until it is entered by double-clicking,
    // matching PowerPoint. state.enteredGroup is the group currently open;
    // null means every group is closed.
    state.enteredGroup = null;

    function enterGroup(group) {
        if (state.enteredGroup === group) return;
        state.enteredGroup?.classList.remove('is-entered');
        state.enteredGroup = group || null;
        group?.classList.add('is-entered');
    }

    function exitGroupIfOutside(el) {
        if (!state.enteredGroup) return;
        // Staying inside means selecting the group itself or something in it.
        // Anything else – another element, or nothing – closes it again.
        if (el && (el === state.enteredGroup || state.enteredGroup.contains(el))) return;
        enterGroup(null);
    }

    function updateSelBox(b) {
        if (!selBox) return;
        if (!b) { hide(selBox); placeSwapChip(); placeAskChip(); return; }
        show(selBox);
        selBox.style.left   = b.left   + 'px';
        selBox.style.top    = b.top    + 'px';
        selBox.style.width  = b.width  + 'px';
        selBox.style.height = b.height + 'px';
        placeSwapChip();
        placeAskChip();
    }

    function updateSnapLines(snapX, snapY) {
        if (snapLineV) {
            if (snapX !== null) { snapLineV.style.left = snapX + 'px'; show(snapLineV); }
            else hide(snapLineV);
        }
        if (snapLineH) {
            if (snapY !== null) { snapLineH.style.top = snapY + 'px'; show(snapLineH); }
            else hide(snapLineH);
        }
    }

    function hideSnapLines() {
        hide(snapLineV);
        hide(snapLineH);
    }

    // Add a thumbnail to the left panel
    function addSlideThumb(position, slideId) {
        const thumb = document.createElement('div');
        thumb.className = 'slide-thumb generating';
        thumb.dataset.position = position;
        thumb.dataset.slideId = slideId || '';

        const inner = document.createElement('div');
        inner.className = 'slide-thumb-inner t-skel';
        inner.dataset.theme = state.selectedTheme;
        // Skeleton and rendered slide share the slot, so the swap is
        // layout-free and cross-fades instead of popping.
        inner.innerHTML =
            '<div class="t-skel-skeleton is-pulsing"><div class="slide-skeleton"></div></div>' +
            '<div class="t-skel-content"></div>';

        const num = document.createElement('div');
        num.className = 'slide-thumb-num';
        num.textContent = position;

        thumb.appendChild(inner);
        thumb.appendChild(num);
        slidePanel.appendChild(thumb);
        return thumb;
    }

    // Mark a slide's thumbnail as failed instead of leaving it stuck on the
    // generating skeleton forever (see slide_error handling in startGenerateStream).
    function markThumbError(slideId, message) {
        const thumb = slidePanel.querySelector(`[data-slide-id="${slideId}"]`);
        if (!thumb) return;
        thumb.classList.remove('generating');
        thumb.classList.add('error');
        const inner = thumb.querySelector('.slide-thumb-inner');
        const target = inner?.querySelector('.t-skel-content') || inner;
        if (target) target.innerHTML = `<div class="slide-thumb-error-msg">${message || 'Generation failed'}</div>`;
        inner?.classList.add('is-revealed');
    }

    function updateThumb(position, slideId, html) {
        let thumb = slidePanel.querySelector(`[data-position="${position}"]`);
        if (!thumb) thumb = addSlideThumb(position, slideId);
        thumb.classList.remove('generating');
        if (slideId) thumb.dataset.slideId = slideId;

        // Clone (clears old listeners) and REPLACE before rendering, so the
        // render runs on the node that actually stays in the panel rather than
        // a copy that is immediately swapped out and discarded.
        const fresh = thumb.cloneNode(true);
        thumb.replaceWith(fresh);
        const inner = fresh.querySelector('.slide-thumb-inner');
        renderSlideByPosition(inner?.querySelector('.t-skel-content') || inner || fresh, position, html);

        // Rebind click + drag on the fresh node.
        bindThumbDrag(fresh);
        fresh.addEventListener('click', () => selectSlide(slideId, html, position));
        // The pending-change mark and review count belong to the slide.
        markPendingThumbs();
        paintReviewPins();
        // Reveal after the swap, or the clone would mount already revealed
        // and the cross-fade would never run.
        const skel = fresh.querySelector('.t-skel');
        if (skel) requestAnimationFrame(() => skel.classList.add('is-revealed'));
    }

    // ── Slide reorder (drag-and-drop in panel) ────────────
    // The dragged thumb is repositioned live as you drag over the list (like
    // Trello/Slides) instead of needing to hit a precise top/bottom half of a
    // target thumb; every other thumb it passes visibly slides out of the way
    // (FLIP-animated: measure before the DOM move, then animate from the old
    // position to the new one) rather than the list just snapping into place.
    let draggedThumb = null;

    function bindThumbDrag(thumb) {
        thumb.draggable = true;
        thumb.addEventListener('dragstart', (e) => {
            draggedThumb = thumb;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', thumb.dataset.slideId);
            // Defer so the ghost isn't transparent immediately
            requestAnimationFrame(() => thumb.classList.add('drag-src'));
        });
        thumb.addEventListener('dragend', () => {
            thumb.classList.remove('drag-src');
            draggedThumb = null;
        });
    }

    // Moves the dragged thumb to sit before `referenceThumb` (or at the end if
    // null), FLIP-animating every other thumb from its old position to its new
    // one so the reflow reads as a smooth slide instead of an instant snap.
    function moveDraggedThumbTo(referenceThumb) {
        const others = Array.from(slidePanel.querySelectorAll('.slide-thumb')).filter(t => t !== draggedThumb);
        const firstRects = new Map(others.map(t => [t, t.getBoundingClientRect()]));

        slidePanel.insertBefore(draggedThumb, referenceThumb);

        others.forEach(t => {
            const firstRect = firstRects.get(t);
            const lastRect = t.getBoundingClientRect();
            const dy = firstRect.top - lastRect.top;
            if (Math.abs(dy) < 0.5) return;
            t.style.transition = 'none';
            t.style.transform = `translateY(${dy}px)`;
            void t.offsetHeight; // force layout so the inverted position registers before animating away from it
            requestAnimationFrame(() => {
                t.style.transition = 'transform 0.18s ease';
                t.style.transform = '';
            });
        });
    }

    slidePanel?.addEventListener('dragover', (e) => {
        if (!draggedThumb) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const others = Array.from(slidePanel.querySelectorAll('.slide-thumb')).filter(t => t !== draggedThumb);
        let target = null; // thumb to insert before, or null = insert at the end
        for (const t of others) {
            const r = t.getBoundingClientRect();
            if (e.clientY < r.top + r.height / 2) { target = t; break; }
        }

        const alreadyThere = target
            ? draggedThumb.nextElementSibling === target
            : draggedThumb === slidePanel.lastElementChild;
        if (!alreadyThere) moveDraggedThumbTo(target);
    });

    slidePanel?.addEventListener('drop', (e) => {
        if (!draggedThumb) return;
        e.preventDefault();
        commitReorder();
    });

    async function commitReorder() {
        // The live drag already reordered the DOM – read the new order from it
        // rather than recomputing from/to indices.
        const orderedIds = Array.from(slidePanel.querySelectorAll('.slide-thumb')).map(t => t.dataset.slideId);
        const byId = new Map([...state.slides.values()].map(s => [s.id, s]));
        const slides = orderedIds.map(id => byId.get(id)).filter(Boolean);
        if (slides.length !== state.slides.size) return; // safety net, shouldn't happen

        // Rebuild state.slides with new 1-based positions
        state.slides.clear();
        slides.forEach((s, i) => state.slides.set(i + 1, s));

        // Update active slide position if it moved
        const newPos = slides.findIndex(s => s.id === state.activeSlideId) + 1;
        if (newPos > 0) state.activeSlidePosition = newPos;

        // Rebuild thumbnail panel DOM (also clears any leftover inline
        // transform/transition left over from the drag animation)
        slidePanel.innerHTML = '';
        slides.forEach((s, i) => updateThumb(i + 1, s.id, s.html));

        // Re-highlight the active slide
        slidePanel.querySelectorAll('.slide-thumb').forEach(t => {
            t.classList.toggle('active', t.dataset.slideId === state.activeSlideId);
        });

        // Update stage info
        if (stageInfo) stageInfo.textContent = `Slide ${state.activeSlidePosition} / ${state.slides.size}`;
        placeSeamThumb();

        // Persist to server
        if (state.presentationId) {
            apiFetch(`/api/presentations/${state.presentationId}/slides/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order: orderedIds }),
            }).catch(() => {}); // fire-and-forget; not critical UX
        }
    }

    // ── Auto-save ─────────────────────────────────────────
    let autoSavePaused = true;
    let autoSaveTimer  = null;

    function pauseAutoSave() {
        autoSavePaused = true;
        clearTimeout(autoSaveTimer);
    }

    function resumeAutoSave() {
        setTimeout(() => { autoSavePaused = false; }, 250);
    }

    // True from the moment an edit schedules a save until that save starts.
    // Anything that is about to replace the stage (another slide, mostly) has
    // to send it first: the timer below is cancelled by pauseAutoSave, and an
    // edit made in the second before a click on another thumbnail used to be
    // dropped without a word.
    let autoSavePending = false;

    function scheduleSave() {
        if (autoSavePaused || !state.activeSlideId || stageShowsAiPreview()) return;
        autoSavePending = true;
        clearTimeout(autoSaveTimer);
        autoSaveTimer = setTimeout(savePptDirect, 1000);
    }

    // JSON columns normally arrive pre-parsed (mysql2), but this guards the
    // rare case they don't rather than assuming the driver's behaviour.
    function parseJson(value) {
        if (value == null) return null;
        if (typeof value === 'string') {
            try { return JSON.parse(value); } catch { return null; }
        }
        return value;
    }

    // Renders into container from whichever data the slide at `position` has:
    // elements_json model when json_schema_version marks it available,
    // else the html_content string (fallback param, since some
    // callers pass fresher html than what's in state.slides yet).
    function renderSlideByPosition(container, position, html) {
        const slideData = state.slides.get(position);
        if (slideData && slideData.json_schema_version === 1) {
            SlideModelRenderer.renderSlideHtml(container, slideData.contents_json, slideData.elements_json);
        } else {
            SlideRenderer.renderSlideHtml(container, html);
        }
    }

    function selectSlide(slideId, html, position) {
        endCrop(false);
        // A draft on the stage steps aside, and waits in the rail.
        blurSeam();
        // Leaving a slide with an edit not yet saved: send it now. Not awaited
        // - savePptDirect reads the stage before its first await, so what it
        // sends is this slide as it stands, whatever is drawn here next.
        if (autoSavePending && state.activeSlideId && state.activeSlideId !== slideId) {
            clearTimeout(autoSaveTimer);
            savePptDirect();
        }
        // Deactivate current drag editor before switching
        deactivateDragEditor();
        pauseAutoSave();

        // Undo/redo history is per-slide
        undoStack.length = 0;
        redoStack.length = 0;
        syncUndoButtons();

        state.activeSlideId = slideId;
        state.activeSlidePosition = position;
        state.aiEditBuffer = '';

        renderSlideByPosition(stageSlideInner, position, html);
        applyTheme(state.selectedTheme);
        scaleStage();
        setError(stageError, '');
        resumeAutoSave();

        // Highlight active thumb
        slidePanel.querySelectorAll('.slide-thumb').forEach(t => {
            t.classList.toggle('active', t.dataset.slideId === slideId);
        });

        const total = state.slides.size;
        if (stageInfo) stageInfo.textContent = `Slide ${position} / ${total}`;

        showSlideNotes(slideId);

        // In direct mode activate drag editor
        if (state.pptMode === 'direct') activateDragEditor();
        // A change waiting on this slide is drawn over it.
        aiAfterSlideSelected();
    }

    // ── Teacher notes ─────────────────────────────────────
    // The script under each slide (slides.speaker_notes). Held apart from
    // state.slides, which is rebuilt in a dozen places that would each have
    // to remember to carry it; keyed by slide id, so reordering cannot
    // attach a note to the wrong slide.

    function rememberNotes(slideId, notes) {
        if (!slideId) return;
        if (typeof notes === 'string' && notes.trim()) slideNotes.set(slideId, notes);
        else slideNotes.delete(slideId);
    }

    function showSlideNotes(slideId) {
        if (!slideNotesBox || !slideNotesInput) return;
        // Leaving a slide with a note still to save: save it against the
        // slide it was typed on, before the box is reused.
        if (notesSaveTimer && notesSlideId && notesSlideId !== slideId) saveSlideNotes();
        notesSlideId = slideId;
        slideNotesInput.value = slideNotes.get(slideId) || '';
        slideNotesBox.hidden = !slideId;
    }

    async function saveSlideNotes() {
        clearTimeout(notesSaveTimer);
        notesSaveTimer = null;
        const slideId = notesSlideId;
        if (!slideId || !slideNotesInput) return;
        const notes = slideNotesInput.value;
        if ((slideNotes.get(slideId) || '') === notes.trim()) return;
        rememberNotes(slideId, notes.trim());
        try {
            const res = await apiFetch(`/api/slides/${slideId}/notes`, { method: 'PUT', body: JSON.stringify({ notes }) });
            if (!res.ok) throw new Error();
        } catch {
            setError(stageError, 'Your teacher notes could not be saved. Try again.');
        }
    }

    slideNotesInput?.addEventListener('input', () => {
        clearTimeout(notesSaveTimer);
        notesSaveTimer = setTimeout(saveSlideNotes, 800);
    });
    slideNotesInput?.addEventListener('blur', () => { if (notesSaveTimer) saveSlideNotes(); });

    // ── Drag editor ───────────────────────────────────────
    // Dragging uses CSS translate() so elements never leave normal flow.
    // No position changes → switching modes never causes layout shifts.

    const dragCleanups = [];

    // New DOM refs for element insertion
    const pptAddText   = $('ppt-add-text');
    const pptAddImgBtn = $('ppt-add-img-btn');
    const pptUploadImgBtn = $('ppt-upload-img-btn');
    const pptAddRect   = $('ppt-add-rect');
    const pptAddCircle = $('ppt-add-circle');
    const pptDeleteEl  = $('ppt-delete-el');
    const pptCropBtn   = $('ppt-crop');
    const pptSwapBtn   = $('ppt-swap-img');
    const pptImgRow    = $('ppt-img-row');
    const pptImgKw     = $('ppt-img-keyword');
    const pptImgConfirm = $('ppt-img-confirm');
    const pptImgCancel = $('ppt-img-cancel');
    const pptImgUploadInput = $('ppt-img-upload-input');
    const managedImageEls = new WeakSet();

    function trackManagedImages(root) {
        if (!root) return;
        root.querySelectorAll('img[data-image-path]').forEach(img => managedImageEls.add(img));
    }

    function activateDragEditor() {
        if (state.dragActive) return;
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return;
        trackManagedImages(slideEl);
        // Direct children, plus anything inside a group. A group's children are
        // absolutely positioned against it, so moving and resizing them is
        // well defined – unlike a flex/grid child of a built-in layout.
        // Ghost rows are clones of the row, so they carry .slide__group too –
        // excluded, or the editor would attach handlers to its own chrome.
        const groupChildren = slideEl.querySelectorAll('.slide__group:not([data-repeat-ghost]) > *');
        [...slideEl.children, ...groupChildren].forEach(el => {
            dragCleanups.push(makeDraggable(el));
        });
        // Pressing on the slide background (empty area) deselects, and
        // dragging from there draws a selection rectangle.
        function onBgClick(e) {
            if (e.target !== slideEl || e.button !== 0) return;
            startMarquee(e, slideEl);
        }
        slideEl.addEventListener('mousedown', onBgClick);
        dragCleanups.push(() => slideEl.removeEventListener('mousedown', onBgClick));
        state.dragActive = true;
    }

    function deactivateDragEditor(preserveSelection = false) {
        while (dragCleanups.length) dragCleanups.pop()();
        stageSlideInner.querySelectorAll('[contenteditable="true"]').forEach(el => {
            if (!preserveSelection || el !== state.selectedEl) {
                el.removeAttribute('contenteditable');
                MathRender.renderMathInElement(el);
            }
        });
        if (!preserveSelection) setSelectedEl(null);
        else if (state.selectedEl) {
            state.selectedEl.style.cursor = state.selectedEl.contentEditable === 'true' ? 'text' : 'grab';
        }
        hideSnapLines();
        // The DOM is often replaced wholesale after this (undo, slide switch,
        // preview), which would leave enteredGroup pointing at a detached node.
        enterGroup(null);
        state.dragActive = false;
    }

    // ── Selection ─────────────────────────────────────────
    function isAbsoluteEl(el) {
        return el?.style?.position === 'absolute';
    }

    // Free positioning (drag, resize, z-order, duplicate) only works on direct
    // children of .slide – those are the elements makeDraggable is attached to,
    // and the only ones getSlideChildren()/appendDraggable() know about. Nested
    // elements can still be selected and text-edited, and can be marked as AI
    // slots, but they stay in their parent's flow.
    function isDirectSlideChild(el) {
        return !!el && el.parentElement === stageSlideInner?.querySelector('.slide');
    }

    function setSelectedEl(el) {
        if (state.selectedEl && state.selectedEl !== el) {
            state.selectedEl.style.outline = '';
            // Exit text edit on the element we're leaving
            if (state.selectedEl.contentEditable === 'true') {
                state.selectedEl.removeAttribute('contenteditable');
                MathRender.renderMathInElement(state.selectedEl);
                state.selectedEl.style.cursor = 'grab';
            }
        }
        // Selecting away from an open group closes it, so the editor can never
        // be left in "inside a group" mode with nothing to show for it.
        exitGroupIfOutside(el);
        if (state.soloEl !== el) state.soloEl = null;

        state.selectedEl = el;
        if (el) {
            el.style.outline = 'none';
            const b = getElBoundsInSlide(el);
            const selectionBounds = getGroupBounds(el);
            // Only force explicit width on added absolute elements (avoids displacing flex siblings).
            // Never pre-set height – clips text on text boxes; startResize sets it fresh.
            if (b && isAbsoluteEl(el) && !el.style.width) {
                el.style.width = b.width + 'px';
            }
            // Handles are shown for any selected direct child – startResize
            // promotes flow (non-absolute) elements to free positioning on
            // demand, the moment a handle is actually grabbed, so template
            // layout (flex/grid) stays intact until the user deliberately
            // resizes something. Nested elements get a selection box without
            // handles: resizing one would absolutely position it against an
            // ancestor rather than the slide, so the bounds we measured no
            // longer describe where it lands.
            if (selBox) {
                const resizable = isResizableEl(el);
                selBox.querySelectorAll('.rh').forEach(h => { h.style.display = resizable ? '' : 'none'; });
            }
            updateSelBox(selectionBounds);
        } else {
            updateSelBox(null);
        }
        if (pptDeleteEl) pptDeleteEl.disabled = !el;
        if (pptCropBtn) pptCropBtn.disabled = !canCrop(el);
        if (pptSwapBtn) pptSwapBtn.disabled = !canSwapPicture(el);
        syncPicturePanel(el);
        syncGroupButtons();
        syncAiScope();
    }

    // ── Multi-select and grouping ─────────────────────────
    // Grouping exists mainly so a repeating row can be built from scratch: a
    // repeat needs a container whose first child is the pattern, and until now
    // every element the toolbar added landed as a direct child of the slide,
    // so there was no way to make one.

    const multiSelected = new Set();

    function multiSelectList() {
        // Document order, so a group's children keep the order they were drawn in.
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return [];
        return [...slideEl.children].filter(el => multiSelected.has(el));
    }

    function paintMultiSelect() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        slideEl?.querySelectorAll('.is-multi-selected').forEach(el => el.classList.remove('is-multi-selected'));
        multiSelected.forEach(el => el.classList.add('is-multi-selected'));
        syncGroupButtons();
        syncAiScope();
    }

    function toggleMultiSelect(el) {
        if (!isDirectSlideChild(el)) return;   // grouping only moves top-level boxes
        // The primary selection joins the set, so shift-clicking a second
        // element groups two rather than one.
        if (state.selectedEl && isDirectSlideChild(state.selectedEl)) multiSelected.add(state.selectedEl);
        if (multiSelected.has(el)) multiSelected.delete(el);
        else multiSelected.add(el);
        paintMultiSelect();
    }

    function clearMultiSelect() {
        if (!multiSelected.size) return;
        multiSelected.clear();
        paintMultiSelect();
    }

    /** The multi-selection plus everything grouped with it, so a drag never splits a group. */
    function multiSelectWithGroups() {
        const out = [];
        multiSelectList().forEach(el => {
            groupMembersOf(el).forEach(m => { if (!out.includes(m)) out.push(m); });
        });
        return out;
    }

    // ── Rubber-band selection ─────────────────────────────
    // Dragging from empty slide selects every top-level element the rectangle
    // touches. Shift adds to what is already selected.
    function startMarquee(e, slideEl) {
        // A text box being edited keeps focus through a prevented mousedown,
        // and would then never commit its edit.
        const active = document.activeElement;
        if (active && active !== document.body && stageSlideInner.contains(active)) active.blur();
        e.preventDefault();

        const additive = e.shiftKey;
        const base = new Set(additive ? multiSelected : []);
        if (additive && state.selectedEl && isDirectSlideChild(state.selectedEl)) base.add(state.selectedEl);
        if (!additive) {
            setSelectedEl(null);
            clearMultiSelect();
        }

        const toSlide = (cx, cy) => {
            const sr = slideEl.getBoundingClientRect();
            const s = 960 / sr.width;
            return { x: (cx - sr.left) * s, y: (cy - sr.top) * s };
        };
        const start = toSlide(e.clientX, e.clientY);
        const startCX = e.clientX, startCY = e.clientY;
        let box = null;

        function hitsIn(r) {
            const hits = [];
            [...slideEl.children].forEach(el => {
                if (el.hasAttribute('data-repeat-ghost')) return;
                const b = visibleBoundsOf(el);
                if (!b || (!b.width && !b.height)) return;
                const touches = b.left < r.right && b.right > r.left && b.top < r.bottom && b.bottom > r.top;
                if (!touches) return;
                // A full-bleed backdrop is touched by every rectangle, so it
                // only counts when the rectangle goes all the way round it.
                const fullBleed = b.width >= 940 && b.height >= 520;
                const inside = b.left >= r.left && b.right <= r.right && b.top >= r.top && b.bottom <= r.bottom;
                if (!fullBleed || inside) {
                    groupMembersOf(el).forEach(m => { if (isDirectSlideChild(m)) hits.push(m); });
                }
            });
            return hits;
        }

        function onMove(mv) {
            if (!box) {
                if (Math.abs(mv.clientX - startCX) < 3 && Math.abs(mv.clientY - startCY) < 3) return;
                box = document.createElement('div');
                box.className = 'marquee-box';
                stageOverlay?.appendChild(box);
            }
            const p = toSlide(mv.clientX, mv.clientY);
            const r = {
                left: Math.min(start.x, p.x), top: Math.min(start.y, p.y),
                right: Math.max(start.x, p.x), bottom: Math.max(start.y, p.y),
            };
            box.style.left = r.left + 'px';
            box.style.top = r.top + 'px';
            box.style.width = (r.right - r.left) + 'px';
            box.style.height = (r.bottom - r.top) + 'px';
            multiSelected.clear();
            base.forEach(el => multiSelected.add(el));
            hitsIn(r).forEach(el => multiSelected.add(el));
            paintMultiSelect();
        }

        function onUp() {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            if (!box) return;   // a plain click on the background
            box.remove();
            const picked = multiSelectList();
            if (!picked.length) return;
            // One element (or one group) is an ordinary selection.
            const single = picked.every(m => groupMembersOf(picked[0]).includes(m));
            if (single) {
                clearMultiSelect();
                setSelectedEl(picked[0]);
                return;
            }
            const primary = picked.includes(state.selectedEl) ? state.selectedEl : picked[0];
            setSelectedEl(primary);
        }

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }

    function syncGroupButtons() {
        const groupBtn = $('ppt-group');
        const ungroupBtn = $('ppt-ungroup');
        const repeatBtn = $('ppt-make-repeat');
        if (groupBtn) groupBtn.disabled = multiSelected.size < 2;
        // Ungroup is available whenever the selection belongs to a model group.
        if (ungroupBtn) ungroupBtn.disabled = !state.selectedEl?.dataset?.groupId;
        if (repeatBtn) {
            repeatBtn.disabled = multiSelected.size < 1
                || !!state.selectedEl?.closest?.('[data-slot-repeat]');
        }
    }

    /** Bakes an element into absolute coordinates relative to the slide. */
    function absolutise(el, bounds) {
        el.style.position = 'absolute';
        el.style.left = bounds.left + 'px';
        el.style.top = bounds.top + 'px';
        el.style.width = bounds.width + 'px';
        el.style.height = bounds.height + 'px';
        el.style.transform = '';
    }

    // Grouping is model-only: every selected element is given the same group
    // id (stamped on the DOM as data-group-id) and the write-back bakes it
    // into elements_json.
    function groupSelected() {
        const members = multiSelectList();
        if (members.length < 2) return null;

        const snap = getSlideHtml();
        const gid = nextModelGroupId();
        members.forEach(el => el.setAttribute('data-group-id', gid));
        clearMultiSelect();
        setSelectedEl(members[members.length - 1]);
        commitHistory(snap);   // syncs elements_json from the stamped DOM
        return gid;
    }

    // Dissolve the selected element's whole group
    function ungroupSelected() {
        const el = state.selectedEl;
        const gid = el?.dataset?.groupId;
        if (!gid) return;

        const snap = getSlideHtml();
        stageSlideInner.querySelectorAll(`[data-group-id="${CSS.escape(gid)}"]`)
            .forEach(m => m.removeAttribute('data-group-id'));
        commitHistory(snap);   // write-back drops the group from each record
        syncGroupButtons();
    }

    const REPEAT_GAP = 16;

    /**
     * Sizes a repeat's rows so the requested number of them fit its area.
     *
     * Rows keep the exact size they were designed at and are scaled as a whole,
     * rather than being squeezed or re-laid-out. Everything inside a row scales
     * together, so overlapping designs – a caption on a card – survive
     * untouched, which is the thing that broke when rows shared the width and
     * their absolutely-positioned contents didn't.
     *
     * Returns the scale applied, so the UI can say when rows have gone small.
     */
    function layoutRepeat(wrap) {
        if (!wrap) return 1;
        const rows = [...wrap.children].filter(el => el.classList.contains('slide__group'));
        const row = rows.find(r => !r.hasAttribute('data-repeat-ghost'));
        if (!row) return 1;

        // style.width is always rewritten as the DESIGNED width, so it stays a
        // stable reference no matter how many times this runs. The visible size
        // comes from the transform, never from shrinking the box.
        const designW = parseFloat(row.style.width) || row.offsetWidth || 1;
        const designH = parseFloat(row.style.height) || row.offsetHeight || 1;

        const count = Math.max(1, parseInt(wrap.getAttribute('data-slot-max'), 10) || 4);
        const areaW = parseFloat(wrap.style.width) || wrap.offsetWidth || designW;
        const usable = areaW - (count - 1) * REPEAT_GAP;
        const scale = Math.max(0.15, Math.min(1, usable / (count * designW)));

        for (const r of rows) {
            r.style.width = designW + 'px';
            r.style.height = designH + 'px';
            r.style.transformOrigin = 'top left';
            r.style.transform = scale < 1 ? `scale(${scale})` : '';
            // Flex reserves the SCALED width – a transform doesn't change
            // layout size, so without this the rows would still overlap.
            r.style.flex = `0 0 ${designW * scale}px`;
        }
        wrap.style.height = (designH * scale) + 'px';
        return scale;
    }

    /**
     * One action from a multi-selection to a finished repeating row: builds the
     * two-level structure the compiler needs, lays the row out so copies can't
     * overlap, and marks each part as an AI slot.
     *
     * Doing this as separate group / wrap / mark steps meant knowing that a
     * repeat container's first child is the pattern – an implementation detail
     * no one should have to learn to use the feature.
     */
    function makeRepeatFromSelection(fromGroup = null) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return null;

        const members = fromGroup ? [fromGroup] : multiSelectList();
        if (!members.length) return null;

        const snap = getSlideHtml();
        const bounds = members.map(el => getElBoundsInSlide(el));
        if (bounds.some(b => !b)) return null;

        const left = Math.min(...bounds.map(b => b.left));
        const top = Math.min(...bounds.map(b => b.top));
        const right = Math.max(...bounds.map(b => b.left + b.width));
        const bottom = Math.max(...bounds.map(b => b.top + b.height));

        const rowWidth = right - left;
        const rowHeight = bottom - top;

        // The area runs from where the row sits to the edge of the slide, so
        // there is somewhere for the copies to go the moment it is created.
        const areaWidth = Math.max(rowWidth, 960 - left - 20);

        const wrap = document.createElement('div');
        wrap.className = 'slide__repeat';
        wrap.style.cssText = `position:absolute; left:${left}px; top:${top}px; `
            + `width:${areaWidth}px; height:${rowHeight}px; `
            + `display:flex; gap:${REPEAT_GAP}px; align-items:flex-start;`;

        // The row: one of these is stamped out per item the AI writes.
        const row = fromGroup && fromGroup.classList.contains('slide__group')
            ? fromGroup
            : document.createElement('div');
        row.classList.add('slide__group');

        slideEl.insertBefore(wrap, members[0]);
        if (row !== fromGroup) {
            // Re-anchor each element against the row it now lives in. Bounds
            // were measured before any of this, so overlaps are preserved
            // exactly – a text box sitting on a coloured card stays on it.
            members.forEach((el, i) => {
                const b = bounds[i];
                absolutise(el, { left: b.left - left, top: b.top - top, width: b.width, height: b.height });
                row.appendChild(el);
            });
        }
        wrap.appendChild(row);

        // The designed size is recorded here; layoutRepeat scales the row as a
        // whole to fit however many copies are asked for. The design inside a
        // row is never rewritten – doing that destroyed overlapping layouts and
        // collapsed empty shapes to zero height.
        row.style.cssText = `position:relative; width:${rowWidth}px; height:${rowHeight}px;`;

        // Default the count to what fits at full size, so a new repeat opens
        // with a preview that makes sense instead of a warning about the four
        // it never asked for.
        const fitsAtFullSize = Math.max(1, Math.floor((areaWidth + REPEAT_GAP) / (rowWidth + REPEAT_GAP)));
        wrap.setAttribute('data-slot-min', String(Math.min(2, Math.max(1, fitsAtFullSize))));
        wrap.setAttribute('data-slot-max', String(Math.max(2, Math.min(4, fitsAtFullSize))));
        layoutRepeat(wrap);

        // Every part of a row is content the AI writes – that is what a repeat
        // is for – so mark them now rather than making the teacher do it
        // element by element afterwards.
        const used = new Set();
        for (const el of row.children) {
            if (el.hasAttribute('data-slot')) { used.add(el.getAttribute('data-slot')); continue; }
            const guess = SlotGuess.forElement(el);
            let name = guess.name;
            for (let i = 2; used.has(name); i++) name = `${guess.name}_${i}`;
            used.add(name);
            el.setAttribute('data-slot', name);
            el.setAttribute('data-slot-type', guess.type);
            el.setAttribute('data-slot-hint', guess.hint);
            if (guess.type === 'image') el.setAttribute('data-slot-img', 'diagram');
        }

        clearMultiSelect();
        if (state.dragActive) {
            dragCleanups.push(makeDraggable(wrap));
            [...row.children].forEach(el => dragCleanups.push(makeDraggable(el)));
        }
        setSelectedEl(wrap);
        commitHistory(snap);
        if (!state.templateMode) setTemplateMode(true); else { refreshRepeatGhosts(); refreshSlotBadges(); }
        return wrap;
    }

    function makeGroupRepeat(group) {
        return makeRepeatFromSelection(group);
    }

    // ── Undo / Redo ───────────────────────────────────────
    const undoStack = [];
    const redoStack = [];
    const MAX_HISTORY = 50;

    /** Removes the translucent repeat previews. They are editor chrome. */
    function stripRepeatGhosts(root) {
        root?.querySelectorAll('[data-repeat-ghost]').forEach(g => g.remove());
        return root;
    }

    // Classes the editor puts on elements to show interaction state. They mean
    // nothing outside a live editing session and must not be stored.
    const EDITOR_STATE_CLASSES = ['is-entered', 'is-multi-selected'];

    function stripEditorState(root) {
        for (const cls of EDITOR_STATE_CLASSES) {
            root?.querySelectorAll(`.${cls}`).forEach(el => {
                el.classList.remove(cls);
                if (!el.getAttribute('class')) el.removeAttribute('class');
            });
        }
        return root;
    }

    // Ghosts are real DOM children so the container's own flex layout positions
    // them for free – but they must never reach the database, an undo snapshot
    // or a saved template, so every path that serialises the slide comes
    // through here.
    function getSlideHtml() {
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return null;
        const needsClean = slideEl.querySelector('[data-repeat-ghost]')
            || EDITOR_STATE_CLASSES.some(cls => slideEl.querySelector(`.${cls}`));
        if (!needsClean) return slideEl.outerHTML;
        return stripEditorState(stripRepeatGhosts(slideEl.cloneNode(true))).outerHTML;
    }

    function syncUndoButtons() {
        if (pptBtnUndo) pptBtnUndo.disabled = undoStack.length === 0;
        if (pptBtnRedo) pptBtnRedo.disabled = redoStack.length === 0;
    }

    function commitHistory(snapshot) {
        if (!snapshot) return;
        aiNoteHandEdit();
        // derive elements_json/contents_json from DOM before the undo
        // snapshot (which stays the pre-edit html; restore re-derives again).
        syncModelFromCurrentDom();
        undoStack.push(snapshot);
        if (undoStack.length > MAX_HISTORY) undoStack.shift();
        redoStack.length = 0;
        syncUndoButtons();
    }

    // The drag editor changes the live DOM; for model slides every committed
    // gesture is reflected back into elements_json/contents_json here. The
    // model's box is canonical – the sync reads each element's rendered box
    // and its group id straight off the DOM.
    function currentSlideModel() {
        if (!state.activeSlideId) return null;
        for (const [position, s] of Array.from(state.slides)) {
            // json_schema_version 1 alone marks a model slide. An empty blank
            // slide's elements_json is null until the first edit populates it,
            // but it still saves and AI-edits through the model endpoints.
            if (s.id === state.activeSlideId && s.json_schema_version === 1) {
                return { position, slide: s };
            }
        }
        return null;
    }

    // Group id is autoincrement, always scan for highest id before adding new
    // (including ids restored by an undo).
    function nextModelGroupId() {
        let max = 0;
        const bump = (v) => {
            const n = parseInt(String(v).replace(/^g/, ''), 10);
            if (Number.isFinite(n)) max = Math.max(max, n);
        };
        const entry = currentSlideModel();
        if (entry) {
            (entry.slide.elements_json || []).forEach(e => { if (e && e.group) bump(e.group); });
        }
        stageSlideInner?.querySelectorAll('[data-group-id]').forEach(n => bump(n.dataset.groupId));
        return `g${max + 1}`;
    }

    // True while the stage shows an AI proposal rather than the slide itself.
    // Read from the DOM, not the AI state, so it holds from anywhere in the file.
    function stageShowsAiPreview() {
        return !!document.getElementById('ppt-stage')?.classList.contains('is-ai-preview');
    }

    function syncModelFromCurrentDom() {
        // A proposal on the stage is not the slide: syncing it would keep it.
        if (stageShowsAiPreview()) return;
        const entry = currentSlideModel();
        if (!entry) return;
        const synced = SlideModelEditor.syncFromDom(
            { contents: entry.slide.contents_json, elements: entry.slide.elements_json },
            stageSlideInner,
        );
        if (!synced) return;
        const html = SlideModelRenderer.buildHtml(synced.contents, synced.elements);
        state.slides.set(entry.position, {
            ...entry.slide,
            contents_json: synced.contents,
            elements_json: synced.elements,
            html,
        });
    }

    function restoreSlideHtml(html) {
        deactivateDragEditor();
        SlideRenderer.renderSlideHtml(stageSlideInner, html);
        applyTheme(state.selectedTheme);
        scaleStage();
        if (state.pptMode === 'direct') activateDragEditor();
        if (state.activeSlidePosition && state.activeSlideId) {
            const prev = state.slides.get(state.activeSlidePosition);
            state.slides.set(state.activeSlidePosition, { ...(prev || {}), id: state.activeSlideId, html });
            updateThumb(state.activeSlidePosition, state.activeSlideId, html);
            // Undo on a model slide restored its pre-edit html – re-derive the
            // JSON columns from that DOM so the model follows the undo.
            syncModelFromCurrentDom();
        }
        syncUndoButtons();
    }

    function undoEdit() {
        endCrop(false);
        // An AI proposal is undone by discarding it, from its own bar.
        if (!undoStack.length || !state.activeSlideId || stageShowsAiPreview()) return;
        const current = getSlideHtml();
        if (current) redoStack.push(current);
        restoreSlideHtml(undoStack.pop());
    }

    function redoEdit() {
        if (!redoStack.length || !state.activeSlideId || stageShowsAiPreview()) return;
        const current = getSlideHtml();
        if (current) undoStack.push(current);
        restoreSlideHtml(redoStack.pop());
    }

    // ── makeDraggable ─────────────────────────────────────

    // Returns the direct .slide child that is visually on top at (clientX, clientY)
    function getTopmostSlideChild(clientX, clientY) {
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return null;
        const all = document.elementsFromPoint(clientX, clientY);
        for (const e of all) {
            if (e.parentElement === slideEl) return e;
        }
        return null;
    }

    function makeDraggable(el) {
        let tx = 0, ty = 0;
        const m = (el.style.transform || '').match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
        if (m) { tx = parseFloat(m[1]); ty = parseFloat(m[2]); }

        function beginTextEdit() {
            // A picture has no text, and an editable one could only be dragged
            // by its edge (see isOnEditHandle).
            if (el.tagName === 'IMG') return;
            if (el.contentEditable === 'true') return;
            el._preEditHtml = getSlideHtml(); // snapshot before text edit
            MathRender.revertMathInElement(el);
            el.contentEditable = 'true';
            el.style.cursor = 'text';
            el.focus();
        }

        const EDIT_HANDLE_PX = 10;

        function isOnEditHandle(e) {
            if (el.contentEditable !== 'true') return true;
            const rect = el.getBoundingClientRect();
            return (
                e.clientX <= rect.left + EDIT_HANDLE_PX ||
                e.clientX >= rect.right - EDIT_HANDLE_PX ||
                e.clientY <= rect.top + EDIT_HANDLE_PX ||
                e.clientY >= rect.bottom - EDIT_HANDLE_PX
            );
        }

        function updateCursor(e) {
            if (el.contentEditable === 'true') {
                el.style.cursor = e && isOnEditHandle(e) ? 'grab' : 'text';
            } else {
                el.style.cursor = 'grab';
            }
        }

        el.style.cursor = el.contentEditable === 'true' ? 'text' : 'grab';

        function onMouseDown(e) {
            if (el.contentEditable === 'true' && !isOnEditHandle(e)) return;
            if (e.button !== 0) return;
            const isSelected = state.selectedEl === el;

            // Who owns this click. As in PowerPoint, a group is a single object
            // until you double-click into it: single clicks select and move the
            // whole group, and only once you are inside does a click reach the
            // element itself. The hit test only knows about direct children of
            // the slide, so a group always wins by default.
            const topEl = getTopmostSlideChild(e.clientX, e.clientY);
            const parentGroup = el.parentElement?.classList?.contains('slide__group')
                ? el.parentElement : null;
            const claimsClick = topEl === el
                || (parentGroup && state.enteredGroup === parentGroup);
            if (!claimsClick) return;
            // Listeners fire child-first, so claiming here stops the group from
            // also starting a drag of itself on the same mousedown.
            if (parentGroup) e.stopPropagation();

            // Shift-click builds a multi-selection instead of starting a drag.
            if (e.shiftKey) {
                e.preventDefault();
                toggleMultiSelect(el);
                return;
            }
            // Pressing on one of several selected elements drags them all;
            // pressing anywhere else starts over with just this one.
            const multiDrag = multiSelected.size > 1 && multiSelected.has(el);
            if (!multiDrag) clearMultiSelect();

            e.preventDefault();

            // A picture has no text to edit, so a double-click crops it. Read
            // off the second press rather than the dblclick event: by then the
            // first click has selected the picture and may have redrawn the
            // frame under the pointer, and the event does not always arrive.
            if (el.tagName === 'IMG' && e.detail >= 2 && canCrop(el)) {
                startCrop(el);
                return;
            }

            // A picture grouped with nothing but its frame is selected on its
            // own straight away, while staying grouped. The group exists so the
            // frame is drawn round the picture, but the picture is the thing a
            // teacher reaches for - to resize or crop - and having to click it
            // twice to get past its frame read as not being able to select it
            // at all. Dragging it still carries the frame along (framedWith
            // below), so the two never come apart by accident. A picture
            // grouped with text (a card) is left alone: there the card is what
            // gets selected.
            let framedWith = null;
            if (el.tagName === 'IMG' && el.dataset?.groupId) {
                const all = Array.from(stageSlideInner.querySelectorAll(
                    `[data-group-id="${CSS.escape(el.dataset.groupId)}"]`));
                const mates = all.filter(m => m !== el);
                const onlyFrame = mates.length > 0 && mates.every(m =>
                    m.classList.contains('el--shape') || m.classList.contains('el--image-placeholder'));
                if (onlyFrame) { framedWith = all; state.soloEl = el; }
            }

            // Re-sync tx/ty from the element's actual transform – startResize clears
            // the transform without updating these closure vars, so they'd be stale.
            const curM = (el.style.transform || '').match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
            tx = curM ? parseFloat(curM[1]) : 0;
            ty = curM ? parseFloat(curM[2]) : 0;

            // Snapshot slide state before this potential drag for undo
            const preDragHtml = getSlideHtml();

            const startMX = e.clientX, startMY = e.clientY;
            const startTX = tx, startTY = ty;
            let moved = false;

            // Grouped elements move as one - each member takes same delta
            const dragMembers = multiDrag ? multiSelectWithGroups() : (framedWith || groupMembersOf(el));

            // Snapshot natural-flow position at drag start. A multi-selection
            // snaps by the box round all of it, not by the one under the pointer.
            const b0 = multiDrag ? unionBounds(dragMembers) : visibleBoundsOf(el);
            if (!b0) return;
            const fx = b0.left - tx;
            const fy = b0.top  - ty;
            const fw = b0.width;
            const fh = b0.height;
            const groupStarts = dragMembers.length > 1
                ? dragMembers.map(m => {
                    const mm = (m.style.transform || '').match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
                    return { m, tx: mm ? parseFloat(mm[1]) : 0, ty: mm ? parseFloat(mm[2]) : 0 };
                })
                : null;

            // The element stays in its own layer while it moves: lifting it
            // above everything for the drag showed it somewhere it would not
            // end up, and hid what it was being lined up behind.
            el.style.cursor = 'grabbing';

            function onMove(mv) {
                moved = true;
                const scale = stageCanvas.offsetWidth / 960;
                let newTX = startTX + (mv.clientX - startMX) / scale;
                let newTY = startTY + (mv.clientY - startMY) / scale;

                // Snap (8 CSS-px threshold)
                const SNAP = 8;
                const elL  = fx + newTX,  elR  = elL + fw;
                const elT  = fy + newTY,  elB  = elT + fh;
                const elCX = elL + fw / 2, elCY = elT + fh / 2;
                let snapX = null, snapY = null;

                if      (Math.abs(elL  -   0) < SNAP) { newTX -= elL;        snapX =   0; }
                else if (Math.abs(elR  - 960) < SNAP) { newTX += 960 - elR;  snapX = 960; }
                else if (Math.abs(elCX - 480) < SNAP) { newTX += 480 - elCX; snapX = 480; }

                if      (Math.abs(elT  -   0) < SNAP) { newTY -= elT;        snapY =   0; }
                else if (Math.abs(elB  - 540) < SNAP) { newTY += 540 - elB;  snapY = 540; }
                else if (Math.abs(elCY - 270) < SNAP) { newTY += 270 - elCY; snapY = 270; }

                const dtx = newTX - startTX;
                const dty = newTY - startTY;
                tx = newTX; ty = newTY;

                if (groupStarts) {
                    // Same delta for every member – the group's shape is fixed.
                    for (const g of groupStarts) {
                        g.m.style.transform = `translate(${g.tx + dtx}px, ${g.ty + dty}px)`;
                    }
                } else {
                    el.style.transform = `translate(${tx}px, ${ty}px)`;
                }

                updateSnapLines(snapX, snapY);
                // In a multi-drag the box may be on another member than the
                // one under the pointer; it moves with the set either way.
                if (state.selectedEl === el || (multiDrag && state.selectedEl)) {
                    updateSelBox(getGroupBounds(state.selectedEl));
                }
            }

            function onUp() {
                updateCursor(e);
                hideSnapLines();
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                if (!moved && multiDrag) {
                    // A click without a drag narrows the selection to this one.
                    clearMultiSelect();
                    setSelectedEl(el);
                } else if (!moved) {
                    // Second click on the already-selected element enters text edit,
                    // and steps inside its group so it moves on its own from here.
                    if (isSelected) {
                        if (el.dataset?.groupId && state.soloEl !== el) {
                            state.soloEl = el;
                            updateSelBox(getGroupBounds(el));
                        }
                        if (el.contentEditable !== 'true') beginTextEdit();
                    }
                    else setSelectedEl(el); // first click selects; background click deselects via onBgClick
                } else {
                    setSelectedEl(el);
                    // Convert the drag translate to an explicit offset before
                    // clearing it, so nothing snaps back. 
                    const bake = (m) => {
                        if (!isAbsoluteEl(m)) return; // Ignore if legacy html
                        const b = getElBoundsInSlide(m);
                        if (!b) return;
                        const origin = positioningOriginInSlide(m);
                        m.style.left = (b.left - origin.left) + 'px';
                        m.style.top  = (b.top  - origin.top)  + 'px';
                        m.style.transform = '';
                    };
                    if (groupStarts) groupStarts.forEach(g => bake(g.m));
                    else bake(el);
                    commitHistory(preDragHtml);
                    updateSelBox(getGroupBounds(el));
                }
            }

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        }

        function onDblClick(e) {
            const parentGroup = el.parentElement?.classList?.contains('slide__group')
                ? el.parentElement : null;

            // First double-click on a grouped element steps inside the group and
            // selects it, the way PowerPoint does – it does NOT start editing
            // text. A second double-click, now that we are inside, does.
            if (parentGroup && state.enteredGroup !== parentGroup) {
                e.stopPropagation();
                e.preventDefault();
                enterGroup(parentGroup);
                setSelectedEl(el);
                return;
            }
            beginTextEdit();
            // Browser's native dblclick word selection is preserved – don't override it
        }

        function onBlur() {
            setTimeout(() => {
                const active = document.activeElement;
                // Keep editing when focus moves to the color picker (or its native dialog)
                if (active === pptColorPicker || _colorPickerOpen) return;
                if (el.contentEditable === 'true') {
                    el.removeAttribute('contenteditable');
                    el.style.cursor = 'grab';
                }
                MathRender.renderMathInElement(el);
                // Commit whether or not we just cleaned up contenteditable –
                // setSelectedEl may have removed it externally before blur fired
                if (el._preEditHtml !== undefined) {
                    const current = getSlideHtml();
                    if (current !== el._preEditHtml) {
                        // Freeze group at designed boxes to prevent element shift
                        el.dataset.modelEdited = '1';
                        commitHistory(el._preEditHtml);
                    }
                    delete el._preEditHtml;
                }
                // Deselect when focus leaves the slide entirely
                if (state.selectedEl === el && !stageSlideInner?.contains(active)) {
                    setSelectedEl(null);
                }
            }, 0);
        }

        el.addEventListener('mousedown', onMouseDown, { passive: false });
        el.addEventListener('mouseenter', updateCursor);
        el.addEventListener('mousemove', updateCursor);
        el.addEventListener('mouseleave', updateCursor);
        el.addEventListener('dblclick',  onDblClick);
        el.addEventListener('blur',      onBlur);

        return () => {
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('mouseenter', updateCursor);
            el.removeEventListener('mousemove', updateCursor);
            el.removeEventListener('mouseleave', updateCursor);
            el.removeEventListener('dblclick',  onDblClick);
            el.removeEventListener('blur',      onBlur);
            el.style.cursor    = '';
            el.style.isolation = '';
        };
    }

    // ── Resize ────────────────────────────────────────────
    selBox?.addEventListener('mousedown', (e) => {
        const handle = e.target.closest('.rh');
        if (!handle || !state.selectedEl) return;
        e.preventDefault();
        e.stopPropagation();
        startResize(state.selectedEl, handle.dataset.dir, e);
    });

    function startResize(el, dir, e) {
        const b = getElBoundsInSlide(el);
        if (!b) return;

        // A group resizes as one object. Each member keeps its own
        // absolute geometry; only the box delta is shared.
        const members = groupMembersOf(el);
        const starts = members
            .map(m => ({ m, b: getElBoundsInSlide(m), origin: positioningOriginInSlide(m) }))
            .filter(s => s.b && s.b.width > 0 && s.b.height > 0);
        if (!starts.length) return;

        const preResizeHtml = getSlideHtml(); // snapshot before resize

        for (const s of starts) {
            // Promote flow members to free positioning: a flow member left
            // inside its parent reflows its siblings, so every member is
            // promoted up front, on every gesture, and any flex/grid sibling
            // of the first is frozen the first time.
            promoteToAbsolute(s.m, s.b, s.origin, s.m === el);
            // A picture is stretched by a resize, not trimmed by it.
            if (s.m.tagName === 'IMG') {
                makeImageExplicit(s.m);
                s.b = getElBoundsInSlide(s.m);
            }
        }

        // A lone cropped picture is resized by what shows of it: the handles
        // sit on the visible edges, and its box is worked back from them.
        const crop = starts.length === 1 ? cropInsetsOf(el) : null;

        // The reference box is the group's union when grouped, else the element's.
        let sb = { ...(crop ? visibleBoundsOf(el) : getElBoundsInSlide(el)) };
        if (starts.length > 1) {
            sb = {
                left:   Math.min(...starts.map(s => s.b.left)),
                top:    Math.min(...starts.map(s => s.b.top)),
                right:  Math.max(...starts.map(s => s.b.right)),
                bottom: Math.max(...starts.map(s => s.b.bottom)),
            };
            sb.width  = sb.right - sb.left;
            sb.height = sb.bottom - sb.top;
        }

        const startMX = e.clientX, startMY = e.clientY;

        function onMove(mv) {
            const scale = stageCanvas.offsetWidth / 960;
            const dx = (mv.clientX - startMX) / scale;
            const dy = (mv.clientY - startMY) / scale;

            let newL = sb.left, newT = sb.top;
            let newW = sb.width, newH = sb.height;
            const MIN = 20;

            if (dir.includes('e')) newW = Math.max(MIN, sb.width  + dx);
            if (dir.includes('s')) newH = Math.max(MIN, sb.height + dy);
            if (dir.includes('w')) { newW = Math.max(MIN, sb.width  - dx); newL = sb.right  - newW; }
            if (dir.includes('n')) { newH = Math.max(MIN, sb.height - dy); newT = sb.bottom - newH; }

            // Shift keeps the proportions. From a corner, whichever way the
            // pointer has gone furthest leads; from an edge, the other
            // dimension follows and grows evenly about its middle.
            if (mv.shiftKey && sb.width > 0 && sb.height > 0) {
                const ratio = sb.width / sb.height;
                const horiz = dir.includes('e') || dir.includes('w');
                const vert  = dir.includes('n') || dir.includes('s');
                const widthLeads = horiz && (!vert
                    || Math.abs(newW / sb.width - 1) >= Math.abs(newH / sb.height - 1));
                if (widthLeads) newH = newW / ratio;
                else newW = newH * ratio;
                if (newW < MIN || newH < MIN) {
                    const up = Math.max(MIN / newW, MIN / newH);
                    newW *= up; newH *= up;
                }
                newL = dir.includes('w') ? sb.right - newW
                    : horiz ? sb.left : sb.left + (sb.width - newW) / 2;
                newT = dir.includes('n') ? sb.bottom - newH
                    : vert ? sb.top : sb.top + (sb.height - newH) / 2;
            }

            if (crop) {
                const s = starts[0];
                const fullW = newW / (1 - crop.l - crop.r);
                const fullH = newH / (1 - crop.t - crop.b);
                s.m.style.left   = (newL - crop.l * fullW - s.origin.left) + 'px';
                s.m.style.top    = (newT - crop.t * fullH - s.origin.top)  + 'px';
                s.m.style.width  = fullW + 'px';
                s.m.style.height = fullH + 'px';
                updateSelBox({ left: newL, top: newT, width: newW, height: newH });
                return;
            }

            // Same delta for every member: the group's box delta, applied to each
            // member's own start box, so relative offsets and gaps are preserved.
            const dLeft = newL - sb.left;
            const dTop  = newT - sb.top;
            const dWidth  = newW - sb.width;
            const dHeight = newH - sb.height;

            for (const s of starts) {
                // Clamp relative to each member's own start size: the MIN guard
                // keeps a member from collapsing, but must never inflate a member
                // (e.g. a 5px accent bar) when its delta in that axis is 0.
                const mw = Math.max(Math.min(MIN, s.b.width),  s.b.width  + dWidth);
                const mh = Math.max(Math.min(MIN, s.b.height), s.b.height + dHeight);
                s.m.style.left   = (s.b.left + dLeft  - s.origin.left) + 'px';
                s.m.style.top    = (s.b.top  + dTop   - s.origin.top)  + 'px';
                s.m.style.width  = mw + 'px';
                s.m.style.height = mh + 'px';
            }

            // Stretching a repeat area re-fits its rows as you drag, so the
            // preview answers "how many will fit here" while you are deciding.
            if (el.hasAttribute('data-slot-repeat')) {
                layoutRepeat(el);
                updateRepeatFitHint();
            }

            // The selection box lives in the overlay over the slide, so it
            // stays in slide coordinates whatever the element is nested in.
            updateSelBox({ left: newL, top: newT, width: newW, height: newH });
        }

        function onUp() {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            commitHistory(preResizeHtml);
            updateSelBox(getGroupBounds(el));
        }

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }

    // ── Context menu ──────────────────────────────────────
    const CTX_DIRECT_ONLY = ['duplicate', 'bring-front', 'bring-forward', 'send-backward', 'send-back'];

    function showCtxMenu(x, y, el) {
        if (!slideCtxMenu) return;
        state.selectedEl = el;

        // Layering and duplication reposition an element against the slide
        // itself, so they only apply to direct children. Hide rather than
        // disable – a greyed-out row of four layer options is just noise.
        const direct = isDirectSlideChild(el);
        CTX_DIRECT_ONLY.forEach(a => {
            const btn = slideCtxMenu.querySelector(`[data-action="${a}"]`);
            if (btn) btn.hidden = !direct;
        });
        const layerSep = slideCtxMenu.querySelector('[data-ctx-group="layer"]');
        if (layerSep) layerSep.hidden = !direct;

        const parentBtn = slideCtxMenu.querySelector('[data-action="select-parent"]');
        if (parentBtn) parentBtn.hidden = direct;

        // Slots and repeating rows only mean something to a template, and
        // templates are only built in the designer. On a deck slide these
        // commands led nowhere, so they aren't offered there.
        const designing = isDesigningTemplate();

        // A model group is any element carrying a shared group id; a DOM
        // group (.slide__group) is a repeat row.
        const isGroup = !!el?.dataset?.groupId;
        const isDomGroup = !!el?.classList?.contains('slide__group');
        const groupBtn = slideCtxMenu.querySelector('[data-action="group"]');
        const ungroupBtn = slideCtxMenu.querySelector('[data-action="ungroup"]');
        const groupRepeatBtn = slideCtxMenu.querySelector('[data-action="group-repeat"]');
        const makeRepeatBtn = slideCtxMenu.querySelector('[data-action="make-repeat"]');
        // The one-step route: offered whenever a selection could become a row,
        // and not inside an existing repeat (nesting is a compile error).
        if (makeRepeatBtn) {
            makeRepeatBtn.hidden = !designing || multiSelected.size < 1 || !!el.closest('[data-slot-repeat]');
        }
        if (groupBtn) groupBtn.hidden = multiSelected.size < 2;
        if (ungroupBtn) ungroupBtn.hidden = !isGroup;
        // Only offered on a group that isn't already inside a repeat – nesting
        // repeats is a compile error, so don't let the menu propose one.
        if (groupRepeatBtn) groupRepeatBtn.hidden = !designing || !isDomGroup || !!el.closest('[data-slot-repeat]');
        const groupSep = slideCtxMenu.querySelector('[data-ctx-group="group"]');
        if (groupSep) {
            groupSep.hidden = !!(makeRepeatBtn?.hidden && groupBtn?.hidden
                && ungroupBtn?.hidden && groupRepeatBtn?.hidden);
        }

        const isSlot = !!el?.hasAttribute?.('data-slot');
        const isRepeat = !!el?.hasAttribute?.('data-slot-repeat');
        const markBtn = slideCtxMenu.querySelector('[data-action="mark-slot"]');
        const repeatBtn = slideCtxMenu.querySelector('[data-action="mark-repeat"]');
        const unmarkBtn = slideCtxMenu.querySelector('[data-action="unmark-slot"]');
        if (markBtn) {
            markBtn.hidden = !designing || isRepeat;
            markBtn.textContent = isSlot ? 'Edit AI slot…' : 'Mark as AI slot…';
        }
        if (repeatBtn) {
            // A repeat needs a container holding the row to repeat. Offering it
            // on a lone box or picture just produces a save-time error the
            // teacher has no way to act on.
            const canRepeat = isRepeat || el.children.length > 0;
            repeatBtn.hidden = !designing || isSlot || !canRepeat;
            repeatBtn.textContent = isRepeat ? 'Edit repeating group…' : 'Mark as repeating group…';
        }
        if (unmarkBtn) unmarkBtn.hidden = !designing || (!isSlot && !isRepeat);
        const slotSep = slideCtxMenu.querySelector('[data-ctx-group="slot"]');
        if (slotSep) {
            slotSep.hidden = !!(markBtn?.hidden && repeatBtn?.hidden && unmarkBtn?.hidden);
        }

        // Reveal on click is about teaching from a deck, so it stays out of
        // the template designer along with the other deck-only commands.
        const revealBtn = slideCtxMenu.querySelector('[data-action="reveal-toggle"]');
        const revealItemsBtn = slideCtxMenu.querySelector('[data-action="reveal-items"]');
        const revealing = !!revealOwnerOf(el);
        if (revealBtn) {
            revealBtn.hidden = designing;
            revealBtn.textContent = revealing ? 'Stop revealing on click' : 'Reveal on click';
        }
        if (revealItemsBtn) revealItemsBtn.hidden = designing || revealing || !revealListOf(revealTargetOf(el));
        const revealSep = slideCtxMenu.querySelector('[data-ctx-group="reveal"]');
        if (revealSep) revealSep.hidden = designing;

        const cropBtn = slideCtxMenu.querySelector('[data-action="crop"]');
        const uncropBtn = slideCtxMenu.querySelector('[data-action="uncrop"]');
        if (cropBtn) cropBtn.hidden = !canCrop(el);
        if (uncropBtn) uncropBtn.hidden = !canCrop(el) || !cropInsetsOf(el);

        const pasteBtn = slideCtxMenu.querySelector('[data-action="paste"]');
        if (pasteBtn) pasteBtn.hidden = !elementClipboard;

        slideCtxMenu.style.left = x + 'px';
        slideCtxMenu.style.top  = y + 'px';
        show(slideCtxMenu);
        // Long enough now to run off the bottom of the window when opened
        // low on a slide, where its last items could not be reached.
        const over = slideCtxMenu.getBoundingClientRect().bottom - (window.innerHeight - 8);
        if (over > 0) slideCtxMenu.style.top = Math.max(8, y - over) + 'px';
    }

    function hideCtxMenu() {
        hide(slideCtxMenu);
    }

    // ── Reveal on click ───────────────────────────────────
    // Present mode already brings in anything numbered data-reveal one click
    // at a time (presentRevealSteps). The worked-example and answer templates
    // number their own parts; this is the teacher doing the same to anything
    // else on a slide. A list revealed bullet by bullet carries its place in
    // the queue on the list itself (data-reveal-items) and a step per bullet.

    // On a model slide only whole elements are stored, so a click that landed
    // on a bullet or a line inside one means the element around it.
    function revealTargetOf(el) {
        if (!el) return null;
        return currentSlideModel() ? (el.closest('[data-el-id]') || el) : el;
    }

    function revealListOf(el) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        const list = el?.closest?.('ul, ol');
        return list && slideEl?.contains(list) && list.querySelector(':scope > li') ? list : null;
    }

    // Whatever already makes this element arrive on a click: itself, the list
    // it is a bullet of, or something it sits inside.
    function revealOwnerOf(el) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        const owner = el?.closest?.('[data-reveal-items], [data-reveal]');
        if (!owner || !slideEl?.contains(owner) || owner === slideEl) return null;
        const list = owner.parentElement?.closest('[data-reveal-items]');
        return list && slideEl.contains(list) ? list : owner;
    }

    function setAttrIfChanged(el, name, value) {
        if (el.getAttribute(name) !== String(value)) el.setAttribute(name, String(value));
    }

    // Closes the gaps a removal leaves and gives a list one step per bullet,
    // so the order on screen is always 1, 2, 3… Only writes what changed:
    // this runs before every save, and a write is what schedules the next one.
    // The things that arrive, in the order they arrive: one entry per marked
    // element, and one for a whole list revealed bullet by bullet.
    function revealUnits(slideEl) {
        return [...slideEl.querySelectorAll('[data-reveal-items], [data-reveal]')]
            .filter(el => !el.closest('[data-repeat-ghost]'))
            .filter(el => el.hasAttribute('data-reveal-items') || !el.parentElement.closest('[data-reveal-items]'))
            .map(el => ({
                el,
                list: el.hasAttribute('data-reveal-items'),
                at: Number(el.dataset.revealItems || el.dataset.reveal) || 0,
            }))
            .filter(u => u.at > 0)
            .sort((a, b) => a.at - b.at);
    }

    function renumberReveals(slideEl) {
        if (!slideEl) return;
        const units = revealUnits(slideEl);
        let step = 1;
        for (const u of units) {
            if (!u.list) { setAttrIfChanged(u.el, 'data-reveal', step++); continue; }
            setAttrIfChanged(u.el, 'data-reveal-items', step);
            u.el.removeAttribute('data-reveal');
            const items = [...u.el.children].filter(c => c.tagName === 'LI');
            if (!items.length) { step++; continue; }
            items.forEach(li => setAttrIfChanged(li, 'data-reveal', step++));
        }
    }

    function nextRevealStep(slideEl) {
        const used = [...slideEl.querySelectorAll('[data-reveal], [data-reveal-items]')]
            .map(el => Number(el.dataset.reveal || el.dataset.revealItems) || 0);
        return Math.max(0, ...used) + 1;
    }

    function clearReveal(owner) {
        owner.removeAttribute('data-reveal');
        if (owner.hasAttribute('data-reveal-items')) {
            owner.removeAttribute('data-reveal-items');
            owner.querySelectorAll(':scope > li').forEach(li => li.removeAttribute('data-reveal'));
        }
    }

    function toggleReveal(el, { byBullet = false } = {}) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl || !el) return;
        const snap = getSlideHtml();
        const owner = revealOwnerOf(el);
        if (owner) {
            clearReveal(owner);
        } else if (byBullet && revealListOf(revealTargetOf(el))) {
            revealListOf(revealTargetOf(el)).setAttribute('data-reveal-items', nextRevealStep(slideEl));
        } else {
            revealTargetOf(el).setAttribute('data-reveal', nextRevealStep(slideEl));
        }
        renumberReveals(slideEl);
        commitHistory(snap);
        refreshRevealBadges();
        if (!owner) showToast('Arrives on a click when you present');
    }

    // Moves one unit a place earlier or later in the order.
    function moveReveal(owner, delta) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl || !owner) return;
        const units = revealUnits(slideEl);
        const from = units.findIndex(u => u.el === owner);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= units.length) return;
        const snap = getSlideHtml();
        units.splice(to, 0, units.splice(from, 1)[0]);
        // Spaced-out placeholders in the new order; renumbering then closes
        // them up and gives a list its step per bullet.
        units.forEach((u, i) => u.el.setAttribute(u.list ? 'data-reveal-items' : 'data-reveal', (i + 1) * 1000));
        renumberReveals(slideEl);
        commitHistory(snap);
        refreshRevealBadges();
    }

    // A badge is the handle on its part's place in the order: clicking one
    // offers to move that part earlier or later, or to stop revealing it.
    const revealBadgeMenu = $('reveal-badge-menu');
    let revealMenuOwner = null;

    function openRevealMenu(badge) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl || !revealBadgeMenu || state.pptMode !== 'direct') return;
        const marked = [...slideEl.querySelectorAll(`[data-reveal="${CSS.escape(badge.dataset.step)}"]`)]
            .find(el => !el.closest('[data-repeat-ghost]'));
        const owner = marked && revealOwnerOf(marked);
        if (!owner) return;
        const units = revealUnits(slideEl);
        const at = units.findIndex(u => u.el === owner);
        revealMenuOwner = owner;
        revealBadgeMenu.querySelector('[data-reveal-act="earlier"]').hidden = at <= 0;
        revealBadgeMenu.querySelector('[data-reveal-act="later"]').hidden = at < 0 || at >= units.length - 1;
        const r = badge.getBoundingClientRect();
        revealBadgeMenu.style.left = r.left + 'px';
        revealBadgeMenu.style.top = (r.bottom + 4) + 'px';
        show(revealBadgeMenu);
        const over = revealBadgeMenu.getBoundingClientRect().bottom - (window.innerHeight - 8);
        if (over > 0) revealBadgeMenu.style.top = Math.max(8, r.top - 4 - revealBadgeMenu.offsetHeight) + 'px';
    }

    revealBadgeMenu?.addEventListener('click', (e) => {
        const act = e.target.closest('[data-reveal-act]')?.dataset.revealAct;
        const owner = revealMenuOwner;
        hide(revealBadgeMenu);
        revealMenuOwner = null;
        if (!act || !owner?.isConnected) return;
        if (act === 'earlier') moveReveal(owner, -1);
        else if (act === 'later') moveReveal(owner, 1);
        else if (act === 'stop') toggleReveal(owner);
    });
    document.addEventListener('mousedown', (e) => {
        if (isShown(revealBadgeMenu) && !revealBadgeMenu.contains(e.target)) hide(revealBadgeMenu);
    });

    // The order is invisible on the slide itself, so the editor numbers each
    // part in the overlay. Never part of the slide: nothing here is saved.
    let revealBadgeLayer = null;
    let revealBadgeFrame = 0;

    function refreshRevealBadges() {
        if (revealBadgeFrame) return;
        revealBadgeFrame = requestAnimationFrame(() => {
            revealBadgeFrame = 0;
            if (!stageOverlay) return;
            if (!revealBadgeLayer) {
                revealBadgeLayer = document.createElement('div');
                revealBadgeLayer.id = 'reveal-badge-layer';
                // On mousedown-less click only: the badge sits over the slide,
                // and must not start a drag of whatever is under it.
                revealBadgeLayer.addEventListener('mousedown', (e) => { if (e.target.closest('.reveal-badge')) e.stopPropagation(); });
                revealBadgeLayer.addEventListener('click', (e) => {
                    const badge = e.target.closest('.reveal-badge');
                    if (badge) { e.stopPropagation(); openRevealMenu(badge); }
                });
                revealBadgeLayer.addEventListener('keydown', (e) => {
                    const badge = e.target.closest('.reveal-badge');
                    if (badge && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); openRevealMenu(badge); }
                });
                stageOverlay.appendChild(revealBadgeLayer);
            }
            const slideEl = stageSlideInner?.querySelector('.slide');
            const marked = slideEl && !isDesigningTemplate()
                ? [...slideEl.querySelectorAll('[data-reveal]')].filter(el => !el.closest('[data-repeat-ghost]'))
                : [];
            revealBadgeLayer.innerHTML = '';
            for (const el of marked) {
                const b = visibleBoundsOf(el);
                if (!b || (!b.width && !b.height)) continue;
                const badge = document.createElement('span');
                badge.className = 'reveal-badge';
                badge.dataset.step = el.dataset.reveal;
                badge.setAttribute('role', 'button');
                badge.tabIndex = 0;
                badge.textContent = el.dataset.reveal;
                badge.title = 'Arrives on click ' + el.dataset.reveal + '. Click to change the order or stop revealing it.';
                // Beside the part, level with its first line, not over its
                // corner: there it sat on top of the first letters of the text.
                badge.style.left = Math.max(2, b.left - 24) + 'px';
                badge.style.top = Math.max(2, b.top + Math.min(6, Math.max(0, (b.height - 18) / 2))) + 'px';
                revealBadgeLayer.appendChild(badge);
            }
        });
    }

    // ── Copy and paste between slides ─────────────────────
    // Held in memory, not on the system clipboard: what travels is a slide
    // element, and nothing outside this editor could do anything with it.
    //
    // A copy is rebuilt as the same kind of element the toolbar adds (text
    // box, picture, shape) with its look written inline. Its original classes
    // only mean something on the slide it came from: a heading's size comes
    // from that slide's template, and would be lost on any other.
    let elementClipboard = null;

    function clipboardTargets() {
        const multi = multiSelectList();
        if (multi.length > 1) return multi;
        const el = state.selectedEl;
        if (!el) return [];
        const base = currentSlideModel() ? (el.closest('[data-el-id]') || el) : el;
        return groupMembersOf(base);
    }

    function clipboardCopyOf(el, slideEl) {
        const b = getElBoundsInSlide(el);
        if (!b || !b.width) return null;
        const cs = getComputedStyle(el);
        const slideCs = getComputedStyle(slideEl);
        const isImage = el.tagName === 'IMG';
        const isShape = !isImage && (el.classList.contains('slide__added-shape') || el.classList.contains('el--shape'));
        let node;

        if (isImage) {
            node = document.createElement('img');
            node.src = el.currentSrc || el.src;
            node.alt = el.alt || '';
            node.className = 'slide__added-image';
            // The credit and the upload's storage path travel with the picture.
            ['imagePath', 'unsplashId', 'unsplashCredit', 'unsplashProfile'].forEach((k) => {
                if (el.dataset[k]) node.dataset[k] = el.dataset[k];
            });
            node.style.display = 'block';
            node.style.objectFit = cs.objectFit;
            node.style.borderRadius = cs.borderRadius;
            node.style.height = b.height + 'px';
            if (el.style.clipPath) node.style.clipPath = el.style.clipPath;   // its crop comes too
        } else if (isShape) {
            node = document.createElement('div');
            node.className = 'slide__added-shape';
            // The inline value first: it is usually a theme variable, and a
            // copy that keeps it still follows a change of theme.
            node.style.background = el.style.background || el.style.backgroundColor
                || (cs.backgroundImage !== 'none' ? cs.backgroundImage : cs.backgroundColor);
            node.style.borderRadius = cs.borderRadius;
            node.style.opacity = cs.opacity;
            node.style.height = b.height + 'px';
        } else {
            const source = el.cloneNode(true);
            MathRender.revertMathInElement(source);   // $…$ source, not rendered KaTeX
            source.querySelectorAll('[data-reveal]').forEach(n => n.removeAttribute('data-reveal'));
            source.querySelectorAll('[contenteditable]').forEach(n => n.removeAttribute('contenteditable'));
            const items = source.matches('ul, ol') ? [...source.children].filter(c => c.tagName === 'LI') : [];
            node = document.createElement('div');
            node.className = 'slide__added-text';
            // A list's markers are drawn by its template, so its bullets
            // become lines of their own rather than vanishing.
            node.innerHTML = items.length
                ? items.map(li => `<p>&bull; ${li.innerHTML}</p>`).join('')
                : source.innerHTML;
            node.style.fontFamily = cs.fontFamily === slideCs.fontFamily ? 'var(--s-font)' : cs.fontFamily;
            node.style.fontSize = cs.fontSize;
            node.style.fontWeight = cs.fontWeight;
            node.style.fontStyle = cs.fontStyle;
            node.style.lineHeight = cs.lineHeight;
            node.style.letterSpacing = cs.letterSpacing;
            node.style.textAlign = cs.textAlign;
            node.style.color = el.style.color || (cs.color === slideCs.color ? 'var(--s-text)' : cs.color);
            if (cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent') {
                node.style.backgroundColor = el.style.backgroundColor || cs.backgroundColor;
                node.style.borderRadius = cs.borderRadius;
            }
            node.style.padding = cs.padding;
        }

        node.style.position = 'absolute';
        node.style.boxSizing = 'border-box';
        node.style.cursor = 'grab';
        node.style.left = Math.round(b.left) + 'px';
        node.style.top = Math.round(b.top) + 'px';
        node.style.width = Math.round(b.width) + 'px';
        return node.outerHTML;
    }

    function copySelection() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return false;
        const items = clipboardTargets().map(el => clipboardCopyOf(el, slideEl)).filter(Boolean);
        if (!items.length) return false;
        elementClipboard = { items, slideId: state.activeSlideId, pastes: 0 };
        return true;
    }

    // Cut leaves an uploaded picture's file where it is: the copy on the
    // clipboard still points at it, and deleting it here would paste a broken
    // image onto the next slide.
    function cutSelection() {
        const targets = clipboardTargets();
        if (!copySelection()) return false;
        const snap = getSlideHtml();
        setSelectedEl(null);
        clearMultiSelect();
        targets.forEach(el => el.remove());
        commitHistory(snap);
        return true;
    }

    function pasteClipboard() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!elementClipboard || !slideEl || state.pptMode !== 'direct') return false;
        // Onto another slide it lands exactly where it was, so a logo or a
        // diagram lines up from slide to slide. Back onto its own slide it
        // steps aside each time, or the copy would hide the original.
        if (elementClipboard.slideId !== state.activeSlideId) {
            elementClipboard.slideId = state.activeSlideId;
            elementClipboard.pastes = 0;
        } else {
            elementClipboard.pastes += 1;
        }
        const shift = elementClipboard.pastes * 20;

        const snap = getSlideHtml();
        const holder = document.createElement('template');
        holder.innerHTML = elementClipboard.items.join('');
        const nodes = [...holder.content.children];
        let z = Math.max(0, ...Array.from(slideEl.children).map(getSlideZ));
        nodes.forEach((node) => {
            node.style.left = (parseFloat(node.style.left) + shift) + 'px';
            node.style.top = (parseFloat(node.style.top) + shift) + 'px';
            node.style.zIndex = String(++z);
            slideEl.appendChild(node);
            if (node.dataset.imagePath) managedImageEls.add(node);
            MathRender.renderMathInElement(node);
            if (state.dragActive) dragCleanups.push(makeDraggable(node));
        });
        commitHistory(snap);
        clearMultiSelect();
        setSelectedEl(nodes[nodes.length - 1] || null);
        return true;
    }

    // An uploaded picture owns a file on the CDN, and deleting the picture
    // deletes the file. Copies (a duplicate, a paste, a duplicated slide) all
    // point at the same file, so it only goes with the last one.
    function imageUsedElsewhere(img) {
        const path = img.dataset?.imagePath;
        if (!path) return false;
        const sameFile = n => n !== img && n.dataset.imagePath === path;
        if ([...stageSlideInner.querySelectorAll('img[data-image-path]')].some(sameFile)) return true;
        for (const s of state.slides.values()) {
            if (s.id !== state.activeSlideId && typeof s.html === 'string' && s.html.includes(path)) return true;
        }
        return false;
    }

    // ── Slide background colour ───────────────────────────
    // A theme sets the whole look of a deck. This is the one thing teachers
    // ask to set by hand: a plain colour behind the slide, usually a tint for
    // pupils who find black on white hard to read.
    //
    // It is stored in the slide itself, so it follows the slide everywhere its
    // markup goes (exports, copies, the pupil's page). A template slide takes
    // it as an inline background on its root. A model slide has no root of its
    // own to style - its markup is rebuilt from its elements - so it takes a
    // full-canvas shape underneath everything else, marked as the background.
    const SLIDE_BG_Z = 2;   // above a template's own backdrop (1), below its content (5+)
    const SLIDE_BG_ID = 'el_slide_bg';
    // Slides designed around their own dark backdrop and light text. Recolouring
    // the canvas under them leaves white writing on a pale page, so "all
    // slides" passes them by; "this slide" still does what it is told.
    const OWN_BACKDROP_RE = /\bslide--(title|title-image|quote|section)\b/;

    function isHexColour(v) {
        return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
    }

    function coversCanvas(box) {
        return !!box && box.x <= 0 && box.y <= 0 && box.width >= 960 && box.height >= 540;
    }

    function slideHasOwnBackdrop(slide) {
        if (slide.json_schema_version === 1) {
            return (slide.elements_json || []).some(e => e && e.role !== 'background' && coversCanvas(e.box));
        }
        const root = (slide.html || '').match(/<div[^>]*class="([^"]*)"/);
        return !!root && OWN_BACKDROP_RE.test(root[1]);
    }

    // `colour` null takes the background off again.
    function setBackgroundOnSlideEl(slideEl, colour, isModel) {
        if (!isModel) {
            if (colour) slideEl.style.background = colour;
            else slideEl.style.removeProperty('background');
            if (!slideEl.getAttribute('style')) slideEl.removeAttribute('style');
            return;
        }
        let bg = slideEl.querySelector(':scope > [data-slide-bg]');
        if (!colour) { bg?.remove(); return; }
        if (!bg) {
            bg = document.createElement('div');
            bg.className = 'el el--shape slide__added-shape';
            bg.setAttribute('data-slide-bg', '');
            bg.style.cssText = `position:absolute; left:0px; top:0px; width:960px; height:540px; z-index:${SLIDE_BG_Z};`;
            slideEl.prepend(bg);
        }
        bg.style.background = colour;
    }

    // The same change made to a slide that is not on the stage, where there is
    // no DOM to edit: straight onto its stored model, or its stored markup.
    function slideWithBackground(slide, colour) {
        if (slide.json_schema_version === 1) {
            const elements = (slide.elements_json || []).filter(e => e && e.role !== 'background');
            if (colour) {
                elements.unshift({
                    id: SLIDE_BG_ID, type: 'shape', content_id: null, role: 'background',
                    box: { x: 0, y: 0, width: 960, height: 540 },
                    z_index: SLIDE_BG_Z, style_overrides: { background: colour },
                    visible: true, transform: { rotation: 0, flipH: false, flipV: false }, group: null,
                });
            }
            const contents = slide.contents_json || {};
            return { ...slide, elements_json: elements, html: SlideModelRenderer.buildHtml(contents, elements) };
        }
        const holder = document.createElement('template');
        holder.innerHTML = slide.html || '';
        const root = holder.content.querySelector('.slide');
        if (!root) return slide;
        setBackgroundOnSlideEl(root, colour, false);
        return { ...slide, html: holder.innerHTML };
    }

    function setActiveSlideBackground(colour) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl || !state.activeSlideId) return;
        const snap = getSlideHtml();
        setSelectedEl(null);
        setBackgroundOnSlideEl(slideEl, colour, !!currentSlideModel());
        commitHistory(snap);
    }

    async function saveSlideRecord(slide) {
        const isModel = slide.json_schema_version === 1;
        const payload = { html: slide.html };
        if (isModel) {
            payload.model = {
                contents_json: slide.contents_json || {},
                elements_json: slide.elements_json || [],
                json_schema_version: 1,
            };
        }
        const res = await apiFetch(`/api/slides/${slide.id}/${isModel ? 'model' : 'html'}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Save failed');
        const saved = await res.json().catch(() => ({}));
        return typeof saved.html === 'string' ? saved.html : slide.html;
    }

    let backgroundBusy = false;

    async function setAllSlideBackgrounds(colour) {
        if (backgroundBusy || !state.slides.size) return;
        backgroundBusy = true;
        setError(stageError, '');
        try {
            // Whatever is half-typed on the stage goes in first, so the slide
            // being rewritten below is the one the teacher is looking at.
            clearTimeout(autoSaveTimer);
            await savePptDirect();
            pauseAutoSave();

            let changed = 0, skipped = 0, failed = 0;
            for (const [position, slide] of [...state.slides.entries()]) {
                if (!slide?.id || typeof slide.html !== 'string') continue;
                if (colour && slideHasOwnBackdrop(slide)) { skipped++; continue; }
                const next = slideWithBackground(slide, colour);
                if (next === slide || next.html === slide.html) continue;
                try {
                    next.html = await saveSlideRecord(next);
                    state.slides.set(position, next);
                    updateThumb(position, next.id, next.html);
                    changed++;
                } catch { failed++; }
            }

            const active = [...state.slides.entries()].find(([, s]) => s.id === state.activeSlideId);
            if (active) selectSlide(active[1].id, active[1].html, active[0]);

            if (failed) setError(stageError, `${failed} slide${failed === 1 ? '' : 's'} could not be saved. Try again.`);
            else if (!colour) showToast(changed ? 'Background colour removed' : 'No slide had a background colour');
            else showToast(`Background set on ${changed} slide${changed === 1 ? '' : 's'}`
                + (skipped ? `, ${skipped} title or section slide${skipped === 1 ? '' : 's'} left as designed` : ''), 3500);
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(stageError, 'Could not change the background.');
        } finally {
            resumeAutoSave();
            backgroundBusy = false;
        }
    }

    // ── Thumbnail context menu ────────────────────────────
    const thumbCtxMenu = $('thumb-ctx-menu');
    let thumbCtxSlideId = null;

    function showThumbCtxMenu(x, y, slideId) {
        if (!thumbCtxMenu) return;
        thumbCtxSlideId = slideId;
        thumbCtxMenu.style.left = x + 'px';
        thumbCtxMenu.style.top  = y + 'px';
        show(thumbCtxMenu);
    }

    function hideThumbCtxMenu() {
        hide(thumbCtxMenu);
        thumbCtxSlideId = null;
    }

    // ── Empty-slide context menu (paste) ──────────────────
    const slideBgCtxMenu = $('slide-bg-ctx-menu');
    slideBgCtxMenu?.addEventListener('click', (e) => {
        if (!e.target.closest('[data-action="paste"]')) return;
        hide(slideBgCtxMenu);
        pasteClipboard();
    });
    document.addEventListener('mousedown', (e) => {
        if (isShown(slideBgCtxMenu) && !slideBgCtxMenu.contains(e.target)) hide(slideBgCtxMenu);
    });

    // ── Background colour controls (Theme menu) ───────────
    const slideBgPicker = $('slide-bg-picker');
    const slideBgRow = $('slide-bg-row');
    // The Theme menu closes on any click inside it, which suits picking a
    // theme and not this: choosing a colour takes more than one click.
    slideBgRow?.addEventListener('click', (e) => {
        e.stopPropagation();
        const btn = e.target.closest('[data-bg]');
        if (!btn) return;
        const colour = isHexColour(slideBgPicker?.value) ? slideBgPicker.value : null;
        if (btn.dataset.bg === 'this' && colour) setActiveSlideBackground(colour);
        else if (btn.dataset.bg === 'all' && colour) setAllSlideBackgrounds(colour);
        else if (btn.dataset.bg === 'clear-this') setActiveSlideBackground(null);
        else if (btn.dataset.bg === 'clear-all') setAllSlideBackgrounds(null);
    });

    function rebuildPanel() {
        const ordered = [...state.slides.entries()].sort((a, b) => a[0] - b[0]).map(([, s]) => s);
        state.slides.clear();
        ordered.forEach((s, i) => state.slides.set(i + 1, s));
        slidePanel.innerHTML = '';
        ordered.forEach((s, i) => updateThumb(i + 1, s.id, s.html));
        slidePanel.querySelectorAll('.slide-thumb').forEach(t => {
            t.classList.toggle('active', t.dataset.slideId === state.activeSlideId);
        });
        if (stageInfo) stageInfo.textContent = `Slide ${state.activeSlidePosition} / ${state.slides.size}`;
        placeSeamThumb();
    }

    slidePanel?.addEventListener('contextmenu', (e) => {
        const thumb = e.target.closest('.slide-thumb');
        if (!thumb) return;
        e.preventDefault();
        showThumbCtxMenu(e.clientX, e.clientY, thumb.dataset.slideId);
    });

    // Copies a slide and puts the copy straight after it. Reached from the
    // thumbnail's right-click menu and from the button under the thumbnails -
    // the menu alone was somewhere most teachers never looked.
    async function duplicateSlide(slideId) {
        if (!slideId || !state.presentationId) return;
        setError(stageError, '');
        try {
            // The copy is made from what is stored, so anything still unsaved
            // on the stage goes in first.
            clearTimeout(autoSaveTimer);
            if (slideId === state.activeSlideId) await savePptDirect();
            const res = await apiFetch(
                `/api/presentations/${state.presentationId}/slides/${slideId}/duplicate`,
                { method: 'POST' }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) { setError(stageError, data.error || 'Could not duplicate the slide. Try again.'); return; }
            rememberNotes(data.slideId, data.speaker_notes);

            // Rebuild state: insert new slide after the original, renumber sequentially
            const ordered = [...state.slides.entries()].sort((a, b) => a[0] - b[0]).map(([, s]) => s);
            state.slides.clear();
            let pos = 1;
            for (const s of ordered) {
                state.slides.set(pos++, s);
                if (s.id === slideId) {
                    state.slides.set(pos++, {
                        id: data.slideId,
                        html: data.html,
                        contents_json: data.contents_json !== undefined ? parseJson(data.contents_json) : s.contents_json,
                        elements_json: data.elements_json !== undefined ? parseJson(data.elements_json) : s.elements_json,
                        json_schema_version: data.json_schema_version !== undefined ? data.json_schema_version : s.json_schema_version,
                    });
                }
            }
            rebuildPanel();
            const newPos = [...state.slides.entries()].find(([, s]) => s.id === data.slideId)?.[0];
            selectSlide(data.slideId, data.html, newPos);
            showToast('Slide duplicated');
        } catch (e) {
            if (e.message !== 'Not authenticated') setError(stageError, 'Could not duplicate the slide. Try again.');
        }
    }

    $('btn-duplicate-slide')?.addEventListener('click', () => duplicateSlide(state.activeSlideId));

    thumbCtxMenu?.addEventListener('click', async (e) => {
        const btn = e.target.closest('.ctx-item');
        if (!btn) return;
        const action  = btn.dataset.action;
        const slideId = thumbCtxSlideId;
        hideThumbCtxMenu();
        if (!slideId || !state.presentationId) return;

        if (action === 'duplicate') {
            await duplicateSlide(slideId);

        } else if (action === 'delete') {
            const total = state.slides.size;
            if (total <= 1) return; // don't delete last slide

            try {
                const res = await apiFetch(
                    `/api/presentations/${state.presentationId}/slides/${slideId}`,
                    { method: 'DELETE' }
                );
                if (!res.ok) return;

                const deletedPos = [...state.slides.entries()].find(([, s]) => s.id === slideId)?.[0];
                state.slides.delete(deletedPos);

                // If deleting the active slide, switch to nearest
                if (state.activeSlideId === slideId) {
                    const remaining = [...state.slides.entries()].sort((a, b) => a[0] - b[0]);
                    const next = remaining.find(([pos]) => pos > deletedPos) || remaining[remaining.length - 1];
                    if (next) {
                        state.activeSlideId = null; // clear before rebuildPanel so highlight resets
                        rebuildPanel();
                        selectSlide(next[1].id, next[1].html, [...state.slides.entries()].find(([, s]) => s.id === next[1].id)?.[0]);
                    }
                } else {
                    rebuildPanel();
                }
            } catch {}
        }
    });

    document.addEventListener('mousedown', (e) => {
        if (isShown(thumbCtxMenu) && !thumbCtxMenu.contains(e.target)) {
            hideThumbCtxMenu();
        }
    });

    stageSlideInner?.addEventListener('contextmenu', (e) => {
        if (state.pptMode !== 'direct') return;
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return;
        // Select the exact element that was right-clicked, at any depth. Marking
        // a nested node (e.g. .slide__heading inside .slide__content) as an AI
        // slot needs to reach it; "Select Parent" walks back up from here.
        const el = e.target === slideEl || !slideEl.contains(e.target) ? null : e.target;
        if (!el) {
            // Empty slide: the only thing to do here is put down a copy.
            if (!elementClipboard || !slideBgCtxMenu) return;
            e.preventDefault();
            slideBgCtxMenu.style.left = e.clientX + 'px';
            slideBgCtxMenu.style.top  = e.clientY + 'px';
            show(slideBgCtxMenu);
            return;
        }
        e.preventDefault();
        // Right-click targets the exact element at any depth – marking a slot
        // inside a repeat row depends on that. If it lands inside a group,
        // open the group too, so the element can then be moved and resized
        // rather than being selected but inert.
        const parentGroup = el.parentElement?.classList?.contains('slide__group')
            ? el.parentElement : null;
        if (parentGroup) enterGroup(parentGroup);
        setSelectedEl(el);
        showCtxMenu(e.clientX, e.clientY, el);
    });

    slideCtxMenu?.addEventListener('click', (e) => {
        const btn = e.target.closest('.ctx-item');
        if (!btn) return;
        const action = btn.dataset.action;
        const el = state.selectedEl;
        hideCtxMenu();
        if (!el) return;

        if (action === 'select-parent') {
            const slideEl = stageSlideInner.querySelector('.slide');
            const parent = el.parentElement;
            if (parent && parent !== slideEl && slideEl?.contains(parent)) setSelectedEl(parent);
            else if (parent === slideEl) setSelectedEl(el); // already top level – no-op

        } else if (action === 'edit-text') {
            el._preEditHtml = getSlideHtml();
            MathRender.revertMathInElement(el);
            el.contentEditable = 'true';
            el.style.cursor = 'text';
            el.focus();
            document.execCommand('selectAll');

        } else if (action === 'duplicate') {
            const b = getElBoundsInSlide(el);
            const clone = el.cloneNode(true);
            if (b) {
                // Bake position so clone is free of percentage values
                clone.style.left      = (b.left + 20) + 'px';
                clone.style.top       = (b.top  + 20) + 'px';
                clone.style.width     = b.width  + 'px';
                clone.style.height    = b.height + 'px';
                clone.style.transform = '';
            }
            appendDraggable(clone); // also calls setSelectedEl(clone)

        } else if (action === 'copy') {
            if (copySelection()) showToast('Copied');

        } else if (action === 'cut') {
            if (cutSelection()) showToast('Cut');

        } else if (action === 'paste') {
            pasteClipboard();

        } else if (action === 'crop') {
            startCrop(el);

        } else if (action === 'uncrop') {
            removeCrop(el);

        } else if (action === 'reveal-toggle') {
            toggleReveal(el);

        } else if (action === 'reveal-items') {
            toggleReveal(el, { byBullet: true });

        } else if (action === 'bring-front') {
            const snap = getSlideHtml();
            layerBringFront(el);
            commitHistory(snap);
            setSelectedEl(el);

        } else if (action === 'bring-forward') {
            const snap = getSlideHtml();
            layerBringForward(el);
            commitHistory(snap);
            setSelectedEl(el);

        } else if (action === 'send-backward') {
            const snap = getSlideHtml();
            layerSendBackward(el);
            commitHistory(snap);
            setSelectedEl(el);

        } else if (action === 'send-back') {
            const snap = getSlideHtml();
            layerSendBack(el);
            commitHistory(snap);
            setSelectedEl(el);

        } else if (action === 'make-repeat') {
            const wrap = makeRepeatFromSelection();
            if (wrap) openSlotForm(wrap, 'repeat');

        } else if (action === 'group') {
            groupSelected();

        } else if (action === 'ungroup') {
            ungroupSelected();

        } else if (action === 'group-repeat') {
            const wrap = makeGroupRepeat(el);
            // Straight into the naming form: a repeat still needs a field name
            // and a row count before it means anything.
            if (wrap) openSlotForm(wrap, 'repeat');

        } else if (action === 'mark-slot') {
            openSlotForm(el, 'slot');

        } else if (action === 'mark-repeat') {
            openSlotForm(el, 'repeat');

        } else if (action === 'unmark-slot') {
            const snap = getSlideHtml();
            SLOT_ATTRS.forEach(a => el.removeAttribute(a));
            commitHistory(snap);
            refreshSlotBadges();

        } else if (action === 'delete') {
            deleteSelectedElement();
        }
    });

    // Close context menu on outside click or Escape
    document.addEventListener('mousedown', (e) => {
        if (isShown(slideCtxMenu) && !slideCtxMenu.contains(e.target)) {
            hideCtxMenu();
        }
    });
    document.addEventListener('keydown', (e) => {
        if (state.presenting) return;
        if (e.key === 'Escape') { hideCtxMenu(); hideThumbCtxMenu(); closeAddSlideModal(); }
    });

    // ── User-created slide templates ──────────────────────
    // A template is the slide the teacher designed, with the AI-fillable
    // regions marked by data-slot attributes. Everything here writes those
    // attributes onto the live slide; the server compiles them into a fill
    // schema on save (utils/templateCompile.js).

    const SLOT_ATTRS = [
        'data-slot', 'data-slot-type', 'data-slot-hint', 'data-slot-min',
        'data-slot-max', 'data-slot-img', 'data-slot-optional',
        'data-slot-repeat', 'data-slot-fit',
    ];
    const SLOT_NAME_RE = /^[a-z][a-z0-9_]{0,29}$/;
    const SLOT_RESERVED = new Set([
        'image_url', 'image_credit', 'diagram_url', 'triggerDownload',
        'position', 'template', 'slide_title', 'key_points', 'notes',
    ]);

    // Only the slot-outline toggle survives in the borrowed toolbar; saving a
    // deck slide as a template and opening the library are gone from it.
    const pptTemplateMode  = $('ppt-template-mode');
    const slotForm         = $('slot-form');
    const slotFormName     = $('slot-form-name');
    const slotFormType     = $('slot-form-type');
    const slotFormImg      = $('slot-form-img');
    const slotFormHint     = $('slot-form-hint');
    const slotFormMin      = $('slot-form-min');
    const slotFormMax      = $('slot-form-max');
    const slotFormOptional = $('slot-form-optional');
    const slotFormFit      = $('slot-form-fit');
    const slotFormError    = $('slot-form-name-error');

    let slotFormTarget = null;   // element being marked
    let slotFormKind   = 'slot'; // 'slot' | 'repeat'
    let slotFormOriginalCount = null; // min/max before the live preview edited them
    let badgeLayer     = null;

    state.templateMode = false;
    state.customTemplates = [];

    // ── Slot badges ───────────────────────────────────────
    function ensureBadgeLayer() {
        if (badgeLayer && document.body.contains(badgeLayer)) return badgeLayer;
        badgeLayer = document.createElement('div');
        badgeLayer.id = 'slot-badge-layer';
        // Badges now only ever exist over the designer, whose slide sits inside
        // a modal – so they always need to clear the modal layer. It used to be
        // a class toggled on and off around the designer's lifetime.
        badgeLayer.classList.add('is-designing');
        document.body.appendChild(badgeLayer);
        return badgeLayer;
    }

    // Badges float above the page at a lower z-index than any modal, so an open
    // modal does not cover them – it only dims them through its translucent
    // backdrop, which reads as badges sitting on top of the dialog. They are
    // about the slide underneath, so they go away while a dialog is up.
    let badgesSuppressed = false;

    function suppressSlotBadges(on) {
        badgesSuppressed = !!on;
        refreshSlotBadges();
    }

    /** "recap_points" is a name for the AI; "Recap points" is a name for a person. */
    function humaniseSlotName(name) {
        const words = String(name || '').replace(/_/g, ' ').trim();
        return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Slot';
    }

    function refreshSlotBadges() {
        // The layer is fixed and always on screen – only its z-index moves – so
        // anything left in it keeps showing over whatever comes next. Marking
        // slots only happens in the designer, so badges may not outlive it,
        // whatever route was taken out.
        if (!isDesigningTemplate() || !state.templateMode || badgesSuppressed) {
            clearSlotBadges();
            return;
        }
        const layer = ensureBadgeLayer();
        layer.replaceChildren();
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return;

        slideEl.querySelectorAll('[data-slot], [data-slot-repeat]').forEach(el => {
            const isRepeat = el.hasAttribute('data-slot-repeat');
            const name = isRepeat ? el.getAttribute('data-slot-repeat') : el.getAttribute('data-slot');
            const rect = el.getBoundingClientRect();
            const badge = document.createElement('div');
            badge.className = 'slot-badge';
            badge.dataset.kind = isRepeat ? 'repeat' : 'slot';

            // Name first and in plain English; the type is the supporting
            // detail, so it is styled as one rather than dot-separated debug
            // output. The raw name stays in the tooltip – it is what the AI
            // sees and what slot errors name.
            const label = document.createElement('span');
            label.className = 'slot-badge-name';
            label.textContent = humaniseSlotName(name);
            const kind = document.createElement('span');
            kind.className = 'slot-badge-kind';
            kind.textContent = isRepeat
                ? `repeats ${el.getAttribute('data-slot-min') || 2}–${el.getAttribute('data-slot-max') || 4}`
                : (el.getAttribute('data-slot-type') || 'short_text').replace(/_/g, ' ');
            badge.append(label, kind);
            badge.title = `${name} · ${kind.textContent}`;

            badge.style.left = rect.left + 'px';
            badge.style.top  = rect.top + 'px';
            layer.appendChild(badge);
        });
    }

    /**
     * Takes the layer out of the document rather than just emptying it. An
     * empty fixed overlay is invisible but still real, and a stale one was
     * how "recap_points · bullets" ended up floating over the deck editor.
     */
    function clearSlotBadges() {
        badgeLayer?.remove();
        badgeLayer = null;
    }

    /**
     * Fills each repeat container with translucent copies of its row, so the
     * area a repeat covers – and how many items fit in it – is visible while
     * designing rather than only after generating a deck.
     *
     * The ghosts are real children, so the container's own flex layout places
     * them exactly where the real rows will land. Everything that serialises the
     * slide strips them (see getSlideHtml).
     */
    function refreshRepeatGhosts() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return;
        stripRepeatGhosts(slideEl);
        if (!state.templateMode) return;

        slideEl.querySelectorAll('[data-slot-repeat]').forEach(wrap => {
            const proto = wrap.firstElementChild;
            if (!proto) return;
            const max = Math.min(parseInt(wrap.getAttribute('data-slot-max'), 10) || 4, 12);
            for (let i = 1; i < max; i++) {
                const ghost = proto.cloneNode(true);
                ghost.setAttribute('data-repeat-ghost', '');
                ghost.setAttribute('aria-hidden', 'true');
                ghost.classList.add('slide__repeat-ghost');
                // Slot markers are stripped so a ghost never draws a badge, and
                // never counts as a slot if one somehow survived to the compiler.
                SLOT_ATTRS.forEach(a => ghost.removeAttribute(a));
                ghost.querySelectorAll('[data-slot], [data-slot-repeat]')
                    .forEach(el => SLOT_ATTRS.forEach(a => el.removeAttribute(a)));
                wrap.appendChild(ghost);
            }
            // Ghosts are clones, so they need the same scale the real row has.
            layoutRepeat(wrap);
        });
    }

    /**
     * Re-measures a repeat's row from what is actually inside it.
     *
     * The row's declared size is the reference layoutRepeat scales against, so
     * it has to follow the design: move or resize something in the row and the
     * row itself must grow or shrink, or the spacing between copies is computed
     * from a size that no longer exists.
     */
    function resizeRowToContents(wrap) {
        const row = [...(wrap?.children || [])].find(el =>
            el.classList.contains('slide__group') && !el.hasAttribute('data-repeat-ghost'));
        if (!row || !row.children.length) return;

        let width = 0;
        let height = 0;
        for (const child of row.children) {
            // Children are positioned against the row, and offsetWidth is the
            // layout size – unaffected by the scale transform on the row.
            const left = parseFloat(child.style.left) || 0;
            const top = parseFloat(child.style.top) || 0;
            width = Math.max(width, left + (parseFloat(child.style.width) || child.offsetWidth || 0));
            height = Math.max(height, top + (parseFloat(child.style.height) || child.offsetHeight || 0));
        }
        if (width > 0) row.style.width = Math.max(20, width) + 'px';
        if (height > 0) row.style.height = Math.max(20, height) + 'px';
    }

    /** Bring every repeat back in step with its row: size, scale, and ghosts. */
    function syncRepeats() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return;
        const wraps = slideEl.querySelectorAll('[data-slot-repeat]');
        if (!wraps.length) return;

        wraps.forEach(resizeRowToContents);
        // Re-clones the ghosts from the row as it is NOW, then rescales them.
        if (state.templateMode) refreshRepeatGhosts();
        else wraps.forEach(layoutRepeat);
        refreshSlotBadges();
        updateRepeatFitHint();

        // Discard the mutations this function just made, so observing them
        // cannot schedule another run of it.
        repeatObserver?.takeRecords();
    }

    const isGhostNode = (n) => n.nodeType === 1 && n.hasAttribute?.('data-repeat-ghost');

    let repeatSyncTimer = null;
    const repeatObserver = window.MutationObserver ? new MutationObserver((records) => {
        const touchesRow = records.some(record => {
            // Adding or removing ghosts is this feature's own bookkeeping.
            if (record.type === 'childList') {
                const nodes = [...record.addedNodes, ...record.removedNodes];
                if (nodes.length && nodes.every(isGhostNode)) return false;
            }
            const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
            if (!target || target.closest('[data-repeat-ghost]')) return false;
            return !!target.closest('[data-slot-repeat]');
        });
        if (!touchesRow) return;
        clearTimeout(repeatSyncTimer);
        repeatSyncTimer = setTimeout(syncRepeats, 120);
    }) : null;

    if (repeatObserver && stageSlideInner) {
        repeatObserver.observe(stageSlideInner, {
            subtree: true, childList: true, attributes: true, characterData: true,
        });
    }

    function setTemplateMode(on) {
        state.templateMode = !!on;
        stageSlideInner?.classList.toggle('ppt-template-mode', state.templateMode);
        pptTemplateMode?.setAttribute('aria-pressed', String(state.templateMode));
        pptTemplateMode?.classList.toggle('active', state.templateMode);
        refreshRepeatGhosts();
        refreshSlotBadges();
    }

    pptTemplateMode?.addEventListener('click', () => setTemplateMode(!state.templateMode));
    // Badges are positioned in viewport coordinates, so anything that moves the
    // slide on screen has to reposition them.
    window.addEventListener('resize', () => { if (state.templateMode) refreshSlotBadges(); });
    window.addEventListener('scroll', () => { if (state.templateMode) refreshSlotBadges(); }, true);

    // ── Slot form ─────────────────────────────────────────
    // Returns '' when there's nothing usable, so callers can fall back to a
    // guess based on the element rather than to a meaningless placeholder.
    function slugifySlotName(text) {
        return String(text || '').trim().toLowerCase()
            .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30)
            .replace(/^[^a-z]+/, '');
    }

    function syncSlotFormRows() {
        const type = slotFormKind === 'repeat' ? 'repeat' : slotFormType.value;
        $('slot-form-type-row').hidden = slotFormKind === 'repeat';
        $('slot-form-img-row').hidden  = type !== 'image';
        $('slot-form-count-row').hidden = type !== 'bullets' && type !== 'repeat';
        $('slot-form-area-hint').hidden = type !== 'repeat';
        if (type === 'repeat') updateRepeatFitHint();
        slotFormFit.parentElement.hidden = type === 'image';
    }

    function openSlotForm(el, kind) {
        if (!slotForm) return;
        slotFormTarget = el;
        slotFormKind = kind;
        slotFormError.hidden = true;
        slotFormOriginalCount = {
            min: el.getAttribute('data-slot-min'),
            max: el.getAttribute('data-slot-max'),
        };

        const existing = kind === 'repeat'
            ? el.getAttribute('data-slot-repeat')
            : el.getAttribute('data-slot');

        // Falling back to the tag name produced slots called "div", which tells
        // neither the teacher nor the AI anything. Repeats default to "items";
        // a slot borrows the same guess the "Suggest slots" button would make.
        slotFormName.value = existing
            || (kind === 'repeat' ? 'items' : slugifySlotName(el.textContent) || SlotGuess.forElement(el).name);
        slotFormHint.value = el.getAttribute('data-slot-hint') || '';
        slotFormOptional.checked = el.hasAttribute('data-slot-optional');
        slotFormFit.checked = el.getAttribute('data-slot-fit') === 'shrink';
        slotFormImg.value = el.getAttribute('data-slot-img') === 'photo' ? 'photo' : 'diagram';

        if (kind === 'repeat') {
            slotFormMin.value = el.getAttribute('data-slot-min') || 2;
            slotFormMax.value = el.getAttribute('data-slot-max') || 4;
        } else {
            // Guess the type from the element, so the common cases need no thought.
            const tag = el.tagName.toLowerCase();
            slotFormType.value = el.getAttribute('data-slot-type')
                || (tag === 'img' ? 'image' : (tag === 'ul' || tag === 'ol') ? 'bullets'
                    : tag === 'p' ? 'paragraph' : 'short_text');
            slotFormMin.value = el.getAttribute('data-slot-min') || 3;
            slotFormMax.value = el.getAttribute('data-slot-max') || 5;
        }

        syncSlotFormRows();

        const rect = el.getBoundingClientRect();
        slotForm.style.left = Math.min(rect.left, window.innerWidth - 280) + 'px';
        slotForm.style.top  = Math.min(rect.bottom + 6, window.innerHeight - 340) + 'px';
        show(slotForm);
        slotFormName.focus();
        slotFormName.select();
    }

    function closeSlotForm({ saved = false } = {}) {
        // The live count preview writes straight to the element, so cancelling
        // has to put the original values back.
        if (!saved && slotFormTarget && slotFormOriginalCount) {
            const { min, max } = slotFormOriginalCount;
            if (min === null) slotFormTarget.removeAttribute('data-slot-min');
            else slotFormTarget.setAttribute('data-slot-min', min);
            if (max === null) slotFormTarget.removeAttribute('data-slot-max');
            else slotFormTarget.setAttribute('data-slot-max', max);
            refreshRepeatGhosts();
            refreshSlotBadges();
        }
        slotFormOriginalCount = null;
        hide(slotForm);
        slotFormTarget = null;
    }

    slotFormType?.addEventListener('change', syncSlotFormRows);

    // Live preview: typing a new maximum immediately redraws the ghost rows, so
    // "how many fit in this area" is answered while choosing the number rather
    // than after saving and generating a deck.
    [slotFormMin, slotFormMax].forEach(input => input?.addEventListener('input', () => {
        if (slotFormKind !== 'repeat' || !slotFormTarget) return;
        slotFormTarget.setAttribute('data-slot-max', slotFormMax.value || '4');
        slotFormTarget.setAttribute('data-slot-min', slotFormMin.value || '2');
        refreshRepeatGhosts();
        refreshSlotBadges();
        updateRepeatFitHint();
    }));

    /**
     * Everything always fits now, because rows scale rather than overflow – so
     * this reports how much they had to shrink instead of refusing a count.
     * Only worth mentioning once the shrinking is enough to hurt legibility.
     */
    function updateRepeatFitHint() {
        const hint = $('slot-form-area-hint');
        if (!hint || slotFormKind !== 'repeat' || !slotFormTarget) return;

        const scale = layoutRepeat(slotFormTarget);
        const base = 'The blue box is the area these repeat across. Drag its handles to resize it –'
            + 'the faded copies show where each item will land.';

        if (scale < 0.55) {
            hint.textContent = `${base} These are scaled to ${Math.round(scale * 100)}% to fit, which will be hard to read. `
                + 'Widen the area, ask for fewer, or make the row itself smaller.';
            hint.classList.add('slot-form-hint-warn');
        } else if (scale < 1) {
            hint.textContent = `${base} Scaled to ${Math.round(scale * 100)}% to fit the area.`;
            hint.classList.remove('slot-form-hint-warn');
        } else {
            hint.textContent = base;
            hint.classList.remove('slot-form-hint-warn');
        }
    }

    $('slot-form-cancel')?.addEventListener('click', closeSlotForm);

    $('slot-form-save')?.addEventListener('click', () => {
        if (!slotFormTarget) return;
        const el = slotFormTarget;
        const name = slotFormName.value.trim();

        // Validated here as well as on the server, because a name the compiler
        // rejects is far cheaper to fix while the element is still selected.
        const fail = (msg) => { slotFormError.textContent = msg; slotFormError.hidden = false; };
        if (!SLOT_NAME_RE.test(name)) {
            return fail('Use lowercase letters, numbers and underscores, starting with a letter.');
        }
        if (SLOT_RESERVED.has(name)) return fail(`"${name}" is reserved. Pick another name.`);

        // Caught here rather than at save: the compiler's version of this error
        // arrives long after the teacher has stopped thinking about this element.
        if (slotFormKind === 'repeat' && el.children.length === 0) {
            return fail('This has nothing inside it to repeat. Select the elements that make up one row, group them, then mark the group.');
        }
        if (slotFormKind === 'slot' && slotFormType.value === 'image' && el.tagName !== 'IMG') {
            return fail('Image slots have to be marked on a picture. Mark the image itself.');
        }

        const slideEl = stageSlideInner.querySelector('.slide');
        const scope = slotFormKind === 'repeat'
            ? slideEl
            : (el.closest('[data-slot-repeat]')?.firstElementChild || slideEl);
        const clash = [...scope.querySelectorAll('[data-slot], [data-slot-repeat]')].some(other => {
            if (other === el) return false;
            const otherScope = other.closest('[data-slot-repeat]')?.firstElementChild || slideEl;
            if (otherScope !== scope) return false;
            return other.getAttribute('data-slot') === name || other.getAttribute('data-slot-repeat') === name;
        });
        if (clash) return fail(`Another slot here is already called "${name}".`);

        const snap = getSlideHtml();
        SLOT_ATTRS.forEach(a => el.removeAttribute(a));

        if (slotFormKind === 'repeat') {
            el.setAttribute('data-slot-repeat', name);
            el.setAttribute('data-slot-min', slotFormMin.value || '2');
            el.setAttribute('data-slot-max', slotFormMax.value || '4');
        } else {
            const type = slotFormType.value;
            el.setAttribute('data-slot', name);
            el.setAttribute('data-slot-type', type);
            if (type === 'image') el.setAttribute('data-slot-img', slotFormImg.value);
            if (type === 'bullets') {
                el.setAttribute('data-slot-min', slotFormMin.value || '3');
                el.setAttribute('data-slot-max', slotFormMax.value || '5');
            }
            if (slotFormOptional.checked) el.setAttribute('data-slot-optional', '');
            if (slotFormFit.checked && type !== 'image') el.setAttribute('data-slot-fit', 'shrink');
        }
        if (slotFormHint.value.trim()) el.setAttribute('data-slot-hint', slotFormHint.value.trim());

        commitHistory(snap);
        closeSlotForm({ saved: true });
        if (!state.templateMode) setTemplateMode(true);
        else { refreshRepeatGhosts(); refreshSlotBadges(); }
    });

    document.addEventListener('mousedown', (e) => {
        if (isShown(slotForm) && !slotForm.contains(e.target) && !slideCtxMenu?.contains(e.target)) {
            closeSlotForm();
        }
    });

    // ── Automatic slot marking ────────────────────────────
    // Naming each box and choosing its type by hand is the fiddliest part of
    // making a template, and it's mostly mechanical: a heading is a heading, a
    // list is a list. This proposes the obvious answer for every element so the
    // teacher edits a marked-up slide instead of starting from a blank one.

    /**
     * An element is fillable if it holds text (or is a picture) and no
     * descendant holds text of its own – marking a wrapper would let the AI
     * overwrite everything nested inside it.
     */
    function isFillableLeaf(el) {
        if (el.tagName === 'IMG') return true;
        if (el.tagName === 'UL' || el.tagName === 'OL') return el.querySelector('li') !== null;
        if (el.closest('ul, ol')) return false;              // handled by the list itself
        if (!el.textContent.trim()) return false;
        return ![...el.children].some(child => child.textContent.trim() || child.tagName === 'IMG');
    }

    function autoMarkSlots() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return 0;

        const snap = getSlideHtml();
        const used = new Set(
            [...slideEl.querySelectorAll('[data-slot], [data-slot-repeat]')]
                .map(el => el.getAttribute('data-slot') || el.getAttribute('data-slot-repeat'))
        );

        const uniqueName = (base) => {
            if (!used.has(base)) { used.add(base); return base; }
            for (let i = 2; i < 50; i++) {
                const candidate = `${base}_${i}`;
                if (!used.has(candidate)) { used.add(candidate); return candidate; }
            }
            return `${base}_${Date.now() % 1000}`;
        };

        let marked = 0;
        for (const el of slideEl.querySelectorAll('*')) {
            if (el.hasAttribute('data-slot') || el.hasAttribute('data-slot-repeat')) continue;
            if (el.closest('[data-slot]')) continue;         // already inside a slot
            if (!isFillableLeaf(el)) continue;

            const guess = SlotGuess.forElement(el);
            el.setAttribute('data-slot', uniqueName(guess.name));
            el.setAttribute('data-slot-type', guess.type);
            el.setAttribute('data-slot-hint', guess.hint);
            if (guess.type === 'bullets') {
                el.setAttribute('data-slot-min', '3');
                el.setAttribute('data-slot-max', '5');
            }
            if (guess.type === 'image') el.setAttribute('data-slot-img', 'diagram');
            marked++;
        }

        if (marked) commitHistory(snap);
        if (!state.templateMode) setTemplateMode(true);
        else { refreshRepeatGhosts(); refreshSlotBadges(); }
        return marked;
    }

    // ── Save as template ──────────────────────────────────

    /**
     * Strips everything the editor added for its own use, so the stored
     * skeleton is the design and nothing else.
     */
    function scrubForTemplate(root) {
        const all = [root, ...root.querySelectorAll('*')];
        for (const el of all) {
            el.removeAttribute('contenteditable');
            el.removeAttribute('data-eid');
            delete el.dataset.fitZoom;
            ['zoom', 'outline', 'outlineOffset', 'cursor', 'userSelect', 'isolation']
                .forEach(prop => { el.style[prop] = ''; });
            if (el.getAttribute('style') === '') el.removeAttribute('style');
        }
        // Fixed-position layouts must not be zoomed on render: zoom scales an
        // absolutely-positioned child's left/top too, so the design would move.
        stripEditorState(root);
        root.setAttribute('data-no-autofit', '');
        root.classList.add('slide--custom');
        return root;
    }

    /**
     * Replaces inline colours that came from the current theme's variables with
     * the variables themselves, so the template follows the deck's theme. The
     * common case is a teacher who never touched colours – those values came
     * from these variables in the first place, so they map back exactly.
     */
    const THEME_COLOUR_VARS = [
        '--s-text', '--s-text-muted', '--s-heading', '--s-accent', '--s-bg',
        '--s-surface', '--s-border', '--s-bullet', '--s-col-heading',
    ];

    function normaliseThemeColours(root, referenceEl) {
        const computed = getComputedStyle(referenceEl);
        const lookup = new Map();
        for (const name of THEME_COLOUR_VARS) {
            const probe = document.createElement('span');
            probe.style.color = computed.getPropertyValue(name).trim();
            if (!probe.style.color) continue;
            referenceEl.appendChild(probe);
            lookup.set(getComputedStyle(probe).color, `var(${name})`);
            probe.remove();
        }

        let unmapped = 0;
        for (const el of [root, ...root.querySelectorAll('*')]) {
            for (const prop of ['color', 'backgroundColor']) {
                const raw = el.style[prop];
                if (!raw) continue;
                const probe = document.createElement('span');
                probe.style.color = raw;
                referenceEl.appendChild(probe);
                const resolved = getComputedStyle(probe).color;
                probe.remove();
                const varName = lookup.get(resolved);
                if (varName) el.style[prop] = varName;
                else unmapped++;
            }
            if (el.tagName === 'FONT' && el.getAttribute('color')) unmapped++;
        }
        return unmapped;
    }

    /** Drops every colour the theme can't drive, letting slides.css take over. */
    function stripFixedColours(root) {
        for (const el of [root, ...root.querySelectorAll('*')]) {
            if (el.style.color && !el.style.color.startsWith('var(')) el.style.color = '';
            if (el.style.backgroundColor && !el.style.backgroundColor.startsWith('var(')) el.style.backgroundColor = '';
            el.style.fontFamily = '';
            if (el.tagName === 'FONT') el.removeAttribute('color');
            if (el.getAttribute('style') === '') el.removeAttribute('style');
        }
    }

    const templateSaveModal = $('template-save-modal');
    const templateSaveName  = $('template-save-name');
    const templateSaveDesc  = $('template-save-desc');
    const templateSaveSlots = $('template-save-slots');
    const templateSaveError = $('template-save-error');
    const templateSaveFollow = $('template-save-follow-theme');
    const templateSaveUseAi = $('template-save-use-ai');
    let pendingTemplateSkeleton = null;

    function describeSlots(slideEl) {
        const nodes = slideEl.querySelectorAll('[data-slot], [data-slot-repeat]');
        templateSaveSlots.replaceChildren();
        if (!nodes.length) {
            const li = document.createElement('li');
            li.className = 'tmpl-slot-none';
            li.textContent = 'None yet – right-click an element and choose "Mark as AI slot".';
            templateSaveSlots.appendChild(li);
            return 0;
        }
        nodes.forEach(el => {
            const isRepeat = el.hasAttribute('data-slot-repeat');
            const li = document.createElement('li');
            li.dataset.kind = isRepeat ? 'repeat' : 'slot';
            const name = document.createElement('strong');
            name.textContent = isRepeat ? el.getAttribute('data-slot-repeat') : el.getAttribute('data-slot');
            const type = document.createElement('span');
            type.className = 'tmpl-slot-type';
            type.textContent = ' · ' + (isRepeat ? 'repeating' : (el.getAttribute('data-slot-type') || 'short text').replace('_', ' '));
            li.append(name, type);
            templateSaveSlots.appendChild(li);
        });
        return nodes.length;
    }

    function openTemplateSaveModal({ onDone = null } = {}) {
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return;

        // Leaving the drag editor strips contenteditable and re-renders KaTeX,
        // so the clone we take is the finished design, not a half-edited one.
        const wasDragging = state.dragActive;
        deactivateDragEditor();

        const clone = slideEl.cloneNode(true);
        MathRender.revertMathInElement(clone);   // KaTeX spans back to $…$ source
        scrubForTemplate(clone);

        pendingTemplateSkeleton = { clone, slideEl, wasDragging, onDone };
        setError(templateSaveError, '');
        // A described template arrives already named and described, because the
        // sentence the teacher wrote said both. Prefilled, not forced – the
        // fields are ordinary and editable.
        templateSaveName.value = designerSuggested?.name || '';
        templateSaveDesc.value = designerSuggested?.guidance || '';
        const count = describeSlots(clone);
        $('template-save-submit').disabled = count === 0;
        $('template-suggest-desc').disabled = count === 0;
        suppressSlotBadges(true);
        show(templateSaveModal);
        templateSaveName.focus();
        templateSaveName.select();
    }

    function closeTemplateSaveModal() {
        hide(templateSaveModal);
        suppressSlotBadges(false);
        if (pendingTemplateSkeleton?.wasDragging) activateDragEditor();
        pendingTemplateSkeleton = null;
    }

    // Reads the layout the teacher just built – slot names, types and the
    // placeholder copy still on the slide – and writes the plan-prompt guidance
    // line for them. Always optional: it fills the box, never submits.
    $('template-suggest-desc')?.addEventListener('click', async () => {
        if (!pendingTemplateSkeleton) return;
        const btn = $('template-suggest-desc');
        btn.disabled = true;
        btn.textContent = 'Reading…';
        setError(templateSaveError, '');
        try {
            const res = await apiFetch('/api/slide-templates/suggest-guidance', {
                method: 'POST',
                body: JSON.stringify({
                    name: templateSaveName.value.trim(),
                    skeletonHtml: pendingTemplateSkeleton.clone.outerHTML,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(templateSaveError, (data.errors || [data.error]).join(' '));
                return;
            }
            templateSaveDesc.value = data.guidance;
            templateSaveDesc.focus();
        } catch (err) {
            if (err.message !== 'Not authenticated') {
                setError(templateSaveError, 'Could not reach the AI. Write a description yourself.');
            }
        } finally {
            btn.disabled = false;
            btn.textContent = 'Suggest';
        }
    });

    $('template-save-submit')?.addEventListener('click', async () => {
        if (!pendingTemplateSkeleton) return;
        const btn = $('template-save-submit');
        const name = templateSaveName.value.trim();
        if (!name) { setError(templateSaveError, 'Give the template a name.'); return; }

        const { clone, slideEl } = pendingTemplateSkeleton;
        const skeleton = clone.cloneNode(true);
        const unmapped = normaliseThemeColours(skeleton, slideEl);
        if (templateSaveFollow.checked) stripFixedColours(skeleton);

        btn.disabled = true;
        btn.textContent = 'Saving…';
        setError(templateSaveError, '');
        try {
            const res = await apiFetch('/api/slide-templates', {
                method: 'POST',
                body: JSON.stringify({
                    name,
                    description: templateSaveDesc.value.trim(),
                    skeletonHtml: skeleton.outerHTML,
                    baseTheme: stageSlideInner?.dataset.theme || null,
                    useInAi: templateSaveUseAi.checked,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                // Compile errors are listed in full and never auto-fixed: a
                // silently renamed slot yields a template the AI half-fills
                // forever with nothing to point at.
                setError(templateSaveError, (data.errors || [data.error]).join(' '));
                return;
            }
            await loadCustomTemplates();
            // An untouched selection picks this up from the defaults; a narrowed
            // one would silently exclude the template the teacher just made.
            if (genSelectionTouched && data.template?.templateId) {
                genTemplateSelection.add(data.template.templateId);
                updateGenTemplateHint();
            }
            const done = pendingTemplateSkeleton?.onDone;
            closeTemplateSaveModal();
            done?.();   // e.g. shut the designer once its template exists
            showToast(unmapped && !templateSaveFollow.checked
                ? `Saved "${name}" – it keeps its own fixed colours.`
                : `Saved "${name}".`);
        } catch (err) {
            setError(templateSaveError, err?.message || 'Could not save the template.');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Save template';
        }
    });

    // ── Template designer ─────────────────────────────────
    // Opens the real slide editor in a modal by MOVING #stage-canvas and the
    // direct-edit toolbar into it. Moving preserves every listener the drag
    // editor bound at start-up, so there is no second editor to keep in sync.
    // Autosave is not a hazard here: scheduleSave() already no-ops without an
    // active slide id, and the designer runs with none.

    const designerModal = $('template-designer-modal');
    let designerRestore = null;

    // Starting points, not a fixed skeleton: any built-in layout can be the
    // basis for a template, and a blank slide is there for a design that
    // resembles none of them. Nothing is marked as a slot automatically –
    // choosing what the AI writes is the teacher's decision, not a default.
    const DESIGNER_BLANK = '<div class="slide slide--custom"></div>';

    // ── The two steps ─────────────────────────────────────
    // Step 2 starts hidden, and the stage lives inside it. Nothing that
    // measures the stage can run until it is on screen: a hidden element is
    // 0px wide, so scaleStage would divide by zero and every slot badge would
    // stack up in the top-left corner.
    let designerStep = 'describe';

    function setDesignerStep(step) {
        designerStep = step;
        const describing = step === 'describe';
        const describeEl = $('designer-step-describe');
        const adjustEl = $('designer-step-adjust');
        if (describeEl) describeEl.hidden = !describing;
        if (adjustEl) adjustEl.hidden = describing;
        const title = $('designer-title');
        if (title) title.textContent = describing ? 'Describe your template' : 'Adjust your template';
        // Focus is the caller's business: this also runs while the modal is
        // being torn down, and focusing into a closing dialog steals the cursor.
    }

    /**
     * Puts a slide on the stage and moves to the adjust step, whether it came
     * from the AI or from a built-in layout. The measuring work is deferred a
     * frame because the stage has no width until the step is unhidden.
     *
     * @param {string} html
     * @param {{suggested?: object|null, recap?: string}} opts
     */
    function loadDesignerSlide(html, { suggested = null, recap = '' } = {}) {
        stageSlideInner.innerHTML = html;
        designerSuggested = suggested;
        undoStack.length = 0;
        redoStack.length = 0;
        syncUndoButtons();

        const recapEl = $('designer-recap-text');
        if (recapEl) recapEl.textContent = recap;

        setDesignerStep('adjust');
        requestAnimationFrame(() => {
            scaleStage();
            if (state.dragActive) deactivateDragEditor();
            activateDragEditor();
            // Outlines on: with a described template the slots are already
            // marked, and seeing which parts the AI claimed is the first thing
            // anyone wants to check.
            setTemplateMode(true);
            refreshSlotBadges();
            updateDesignerSummary();
        });
    }

    /** True when the stage holds work that redrawing would throw away. */
    function designerHasMarkedWork() {
        const slideEl = stageSlideInner?.querySelector('.slide');
        return (slideEl?.querySelectorAll('[data-slot], [data-slot-repeat]').length || 0) > 0;
    }

    /** Open, and therefore borrowing the slide editor for template work. */
    function isDesigningTemplate() {
        return !!designerRestore;
    }

    // Back to the description, with what you typed still in the box.
    $('designer-redraw')?.addEventListener('click', () => {
        if (designerHasMarkedWork()
            && !confirm('Redrawing will discard this slide. Continue?')) return;
        exitDesignerPreview();
        setDesignerStep('describe');
        setDescribeMsg('');
        describeInput?.select();
    });

    // ── Step 1: describe it ───────────────────────────────
    // The server compiles what the model returns and retries on its own
    // errors, so anything that arrives here is already a valid skeleton.
    const describeInput = $('designer-describe-input');
    const describeMsg   = $('designer-describe-msg');
    const describeGo    = $('designer-describe-go');

    // Named so the blank page has an answer. These are phrasings that work,
    // not just topics – the shape of the sentence is the thing being taught.
    const DESCRIBE_EXAMPLES = [
        'A title, then three key terms in a row, each with a diagram above it and a short definition underneath',
        'A worked example on the left and a "your turn" question on the right, with space for four steps',
        'A big central diagram with four labelled callouts around it',
        'A starter question at the top and a five-bullet recap underneath',
    ];

    /** What the AI proposed to call this template, offered in the save modal. */
    let designerSuggested = null;

    function renderDescribeExamples() {
        const host = $('designer-describe-examples');
        if (!host || host.children.length) return;
        for (const text of DESCRIBE_EXAMPLES) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'designer-describe-example';
            // Enough to recognise the shape; the full text goes in the box.
            chip.textContent = text.length > 46 ? `${text.slice(0, 46)}…` : text;
            chip.title = text;
            chip.addEventListener('click', () => {
                describeInput.value = text;
                describeInput.focus();
            });
            host.appendChild(chip);
        }
    }

    function setDescribeMsg(text, isError = false) {
        if (!describeMsg) return;
        describeMsg.textContent = text || '';
        describeMsg.hidden = !text;
        describeMsg.classList.toggle('is-error', !!isError);
    }

    const TEMPLATE_PRO_MESSAGE =
        'Designing slide templates is a Pro feature. On the free plan you can use templates other teachers have shared.';

    /** PRO on every "make one" button, so the wall is visible before it is hit. */
    function syncTemplateProBadges() {
        const pro = state.user?.plan === 'pro';
        document.querySelectorAll('[data-template-pro]').forEach(el => { el.hidden = pro; });
    }

    async function drawTemplateFromDescription() {
        // The wall comes before the typing, not after it. The server checks
        // again – this is about not wasting someone's time.
        if (state.user?.plan !== 'pro') {
            showQuotaModal({ error: 'pro_required', message: TEMPLATE_PRO_MESSAGE });
            return;
        }

        const description = describeInput?.value.trim();
        if (!description || description.length < 8) {
            setDescribeMsg('Describe the slide in a sentence or two.', true);
            describeInput?.focus();
            return;
        }

        describeGo.disabled = true;
        describeGo.textContent = 'Drawing…';
        setDescribeMsg('This takes a few seconds.');
        try {
            const res = await apiFetch('/api/slide-templates/design', {
                method: 'POST',
                body: JSON.stringify({ description }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // A stale client plan, or a subscription that lapsed mid-session.
                if (data.error === 'pro_required') {
                    showQuotaModal({ error: 'pro_required', message: data.message || TEMPLATE_PRO_MESSAGE });
                    return;
                }
                setDescribeMsg(data.error || 'Could not draw that. Try describing it more simply.', true);
                return;
            }

            loadDesignerSlide(data.skeleton, {
                suggested: { name: data.name || '', guidance: data.guidance || '' },
                recap: description,
            });
            const slots = data.slotCount === 1 ? '1 AI slot' : `${data.slotCount} AI slots`;
            showToast(`Drew "${data.name || 'your template'}" with ${slots} – drag anything to adjust it`);
        } catch (err) {
            if (err.message !== 'Not authenticated') {
                setDescribeMsg('Could not reach the AI. Try again, or start from a layout.', true);
            }
        } finally {
            describeGo.disabled = false;
            describeGo.textContent = 'Draw it';
        }
    }

    describeGo?.addEventListener('click', drawTemplateFromDescription);
    describeInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); drawTemplateFromDescription(); }
    });

    function openTemplateDesigner() {
        if (!designerModal || designerRestore) return;

        const stageCanvasEl = $('stage-canvas');
        if (!stageCanvasEl) return;

        // Every node the editor needs, not just the obvious two. The image
        // keyword row, its file input and the error line are SIBLINGS of the
        // toolbar, so moving only the toolbar left "Add image" toggling a row
        // that was still back in the hidden editor.
        const moved = [
            { el: stageCanvasEl, host: 'designer-stage-host' },
            { el: $('ppt-direct-bar'), host: 'designer-toolbar-host' },
            { el: $('ppt-img-row'), host: 'designer-toolbar-host' },
            { el: $('ppt-img-upload-input'), host: 'designer-toolbar-host' },
            { el: $('stage-error'), host: 'designer-stage-host' },
        ].filter(m => m.el);

        designerRestore = {
            moved: moved.map(m => ({ el: m.el, parent: m.el.parentElement, next: m.el.nextSibling })),
            slideHtml: stageSlideInner.innerHTML,
            activeSlideId: state.activeSlideId,
            templateMode: state.templateMode,
            pptMode: state.pptMode,
        };

        // No slide id while designing – this is what keeps autosave off the deck.
        state.activeSlideId = null;
        state.pptMode = 'direct';
        deactivateDragEditor();

        moved.forEach(m => $(m.host).appendChild(m.el));
        $('ppt-direct-bar')?.classList.add('is-designing');
        hide($('ppt-img-row'));

        setError($('designer-error'), '');
        suppressSlotBadges(false);
        // The layer builds itself when the first badge needs it, and is removed
        // again on close – nothing to set up here.

        // Opens on the description with an empty stage. Nothing is measured or
        // rendered yet: the slide arrives when there is something to draw, and
        // scaleStage runs then, from loadDesignerSlide.
        if (describeInput) describeInput.value = '';
        setDescribeMsg('');
        renderDescribeExamples();
        stageSlideInner.innerHTML = DESIGNER_BLANK;
        setDesignerStep('describe');
        show(designerModal);
        describeInput?.focus();
    }

    function closeTemplateDesigner() {
        if (!designerRestore) return;
        // Restore the design before unwinding, so the deck slide we put back is
        // the deck slide and not a preview rendered over it.
        exitDesignerPreview({ resumeEditing: false });
        const r = designerRestore;
        designerRestore = null;
        designerSuggested = null;
        setDesignerStep('describe');   // next open starts at the beginning

        deactivateDragEditor();
        setTemplateMode(r.templateMode);
        // Unconditional: setTemplateMode only clears them when it happens to be
        // turning the mode off, and the designer is the only thing badges ever
        // belonged to. Closing it is the end of them either way.
        clearSlotBadges();

        $('ppt-direct-bar')?.classList.remove('is-designing');
        hide($('ppt-img-row'));
        // Reversed, so each node goes back before the sibling it was recorded
        // against – restoring in forward order would put them back out of order.
        [...r.moved].reverse().forEach(m => m.parent?.insertBefore(m.el, m.next));

        stageSlideInner.innerHTML = r.slideHtml;
        state.activeSlideId = r.activeSlideId;
        state.pptMode = r.pptMode;
        undoStack.length = 0;
        redoStack.length = 0;
        syncUndoButtons();

        hide(designerModal);
        scaleStage();
        // Restoring a deck slide means the editor is live again.
        if (state.activeSlideId && isShown(pptEditor)) activateDragEditor();
    }

    function updateDesignerSummary() {
        const el = $('designer-slot-summary');
        if (!el) return;
        const slideEl = stageSlideInner?.querySelector('.slide');
        const count = slideEl ? slideEl.querySelectorAll('[data-slot], [data-slot-repeat]').length : 0;
        el.textContent = count
            ? `${count} part${count === 1 ? '' : 's'} the AI will write.`
            : 'Nothing marked yet – the AI needs at least one part to write.';
    }

    // Opt-in only. It proposes a slot per element as a starting point; every one
    // can then be renamed, retyped or removed from the right-click menu.
    $('designer-auto-mark')?.addEventListener('click', () => {
        const added = autoMarkSlots();
        updateDesignerSummary();
        showToast(added
            ? `Suggested ${added} slot${added === 1 ? '' : 's'} – right-click any of them to change or remove it.`
            : 'Nothing left to suggest.');
    });

    // ── Preview: the template as the AI will leave it ─────
    // The designer shows your placeholder text at whatever length you typed it,
    // which says nothing about how the layout copes once it is full. Preview
    // renders through the real compiler and renderer on the server, so what it
    // shows is what a generated deck produces – a client-side approximation
    // would drift from that, which is the one thing a preview must not do.

    let designerPreviewSkeleton = null;   // the design, parked while previewing

    function isDesignerPreviewing() {
        return designerPreviewSkeleton !== null;
    }

    /**
     * Puts the design back. Every exit from the designer calls this first:
     * saving or closing while a preview is on screen would store the FILLED
     * slide as the template, replacing the slots with sample content.
     */
    function exitDesignerPreview({ resumeEditing = true } = {}) {
        if (!isDesignerPreviewing()) return;
        stageSlideInner.innerHTML = designerPreviewSkeleton;
        designerPreviewSkeleton = null;
        const btn = $('designer-preview');
        if (btn) { btn.textContent = 'Preview filled'; btn.classList.remove('active'); }
        if (resumeEditing) {
            activateDragEditor();
            setTemplateMode(true);
            updateDesignerSummary();
        }
    }

    async function toggleDesignerPreview() {
        const btn = $('designer-preview');
        if (isDesignerPreviewing()) {
            exitDesignerPreview();
            return;
        }

        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl) return;
        if (!slideEl.querySelectorAll('[data-slot], [data-slot-repeat]').length) {
            setError($('designer-error'), 'Mark at least one part for the AI to write before previewing.');
            return;
        }

        const topic = $('designer-preview-topic')?.value.trim() || '';
        btn.disabled = true;
        btn.textContent = topic ? 'Asking the AI…' : 'Filling…';
        setError($('designer-error'), '');

        // Snapshot the design exactly as the save path would, so previewing and
        // saving can never disagree about what the template is.
        deactivateDragEditor();
        const clone = slideEl.cloneNode(true);
        MathRender.revertMathInElement(clone);
        scrubForTemplate(clone);
        stripRepeatGhosts(clone);

        try {
            const res = await apiFetch('/api/slide-templates/preview', {
                method: 'POST',
                body: JSON.stringify({ skeletonHtml: clone.outerHTML, topic }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError($('designer-error'), (data.errors || [data.error]).join(' '));
                activateDragEditor();
                return;
            }
            designerPreviewSkeleton = stageSlideInner.innerHTML;
            setTemplateMode(false);          // no outlines or ghosts over a preview
            stageSlideInner.innerHTML = data.html;
            MathRender.renderMathInElement(stageSlideInner);
            btn.textContent = 'Back to editing';
            btn.classList.add('active');
        } catch (err) {
            setError($('designer-error'), err?.message || 'Could not build a preview.');
            activateDragEditor();
        } finally {
            btn.disabled = false;
        }
    }

    $('designer-preview')?.addEventListener('click', toggleDesignerPreview);
    $('designer-preview-topic')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !isDesignerPreviewing()) { e.preventDefault(); toggleDesignerPreview(); }
    });

    $('designer-cancel')?.addEventListener('click', closeTemplateDesigner);

    $('designer-save')?.addEventListener('click', () => {
        exitDesignerPreview();   // never save the preview instead of the design
        const slideEl = stageSlideInner?.querySelector('.slide');
        if (!slideEl?.querySelectorAll('[data-slot], [data-slot-repeat]').length) {
            setError($('designer-error'), 'Mark at least one part for the AI to write – try "Mark slots for me".');
            return;
        }
        // Hand off to the same save modal the in-editor route uses, so there is
        // one place that names, describes and validates a template.
        openTemplateSaveModal({ onDone: closeTemplateDesigner });
    });

    // Keeps the count honest as slots are added or removed by hand.
    if (window.MutationObserver && stageSlideInner) {
        new MutationObserver(() => { if (designerRestore) updateDesignerSummary(); })
            .observe(stageSlideInner, { subtree: true, attributes: true, attributeFilter: ['data-slot', 'data-slot-repeat'] });
    }

    // ── Template library ──────────────────────────────────
    const templateManagerModal   = $('template-manager-modal');
    const templateManagerList    = $('template-manager-list');
    const templateManagerNote    = $('template-manager-note');
    const templateManagerEmpty   = $('template-manager-empty');
    const templateManagerError   = $('template-manager-error');
    const templateManagerCount   = $('template-manager-count');
    const templateManagerNoMatch = $('template-manager-nomatch');
    const templateSearchInput    = $('template-search');
    const templateSearchWrap     = $('template-search-wrap');

    // The plan prompt only carries this many, newest first – past that the
    // guidance block starts crowding out the built-in template descriptions.
    const AI_TEMPLATE_LIMIT = 8;

    async function loadCustomTemplates() {
        try {
            const res = await apiFetch('/api/slide-templates');
            if (!res.ok) return;
            const data = await res.json();
            state.customTemplates = data.templates || [];
            SlideRenderer.setCustomTemplates(state.customTemplates);
            renderAddSlideTemplateChips();
            renderGenTemplatePicker();
        } catch { /* library is optional – never block the editor on it */ }
    }

    // ── Which layouts the AI may use for a new deck ───────
    // Every built-in plus the teacher's own, shown as real rendered slides.
    // Everything starts selected, so the picker narrows a deck rather than
    // being a gate you have to pass before you can generate one.

    const genTemplateSelection = new Set();
    let genSelectionTouched = false;

    function defaultGenSelection() {
        const ids = TemplateSamples.names();
        for (const t of state.customTemplates || []) {
            if (t.useInAi) ids.push(t.templateId);
        }
        return ids;
    }

    function renderGenTemplatePicker() {
        // Rebuilt whenever the library changes; an untouched selection tracks
        // the defaults so a newly saved template is included straight away.
        if (!genSelectionTouched) {
            genTemplateSelection.clear();
            defaultGenSelection().forEach(id => genTemplateSelection.add(id));
        }
        updateGenTemplateHint();
    }

    function buildGenTile({ id, name, blurb, html, theme, template = null }) {
        // The tile sits in a wrapper rather than being the grid item itself:
        // it is a <button>, and a delete control nested inside another button
        // is invalid markup that browsers resolve unpredictably.
        const cell = document.createElement('div');
        cell.className = 'gen-template-cell';

        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'gen-template-tile';
        tile.dataset.templateId = id;
        tile.classList.toggle('active', genTemplateSelection.has(id));

        const preview = document.createElement('div');
        preview.className = 'gen-template-preview';
        preview.dataset.theme = theme;
        preview.innerHTML = html;

        const body = document.createElement('div');
        body.className = 'gen-template-tile-body';

        const nameEl = document.createElement('div');
        nameEl.className = 'gen-template-tile-name';
        nameEl.append(name);
        const check = document.createElement('span');
        check.className = 'gen-template-check';
        nameEl.appendChild(check);

        const blurbEl = document.createElement('div');
        blurbEl.className = 'gen-template-tile-blurb';
        blurbEl.textContent = blurb;

        body.append(nameEl, blurbEl);
        tile.append(preview, body);

        tile.addEventListener('click', () => {
            genSelectionTouched = true;
            if (genTemplateSelection.has(id)) genTemplateSelection.delete(id);
            else genTemplateSelection.add(id);
            tile.classList.toggle('active', genTemplateSelection.has(id));
            updateGenTemplateHint();
        });
        cell.appendChild(tile);

        // Only your own templates can be deleted – the built-in layouts are not
        // yours to remove, so they get no control at all rather than a dead one.
        if (template) {
            const del = document.createElement('button');
            del.type = 'button';
            del.className = 'gen-template-del';
            del.title = `Delete "${template.name}"`;
            del.setAttribute('aria-label', `Delete ${template.name}`);
            del.innerHTML = '&times;';
            del.addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!confirm(`Delete the template "${template.name}"? Slides already made with it are not affected.`)) return;
                del.disabled = true;
                try {
                    const res = await apiFetch(`/api/slide-templates/${template.id}`, { method: 'DELETE' });
                    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Delete failed');
                    genTemplateSelection.delete(id);
                    await loadCustomTemplates();
                    renderGenTemplateModal();
                    showToast(`Deleted "${template.name}"`);
                } catch (err) {
                    del.disabled = false;
                    showToast(err.message || 'Could not delete that template');
                }
            });
            cell.appendChild(del);
        }
        return cell;
    }

    function renderGenTemplateModal() {
        const theme = state.selectedTheme || 'classic';
        const builtinGrid = $('gen-template-builtin');
        const mineGrid = $('gen-template-mine');
        const mineWrap = $('gen-template-mine-wrap');
        if (!builtinGrid || !mineGrid) return;

        builtinGrid.replaceChildren();
        for (const name of TemplateSamples.names()) {
            builtinGrid.appendChild(buildGenTile({
                id: name,
                name: TemplateSamples.labelFor(name),
                blurb: TemplateSamples.blurbFor(name),
                // The real renderer with sample fields – a preview can't drift
                // from the template because it IS the template.
                html: SlideRenderer.buildHtml(name, TemplateSamples.fieldsFor(name)),
                theme,
            }));
        }

        const mine = state.customTemplates || [];
        mineWrap.hidden = mine.length === 0;
        mineGrid.replaceChildren();
        for (const tmpl of mine) {
            mineGrid.appendChild(buildGenTile({
                id: tmpl.templateId,
                name: tmpl.name,
                blurb: tmpl.description || 'No guidance written for the AI yet.',
                html: tmpl.thumbnailHtml || tmpl.skeletonHtml || '',
                theme: tmpl.baseTheme || theme,
                template: tmpl,
            }));
        }

        fitGenTemplatePreviews();
        updateGenTemplateHint();
    }

    /** Same measurement problem as the template manager: 1fr columns, fixed
     *  960px slides, and no way for CSS to divide one by the other. */
    function fitGenTemplatePreviews() {
        document.querySelectorAll('.gen-template-preview').forEach(preview => {
            const width = preview.clientWidth;
            if (width) preview.style.setProperty('--gen-preview-scale', String(width / 960));
        });
    }

    function updateGenTemplateHint() {
        const chosen = selectedGenTemplateIds() || [];
        const customChosen = (state.customTemplates || []).filter(t => genTemplateSelection.has(t.templateId));
        const builtinChosen = chosen.length - customChosen.length;

        const summary = !genSelectionTouched
            ? 'All layouts – the AI picks whatever suits each slide.'
            : !chosen.length
                ? 'None selected – every layout will be available.'
                : `${builtinChosen} built-in${customChosen.length ? ` and ${customChosen.length} of yours` : ''} selected.`;

        const hint = $('gen-template-hint');
        if (hint) hint.textContent = summary;

        const count = $('gen-template-count');
        if (count) {
            // Mirrors MAX_AI_TEMPLATES in routes/presentations.js: past this the
            // guidance block crowds out the built-in template descriptions.
            count.textContent = customChosen.length > AI_TEMPLATE_LIMIT
                ? `${summary} Only your ${AI_TEMPLATE_LIMIT} most recently updated templates will be offered.`
                : summary;
        }
    }

    /**
     * null means "no opinion – every built-in, plus whatever the library offers
     * the AI". A list restricts the deck to exactly those layouts.
     */
    function selectedGenTemplateIds() {
        if (!genSelectionTouched) return null;
        return [...genTemplateSelection];
    }

    $('gen-template-open')?.addEventListener('click', () => {
        syncTemplateProBadges();
        renderGenTemplateModal();
        show($('gen-template-modal'));
        // Tiles are measured after the modal has size; before that they're 0.
        requestAnimationFrame(fitGenTemplatePreviews);
    });

    $('gen-template-new')?.addEventListener('click', () => {
        if (state.user?.plan !== 'pro') {
            showQuotaModal({ error: 'pro_required', message: TEMPLATE_PRO_MESSAGE });
            return;
        }
        hide($('gen-template-modal'));
        openTemplateDesigner();
    });

    // Renaming, sharing and deleting all live in the library. Reachable from
    // here because the picker is where you notice a template needs managing.
    $('gen-template-manage')?.addEventListener('click', async () => {
        hide($('gen-template-modal'));
        await loadCustomTemplates();
        openTemplateManager();
    });

    $('gen-template-all')?.addEventListener('click', () => {
        genSelectionTouched = true;
        // Everything, including templates the library isn't currently offering
        // the AI – "select all" in a per-deck picker means this deck, not a
        // change to the library's own flags.
        TemplateSamples.names().forEach(id => genTemplateSelection.add(id));
        (state.customTemplates || []).forEach(t => genTemplateSelection.add(t.templateId));
        renderGenTemplateModal();
    });

    $('gen-template-none')?.addEventListener('click', () => {
        genSelectionTouched = true;
        genTemplateSelection.clear();
        renderGenTemplateModal();
    });

    $('gen-template-done')?.addEventListener('click', () => hide($('gen-template-modal')));

    window.addEventListener('resize', fitGenTemplatePreviews);

    // What the search box is currently narrowing the library to.
    let templateFilter = '';

    function matchesTemplateFilter(tmpl) {
        if (!templateFilter) return true;
        const hay = `${tmpl.name} ${tmpl.description || ''}`.toLowerCase();
        return hay.includes(templateFilter);
    }

    function renderTemplateManager() {
        closeTemplateMenu();
        templateManagerList.replaceChildren();

        const all = state.customTemplates || [];
        const list = all.filter(matchesTemplateFilter);

        // Search only earns its space once there is enough to search through.
        if (templateSearchWrap) templateSearchWrap.hidden = all.length < 5 && !templateFilter;
        if (templateManagerCount) {
            templateManagerCount.textContent = String(all.length);
            templateManagerCount.hidden = all.length === 0;
        }
        templateManagerEmpty.hidden = all.length > 0;
        if (templateManagerNoMatch) templateManagerNoMatch.hidden = all.length === 0 || list.length > 0;

        const aiCount = all.filter(t => t.useInAi).length;
        if (aiCount > AI_TEMPLATE_LIMIT) {
            templateManagerNote.textContent =
                `The AI uses your ${AI_TEMPLATE_LIMIT} most recently updated templates when planning a deck (${aiCount} are switched on).`;
            templateManagerNote.hidden = false;
        } else {
            templateManagerNote.hidden = true;
        }

        for (const tmpl of list) templateManagerList.appendChild(buildTemplateCard(tmpl));
        fitTemplatePreviews();
    }

    /**
     * One template as a card: a preview, what it is, and exactly two controls –
     * the AI switch and its share code. Rename, guidance, sharing and delete are
     * one-off commands and live behind the card's ⋯ menu, which is what keeps a
     * 240px column from turning into a row of six half-width buttons.
     */
    function buildTemplateCard(tmpl) {
        const card = document.createElement('article');
        card.className = 'tmpl-card';

        const preview = document.createElement('div');
        preview.className = 'tmpl-card-preview';
        preview.dataset.theme = tmpl.baseTheme || stageSlideInner?.dataset.theme || 'classic';
        preview.innerHTML = tmpl.thumbnailHtml || tmpl.skeletonHtml || '';
        // Scale is applied by fitTemplatePreviews() once the grid has laid
        // out – the columns are 1fr, so their width isn't known until then.

        const body = document.createElement('div');
        body.className = 'tmpl-card-body';

        const head = document.createElement('div');
        head.className = 'tmpl-card-head';

        const nameEl = document.createElement('h3');
        nameEl.className = 'tmpl-card-name';
        nameEl.textContent = tmpl.name;

        const menuBtn = document.createElement('button');
        menuBtn.type = 'button';
        menuBtn.className = 'tmpl-card-menu-btn';
        menuBtn.textContent = '⋯';
        menuBtn.title = 'More actions';
        menuBtn.setAttribute('aria-label', `More actions for ${tmpl.name}`);
        menuBtn.setAttribute('aria-haspopup', 'menu');
        menuBtn.setAttribute('aria-expanded', 'false');
        menuBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (openTemplateCardMenu?.btn === menuBtn) closeTemplateMenu();
            else openTemplateMenu(menuBtn, card, tmpl);
        });

        head.append(nameEl, menuBtn);

        const descEl = document.createElement('p');
        descEl.className = 'tmpl-card-desc';
        if (tmpl.description) {
            descEl.textContent = tmpl.description;
        } else {
            descEl.textContent = 'No guidance for the AI yet.';
            descEl.classList.add('is-empty');
        }

        const tags = document.createElement('div');
        tags.className = 'tmpl-card-tags';
        if (tmpl.baseTheme) tags.appendChild(makeTag(`${tmpl.baseTheme} theme`, 'tmpl-tag-theme',
            'Designed with this theme’s colours baked in.'));
        if (tmpl.importedFrom) tags.appendChild(makeTag('Imported', '', 'Added from a colleague’s share code.'));
        if (tmpl.shareCode && tmpl.shareStatus && tmpl.shareStatus !== 'published') {
            tags.appendChild(makeTag('Being checked', 'tmpl-tag-pending',
                'Others can import it once it has been checked.'));
        }
        if (tmpl.listingId) {
            const listed = tmpl.listingStatus === 'published';
            tags.appendChild(makeTag(
                listed
                    ? (tmpl.listingClones ? `On the Marketplace · ${tmpl.listingClones} taken` : 'On the Marketplace')
                    : 'Marketplace: being checked',
                listed ? 'tmpl-tag-listed' : 'tmpl-tag-pending',
                listed
                    ? 'Any teacher can find this and take a copy.'
                    : 'It goes live on the Marketplace once it has been checked.'
            ));
        }

        body.append(head, descEl);
        if (tags.children.length) body.appendChild(tags);

        card.append(preview, body);
        // A live code gets a row to itself: it must be readable in full to be
        // worth anything, and it does not fit beside the switch at this width.
        if (tmpl.shareCode) card.appendChild(buildShareRow(tmpl));
        card.appendChild(buildTemplateCardFoot(tmpl));
        return card;
    }

    function makeTag(text, extraClass, title) {
        const el = document.createElement('span');
        el.className = extraClass ? `tmpl-tag ${extraClass}` : 'tmpl-tag';
        el.textContent = text;
        if (title) el.title = title;
        return el;
    }

    /** The card's footer: the AI switch, and the share code or a way to make one. */
    function buildTemplateCardFoot(tmpl) {
        const foot = document.createElement('div');
        foot.className = 'tmpl-card-foot';

        const aiWrap = document.createElement('div');
        aiWrap.className = 'tmpl-ai-toggle';

        const sw = document.createElement('label');
        sw.className = 'toggle-switch t-toggle';
        sw.dataset.on = String(!!tmpl.useInAi);
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = !!tmpl.useInAi;
        input.id = `tmpl-ai-${tmpl.id}`;
        const track = document.createElement('span');
        track.className = 'toggle-track';
        const thumb = document.createElement('span');
        thumb.className = 'toggle-thumb t-toggle-thumb';
        sw.append(input, track, thumb);

        const aiLabel = document.createElement('label');
        aiLabel.className = 'tmpl-ai-label';
        aiLabel.htmlFor = input.id;
        aiLabel.textContent = 'AI can use this';
        aiWrap.append(sw, aiLabel);

        input.addEventListener('change', async () => {
            const wanted = input.checked;
            // .is-init only after the first flip, so the overshoot keyframes
            // never play as the library draws.
            sw.classList.add('is-init');
            sw.dataset.on = String(wanted);
            input.disabled = true;
            // No redraw on success: the switch is the only thing that changed,
            // and a redraw here would cancel its animation and drop focus.
            const ok = await patchTemplate(tmpl.id, { useInAi: wanted }, { redraw: false });
            input.disabled = false;
            if (!ok) {
                input.checked = !wanted;
                sw.dataset.on = String(!wanted);
            } else {
                renderAiCountNote();
            }
        });

        foot.appendChild(aiWrap);
        // Shared templates show their code on its own row above, so the footer
        // only ever carries the one button.
        if (!tmpl.shareCode) foot.appendChild(buildShareBtn(tmpl));
        return foot;
    }

    /** The share code, click-to-copy, with how often it has been taken. */
    function buildShareRow(tmpl) {
        const row = document.createElement('div');
        row.className = 'tmpl-card-share';
        row.appendChild(buildShareCodeBtn(tmpl));
        if (tmpl.importCount) {
            const taken = document.createElement('span');
            taken.className = 'tmpl-card-taken';
            taken.textContent = `Taken ${tmpl.importCount}×`;
            taken.title = `Imported by others ${tmpl.importCount} time${tmpl.importCount === 1 ? '' : 's'}.`;
            row.appendChild(taken);
        }
        return row;
    }

    function buildShareCodeBtn(tmpl) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tmpl-share-code-btn';
        btn.title = `Copy ${tmpl.shareCode} – anyone with this code can add a copy of this template`;
        btn.setAttribute('aria-label', `Copy share code ${tmpl.shareCode}`);

        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('fill', 'none');
        icon.setAttribute('stroke', 'currentColor');
        icon.setAttribute('stroke-width', '2');
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<rect x="9" y="9" width="12" height="12" rx="2"></rect>'
            + '<path d="M5 15V5a2 2 0 0 1 2-2h10"></path>';

        const text = document.createElement('span');
        text.className = 'tmpl-share-code-text';
        text.textContent = tmpl.shareCode;
        btn.append(icon, text);

        btn.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(tmpl.shareCode);
                showToast('Share code copied');
            } catch {
                setError(templateManagerError, 'Could not copy – select the code and copy it by hand.');
            }
        });
        return btn;
    }

    function buildShareBtn(tmpl) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tmpl-btn';
        btn.textContent = 'Share';
        btn.title = 'Get a code you can give to a colleague';
        btn.addEventListener('click', async () => {
            btn.disabled = true;
            btn.textContent = 'Checking…';
            await shareRequest(`/api/slide-templates/${tmpl.id}/share`, 'POST');
        });
        return btn;
    }

    /** Only the "too many templates for the AI" line, without a full redraw. */
    function renderAiCountNote() {
        const aiCount = (state.customTemplates || []).filter(t => t.useInAi).length;
        if (aiCount > AI_TEMPLATE_LIMIT) {
            templateManagerNote.textContent =
                `The AI uses your ${AI_TEMPLATE_LIMIT} most recently updated templates when planning a deck (${aiCount} are switched on).`;
            templateManagerNote.hidden = false;
        } else {
            templateManagerNote.hidden = true;
        }
    }

    // ── The card's ⋯ menu ─────────────────────────────────
    // Rendered into <body> and fixed-positioned: the grid scrolls, so a menu
    // laid out inside a card would be clipped by its own container.
    let openTemplateCardMenu = null;

    function closeTemplateMenu() {
        if (!openTemplateCardMenu) return;
        openTemplateCardMenu.el.remove();
        openTemplateCardMenu.btn.setAttribute('aria-expanded', 'false');
        openTemplateCardMenu.card.classList.remove('is-menu-open');
        openTemplateCardMenu = null;
    }

    function openTemplateMenu(btn, card, tmpl) {
        closeTemplateMenu();

        const el = document.createElement('div');
        el.className = 'tmpl-menu';
        el.setAttribute('role', 'menu');

        const add = (label, onPick, danger = false) => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = danger ? 'tmpl-menu-item is-danger' : 'tmpl-menu-item';
            item.setAttribute('role', 'menuitem');
            item.textContent = label;
            item.addEventListener('click', () => { closeTemplateMenu(); onPick(); });
            el.appendChild(item);
            return item;
        };
        const sep = () => {
            const s = document.createElement('div');
            s.className = 'tmpl-menu-sep';
            el.appendChild(s);
        };

        add('Rename', () => startInlineEdit(card, tmpl, 'name'));
        add(tmpl.description ? 'Edit AI guidance' : 'Add AI guidance', () => startInlineEdit(card, tmpl, 'description'));
        sep();
        // The Marketplace reaches everyone; a share code reaches one colleague.
        // Both hand out a frozen copy, so both live in the same part of the menu.
        if (tmpl.listingId) {
            add('View on the Marketplace', () => {
                window.open(`/marketplace/templates/${tmpl.listingId}`, '_blank', 'noopener');
            });
            add('Take off the Marketplace', async () => {
                if (!confirm(`Take "${tmpl.name}" off the Marketplace? Copies teachers already took are unaffected.`)) return;
                await listingRequest(`/api/slide-templates/${tmpl.id}/publish`, 'DELETE');
            });
        } else {
            add('Publish to the Marketplace', async () => {
                if (!confirm(`Publish "${tmpl.name}" to the Marketplace? Any teacher will be able to take a copy.`)) return;
                await listingRequest(`/api/slide-templates/${tmpl.id}/publish`, 'POST');
            });
        }
        sep();
        if (tmpl.shareCode) {
            add('Copy share code', async () => {
                try {
                    await navigator.clipboard.writeText(tmpl.shareCode);
                    showToast('Share code copied');
                } catch { setError(templateManagerError, 'Could not copy – select the code and copy it by hand.'); }
            });
            add('Stop sharing', async () => {
                if (!confirm('Stop sharing this template? The code will stop working. Copies people already took are unaffected.')) return;
                await shareRequest(`/api/slide-templates/${tmpl.id}/share`, 'DELETE');
            });
        } else {
            add('Share with a code', () => shareRequest(`/api/slide-templates/${tmpl.id}/share`, 'POST'));
        }
        sep();
        add('Delete template', async () => {
            if (!confirm(`Delete the template "${tmpl.name}"? Slides already made with it are not affected.`)) return;
            try {
                const res = await apiFetch(`/api/slide-templates/${tmpl.id}`, { method: 'DELETE' });
                if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
                await loadCustomTemplates();
                renderTemplateManager();
                showToast(`Deleted "${tmpl.name}"`);
            } catch (err) { setError(templateManagerError, err.message); }
        }, true);

        document.body.appendChild(el);

        // Placed under the button, flipped up or pulled inward whenever that
        // would run it off the viewport.
        const r = btn.getBoundingClientRect();
        const m = el.getBoundingClientRect();
        const gap = 6;
        let left = Math.min(r.right - m.width, window.innerWidth - m.width - 8);
        left = Math.max(8, left);
        let top = r.bottom + gap;
        if (top + m.height > window.innerHeight - 8) top = Math.max(8, r.top - m.height - gap);
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;

        btn.setAttribute('aria-expanded', 'true');
        card.classList.add('is-menu-open');
        openTemplateCardMenu = { el, btn, card };
        el.querySelector('.tmpl-menu-item')?.focus();
    }

    document.addEventListener('click', (e) => {
        if (openTemplateCardMenu && !e.target.closest('.tmpl-menu')) closeTemplateMenu();
    });
    // Escape peels one layer at a time: the open menu first, then the library.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (openTemplateCardMenu) {
            const btn = openTemplateCardMenu.btn;
            closeTemplateMenu();
            btn.focus();
        } else if (isShown(templateManagerModal)) {
            hide(templateManagerModal);
            suppressSlotBadges(false);
        }
    });
    // The menu is fixed to the viewport, so anything that moves the card under
    // it would leave it stranded.
    templateManagerList?.addEventListener('scroll', closeTemplateMenu);
    window.addEventListener('resize', closeTemplateMenu);

    // ── Editing a name or its guidance, in place ──────────
    /**
     * Swaps the card's name or description for an editor. Enter saves, Escape
     * puts it back, and blurring saves too – a browser prompt() over the card
     * you are looking at was the old way and it hid the thing being renamed.
     */
    function startInlineEdit(card, tmpl, field) {
        const target = card.querySelector(field === 'name' ? '.tmpl-card-name' : '.tmpl-card-desc');
        if (!target || target.dataset.editing) return;

        const isName = field === 'name';
        const editor = document.createElement(isName ? 'input' : 'textarea');
        editor.className = 'tmpl-inline-edit';
        editor.value = (isName ? tmpl.name : tmpl.description) || '';
        editor.maxLength = isName ? 120 : 400;
        if (isName) editor.type = 'text';
        else { editor.rows = 3; editor.placeholder = 'e.g. a row of 3 key terms with a diagram each'; }
        editor.setAttribute('aria-label', isName ? 'Template name' : 'When should the AI use this slide?');

        const hint = document.createElement('div');
        hint.className = 'tmpl-inline-hint';
        hint.textContent = isName ? 'Enter to save, Esc to cancel' : 'Esc to cancel';

        target.dataset.editing = '1';
        target.replaceWith(editor);
        editor.after(hint);
        editor.focus();
        editor.select();

        let settled = false;
        const restore = () => {
            if (settled) return;
            settled = true;
            hint.remove();
            editor.replaceWith(target);
            delete target.dataset.editing;
        };

        const save = async () => {
            if (settled) return;
            const next = editor.value.trim();
            const current = (isName ? tmpl.name : tmpl.description) || '';
            if (isName && !next) { restore(); return; }      // a nameless template is not a save
            if (next === current) { restore(); return; }
            settled = true;
            hint.textContent = 'Saving…';
            editor.disabled = true;
            const ok = await patchTemplate(tmpl.id, isName ? { name: next } : { description: next });
            if (!ok) { settled = false; editor.disabled = false; hint.textContent = 'Could not save. Esc to cancel'; }
        };

        editor.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); restore(); }
            else if (e.key === 'Enter' && (isName || e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
        });
        editor.addEventListener('blur', save);
    }

    /**
     * Sizes each preview's slide to its tile. A slide is always a 960x540 box,
     * so the factor is just tileWidth / 960 – but the tiles are 1fr grid
     * columns, so that width only exists after layout, and CSS has no way to
     * divide a resolved width by 960 itself.
     */
    function fitTemplatePreviews() {
        templateManagerList?.querySelectorAll('.tmpl-card-preview').forEach(preview => {
            const width = preview.clientWidth;
            if (!width) return;
            preview.style.setProperty('--tmpl-preview-scale', String(width / 960));
        });
    }

    // The grid reflows with the modal, so the scale has to follow it.
    if (window.ResizeObserver && templateManagerList) {
        new ResizeObserver(fitTemplatePreviews).observe(templateManagerList);
    }

    /** Publish / unpublish one template's Marketplace listing, then redraw. */
    async function listingRequest(path, method) {
        setError(templateManagerError, '');
        try {
            const res = await apiFetch(path, { method });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'That did not work.');
            await loadCustomTemplates();
            renderTemplateManager();
            if (method === 'POST') {
                showToast(data.status === 'published'
                    ? 'Published to the Marketplace'
                    : 'Published – it needs checking before others can take it.');
            } else {
                showToast('Taken off the Marketplace');
            }
        } catch (err) {
            setError(templateManagerError, err.message);
            renderTemplateManager();
        }
    }

    /** Share / revoke share one template, then redraw the library. */
    async function shareRequest(path, method) {
        setError(templateManagerError, '');
        try {
            const res = await apiFetch(path, { method });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'That did not work.');
            await loadCustomTemplates();
            renderTemplateManager();
            if (method === 'POST' && data.code) {
                showToast(data.status === 'published'
                    ? `Share code ${data.code}`
                    : 'Shared – it needs checking before others can import it.');
            }
        } catch (err) {
            setError(templateManagerError, err.message);
            renderTemplateManager();   // put the button back
        }
    }

    $('template-import-btn')?.addEventListener('click', async () => {
        const input = $('template-import-code');
        const msg = $('template-import-msg');
        const code = input?.value.trim();
        if (!code) return;

        const btn = $('template-import-btn');
        btn.disabled = true;
        btn.textContent = 'Importing…';
        setError(templateManagerError, '');
        msg.hidden = true;
        try {
            const res = await apiFetch(`/api/slide-templates/import/${encodeURIComponent(code)}`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'That code did not work.');
            input.value = '';
            msg.textContent = `Imported "${data.template.name}" – it is now yours to edit.`;
            msg.hidden = false;
            await loadCustomTemplates();
            renderTemplateManager();
        } catch (err) {
            setError(templateManagerError, err.message);
        } finally {
            btn.disabled = false;
            btn.textContent = 'Import';
        }
    });

    $('template-import-code')?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); $('template-import-btn')?.click(); }
    });

    /**
     * @param {{redraw?: boolean}} opts  redraw:false leaves the card alone – for
     *        the AI switch, whose own animation a redraw would cancel.
     * @returns {Promise<boolean>} whether the change stuck
     */
    async function patchTemplate(id, patch, { redraw = true } = {}) {
        setError(templateManagerError, '');
        try {
            const res = await apiFetch(`/api/slide-templates/${id}`, {
                method: 'PATCH',
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Update failed');
            await loadCustomTemplates();
            if (redraw) renderTemplateManager();
            return true;
        } catch (err) {
            setError(templateManagerError, err.message);
            return false;
        }
    }

    function openTemplateManager() {
        setError(templateManagerError, '');
        templateFilter = '';
        if (templateSearchInput) templateSearchInput.value = '';
        hideImportPanel();
        syncTemplateProBadges();
        renderTemplateManager();
        suppressSlotBadges(true);
        show(templateManagerModal);
    }

    // The library is reached from the layout picker ("Slide layouts → Choose" on the
    // deck form), not from the slide editor.

    templateSearchInput?.addEventListener('input', () => {
        templateFilter = templateSearchInput.value.trim().toLowerCase();
        renderTemplateManager();
    });

    // Escape in the search box clears the search rather than shutting the whole
    // library – losing your place is not what "cancel this search" should mean.
    templateSearchInput?.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !templateSearchInput.value) return;
        e.stopPropagation();
        templateSearchInput.value = '';
        templateFilter = '';
        renderTemplateManager();
    });

    // The import field is the rarer of the two ways into the library, so it
    // stays folded away until asked for rather than heading the modal.
    const templateImportPanel  = $('template-import-panel');
    const templateImportToggle = $('template-import-toggle');

    function hideImportPanel() {
        if (!templateImportPanel) return;
        templateImportPanel.hidden = true;
        templateImportToggle?.setAttribute('aria-expanded', 'false');
        const msg = $('template-import-msg');
        if (msg) msg.hidden = true;
    }

    templateImportToggle?.addEventListener('click', () => {
        const opening = templateImportPanel.hidden;
        templateImportPanel.hidden = !opening;
        templateImportToggle.setAttribute('aria-expanded', String(opening));
        if (opening) $('template-import-code')?.focus();
    });

    // Both routes to a new template go through the designer, which opens on the
    // description – so neither needs to say anything more than "start one".
    // Designing is Pro; importing and using what others made is not, so a free
    // user reaches the wall here rather than after describing a slide.
    const startNewTemplate = () => {
        if (state.user?.plan !== 'pro') {
            showQuotaModal({ error: 'pro_required', message: TEMPLATE_PRO_MESSAGE });
            return;
        }
        hide(templateManagerModal);
        suppressSlotBadges(false);
        openTemplateDesigner();
    };
    $('template-manager-new')?.addEventListener('click', startNewTemplate);
    $('template-empty-new')?.addEventListener('click', startNewTemplate);

    document.addEventListener('click', (e) => {
        const closer = e.target.closest('[data-close]');
        if (!closer) return;
        if (closer.dataset.close === 'gen-template') hide($('gen-template-modal'));
        if (closer.dataset.close === 'template-designer') closeTemplateDesigner();
        if (closer.dataset.close === 'template-save') closeTemplateSaveModal();
        if (closer.dataset.close === 'template-manager') {
            closeTemplateMenu();
            hide(templateManagerModal);
            suppressSlotBadges(false);
        }
    });

    // ── Layer / z-index helpers ───────────────────────────
    function getSlideZ(el) { return parseInt(el.style.zIndex) || 0; }

    function getSlideChildren() {
        const slideEl = stageSlideInner.querySelector('.slide');
        // The slide's background colour is not a layer: nothing goes behind it.
        return slideEl ? Array.from(slideEl.children).filter(c => !c.hasAttribute('data-slide-bg')) : [];
    }

    function layerBringFront(el) {
        const maxZ = Math.max(0, ...getSlideChildren().map(getSlideZ));
        el.style.zIndex = (maxZ + 1).toString();
    }

    function layerSendBack(el) {
        const others = getSlideChildren().filter(c => c !== el);
        if (!others.length) return;
        const minZ = Math.min(...others.map(getSlideZ));
        const newZ = minZ - 1;
        const floor = stageSlideInner.querySelector('.slide > [data-slide-bg]') ? SLIDE_BG_Z + 1 : 1;
        if (newZ < floor) {
            // Shift all others up to keep this element above the slide background
            const shift = floor - newZ;
            others.forEach(c => c.style.zIndex = (getSlideZ(c) + shift).toString());
            el.style.zIndex = String(floor);
        } else {
            el.style.zIndex = newZ.toString();
        }
    }

    function layerBringForward(el) {
        const children = getSlideChildren();
        const elZ = getSlideZ(el);
        const above = children
            .filter(c => c !== el && getSlideZ(c) > elZ)
            .sort((a, b) => getSlideZ(a) - getSlideZ(b));
        if (!above.length) { layerBringFront(el); return; }
        const swap = above[0];
        const swapZ = getSlideZ(swap);
        el.style.zIndex = swapZ.toString();
        swap.style.zIndex = elZ.toString();
    }

    function layerSendBackward(el) {
        const children = getSlideChildren();
        const elZ = getSlideZ(el);
        const below = children
            .filter(c => c !== el && getSlideZ(c) < elZ)
            .sort((a, b) => getSlideZ(b) - getSlideZ(a));
        if (!below.length) { layerSendBack(el); return; }
        const swap = below[0];
        const swapZ = getSlideZ(swap);
        el.style.zIndex = swapZ.toString();
        swap.style.zIndex = elZ.toString();
    }

    // ── Add elements ──────────────────────────────────────
    function appendDraggable(el) {
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return;
        const preAddHtml = getSlideHtml();
        // Always place new element above everything currently on the slide
        const maxZ = Math.max(0, ...Array.from(slideEl.children).map(getSlideZ));
        el.style.zIndex = (maxZ + 1).toString();
        slideEl.appendChild(el);
        commitHistory(preAddHtml);
        if (state.dragActive) dragCleanups.push(makeDraggable(el));
        setSelectedEl(el);
    }

    pptAddText?.addEventListener('click', () => {
        const el = document.createElement('div');
        el.className = 'slide__added-text';
        el.textContent = 'New text';
        el.style.cssText = `
            display: inline-block; min-width: 120px; padding: 6px 10px;
            font-size: 1.4rem; font-family: var(--s-font); color: var(--s-text);
            position: absolute; left: 35%; top: 38%;
            cursor: grab; box-sizing: border-box;
        `;
        appendDraggable(el); // also calls setSelectedEl(el)
        // Jump into text edit immediately
        el.contentEditable = 'true';
        el.style.cursor = 'text';
        el.focus();
        document.execCommand('selectAll');
    });

    pptAddImgBtn?.addEventListener('click', () => {
        // Toggle the image keyword row
        const hidden = pptImgRow?.hasAttribute('hidden');
        if (hidden) { show(pptImgRow); pptImgKw?.focus(); }
        else          { hide(pptImgRow); }
    });

    pptImgCancel?.addEventListener('click', () => {
        hide(pptImgRow);
        if (pptImgKw) pptImgKw.value = '';
    });

    function addImage(url, altText = 'Uploaded image', imagePath = '') {
        const img = document.createElement('img');
        img.src = url;
        img.alt = altText;
        if (imagePath) {
            img.dataset.imagePath = imagePath;
            managedImageEls.add(img);
        }
        img.className = 'slide__added-image';
        img.style.cssText = `
            display: block; width: 240px; height: 160px;
            object-fit: cover; border-radius: 4px;
            position: absolute; left: 30%; top: 28%;
            cursor: grab; box-sizing: border-box;
        `;
        return img;
    }

    async function insertUnsplashImage(keyword) {
        if (!keyword) return;
        try {
            const res = await apiFetch('/api/slides/unsplash?keyword=' + encodeURIComponent(keyword));
            const data = await res.json();
            if (!data.url) { setError(stageError, 'No image found for that keyword'); return; }

            const img = addImage(data.url, keyword);
            // Attribution travels with the image so the credit strip can find it
            // again after a reload, an undo, or an AI edit.
            if (data.credit?.name) {
                img.dataset.unsplashId = data.id || '';
                img.dataset.unsplashCredit = data.credit.name;
                img.dataset.unsplashProfile = data.credit.profileUrl || '';
            }
            appendDraggable(img);
            hide(pptImgRow);
            if (pptImgKw) pptImgKw.value = '';

            // The photo is on the slide now, so report it as used.
            if (data.downloadLocation) {
                apiFetch('/api/slides/unsplash/download', {
                    method: 'POST',
                    body: JSON.stringify({ downloadLocation: data.downloadLocation }),
                }).catch(() => {});
            }
        } catch (e) {
            setError(stageError, 'Image fetch failed');
        }
    }

    /**
     * A pasted address, or a keyword to search Unsplash for.
     *
     * One box rather than two, because the two are never ambiguous: an image
     * address always starts with a scheme, and no Unsplash keyword ever does.
     * http(s) only – a slide is saved to the server and served back to whoever
     * opens the deck, so the schemes that can carry code are not addresses we
     * put in an src.
     */
    function asImageUrl(text) {
        if (!/^(https?:)?\/\//i.test(text)) return null;
        try {
            const url = new URL(text, window.location.href);
            return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
        } catch { return null; }
    }

    /**
     * Insert an image the teacher has the address of.
     *
     * Loaded once before it goes on the slide: a link that 404s, or points at a
     * page rather than a file, otherwise lands as a broken icon they have to
     * notice, select and delete. Failing here costs a message instead.
     *
     * The address stays remote, exactly as an Unsplash one does – export
     * fetches and inlines both server-side at PDF time (see fetchImageAsDataUri
     * in utils/pdf.js), so neither needs a copy of its own.
     */
    // The file's own name is the closest thing to a description the address
    // carries. decodeURIComponent throws on a stray percent, which a URL is
    // perfectly entitled to contain, so a failure here just falls back.
    function altFromUrl(url) {
        const file = url.split('/').pop().split('?')[0] || '';
        let name = file;
        try { name = decodeURIComponent(file); } catch { /* keep it encoded */ }
        return name.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[-_]+/g, ' ').trim() || 'Image';
    }

    function insertImageFromUrl(url) {
        setError(stageError, '');
        const probe = new Image();
        probe.onload = () => {
            appendDraggable(addImage(url, altFromUrl(url)));
            hide(pptImgRow);
            if (pptImgKw) pptImgKw.value = '';
        };
        probe.onerror = () => setError(stageError, 'That link did not load as an image. Check it opens the picture itself, not the page it sits on.');
        probe.src = url;
    }

    function insertFromImgRow() {
        const text = pptImgKw?.value.trim();
        if (!text) return;
        const url = asImageUrl(text);
        if (url) insertImageFromUrl(url);
        else insertUnsplashImage(text);
    }

    pptImgConfirm?.addEventListener('click', insertFromImgRow);
    pptImgKw?.addEventListener('keydown', e => {
        if (e.key === 'Enter') { e.preventDefault(); insertFromImgRow(); }
        if (e.key === 'Escape') { hide(pptImgRow); }
    });

    async function uploadCustomImage(file) {
        if (!state.presentationId) {
            throw new Error('No active presentation');
        }

        const formData = new FormData();
        formData.append('image', file);
        formData.append('filename', file.name);

        const response = await fetch(`/api/presentations/${state.presentationId}/images`, {
            method: 'POST',
            body: formData,
        });

        if (response.status === 401) {
            showAuth();
            throw new Error('Not authenticated');
        }

        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.url) {
            const msg = data?.error || 'Image upload failed';
            throw new Error(msg);
        }

        return data;
    }

    async function deleteCustomImage(el) {
        if (!el || el.tagName !== 'IMG') {
            throw new Error('Invalid image element');
        }

        const imagePath = el.dataset?.imagePath || '';
        if (!imagePath) {
            throw new Error('Missing imagePath for managed image');
        }

        if (!state.presentationId) {
            throw new Error('No active presentation');
        }

        const res = await apiFetch(`/api/presentations/${state.presentationId}/images`, {
            method: 'DELETE',
            body: JSON.stringify({
                imagePath,
            }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data?.error || 'Image CDN delete failed');
        }
    }

    pptUploadImgBtn?.addEventListener('click', () => {
        pptImgUploadInput?.click();
    });

    pptImgUploadInput?.addEventListener('change', async (e) => {
        const input = e.currentTarget;
        const file = input?.files?.[0];
        if (!file) return;

        try {
            setError(stageError, '');
            const uploadedImg = await uploadCustomImage(file);
            const img = addImage(
                uploadedImg.url,
                file.name || 'Uploaded image',
                uploadedImg.imagePath || ''
            );
            appendDraggable(img);
            hide(pptImgRow);
            if (pptImgKw) pptImgKw.value = '';
        } catch (err) {
            setError(stageError, err?.message || 'Image upload failed');
        } finally {
            if (input) input.value = '';
        }
    });

    function addShape(type) {
        const el = document.createElement('div');
        el.className = 'slide__added-shape';
        el.style.cssText = `
            width: 160px; height: 90px;
            background: var(--s-accent, #0077ff);
            ${type === 'circle' ? 'border-radius: 50%; width: 110px; height: 110px;' : 'border-radius: 6px;'}
            position: absolute; left: 35%; top: 35%;
            cursor: grab; box-sizing: border-box; opacity: 0.85;
        `;
        appendDraggable(el);
    }

    $('ppt-group')?.addEventListener('click', () => groupSelected());
    $('ppt-ungroup')?.addEventListener('click', () => ungroupSelected());
    $('ppt-make-repeat')?.addEventListener('click', () => {
        const wrap = makeRepeatFromSelection();
        if (wrap) openSlotForm(wrap, 'repeat');
    });

    pptAddRect?.addEventListener('click',   () => addShape('rect'));
    pptAddCircle?.addEventListener('click', () => addShape('circle'));

    pptDeleteEl?.addEventListener('click', () => {
        deleteSelectedElement();
    });

    pptCropBtn?.addEventListener('click', () => {
        if (canCrop(state.selectedEl)) startCrop(state.selectedEl);
    });

    // ── Picture panel (public/js/image-bank-panel.js) ─────
    // Swap the selected picture for one from the picture bank, Unsplash or the
    // web, or add one when nothing is selected. The panel stays open beside
    // the slide; every swap is one undo step.

    window.ImageBankPanel?.init({ api: apiFetch });
    // The panel belongs to the deck being edited: leaving the editor (back to
    // the library, another page) closes it. Watched on the whole page because
    // leaving usually hides a view the editor sits inside, not the editor.
    if (pptEditor) {
        new MutationObserver(() => {
            if (window.ImageBankPanel?.isOpen() && pptEditor.closest('[hidden]')) window.ImageBankPanel.close();
        }).observe(document.body, { attributes: true, attributeFilter: ['hidden'], subtree: true });
    }

    // Everything a teacher would call "the picture": the image itself, the
    // empty box a picture was meant to fill, the one picture inside a card or
    // group they clicked, or the photo under a title slide's tint.
    const PLACEHOLDER_CLASSES = ['el--image-placeholder', 'slide__image-placeholder', 'slide__title-image-placeholder', 'slide__term-image-placeholder'];
    // A model "image" element with no picture is an empty picture box only
    // when nothing paints it: a title slide's tint and background are image
    // elements too, filled with a colour, and are not waiting for a picture.
    const isPicturePlaceholder = (el) => !!el && PLACEHOLDER_CLASSES.some(c => el.classList?.contains(c))
        && !(el.style.background || el.style.backgroundColor || el.style.backgroundImage);
    const isPictureEl = (el) => !!el && (el.tagName === 'IMG' || isPicturePlaceholder(el));

    function resolveSwapTarget(el) {
        if (!el || !stageSlideInner?.contains(el)) return null;
        if (isPictureEl(el)) return el;
        const selector = 'img, ' + PLACEHOLDER_CLASSES.map(c => '.' + c).join(', ');
        const inside = [...el.querySelectorAll(selector)];
        if (inside.length === 1) return inside[0];
        const gid = el.dataset?.groupId;
        if (gid) {
            const members = [...stageSlideInner.querySelectorAll(`[data-group-id="${CSS.escape(gid)}"]`)].filter(isPictureEl);
            if (members.length === 1) return members[0];
        }
        // A tint or frame with no words of its own, laid over a picture.
        if (!inside.length && !el.textContent.trim()) {
            const b = getElBoundsInSlide(el);
            if (b) {
                const cx = b.left + b.width / 2, cy = b.top + b.height / 2;
                const under = [...stageSlideInner.querySelectorAll(selector)].find((p) => {
                    if (p === el || !isPictureEl(p)) return false;
                    const pb = getElBoundsInSlide(p);
                    return pb && cx >= pb.left && cx <= pb.left + pb.width && cy >= pb.top && cy <= pb.top + pb.height;
                });
                if (under) return under;
            }
        }
        return null;
    }

    function canSwapPicture(el) {
        return !!resolveSwapTarget(el) && !isDesigningTemplate() && state.pptMode === 'direct';
    }

    // Looked up rather than held in a const: updateSelBox calls this, and it
    // can run before this part of the file has.
    function placeSwapChip() {
        const picSwapChip = document.getElementById('pic-swap-chip');
        if (!picSwapChip) return;
        const target = !cropState && !state.presenting && canSwapPicture(state.selectedEl) ? resolveSwapTarget(state.selectedEl) : null;
        const b = target && getElBoundsInSlide(target);
        if (!b || !b.width || !stageCanvas?.offsetWidth) { picSwapChip.hidden = true; return; }
        const k = stageCanvas.offsetWidth / 960;
        const empty = isPicturePlaceholder(target);
        picSwapChip.classList.toggle('is-empty', empty);
        picSwapChip.querySelector('span').textContent = empty ? 'Choose a picture' : 'Swap picture';
        // Centred in an empty box; along the bottom edge of a picture, inside
        // it, so the canvas edge never clips it.
        const x = (b.left + b.width / 2) * k;
        const y = empty ? (b.top + b.height / 2) * k : Math.max(4, (b.top + b.height) * k - 46);
        picSwapChip.style.left = Math.min(Math.max(x, 70), stageCanvas.offsetWidth - 70) + 'px';
        picSwapChip.style.top = y + 'px';
        picSwapChip.hidden = false;
    }
    const picSwapChip = $('pic-swap-chip');
    picSwapChip?.addEventListener('mousedown', (e) => e.stopPropagation());
    picSwapChip?.addEventListener('click', (e) => {
        e.stopPropagation();
        if (canSwapPicture(state.selectedEl)) openPicturePanel();
    });
    if (stageCanvas && window.ResizeObserver) new ResizeObserver(placeSwapChip).observe(stageCanvas);

    const BANK_URL_RE = /\/image-bank\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.[a-z0-9]+(?:[?#]|$)/i;
    const bankIdOf = (url) => (BANK_URL_RE.exec(String(url || '')) || [])[1] || null;

    function pictureTarget() {
        return resolveSwapTarget(state.selectedEl);
    }

    /**
     * An empty picture box becomes an <img> in the same place, keeping every
     * attribute the slide model and the drag editor know it by (data-el-id,
     * group, reveal step, inline box), so the model sync sees the same element
     * now with a picture in it.
     */
    function fillPlaceholder(box) {
        const img = document.createElement('img');
        for (const a of [...box.attributes]) if (a.name !== 'class') img.setAttribute(a.name, a.value);
        if (box.classList.contains('el--image-placeholder')) {
            img.className = 'el el--image';
            if (!img.hasAttribute('data-object-fit')) img.setAttribute('data-object-fit', 'contain');
        } else {
            img.className = box.classList.contains('slide__title-image-placeholder') ? 'slide__title-image-bg' : 'slide__image';
        }
        img.loading = 'lazy';
        box.replaceWith(img);
        return img;
    }

    function openPicturePanel() {
        if (!window.ImageBankPanel || !state.presentationId) return;
        const img = pictureTarget();
        window.ImageBankPanel.open({
            presentationId: state.presentationId,
            mode: img ? 'swap' : 'add',
            currentUrl: img && img.tagName === 'IMG' ? (img.currentSrc || img.src) : '',
            hint: img?.alt || state.presentationTopic || '',
            onPick: applyPicture,
        });
    }

    function syncPicturePanel(el) {
        placeSwapChip();
        if (!window.ImageBankPanel?.isOpen()) return;
        const img = resolveSwapTarget(el);
        window.ImageBankPanel.retarget({
            mode: img ? 'swap' : 'add',
            currentUrl: img && img.tagName === 'IMG' ? (img.currentSrc || img.src) : '',
            hint: img?.alt || state.presentationTopic || '',
        });
    }

    function stampPictureSource(img, choice) {
        ['unsplashId', 'unsplashCredit', 'unsplashProfile', 'imagePath'].forEach((k) => { delete img.dataset[k]; });
        managedImageEls.delete(img);
        if (choice.unsplash?.name) {
            img.dataset.unsplashId = choice.unsplash.id || '';
            img.dataset.unsplashCredit = choice.unsplash.name;
            img.dataset.unsplashProfile = choice.unsplash.profileUrl || '';
        }
    }

    // Counted once the picture is on the slide: the bank learns what teachers
    // keep, and Unsplash is told about the one photo actually used.
    function reportPictureUse(choice, replacedUrl) {
        const replaced = bankIdOf(replacedUrl);
        if (choice.bankId || replaced) {
            apiFetch('/api/image-bank/used', {
                method: 'POST', body: JSON.stringify({ id: choice.bankId || null, replaced }),
            }).catch(() => {});
        }
        if (choice.unsplash?.downloadLocation) {
            apiFetch('/api/slides/unsplash/download', {
                method: 'POST', body: JSON.stringify({ downloadLocation: choice.unsplash.downloadLocation }),
            }).catch(() => {});
        }
    }

    function applyPicture(choice) {
        let img = pictureTarget();
        if (!img) {
            // Nothing selected: a new picture, sized to its own shape.
            const w = choice.width || 4, h = choice.height || 3;
            const width = Math.round(w / h >= 1 ? 360 : 360 * w / h);
            const added = addImage(choice.url, choice.alt || 'Picture');
            added.style.width = width + 'px';
            added.style.height = Math.round(width * h / w) + 'px';
            added.style.objectFit = choice.kind === 'photo' ? 'cover' : 'contain';
            stampPictureSource(added, choice);
            appendDraggable(added);   // selects it, so the next pick swaps it
            reportPictureUse(choice, '');
            return;
        }

        const snap = getSlideHtml();
        if (img.tagName !== 'IMG') img = fillPlaceholder(img);
        const replaced = img.currentSrc || img.src;
        // A crop was cut for the old picture. Keep the part of the box that
        // showed, and drop the crop, so the new picture lands in the frame the
        // teacher could see.
        const ins = cropInsetsOf(img);
        if (ins && (ins.t || ins.r || ins.b || ins.l) && isAbsoluteEl(img)) {
            const v = visibleBoundsOf(img);
            const o = positioningOriginInSlide(img);
            img.style.left = (v.left - o.left) + 'px';
            img.style.top = (v.top - o.top) + 'px';
            img.style.width = v.width + 'px';
            img.style.height = v.height + 'px';
        }
        if (ins) setCropInsets(img, { t: 0, r: 0, b: 0, l: 0, round: ins.round });

        img.removeAttribute('srcset');
        img.src = choice.url;
        img.alt = choice.alt || '';
        stampPictureSource(img, choice);
        // A diagram is fitted whole, never trimmed: its labels are at the edges.
        // A photo fills the box, unless it was the stretched "fill" a crop leaves.
        const fit = choice.kind === 'photo'
            ? (getComputedStyle(img).objectFit === 'contain' ? 'contain' : 'cover')
            : 'contain';
        img.style.objectFit = fit;
        if (img.hasAttribute('data-object-fit')) img.dataset.objectFit = fit;

        commitHistory(snap);
        setSelectedEl(img);
        reportPictureUse(choice, replaced);
    }

    pptSwapBtn?.addEventListener('click', () => {
        if (canSwapPicture(state.selectedEl)) openPicturePanel();
    });
    $('ppt-img-bank')?.addEventListener('click', () => {
        hide(pptImgRow);
        setSelectedEl(null);
        openPicturePanel();
    });

    async function deleteSelectedElement() {
        if (!state.selectedEl) return;
        const multi = multiSelectList();
        const targets = multi.length > 1 ? multi : [state.selectedEl];

        try {
            setError(stageError, '');

            for (const el of targets) {
                const isImage = el.tagName === 'IMG';
                const hasImagePath = !!el.dataset?.imagePath;
                const isManagedImage = isImage && (managedImageEls.has(el) || hasImagePath);
                if (isManagedImage && !hasImagePath) {
                    throw new Error('Managed image metadata is missing');
                }
            }

            for (const el of targets) {
                if (el.tagName === 'IMG' && el.dataset?.imagePath && !imageUsedElsewhere(el)) {
                    // Only remove from slide after backend delete succeeds.
                    await deleteCustomImage(el);
                }
            }

            const snap = getSlideHtml();
            setSelectedEl(null);
            clearMultiSelect();
            targets.forEach(el => el.remove());
            commitHistory(snap);
        } catch (err) {
            setError(stageError, err?.message || 'Delete failed. Element was not removed.');
        }
    }

    // Undo / Redo / Delete keyboard shortcuts
    document.addEventListener('keydown', (e) => {
        if (state.presenting || !state.activeSlideId) return;
        const active = document.activeElement;
        const isTypingTarget = active && (
            active.contentEditable === 'true' ||
            /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)
        );

        if ((e.key === 'Delete' || e.key === 'Backspace') &&
            !e.ctrlKey && !e.metaKey && !e.altKey &&
            state.selectedEl && !isTypingTarget) {
            e.preventDefault();
            deleteSelectedElement();
            return;
        }

        // Copy, cut and paste for slide elements. Left to the browser when
        // there is text highlighted or a field being typed into - with two
        // exceptions that used to make Cmd+X look dead:
        //  - a text box with the caret in it but nothing highlighted. One
        //    click too many puts a box into text editing, and the browser's
        //    own cut then has nothing to cut. With nothing highlighted, copy
        //    and cut mean the box.
        //  - focus left on a control that is not for typing (the colour
        //    picker, a file chooser), which is not a reason to ignore the slide.
        const modKey = (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
        const clipKey = modKey ? e.key.toLowerCase() : '';
        if (clipKey && 'cxv'.includes(clipKey) && state.pptMode === 'direct' && isShown(pptEditor)) {
            const nothingHighlighted = !String(window.getSelection?.() || '');
            const editingBox = !!active && active.contentEditable === 'true' && !!stageSlideInner?.contains(active);
            const notForTyping = !!active && active.tagName === 'INPUT'
                && ['color', 'file', 'checkbox', 'radio', 'range', 'button'].includes(active.type);
            const ours = nothingHighlighted && (!isTypingTarget || notForTyping || (editingBox && clipKey !== 'v'));
            if (ours) {
                if (clipKey === 'c' && copySelection()) { e.preventDefault(); showToast('Copied'); return; }
                if (clipKey === 'x' && cutSelection()) { e.preventDefault(); showToast('Cut'); return; }
                if (clipKey === 'v' && pasteClipboard()) { e.preventDefault(); return; }
            }
        }
        if (isTypingTarget) return;
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            undoEdit();
        } else if ((e.ctrlKey || e.metaKey) && ((e.key === 'Z' || (e.key === 'z' && e.shiftKey)) || e.key === 'y')) {
            // Ctrl + Shift + z or Ctrl + y
            e.preventDefault();
            redoEdit();
        }
    });

    pptBtnUndo?.addEventListener('click', undoEdit);
    pptBtnRedo?.addEventListener('click', redoEdit);

    // ── Editor mode ───────────────────────────────────────
    // There is one editing mode now – AI is a tool on the bar (see "AI
    // editing" below) – but opening a deck still goes through here to put the
    // toolbar and the drag editor back.
    function switchPptMode() {
        state.pptMode = 'direct';
        show(pptDirectBar);
        if (state.activeSlideId && !state.dragActive) activateDragEditor();
    }

    // Direct toolbar formatting (text commands via execCommand)
    pptDirectBar?.addEventListener('mousedown', (e) => {
        const btn = e.target.closest('.toolbar-btn');
        if (!btn || btn.dataset.valign) return; // valign buttons handled separately
        e.preventDefault();
        const cmd = btn.dataset.cmd || '';
        const el = state.selectedEl;
        // With the text box selected but the caret NOT inside its text, the
        // align buttons act on the whole box (PowerPoint text frames): the
        // inline text-align is read back into the element's align.h on save.
        const hAlign = { justifyLeft: 'left', justifyCenter: 'center', justifyRight: 'right' }[cmd];
        if (hAlign && el && !selectionInside(el)) {
            el.style.textAlign = hAlign;
            // Per-line alignment leaves an inline text-align on the block it
            // landed in, and an inline value on a child beats the box's
            // inherited one - so those lines ignored the box and the change
            // looked dead or partial. Clearing them hands the whole box back
            // to align.h, which is the only value the model stores anyway.
            el.querySelectorAll('[style*="text-align" i]').forEach(n => {
                n.style.textAlign = '';
            });
            return;
        }
        // The same goes for size, bold and italic. A teacher clicks a text box
        // and then a button, and expects the box to change; with no caret in
        // it the browser's own command has nothing to act on, and the button
        // used to look dead.
        if (el && !selectionInside(el) && formatWholeElement(el, cmd, btn.dataset.val)) return;
        // Stepping the size is only ever about the whole box.
        if (cmd === 'fontStep') return;
        // Otherwise the alignment targets the run holding the caret, exactly
        // like the other text commands.
        // Without this, Chromium answers foreColor with <font color> –
        // deprecated markup that styles nothing our CSS knows about. Asking for CSS keeps edits in the same vocabulary as the
        // rest of a slide: spans carrying inline styles.
        document.execCommand('styleWithCSS', false, true);
        document.execCommand(cmd, false, btn.dataset.val || null);
    });

    const FONT_STEP_PX = 2;
    const FONT_MIN_PX = 8;
    const FONT_MAX_PX = 200;

    function isTextBox(el) {
        return !!el && el.tagName !== 'IMG'
            && !el.classList.contains('slide__added-shape') && !el.classList.contains('el--shape')
            && !el.hasAttribute('data-slide-bg');
    }

    // Applies a text command to a whole selected box. Returns false for a
    // command (or an element) this does not cover, which then falls through to
    // the browser's own handling.
    function formatWholeElement(el, cmd, val) {
        if (!isTextBox(el) || !['fontStep', 'bold', 'italic'].includes(cmd)) return false;
        const snap = getSlideHtml();
        if (cmd === 'bold' || cmd === 'italic') {
            const cs = getComputedStyle(el);
            if (cmd === 'bold') el.style.fontWeight = (parseInt(cs.fontWeight, 10) || 400) >= 600 ? '400' : '700';
            else el.style.fontStyle = cs.fontStyle === 'italic' ? 'normal' : 'italic';
        } else {
            // A size set on part of the text earlier would outrank the box's.
            el.querySelectorAll('[style*="font-size"]').forEach((n) => {
                n.style.removeProperty('font-size');
                if (!n.getAttribute('style')) n.removeAttribute('style');
            });
            const now = parseFloat(getComputedStyle(el).fontSize) || 16;
            const next = Math.min(FONT_MAX_PX, Math.max(FONT_MIN_PX, Math.round(now) + Number(val) * FONT_STEP_PX));
            el.style.fontSize = next + 'px';
        }
        commitHistory(snap);
        setSelectedEl(el);   // the box changed size, so the selection frame has to follow
        updateSelBox(getGroupBounds(el));
        return true;
    }

    // True when `el` is being text-edited with the caret or a selection inside
    // it – the "text selected" half of the toolbar's alignment behaviour.
    function selectionInside(el) {
        if (!el || !el.contains) return false;
        if (el.contentEditable !== 'true') return false;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return false;
        const range = sel.getRangeAt(0);
        return el === range.commonAncestorContainer || el.contains(range.commonAncestorContainer);
    }

    // Vertical alignment – a flex column needs the inline flow in ONE flex item.
    pptDirectBar?.addEventListener('mousedown', (e) => {
        const btn = e.target.closest('.toolbar-btn[data-valign]');
        if (!btn || !state.selectedEl) return;
        e.preventDefault();
        const el = state.selectedEl;
        const justify = btn.dataset.valign === 'top' ? 'flex-start'
                      : btn.dataset.valign === 'middle' ? 'center'
                      : 'flex-end';
        // Returning to top clears the flex triple and restores flat content.
        if (btn.dataset.valign === 'top') {
            el.style.display = '';
            el.style.flexDirection = '';
            el.style.justifyContent = '';
            SlideModelEditor.unwrapValignContent(el);
            return;
        }
        SlideModelEditor.wrapValignContent(el);
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
        el.style.justifyContent = justify;
    });

    // ── Color picker (split button) ───────────────────────
    // "A" button → instantly applies current colour to selection (no dialog).
    // Chevron/swatch → opens native colour picker to choose a new colour.
    // Both save the selection on mousedown before focus can leave the editor.
    let _savedColorRange = null;
    let _colorPickerOpen = false;

    const pptColorApply = $('text-color-apply');
    const pptColorBar   = $('text-color-bar');

    function syncColorBar() {
        if (pptColorBar) pptColorBar.style.background = pptColorPicker?.value || '#e11d48';
    }
    syncColorBar();

    function saveSelectionRange() {
        const editEl = stageSlideInner?.querySelector('[contenteditable="true"]');
        if (!editEl) { _savedColorRange = null; return; }
        const sel = window.getSelection();
        _savedColorRange = (sel && sel.rangeCount > 0) ? sel.getRangeAt(0).cloneRange() : null;
    }

    function applyColorToSelection(color) {
        const editEl = stageSlideInner?.querySelector('[contenteditable="true"]');
        const isShapeEl = state.selectedEl?.classList?.contains('slide__added-shape')
            || state.selectedEl?.classList?.contains('el--shape');
        if (editEl && _savedColorRange) {
            // Text colour mode – apply to selected characters
            const preSnap = getSlideHtml();
            editEl.focus();
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(_savedColorRange);
            document.execCommand('styleWithCSS', false, true);   // span style, not <font color>
            document.execCommand('foreColor', false, color);
            if (sel.rangeCount > 0) _savedColorRange = sel.getRangeAt(0).cloneRange();
            if (preSnap) commitHistory(preSnap);
        } else if (state.selectedEl) {
            const preSnap = getSlideHtml();
            if (isShapeEl) {
                // Shape fill.
                state.selectedEl.style.background = color;
            } else {
                // Whole-element text colour
                state.selectedEl.style.color = color;
                state.selectedEl.querySelectorAll('[style*="color"]').forEach(s => {
                    if (!s.style.color) return; // *-color matches, `color` may not be set
                    s.style.removeProperty('color');
                    if (!s.getAttribute('style')) s.removeAttribute('style');
                });
            }
            commitHistory(preSnap);
        }
    }

    // "A" button: save selection then apply current colour (no dialog)
    pptColorApply?.addEventListener('mousedown', (e) => {
        e.preventDefault(); // keep focus on the contenteditable
        saveSelectionRange();
    });
    pptColorApply?.addEventListener('click', () => {
        applyColorToSelection(pptColorPicker?.value || '#e11d48');
    });

    // Chevron: save selection, then open native colour dialog
    const _colorPickerLabel = pptColorPicker?.closest('label');
    _colorPickerLabel?.addEventListener('mousedown', () => {
        saveSelectionRange();
        _colorPickerOpen = true;
        // Trigger the hidden input after this event so mousedown completes first
        requestAnimationFrame(() => pptColorPicker?.click());
    });

    pptColorPicker?.addEventListener('change', () => {
        _colorPickerOpen = false;
        syncColorBar();
        applyColorToSelection(pptColorPicker.value);
    });

    pptColorPicker?.addEventListener('blur', () => {
        _colorPickerOpen = false;
    });

    // Save direct edits (called by auto-save debounce)
    async function savePptDirect() {
        endCrop(false);
        autoSavePending = false;
        if (!state.activeSlideId || stageShowsAiPreview()) return;
        // Capture which slide we're saving now – state.activeSlideId/
        // activeSlidePosition can change while the PUT below is in flight
        // (user switches or reorders slides), and using the live values
        // after the await would stamp this slide's html onto whichever
        // slide/position happens to be active by the time the response lands.
        const savingSlideId = state.activeSlideId;
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) return;

        deactivateDragEditor(true);
        const editEl = slideEl.querySelector('[contenteditable="true"]');
        // If an element is still being edited but has already diverged from its pre-edit
        // snapshot, mark it edited otherwise the next reload shifts siblings.
        if (editEl && editEl._preEditHtml !== undefined && getSlideHtml() !== editEl._preEditHtml) {
            editEl.dataset.modelEdited = '1';
        }
        // A bullet typed into a list that reveals one by one has no step yet.
        renumberReveals(slideEl);
        const html = getSlideHtml();   // ghost-free: previews are editor chrome
        // Autosave can fire between the DOM change and blur. 
        // Base it on the live DOM or a half-synced model – missing the last edit,
        // the underline, or a group's freeze – gets stored and undoes it on
        // the next reload.
        if (state.activeSlideId === savingSlideId) syncModelFromCurrentDom();
        // Held here straight away, not only once the server has answered:
        // Present and the thumbnails both read this copy, and until it caught
        // up they showed the slide as it was before the last edit.
        state.slides.forEach((sl, pos) => {
            if (sl.id === savingSlideId) state.slides.set(pos, { ...sl, html });
        });
        const model = currentSlideModel();
        setError(stageError, '');

        const payload = { html };
        if (model) {
            payload.model = {
                contents_json: model.slide.contents_json,
                elements_json: model.slide.elements_json,
                json_schema_version: model.slide.json_schema_version,
            };
        }

        // Save logic is the same for both storage paths – only the endpoint
        // differs. Model slides go to /model, legacy html-only slides to /html.
        const endpoint = model ? 'model' : 'html';

        try {
            const res = await apiFetch(`/api/slides/${savingSlideId}/${endpoint}`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(stageError, data.error || 'Save failed');
            } else {
                // The server strips anything executable before storing, so what
                // it kept is the truth about this slide – hold that in state
                // rather than what was sent, or the editor shows markup that
                // the next refresh will not.
                const saved = await res.json().catch(() => ({}));
                const storedHtml = typeof saved.html === 'string' ? saved.html : html;
                let savedPosition = null;
                state.slides.forEach((s, pos) => {
                    if (s.id === savingSlideId) { state.slides.set(pos, { ...s, html: storedHtml }); savedPosition = pos; }
                });
                pauseAutoSave();
                if (savedPosition != null) updateThumb(savedPosition, savingSlideId, storedHtml);
                resumeAutoSave();
            }
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(stageError, 'Save failed');
        } finally {
            if (state.pptMode === 'direct' && state.activeSlideId === savingSlideId) activateDragEditor();
        }
    }

    // ── Unsplash and picture-bank attribution ─────────────
    // Picture-bank files are Creative Commons, which asks the same thing.
    // Unsplash's API terms require the photographer and Unsplash to be credited
    // wherever one of their photos is shown. The credit lives in the editor
    // chrome under the canvas rather than on the slide itself: the teacher's
    // design and their exported deck stay untouched, and the attribution is
    // still on screen the whole time the photo is.

    const stageCredits = $('stage-credits');
    const bankCredits = new Map();   // bank id -> { credit, sourceUrl }, null while asked, false if unknown

    // Photographer links arrive from the server already carrying this; only the
    // "on Unsplash" link is built here. Keep in step with UNSPLASH_APP_NAME.
    const UNSPLASH_HOME = 'https://unsplash.com/?utm_source=ba_productivity&utm_medium=referral';

    // Credits every Unsplash photo inside sourceEl, writing into targetEl.
    // Used for both the editor stage and present mode: a photo shown fullscreen
    // to a class is still the app showing it, so it needs the credit too.
    function renderUnsplashCredits(sourceEl = stageSlideInner, targetEl = stageCredits, linked = true) {
        if (!targetEl || !sourceEl) return;

        // De-duplicated by photographer: two photos by the same person read as
        // one credit, not a repeated name.
        const byName = new Map();
        sourceEl.querySelectorAll('img[data-unsplash-credit]').forEach(img => {
            const name = img.dataset.unsplashCredit;
            if (name && !byName.has(name)) byName.set(name, img.dataset.unsplashProfile || '');
        });

        // Picture-bank files carry their licence's credit (an author and a
        // Creative Commons licence) on the server, found by the id in their
        // URL. Fetched once per picture, then the strip is drawn again.
        const bankIds = [...new Set([...sourceEl.querySelectorAll('img')].map(img => bankIdOf(img.currentSrc || img.src)).filter(Boolean))];
        const unknown = bankIds.filter(id => !bankCredits.has(id));
        if (unknown.length) {
            unknown.forEach(id => bankCredits.set(id, null));
            apiFetch('/api/image-bank/credits?ids=' + unknown.join(','))
                .then(r => r.ok ? r.json() : { credits: {} })
                .then(({ credits }) => {
                    unknown.forEach(id => bankCredits.set(id, (credits || {})[id] || false));
                    renderUnsplashCredits(sourceEl, targetEl, linked);
                })
                .catch(() => unknown.forEach(id => bankCredits.delete(id)));
        }
        const bankLines = bankIds.map(id => bankCredits.get(id)).filter(Boolean);

        if (!byName.size && !bankLines.length) { targetEl.hidden = true; targetEl.textContent = ''; return; }

        // In present mode the overlay is fullscreen and the audience cannot
        // click anything, so the credit is plain text there – the name and
        // "Unsplash" are still both shown, which is what the guideline asks.
        const link = (href, text) => {
            if (!linked) return document.createTextNode(text);
            const a = document.createElement('a');
            a.href = href;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.textContent = text;
            return a;
        };

        targetEl.innerHTML = '';
        if (byName.size) {
            targetEl.append(byName.size > 1 ? 'Photos by ' : 'Photo by ');

            [...byName.entries()].forEach(([name, profile], i) => {
                if (i > 0) targetEl.append(i === byName.size - 1 ? ' and ' : ', ');
                targetEl.append(link(profile || UNSPLASH_HOME, name));
            });

            targetEl.append(' on ');
            targetEl.append(link(UNSPLASH_HOME, 'Unsplash'));
        }
        bankLines.forEach((c, i) => {
            targetEl.append(i === 0 ? (byName.size ? ' · Picture: ' : 'Picture: ') : '; ');
            targetEl.append(link(c.sourceUrl, c.credit));
        });
        targetEl.hidden = false;
    }

    // MutationObserver triggers auto-save whenever the slide DOM changes
    if (stageSlideInner) {
        new MutationObserver(() => { scheduleSave(); renderUnsplashCredits(); refreshRevealBadges(); }).observe(stageSlideInner, {
            childList: true, subtree: true, characterData: true, attributes: true,
        });
        renderUnsplashCredits();
    }

    // ── Send to BlueAcorn Education ───────────────────────
    // Presentations, worksheets and quizzes all cross the same bridge, so
    // they share one modal. A kind config supplies the copy, the endpoint,
    // and whatever extra choices that kind needs before it can go.

    const sendModal = $('send-modal');
    const sendBody  = $('send-body');
    const sendTitle = $('send-title');

    function countLabel(n, word) {
        return n ? `${n} ${word}${n === 1 ? '' : 's'}` : '';
    }

    function questionCount(data) {
        return (data?.sections || []).reduce((n, s) => n + (s.questions?.length || 0), 0);
    }

    const SEND_KINDS = {
        presentation: {
            title: 'Send presentation',
            cta: 'Send presentation',
            done: 'Presentation sent',
            ready: () => !!state.presentationId,
            item: () => ({
                title: state.presentationTopic || 'Untitled presentation',
                meta: countLabel(state.slides?.size, 'slide'),
            }),
            endpoint: () => `/api/export/ba-edu/${state.presentationId}`,
            urlOf: (d) => d.presentation_url,
            urlLabel: 'Open presentation in Education',
        },
        worksheet: {
            title: 'Send worksheet',
            cta: 'Send handout',
            done: 'Handout sent',
            ready: () => !!currentWorksheetId,
            item: () => ({
                title: currentWorksheetData?.title || 'Untitled worksheet',
                meta: countLabel(questionCount(currentWorksheetData), 'question'),
            }),
            endpoint: () => `/api/export/ba-edu/handout/${currentWorksheetId}`,
            payload: () => ({
                pdf_id: $('send-dest')?.value,
                title: currentWorksheetData?.title || '',
                with_answers: sendBody?.querySelector('.send-seg-btn.is-on')?.dataset.val === 'with',
            }),
            urlOf: (d) => d.handout_url || d.url,
            urlLabel: 'Open handout in Education',
            // A handout has to land on one of the teacher's Education presentations.
            needsDestination: true,
        },
        quiz: {
            title: 'Send quiz',
            cta: 'Send quiz',
            done: 'Quiz sent',
            ready: () => !!currentQuizId,
            item: () => ({
                title: currentQuizData?.title || 'Untitled quiz',
                meta: countLabel(questionCount(currentQuizData), 'question'),
            }),
            endpoint: () => `/api/export/ba-edu/quiz/${currentQuizId}`,
            urlOf: (d) => d.quiz_url,
            urlLabel: 'Open quiz in Education',
        },
    };

    let sendKind = null;
    let sendLastFocus = null;

    async function showSendModal(kind) {
        const cfg = SEND_KINDS[kind];
        if (!sendModal || !cfg || !cfg.ready()) return;
        sendKind = kind;
        sendLastFocus = document.activeElement;
        if (sendTitle) sendTitle.textContent = cfg.title;
        show(sendModal);
        document.body.style.overflow = 'hidden';
        sendBody.innerHTML = `${stageHTML('waiting')}<p class="send-status">Checking your connection…</p>`;
        try {
            const res = await apiFetch('/api/settings/baedu');
            const data = await res.json();
            (data.linked && !data.needsReauth)
                ? renderSendReady()
                : renderSendBlocked(data.needsReauth);
        } catch {
            sendBody.innerHTML = `${stageHTML('blocked')}<p class="msg msg-error">Could not check your connection. Close this and try again.</p>`;
        }
    }

    function hideSendModal() {
        hide(sendModal);
        document.body.style.overflow = '';
        sendLastFocus?.focus?.();
    }

    // The work itself is the hero: a sheet holding a rough impression of the
    // real content. While it uploads, a scan line reads down the page and the
    // meter runs, so the wait is the thing being sent, not a spinner.
    // phase – waiting | ready | sending | done | blocked
    function stageHTML(phase) {
        const cfg = SEND_KINDS[sendKind] || {};
        const item = cfg.item ? cfg.item() : { title: '', meta: '' };
        // Rough impression of the page – enough to read as "your work", no more.
        // A slide is landscape and holds less, so it gets fewer lines.
        const widths = sendKind === 'presentation'
            ? [82, 64, 44]
            : [92, 74, 88, 60, 84, 70, 46];
        const rows = widths.map(w => `<span class="send-sheet-line" style="width:${w}%"></span>`).join('');
        return `
            <div class="send-stage" data-phase="${phase}" data-kind="${sendKind}">
                <div class="send-sheet">
                    <span class="send-sheet-back s2"></span>
                    <span class="send-sheet-back s1"></span>
                    <div class="send-sheet-face">
                        <span class="send-sheet-kind">${LIST_ICONS[sendKind] || ''}</span>
                        <span class="send-sheet-head"></span>
                        ${rows}
                        <span class="send-sheet-beam"></span>
                    </div>
                    <span class="send-sheet-stamp" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.6 4.6L19 7.2"/></svg>
                    </span>
                </div>
                <div class="send-meter"><span class="send-meter-fill"></span></div>
                <p class="send-item">
                    <strong>${esc(item.title)}</strong>
                    <span id="send-phase-text">${esc(item.meta)}</span>
                </p>
            </div>
        `;
    }

    function setSendPhase(phase, text) {
        sendBody?.querySelector('.send-stage')?.setAttribute('data-phase', phase);
        const textEl = $('send-phase-text');
        if (textEl && text) textEl.textContent = text;
    }

    function renderSendReady() {
        const cfg = SEND_KINDS[sendKind];
        sendBody.innerHTML = `
            ${stageHTML('ready')}
            <div id="send-options"></div>
            <div id="send-msg"></div>
            <button id="send-go" class="btn btn-baedu send-go">${esc(cfg.cta)}</button>
            <button id="send-manage" class="send-quiet-btn" type="button">Manage this connection</button>
        `;
        $('send-go')?.addEventListener('click', doSend);
        $('send-manage')?.addEventListener('click', () => { hideSendModal(); showSettings(); });
        if (cfg.needsDestination) loadSendDestinations();
    }

    function renderSendBlocked(needsReauth) {
        sendBody.innerHTML = `
            ${stageHTML('blocked')}
            <p class="send-status">${needsReauth
                ? 'BlueAcorn Education needs a few more permissions before anything can cross.'
                : 'Connect your BlueAcorn Education account to send work to your classes.'}</p>
            <button id="send-settings" class="btn btn-baedu send-go" type="button">${needsReauth ? 'Grant access' : 'Connect account'}</button>
        `;
        $('send-settings')?.addEventListener('click', () => { hideSendModal(); showSettings(); });
    }

    // Worksheets land on an existing Education presentation, so the far end of
    // the wire becomes whichever one you pick.
    async function loadSendDestinations() {
        const wrap = $('send-options');
        if (!wrap) return;
        wrap.innerHTML = '<p class="send-status">Loading your presentations…</p>';
        try {
            const res = await apiFetch('/api/export/ba-edu/presentations');
            if (!res.ok) {
                if (res.status === 409) {
                    const errData = await res.json().catch(() => ({}));
                    renderSendBlocked(errData.needsReauth);
                    return;
                }
                wrap.innerHTML = '<p class="msg msg-error">Could not load your presentations.</p>';
                return;
            }
            const { presentations } = await res.json();
            if (!presentations?.length) {
                wrap.innerHTML = '<p class="send-status">No presentations in your Education account yet. Create one there first.</p>';
                $('send-go')?.setAttribute('disabled', '');
                return;
            }
            wrap.innerHTML = `
                <label class="send-label" for="send-dest">Attach to</label>
                <select id="send-dest" class="send-select">
                    ${presentations.map(p => `<option value="${esc(p.pdf_id)}">${esc(p.title)}</option>`).join('')}
                </select>
                <span class="send-label">Version</span>
                <div class="send-seg" role="group" aria-label="Handout version">
                    <button type="button" class="send-seg-btn is-on" data-val="without">Student copy</button>
                    <button type="button" class="send-seg-btn" data-val="with">With answers</button>
                </div>
            `;
            wrap.querySelector('.send-seg')?.addEventListener('click', (e) => {
                const btn = e.target.closest('.send-seg-btn');
                if (!btn) return;
                wrap.querySelectorAll('.send-seg-btn').forEach(b => b.classList.toggle('is-on', b === btn));
            });
        } catch {
            wrap.innerHTML = '<p class="msg msg-error">Could not load your presentations.</p>';
        }
    }

    async function doSend() {
        const cfg = SEND_KINDS[sendKind];
        const btn = $('send-go');
        const msgEl = $('send-msg');
        if (msgEl) msgEl.innerHTML = '';
        if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
        const dest = $('send-dest');
        const destName = dest?.options[dest.selectedIndex]?.text;
        setSendPhase('sending', destName ? `Sending to ${destName}` : 'Sending to BlueAcorn Education');

        try {
            const fetchOpts = { method: 'POST' };
            if (cfg.payload) fetchOpts.body = JSON.stringify(cfg.payload());
            const res = await apiFetch(cfg.endpoint(), fetchOpts);
            const data = await res.json();
            if (!res.ok) {
                setSendPhase('ready', cfg.item().meta || 'Ready to send');
                if (msgEl) msgEl.innerHTML = `<p class="msg msg-error">${esc(data.error || 'That did not go through.')}</p>`;
                if (btn) { btn.disabled = false; btn.textContent = cfg.cta; }
                if (res.status === 409) renderSendBlocked(data.needsReauth);
                return;
            }
            renderSendDone(cfg.urlOf(data) || '');
        } catch {
            setSendPhase('ready', cfg.item().meta || 'Ready to send');
            if (msgEl) msgEl.innerHTML = '<p class="msg msg-error">Network error. Try again.</p>';
            if (btn) { btn.disabled = false; btn.textContent = cfg.cta; }
        }
    }

    function renderSendDone(url) {
        const cfg = SEND_KINDS[sendKind];
        sendBody.innerHTML = `
            ${stageHTML('done')}
            <p class="send-done">${esc(cfg.done)}</p>
            ${url ? `<a href="${esc(url)}" target="_blank" rel="noopener" class="btn btn-baedu send-go">${esc(cfg.urlLabel)} →</a>` : ''}
            <button id="send-dismiss" class="send-quiet-btn" type="button">Done</button>
        `;
        $('send-dismiss')?.addEventListener('click', hideSendModal);
    }

    // Kept as named entry points – each toolbar button opens the same modal.
    const showBaeduModal     = () => showSendModal('presentation');
    const showQuizBaeduModal = () => showSendModal('quiz');

    pptBtnExportBaedu?.addEventListener('click', showBaeduModal);
    $('send-close')?.addEventListener('click', hideSendModal);
    sendModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-send-close]')) hideSendModal();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !isShown(sendModal)) return;
        e.preventDefault();
        hideSendModal();
    });

    // ── AI element-level edit ─────────────────────────────
    // Tag every element so the model can target it by data-eid (document order).
    function annotateEids(root) {
        let i = 0;
        root.setAttribute('data-eid', String(i++));
        root.querySelectorAll('*').forEach(el => el.setAttribute('data-eid', String(i++)));
    }

    function stripEids(root) {
        root.removeAttribute('data-eid');
        root.querySelectorAll('[data-eid]').forEach(el => el.removeAttribute('data-eid'));
    }

    // True if the element directly holds visible text (not just child elements)
    function hasOwnText(el) {
        for (const node of el.childNodes) {
            if (node.nodeType === 3 && node.textContent.trim()) return true;
        }
        return false;
    }

    // Compact map of eid → tag.class "text" so the model can target precisely
    function buildEidManifest(root) {
        const lines = [];
        const describe = (el) => {
            const eid = el.getAttribute('data-eid');
            const cls = (el.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean).join('.');
            const tag = el.tagName.toLowerCase() + (cls ? '.' + cls : '');
            let txt = '';
            if (hasOwnText(el)) {
                txt = el.textContent.replace(/\s+/g, ' ').trim().slice(0, 50);
            }
            lines.push(`[${eid}] ${tag}${txt ? ` – "${txt}"` : ''}`);
        };
        describe(root);
        root.querySelectorAll('[data-eid]').forEach(describe);
        return lines.join('\n');
    }

    // Text properties a child element commonly re-declares, so a value set on a
    // container won't visibly apply unless we also set it on the text leaves.
    const INHERITED_TEXT_PROPS = new Set([
        'color', 'font-family', 'font-style', 'font-weight', 'text-transform',
        'letter-spacing', 'text-decoration', 'line-height', 'text-align',
    ]);

    // Merge "prop:val;prop:val" declarations onto an element's inline style.
    // Inherited text properties are also pushed onto text-bearing descendants so
    // a change made on a container (e.g. a <ul>) still wins over each child's own
    // CSS color/font rule.
    function mergeInlineStyle(el, decls) {
        const inherited = [];
        decls.split(';').forEach(d => {
            const idx = d.indexOf(':');
            if (idx < 1) return;
            const prop = d.slice(0, idx).trim().toLowerCase();
            const val  = d.slice(idx + 1).trim();
            if (!prop || !val) return;
            try { el.style.setProperty(prop, val); } catch { return; }
            if (INHERITED_TEXT_PROPS.has(prop)) inherited.push([prop, val]);
        });
        if (inherited.length) {
            el.querySelectorAll('*').forEach(child => {
                if (!hasOwnText(child)) return;
                inherited.forEach(([prop, val]) => {
                    try { child.style.setProperty(prop, val); } catch {}
                });
            });
        }
    }

    const INSERT_POS = { before: 'beforebegin', after: 'afterend', prepend: 'afterbegin', append: 'beforeend' };

    function applySlideOps(root, ops) {
        let applied = 0;
        for (const op of ops) {
            if (!op || op.eid == null) continue;
            const eid = String(op.eid);
            const el = root.getAttribute('data-eid') === eid
                ? root
                : root.querySelector(`[data-eid="${eid}"]`);
            if (!el) continue;
            switch (op.action) {
                case 'set_text':
                    el.textContent = op.text ?? '';
                    applied++;
                    break;
                case 'set_html':
                    el.innerHTML = op.html ?? '';
                    applied++;
                    break;
                case 'set_style':
                    if (op.style) { mergeInlineStyle(el, op.style); applied++; }
                    break;
                case 'remove':
                    if (el !== root) { el.remove(); applied++; }
                    break;
                case 'insert_html':
                    if (op.html) {
                        el.insertAdjacentHTML(INSERT_POS[op.position] || 'afterend', op.html);
                        applied++;
                    }
                    break;
            }
        }
        return applied;
    }

    // ── AI editing: Ask, preview, Copilot and Review ─────────
    // Every AI change, wherever it was asked for, becomes a "turn": the request,
    // and one result per slide it changed (the slide before and after). A turn
    // is previewed on the stage until the teacher keeps or discards it, and
    // once kept it can still be undone from the Copilot log on its own. Ask
    // (the box by the selection), the Copilot composer and Review's fixes all
    // feed the same log, which is also the conversation a follow-up such as
    // "a bit smaller" is read against.

    const AI_BATCH_CAP = 5;   // matches BATCH_EDIT_CAP in utils/aiBatch.js
    const ai = {
        presId: null,
        turns: [],
        nextId: 1,
        preview: null,        // { turn, index } while a change waits on keep/discard
        handEdits: new Map(), // slide id -> count of edits made by hand, for undo's warning
        dismissed: { slide: null, sel: null }, // context the teacher x'd out, until it changes
        busy: false,
        review: { items: [], ran: false, loading: false, skipped: 0 },
    };

    const aiAskChip = $('ai-ask-chip');
    const aiAsk = $('ai-ask');
    const aiAskForm = $('ai-ask-form');
    const aiAskInput = $('ai-ask-input');
    const aiAskLabel = $('ai-ask-label');
    const aiAskMeta = $('ai-ask-meta');
    const aiAskChips = $('ai-ask-chips');
    const aiAskStatus = $('ai-ask-status');
    const aiPreviewBar = $('ai-preview-bar');
    const aiPreviewText = $('ai-preview-text');
    const aiPreviewNav = $('ai-preview-nav');
    const aiPreviewCount = $('ai-preview-count');
    const aiPanel = $('ai-panel');
    const aiCopilotBtn = $('ppt-btn-copilot');
    const aiLog = $('ai-log');
    const aiComposer = $('ai-composer');
    const aiInput = $('ai-input');
    const aiSend = $('ai-send');
    const aiReviewList = $('ai-review-list');
    const aiReviewRun = $('ai-review-run');
    const aiReviewCount = $('ai-review-count');

    const IS_MAC = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
    document.querySelectorAll('[data-mod-key]').forEach(k => { k.textContent = IS_MAC ? '⌘K' : 'Ctrl K'; });

    // A different deck starts a fresh conversation: turns hold slide ids and
    // undo snapshots that mean nothing outside the deck they were made in.
    function aiSyncDeck() {
        if (ai.presId === state.presentationId) return;
        if (ai.preview) endAiPreview();
        ai.presId = state.presentationId;
        ai.turns = [];
        ai.handEdits = new Map();
        ai.review = { items: [], ran: false, loading: false, skipped: 0 };
        renderAiLog();
        renderReview();
    }

    function slideEntryById(slideId) {
        for (const [pos, s] of state.slides) if (s.id === slideId) return { pos, s };
        return null;
    }

    const isModelSlide = s => !!s && s.json_schema_version === 1;
    // [position, slide] in deck order.
    const deckSlides = () => [...state.slides.entries()].sort((a, b) => a[0] - b[0]);
    const cloneJson = v => JSON.parse(JSON.stringify(v));

    function modelOf(s) {
        return {
            contents: (s.contents_json && typeof s.contents_json === 'object') ? s.contents_json : {},
            elements: Array.isArray(s.elements_json) ? s.elements_json : [],
        };
    }

    // A slide's name in the log, the deck outline and Review: its heading.
    function slideTitle(s) {
        if (isModelSlide(s)) {
            const { contents, elements } = modelOf(s);
            const heading = elements.find(e => e && e.type === 'heading' && e.visible !== false);
            const rec = heading && contents[heading.content_id];
            if (rec && typeof rec.text === 'string') return plainText(rec.text).slice(0, 80);
        }
        const div = document.createElement('div');
        div.innerHTML = s.html || '';
        const h = div.querySelector('h1, h2, h3');
        return h ? h.textContent.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
    }

    function plainText(html) {
        const div = document.createElement('div');
        div.innerHTML = String(html || '');
        return div.textContent.replace(/\s+/g, ' ').trim();
    }

    // Which elements a result changed, by diffing the two models rather than
    // reading the operations: ids can be renamed on add, and an operation that
    // was rejected changed nothing.
    function changedElementIds(before, after) {
        const prev = new Map(before.elements.map(e => [e.id, e]));
        return after.elements.filter(e => {
            const old = prev.get(e.id);
            if (!old) return true;
            if (JSON.stringify(old) !== JSON.stringify(e)) return true;
            return JSON.stringify(before.contents[e.content_id] || null) !== JSON.stringify(after.contents[e.content_id] || null);
        }).map(e => e.id);
    }

    // Every text box made big enough for its text, measured in this browser
    // (slideTextFit.js): an AI edit or a generated slide can size a box for
    // text it cannot see, and the text then runs over what is underneath.
    // Given the slide as it was (`beforeElements`), a card the edit completed is
    // first laid out like its sibling cards.
    function fitToText(model, beforeElements = null) {
        let elements = model.elements;
        if (beforeElements) {
            const aligned = SlideTextFit.alignCompleted(beforeElements, elements);
            if (aligned.changed) elements = aligned.elements;
        }
        const out = SlideTextFit.fit(model.contents, elements, { theme: state.selectedTheme });
        if (out.changed) elements = out.elements;
        return elements === model.elements ? model : { ...model, elements };
    }

    // The model as it would be after `ops`, as a result the preview can show.
    // Null when the operations changed nothing: compared against the model put
    // through no operations, so writing back the same text does not count.
    function modelResult(slideId, s, ops, summary) {
        const before = cloneJson(modelOf(s));
        const out = SlideModelEditor.applyModelOps(before.contents, before.elements, ops);
        if (!out.changed) return null;
        const base = SlideModelEditor.applyModelOps(before.contents, before.elements, []);
        if (JSON.stringify([base.contents, base.elements]) === JSON.stringify([out.contents, out.elements])) return null;
        const after = fitToText({ contents: out.contents, elements: out.elements }, before.elements);
        return {
            slideId,
            kind: 'model',
            before: { ...before, html: s.html },
            after: { ...after, html: SlideModelRenderer.buildHtml(after.contents, after.elements) },
            changedIds: changedElementIds({ contents: base.contents, elements: base.elements }, after),
            summary: summary || '',
            skippedAlign: (out.rejected || []).length > 0,
        };
    }

    // What the teacher has selected on the stage, as the edit should see it.
    function aiSelection() {
        const els = multiSelected.size ? multiSelectList() : (state.selectedEl ? [state.selectedEl] : []);
        const entry = slideEntryById(state.activeSlideId);
        if (!els.length || !entry) return null;
        if (isModelSlide(entry.s)) {
            const ids = els.map(el => el.closest('[data-el-id]')?.dataset.elId).filter(Boolean);
            if (!ids.length) return null;
            const types = ids.map(id => (modelOf(entry.s).elements.find(e => e.id === id) || {}).type || 'text');
            return { ids: [...new Set(ids)], types, text: '' };
        }
        const text = els.map(el => el.textContent.replace(/\s+/g, ' ').trim()).join(' / ').slice(0, 200);
        const types = els.map(el => (el.tagName === 'IMG' || el.querySelector('img') && !el.textContent.trim() ? 'image' : /^H[1-3]$/.test(el.tagName) ? 'heading' : 'text'));
        return { ids: [], types, text };
    }

    const TYPE_NAMES = { heading: 'Heading', image: 'Picture', shape: 'Shape', bullets: 'Bullet list', steps: 'Steps', text: 'Text box' };

    function selectionLabel(sel) {
        if (!sel) return 'This slide';
        if (sel.types.length > 1) return `${sel.types.length} elements`;
        return TYPE_NAMES[sel.types[0]] || 'Text box';
    }

    // Quick requests offered in the Ask box, by what is selected.
    function askSuggestions(sel) {
        if (!sel) return ['Less text', 'Add a picture', 'Tidy the layout', 'Add a check question'];
        if (sel.types.length > 1) return ['Line them up', 'Make them match', 'Space them evenly'];
        const t = sel.types[0];
        if (t === 'image') return ['Find a better picture', 'Use a diagram instead', 'Make it smaller'];
        if (t === 'shape') return ['Softer colour', 'Round the corners', 'Make it smaller'];
        if (t === 'heading') return ['Shorter', 'Make it a question', 'Bigger'];
        return ['Shorten', 'Simpler words', 'Bold the key words', 'Turn into a question'];
    }

    // The conversation the server reads a follow-up against.
    function aiHistory() {
        return ai.turns
            .filter(t => t.status === 'kept' || t.status === 'preview')
            .slice(-4)
            .map(t => ({ instruction: t.instruction, summary: t.summary || '' }));
    }

    function newTurn(instruction, fields) {
        const turn = {
            id: ai.nextId++, instruction, status: 'working', results: [],
            summary: '', error: '', cost: 0, slideIds: [], origin: 'ask', ...fields,
        };
        ai.turns.push(turn);
        renderAiLog();
        return turn;
    }

    function friendlyError(data, fallback) {
        if (data && data.error === 'quota_exceeded') return data.message || 'You have no AI edits left this month.';
        return (data && (data.message || data.error)) || fallback;
    }

    // ── Running edits ──

    // One slide, through the endpoint that matches how the slide is stored.
    // The html path can only edit the slide that is on the stage: it tags the
    // live markup so the operations it gets back can be found again.
    async function runSlideEdit(slideId, instruction, focus) {
        const entry = slideEntryById(slideId);
        if (!entry) throw new Error('That slide is no longer in the deck');
        if (state.activeSlideId === slideId) syncModelFromCurrentDom();
        const s = slideEntryById(slideId).s;

        if (isModelSlide(s)) {
            const { contents, elements } = modelOf(s);
            const res = await apiFetch(`/api/slides/${slideId}/edit/model`, {
                method: 'POST',
                body: JSON.stringify({
                    instruction, contents_json: contents, elements_json: elements,
                    focus: focus?.ids || [], history: aiHistory(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(friendlyError(data, 'The edit failed'));
            const missing = Array.isArray(data.missing_pictures) ? data.missing_pictures : [];
            const result = modelResult(slideId, s, Array.isArray(data.operations) ? data.operations : [], data.summary);
            if (!result && missing.length) {
                throw new Error(`Couldn’t find a picture for “${missing[0]}”. Try describing it another way.`);
            }
            if (result && missing.length) result.summary = `${result.summary ? `${result.summary} ` : ''}(No picture found for “${missing[0]}”.)`;
            return { results: result ? [result] : [], cost: 1 };
        }

        if (state.activeSlideId !== slideId) throw new Error('Open this slide to edit it with AI');
        const slideEl = stageSlideInner.querySelector('.slide');
        if (!slideEl) throw new Error('Nothing to edit on this slide');
        const beforeHtml = getSlideHtml();
        const work = slideEl.cloneNode(true);
        annotateEids(work);
        const res = await apiFetch(`/api/slides/${slideId}/edit`, {
            method: 'POST',
            body: JSON.stringify({ instruction, html: work.outerHTML, manifest: buildEidManifest(work), focus_text: focus?.text || '' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(friendlyError(data, 'The edit failed'));
        const ops = Array.isArray(data.operations) ? data.operations : [];
        ops.forEach(op => {
            const el = op && op.eid != null && work.querySelector(`[data-eid="${String(op.eid)}"]`);
            if (el && op.action !== 'remove') el.setAttribute('data-ai-changed', '');
        });
        if (!applySlideOps(work, ops)) return { results: [], cost: 1 };
        stripEids(work);
        const previewHtml = work.outerHTML;
        work.querySelectorAll('[data-ai-changed]').forEach(el => el.removeAttribute('data-ai-changed'));
        return {
            results: [{
                slideId, kind: 'html',
                before: { html: beforeHtml },
                after: { html: work.outerHTML, previewHtml },
                changedIds: [], summary: '',
            }],
            cost: 1,
        };
    }

    // Several model slides in one request. The server charges one edit per
    // slide that came back changed, capped.
    async function runBatchEdit(slideIds, instruction) {
        if (state.activeSlideId && slideIds.includes(state.activeSlideId)) syncModelFromCurrentDom();
        const entries = slideIds.map(slideEntryById).filter(Boolean);
        const slides = entries.map(({ pos, s }) => ({ id: s.id, position: pos, ...(() => { const m = modelOf(s); return { contents_json: m.contents, elements_json: m.elements }; })() }));
        const outline = deckSlides().map(([, s]) => slideTitle(s));
        const res = await apiFetch('/api/slides/ai/batch', {
            method: 'POST',
            body: JSON.stringify({ instruction, slides, history: aiHistory(), outline, total: state.slides.size }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(friendlyError(data, 'The edit failed'));
        const results = [];
        let failed = 0;
        (Array.isArray(data.results) ? data.results : []).forEach(r => {
            if (r.error) { failed += 1; return; }
            const entry = slideEntryById(r.slide_id);
            if (!entry) return;
            const result = modelResult(r.slide_id, entry.s, Array.isArray(r.operations) ? r.operations : [], r.summary);
            if (result) results.push(result);
        });
        return { results, cost: Number(data.charged) || 0, failed };
    }

    // Asks for a change and opens it as a preview. A change still waiting from
    // before is kept first: asking again is how "now a bit smaller" builds on
    // what is on the screen.
    async function requestAiEdit({ instruction, slideIds, focus = null, origin = 'ask', reviewItems = null }) {
        aiSyncDeck();
        instruction = String(instruction || '').trim();
        if (!instruction || !slideIds.length || ai.busy) return null;
        if (ai.preview) keepAiPreview();
        ai.busy = true;
        syncAiBusy();
        const turn = newTurn(instruction, { slideIds, origin, focus, focusLabel: focus ? selectionLabel(focus) : '', reviewItems });
        showAiWorking(turn);
        try {
            const batch = slideIds.length > 1;
            const out = batch ? await runBatchEdit(slideIds, instruction) : await runSlideEdit(slideIds[0], instruction, focus);
            turn.results = out.results;
            turn.cost = out.cost;
            turn.failed = out.failed || 0;
            if (!out.results.length) {
                turn.status = 'nochange';
                turn.summary = batch ? 'None of these slides needed changing.' : 'Nothing changed. Try saying it another way.';
                hideAiWorking();
            } else {
                turn.summary = summariseTurn(turn);
                startAiPreview(turn);
            }
        } catch (err) {
            turn.status = 'error';
            turn.error = err.message === 'Not authenticated' ? 'Sign in again to keep editing.' : (err.message || 'Network error');
            hideAiWorking();
        } finally {
            ai.busy = false;
            syncAiBusy();
            renderAiLog();
        }
        return turn;
    }

    // ── The copilot's agent ──
    // A message from the chat goes to the server's agent (utils/slideAgent.js),
    // which reads and edits the deck in as many steps as the message needs.
    // Its steps stream into the turn as it works; the slides it changed come
    // back at the end and are reviewed like any other change.

    // The conversation so far, as the agent reads it.
    function agentHistory() {
        const out = [];
        ai.turns.filter(t => t.status !== 'working').slice(-6).forEach(t => {
            out.push({ role: 'user', text: t.instruction });
            const said = t.reply || t.summary || t.error || '';
            const fate = t.status === 'kept' ? ' (the teacher kept these changes)'
                : t.status === 'discarded' ? ' (the teacher discarded these changes)'
                : t.status === 'undone' ? ' (the teacher undid these changes)' : '';
            const shape = !t.structure ? ''
                : t.structure.status === 'kept' ? ' (the teacher applied the new slide order)'
                : t.structure.status === 'discarded' ? ' (the teacher discarded the new slide order)'
                : t.structure.status === 'partial' ? ' (only part of the new slide order was applied)'
                : ' (the new slide order is waiting for the teacher)';
            if (said || fate || shape) out.push({ role: 'assistant', text: `${said}${fate}${shape}` });
        });
        return out;
    }

    async function runAgentTurn(message) {
        aiSyncDeck();
        message = String(message || '').trim();
        if (!message || ai.busy || !state.slides.size) return null;
        if (ai.preview) keepAiPreview();
        if (state.activeSlideId) syncModelFromCurrentDom();
        const history = agentHistory();
        const ctx = aiContext();
        const sel = ctx.selection;
        ai.busy = true;
        syncAiBusy();
        const turn = newTurn(message, { origin: 'agent', slideIds: [], steps: [], focusLabel: sel ? selectionLabel(sel) : '' });
        if (aiPanel.hidden) openAiPanel('chat');

        // What was sent, to tell afterwards whether a slide moved on meanwhile.
        const sent = new Map();
        const sentOrder = deckSlides().map(([, s]) => s.id);
        const slides = deckSlides().map(([pos, s]) => {
            const model = isModelSlide(s);
            const m = modelOf(s);
            sent.set(s.id, JSON.stringify([m.contents, m.elements]));
            return model
                ? { id: s.id, position: pos, contents_json: m.contents, elements_json: m.elements }
                : { id: s.id, position: pos, html: s.html || '' };
        });

        try {
            const res = await apiFetch('/api/slides/ai/agent', {
                method: 'POST',
                body: JSON.stringify({
                    message, slides, history,
                    current_slide_id: ctx.slide ? ctx.slide.s.id : null,
                    selection: sel?.ids || [],
                    selection_text: sel && !sel.ids.length ? sel.text : '',
                    title: pptChromeTitle?.textContent || '',
                }),
            });
            if (!res.ok || !res.body) {
                const data = await res.json().catch(() => ({}));
                throw new Error(friendlyError(data, 'The copilot couldn’t do that'));
            }
            let done = null;
            await readNdjson(res, (line) => {
                if (line.type === 'step' && line.step) {
                    const i = turn.steps.findIndex(x => x.id === line.step.id);
                    if (i >= 0) turn.steps[i] = line.step; else turn.steps.push(line.step);
                    renderAiLog();
                } else if (line.type === 'quota') {
                    showQuotaModal(line.body || {});
                } else if (line.type === 'done') {
                    done = line;
                } else if (line.type === 'error') {
                    throw new Error(line.error || 'The copilot couldn’t do that');
                }
            });
            if (!done) throw new Error('The copilot stopped before it finished. Try again.');

            turn.reply = done.reply || '';
            turn.cost = Number(done.charged) || 0;
            turn.results = (Array.isArray(done.results) ? done.results : []).map(r => {
                const entry = slideEntryById(r.slide_id);
                if (!entry || !isModelSlide(entry.s)) return null;
                const now = modelOf(entry.s);
                const before = { ...cloneJson(now), html: entry.s.html };
                const base = SlideModelEditor.applyModelOps(before.contents, before.elements, []);
                const after = fitToText({ contents: r.contents_json, elements: r.elements_json }, before.elements);
                after.html = SlideModelRenderer.buildHtml(after.contents, after.elements);
                return {
                    slideId: r.slide_id, kind: 'model', before, after,
                    changedIds: changedElementIds({ contents: base.contents, elements: base.elements }, after),
                    summary: r.summary || '',
                    stale: sent.get(r.slide_id) !== JSON.stringify([now.contents, now.elements]),
                };
            }).filter(Boolean).sort((a, b) => (slideEntryById(a.slideId)?.pos || 0) - (slideEntryById(b.slideId)?.pos || 0));
            turn.slideIds = turn.results.map(r => r.slideId);
            // Slides added, removed or moved: nothing happens until the teacher applies it.
            if (done.structure && Array.isArray(done.structure.order)) {
                turn.structure = { ...done.structure, sentOrder, status: 'pending', error: '' };
            }
            if (turn.results.length) {
                turn.summary = turn.reply;
                startAiPreview(turn);
            } else {
                turn.status = 'answered';
            }
        } catch (err) {
            turn.status = 'error';
            turn.error = err.message === 'Not authenticated' ? 'Sign in again to keep going.' : (err.message || 'Network error');
        } finally {
            ai.busy = false;
            syncAiBusy();
            renderAiLog();
        }
        return turn;
    }

    // Calls onLine for each JSON line of a streamed response; blank lines are
    // the server keeping the connection open.
    async function readNdjson(res, onLine) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
            const { done, value } = await reader.read();
            if (value) buffer += decoder.decode(value, { stream: !done });
            let nl;
            while ((nl = buffer.indexOf('\n')) >= 0) {
                const text = buffer.slice(0, nl).trim();
                buffer = buffer.slice(nl + 1);
                if (!text) continue;
                let line;
                try { line = JSON.parse(text); } catch { continue; }
                onLine(line);
            }
            if (done) break;
        }
        if (buffer.trim()) { try { onLine(JSON.parse(buffer.trim())); } catch { /* cut short */ } }
    }

    function summariseTurn(turn) {
        if (turn.results.length === 1) {
            const r = turn.results[0];
            return r.summary || `Changed slide ${slideEntryById(r.slideId)?.pos || ''}`.trim();
        }
        const one = turn.results.find(r => r.summary)?.summary;
        return `${turn.results.length} slides changed${one ? ` – e.g. ${one.charAt(0).toLowerCase()}${one.slice(1)}` : ''}`;
    }

    // ── Preview ──

    function previewPositions(turn) {
        return turn.results.map(r => slideEntryById(r.slideId)?.pos).filter(Boolean);
    }

    // A change over several slides is reviewed slide by slide: each can be
    // seen before and after, and left out of what is kept. A slide edited by
    // hand while the AI worked starts left out – keeping it would undo that.
    function startAiPreview(turn) {
        turn.status = 'preview';
        turn.results.forEach(r => { if (r.include === undefined) r.include = !r.stale; });
        ai.preview = { turn, index: 0, view: 'after' };
        // Out of the chat box, so ← → and B step through the review.
        if (document.activeElement === aiInput) aiInput.blur();
        const onStage = turn.results.findIndex(r => r.slideId === state.activeSlideId);
        if (onStage >= 0) {
            ai.preview.index = onStage;
            renderAiPreviewStage();
        } else {
            goToPreviewSlide(0);
        }
        renderAiPreviewBar();
        markPendingThumbs();
    }

    // Brings a changed slide onto the stage. selectSlide draws the slide as it
    // is; aiAfterSlideSelected then draws the proposal over it.
    function goToPreviewSlide(index) {
        const p = ai.preview;
        if (!p) return;
        p.index = (index + p.turn.results.length) % p.turn.results.length;
        const r = p.turn.results[p.index];
        const entry = slideEntryById(r.slideId);
        if (!entry) return;
        if (state.activeSlideId === r.slideId) renderAiPreviewStage();
        else selectSlide(entry.s.id, entry.s.html, entry.pos);
        renderAiPreviewBar();
    }

    function currentPreviewResult() {
        const p = ai.preview;
        if (!p) return null;
        return p.turn.results.find(r => r.slideId === state.activeSlideId) || null;
    }

    function renderAiPreviewStage() {
        const r = currentPreviewResult();
        if (!r) return;
        pauseAutoSave();
        deactivateDragEditor();
        const before = ai.preview.view === 'before';
        const v = before ? r.before : r.after;
        if (r.kind === 'model') SlideModelRenderer.renderSlideHtml(stageSlideInner, v.contents, v.elements);
        else SlideRenderer.renderSlideHtml(stageSlideInner, before ? v.html : (v.previewHtml || v.html));
        applyTheme(state.selectedTheme);
        scaleStage();
        if (!before) {
            r.changedIds.forEach(id => {
                stageSlideInner.querySelectorAll(`[data-el-id="${CSS.escape(id)}"]`).forEach(el => el.classList.add('ai-changed'));
            });
        }
        $('ppt-stage')?.classList.add('is-ai-preview');
        placeAskChip();
    }

    function renderAiPreviewBar() {
        const p = ai.preview;
        if (!p || !aiPreviewBar) return;
        const many = p.turn.results.length > 1;
        const r = currentPreviewResult();
        const pos = r ? slideEntryById(r.slideId)?.pos : null;
        const kept = p.turn.results.filter(x => x.include).length;
        aiPreviewText.textContent = r
            ? (many ? `Slide ${pos}${r.summary ? ` · ${r.summary}` : ''}` : (p.turn.summary || 'Here is the change'))
            : `Changes waiting on slides ${previewPositions(p.turn).join(', ')}`;
        aiPreviewText.title = aiPreviewText.textContent;
        $('ai-preview-view').hidden = !r;
        $('ai-preview-view').querySelectorAll('[data-view]').forEach(b => b.classList.toggle('on', b.dataset.view === p.view));
        if (aiPreviewNav) aiPreviewNav.hidden = !many;
        if (aiPreviewCount && many) aiPreviewCount.textContent = `${r ? p.turn.results.indexOf(r) + 1 : '–'} of ${p.turn.results.length}`;
        const include = $('ai-preview-include');
        include.hidden = !many || !r;
        if (r) include.querySelector('input').checked = !!r.include;
        const keep = $('ai-preview-keep');
        keep.textContent = many ? (kept === p.turn.results.length ? `Keep all ${kept}` : `Keep ${kept} of ${p.turn.results.length}`) : 'Keep';
        keep.disabled = !kept;
        keep.hidden = false;
        $('ai-preview-retry').hidden = many || p.turn.origin === 'rule' || p.turn.origin === 'agent';
        $('ai-preview-discard').hidden = false;
        aiPreviewBar.classList.remove('is-working');
        show(aiPreviewBar);
    }

    function setPreviewView(view) {
        if (!ai.preview || !currentPreviewResult()) return;
        ai.preview.view = view;
        renderAiPreviewStage();
        renderAiPreviewBar();
    }

    function setResultIncluded(r, on) {
        r.include = !!on;
        renderAiPreviewBar();
        renderAiLog();
    }

    function showAiWorking(turn) {
        if (!aiPreviewBar) return;
        const n = turn.slideIds.length;
        aiPreviewText.textContent = n > 1 ? `Working on ${n} slides…` : (turn.origin === 'review' ? 'Fixing it…' : 'Working on it…');
        if (aiPreviewNav) aiPreviewNav.hidden = true;
        $('ai-preview-retry').hidden = true;
        $('ai-preview-discard').hidden = true;
        $('ai-preview-keep').hidden = true;
        aiPreviewBar.classList.add('is-working');
        show(aiPreviewBar);
    }

    function hideAiWorking() {
        $('ai-preview-keep').hidden = false;
        aiPreviewBar?.classList.remove('is-working');
        if (!ai.preview) hide(aiPreviewBar);
    }

    // Puts the stage back to the slide as stored, editable again.
    function redrawActiveSlide() {
        const entry = slideEntryById(state.activeSlideId);
        $('ppt-stage')?.classList.remove('is-ai-preview');
        if (!entry) return;
        deactivateDragEditor();
        renderSlideByPosition(stageSlideInner, entry.pos, entry.s.html);
        applyTheme(state.selectedTheme);
        scaleStage();
        activateDragEditor();
        resumeAutoSave();
    }

    function endAiPreview() {
        const wasOnStage = !!currentPreviewResult();
        ai.preview = null;
        hide(aiPreviewBar);
        $('ai-preview-keep').hidden = false;
        markPendingThumbs();
        if (wasOnStage) redrawActiveSlide();
        else $('ppt-stage')?.classList.remove('is-ai-preview');
    }

    function discardAiPreview() {
        const p = ai.preview;
        if (!p) return;
        p.turn.status = 'discarded';
        endAiPreview();
        renderAiLog();
    }

    // Writes a set of slide versions into the deck: state, thumbnails, the
    // stage when one of them is on it, and the server. Used by keep and undo.
    function writeSlideVersions(versions, { history = true } = {}) {
        versions.forEach(({ slideId, kind, version, previous }) => {
            const entry = slideEntryById(slideId);
            if (!entry) return;
            const next = kind === 'model'
                ? { ...entry.s, contents_json: cloneJson(version.contents), elements_json: cloneJson(version.elements), html: version.html }
                : { ...entry.s, html: version.html };
            state.slides.set(entry.pos, next);
            if (slideId === state.activeSlideId && history && previous) {
                // Ctrl+Z on the stage still steps back over an AI change.
                undoStack.push(previous.html);
                if (undoStack.length > MAX_HISTORY) undoStack.shift();
                redoStack.length = 0;
                syncUndoButtons();
            }
            updateThumb(entry.pos, slideId, next.html);
        });
        const active = versions.find(v => v.slideId === state.activeSlideId);
        if (active) {
            redrawActiveSlide();
            savePptDirect();
        }
        versions.filter(v => v.slideId !== state.activeSlideId).forEach(v => saveSlideById(v.slideId));
        slidePanel.querySelectorAll('.slide-thumb').forEach(t => t.classList.toggle('active', t.dataset.slideId === state.activeSlideId));
    }

    // Saves a slide that is not on the stage, straight from state.
    async function saveSlideById(slideId) {
        const entry = slideEntryById(slideId);
        if (!entry) return;
        const s = entry.s;
        const model = isModelSlide(s);
        const payload = model
            ? { html: s.html, model: { contents_json: s.contents_json, elements_json: s.elements_json, json_schema_version: s.json_schema_version } }
            : { html: s.html };
        try {
            const res = await apiFetch(`/api/slides/${slideId}/${model ? 'model' : 'html'}`, { method: 'PUT', body: JSON.stringify(payload) });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(stageError, data.error || `Slide ${entry.pos} could not be saved`);
            }
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(stageError, `Slide ${entry.pos} could not be saved`);
        }
    }

    // Keeps the slides left ticked; the rest stay as they were.
    function keepAiPreview() {
        const p = ai.preview;
        if (!p) return;
        const turn = p.turn;
        const keep = turn.results.filter(r => r.include !== false);
        if (!keep.length) { discardAiPreview(); return; }
        turn.skipped = turn.results.length - keep.length;
        turn.results = keep;
        ai.preview = null;
        hide(aiPreviewBar);
        $('ppt-stage')?.classList.remove('is-ai-preview');
        turn.status = 'kept';
        turn.results.forEach(r => { r.handEditsAtKeep = ai.handEdits.get(r.slideId) || 0; });
        writeSlideVersions(turn.results.map(r => ({ slideId: r.slideId, kind: r.kind, version: r.after, previous: r.before })));
        markPendingThumbs();
        if (turn.reviewItems) { turn.reviewItems.forEach(i => { i.done = true; }); renderReview(); }
        if (turn.results.some(r => r.skippedAlign)) showToast('Kept. One alignment change was skipped.', 3000);
        renderAiLog();
    }

    function retryAiPreview() {
        const p = ai.preview;
        if (!p) return;
        const { instruction, slideIds, focus, origin, reviewItems } = p.turn;
        discardAiPreview();
        requestAiEdit({ instruction, slideIds, focus, origin, reviewItems });
    }

    // Undoing a kept turn puts its slides back as they were before it. Anything
    // done to those slides since goes too, so that is said first.
    function undoAiTurn(turn) {
        if (turn.status !== 'kept') return;
        const later = ai.turns.filter(t => t.id > turn.id && t.status === 'kept'
            && t.results.some(r => turn.results.some(x => x.slideId === r.slideId)));
        const touched = turn.results.some(r => (ai.handEdits.get(r.slideId) || 0) !== r.handEditsAtKeep);
        if ((later.length || touched) && !confirm('Those slides have changed since. Undoing puts them back as they were before this change, and anything done to them after it is lost too. Undo anyway?')) return;
        if (ai.preview) discardAiPreview();
        writeSlideVersions(turn.results.map(r => ({ slideId: r.slideId, kind: r.kind, version: r.before, previous: null })), { history: false });
        turn.status = 'undone';
        later.forEach(t => { t.status = 'undone'; });
        renderAiLog();
    }

    // Every edit made by hand passes through commitHistory, which reports it
    // here: undoing an AI change warns only when there is other work to lose.
    function aiNoteHandEdit() {
        if (state.activeSlideId) ai.handEdits.set(state.activeSlideId, (ai.handEdits.get(state.activeSlideId) || 0) + 1);
    }

    // The thumbnails of slides with a change waiting carry a mark.
    function markPendingThumbs() {
        const ids = new Set(ai.preview ? ai.preview.turn.results.map(r => r.slideId) : []);
        slidePanel?.querySelectorAll('.slide-thumb').forEach(t => t.classList.toggle('ai-pending', ids.has(t.dataset.slideId)));
    }

    // Called from selectSlide on its way in and out. Leaving a previewed slide
    // needs nothing – selectSlide redraws the stage – and arriving on one draws
    // the proposal rather than the stored slide.
    function aiAfterSlideSelected() {
        closeAsk();
        renderAiContext();
        $('ppt-stage')?.classList.remove('is-ai-preview');
        if (!ai.preview) return;
        if (currentPreviewResult()) renderAiPreviewStage();
        renderAiPreviewBar();
        markPendingThumbs();
    }

    $('ai-preview-keep')?.addEventListener('click', keepAiPreview);
    $('ai-preview-view')?.querySelectorAll('[data-view]').forEach(b => b.addEventListener('click', () => setPreviewView(b.dataset.view)));
    $('ai-preview-include')?.querySelector('input')?.addEventListener('change', (e) => {
        const r = currentPreviewResult();
        if (r) setResultIncluded(r, e.target.checked);
    });
    $('ai-preview-discard')?.addEventListener('click', discardAiPreview);
    $('ai-preview-retry')?.addEventListener('click', retryAiPreview);
    $('ai-preview-prev')?.addEventListener('click', () => {
        const r = currentPreviewResult();
        goToPreviewSlide((r ? ai.preview.turn.results.indexOf(r) : 0) - 1);
    });
    $('ai-preview-next')?.addEventListener('click', () => {
        const r = currentPreviewResult();
        goToPreviewSlide((r ? ai.preview.turn.results.indexOf(r) : -1) + 1);
    });

    // ── Ask: the box by the selection ──

    let askFocus = null;

    function openAsk(prefill = '') {
        if (!aiAsk || !state.activeSlideId || state.presenting || isDesigningTemplate() || !isShown(pptEditor)) return;
        aiSyncDeck();
        // The quick box is for the element in hand; anything wider is the copilot's.
        askFocus = ai.preview ? null : aiSelection();
        if (!askFocus) return;
        aiAskLabel.textContent = `Editing: ${selectionLabel(askFocus)}`;
        const entry = slideEntryById(state.activeSlideId);
        aiAskMeta.textContent = askFocus ? '' : `slide ${entry?.pos || ''}`;
        aiAskChips.innerHTML = '';
        askSuggestions(askFocus).forEach(label => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'ai-ask-sugg';
            b.textContent = label;
            b.addEventListener('click', () => submitAsk(label));
            aiAskChips.appendChild(b);
        });
        aiAskInput.value = prefill;
        aiAskStatus.hidden = true;
        aiAskForm.classList.remove('is-busy');
        aiAsk.hidden = false;
        placeAsk();
        aiAskInput.focus();
    }

    function closeAsk() {
        if (aiAsk) aiAsk.hidden = true;
    }

    // Under the selection when there is room, above it when not; centred over
    // the top of the slide when nothing is selected.
    function placeAsk() {
        const stage = $('ppt-stage');
        if (!stage || aiAsk.hidden) return;
        const sr = stage.getBoundingClientRect();
        const cr = stageCanvas.getBoundingClientRect();
        const box = askFocus && selBox && !selBox.hidden ? selBox.getBoundingClientRect() : null;
        const w = Math.min(400, sr.width - 24);
        aiAsk.style.width = w + 'px';
        const h = aiAsk.offsetHeight || 150;
        let left; let top;
        if (box && box.width) {
            left = box.left - sr.left;
            top = box.bottom - sr.top + 10;
            if (top + h > sr.height - 8) top = Math.max(8, box.top - sr.top - h - 10);
        } else {
            left = cr.left - sr.left + (cr.width - w) / 2;
            top = cr.top - sr.top + 16;
        }
        aiAsk.style.left = Math.max(12, Math.min(left, sr.width - w - 12)) + 'px';
        aiAsk.style.top = Math.max(8, top) + 'px';
    }

    // The box is about the element, but its words can still reach further
    // ("do the same on slide 6") – decided on send, like the copilot's.
    async function submitAsk(text) {
        const instruction = String(text || '').trim();
        if (!instruction || ai.busy) return;
        aiAskForm.classList.add('is-busy');
        aiAskStatus.textContent = 'Working on it…';
        aiAskStatus.hidden = false;
        const t = resolveTarget(instruction, askFocus);
        if (!(t.sure && (t.kind === 'selection' || t.kind === 'slide'))) {
            // Wider than this element: the copilot takes it, and shows its work there.
            aiAskForm.classList.remove('is-busy');
            closeAsk();
            runAgentTurn(instruction);
            return;
        }
        const turn = await sendAiRequest(instruction, { sel: askFocus, origin: 'ask' });
        aiAskForm.classList.remove('is-busy');
        if (turn && (turn.status === 'error' || turn.status === 'nochange')) {
            aiAskStatus.textContent = turn.error || turn.summary;
            aiAskInput.focus();
            return;
        }
        closeAsk();
    }

    aiAskForm?.addEventListener('submit', (e) => { e.preventDefault(); submitAsk(aiAskInput.value); });
    aiAskChip?.addEventListener('mousedown', (e) => e.stopPropagation());
    aiAskChip?.addEventListener('click', (e) => { e.stopPropagation(); openAsk(); });
    aiAsk?.addEventListener('mousedown', (e) => e.stopPropagation());
    $('ai-ask-more')?.addEventListener('click', () => {
        const text = aiAskInput.value;
        const hadFocus = !!askFocus;
        closeAsk();
        openAiPanel('chat');
        aiInput.value = text;
        aiInput.focus();
        void hadFocus;
    });
    document.addEventListener('mousedown', (e) => {
        if (aiAsk && !aiAsk.hidden && !aiAsk.contains(e.target) && e.target !== aiAskChip) closeAsk();
    });

    // The ✦ beside a selected element, top right of its box.
    function placeAskChip() {
        if (!aiAskChip) return;
        const stage = $('ppt-stage');
        const show = !!state.selectedEl && selBox && !selBox.hidden && !ai.preview && !state.presenting
            && !isDesigningTemplate() && !cropState && stage && !stage.classList.contains('is-ai-preview');
        if (!show) { aiAskChip.hidden = true; return; }
        const cr = stageCanvas.getBoundingClientRect();
        const b = selBox.getBoundingClientRect();
        aiAskChip.style.left = Math.min(b.right - cr.left + 6, cr.width - 30) + 'px';
        aiAskChip.style.top = Math.max(4, b.top - cr.top - 12) + 'px';
        aiAskChip.hidden = false;
    }

    // Enter and Esc keep or drop a change on preview.
    document.addEventListener('keydown', (e) => {
        if (state.presenting || !isShown(pptEditor)) return;
        // ⌘K / Ctrl+K is the toolbar's Ask AI: it opens the copilot ready to
        // type, and closes it again from there.
        if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
            if (!state.activeSlideId || isDesigningTemplate()) return;
            e.preventDefault();
            closeAsk();
            if (!aiPanel.hidden && document.activeElement === aiInput) closeAiPanel();
            else { openAiPanel('chat'); aiInput.focus(); syncAiScope(); }
            return;
        }
        if (e.key === 'Escape' && aiAsk && !aiAsk.hidden) { e.preventDefault(); closeAsk(); return; }
        if (!ai.preview) return;
        const active = document.activeElement;
        if (active && (/^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) || active.isContentEditable)) return;
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); discardAiPreview(); }
        else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && ai.preview.turn.results.length > 1 && !e.metaKey && !e.ctrlKey) {
            e.preventDefault(); e.stopPropagation();
            const r = currentPreviewResult();
            const i = r ? ai.preview.turn.results.indexOf(r) : -1;
            goToPreviewSlide(e.key === 'ArrowLeft' ? i - 1 : i + 1);
        } else if (e.key.toLowerCase() === 'b' && !e.metaKey && !e.ctrlKey && currentPreviewResult()) {
            e.preventDefault(); e.stopPropagation();
            setPreviewView(ai.preview.view === 'before' ? 'after' : 'before');
        }
        // Enter on a focused button is that button's own click.
        else if (e.key === 'Enter' && !e.shiftKey && active?.tagName !== 'BUTTON') { e.preventDefault(); e.stopPropagation(); keepAiPreview(); }
    }, true);

    // Esc lets go of the selection – after anything with a better claim to
    // the key (a crop, an open menu, a change on preview) has had it. Typing
    // in a text box ends there too.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || e.defaultPrevented || state.presenting || !isShown(pptEditor)) return;
        if (!state.selectedEl && !multiSelected.size) return;
        const active = document.activeElement;
        if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;
        if (active && active.isContentEditable && stageSlideInner.contains(active)) active.blur();
        // Blurring a text box leaves its highlighted words highlighted; let go of those too.
        const range = window.getSelection();
        if (range && range.rangeCount && stageSlideInner.contains(range.anchorNode)) range.removeAllRanges();
        setSelectedEl(null);
        clearMultiSelect();
    });

    window.addEventListener('resize', () => { placeAsk(); placeAskChip(); });

    // ── Copilot panel ──

    function openAiPanel(tab) {
        if (!aiPanel) return;
        aiSyncDeck();
        if (aiPanel.hidden) {
            aiPanel.hidden = false;
            aiCopilotBtn?.setAttribute('aria-pressed', 'true');
            aiCopilotBtn?.classList.add('is-on');
            try { localStorage.setItem('ai-panel-open', '1'); } catch {}
            requestAnimationFrame(() => { scaleStage(); placeAskChip(); });
        }
        if (tab) setAiTab(tab);
        renderAiContext();
        renderAiLog();
    }

    function closeAiPanel() {
        if (!aiPanel || aiPanel.hidden) return;
        aiPanel.hidden = true;
        aiCopilotBtn?.setAttribute('aria-pressed', 'false');
        aiCopilotBtn?.classList.remove('is-on');
        try { localStorage.removeItem('ai-panel-open'); } catch {}
        requestAnimationFrame(() => { scaleStage(); placeAskChip(); });
    }

    // New chat clears the conversation – and with it what follow-ups are read
    // against, and the Undo links in the log. Kept changes stay kept (Ctrl+Z on
    // the slide still steps back over them); one waiting for review is only
    // dropped if the teacher says so.
    function syncNewChat() {
        const b = $('ai-new-chat');
        if (!b) return;
        b.hidden = !ai.turns.length || $('ai-chat')?.hidden;
        b.disabled = ai.busy;
    }

    function startNewChat() {
        if (ai.busy) return;
        if (ai.preview) {
            if (!confirm('A change is still waiting for you to review. Discard it and start a new chat?')) return;
            discardAiPreview();
        }
        ai.turns = [];
        renderAiLog();
        aiInput.value = '';
        fitAiInput();
        aiInput.focus();
    }

    $('ai-new-chat')?.addEventListener('click', startNewChat);

    function setAiTab(tab) {
        aiPanel.querySelectorAll('[data-ai-tab]').forEach(b => b.classList.toggle('active', b.dataset.aiTab === tab));
        $('ai-chat').hidden = tab !== 'chat';
        $('ai-review').hidden = tab !== 'review';
        syncNewChat();
        if (tab === 'review') renderReview();
    }

    aiCopilotBtn?.addEventListener('click', () => {
        if (!aiPanel.hidden) { closeAiPanel(); return; }
        openAiPanel('chat');
        aiInput.focus();
        syncAiScope();
    });
    $('ai-panel-close')?.addEventListener('click', closeAiPanel);
    aiPanel?.querySelectorAll('[data-ai-tab]').forEach(b => b.addEventListener('click', () => setAiTab(b.dataset.aiTab)));

    // ── What a request changes ──
    // Decided when the request is sent, from its own words (aiScope.js):
    // "every heading", "slides 2-5", "the last slide"; otherwise the selection,
    // or the slide on screen. Wording that only hints at the whole deck is
    // asked about in the log before anything runs or is charged.

    function idsAt(positions) {
        return positions.map(pos => state.slides.get(pos)?.id).filter(Boolean);
    }

    // The panel's idea of the selection. Moving focus into the composer ends
    // text editing, and that deselects the box – so while focus is in the
    // panel, the selection it had a moment ago still counts.
    function panelSelection() {
        if (ai.preview) return null;
        const sel = aiSelection();
        const inPanel = aiPanel && aiPanel.contains(document.activeElement);
        if (sel || !inPanel) ai.lastSel = sel ? { slideId: state.activeSlideId, sel } : null;
        return sel || (ai.lastSel && ai.lastSel.slideId === state.activeSlideId ? ai.lastSel.sel : null);
    }

    // Called whenever the selection changes: the panel remembers it, and the
    // context chips follow it.
    function syncAiScope() {
        renderAiContext();
    }

    // ── Context ──
    // Like a file open beside a chat: the slide on screen and what is selected
    // on it go with every message, shown as chips above the box. × leaves one
    // out until the slide, or the selection, changes. Leaving the slide out
    // leaves its selection out too – a selection means nothing without it.

    function aiContext() {
        const entry = slideEntryById(state.activeSlideId);
        const sel = panelSelection();
        const selKey = sel ? `${state.activeSlideId}|${sel.ids.join(',')}|${sel.text}` : '';
        const slide = entry && ai.dismissed.slide !== entry.s.id ? entry : null;
        const selection = slide && sel && ai.dismissed.sel !== selKey ? sel : null;
        return { slide, selection, selKey };
    }

    // What the selection says, for its chip: the first selected element's text.
    function selectionPreview(sel, entry) {
        if (!sel || !entry) return '';
        if (!isModelSlide(entry.s)) return sel.text;
        const { contents, elements } = modelOf(entry.s);
        const el = elements.find(e => e.id === sel.ids[0]);
        const rec = el && contents[el.content_id];
        if (!rec) return '';
        return el.type === 'image' ? (rec.keyword || '') : recordText(rec);
    }

    function renderAiContext() {
        const box = $('ai-context');
        if (!box) return;
        const ctx = aiContext();
        box.innerHTML = '';
        const chip = (icon, label, detail, title, onRemove) => {
            const c = document.createElement('span');
            c.className = 'ai-ctx';
            c.title = title;
            c.innerHTML = `<span class="ai-ctx-icon" aria-hidden="true">${icon}</span><b>${esc(label)}</b>${detail ? `<span class="ai-ctx-detail">${esc(detail)}</span>` : ''}`;
            const x = document.createElement('button');
            x.type = 'button';
            x.className = 'ai-ctx-x';
            x.setAttribute('aria-label', `Leave ${label} out`);
            x.textContent = '×';
            x.addEventListener('click', () => { onRemove(); renderAiContext(); aiInput?.focus(); });
            c.appendChild(x);
            box.appendChild(c);
        };
        if (ctx.slide) {
            const title = slideTitle(ctx.slide.s);
            chip('▭', `Slide ${ctx.slide.pos}`, title, 'The slide on screen goes with your message', () => { ai.dismissed.slide = ctx.slide.s.id; });
        }
        if (ctx.selection) {
            const words = selectionPreview(ctx.selection, ctx.slide);
            chip('⬚', selectionLabel(ctx.selection), words ? `“${words.slice(0, 40)}${words.length > 40 ? '…' : ''}”` : '', 'What you have selected goes with your message', () => { ai.dismissed.sel = ctx.selKey; });
        }
        box.hidden = !box.children.length;
    }

    // { kind, positions, ids, focus, sel, sure } for a request.
    function resolveTarget(text, sel = panelSelection()) {
        const current = slideEntryById(state.activeSlideId)?.pos || 1;
        const { kind, positions, sure } = AiScope.infer(text, { total: state.slides.size, current, hasSelection: !!sel });
        return { kind, positions, sure, ids: idsAt(positions), focus: kind === 'selection' ? sel : null, sel };
    }

    // Older slides cannot be edited several at a time; a request over a few
    // slides runs on the rest.
    function runnableIds(ids) {
        return ids.length > 1 ? ids.filter(id => isModelSlide(slideEntryById(id)?.s)) : ids;
    }

    // The Ask box beside an element: a request about that element (or its
    // slide) is a quick one-slide edit; anything wider goes to the agent,
    // which works out the slides itself and asks if it cannot tell.
    function sendAiRequest(instruction, { sel = panelSelection(), origin = 'ask' } = {}) {
        const t = resolveTarget(instruction, sel);
        if (t.sure && (t.kind === 'selection' || t.kind === 'slide')) {
            return requestAiEdit({ instruction, slideIds: t.ids, focus: t.focus, origin });
        }
        return runAgentTurn(instruction);
    }

    aiInput?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); aiComposer.requestSubmit(); }
    });
    // The box grows with what is typed, up to a few lines.
    function fitAiInput() {
        if (!aiInput) return;
        aiInput.style.height = 'auto';
        aiInput.style.height = `${Math.min(aiInput.scrollHeight, 140)}px`;
    }
    aiInput?.addEventListener('input', fitAiInput);
    // A click anywhere in the box, chips aside, puts the caret in it.
    document.querySelector('.ai-box')?.addEventListener('mousedown', (e) => {
        if (e.target.closest('button, textarea, input')) return;
        e.preventDefault();
        aiInput.focus();
    });
    aiComposer?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const instruction = aiInput.value.trim();
        if (!instruction || ai.busy) return;
        aiInput.value = '';
        fitAiInput();
        await runAgentTurn(instruction);
    });

    function syncAiBusy() {
        if (aiSend) { aiSend.disabled = ai.busy; aiSend.classList.toggle('is-busy', ai.busy); }
        syncNewChat();
        aiCopilotBtn?.classList.toggle('is-busy', ai.busy);
    }

    function slideList(turn) {
        const positions = (turn.results.length ? turn.results.map(r => r.slideId) : turn.slideIds)
            .map(id => slideEntryById(id)?.pos).filter(Boolean).sort((a, b) => a - b);
        if (!positions.length) return '';
        return positions.length === 1 ? `Slide ${positions[0]}` : `Slides ${positions.join(', ')}`;
    }

    const AI_EXAMPLES = [
        'Which slides have no picture? Add one to each',
        'Simplify the wording for Year 7, then bold the key terms',
        'Where do I check understanding in this lesson?',
    ];

    function renderAiLog() {
        syncNewChat();
        if (!aiLog || aiPanel?.hidden) return;
        aiLog.innerHTML = '';
        if (!ai.turns.length) {
            const empty = document.createElement('div');
            empty.className = 'ai-empty';
            empty.innerHTML = '<span class="ai-empty-mark" aria-hidden="true">✦</span>'
                + '<h3>Ask about this deck, or change it</h3>'
                + '<p>One slide or all of them. You see every change before it’s kept.</p>';
            const list = document.createElement('div');
            list.className = 'ai-empty-list';
            AI_EXAMPLES.forEach(text => {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'ai-empty-sugg';
                b.innerHTML = `<span>${esc(text)}</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>`;
                b.addEventListener('click', () => {
                    aiInput.value = text;
                    fitAiInput();
                    aiInput.focus();
                });
                list.appendChild(b);
            });
            empty.appendChild(list);
            aiLog.appendChild(empty);
            return;
        }
        ai.turns.forEach(turn => aiLog.appendChild(renderTurn(turn)));
        // Stay with the slide under review rather than jumping to the bottom.
        const active = aiLog.querySelector('.ai-row.is-active');
        if (active) active.scrollIntoView({ block: 'nearest' });
        else aiLog.scrollTop = aiLog.scrollHeight;
    }

    // ── What changed on a slide, in words ──
    // Text is compared word by word, so a rewrite reads as what was cut and
    // what was put in; anything else is named (a new picture, a moved box).

    // A light edit reads best marked up in place; a rewrite reads best as the
    // old text and the new one, one above the other.
    function textChange(a, b) {
        const words = t => String(t || '').split(/\s+/).filter(Boolean);
        const x = words(a); const y = words(b);
        const shared = x.filter(w => y.includes(w)).length;
        if (!x.length || shared / Math.max(x.length, y.length) < 0.55) {
            return `<span class="ai-diff-was">${esc(a)}</span><span class="ai-diff-now">${esc(b)}</span>`;
        }
        return wordDiff(a, b);
    }

    function wordDiff(a, b) {
        const x = String(a || '').split(/(\s+)/).filter(Boolean);
        const y = String(b || '').split(/(\s+)/).filter(Boolean);
        if (x.length * y.length > 250000) return `<del>${esc(a)}</del> <ins>${esc(b)}</ins>`;
        // Longest common subsequence, then walked back into runs.
        const dp = Array.from({ length: x.length + 1 }, () => new Uint16Array(y.length + 1));
        for (let i = x.length - 1; i >= 0; i--) {
            for (let j = y.length - 1; j >= 0; j--) {
                dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
            }
        }
        const out = [];
        const push = (kind, text) => {
            const last = out[out.length - 1];
            if (last && last.kind === kind) last.text += text; else out.push({ kind, text });
        };
        let i = 0; let j = 0;
        while (i < x.length && j < y.length) {
            if (x[i] === y[j]) { push('same', x[i]); i++; j++; }
            else if (dp[i + 1][j] >= dp[i][j + 1]) { push('del', x[i]); i++; }
            else { push('ins', y[j]); j++; }
        }
        while (i < x.length) push('del', x[i++]);
        while (j < y.length) push('ins', y[j++]);
        return out.map(r => (r.kind === 'same' ? esc(r.text) : r.text.trim() ? `<${r.kind}>${esc(r.text)}</${r.kind}>` : esc(r.text))).join('');
    }

    function recordText(rec) {
        if (!rec) return '';
        const parts = ['label', 'term', 'text', 'definition'].map(k => (typeof rec[k] === 'string' ? plainText(rec[k]) : '')).filter(Boolean);
        (Array.isArray(rec.items) ? rec.items : []).forEach(i => { if (typeof i === 'string' && plainText(i)) parts.push(`• ${plainText(i)}`); });
        return parts.join(' ');
    }

    // [{ name, html }] for each element the result changed.
    function resultChanges(r) {
        if (r.kind !== 'model') {
            const a = plainText(r.before.html); const b = plainText(r.after.html);
            return a === b ? [{ name: 'Slide', html: 'Restyled' }] : [{ name: 'Slide', html: wordDiff(a, b) }];
        }
        const prev = new Map(r.before.elements.map(e => [e.id, e]));
        const next = new Map(r.after.elements.map(e => [e.id, e]));
        const nameOf = e => TYPE_NAMES[e.type] || 'Text box';
        const out = [];
        let laidOut = 0;   // boxes only moved or restyled: one line for all of them
        r.after.elements.forEach(e => {
            const old = prev.get(e.id);
            const rec = r.after.contents[e.content_id];
            if (!old) {
                out.push({ name: `New ${nameOf(e).toLowerCase()}`, html: e.type === 'image' ? esc(rec?.keyword || 'picture') : `<ins>${esc(recordText(rec))}</ins>` });
                return;
            }
            const a = recordText(r.before.contents[old.content_id]);
            const b = recordText(rec);
            const restyled = JSON.stringify(old.style_overrides || {}) !== JSON.stringify(e.style_overrides || {})
                || JSON.stringify(old.align || null) !== JSON.stringify(e.align || null);
            if (e.type === 'image' && JSON.stringify(r.before.contents[old.content_id] || null) !== JSON.stringify(rec || null)) {
                out.push({ name: 'Picture', html: `New picture${rec?.keyword ? `: ${esc(rec.keyword)}` : ''}` });
            } else if (old.visible !== false && e.visible === false) {
                out.push({ name: nameOf(e), html: '<span class="ai-diff-note">hidden</span>' });
            } else if (a !== b) {
                out.push({ name: nameOf(e), html: textChange(a, b) });
            } else if (restyled && /fontWeight|fontStyle|textDecoration/.test(JSON.stringify(e.style_overrides || {}))) {
                out.push({ name: nameOf(e), html: '<span class="ai-diff-note">restyled</span>' });
            } else if (restyled || JSON.stringify(old.box) !== JSON.stringify(e.box)) {
                laidOut += 1;
            }
        });
        r.before.elements.forEach(e => {
            if (!next.has(e.id)) out.push({ name: `Removed ${nameOf(e).toLowerCase()}`, html: e.type === 'image' ? '' : `<del>${esc(recordText(r.before.contents[e.content_id]))}</del>` });
        });
        if (laidOut) out.push({ name: 'Layout', html: `<span class="ai-diff-note">${laidOut} box${laidOut === 1 ? '' : 'es'} moved or restyled</span>` });
        return out;
    }

    // A turn reads like a chat: what was asked, then the copilot's answer in
    // plain text – its steps folded into one line once done, the slides to
    // review while a change waits, and a quiet line saying what became of it.
    function renderTurn(turn) {
        const wrap = document.createElement('div');
        wrap.className = 'ai-turn';
        const me = document.createElement('div');
        me.className = 'ai-me';
        me.textContent = turn.instruction;
        if (turn.focusLabel || turn.origin === 'review') {
            const tag = document.createElement('span');
            tag.className = 'ai-me-tag';
            tag.textContent = turn.origin === 'review' ? 'From Review' : `${turn.focusLabel} selected`;
            me.appendChild(tag);
        }
        wrap.appendChild(me);

        const msg = document.createElement('div');
        msg.className = `ai-msg is-${turn.status}`;
        msg.innerHTML = '<span class="ai-avatar" aria-hidden="true">✦</span>';
        const body = document.createElement('div');
        body.className = 'ai-msg-body';
        msg.appendChild(body);

        const steps = turn.steps || [];
        if (turn.status === 'working') {
            const list = renderSteps(steps.length ? steps : [{ label: turn.origin === 'agent' ? 'Reading the deck' : 'Working on it', status: 'running' }]);
            body.appendChild(list);
        } else if (steps.length) {
            const fold = document.createElement('details');
            fold.className = 'ai-steps-fold';
            const failed = steps.some(st => st.status === 'error');
            const names = steps.map(st => st.label.split(' · ')[0]);
            fold.innerHTML = `<summary><span class="ai-step-mark${failed ? ' is-error' : ''}" aria-hidden="true">${failed ? '!' : '✓'}</span>${steps.length === 1 ? esc(names[0]) : `${steps.length} steps · ${esc(names.join(', '))}`}</summary>`;
            fold.appendChild(renderSteps(steps));
            body.appendChild(fold);
        }

        const n = turn.results.length;
        // A change that was dropped no longer happened: its "I've made…" would mislead.
        const gone = turn.status === 'discarded' || turn.status === 'undone';
        const text = turn.status === 'error' ? turn.error
            : turn.status === 'working' || gone ? ''
            : turn.origin === 'agent' ? turn.reply
            : turn.summary || '';
        if (text) {
            const p = document.createElement('p');
            p.className = 'ai-msg-text';
            p.textContent = text;
            body.appendChild(p);
        }
        if (turn.failed) {
            const warn = document.createElement('p');
            warn.className = 'ai-msg-note';
            warn.textContent = `${turn.failed} slide${turn.failed === 1 ? '' : 's'} couldn’t be edited and were left alone.`;
            body.appendChild(warn);
        }
        if (turn.status === 'preview') body.appendChild(renderReviewList(turn));
        if (turn.structure) body.appendChild(renderDeckChanges(turn));

        // What became of it, and what can still be done: one quiet line.
        const foot = document.createElement('div');
        foot.className = 'ai-msg-foot';
        const bit = (t, cls = '') => { const sp = document.createElement('span'); sp.className = cls; sp.textContent = t; foot.appendChild(sp); };
        const link = (t, fn) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'ai-link';
            b.textContent = t;
            b.addEventListener('click', fn);
            foot.appendChild(b);
        };
        const where = n ? slideList(turn) : '';
        const cost = turn.cost ? `${turn.cost} edit${turn.cost === 1 ? '' : 's'}` : '';
        if (turn.status === 'kept') {
            bit(turn.skipped ? `Kept ${n} of ${n + turn.skipped} slides` : `Kept · ${where}`, 'ai-foot-kept');
            if (cost) bit(cost);
            link('Show', () => {
                const entry = slideEntryById(turn.results[0]?.slideId);
                if (entry) selectSlide(entry.s.id, entry.s.html, entry.pos);
            });
            link('Undo', () => undoAiTurn(turn));
        } else if (turn.status === 'discarded' || turn.status === 'undone' || turn.status === 'nochange') {
            bit(turn.status === 'discarded' ? `Discarded${n ? ` · ${slideList(turn).toLowerCase()}` : ''}` : turn.status === 'undone' ? `Undone${n ? ` · ${slideList(turn).toLowerCase()}` : ''}` : 'No change');
            if (cost) bit(cost);
            if (turn.origin !== 'rule') link('Ask again', () => { aiInput.value = turn.instruction; fitAiInput(); aiInput.focus(); });
        } else if (turn.status === 'error') {
            if (turn.origin !== 'rule') link('Try again', () => { aiInput.value = turn.instruction; fitAiInput(); aiInput.focus(); });
        } else if (turn.status === 'preview' && cost) {
            bit(cost);
        }
        if (foot.children.length) body.appendChild(foot);

        wrap.appendChild(msg);
        return wrap;
    }

    function renderSteps(steps) {
        const list = document.createElement('ul');
        list.className = 'ai-steps';
        steps.forEach(st => {
            const li = document.createElement('li');
            li.className = `is-${st.status}`;
            li.innerHTML = `<span class="ai-step-mark" aria-hidden="true">${st.status === 'running' ? '' : st.status === 'error' ? '!' : '✓'}</span><span>${esc(st.label)}${st.detail ? ` <em>${esc(st.detail)}</em>` : ''}</span>`;
            list.appendChild(li);
        });
        return list;
    }

    // One row per changed slide: tick to keep, click to look. The slide on
    // the stage is open, showing what changed on it.
    function renderReviewList(turn) {
        const list = document.createElement('div');
        list.className = 'ai-rows';
        const onStage = currentPreviewResult();
        const many = turn.results.length > 1;
        if (many) {
            const kept = turn.results.filter(r => r.include).length;
            const bar = document.createElement('div');
            bar.className = 'ai-rows-head';
            bar.innerHTML = `<span><kbd class="ai-kbd">←</kbd><kbd class="ai-kbd">→</kbd> step through · <kbd class="ai-kbd">B</kbd> before / after</span>`;
            const all = document.createElement('button');
            all.type = 'button';
            all.className = 'ai-link';
            all.textContent = kept === turn.results.length ? 'Untick all' : 'Tick all';
            all.addEventListener('click', () => {
                const on = kept !== turn.results.length;
                turn.results.forEach(r => { r.include = on; });
                renderAiPreviewBar();
                renderAiLog();
            });
            bar.appendChild(all);
            list.appendChild(bar);
        }
        turn.results.forEach((r, i) => {
            const pos = slideEntryById(r.slideId)?.pos;
            const row = document.createElement('div');
            row.className = `ai-row${r === onStage ? ' is-active' : ''}${r.include ? '' : ' is-off'}`;
            const top = document.createElement('div');
            top.className = 'ai-row-top';
            if (many) {
                const tick = document.createElement('input');
                tick.type = 'checkbox';
                tick.checked = !!r.include;
                tick.setAttribute('aria-label', `Keep slide ${pos}`);
                tick.addEventListener('click', (e) => e.stopPropagation());
                tick.addEventListener('change', () => setResultIncluded(r, tick.checked));
                top.appendChild(tick);
            }
            const label = document.createElement('button');
            label.type = 'button';
            label.className = 'ai-row-label';
            label.innerHTML = `<b>Slide ${pos}</b><span>${esc(r.summary || 'Changed')}</span>`;
            label.addEventListener('click', () => goToPreviewSlide(i));
            top.appendChild(label);
            row.appendChild(top);
            if (r.stale) {
                const warn = document.createElement('p');
                warn.className = 'ai-card-note';
                warn.textContent = 'You edited this slide while the copilot worked – keeping this would undo that.';
                row.appendChild(warn);
            }
            if (r === onStage || !many) {
                const diff = document.createElement('dl');
                diff.className = 'ai-diff';
                resultChanges(r).forEach(c => {
                    const dt = document.createElement('dt');
                    dt.textContent = c.name;
                    const dd = document.createElement('dd');
                    dd.innerHTML = c.html || '';
                    diff.append(dt, dd);
                });
                if (diff.children.length) row.appendChild(diff);
            }
            list.appendChild(row);
        });
        const kept = turn.results.filter(r => r.include).length;
        const n = turn.results.length;
        const acts = document.createElement('div');
        acts.className = 'ai-rows-acts';
        const keep = document.createElement('button');
        keep.type = 'button';
        keep.className = 'btn btn-primary btn-sm';
        keep.textContent = n > 1 ? (kept === n ? `Keep all ${n}` : `Keep ${kept} of ${n}`) : 'Keep';
        keep.disabled = !kept;
        keep.addEventListener('click', keepAiPreview);
        const drop = document.createElement('button');
        drop.type = 'button';
        drop.className = 'btn btn-ghost btn-sm';
        drop.textContent = n > 1 ? 'Discard all' : 'Discard';
        drop.addEventListener('click', discardAiPreview);
        acts.append(keep, drop);
        list.appendChild(acts);
        return list;
    }

    // ── Slides added, removed and moved by the copilot ──
    // The deck's new order, as one list: new slides shown small, moved ones
    // marked, runs of untouched slides folded, removed ones under it. Applied
    // or discarded as a whole – deleting a slide is the one change here that
    // can't be undone from the chat, so nothing is done until Apply.

    function renderDeckChanges(turn) {
        const st = turn.structure;
        const box = document.createElement('div');
        box.className = 'ai-rows ai-deck';
        const head = document.createElement('div');
        head.className = 'ai-rows-head';
        const nNew = st.order.filter(o => o.key).length;
        const bits = [nNew && `${nNew} new`, st.removed.length && `${st.removed.length} removed`].filter(Boolean);
        head.innerHTML = `<span>New slide order${bits.length ? ` · ${esc(bits.join(' · '))}` : ''}</span>`;
        box.appendChild(head);

        // A slide counts as moved when it is out of step with the others it
        // came with: the longest run still in their old order stayed put.
        const kept = st.order.map((o, i) => ({ i, from: o.from })).filter(x => x.from);
        const best = []; const prev = [];
        kept.forEach((x, k) => {
            let lo = 0; let hi = best.length;
            while (lo < hi) { const mid = (lo + hi) >> 1; if (kept[best[mid]].from < x.from) lo = mid + 1; else hi = mid; }
            prev[k] = lo ? best[lo - 1] : -1;
            best[lo] = k;
        });
        const stayed = new Set();
        for (let k = best.length ? best[best.length - 1] : -1; k >= 0; k = prev[k]) stayed.add(kept[k].i);

        const list = document.createElement('ol');
        list.className = 'ai-deck-list';
        let quiet = 0;
        const flush = () => {
            if (!quiet) return;
            const li = document.createElement('li');
            li.className = 'ai-deck-quiet';
            li.textContent = `${quiet} slide${quiet === 1 ? '' : 's'} as before`;
            list.appendChild(li);
            quiet = 0;
        };
        st.order.forEach((o, i) => {
            const isNew = !!o.key;
            const moved = !isNew && !stayed.has(i);
            if (!isNew && !moved) { quiet += 1; return; }
            flush();
            const li = document.createElement('li');
            li.className = `ai-deck-row${isNew ? ' is-new' : ' is-moved'}`;
            if (isNew) {
                const draft = st.added.find(g => g.key === o.key)?.slides?.[o.part];
                if (draft) {
                    const thumb = document.createElement('div');
                    thumb.className = 'ai-deck-thumb';
                    const inner = document.createElement('div');
                    inner.className = 'slide-thumb-inner';
                    thumb.appendChild(inner);
                    renderSeamSlide(inner, draft);
                    li.appendChild(thumb);
                }
            }
            const label = document.createElement('span');
            label.className = 'ai-deck-label';
            label.innerHTML = `<b>${i + 1}.</b> ${esc(o.title || 'Untitled')}`;
            const tag = document.createElement('span');
            tag.className = 'ai-diff-note';
            tag.textContent = isNew ? 'New' : `Moved from ${o.from}`;
            label.appendChild(tag);
            li.appendChild(label);
            list.appendChild(li);
        });
        flush();
        box.appendChild(list);

        if (st.removed.length) {
            const gone = document.createElement('div');
            gone.className = 'ai-deck-removed';
            gone.innerHTML = `<b>Removed</b>${st.removed.map(r => `<span><del>${r.n}. ${esc(r.title || 'Untitled')}</del></span>`).join('')}`;
            box.appendChild(gone);
        }
        if (st.error) {
            const err = document.createElement('p');
            err.className = 'ai-msg-note ai-deck-error';
            err.textContent = st.error;
            box.appendChild(err);
        }

        const acts = document.createElement('div');
        acts.className = 'ai-rows-acts';
        if (st.status === 'pending') {
            const apply = document.createElement('button');
            apply.type = 'button';
            apply.className = 'btn btn-primary btn-sm';
            apply.textContent = st.busy ? 'Applying…' : st.removed.length ? `Apply · removes ${st.removed.length} slide${st.removed.length === 1 ? '' : 's'}` : 'Apply';
            apply.disabled = !!st.busy;
            apply.addEventListener('click', () => applyDeckChanges(turn));
            const drop = document.createElement('button');
            drop.type = 'button';
            drop.className = 'btn btn-ghost btn-sm';
            drop.textContent = 'Discard';
            drop.disabled = !!st.busy;
            drop.addEventListener('click', () => { st.status = 'discarded'; st.error = ''; renderAiLog(); });
            acts.append(apply, drop);
        } else {
            const said = document.createElement('span');
            said.className = 'ai-deck-done';
            said.textContent = st.status === 'kept' ? 'Applied' : st.status === 'partial' ? 'Partly applied' : 'Discarded';
            acts.appendChild(said);
        }
        box.appendChild(acts);
        return box;
    }

    // Puts the new slides in (each at the end, as sealed when drafted), takes
    // the removed ones out, then sets the whole order. Only for the deck the
    // copilot was shown: anything added, removed or moved since, and it stops.
    async function applyDeckChanges(turn) {
        const st = turn.structure;
        if (!st || st.status !== 'pending' || st.busy || !state.presentationId) return;
        if (ai.preview) { showToast('Keep or discard the slide changes first.', 3000); return; }
        const now = orderedSlideList().map(s => s.id);
        if (now.join() !== st.sentOrder.join()) {
            st.error = 'The deck has changed since I suggested this, so I’ve left it alone. Ask again.';
            renderAiLog();
            return;
        }
        st.busy = true;
        st.error = '';
        renderAiLog();
        const base = `/api/presentations/${state.presentationId}`;
        const made = new Map();
        let landed = false;
        try {
            clearTimeout(autoSaveTimer);
            await savePptDirect();
            let last = now[now.length - 1];
            for (const g of st.added) {
                const res = await apiFetch(`${base}/seam/keep`, { method: 'POST', body: JSON.stringify({ after: last, token: g.token }) });
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.slides?.length) throw new Error(data.error || 'A new slide couldn’t be added.');
                landed = true;
                made.set(g.key, data.slides.map(x => x.slideId));
                last = data.slides[data.slides.length - 1].slideId;
            }
            for (const r of st.removed) {
                const res = await apiFetch(`${base}/slides/${r.id}`, { method: 'DELETE' });
                if (!res.ok) throw new Error(`Slide ${r.n} couldn’t be removed.`);
                landed = true;
            }
            const order = st.order.map(o => o.id || made.get(o.key)?.[o.part]);
            if (order.some(id => !id)) throw new Error('The new order couldn’t be worked out.');
            const res = await apiFetch(`${base}/slides/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });
            if (!res.ok) throw new Error('The new order couldn’t be saved.');
            st.status = 'kept';
            const firstNew = st.order.find(o => o.key);
            const show = firstNew ? made.get(firstNew.key)?.[firstNew.part] : null;
            await reloadDeckSlides(show || (order.includes(state.activeSlideId) ? state.activeSlideId : order[0]));
        } catch (err) {
            st.error = err.message === 'Not authenticated' ? 'Sign in again to keep going.'
                : `${err.message || 'That didn’t work.'}${landed ? ' Part of it was done – check the slides.' : ''}`;
            if (landed) {
                st.status = 'partial';
                await reloadDeckSlides(state.activeSlideId).catch(() => {});
            }
        } finally {
            st.busy = false;
            renderAiLog();
        }
    }

    // ── Review ──
    // Layout checks run here, on the stored models, and cost nothing; their
    // fixes are computed here too. The judgement half is one server call, and
    // its fixes are ordinary edits.

    const DENSE_WORDS = 80;
    // Themes set captions at 14px on purpose; below that is a slip.
    const MIN_FONT_PX = 14;

    function slideWords(s) {
        const { contents, elements } = modelOf(s);
        let words = 0;
        elements.forEach(e => {
            if (!e || e.visible === false || e.type === 'image' || e.type === 'shape') return;
            const rec = contents[e.content_id] || {};
            const text = ['label', 'term', 'text', 'definition'].map(k => (typeof rec[k] === 'string' ? plainText(rec[k]) : ''))
                .concat((Array.isArray(rec.items) ? rec.items : []).map(i => (typeof i === 'string' ? plainText(i) : '')))
                .join(' ');
            words += text.split(/\s+/).filter(w => /\w/.test(w)).length;
        });
        return words;
    }

    const pxOf = v => { const m = /^(\d+(?:\.\d+)?)px$/.exec(String(v || '').trim()); return m ? Number(m[1]) : null; };

    // A rule fix: a copy of the slide's model with `mutate` applied to it.
    function ruleResult(slideId, s, mutate, summary) {
        const before = cloneJson(modelOf(s));
        const after = cloneJson(before);
        mutate(after);
        after.html = SlideModelRenderer.buildHtml(after.contents, after.elements);
        return {
            slideId, kind: 'model',
            before: { ...before, html: s.html },
            after,
            changedIds: changedElementIds(before, after),
            summary,
        };
    }

    // Each mutate looks its elements up again by id, so it applies to whatever
    // version of the slide it is handed – the stored one, or one another fix
    // on the same slide has already changed.
    const onElement = (m, id, fn) => { const el = m.elements.find(e => e && e.id === id); if (el) fn(el); };

    function layoutChecks() {
        const items = [];
        const slides = deckSlides().filter(([, s]) => isModelSlide(s));
        // Heading size: the size most slides use is the deck's. The title slide
        // is meant to be bigger, so it neither sets the size nor gets flagged.
        const sizeOf = e => (e.style_overrides && e.style_overrides.fontSize) || '';
        // Fitting text nudges a size by a pixel or two; that is not a mismatch.
        const sameSize = (a, b) => {
            if (a === b) return true;
            const x = pxOf(a); const y = pxOf(b);
            return x != null && y != null && Math.abs(x - y) <= Math.max(2, y * 0.1);
        };
        const counts = new Map();
        slides.filter(([pos]) => pos > 1).forEach(([, s]) => {
            const h = modelOf(s).elements.find(e => e && e.type === 'heading' && e.visible !== false);
            if (h) counts.set(sizeOf(h), (counts.get(sizeOf(h)) || 0) + 1);
        });
        const usual = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
        slides.forEach(([pos, s]) => {
            const visible = modelOf(s).elements.filter(e => e && e.visible !== false);
            if (!visible.length) {
                items.push({ kind: 'info', slideId: s.id, title: 'Empty slide', detail: 'There is nothing on this slide yet.' });
                return;
            }
            const h = visible.find(e => e.type === 'heading');
            if (h && pos > 1 && usual && counts.size > 1 && usual[1] >= 2 && !sameSize(sizeOf(h), usual[0])) {
                const want = usual[0];
                items.push({
                    kind: 'rule', slideId: s.id, title: 'Heading size differs',
                    detail: `Most slides use ${want || 'the theme’s'} heading size; this one is ${sizeOf(h) || 'the theme’s'}.`,
                    summary: 'Matched the heading size to the rest of the deck',
                    mutate: m => onElement(m, h.id, el => {
                        el.style_overrides = { ...(el.style_overrides || {}) };
                        if (want) el.style_overrides.fontSize = want; else delete el.style_overrides.fontSize;
                    }),
                });
            }
            const off = visible.filter(e => e.box && (e.box.x < -4 || e.box.y < -4 || e.box.x + e.box.width > 964 || e.box.y + e.box.height > 544));
            if (off.length) {
                items.push({
                    kind: 'rule', slideId: s.id, title: 'Something runs off the slide',
                    detail: `${off.length === 1 ? 'One box goes' : `${off.length} boxes go`} past the edge, so part of ${off.length === 1 ? 'it' : 'them'} will not show.`,
                    summary: 'Moved everything back inside the slide',
                    mutate: m => off.forEach(o => onElement(m, o.id, el => {
                        const b = { ...el.box };
                        b.width = Math.min(b.width, 960); b.height = Math.min(b.height, 540);
                        b.x = Math.max(0, Math.min(b.x, 960 - b.width)); b.y = Math.max(0, Math.min(b.y, 540 - b.height));
                        el.box = b;
                    })),
                });
            }
            const tiny = visible.filter(e => e.type !== 'image' && e.type !== 'shape' && pxOf(e.style_overrides?.fontSize) != null && pxOf(e.style_overrides.fontSize) < MIN_FONT_PX);
            if (tiny.length) {
                items.push({
                    kind: 'rule', slideId: s.id, title: 'Text too small to read',
                    detail: `Some text is under ${MIN_FONT_PX}px, which is hard to read from the back of a room.`,
                    summary: `Raised small text to ${MIN_FONT_PX + 2}px`,
                    mutate: m => tiny.forEach(t => onElement(m, t.id, el => {
                        el.style_overrides = { ...(el.style_overrides || {}), fontSize: `${MIN_FONT_PX + 2}px` };
                    })),
                });
            }
            const spilling = SlideTextFit.overflowing(modelOf(s).contents, modelOf(s).elements, { theme: state.selectedTheme });
            if (spilling.length) {
                items.push({
                    kind: 'rule', slideId: s.id, title: 'Text spills out of its box',
                    detail: `${spilling.length === 1 ? 'One box is' : `${spilling.length} boxes are`} too small for ${spilling.length === 1 ? 'its' : 'their'} text, so it runs over what is underneath.`,
                    summary: 'Made the boxes fit their text',
                    mutate: m => { m.elements = SlideTextFit.fit(m.contents, m.elements, { theme: state.selectedTheme }).elements; },
                });
            }
            const words = slideWords(s);
            if (words > DENSE_WORDS) {
                items.push({
                    kind: 'ai', slideId: s.id, title: `A lot of text (${words} words)`,
                    detail: 'Pupils read or listen, not both. Cutting it down keeps attention on you.',
                    instruction: 'Cut the text on this slide to about 50 words, keeping the key terms and the meaning. Keep the layout.',
                });
            }
        });
        return items;
    }

    async function runReview() {
        aiSyncDeck();
        if (ai.review.loading) return;
        if (state.activeSlideId) syncModelFromCurrentDom();
        const modelSlides = deckSlides().map(([, s]) => s).filter(isModelSlide);
        ai.review = { items: layoutChecks(), ran: true, loading: modelSlides.length > 0, skipped: state.slides.size - modelSlides.length };
        renderReview();
        if (!modelSlides.length) return;
        try {
            const res = await apiFetch('/api/slides/ai/review', {
                method: 'POST',
                body: JSON.stringify({
                    title: pptChromeTitle?.textContent || '',
                    slides: modelSlides.map(s => { const m = modelOf(s); return { id: s.id, contents_json: m.contents, elements_json: m.elements }; }),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'The review failed');
            (Array.isArray(data.suggestions) ? data.suggestions : []).forEach(sg => {
                if (!slideEntryById(sg.slide_id)) return;
                ai.review.items.push({ kind: 'ai', slideId: sg.slide_id, title: sg.title, detail: sg.detail, instruction: sg.instruction });
            });
        } catch (err) {
            ai.review.error = err.message === 'Not authenticated' ? '' : (err.message || 'The review failed');
        } finally {
            ai.review.loading = false;
            renderReview();
        }
    }

    function reviewItemsSorted() {
        return ai.review.items
            .filter(i => !i.done)
            .map(i => ({ i, pos: slideEntryById(i.slideId)?.pos || 999 }))
            .sort((a, b) => a.pos - b.pos)
            .map(x => x.i);
    }

    function resolveReviewItem(item) {
        item.done = true;
        renderReview();
    }

    // Free layout fixes are worked out here and previewed like any other
    // change; the AI ones are ordinary edits of their slide.
    function startRuleTurn(title, items) {
        aiSyncDeck();
        if (ai.preview) keepAiPreview();
        if (state.activeSlideId) syncModelFromCurrentDom();
        const bySlide = new Map();
        items.forEach(item => {
            if (!bySlide.has(item.slideId)) bySlide.set(item.slideId, []);
            bySlide.get(item.slideId).push(item);
        });
        const results = [...bySlide.entries()].map(([slideId, list]) => {
            const entry = slideEntryById(slideId);
            if (!entry) return null;
            return ruleResult(slideId, entry.s, m => list.forEach(i => i.mutate(m)), list.map(i => i.summary).join('; '));
        }).filter(r => r && r.changedIds.length);
        if (!results.length) { items.forEach(i => { i.done = true; }); renderReview(); return; }
        const turn = newTurn(title, { slideIds: results.map(r => r.slideId), origin: 'rule', cost: 0, reviewItems: items });
        turn.results = results;
        turn.summary = results.length === 1 ? results[0].summary : `Layout fixed on ${results.length} slides`;
        startAiPreview(turn);
        renderAiLog();
    }

    function fixReviewItem(item) {
        if (ai.busy) return;
        if (item.kind === 'rule') startRuleTurn(item.title, [item]);
        else requestAiEdit({ instruction: item.instruction, slideIds: [item.slideId], origin: 'review', reviewItems: [item] });
    }

    function fixAllRules() {
        if (ai.busy) return;
        const rules = reviewItemsSorted().filter(i => i.kind === 'rule');
        if (rules.length) startRuleTurn(`Fix ${rules.length} layout issue${rules.length === 1 ? '' : 's'}`, rules);
    }

    function renderReview() {
        if (!aiReviewList) return;
        const open = reviewItemsSorted();
        const actionable = open.filter(i => i.kind !== 'info');
        if (aiReviewCount) {
            aiReviewCount.hidden = !actionable.length;
            aiReviewCount.textContent = String(actionable.length);
        }
        paintReviewPins(open);
        if (aiReviewRun) {
            aiReviewRun.disabled = ai.review.loading;
            aiReviewRun.textContent = ai.review.loading ? 'Reviewing…' : ai.review.ran ? 'Review again' : 'Review deck';
        }
        aiReviewList.innerHTML = '';
        if (!ai.review.ran) return;
        const rules = open.filter(i => i.kind === 'rule');
        if (rules.length > 1) {
            const all = document.createElement('button');
            all.type = 'button';
            all.className = 'btn btn-secondary btn-sm ai-review-all';
            all.textContent = `Fix all ${rules.length} layout issues · free`;
            all.addEventListener('click', fixAllRules);
            aiReviewList.appendChild(all);
        }
        open.forEach((item, n) => {
            const pos = slideEntryById(item.slideId)?.pos;
            const card = document.createElement('div');
            card.className = 'ai-sugg';
            card.innerHTML = `<div class="ai-sugg-top"><span class="ai-sugg-n">${n + 1}</span><b>${esc(item.title)}</b>`
                + `<span class="ai-chip">${item.kind === 'ai' ? 'AI' : item.kind === 'rule' ? 'Layout' : 'Note'}</span></div>`
                + `<p class="ai-sugg-detail"><span class="ai-sugg-where">Slide ${pos}</span> ${esc(item.detail || '')}</p>`;
            const acts = document.createElement('div');
            acts.className = 'ai-card-acts';
            const add = (label, cls, fn) => {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = `btn btn-sm ${cls}`;
                b.textContent = label;
                b.disabled = ai.busy;
                b.addEventListener('click', fn);
                acts.appendChild(b);
            };
            if (item.kind !== 'info') add(item.kind === 'ai' ? 'Fix · 1 edit' : 'Fix · free', 'btn-primary', () => fixReviewItem(item));
            add('Show', 'btn-secondary', () => {
                const entry = slideEntryById(item.slideId);
                if (entry && entry.s.id !== state.activeSlideId) selectSlide(entry.s.id, entry.s.html, entry.pos);
            });
            add('Dismiss', 'btn-ghost', () => resolveReviewItem(item));
            card.appendChild(acts);
            aiReviewList.appendChild(card);
        });
        const foot = document.createElement('p');
        foot.className = 'ai-review-foot';
        if (ai.review.loading) foot.textContent = 'Reading the slides for anything a colleague would point out…';
        else if (ai.review.error) foot.textContent = ai.review.error;
        else if (!open.length) foot.textContent = 'Nothing to fix. This deck looks ready to teach.';
        else foot.textContent = '';
        if (ai.review.skipped) foot.textContent += `${foot.textContent ? ' ' : ''}${ai.review.skipped} older slide${ai.review.skipped === 1 ? ' was' : 's were'} not checked.`;
        if (foot.textContent) aiReviewList.appendChild(foot);
    }

    // A count on each thumbnail that has open suggestions.
    function paintReviewPins(open = reviewItemsSorted()) {
        if (!slidePanel) return;
        const per = new Map();
        open.filter(i => i.kind !== 'info').forEach(i => per.set(i.slideId, (per.get(i.slideId) || 0) + 1));
        slidePanel.querySelectorAll('.slide-thumb').forEach(t => {
            let pin = t.querySelector('.ai-pin');
            const n = per.get(t.dataset.slideId) || 0;
            if (!n) { pin?.remove(); return; }
            if (!pin) { pin = document.createElement('span'); pin.className = 'ai-pin'; t.appendChild(pin); }
            pin.textContent = String(n);
            pin.title = `${n} review suggestion${n === 1 ? '' : 's'}`;
        });
    }

    aiReviewRun?.addEventListener('click', runReview);

    // Restored from the last visit; harmless when storage is unavailable.
    try { if (localStorage.getItem('ai-panel-open') === '1' && aiPanel) { aiPanel.hidden = false; aiCopilotBtn?.setAttribute('aria-pressed', 'true'); aiCopilotBtn?.classList.add('is-on'); } } catch {}
    renderAiLog();

    // ── Add Slide ─────────────────────────────────────────

    const addSlideModal       = $('add-slide-modal');
    const addSlideClose       = $('add-slide-close');
    const addSlideInstruction = $('add-slide-instruction');
    const addSlideError       = $('add-slide-error');
    const btnDoAddBlank       = $('btn-do-add-blank');
    const btnDoAddAi          = $('btn-do-add-ai');
    const btnAddSlide         = $('btn-add-slide');

    // ── Starter activities ───────────────────────────────────
    // Recall questions on lessons already taught. The slide dialog and the
    // quiz composer both ask the same thing first - which lessons? - so they
    // share the list. The server caps it at RECALL_MAX and chooses for itself
    // when nothing is sent (utils/doNow.js).
    const RECALL_MAX = 5;
    const RECALL_DEFAULT = 3;

    async function renderRecallList(listEl, { excludeId = '', subject = '' } = {}) {
        if (!listEl) return;
        listEl.innerHTML = '<p class="recall-empty">Loading your lessons…</p>';
        let rows = [];
        try {
            const res = await apiFetch('/api/presentations?limit=30');
            if (res.ok) rows = await res.json();
        } catch { /* falls through to the empty state */ }
        const lessons = rows.filter(p => p.id !== excludeId && p.status === 'done');
        if (!lessons.length) {
            listEl.innerHTML = '<p class="recall-empty">No earlier presentations yet. A starter activity is built from lessons you have already made here.</p>';
            return;
        }
        // Newest first, and the same subject where there is one to go on:
        // last week's history is not a starter for today's maths.
        const sameSubject = subject ? lessons.filter(p => p.subject === subject) : [];
        const ticked = new Set((sameSubject.length ? sameSubject : lessons).slice(0, RECALL_DEFAULT).map(p => p.id));
        listEl.replaceChildren(...lessons.map((p) => {
            const row = document.createElement('label');
            row.className = 'recall-row';
            const box = document.createElement('input');
            box.type = 'checkbox';
            box.value = p.id;
            box.checked = ticked.has(p.id);
            const name = document.createElement('span');
            name.className = 'recall-name';
            name.textContent = p.title || p.topic || 'Untitled';
            const when = document.createElement('span');
            when.className = 'recall-when';
            when.textContent = new Date(p.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
            row.append(box, name, when);
            return row;
        }));
    }

    function recallTicked(listEl) {
        return [...(listEl?.querySelectorAll('input[type="checkbox"]:checked') || [])].map(box => ({
            id: box.value,
            title: box.closest('.recall-row')?.querySelector('.recall-name')?.textContent || '',
        }));
    }

    // One listener for both lists: a sixth tick is refused rather than
    // silently dropped by the server.
    document.addEventListener('change', (e) => {
        const list = e.target.closest?.('.recall-list');
        if (!list || !e.target.checked) return;
        if (recallTicked(list).length > RECALL_MAX) {
            e.target.checked = false;
            showToast(`Up to ${RECALL_MAX} lessons at a time`);
        }
    });

    const btnDoNow      = $('btn-do-now');
    const btnDoNowGo    = $('btn-do-now-go');
    const doNowPanel    = $('do-now-panel');
    const doNowSources  = $('do-now-sources');

    function setDoNowPanel(open) {
        if (!doNowPanel) return;
        doNowPanel.hidden = !open;
        btnDoNow?.setAttribute('aria-expanded', String(!!open));
    }

    btnDoNow?.addEventListener('click', () => {
        const opening = doNowPanel?.hidden;
        setDoNowPanel(opening);
        if (opening) renderRecallList(doNowSources, { excludeId: state.presentationId, subject: state.presentationSubject });
    });

    // The new slides go in near the front, which moves every slide after them
    // along one or two places. Re-reading the deck is simpler, and safer, than
    // renumbering what is held here to match.
    async function reloadDeckSlides(selectSlideId) {
        const res = await apiFetch('/api/presentations/' + state.presentationId);
        if (!res.ok) return;
        const pres = await res.json();
        const sorted = (pres.slides || []).slice().sort((a, b) => a.position - b.position);
        state.slides.clear();
        if (slidePanel) slidePanel.innerHTML = '';
        sorted.forEach((slide) => {
            rememberNotes(slide.id, slide.speaker_notes);
            state.slides.set(slide.position, {
                id: slide.id,
                html: slide.html_content,
                contents_json: parseJson(slide.contents_json),
                elements_json: parseJson(slide.elements_json),
                json_schema_version: slide.json_schema_version,
            });
            updateThumb(slide.position, slide.id, slide.html_content);
        });
        const target = sorted.find(sl => sl.id === selectSlideId) || sorted[0];
        if (target) selectSlide(target.id, target.html_content, target.position);
        placeSeamThumb();
    }

    // Writes the starter and its answers into the open deck, then re-reads
    // the deck. Throws with a message fit to show the teacher.
    async function addStarterActivity(sourceIds) {
        // Anything still unsaved on the stage goes in before the deck is re-read.
        clearTimeout(autoSaveTimer);
        await savePptDirect();
        const res = await apiFetch(`/api/presentations/${state.presentationId}/slides/do-now`, {
            method: 'POST',
            body: JSON.stringify({ sourceIds }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not add the starter activity');
        await reloadDeckSlides(data.slides?.[0]?.slideId);
        return data;
    }

    btnDoNowGo?.addEventListener('click', async () => {
        if (!state.presentationId) return;
        const sourceIds = recallTicked(doNowSources).map(src => src.id);
        if (!sourceIds.length) { setError(addSlideError, 'Tick at least one lesson to recall.'); return; }
        btnDoNowGo.disabled = true;
        btnDoNowGo.textContent = 'Writing questions…';
        setError(addSlideError, '');
        try {
            const data = await addStarterActivity(sourceIds);
            closeAddSlideModal();
            showToast(data.slides?.length > 1 ? 'Starter activity added, with its answers on the next slide' : 'Starter activity added', 3000);
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(addSlideError, err.message || 'Network error');
        } finally {
            btnDoNowGo.disabled = false;
            btnDoNowGo.textContent = 'Add starter activity';
        }
    });

    // The same choice offered up front, in the box a presentation is made
    // from. The lessons ticked there are remembered when Generate is pressed
    // and the starter is written once the deck itself is done.
    const genStarterList = $('gen-starter-list');
    $('gen-starter-toggle')?.addEventListener('click', () => {
        // The shared handler has already flipped the panel by the time this
        // runs or is about to; either way a closed list has nothing to load.
        setTimeout(() => {
            const panel = genStarterList?.closest('[data-src-panel]');
            if (panel && !panel.hidden) renderRecallList(genStarterList, { subject: $('subject')?.value || '' });
        }, 0);
    });

    function genStarterSources() {
        const panel = genStarterList?.closest('[data-src-panel]');
        return panel && !panel.hidden ? recallTicked(genStarterList).map(src => src.id) : [];
    }

    async function addPendingStarter(presId) {
        const sourceIds = state.pendingStarterIds || [];
        state.pendingStarterIds = null;
        if (!sourceIds.length || state.presentationId !== presId) return;
        try {
            await addStarterActivity(sourceIds);
            showToast('Starter activity added after the title slide', 3000);
        } catch (err) {
            if (err.message !== 'Not authenticated') {
                setError(stageError, (err.message || 'Could not add the starter activity')
                    + ' You can add one from "+ Add Slide".');
            }
        }
    }

    function openAddSlideModal() {
        if (!addSlideModal) return;
        setError(addSlideError, '');
        setDoNowPanel(false);
        if (addSlideInstruction) addSlideInstruction.value = '';
        renderAddSlideTemplateChips();
        show(addSlideModal);
        addSlideInstruction?.focus();
    }

    // Templates the teacher made are offered alongside "add blank slide", so a
    // layout they built is one click from being on the deck.
    function renderAddSlideTemplateChips() {
        const wrap = $('add-slide-templates');
        const list = $('add-slide-template-list');
        if (!wrap || !list) return;
        list.replaceChildren();
        const templates = state.customTemplates || [];
        wrap.hidden = templates.length === 0;
        for (const tmpl of templates) {
            const chip = document.createElement('button');
            chip.className = 'tmpl-chip';
            chip.textContent = tmpl.name;
            chip.addEventListener('click', () => addBlankSlide(tmpl.templateId));
            list.appendChild(chip);
        }
    }

    function closeAddSlideModal() {
        hide(addSlideModal);
    }

    addSlideClose?.addEventListener('click', closeAddSlideModal);
    addSlideModal?.addEventListener('click', (e) => {
        if (e.target === addSlideModal || e.target.classList.contains('add-slide-backdrop')) closeAddSlideModal();
    });

    btnAddSlide?.addEventListener('click', () => {
        if (!state.presentationId) return;
        openAddSlideModal();
    });

    addSlideInstruction?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); btnDoAddAi?.click(); }
    });

    function afterSlideAdded(slideId, html, position, model) {
        state.slides.set(position, {
            id: slideId,
            html,
            contents_json: model?.contents_json != null ? parseJson(model.contents_json) : null,
            elements_json: model?.elements_json != null ? parseJson(model.elements_json) : null,
            json_schema_version: model?.json_schema_version ?? null,
        });
        updateThumb(position, slideId, html);
        selectSlide(slideId, html, position);
        if (stageInfo) stageInfo.textContent = `Slide ${position} / ${state.slides.size}`;
        placeSeamThumb();
        closeAddSlideModal();
    }

    // template is a built-in name, or "custom:<slug>" for one the teacher made.
    async function addBlankSlide(template = 'content') {
        if (!state.presentationId) return;
        btnDoAddBlank.disabled = true;
        btnDoAddBlank.textContent = 'Adding…';
        setError(addSlideError, '');
        try {
            const res = await apiFetch(`/api/presentations/${state.presentationId}/slides/blank`, {
                method: 'POST',
                body: JSON.stringify({ template }),
            });
            const data = await res.json();
            if (!res.ok) { setError(addSlideError, data.error || 'Failed to add slide'); return; }
            afterSlideAdded(data.slideId, data.html, data.position, {
                contents_json: data.contents_json,
                elements_json: data.elements_json,
                json_schema_version: data.json_schema_version,
            });
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(addSlideError, 'Network error');
        } finally {
            btnDoAddBlank.disabled = false;
            btnDoAddBlank.textContent = '+ Add blank slide';
        }
    }

    btnDoAddBlank?.addEventListener('click', () => addBlankSlide('content'));

    btnDoAddAi?.addEventListener('click', () => {
        const instruction = addSlideInstruction?.value.trim();
        if (!instruction) { setError(addSlideError, 'Describe what should be on the slide'); return; }
        if (!state.presentationId) return;

        btnDoAddAi.disabled = true;
        btnDoAddAi.textContent = 'Generating…';
        setError(addSlideError, '');

        fetch(`/api/presentations/${state.presentationId}/slides/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ instruction }),
            credentials: 'include',
        }).then(async (res) => {
            if (!res.ok || !res.body) {
                const data = await res.json().catch(() => ({}));
                setError(addSlideError, data.error || 'Generation failed');
                btnDoAddAi.disabled = false; btnDoAddAi.textContent = 'Generate with AI';
                return;
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let lineBuf = '';

            const processLine = (line) => {
                if (!line.startsWith('data: ')) return;
                try {
                    const payload = JSON.parse(line.slice(6));
                    if (payload.message) { setError(addSlideError, payload.message); }
                    if (payload.html !== undefined) {
                        afterSlideAdded(payload.slideId, payload.html, payload.position, {
                            contents_json: payload.contents_json,
                            elements_json: payload.elements_json,
                            json_schema_version: payload.json_schema_version,
                        });
                    }
                } catch {}
            };

            const pump = async () => {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    lineBuf += decoder.decode(value, { stream: true });
                    const lines = lineBuf.split('\n');
                    lineBuf = lines.pop();
                    lines.forEach(processLine);
                }
            };

            pump().catch(() => setError(addSlideError, 'Stream error'))
                  .finally(() => { btnDoAddAi.disabled = false; btnDoAddAi.textContent = 'Generate with AI'; });
        }).catch(err => {
            if (err.message !== 'Not authenticated') setError(addSlideError, 'Network error');
            btnDoAddAi.disabled = false; btnDoAddAi.textContent = 'Generate with AI';
        });
    });

    // ── Seams ─────────────────────────────────────────────
    // A slide asked for where it should go (utils/slideSeam.js). Hovering the
    // gap between two thumbnails shows a "+"; pressing it opens a box to say
    // what goes there, with three ideas written for that gap. What comes back
    // is a draft: it takes the stage, with a dashed thumbnail holding its
    // place, and a bar under it to flick between versions, nudge it ("harder",
    // "other layout": the same content arranged another way) or ask for a
    // change in words. Each of those writes a new
    // version from the last one. Nothing is in the deck until Keep, which the
    // server does from the version it signed; Discard just closes it.
    //
    // The stage underneath is only hidden, never touched, so the slide that
    // was open (and its autosave) is exactly as it was when the draft closes.

    const pptStage = $('ppt-stage');
    let seam = null;        // { afterId, ask, versions, at, busy, error, focused }
    // One set of ideas per deck, the same at every gap, kept in this browser so
    // it is asked for once: across reloads too. An idea made into a slide that
    // was kept is crossed off, and only once all are used is the deck asked again.
    const SEAM_IDEAS_KEY = 'seam-ideas-v2';
    const seamIdeas = (() => {
        let saved = {};
        try { saved = JSON.parse(localStorage.getItem(SEAM_IDEAS_KEY) || '{}') || {}; } catch { /* storage unavailable */ }
        const map = new Map(Object.entries(saved));
        return {
            get: (k) => map.get(k),
            set(k, ideas) {
                map.delete(k);
                map.set(k, ideas);
                // The decks opened most recently are plenty.
                while (map.size > 100) map.delete(map.keys().next().value);
                try { localStorage.setItem(SEAM_IDEAS_KEY, JSON.stringify(Object.fromEntries(map))); } catch { /* full or blocked: memory only */ }
            },
        };
    })();
    let seamIdeasPending = null;   // { deck, promise }: one request however often the box opens

    const seamLine = document.createElement('div');
    seamLine.className = 'seam-line';
    seamLine.hidden = true;
    seamLine.innerHTML = '<button class="seam-plus" type="button" aria-label="Add a slide here" title="Add a slide here">+</button>';
    slidePanelWrap?.append(seamLine);

    const seamPop = document.createElement('form');
    seamPop.className = 'seam-pop';
    seamPop.hidden = true;
    seamPop.innerHTML = `
        <input class="seam-pop-input" type="text" maxlength="600" placeholder="What should go here?" aria-label="What should go here?" autocomplete="off">
        <p class="seam-pop-label">Ideas for here</p>
        <div class="seam-ideas"></div>`;
    document.body.append(seamPop);
    const seamPopInput = seamPop.querySelector('.seam-pop-input');
    const seamIdeasEl = seamPop.querySelector('.seam-ideas');

    const seamView = document.createElement('div');
    seamView.className = 'seam-view';
    seamView.innerHTML = `
        <div class="seam-head"><span class="seam-tag">Draft</span><span class="seam-where"></span></div>
        <div class="seam-canvas"><div class="seam-inner"></div><div class="seam-writing" hidden><span class="seam-spin" aria-hidden="true"></span><span></span></div></div>
        <div class="seam-bar">
            <span class="seam-stepper"><button type="button" data-seam="prev" aria-label="Previous version">‹</button><span class="seam-count"></span><button type="button" data-seam="next" aria-label="Next version">›</button></span>
            <span class="seam-nudges">
                <button type="button" data-nudge="Make it simpler: shorter sentences, easier words.">Simpler</button>
                <button type="button" data-nudge="Make it more challenging for the most able.">Harder</button>
                <button type="button" data-nudge="Add a picture or diagram that helps.">Add a picture</button>
                <button type="button" data-seam="layout">Other layout</button>
                <button type="button" data-seam="another">Another take</button>
            </span>
            <form class="seam-change"><input type="text" maxlength="400" placeholder="Or say what to change…" aria-label="What to change"><button type="submit" aria-label="Make this change" title="Make this change"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6"/></svg></button></form>
            <span class="seam-acts"><button class="btn btn-primary btn-sm" type="button" data-seam="keep">Keep</button><button class="btn btn-ghost btn-sm" type="button" data-seam="discard">Discard</button></span>
        </div>
        <p class="seam-error" hidden></p>`;
    pptStage?.append(seamView);
    const seamCanvas = seamView.querySelector('.seam-canvas');
    const seamInner = seamView.querySelector('.seam-inner');

    const seamThumbs = () => [...(slidePanel?.querySelectorAll('.slide-thumb') || [])];
    const seamNumberOf = (id) => (id ? orderedSlideList().findIndex(s => s.id === id) + 1 : 0);

    function renderSeamSlide(container, s) {
        container.dataset.theme = state.selectedTheme;
        if (s.json_schema_version === 1) SlideModelRenderer.renderSlideHtml(container, s.contents_json, s.elements_json);
        else SlideRenderer.renderSlideHtml(container, s.html);
    }

    // The "+" follows the pointer to whichever gap it is nearest: the space
    // between two thumbnails and the top or bottom edge of each.
    slidePanelWrap?.addEventListener('mousemove', (e) => {
        if (e.target.closest('.seam-line')) return;
        const thumbs = seamThumbs();
        // One draft at a time: asking for another would lose this one.
        const busy = !state.presentationId || thumbs.some(t => t.classList.contains('generating')) || !!seam;
        if (busy || !thumbs.length || !slidePanel.contains(e.target)) { seamLine.hidden = true; return; }
        const wrapTop = slidePanelWrap.getBoundingClientRect().top;
        let gap = null;
        for (let i = 0; i < thumbs.length && !gap; i++) {
            const r = thumbs[i].getBoundingClientRect();
            const next = thumbs[i + 1]?.getBoundingClientRect();
            const edge = r.height * 0.22;
            if (i === 0 && e.clientY < r.top + edge) gap = { afterId: null, y: r.top - 5 };
            else if (e.clientY > r.bottom - edge && (!next || e.clientY < next.top + next.height * 0.22)) {
                gap = { afterId: thumbs[i].dataset.slideId, y: next ? (r.bottom + next.top) / 2 : r.bottom + 5 };
            }
        }
        if (!gap) { seamLine.hidden = true; return; }
        seamLine.hidden = false;
        seamLine.style.top = `${gap.y - wrapTop}px`;
        // As wide as the thumbnails, a little in from their edges, not the whole panel.
        const t = thumbs[0].getBoundingClientRect();
        const wrapLeft = slidePanelWrap.getBoundingClientRect().left;
        seamLine.style.left = `${t.left - wrapLeft + 12}px`;
        seamLine.style.width = `${t.width - 24}px`;
        seamLine.dataset.after = gap.afterId || '';
    });
    slidePanelWrap?.addEventListener('mouseleave', () => { seamLine.hidden = true; });
    slidePanel?.addEventListener('scroll', () => { seamLine.hidden = true; });

    seamLine.querySelector('.seam-plus').addEventListener('click', () => openSeamPop(seamLine.dataset.after || null));

    function openSeamPop(afterId) {
        if (seam) return;
        seamPop.dataset.after = afterId || '';
        seamPop.hidden = false;
        seamPopInput.value = '';
        placeSeamPop();
        seamPopInput.focus();
        drawSeamIdeas(afterId);
    }
    const closeSeamPop = () => { seamPop.hidden = true; };

    /** Where the gap after `afterId` (or before the first slide) is on screen. */
    function seamGapY(afterId) {
        const thumbs = seamThumbs();
        if (!thumbs.length) return null;
        if (!afterId) return thumbs[0].getBoundingClientRect().top - 5;
        const i = thumbs.findIndex(t => t.dataset.slideId === afterId);
        if (i < 0) return null;
        const r = thumbs[i].getBoundingClientRect();
        const next = thumbs[i + 1]?.getBoundingClientRect();
        return next ? (r.bottom + next.top) / 2 : r.bottom + 5;
    }

    // The box stays beside the gap it was opened at: as the list scrolls, and
    // as the ideas arrive and make it taller. It keeps inside the window,
    // moving up rather than running off the bottom, and closes once its gap
    // has scrolled out of the list.
    function placeSeamPop() {
        if (seamPop.hidden) return;
        const y = seamGapY(seamPop.dataset.after || null);
        const list = slidePanel.getBoundingClientRect();
        if (y == null || y < list.top - 4 || y > list.bottom + 4) { closeSeamPop(); return; }
        const right = (slidePanelWrap || slidePanel).getBoundingClientRect().right;
        seamPop.style.left = `${Math.max(12, Math.min(right + 8, window.innerWidth - seamPop.offsetWidth - 12))}px`;
        seamPop.style.top = `${Math.max(12, Math.min(y - 24, window.innerHeight - seamPop.offsetHeight - 12))}px`;
    }
    window.addEventListener('scroll', placeSeamPop, true);
    window.addEventListener('resize', placeSeamPop);
    new ResizeObserver(placeSeamPop).observe(seamPop);

    async function drawSeamIdeas(afterId) {
        const key = state.presentationId;
        const show = (ideas) => {
            if (seamPop.hidden || seamPop.dataset.after !== (afterId || '')) return;
            seamIdeasEl.replaceChildren(...ideas.map((idea) => {
                const b = document.createElement('button');
                b.type = 'button';
                b.textContent = idea;
                b.addEventListener('click', () => startSeam(afterId, idea, true));
                return b;
            }));
            seamPop.querySelector('.seam-pop-label').hidden = !ideas.length;
        };
        if (seamIdeas.get(key)?.length) return show(seamIdeas.get(key));
        seamIdeasEl.innerHTML = '<span class="seam-idea-wait"></span><span class="seam-idea-wait"></span><span class="seam-idea-wait"></span>';
        seamPop.querySelector('.seam-pop-label').hidden = false;
        if (seamIdeasPending?.deck !== key) {
            seamIdeasPending = {
                deck: key,
                promise: apiFetch(`/api/presentations/${key}/seam/ideas`, { method: 'POST', body: '{}' })
                    .then(async (res) => {
                        const data = await res.json().catch(() => ({}));
                        const ideas = res.ok && Array.isArray(data.ideas) ? data.ideas : [];
                        if (ideas.length) seamIdeas.set(key, ideas);
                        return ideas;
                    })
                    .catch(() => [])
                    .finally(() => { if (seamIdeasPending?.deck === key) seamIdeasPending = null; }),
            };
        }
        show(await seamIdeasPending.promise);
    }

    /** An idea that is now a slide in the deck is no longer one to offer. */
    function useUpSeamIdea(idea) {
        const left = (seamIdeas.get(state.presentationId) || []).filter(i => i !== idea);
        seamIdeas.set(state.presentationId, left);
    }

    seamPop.addEventListener('submit', (e) => {
        e.preventDefault();
        const ask = seamPopInput.value.trim();
        if (ask.length >= 2) startSeam(seamPop.dataset.after || null, ask);
    });
    seamPop.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSeamPop(); });
    document.addEventListener('mousedown', (e) => {
        if (!seamPop.hidden && !seamPop.contains(e.target) && !seamLine.contains(e.target)) closeSeamPop();
    });

    function startSeam(afterId, ask, fromIdea = false) {
        closeSeamPop();
        if (seam) return;
        seam = { afterId, ask, fromIdea, versions: [], at: -1, busy: false, error: '', focused: true };
        writeSeam({});
    }

    /**
     * Writes the next version: the first from what they asked, or from the
     * one on show with a change, another take or another layout.
     */
    async function writeSeam({ change = '', layout = false } = {}) {
        if (!seam || seam.busy) return;
        const mine = seam;
        const prev = mine.versions[mine.at];
        Object.assign(mine, { busy: true, error: '', focused: true, writing: !prev ? 'Writing the slide…' : layout ? 'Trying another layout…' : change ? 'Making that change…' : 'Writing another take…' });
        drawSeam();
        try {
            const res = await apiFetch(`/api/presentations/${state.presentationId}/seam/draft`, {
                method: 'POST',
                body: JSON.stringify({
                    after: mine.afterId,
                    ask: mine.ask,
                    previous: prev ? prev.spec : undefined,
                    change: change || undefined,
                    rearrange: layout || undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.draft) throw new Error(data.error || 'That didn’t work. Try again.');
            mine.versions.push(data.draft);
            mine.at = mine.versions.length - 1;
        } catch (err) {
            mine.error = err.message === 'Not authenticated' ? 'You’ve been signed out.' : (err.message || 'That didn’t work. Try again.');
        }
        mine.busy = false;
        if (seam !== mine) return;
        // Nothing to show for a first try that failed: back to the slide it came from.
        if (!mine.versions.length) {
            closeSeam();
            showToast(mine.error, 4000);
            return;
        }
        drawSeam();
    }

    async function keepSeam() {
        if (!seam || seam.busy) return;
        const mine = seam;
        const v = mine.versions[mine.at];
        // The slide it was to follow may have been deleted meanwhile: it goes at the end.
        const after = mine.afterId && seamNumberOf(mine.afterId) ? mine.afterId : (orderedSlideList().pop()?.id || null);
        Object.assign(mine, { busy: true, error: '', writing: 'Adding it to the deck…' });
        drawSeam();
        try {
            clearTimeout(autoSaveTimer);
            await savePptDirect();
            const res = await apiFetch(`/api/presentations/${state.presentationId}/seam/keep`, {
                method: 'POST', body: JSON.stringify({ after, token: v.token }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.slides?.length) throw new Error(data.error || 'It couldn’t be added. Try again.');
            if (mine.fromIdea) useUpSeamIdea(mine.ask);
            closeSeam();
            await reloadDeckSlides(data.slides[0].slideId);
            showToast(v.slides.length > 1 ? 'Added, with its answers on the next slide' : 'Slide added', 2500);
        } catch (err) {
            mine.busy = false;
            mine.error = err.message;
            drawSeam();
        }
    }

    function closeSeam() {
        seam = null;
        slidePanel?.querySelector('.seam-thumb')?.remove();
        pptStage?.classList.remove('is-seam');
        scaleStage();
    }

    /** Back to the slide that was open, with the draft waiting in the rail. */
    function blurSeam() {
        try {
            if (!seam || !seam.focused) return;
            seam.focused = false;
            drawSeam();
        } catch { /* not set up yet */ }
    }

    /** The draft's dashed thumbnail, in the gap it was asked for. */
    function placeSeamThumb() {
        try {
            if (!seam || !slidePanel) return;
            let thumb = slidePanel.querySelector('.seam-thumb');
            if (!thumb) {
                thumb = document.createElement('div');
                // Not a .slide-thumb: reordering reads those, and this isn't a slide yet.
                thumb.className = 'seam-thumb';
                thumb.innerHTML = '<div class="slide-thumb-inner"></div><span class="seam-thumb-tag">Draft</span>';
                thumb.addEventListener('click', () => {
                    if (!seam || seam.focused) return;
                    seam.focused = true;
                    drawSeam();
                });
            }
            const afterThumb = seam.afterId && slidePanel.querySelector(`.slide-thumb[data-slide-id="${CSS.escape(seam.afterId)}"]`);
            if (afterThumb) afterThumb.after(thumb);
            else if (seam.afterId) slidePanel.append(thumb);
            else slidePanel.prepend(thumb);
            thumb.classList.toggle('active', seam.focused);
            thumb.classList.toggle('is-busy', seam.busy);
            const v = seam.versions[seam.at];
            const inner = thumb.querySelector('.slide-thumb-inner');
            if (v && inner.dataset.shown !== `${seam.at}`) {
                renderSeamSlide(inner, v.slides[0]);
                inner.dataset.shown = `${seam.at}`;
            }
            if (seam.focused) slidePanel.querySelectorAll('.slide-thumb.active').forEach(t => t.classList.remove('active'));
            else slidePanel.querySelectorAll('.slide-thumb').forEach(t => t.classList.toggle('active', t.dataset.slideId === state.activeSlideId));
        } catch { /* not set up yet */ }
    }

    function scaleSeam() {
        const w = seamCanvas.offsetWidth;
        if (w) seamInner.style.transform = `scale(${w / 960})`;
    }
    new ResizeObserver(scaleSeam).observe(seamCanvas);

    function drawSeam() {
        if (!seam) return;
        placeSeamThumb();
        pptStage?.classList.toggle('is-seam', seam.focused);
        if (!seam.focused) { scaleStage(); return; }
        const v = seam.versions[seam.at];
        const n = seamNumberOf(seam.afterId);
        // What a teacher can't see on a draft: how much of it arrives on a click.
        const clicks = v ? (v.slides[0].elements_json || []).reduce((a, el) => a + (el.reveal ? (el.reveal_items ? (v.slides[0].contents_json?.[el.content_id]?.items?.length || 1) : 1) : 0), 0) : 0;
        seamView.querySelector('.seam-where').textContent = `${n ? `After slide ${n}` : 'At the start'} · “${seam.ask}”${clicks ? ` · ${clicks} ${clicks === 1 ? 'part arrives' : 'parts arrive'} on a click` : ''}${v && v.slides.length > 1 ? ' · with its answers slide' : ''}`;
        if (v && seamInner.dataset.shown !== `${seam.at}:${seam.versions.length}`) {
            renderSeamSlide(seamInner, v.slides[0]);
            seamInner.dataset.shown = `${seam.at}:${seam.versions.length}`;
        }
        seamInner.hidden = !v;
        const writing = seamView.querySelector('.seam-writing');
        writing.hidden = !seam.busy;
        writing.lastElementChild.textContent = seam.writing || '';
        seamView.classList.toggle('is-busy', seam.busy);
        seamView.querySelector('.seam-count').textContent = v ? `${seam.at + 1} / ${seam.versions.length}` : '…';
        seamView.querySelector('[data-seam="prev"]').disabled = seam.busy || seam.at <= 0;
        seamView.querySelector('[data-seam="next"]').disabled = seam.busy || seam.at >= seam.versions.length - 1;
        seamView.querySelectorAll('.seam-bar button, .seam-bar input').forEach((el) => {
            if (!el.matches('[data-seam="prev"], [data-seam="next"], [data-seam="discard"]')) el.disabled = seam.busy || !v;
        });
        const err = seamView.querySelector('.seam-error');
        err.hidden = !seam.error;
        err.textContent = seam.error;
        scaleSeam();
    }

    seamView.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b || !seam || b.disabled) return;
        if (b.dataset.nudge) return writeSeam({ change: b.dataset.nudge });
        const act = b.dataset.seam;
        if (act === 'prev' || act === 'next') {
            seam.at = Math.max(0, Math.min(seam.versions.length - 1, seam.at + (act === 'prev' ? -1 : 1)));
            drawSeam();
        } else if (act === 'layout') writeSeam({ layout: true });
        else if (act === 'another') writeSeam({});
        else if (act === 'keep') keepSeam();
        else if (act === 'discard') closeSeam();
    });
    seamView.querySelector('.seam-change').addEventListener('submit', (e) => {
        e.preventDefault();
        const input = e.currentTarget.querySelector('input');
        const change = input.value.trim();
        if (!change || !seam || seam.busy) return;
        input.value = '';
        writeSeam({ change });
    });

    // ── Worksheet page ────────────────────────────────────
    const worksheetTopicEl   = $('worksheet-topic');
    const wsContext          = $('ws-context');
    const wsExamBoard        = $('ws-exam-board');
    const wsSourcePres       = $('ws-source-pres');
    const wsSubject          = $('ws-subject');
    const wsLevel            = $('ws-level');
    const worksheetError     = $('worksheet-error');
    const wsPageForm         = $('ws-page-form');
    const wsPagePreview      = $('ws-page-preview');
    const wsSheetCanvas      = $('ws-sheet-canvas');
    const wsSheetScaler      = $('ws-sheet-scaler');
    const wsSheetTabs        = $('ws-sheet-tabs');

    // The other sheet of a main/supported pair is fetched as soon as this one
    // opens, so a tab switch waits on nothing but the save of the sheet being
    // left - and not even that, since the save has its copy of the sheet
    // before it goes. A fetch of a sheet just left waits for its save, or it
    // would bring back the version before the last edit.
    const wsPairCache = new Map();          // worksheet id -> Promise of it (or null)
    let wsPairSaving = Promise.resolve();
    function prefetchWsPair(id) {
        if (!id || wsPairCache.has(id)) return;
        wsPairCache.set(id, wsPairSaving
            .then(() => apiFetch(`/api/worksheets/${id}`))
            .then(r => (r.ok ? r.json() : null))
            .catch(() => null));
    }
    const wsBarTitle         = $('ws-bar-title');
    const wsSheetFrame       = $('ws-sheet-frame');
    const wsSaveStatus       = $('ws-save-status');
    const btnGenerateWs      = $('btn-generate-worksheet');
    const btnWsBack          = $('btn-ws-back');
    const btnWsPrint         = $('btn-ws-print');
    const wsA11ySelect       = $('ws-a11y-select');
    const wsTintSelect       = $('ws-tint-select');
    const wsListEl           = $('ws-list');
    const wsListError        = $('ws-list-error');

    // The panel's height is driven by the shared .t-acc grid track, which reads
    // data-open – a class alone leaves it shut.
    function setWsContextOpen(open) { setSourcePanel('ws-context', 'notes', open); }

    // ── Composer steps ────────────────────────────────────
    //
    // Curriculum → topic → questions, one at a time. The curriculum leads
    // because it decides whether the topic step can offer a tick list from the
    // board's specification; it is still optional, and only the topic gates
    // anything, so the wizard is a reading order, not a set of hurdles.
    const wsWizard = initComposerWizard({
        formId: 'ws-page-form',
        submitId: 'btn-generate-worksheet',
        nextLabels: ['Next: questions →'],
        hints: [
            'A topic, your own files, or both.',
            'Generating uses one of your monthly worksheet allowances – two for 26 questions or more.',
        ],
        isReady: () => {
            if (!topicLineFor(worksheetTopicEl, wsTopicPicker)) {
                setError(worksheetError, 'Type a topic, add a file, or tick topics from the specification');
                worksheetTopicEl?.focus();
                return false;
            }
            setError(worksheetError, '');
            return true;
        },
    });


    let currentWorksheetData = null;  // sections format: { title, sections: [] }
    let currentWorksheetId   = null;  // null = not saved yet
    // The other sheet of a main/supported pair: { id, role, supports, language }
    // where role is the OTHER sheet's. null when there is no pair.
    let currentWorksheetPair = null;
    let worksheetReturnTo    = 'list';
    let wsAutoSaveTimer      = null;

    // Single source of truth lives in /js/worksheet-render.js
    const WS_SECTION_LABELS       = WorksheetRender.WS_SECTION_LABELS;
    const WS_SECTION_INSTRUCTIONS = WorksheetRender.WS_SECTION_INSTRUCTIONS;
    const WS_TYPE_ORDER = ['multiple_choice', 'true_false', 'fill_blank', 'matching', 'short_answer', 'extended_response', 'whiteboard'];

    function normalizeWorksheetData(raw) {
        // A sheet the teacher described comes back already in sections, in the
        // order they asked for (see describedSections in utils/worksheetGen.js),
        // and sorting it by type would undo the point of describing it.
        const sections = Array.isArray(raw.sections)
            ? raw.sections.filter(s => s && WS_TYPE_ORDER.includes(s.type) && Array.isArray(s.questions))
                .map(s => ({ ...s, questions: s.questions.map(q => ({ ...q })) }))
            : [];
        if (!Array.isArray(raw.sections)) WS_TYPE_ORDER.forEach(type => {
            const qs = raw[type] || [];
            if (!qs.length) return;
            sections.push({ type, questions: qs.map(q => ({ ...q })) });
        });
        // Carried onto data_json rather than dropped: which supports a sheet
        // was built with cannot be recovered by reading the questions back,
        // and both the editor and the Marketplace listing need to know.
        const out = { title: raw.title || '', sections };
        if (Array.isArray(raw.supports) && raw.supports.length) out.supports = raw.supports;
        // The home language is part of what the document is, not a setting of
        // the run that made it: the printed sheet reads it back off here.
        if (raw.language) out.language = raw.language;
        // The key-terms glossary is part of the document, not a question type,
        // so it survives here or not at all – the section loop above only
        // knows about question types.
        if (Array.isArray(raw.glossary) && raw.glossary.length) out.glossary = raw.glossary;
        // A primary-level sheet comes back from /generate already Bright.
        if (WorksheetRender.themeOf(raw) !== 'classic') out.theme = raw.theme;
        return out;
    }

    // The sheet's title, in the bar above it. Kept in step from here and from
    // every redraw, since a title typed on the sheet redraws nothing.
    function syncWsBarTitle() {
        if (wsBarTitle) wsBarTitle.textContent = currentWorksheetData?.title || 'Untitled worksheet';
    }

    function scheduleWsSave() {
        clearTimeout(wsAutoSaveTimer);
        syncWsBarTitle();
        if (wsSaveStatus) wsSaveStatus.textContent = '';
        wsAutoSaveTimer = setTimeout(saveWorksheet, 1000);
    }

    async function saveWorksheet() {
        if (!currentWorksheetData) return;
        const body = {
            title:     currentWorksheetData.title || '',
            topic:     topicLineFor(worksheetTopicEl, wsTopicPicker),
            examBoard: wsExamBoard?.value || '',
            subject:   wsSubject?.value.trim() || '',
            level:     wsLevel?.value || '',
            sourceType: wsSourcePres?.value ? 'presentation' : undefined,
            sourceId:   wsSourcePres?.value || undefined,
            data_json: currentWorksheetData,
        };
        try {
            if (wsSaveStatus) wsSaveStatus.textContent = 'Saving…';
            let res;
            if (currentWorksheetId) {
                res = await apiFetch(`/api/worksheets/${currentWorksheetId}`, {
                    method: 'PUT', body: JSON.stringify(body),
                });
            } else {
                res = await apiFetch('/api/worksheets', {
                    method: 'POST', body: JSON.stringify(body),
                });
                if (res.ok) {
                    const d = await res.json();
                    currentWorksheetId = d.id;
                    linkToTutoringSession('worksheet', d.id);
                    // It has an id now, so it can be set to a class.
                    syncReadingSetWork();
                    // Switch URL from /worksheets → /worksheets/:id
                    if (window.location.pathname !== `/worksheets/${d.id}`) {
                        history.replaceState({ worksheetId: d.id }, '', `/worksheets/${d.id}`);
                    }
                }
            }
            if (wsSaveStatus) {
                wsSaveStatus.textContent = res.ok ? 'Saved' : 'Save failed';
                setTimeout(() => { if (wsSaveStatus) wsSaveStatus.textContent = ''; }, 2000);
            }
        } catch {
            if (wsSaveStatus) wsSaveStatus.textContent = '';
        }
    }

    // ── Worksheet editor ──────────────────────────────────

    function escWs(str) {
        return String(str ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    }

    function escAttr(str) {
        return String(str ?? '').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    }

    function makeEmptyQuestion(type) {
        if (type === 'multiple_choice')   return { question: '', a: '', b: '', c: '', d: '', answer: 'a' };
        if (type === 'true_false')        return { statement: '', answer: true };
        if (type === 'fill_blank')        return { sentence: '', answer: '' };
        if (type === 'matching')          return { instruction: 'Match each term to its definition.', pairs: [{ left: '', right: '' }, { left: '', right: '' }, { left: '', right: '' }] };
        if (type === 'extended_response') return { question: '', guidance: '', marks: 8, modelAnswer: '' };
        if (type === 'whiteboard')        return { question: '', answer: '' };
        return { question: '', answer: '', marks: 2 };
    }

    function renumberQuestions(container) {
        if (!container) return;
        container.querySelectorAll('.ws-q-num').forEach((el, i) => { el.textContent = i + 1; });
    }

    const PART_LETTERS = 'abcdefghijklmnop';

    /**
     * Editable grid for a part's data table (exam-style generation only –
     * teacher-authored parts never get a `table`). Column count comes from
     * generation; rows can be added/removed, but no add/remove-column UI
     * here, to keep this bounded like everything else in the parts editor.
     */
    function buildPartTableEditor(part, saveFn) {
        const wrap = document.createElement('div');
        wrap.className = 'ws-part-table-editor';
        const grid = document.createElement('div');
        grid.className = 'ws-table-grid';
        wrap.appendChild(grid);

        const renderGrid = () => {
            grid.innerHTML = '';
            const table = part.table;
            grid.style.setProperty('--ws-table-cols', table.headers.length);

            const headerRow = document.createElement('div');
            headerRow.className = 'ws-table-row';
            table.headers.forEach((h, c) => {
                const inp = document.createElement('input');
                inp.className = 'ws-edit-input ws-table-cell';
                inp.value = h;
                inp.placeholder = 'Header';
                inp.addEventListener('input', () => { table.headers[c] = inp.value; saveFn(); });
                headerRow.appendChild(inp);
            });
            headerRow.appendChild(document.createElement('span'));
            grid.appendChild(headerRow);

            table.rows.forEach((row, r) => {
                const rowEl = document.createElement('div');
                rowEl.className = 'ws-table-row';
                row.forEach((cell, c) => {
                    const inp = document.createElement('input');
                    inp.className = 'ws-edit-input ws-table-cell';
                    inp.value = cell;
                    inp.addEventListener('input', () => { table.rows[r][c] = inp.value; saveFn(); });
                    rowEl.appendChild(inp);
                });
                const delBtn = document.createElement('button');
                delBtn.type = 'button';
                delBtn.className = 'ws-del-q';
                delBtn.title = 'Remove row';
                delBtn.textContent = '✕';
                delBtn.addEventListener('click', () => { table.rows.splice(r, 1); renderGrid(); saveFn(); });
                rowEl.appendChild(delBtn);
                grid.appendChild(rowEl);
            });
        };
        renderGrid();

        const controls = document.createElement('div');
        controls.className = 'ws-table-controls';
        const addRowBtn = document.createElement('button');
        addRowBtn.type = 'button';
        addRowBtn.className = 'ws-add-part-btn';
        addRowBtn.textContent = '+ Add row';
        addRowBtn.addEventListener('click', () => {
            part.table.rows.push(part.table.headers.map(() => ''));
            renderGrid();
            saveFn();
        });
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'ws-add-part-btn';
        removeBtn.textContent = 'Remove table';
        removeBtn.addEventListener('click', () => {
            delete part.table;
            wrap.remove();
            saveFn();
        });
        controls.append(addRowBtn, removeBtn);
        wrap.appendChild(controls);
        return wrap;
    }

    /** A single labelled input bound directly to one key of a part's data object. */
    function buildFieldInput(label, obj, key, type, saveFn) {
        const lbl = document.createElement('label');
        lbl.className = 'ws-graph-field';
        const inp = document.createElement('input');
        inp.className = 'ws-edit-input';
        inp.type = type;
        if (type === 'number') inp.step = 'any';
        inp.value = obj[key] ?? '';
        inp.addEventListener('input', () => {
            obj[key] = type === 'number' ? (parseFloat(inp.value) || 0) : inp.value;
            saveFn();
        });
        lbl.append(label + ': ', inp);
        return lbl;
    }

    /** Editable axis config + point list for a part's graph (exam-style generation only). */
    function buildPartGraphEditor(part, saveFn) {
        const wrap = document.createElement('div');
        wrap.className = 'ws-part-graph-editor';

        const fieldsRow = document.createElement('div');
        fieldsRow.className = 'ws-graph-fields';
        fieldsRow.append(
            buildFieldInput('X label', part.graph, 'xLabel', 'text', saveFn),
            buildFieldInput('Y label', part.graph, 'yLabel', 'text', saveFn),
            buildFieldInput('X min', part.graph, 'xMin', 'number', saveFn),
            buildFieldInput('X max', part.graph, 'xMax', 'number', saveFn),
            buildFieldInput('Y min', part.graph, 'yMin', 'number', saveFn),
            buildFieldInput('Y max', part.graph, 'yMax', 'number', saveFn),
        );
        wrap.appendChild(fieldsRow);

        const pointsGrid = document.createElement('div');
        pointsGrid.className = 'ws-graph-points';
        wrap.appendChild(pointsGrid);

        const renderPoints = () => {
            pointsGrid.innerHTML = '';
            part.graph.points.forEach((pt, i) => {
                const row = document.createElement('div');
                row.className = 'ws-graph-point-row';
                const xIn = document.createElement('input');
                xIn.className = 'ws-edit-input'; xIn.type = 'number'; xIn.step = 'any'; xIn.placeholder = 'x'; xIn.value = pt.x;
                xIn.addEventListener('input', () => { pt.x = parseFloat(xIn.value) || 0; saveFn(); });
                const yIn = document.createElement('input');
                yIn.className = 'ws-edit-input'; yIn.type = 'number'; yIn.step = 'any'; yIn.placeholder = 'y'; yIn.value = pt.y;
                yIn.addEventListener('input', () => { pt.y = parseFloat(yIn.value) || 0; saveFn(); });
                const delBtn = document.createElement('button');
                delBtn.type = 'button'; delBtn.className = 'ws-del-q'; delBtn.title = 'Remove point'; delBtn.textContent = '✕';
                delBtn.addEventListener('click', () => { part.graph.points.splice(i, 1); renderPoints(); saveFn(); });
                row.append(xIn, yIn, delBtn);
                pointsGrid.appendChild(row);
            });
        };
        renderPoints();

        const controls = document.createElement('div');
        controls.className = 'ws-table-controls';
        const addBtn = document.createElement('button');
        addBtn.type = 'button'; addBtn.className = 'ws-add-part-btn'; addBtn.textContent = '+ Add point';
        addBtn.addEventListener('click', () => { part.graph.points.push({ x: 0, y: 0 }); renderPoints(); saveFn(); });
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button'; removeBtn.className = 'ws-add-part-btn'; removeBtn.textContent = 'Remove graph';
        removeBtn.addEventListener('click', () => { delete part.graph; wrap.remove(); saveFn(); });
        controls.append(addBtn, removeBtn);
        wrap.appendChild(controls);
        return wrap;
    }

    /** Editable energy values + labels for a part's reaction-profile diagram. */
    function buildPartReactionProfileEditor(part, saveFn) {
        const wrap = document.createElement('div');
        wrap.className = 'ws-part-profile-editor';
        const fieldsRow = document.createElement('div');
        fieldsRow.className = 'ws-graph-fields';
        fieldsRow.append(
            buildFieldInput('Start energy', part.reactionProfile, 'startEnergy', 'number', saveFn),
            buildFieldInput('Peak energy', part.reactionProfile, 'peakEnergy', 'number', saveFn),
            buildFieldInput('End energy', part.reactionProfile, 'endEnergy', 'number', saveFn),
            buildFieldInput('Reactants label', part.reactionProfile, 'substancesLabel', 'text', saveFn),
            buildFieldInput('Products label', part.reactionProfile, 'productsLabel', 'text', saveFn),
        );
        wrap.appendChild(fieldsRow);
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button'; removeBtn.className = 'ws-add-part-btn'; removeBtn.textContent = 'Remove diagram';
        removeBtn.addEventListener('click', () => { delete part.reactionProfile; wrap.remove(); saveFn(); });
        wrap.appendChild(removeBtn);
        return wrap;
    }

    /** Editable symbol/charge + per-shell electron counts for a part's electron-shell diagram. */
    function buildPartElectronShellsEditor(part, saveFn) {
        const wrap = document.createElement('div');
        wrap.className = 'ws-part-shells-editor';
        const fieldsRow = document.createElement('div');
        fieldsRow.className = 'ws-graph-fields';
        fieldsRow.append(
            buildFieldInput('Symbol', part.electronShells, 'symbol', 'text', saveFn),
            buildFieldInput('Charge', part.electronShells, 'charge', 'text', saveFn),
        );
        wrap.appendChild(fieldsRow);

        const shellsRow = document.createElement('div');
        shellsRow.className = 'ws-graph-points';
        wrap.appendChild(shellsRow);
        const renderShells = () => {
            shellsRow.innerHTML = '';
            part.electronShells.shells.forEach((n, i) => {
                const row = document.createElement('div');
                row.className = 'ws-graph-point-row ws-shell-row';
                const inp = document.createElement('input');
                inp.className = 'ws-edit-input'; inp.type = 'number'; inp.min = '0'; inp.placeholder = `Shell ${i + 1}`; inp.value = n;
                inp.addEventListener('input', () => { part.electronShells.shells[i] = parseInt(inp.value, 10) || 0; saveFn(); });
                const delBtn = document.createElement('button');
                delBtn.type = 'button'; delBtn.className = 'ws-del-q'; delBtn.title = 'Remove shell'; delBtn.textContent = '✕';
                delBtn.addEventListener('click', () => { part.electronShells.shells.splice(i, 1); renderShells(); saveFn(); });
                row.append(inp, delBtn);
                shellsRow.appendChild(row);
            });
        };
        renderShells();

        const controls = document.createElement('div');
        controls.className = 'ws-table-controls';
        const addBtn = document.createElement('button');
        addBtn.type = 'button'; addBtn.className = 'ws-add-part-btn'; addBtn.textContent = '+ Add shell';
        addBtn.addEventListener('click', () => { part.electronShells.shells.push(1); renderShells(); saveFn(); });
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button'; removeBtn.className = 'ws-add-part-btn'; removeBtn.textContent = 'Remove diagram';
        removeBtn.addEventListener('click', () => { delete part.electronShells; wrap.remove(); saveFn(); });
        controls.append(addBtn, removeBtn);
        wrap.appendChild(controls);
        return wrap;
    }

    /** Editable label + passage text for a part's source extract. */
    function buildPartSourceEditor(part, saveFn) {
        const wrap = document.createElement('div');
        wrap.className = 'ws-part-source-editor';
        const labelIn = document.createElement('input');
        labelIn.className = 'ws-edit-input';
        labelIn.type = 'text';
        labelIn.placeholder = 'Source label (e.g. Source A)';
        labelIn.value = part.source.label || '';
        labelIn.addEventListener('input', () => { part.source.label = labelIn.value; saveFn(); });

        const textIn = document.createElement('textarea');
        textIn.className = 'ws-edit-input';
        textIn.rows = 3;
        textIn.placeholder = 'Source text';
        textIn.value = part.source.text || '';
        textIn.addEventListener('input', () => { part.source.text = textIn.value; saveFn(); });

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button'; removeBtn.className = 'ws-add-part-btn'; removeBtn.textContent = 'Remove source';
        removeBtn.addEventListener('click', () => { delete part.source; wrap.remove(); saveFn(); });

        wrap.append(labelIn, textIn, removeBtn);
        return wrap;
    }

    /** Editable language + preformatted text for a part's code/pseudocode block. */
    function buildPartCodeEditor(part, saveFn) {
        const wrap = document.createElement('div');
        wrap.className = 'ws-part-code-editor';
        const langIn = document.createElement('input');
        langIn.className = 'ws-edit-input';
        langIn.type = 'text';
        langIn.placeholder = 'Language (e.g. python, pseudocode)';
        langIn.value = part.code.language || '';
        langIn.addEventListener('input', () => { part.code.language = langIn.value; saveFn(); });

        const textIn = document.createElement('textarea');
        textIn.className = 'ws-edit-input ws-code-editor-text';
        textIn.rows = 8;
        textIn.spellcheck = false;
        textIn.placeholder = 'Code / pseudocode - indentation and line breaks are preserved exactly as typed';
        textIn.value = part.code.text || '';
        textIn.addEventListener('input', () => { part.code.text = textIn.value; saveFn(); });

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button'; removeBtn.className = 'ws-add-part-btn'; removeBtn.textContent = 'Remove code block';
        removeBtn.addEventListener('click', () => { delete part.code; wrap.remove(); saveFn(); });

        wrap.append(langIn, textIn, removeBtn);
        return wrap;
    }

    /**
     * Wires the "+ Add part" editor shared by short_answer and
     * extended_response: lettered sub-questions, each with its own marks and
     * indicative answer. Presence of `q.parts` entries is the mode switch –
     * when parts exist, `simpleEl` (the plain marks/answer fields) is hidden
     * rather than tracked with a separate flag.
     */
    function wirePartsEditor(fields, q, saveFn, simpleEl) {
        const partsEl = fields.querySelector('.ws-parts');
        const addBtn = fields.querySelector('.ws-add-part-btn');

        const updateMode = () => {
            const has = Array.isArray(q.parts) && q.parts.length > 0;
            if (simpleEl) simpleEl.style.display = has ? 'none' : '';
        };

        const addPartRow = (part) => {
            const pr = document.createElement('div');
            pr.className = 'ws-part-row';
            pr.innerHTML = `
                <span class="ws-part-letter">(<span class="ws-part-letter-text"></span>)</span>
                <input class="ws-edit-input ws-part-text" type="text" placeholder="Sub-question text">
                <label class="ws-answer-label">Marks: <input class="ws-edit-input ws-marks-input ws-part-marks" type="number" min="1" max="20"></label>
                <textarea class="ws-edit-input ws-part-answer" rows="2" placeholder="Indicative answer (optional)"></textarea>
                <button class="ws-del-q ws-part-del-btn" type="button" title="Remove part">✕</button>`;
            const textIn = pr.querySelector('.ws-part-text');
            const marksIn = pr.querySelector('.ws-part-marks');
            const ansIn = pr.querySelector('.ws-part-answer');
            textIn.value = part.text || '';
            marksIn.value = part.marks ?? 2;
            ansIn.value = part.answer || '';
            textIn.addEventListener('input', () => { part.text = textIn.value; saveFn(); });
            marksIn.addEventListener('input', () => { part.marks = parseInt(marksIn.value, 10) || 1; saveFn(); });
            ansIn.addEventListener('input', () => { part.answer = ansIn.value; saveFn(); });
            const insertPoint = pr.querySelector('.ws-part-answer');
            if (part.table) pr.insertBefore(buildPartTableEditor(part, saveFn), insertPoint);
            if (part.graph) pr.insertBefore(buildPartGraphEditor(part, saveFn), insertPoint);
            if (part.reactionProfile) pr.insertBefore(buildPartReactionProfileEditor(part, saveFn), insertPoint);
            if (part.electronShells) pr.insertBefore(buildPartElectronShellsEditor(part, saveFn), insertPoint);
            if (part.source) pr.insertBefore(buildPartSourceEditor(part, saveFn), insertPoint);
            if (part.code) pr.insertBefore(buildPartCodeEditor(part, saveFn), insertPoint);
            pr.querySelector('.ws-part-del-btn').addEventListener('click', () => {
                const pi = q.parts.indexOf(part);
                if (pi !== -1) q.parts.splice(pi, 1);
                pr.remove();
                relabel();
                updateMode();
                saveFn();
            });
            partsEl.appendChild(pr);
        };

        const relabel = () => {
            partsEl.querySelectorAll('.ws-part-letter-text').forEach((el, i) => {
                el.textContent = PART_LETTERS[i] || String(i + 1);
            });
        };

        (q.parts || []).forEach(addPartRow);
        relabel();
        updateMode();

        addBtn.addEventListener('click', () => {
            if (!Array.isArray(q.parts)) q.parts = [];
            const part = { text: '', marks: 2, answer: '' };
            q.parts.push(part);
            addPartRow(part);
            relabel();
            updateMode();
            saveFn();
            partsEl.lastElementChild?.querySelector('.ws-part-text')?.focus();
        });
    }

    // ---- Rendered maths in the question editors -------------------------
    //
    // Maths questions come out of the generator with real LaTeX in them
    // ($0$, $250\,000$). The editor has to hand you the source, because that
    // is the only thing there is to type into - but a teacher scanning a quiz
    // was being shown the source of every field at once, which reads as a
    // bug. So a text field renders its maths whenever it is not the field
    // being edited: click into the box and you get the LaTeX back.
    //
    // The overlay sits inside the input's own border and is pointer-transparent,
    // so a click lands on the input underneath and focus hides the overlay in
    // the same tick - there is nothing to click "through".
    const MATH_FIELD_RE = /\$\$?[^$\n]+\$\$?/;

    function attachMathPreview(input) {
        if (input._mathPreview || typeof MathRender === 'undefined' || !window.katex) return;

        const multiline = input.tagName === 'TEXTAREA';
        const wrap = document.createElement('span');
        wrap.className = 'ws-math-field' + (multiline ? ' ws-math-field-block' : '');

        // The wrapper takes the field's place in the layout, so anything the
        // stylesheet placed by grid area (the sub-question rows) has to be
        // carried across - otherwise the wrapped field auto-places and the row
        // falls apart. Read before the move, and left alone for flex parents,
        // which the .ws-math-field rule already handles.
        const parentStyle = getComputedStyle(input.parentNode);
        if (parentStyle.display === 'grid' || parentStyle.display === 'inline-grid') {
            const cs = getComputedStyle(input);
            wrap.style.gridArea = [cs.gridRowStart, cs.gridColumnStart, cs.gridRowEnd, cs.gridColumnEnd].join(' / ');
        }

        input.parentNode.insertBefore(wrap, input);
        wrap.appendChild(input);

        const preview = document.createElement('span');
        preview.className = 'ws-math-preview' + (multiline ? ' ws-math-preview-block' : '');
        // Decorative: the input it covers is the accessible field, and a
        // screen reader should read the source, not KaTeX's duplicated markup.
        preview.setAttribute('aria-hidden', 'true');
        wrap.appendChild(preview);
        input._mathPreview = preview;

        const update = () => {
            const on = document.activeElement !== input && MATH_FIELD_RE.test(input.value);
            if (on) {
                // One wrapper element, not the runs on their own: the overlay is
                // a flex box, and flex drops the whitespace between its items -
                // which would eat the space in front of "$-7 + 12 - 5$".
                preview.innerHTML = '<span class="ws-math-preview-line">'
                    + MathRender.renderTextWithMath(input.value) + '</span>';
                // Copied rather than set in CSS: these fields come in several
                // type sizes (.ws-q-main is larger, the answer boxes smaller)
                // and the overlay has to match whichever it is covering. The
                // line height matters too, so a wrapped answer sits on the
                // same lines the textarea would have used.
                const cs = getComputedStyle(input);
                preview.style.fontSize   = cs.fontSize;
                preview.style.lineHeight = cs.lineHeight;
            }
            preview.style.display = on ? '' : 'none';
            input.classList.toggle('ws-math-masked', on);
        };

        input.addEventListener('focus', update);
        input.addEventListener('blur', update);
        input.addEventListener('input', update);
        update();
    }

    // Text fields, single- and multi-line: question stems, options, the
    // indicative-answer boxes and the model answers all get maths. Number and
    // select fields never hold any, and the code editors are deliberately
    // excluded - a "$" in a code block is a shell variable, not maths.
    const MATH_FIELD_SELECTOR =
        'input.ws-edit-input[type="text"], textarea.ws-edit-input:not(.ws-code-editor-text)';

    function scanMathPreviews(root) {
        if (!root || root.nodeType !== 1) return;
        root.querySelectorAll(MATH_FIELD_SELECTOR).forEach(attachMathPreview);
        if (root.matches?.(MATH_FIELD_SELECTOR)) attachMathPreview(root);
    }

    // One observer per editor rather than a call in each builder: question
    // rows, sub-parts and matching pairs are all added from different places,
    // and every one of them should get the same treatment.
    function watchMathPreviews(container) {
        if (!container || container._mathWatched) return;
        container._mathWatched = true;
        scanMathPreviews(container);
        new MutationObserver(records => {
            records.forEach(r => r.addedNodes.forEach(scanMathPreviews));
        }).observe(container, { childList: true, subtree: true });
    }

    /**
     * The fields that do NOT print on a student copy: the correct answer, the
     * marks, the indicative/model answer.
     *
     * Split out of buildQuestionRow because the worksheet editor now edits
     * everything that prints on the sheet itself and needs somewhere to put
     * the rest - the inspector panel renders exactly this, and the quiz
     * editor's form rows go on interpolating it where they always had it, so
     * there is one definition of what "Answer:" looks like rather than two.
     *
     * `opts.withMarks` adds a marks box for short_answer. Only the inspector
     * asks for it: the form row never had one, and a quiz is not marked out
     * of anything per question.
     */
    function answerFieldsHtml(type, q, opts) {
        opts = opts || {};
        if (type === 'matching') return '';   // the pairs ARE the answer, and they print

        if (type === 'multiple_choice') return `
                <label class="ws-answer-label">Answer: <select class="ws-answer-select">
                    <option value="a"${q.answer==='a'?' selected':''}>A</option>
                    <option value="b"${q.answer==='b'?' selected':''}>B</option>
                    <option value="c"${q.answer==='c'?' selected':''}>C</option>
                    ${q.d !== undefined ? `<option value="d"${q.answer==='d'?' selected':''}>D</option>` : ''}
                </select></label>`;

        if (type === 'true_false') return `
                <label class="ws-answer-label">Answer: <select class="ws-answer-select">
                    <option value="true"${q.answer===true?' selected':''}>True</option>
                    <option value="false"${q.answer===false?' selected':''}>False</option>
                </select></label>`;

        if (type === 'fill_blank') return `
                <label class="ws-answer-label">Answer: <input class="ws-edit-input ws-inline-answer" type="text" placeholder="Correct word/phrase" value="${escAttr(q.answer)}"></label>`;

        if (type === 'whiteboard') return `
                <label class="ws-answer-label">Answer: <input class="ws-edit-input ws-inline-answer" type="text" placeholder="Indicative answer, teacher only (optional)" value="${escAttr(q.answer)}"></label>`;

        if (type === 'extended_response') return `
                <div class="ws-er-simple">
                    <div class="ws-er-meta">
                        <label class="ws-answer-label">Marks: <input class="ws-edit-input ws-marks-input" type="number" min="1" max="20" value="${escAttr(q.marks ?? 8)}"></label>
                    </div>
                    <textarea class="ws-edit-input ws-model-answer" rows="3" placeholder="Model / indicative answer (shown on answer key)">${escWs(q.modelAnswer)}</textarea>
                </div>`;

        // short_answer
        return `
                <label class="ws-answer-label ws-sa-simple">Answer: <input class="ws-edit-input ws-inline-answer" type="text" placeholder="Indicative answer (optional)" value="${escAttr(q.answer)}"></label>`
            + (opts.withMarks ? `
                <label class="ws-answer-label">Marks: <input class="ws-edit-input ws-marks-input" type="number" min="1" max="20" value="${escAttr(q.marks ?? 2)}"></label>` : '');
    }

    /** Wires whatever answerFieldsHtml put inside `scope`. Safe on a scope with none. */
    function wireAnswerFields(scope, type, q, saveFn) {
        const sel     = scope.querySelector('.ws-answer-select');
        const ansIn   = scope.querySelector('.ws-inline-answer');
        const marksIn = scope.querySelector('.ws-marks-input');
        const modelIn = scope.querySelector('.ws-model-answer');

        if (sel && type === 'multiple_choice') sel.addEventListener('change', () => { q.answer = sel.value; saveFn(); });
        if (sel && type === 'true_false')      sel.addEventListener('change', () => { q.answer = sel.value === 'true'; saveFn(); });
        ansIn?.addEventListener('input',   () => { q.answer      = ansIn.value; saveFn(); });
        modelIn?.addEventListener('input', () => { q.modelAnswer = modelIn.value; saveFn(); });
        // Marks drive both the printed [n marks] tag and how many ruled answer
        // lines the question gets, so the sheet has to be redrawn - but only
        // for worksheets, where saveFn is the worksheet's own debounce.
        marksIn?.addEventListener('input', () => {
            q.marks = parseInt(marksIn.value, 10) || 1;
            saveFn();
        });
    }

    /**
     * One question as a row of form fields.
     *
     * Still the quiz editor's whole UI (#quiz-editor). Worksheets now edit the
     * printed text on the sheet itself and take only the answer fields from
     * answerFieldsHtml, which this shares.
     */
    function buildQuestionRow(type, q, saveFn) {
        saveFn = saveFn || scheduleWsSave;
        const row = document.createElement('div');
        row.className = 'ws-edit-q-row';

        const num = document.createElement('span');
        num.className = 'ws-q-num';

        const fields = document.createElement('div');
        fields.className = 'ws-edit-q-fields';

        if (type === 'multiple_choice') {
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Question" value="${escAttr(q.question)}">
                <div class="ws-mc-options">
                    <label class="ws-mc-opt-label">A)<input class="ws-edit-input ws-mc-opt" type="text" placeholder="Option A" value="${escAttr(q.a)}"></label>
                    <label class="ws-mc-opt-label">B)<input class="ws-edit-input ws-mc-opt" type="text" placeholder="Option B" value="${escAttr(q.b)}"></label>
                    <label class="ws-mc-opt-label">C)<input class="ws-edit-input ws-mc-opt" type="text" placeholder="Option C" value="${escAttr(q.c)}"></label>
                    ${q.d !== undefined ? `<label class="ws-mc-opt-label">D)<input class="ws-edit-input ws-mc-opt" type="text" placeholder="Option D" value="${escAttr(q.d)}"></label>` : ''}
                </div>${answerFieldsHtml(type, q)}`;
            const qIn = fields.querySelector('.ws-q-main');
            const [optA, optB, optC, optD] = fields.querySelectorAll('.ws-mc-opt');
            qIn.addEventListener('input',  () => { q.question = qIn.value;  saveFn(); });
            optA.addEventListener('input', () => { q.a        = optA.value; saveFn(); });
            optB.addEventListener('input', () => { q.b        = optB.value; saveFn(); });
            optC.addEventListener('input', () => { q.c        = optC.value; saveFn(); });
            optD?.addEventListener('input', () => { q.d        = optD.value; saveFn(); });
        } else if (type === 'true_false') {
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Statement" value="${escAttr(q.statement)}">${answerFieldsHtml(type, q)}`;
            const stmtIn = fields.querySelector('.ws-q-main');
            stmtIn.addEventListener('input', () => { q.statement = stmtIn.value; saveFn(); });
        } else if (type === 'fill_blank') {
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Sentence (use _____ for the blank)" value="${escAttr(q.sentence)}">${answerFieldsHtml(type, q)}`;
            const sentIn = fields.querySelector('.ws-q-main');
            sentIn.addEventListener('input', () => { q.sentence = sentIn.value; saveFn(); });
        } else if (type === 'matching') {
            if (!Array.isArray(q.pairs)) q.pairs = [];
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Instruction (e.g. Match each term to its definition)" value="${escAttr(q.instruction)}">
                <div class="ws-match-pairs"></div>
                <button class="ws-add-pair-btn" type="button">+ Add pair</button>`;
            const instrIn = fields.querySelector('input');
            instrIn.addEventListener('input', () => { q.instruction = instrIn.value; saveFn(); });
            const pairsEl = fields.querySelector('.ws-match-pairs');
            const addPairRow = (pair) => {
                const pr = document.createElement('div');
                pr.className = 'ws-match-pair-row';
                pr.innerHTML = `
                    <input class="ws-edit-input" type="text" placeholder="Term" value="${escAttr(pair.left)}">
                    <span class="ws-match-arrow">&rarr;</span>
                    <input class="ws-edit-input" type="text" placeholder="Definition / match" value="${escAttr(pair.right)}">
                    <button class="ws-del-q" type="button" title="Remove pair">&#10005;</button>`;
                const [leftIn, rightIn] = pr.querySelectorAll('input');
                leftIn.addEventListener('input',  () => { pair.left  = leftIn.value;  saveFn(); });
                rightIn.addEventListener('input', () => { pair.right = rightIn.value; saveFn(); });
                pr.querySelector('button').addEventListener('click', () => {
                    const pi = q.pairs.indexOf(pair);
                    if (pi !== -1) q.pairs.splice(pi, 1);
                    pr.remove();
                    saveFn();
                });
                pairsEl.appendChild(pr);
            };
            q.pairs.forEach(addPairRow);
            fields.querySelector('.ws-add-pair-btn').addEventListener('click', () => {
                const pair = { left: '', right: '' };
                q.pairs.push(pair);
                addPairRow(pair);
                saveFn();
                pairsEl.lastElementChild?.querySelector('input')?.focus();
            });
        } else if (type === 'extended_response') {
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Exam-style question / stem" value="${escAttr(q.question)}">
                <input class="ws-edit-input ws-guidance-input" type="text" placeholder="Guidance (what a good answer covers &#8211; optional)" value="${escAttr(q.guidance)}">${answerFieldsHtml(type, q)}
                <div class="ws-parts"></div>
                <button class="ws-add-part-btn" type="button">+ Add part</button>`;
            const qIn = fields.querySelector('.ws-q-main');
            const guideIn = fields.querySelector('.ws-guidance-input');
            const simpleEl = fields.querySelector('.ws-er-simple');
            qIn.addEventListener('input',     () => { q.question = qIn.value;     saveFn(); });
            guideIn.addEventListener('input', () => { q.guidance = guideIn.value; saveFn(); });
            wirePartsEditor(fields, q, saveFn, simpleEl);
        } else if (type === 'whiteboard') {
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Problem to solve on the whiteboard (students draw their answer)" value="${escAttr(q.question)}">${answerFieldsHtml(type, q)}`;
            const qIn = fields.querySelector('.ws-q-main');
            qIn.addEventListener('input', () => { q.question = qIn.value; saveFn(); });
        } else { // short_answer
            fields.innerHTML = `
                <input class="ws-edit-input ws-q-main" type="text" placeholder="Question / stem" value="${escAttr(q.question)}">${answerFieldsHtml(type, q)}
                <div class="ws-parts"></div>
                <button class="ws-add-part-btn" type="button">+ Add part</button>`;
            const qIn = fields.querySelector('.ws-q-main');
            const simpleEl = fields.querySelector('.ws-sa-simple');
            qIn.addEventListener('input', () => { q.question = qIn.value; saveFn(); });
            wirePartsEditor(fields, q, saveFn, simpleEl);
        }

        wireAnswerFields(fields, type, q, saveFn);

        const del = document.createElement('button');
        del.className = 'ws-del-q';
        del.title = 'Remove question';
        del.textContent = '\u2715';
        del.addEventListener('click', () => {
            const parent = row.parentElement;
            const card   = row.closest('.ws-edit-card');
            if (card && card._section) {
                const qi = card._section.questions.indexOf(q);
                if (qi !== -1) card._section.questions.splice(qi, 1);
            }
            row.remove();
            renumberQuestions(parent);
            saveFn();
        });

        row.appendChild(num);
        row.appendChild(fields);
        row.appendChild(del);
        return row;
    }

    function buildSectionCard(section, dataObj, saveFn) {
        dataObj = dataObj || currentWorksheetData;
        saveFn  = saveFn  || scheduleWsSave;
        const sectionLabel = WS_SECTION_LABELS[section.type] || section.type;
        const card = document.createElement('div');
        card.className = 'ws-edit-card';
        card.dataset.type = section.type;
        card._section = section;

        const header = document.createElement('div');
        header.className = 'ws-edit-card-header';
        header.innerHTML = `
            <span class="ws-drag-handle" title="Drag to reorder">&#8942;</span>
            <span class="ws-card-label">${esc(sectionLabel)}</span>
            <button class="ws-remove-section" title="Remove section">✕</button>`;

        const questionsEl = document.createElement('div');
        questionsEl.className = 'ws-edit-questions';

        section.questions.forEach(q => questionsEl.appendChild(buildQuestionRow(section.type, q, saveFn)));
        renumberQuestions(questionsEl);

        const addBtn = document.createElement('button');
        addBtn.className = 'ws-add-q-btn';
        addBtn.textContent = '+ Add question';
        addBtn.addEventListener('click', () => {
            const newQ = makeEmptyQuestion(section.type);
            section.questions.push(newQ);
            const qRow = buildQuestionRow(section.type, newQ, saveFn);
            questionsEl.appendChild(qRow);
            renumberQuestions(questionsEl);
            saveFn();
            qRow.querySelector('input')?.focus();
        });

        header.querySelector('.ws-remove-section').addEventListener('click', () => {
            if (dataObj) {
                const si = dataObj.sections.indexOf(section);
                if (si !== -1) dataObj.sections.splice(si, 1);
            }
            card.remove();
            saveFn();
        });

        card.appendChild(header);
        card.appendChild(questionsEl);
        card.appendChild(addBtn);
        return card;
    }

    // ── Worksheet sheet editor ────────────────────────────
    //
    // The sheet is the editor. #ws-sheet-frame renders exactly the document
    // buildPrintPage produces for the PDF, with opts.editable, which stamps
    // every model-backed string with the JSON path it was read from. Typing
    // writes back through that path; the DOM is never serialised, because
    // worksheets.data_json stays the source of truth.
    //
    // This is the slide editor's idea (the preview IS the document) without
    // its constraint: a slide stores free HTML and so has to strip its own
    // chrome before saving, while a worksheet stores structure and can throw
    // the whole rendered page away. That is why the hover toolbars and add
    // buttons live inside the frame rather than in an overlay above it.

    const WS_SHEET_W = 794;          // 210mm at 96dpi, the width the sheet is laid out at
    const WS_UNDO_LIMIT = 50;

    let wsSelectedPath  = null;      // 'sections.1.questions.3', or null
    let wsUndoStack     = [];
    let wsRenderToken   = 0;         // guards against two renders racing on the await below
    let wsFrameRO       = null;
    let wsRefreshTimer  = null;
    let wsRestoreFocus  = null;      // path to put the caret back in after a re-render

    /** Reads 'sections.0.questions.2.question' off the worksheet. */
    function wsGetPath(obj, path) {
        if (!path) return undefined;
        return path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);
    }

    function wsSetPath(obj, path, value) {
        const segs = path.split('.');
        const last = segs.pop();
        const parent = segs.reduce((o, k) => (o == null ? o : o[k]), obj);
        if (parent && typeof parent === 'object') parent[last] = value;
    }

    /** The array a path's last segment indexes into, plus that index. */
    function wsParentArray(obj, path) {
        const segs = path.split('.');
        const idx = parseInt(segs.pop(), 10);
        const arr = wsGetPath(obj, segs.join('.'));
        return Array.isArray(arr) ? { arr, idx } : { arr: null, idx: -1 };
    }

    /** The question's section object, from a question path. */
    function wsSectionOf(qPath) {
        return wsGetPath(currentWorksheetData, qPath.split('.').slice(0, 2).join('.'));
    }

    // ── Undo ──────────────────────────────────────────────
    //
    // Structural only. Text edits inside a field are covered by the browser's
    // own undo, which fires `input` and so writes the model on its way back -
    // but deleting a question has nothing to catch it, and that is the one
    // action here a teacher cannot retype their way out of.

    /**
     * Called whenever a different worksheet is loaded into the editor.
     *
     * The undo stack holds whole-document snapshots, so carrying it across
     * documents would let Ctrl+Z on worksheet B restore worksheet A over the
     * top of it - and then save that. The slide editor clears its stacks on
     * every slide change for the same reason.
     */
    function wsResetEditorState() {
        wsUndoStack = [];
        wsSelectedPath = null;
        wsRestoreFocus = null;
        clearTimeout(wsRefreshTimer);
    }

    function wsSnapshot() {
        if (!currentWorksheetData) return;
        wsUndoStack.push(JSON.stringify(currentWorksheetData));
        if (wsUndoStack.length > WS_UNDO_LIMIT) wsUndoStack.shift();
    }

    function wsUndo() {
        const prev = wsUndoStack.pop();
        if (!prev) return;
        currentWorksheetData = JSON.parse(prev);
        wsSelectedPath = null;
        scheduleWsSave();
        renderWsEditor(currentWorksheetData);
    }

    /**
     * Snapshot, mutate, save, redraw - the shape of every structural action.
     *
     * `focusAfter` runs once the mutation has happened and returns the path to
     * put the caret in, which is how "+ Add question" lands you in the blank
     * question rather than merely next to it. It has to be evaluated here
     * rather than by the caller, because the index it names only exists after
     * the mutation.
     */
    function wsStructural(mutate, focusAfter) {
        if (!currentWorksheetData) return;
        wsSnapshot();
        mutate();
        if (focusAfter) wsRestoreFocus = focusAfter();
        scheduleWsSave();
        renderWsEditor(currentWorksheetData);
    }

    // ── Rendering the sheet ───────────────────────────────

    function wsSheetMeta(mark) {
        const opts = a11yOptsFrom(wsA11ySelect, wsTintSelect);
        return {
            subject:   wsSubject?.value.trim() || '',
            level:     wsLevel?.value || '',
            examBoard: wsExamBoard?.value || '',
            a11y:      opts.a11y || undefined,
            tint:      opts.tint || undefined,
            mark,
        };
    }

    /**
     * Draws (or redraws) the whole sheet.
     *
     * Keeps the name the rest of the page already calls: opening a worksheet
     * and finishing a generation both land here. A full redraw is the only
     * render there is - it re-letters sections, renumbers questions and
     * recomputes the score box for free - so it must never run while the
     * caret is in the sheet. Text edits deliberately do not call it.
     */
    async function renderWsEditor(data) {
        if (!wsSheetFrame || !data) return;
        const token = ++wsRenderToken;
        const mark = await loadResourceMark();
        // Another render started while we were waiting for the logo; that one
        // is drawing newer data, so this one has nothing to say.
        if (token !== wsRenderToken) return;

        const html = WorksheetRender.buildPrintPage(data, false, {
            editable: true,
            showAnswers: wsShowAnswers,
            meta: wsSheetMeta(mark),
        });
        // The toolbar and the tabs follow the sheet, which may have just been
        // opened.
        syncWsLook(data);
        syncWsBarTitle();
        renderWsPair();
        // document.write into a same-origin frame rather than srcdoc: the
        // editor needs a contentDocument to listen on, and linking the
        // stylesheets (which srcdoc with no origin cannot do) keeps them cached
        // across every redraw.
        const doc = wsSheetFrame.contentDocument;
        if (!doc) return;
        doc.open();
        doc.write(html);
        doc.close();

        wireWsFrame(doc);
        wsMarkSelected(doc);
        wsScaleSheet();
        // A new document each time, so the old observer is watching a node
        // that no longer exists.
        wsFrameRO?.disconnect();
        wsFrameRO = new ResizeObserver(wsScaleSheet);
        wsFrameRO.observe(doc.documentElement);

        if (wsRestoreFocus) {
            const el = doc.querySelector(`[data-ws-path="${wsRestoreFocus}"]`);
            wsRestoreFocus = null;
            if (el) { el.focus(); wsPlaceCaretAtEnd(doc, el); }
        }
    }

    /**
     * Fits the sheet to the pane, the same way scaleStage() fits a slide.
     *
     * The sheet is laid out at its true A4 width and scaled down when the pane
     * is narrower - never up, because a printed document blown past 100% is
     * just blurry. The frame carries the transform and the scaler carries the
     * post-scale box, so the page scrolls as one long sheet instead of nesting
     * a second scrollbar inside the frame.
     */
    function wsScaleSheet() {
        if (!wsSheetFrame || !wsSheetCanvas || !wsSheetScaler) return;
        const doc = wsSheetFrame.contentDocument;
        const h = doc?.documentElement?.scrollHeight || 0;
        if (!h) return;
        const styles = getComputedStyle(wsSheetCanvas);
        const avail = wsSheetCanvas.clientWidth
            - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
        // Zero or negative while the worksheet view is still hidden. Scaling
        // to that would collapse the sheet to nothing and never recover,
        // because the observer only fires again when the width changes.
        if (avail <= 0) return;
        const scale = Math.min(1, avail / WS_SHEET_W);
        const height = h + 'px';
        // Writing the same values back would have the observer that called us
        // fire again on a layout that did not change.
        if (wsSheetFrame.style.height === height
            && wsSheetFrame.dataset.scale === String(scale)) return;
        wsSheetFrame.style.height = height;
        wsSheetFrame.style.transform = `scale(${scale})`;
        wsSheetFrame.dataset.scale = String(scale);
        wsSheetScaler.style.width  = (WS_SHEET_W * scale) + 'px';
        wsSheetScaler.style.height = (h * scale) + 'px';
        // The tab row sits on the paper, so it is exactly as wide.
        if (wsSheetTabs) wsSheetTabs.style.width = (WS_SHEET_W * scale) + 'px';
    }

    if (wsSheetCanvas) new ResizeObserver(wsScaleSheet).observe(wsSheetCanvas);

    function wsPlaceCaretAtEnd(doc, el) {
        const range = doc.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = doc.defaultView.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    }

    // The teacher's copy or the pupil's. Remembered per browser: a teacher
    // who prefers to edit the clean sheet should not have to switch it off
    // every time.
    const wsAnswersCheck = $('ws-answers-check');

    // The sheet's look (data.theme), chosen from a gallery built off
    // WorksheetRender.WS_THEME_INFO. Classic is no field at all, like the full
    // header, so a sheet that never chose prints as it always did. The menu
    // opens and closes with the other toolbar menus (.export-menu-wrap).
    const wsLookGrid = $('ws-look-grid');
    const lookDots = (t) => t.mini.badges.map(c => `<i style="background:${c}"></i>`).join('');
    if (wsLookGrid) {
        wsLookGrid.innerHTML = WorksheetRender.WS_THEME_INFO.map((t) => {
            const m = t.mini;
            const vars = `--lk-ink:${m.ink};--lk-band:${m.band};--lk-rule:${m.rule};--lk-font:${m.font};`
                + `--lk-r:${m.round ? '6px' : '0'};--lk-badge-r:${m.round ? '50%' : '3px'}`;
            const sections = m.badges.map(c => `<span class="lk-sec"><span class="lk-badge" style="background:${c}"></span><span class="lk-lines"><i></i><i></i></span></span>`).join('');
            return `<button type="button" class="ws-look-card" data-look="${t.id}" aria-pressed="false" style="${esc(vars)}">
                <span class="ws-look-mini" aria-hidden="true"><span class="lk-title">Aa Worksheet</span>${sections}</span>
                <span class="ws-look-name">${esc(t.name)}</span>
                <span class="ws-look-blurb">${esc(t.blurb)}</span>
            </button>`;
        }).join('');
        wsLookGrid.addEventListener('click', (e) => {
            const card = e.target.closest('[data-look]');
            if (!card || !currentWorksheetData) return;
            const theme = card.dataset.look;
            if (theme === WorksheetRender.themeOf(currentWorksheetData)) return;
            wsStructural(() => {
                if (theme === 'classic') delete currentWorksheetData.theme;
                else currentWorksheetData.theme = theme;
            });
        });
    }
    function syncWsLook(data) {
        const id = WorksheetRender.themeOf(data);
        const t = WorksheetRender.WS_THEME_INFO.find(x => x.id === id);
        const name = $('ws-look-name');
        const dots = $('ws-look-dots');
        if (name) name.textContent = t.name;
        if (dots) dots.innerHTML = lookDots(t);
        wsLookGrid?.querySelectorAll('[data-look]').forEach(c => c.setAttribute('aria-pressed', String(c.dataset.look === id)));
    }
    let wsShowAnswers = true;
    try { wsShowAnswers = localStorage.getItem('wsShowAnswers') !== '0'; } catch {}
    function syncWsAnswersSwitch() {
        if (!wsAnswersCheck) return;
        wsAnswersCheck.checked = wsShowAnswers;
        const sw = wsAnswersCheck.closest('.toggle-switch');
        if (sw) { sw.classList.add('is-init'); sw.dataset.on = String(wsShowAnswers); }
    }
    syncWsAnswersSwitch();
    wsAnswersCheck?.addEventListener('change', () => {
        wsShowAnswers = wsAnswersCheck.checked;
        try { localStorage.setItem('wsShowAnswers', wsShowAnswers ? '1' : '0'); } catch {}
        syncWsAnswersSwitch();
        if (currentWorksheetData) renderWsEditor(currentWorksheetData);
    });

    /** Redraws the sheet a beat after the last diagram-data edit. */
    function wsScheduleSheetRefresh() {
        clearTimeout(wsRefreshTimer);
        wsRefreshTimer = setTimeout(() => renderWsEditor(currentWorksheetData), 500);
    }


    // ── Editing inside the frame ──────────────────────────

    function wireWsFrame(doc) {
        // Re-wired on every redraw: doc.open() throws the old document away,
        // and with it every listener that was on it.
        MathRender.installLiveEditing(doc.body);

        // A field shows raw $LaTeX$ while it has focus (MathRender reverts it
        // above), so its textContent IS the stored value - no HTML to parse.
        doc.addEventListener('input', (e) => {
            const el = e.target.closest?.('.ws-ed');
            if (!el || !currentWorksheetData) return;
            const sharedIn = wsStepsSharedAt(el.dataset.wsPath);
            wsSetPath(currentWorksheetData, el.dataset.wsPath, el.textContent);
            if (sharedIn) wsKeepStepsShared(sharedIn);
            scheduleWsSave();
        });

        // A question holding a table is drawn as one, but edited as the pipes
        // it is stored as: swapped to its raw text on the way in (after
        // MathRender's revert, which listens on the body) and redrawn on the
        // way out.
        doc.addEventListener('focusin', (e) => {
            const el = e.target.closest?.('.ws-ed-table');
            if (el && el.dataset.wsRaw != null) el.textContent = el.dataset.wsRaw;
        });
        doc.addEventListener('focusout', (e) => {
            const el = e.target.closest?.('.ws-ed-table');
            if (!el || !window.WorksheetRender?.tableText) return;
            el.dataset.wsRaw = el.textContent;
            el.innerHTML = window.WorksheetRender.tableText(el.textContent);
        });

        doc.addEventListener('focusin', (e) => {
            const el = e.target.closest?.('.ws-ed');
            if (!el) return;
            const q = el.closest('[data-ws-q]');
            wsSelect(q ? q.dataset.wsQ : null, doc);
        });

        // A worksheet field holds a string. Enter would put a <br> or a <div>
        // into it that textContent then flattens away on the next keystroke,
        // so it is refused outright - except in a code listing, where the line
        // breaks are the point.
        doc.addEventListener('keydown', (e) => {
            const el = e.target.closest?.('.ws-ed');
            if (el && e.key === 'Enter' && !el.classList.contains('ws-ed-code')) {
                e.preventDefault();
                el.blur();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.target.isContentEditable) {
                e.preventDefault();
                wsUndo();
            }
        });

        // Plain text only, for the same reason: pasting from a web page would
        // otherwise drop a paragraph of markup into a field that can only
        // store its text.
        doc.addEventListener('paste', (e) => {
            if (!e.target.closest?.('.ws-ed')) return;
            e.preventDefault();
            const text = (e.clipboardData || doc.defaultView.clipboardData)?.getData('text/plain') || '';
            doc.execCommand('insertText', false, text.replace(/\r?\n/g, ' '));
        });

        // ── Drag to reorder ──
        // A question or a matching pair is picked up by its grip and dropped
        // on another in the same list: above its middle to go before it,
        // below to go after. Both are plain array moves on the document.
        let wsDrag = null;   // { kind, path, listPath }
        const dropSel = { q: '[data-ws-q]', pair: '[data-ws-pair]' };
        const pathOf = (el, kind) => (kind === 'q' ? el.dataset.wsQ : el.dataset.wsPair);
        const listOf = (path) => path.split('.').slice(0, -1).join('.');
        const clearDrop = () => doc.querySelectorAll('.ws-ed-drop-before, .ws-ed-drop-after')
            .forEach(el => el.classList.remove('ws-ed-drop-before', 'ws-ed-drop-after'));

        function dropTarget(e) {
            if (!wsDrag) return null;
            const el = e.target.closest?.(dropSel[wsDrag.kind]);
            if (!el) return null;
            const path = pathOf(el, wsDrag.kind);
            // Only within the list it came from: a question cannot become a
            // question of another type by being dropped in its section.
            if (listOf(path) !== wsDrag.listPath) return null;
            const r = el.getBoundingClientRect();
            return { el, path, after: e.clientY > r.top + r.height / 2 };
        }

        doc.addEventListener('dragstart', (e) => {
            const grip = e.target.closest?.('.ws-ed-grip');
            if (!grip) return;
            const path = grip.dataset.wsTarget;
            wsDrag = { kind: grip.dataset.wsDrag, path, listPath: listOf(path) };
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', path);
            const item = grip.closest(dropSel[wsDrag.kind]);
            if (item) {
                e.dataTransfer.setDragImage(item, 12, 12);
                // After the browser has taken its picture of it.
                setTimeout(() => { if (wsDrag) item.classList.add('ws-ed-dragging'); }, 0);
            }
        });

        doc.addEventListener('dragover', (e) => {
            const t = dropTarget(e);
            clearDrop();
            if (!t) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (t.path !== wsDrag.path) t.el.classList.add(t.after ? 'ws-ed-drop-after' : 'ws-ed-drop-before');
        });

        doc.addEventListener('drop', (e) => {
            const t = dropTarget(e);
            const drag = wsDrag;
            if (!t || !drag) return;
            e.preventDefault();
            const { arr, idx: from } = wsParentArray(currentWorksheetData, drag.path);
            const target = parseInt(t.path.split('.').pop(), 10);
            if (!arr || from < 0 || Number.isNaN(target)) return;
            let to = target + (t.after ? 1 : 0);
            if (from < to) to -= 1;   // taking it out shifts everything after it up one
            if (to === from) return;
            wsStructural(() => {
                const [item] = arr.splice(from, 1);
                arr.splice(to, 0, item);
                wsSelectedPath = null;
            });
        });

        doc.addEventListener('dragend', () => {
            wsDrag = null;
            clearDrop();
            doc.querySelectorAll('.ws-ed-dragging').forEach(el => el.classList.remove('ws-ed-dragging'));
        });

        // ── Which question's toolbar is showing ──
        // Hover, but with a safe path to the toolbar. A question's bar sits
        // above it, so the pointer on its way there crosses the question
        // before - which, on plain :hover, then took the bar over. While the
        // pointer is inside the shape between where it left the question and
        // the bar, the question keeps its bar; resting there for a moment
        // still hands it over, so nothing feels stuck.
        let hoverQ = null;
        let hoverFrom = null;   // where the pointer last was on hoverQ
        let hoverTimer = null;
        let hoverNext = null;   // what is under the pointer while it is on the path
        const setHover = (q) => {
            clearTimeout(hoverTimer);
            hoverTimer = null;
            if (hoverQ === q) return;
            hoverQ?.classList.remove('is-hover');
            hoverQ = q;
            q?.classList.add('is-hover');
        };
        const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
        const inTriangle = (p, a, b, c) => {
            const d1 = cross(p, a, b), d2 = cross(p, b, c), d3 = cross(p, c, a);
            return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
        };
        // Inside the hull of the point it left from and the bar (a little
        // larger than the bar, for an imprecise hand).
        const onPathToBar = (p) => {
            const bar = hoverQ?.isConnected && hoverQ.querySelector(':scope > .ws-ed-qtools');
            const r = bar && bar.getBoundingClientRect();
            if (!r || !r.width || !hoverFrom) return false;
            const c = [
                { x: r.left - 12, y: r.top - 6 }, { x: r.right + 12, y: r.top - 6 },
                { x: r.right + 12, y: r.bottom + 6 }, { x: r.left - 12, y: r.bottom + 6 },
            ];
            return c.some((a, i) => inTriangle(p, hoverFrom, a, c[(i + 1) % 4]));
        };
        doc.addEventListener('mousemove', (e) => {
            const p = { x: e.clientX, y: e.clientY };
            const bar = e.target.closest?.('.ws-ed-qtools');
            const q = bar ? bar.closest('[data-ws-q]') : (e.target.closest?.('[data-ws-q]') || null);
            if (q && q === hoverQ) { setHover(q); hoverFrom = bar ? hoverFrom : p; return; }
            if (hoverQ && onPathToBar(p)) {
                hoverNext = q;
                if (!hoverTimer) hoverTimer = setTimeout(() => { setHover(hoverNext); hoverFrom = null; }, 350);
                return;
            }
            setHover(q);
            hoverFrom = q ? p : null;
        });
        doc.documentElement.addEventListener('mouseleave', () => { setHover(null); hoverFrom = null; });

        doc.addEventListener('click', (e) => {
            const btn = e.target.closest?.('.ws-ed-btn');
            if (btn) { wsRunAction(btn.dataset.wsAct, btn.dataset.wsTarget, btn.dataset.wsType); return; }
            const q = e.target.closest?.('[data-ws-q]');
            wsSelect(q ? q.dataset.wsQ : null, doc);
        });
    }

    function wsSelect(path, doc) {
        if (wsSelectedPath === path) return;
        wsSelectedPath = path;
        wsMarkSelected(doc);
    }

    function wsMarkSelected(doc) {
        doc = doc || wsSheetFrame?.contentDocument;
        if (!doc) return;
        doc.querySelectorAll('.ws-ed-q.is-selected').forEach(el => el.classList.remove('is-selected'));
        if (!wsSelectedPath) return;
        doc.querySelector(`[data-ws-q="${wsSelectedPath}"]`)?.classList.add('is-selected');
    }

    // ── Structural actions ────────────────────────────────

    /** Moves arr[idx] by `delta`, clamped. Returns the new index, or -1. */
    function wsMove(arr, idx, delta) {
        const to = idx + delta;
        if (!arr || idx < 0 || to < 0 || to >= arr.length) return -1;
        const [item] = arr.splice(idx, 1);
        arr.splice(to, 0, item);
        return to;
    }

    // Steps every question in a section shares are printed once, at the head
    // of the section (WorksheetRender.sharedSteps), from the first question's
    // copy. So an edit to that copy has to reach the others, or they stop
    // matching and the steps reappear under every question.
    //
    // Called BEFORE an edit: returns the section if `path` is inside a
    // question's steps and that section's steps are currently shared.
    function wsStepsSharedAt(path) {
        const m = /^(sections\.\d+)\.questions\.\d+(?:\.steps\b.*)?$/.exec(path || '');
        const section = m ? wsGetPath(currentWorksheetData, m[1]) : null;
        return section && WorksheetRender.sharedSteps(section) ? section : null;
    }

    /** After the edit: every question takes the first one's steps. */
    function wsKeepStepsShared(section) {
        const qs = section?.questions || [];
        const steps = Array.isArray(qs[0]?.steps) ? qs[0].steps : [];
        qs.slice(1).forEach((q) => { q.steps = steps.slice(); });
    }

    function wsRunAction(act, path, type) {
        if (!currentWorksheetData) return;
        const data = currentWorksheetData;

        // ── Layout choices (see edOption in worksheet-render.js) ──
        if (act === 'doc-header') {
            wsStructural(() => {
                if (type === 'full') delete data.header;
                else data.header = type;
            });
            return;
        }

        if (act === 's-wordbank' || act === 's-matchstyle') {
            const section = wsGetPath(data, path);
            if (!section) return;
            wsStructural(() => {
                if (act === 's-wordbank') {
                    if (section.wordBank) delete section.wordBank;
                    else section.wordBank = true;
                } else if (type === 'lines') {
                    section.matchStyle = 'lines';
                } else {
                    delete section.matchStyle;
                }
            });
            return;
        }

        // ── The teacher's copy (keyOn in worksheet-render.js) ──
        if (act === 'mc-answer' || act === 'tf-answer') {
            const q = wsGetPath(data, path);
            if (!q) return;
            wsStructural(() => { q.answer = act === 'tf-answer' ? type === 'true' : type; });
            return;
        }
        if (act === 'marks-inc' || act === 'marks-dec') {
            const target = wsGetPath(data, path);
            if (!target) return;
            const now = parseInt(type, 10) || 1;
            const next = Math.min(20, Math.max(1, now + (act === 'marks-inc' ? 1 : -1)));
            if (next === now) return;
            wsStructural(() => { target.marks = next; });
            return;
        }
        if (act === 'q-steps' || act === 'q-starter') {
            const q = wsGetPath(data, path);
            if (!q) return;
            if (act === 'q-steps') wsStructural(() => { q.steps = ['']; }, () => `${path}.steps.0`);
            else wsStructural(() => { q.starter = ''; }, () => `${path}.starter`);
            return;
        }
        if (act === 'part-data') {
            const part = wsGetPath(data, path);
            if (part) openPartDataModal(part);
            return;
        }

        if (act === 'we-del') {
            const section = wsGetPath(data, path);
            if (!section) return;
            wsStructural(() => { delete section.workedExample; });
            return;
        }

        if (act === 'pair-up' || act === 'pair-down') {
            const { arr, idx } = wsParentArray(data, path);
            if (!arr) return;
            wsStructural(() => { wsMove(arr, idx, act === 'pair-up' ? -1 : 1); });
            return;
        }

        if (act === 's-add') {
            wsStructural(() => {
                data.sections.push({ type, questions: [makeEmptyQuestion(type)] });
            });
            return;
        }

        if (act === 's-up' || act === 's-down') {
            const { arr, idx } = wsParentArray(data, path);
            wsStructural(() => { wsMove(arr, idx, act === 's-up' ? -1 : 1); wsSelectedPath = null; });
            return;
        }

        if (act === 's-del') {
            const section = wsGetPath(data, path);
            const n = section?.questions?.length || 0;
            // Only when there is something to lose. Confirming the removal of
            // an empty section a teacher just added is nagging.
            if (n && !confirm(`Delete this section and its ${n} question${n === 1 ? '' : 's'}?`)) return;
            const { arr, idx } = wsParentArray(data, path);
            wsStructural(() => { arr.splice(idx, 1); wsSelectedPath = null; });
            return;
        }

        if (act === 'q-add') {
            const section = wsGetPath(data, path);
            if (!section) return;
            // The field a question of this type actually starts with - landing
            // in the blank you just made is the whole point of the button.
            const firstField = section.type === 'true_false' ? 'statement'
                : section.type === 'fill_blank' ? 'sentence'
                : section.type === 'matching' ? 'instruction' : 'question';
            // A section whose steps are printed once keeps them that way: the
            // new question starts with the same ones.
            const shared = WorksheetRender.sharedSteps(section);
            wsStructural(
                () => {
                    const q = makeEmptyQuestion(section.type);
                    if (shared) q.steps = shared.slice();
                    section.questions.push(q);
                },
                () => `${path}.questions.${section.questions.length - 1}.${firstField}`,
            );
            return;
        }

        if (act === 'q-up' || act === 'q-down' || act === 'q-dup' || act === 'q-del') {
            const { arr, idx } = wsParentArray(data, path);
            if (!arr) return;
            wsStructural(() => {
                if (act === 'q-del') arr.splice(idx, 1);
                else if (act === 'q-dup') arr.splice(idx + 1, 0, JSON.parse(JSON.stringify(arr[idx])));
                else wsMove(arr, idx, act === 'q-up' ? -1 : 1);
                wsSelectedPath = null;
            });
            return;
        }

        if (act === 'pair-add') {
            const q = wsGetPath(data, path);
            if (!q) return;
            if (!Array.isArray(q.pairs)) q.pairs = [];
            wsStructural(
                () => { q.pairs.push({ left: '', right: '' }); },
                () => `${path}.pairs.${q.pairs.length - 1}.left`,
            );
            return;
        }

        if (act === 'part-add') {
            const q = wsGetPath(data, path);
            if (!q) return;
            if (!Array.isArray(q.parts)) q.parts = [];
            wsStructural(
                () => { q.parts.push({ text: '', marks: 2, answer: '' }); },
                () => `${path}.parts.${q.parts.length - 1}.text`,
            );
            return;
        }

        if (act === 'step-add') {
            const q = wsGetPath(data, path);
            if (!q) return;
            if (!Array.isArray(q.steps)) q.steps = [];
            const sharedIn = wsStepsSharedAt(path);
            wsStructural(
                () => { q.steps.push(''); if (sharedIn) wsKeepStepsShared(sharedIn); },
                () => `${path}.steps.${q.steps.length - 1}`,
            );
            return;
        }

        if (act === 'gloss-add') {
            if (!Array.isArray(data.glossary)) data.glossary = [];
            wsStructural(
                () => { data.glossary.push({ term: '', definition: '' }); },
                () => `glossary.${data.glossary.length - 1}.term`,
            );
            return;
        }

        if (act === 'pair-del' || act === 'part-del' || act === 'step-del' || act === 'gloss-del') {
            const { arr, idx } = wsParentArray(data, path);
            if (!arr) return;
            const sharedIn = act === 'step-del' ? wsStepsSharedAt(path) : null;
            wsStructural(() => {
                arr.splice(idx, 1);
                if (sharedIn) wsKeepStepsShared(sharedIn);
                // An empty glossary is no glossary - the renderer drops the
                // whole box, and an empty string key would keep it alive with
                // nothing in it.
                if (act === 'gloss-del' && !arr.length) delete data.glossary;
            });
        }
    }

    // ── Diagram data ──────────────────────────────────────
    //
    // Everything else a student copy does not show - answers, marks, model
    // answers - is edited on the sheet itself while answers are showing. The
    // numbers behind an exam-style graph, reaction profile or electron shell
    // diagram are the exception: the sheet draws them as a picture, which
    // cannot be typed into, so they open here from the button under it.

    function openPartDataModal(part) {
        const modal = $('ws-data-modal');
        const body = $('ws-data-body');
        if (!modal || !body) return;
        body.innerHTML = '';
        const save = () => { scheduleWsSave(); wsScheduleSheetRefresh(); };
        if (part.graph) body.appendChild(buildPartGraphEditor(part, save));
        if (part.reactionProfile) body.appendChild(buildPartReactionProfileEditor(part, save));
        if (part.electronShells) body.appendChild(buildPartElectronShellsEditor(part, save));
        show(modal);
        modal.querySelector('[data-data-close]')?.focus();
    }
    $('ws-data-modal')?.addEventListener('click', (e) => {
        if (e.target.closest('[data-data-close]')) hide($('ws-data-modal'));
    });
    $('ws-data-modal')?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') hide($('ws-data-modal'));
    });

    // Changing the accessibility layout or the paper tint changes what the
    // sheet looks like, and the sheet is what you are editing - so it redraws
    // rather than waiting for the next print.
    wsA11ySelect?.addEventListener('change', () => renderWsEditor(currentWorksheetData));
    wsTintSelect?.addEventListener('change', () => renderWsEditor(currentWorksheetData));

    // ── Cross-generation source picker ────────────────────
    // Fills a "Use content from a presentation" <select> with the user's
    // presentations, optionally preselecting one (e.g. "Quiz from this").
    async function populateSourcePresPicker(selectEl, preselectId) {
        if (!selectEl) return;
        selectEl.length = 1;           // keep the "None" option
        selectEl.value  = '';
        try {
            const res = await apiFetch('/api/presentations');
            if (!res.ok) return;
            const rows = await res.json();
            rows.forEach(p => {
                const opt = document.createElement('option');
                opt.value = p.id;
                opt.textContent = p.title || p.topic || 'Untitled';
                selectEl.appendChild(opt);
            });
            if (preselectId) selectEl.value = preselectId;
        } catch {}
    }

    // ── Booklets ──────────────────────────────────────────
    //
    // Three screens around a server-side generation: the composer, the
    // plan-approval gate, and the reader.
    //
    // The reader doubles as the progress view. Sections appear as skeletons
    // the moment generation starts and fill in as each one lands, which is
    // what makes resuming a dropped generation invisible – reopening the
    // booklet re-runs the same stream and the sections that already finished
    // simply arrive first. See routes/booklets.js for the other half.

    const bkPageForm      = $('bk-page-form');
    const bkPlanView      = $('bk-plan-view');
    const bkReader        = $('bk-reader');
    const bkError         = $('booklet-error');
    const bkPlanError     = $('bk-plan-error');
    const bkReaderError   = $('bk-reader-error');
    const bkExamBoard        = $('bk-exam-board');
    const bkLevel            = $('bk-level');
    const bkTitleInput       = $('bk-title-input');
    const bkDescriptionInput = $('bk-description-input');
    const bkLengthTiles      = $('bk-length-tiles');
    const bkPlanList         = $('bk-plan-list');
    const bkReaderBody       = $('bk-reader-body');
    const bkProgress         = $('bk-progress-status');
    const bkGenerating       = $('bk-generating');
    const bkGenIcon          = $('bk-gen-icon');
    const bkGenCount         = $('bk-gen-count');
    const bkGenSub           = $('bk-gen-sub');
    const bkGenBar           = $('bk-gen-bar');
    const bkGenFill          = $('bk-gen-fill');
    const bkCoverageEl       = $('bk-coverage');
    const bkTopicsGroup      = $('bk-topics-group');
    const bkTopicsEl         = $('bk-topics');
    const bkTopicsCount      = $('bk-topics-count');
    const bkTopicsClear      = $('bk-topics-clear');
    const bkTopicsWarning    = $('bk-topics-warning');
    const bkDescriptionGroup = $('bk-description-group');
    const bkDescriptionLabel = bkDescriptionGroup?.querySelector('label');
    const bkBriefSub         = $('bk-brief-sub');

    // Step 2 describes itself differently depending on which of its two forms
    // is showing. Held as a mutable array because initComposerWizard reads
    // hints[step - 1] each time it syncs, so rewriting the entry is what
    // changes the hint — and topics load while the teacher is still on step 1,
    // before this is ever read.
    const BK_HINT_BRIEF = 'Describe it or add your material. A booklet needs a subject, board and level.';
    const BK_HINT_TOPICS = 'Tick topics, describe it, or add your material.';
    const BK_SUB_BRIEF = 'Describe it or add your own material, and set the subject, exam board and level. We write it against that board’s specification.';
    const BK_SUB_TOPICS = 'Tick topics from the specification, describe it, or add your own material. We write it against the board’s own specification.';
    // One hint per step: what it covers, then its shape.
    const bkHints = [BK_HINT_BRIEF, ''];

    // Everything about the booklet being composed, planned or read. One object
    // rather than a scatter of module-level variables, because the three
    // screens hand the same booklet between them and a half-updated set is how
    // the plan screen ends up approving a different booklet than the one shown.
    const bkState = {
        id: null,
        length: 'standard',
        plan: null,
        // How many sections the approved plan holds. The stream cannot tell us
        // this – it announces a section only as it starts one – so without it
        // the progress bar would show "1 of 1" complete on the first section.
        plannedSections: 0,
        sections: [],
        meta: null,
        streaming: false,
        // Which document the reader is showing, and the id of the section with
        // an edit form open in it (at most one at a time).
        view: 'guide',
        editing: null,
        // 'workbook' for a workbook made on its own – the booklet pipeline
        // with no guide written, only the questions (include_guide = 0).
        kind: 'booklet',
    };

    function bkIsWorkbook() { return bkState.kind === 'workbook'; }

    const BK_SUB_CURRICULUM = $('bk-curriculum-sub')?.textContent || '';

    /**
     * Word the composer, plan and reader for what is being made.
     *
     * A workbook on its own is the same three screens as a booklet, so rather
     * than a second composer this rewrites the few places that name the guide
     * and hides the controls that only make sense with one: the Include step
     * (it IS the workbook, and diagrams only illustrate guide text), the
     * Guide/Workbook toggle, the guide's downloads, and setting it to be read.
     */
    function applyBookletKind() {
        const wb = bkIsWorkbook();
        const kicker = $('bk-kicker-label');
        if (kicker) kicker.textContent = wb ? 'New workbook' : 'New booklet';
        const sub = $('bk-curriculum-sub');
        if (sub) {
            sub.textContent = wb
                ? 'A workbook is a bank of exam questions written against the board’s own published specification, so all three are needed before you can choose what it covers.'
                : BK_SUB_CURRICULUM;
        }
        const shape = $('bk-shape-title');
        if (shape) shape.textContent = wb ? 'How long should it be?' : 'How long, and what comes with it?';
        const include = $('bk-include-group');
        if (include) include.hidden = wb;
        const write = $('btn-write-booklet');
        if (write && !write.disabled) write.textContent = bkWriteLabel();
        const toggle = document.querySelector('.bk-view-toggle');
        if (toggle) toggle.hidden = wb;
        const previewToggle = $('bkp-kind-toggle');
        if (previewToggle) previewToggle.hidden = wb;
        // A workbook-only booklet has no guide to download.
        const guideOpt = document.querySelector('#bk-reader [data-bk-what-guide]');
        if (guideOpt) {
            guideOpt.hidden = wb;
            const radio = guideOpt.querySelector('input');
            if (wb && radio.checked) document.querySelector('#bk-reader input[name="bk-export-what"][value="workbook"]').checked = true;
            if (!wb && !radio.checked) radio.checked = true;
        }
        bkState.view = wb ? 'workbook' : 'guide';
        document.querySelectorAll('.bk-view-btn').forEach((b) => {
            const on = b.dataset.bkView === bkState.view;
            b.classList.toggle('active', on);
            b.setAttribute('aria-pressed', String(on));
        });
    }

    function bkWriteLabel() { return bkIsWorkbook() ? 'Write the workbook' : 'Write the booklet'; }

    // Step 1's two ticks: the guide, the workbook, or both. Without the guide
    // it is a workbook on its own; with both, the workbook follows the guide.
    const bkMakeGuide    = $('bk-make-guide');
    const bkMakeWorkbook = $('bk-make-workbook');

    /** Set which documents are being made, keeping the ticks in step. */
    function setBookletParts(guide, workbook) {
        if (bkMakeGuide) bkMakeGuide.checked = guide;
        if (bkMakeWorkbook) bkMakeWorkbook.checked = workbook;
        bkState.kind = guide ? 'booklet' : 'workbook';
        applyBookletKind();
    }

    // Unticking the last one ticks the other instead: a booklet with neither
    // document has nothing to write, so the choice becomes a swap.
    function bkMakeChanged(e) {
        if (!bkMakeGuide?.checked && !bkMakeWorkbook?.checked) {
            const other = e.target === bkMakeGuide ? bkMakeWorkbook : bkMakeGuide;
            if (other) other.checked = true;
        }
        setBookletParts(!!bkMakeGuide?.checked, !!bkMakeWorkbook?.checked);
    }
    bkMakeGuide?.addEventListener('change', bkMakeChanged);
    bkMakeWorkbook?.addEventListener('change', bkMakeChanged);

    const bkSubjectCombobox = initSubjectCombobox({
        inputId: 'bk-subject-input', hiddenId: 'bk-subject',
        dropId: 'bk-sc-dropdown', clearId: 'bk-subject-clear',
    });
    // Registered late: this combobox is built well below the coupling block,
    // so the initial refresh ran without it. Re-run now that it exists, or the
    // booklet composer keeps the full subject list until the next change event.
    NC_SUBJECT_COMBOS['bk-exam-board'] = bkSubjectCombobox;
    refreshBoardTileCoupling();

    function bkSubject() { return $('bk-subject')?.value || ''; }

    function bkCurriculumReady() {
        return !!(bkSubject() && bkExamBoard?.value && bkLevel?.value);
    }

    // ── Step 1: curriculum ────────────────────────────────

    async function updateBookletCoverage() {
        if (!bkCoverageEl) return;
        if (!bkCurriculumReady()) { hide(bkCoverageEl); return; }
        try {
            const res = await apiFetch('/api/spec-coverage?board=' + encodeURIComponent(bkExamBoard.value)
                + '&subject=' + encodeURIComponent(bkSubject())
                + '&level=' + encodeURIComponent(bkLevel.value)
                + '&tier=' + encodeURIComponent($('bk-tier')?.value || ''));
            const a = await res.json();
            // Three states, each saying exactly what it is. 'aligned' is real
            // grounding but must not borrow the verified wording: the NC data
            // is authored against the programme of study, not extracted from
            // a PDF word-for-word.
            const state = a.state || (a.verified ? 'verified' : 'generic');
            bkCoverageEl.dataset.state = state;
            if (state === 'verified') {
                bkCoverageEl.innerHTML = '<span class="spec-coverage-icon">✓</span><div><b>' + esc(a.board) + ' ' + esc(a.subject)
                  + ' ' + esc(a.level) + '</b> – ' + a.points
                  + ' specification points held, quoted verbatim from the published specification.</div>';
            } else if (state === 'aligned') {
                bkCoverageEl.innerHTML = '<span class="spec-coverage-icon">✓</span><div><b>' + esc(a.board) + ' ' + esc(a.subject) + ' ' + esc(a.level) + '</b> – '
                  + (a.viaKeyStage
                      ? esc(curriculumSpanLine(a, 'The booklet')).replace(/^The /, 'the ')
                      : !isCurriculumBoard(a.board)
                      ? a.statements + ' points held, summarised from the published specification.'
                      : a.statements + ' objectives held, aligned to the published '
                        + (a.board === CFE_BOARD_VALUE ? 'Benchmarks.' : 'programme of study.'))
                  + '</div>';
            } else {
                bkCoverageEl.innerHTML = '<div>We do not hold this board’s published specification, so a booklet cannot be built from it yet.</div>';
            }
            show(bkCoverageEl);
        } catch { hide(bkCoverageEl); }
    }

    // ── Step 2: what it covers ──────────────────────

    /**
     * The tick list for this board/subject/level, where one is authored.
     *
     * The picker itself is /js/topic-picker.js, shared with the presentation,
     * worksheet and quiz composers. What is booklet-specific stays here: the
     * list REPLACES the free-text brief rather than sitting beside it, so the
     * box below changes from "what should it cover?" to "anything else?", the
     * step's own hint changes with it, and the count is weighed against how
     * many sections the chosen length has.
     */
    const bkPicker = window.TopicPicker?.create({
        group: bkTopicsGroup,
        list: bkTopicsEl,
        countEl: bkTopicsCount,
        clearBtn: bkTopicsClear,
        apiFetch,
        onChange: (picker) => {
            renderBookletBrief(picker);
            updateBookletTopicWarning();
            setError(bkError, '');
        },
    });

    /** Which of step 2's two forms is in front of the teacher. */
    function renderBookletBrief(picker) {
        if (!bkDescriptionGroup) return;

        if (!picker.catalogue) {
            show(bkDescriptionGroup);
            bkHints[0] = BK_HINT_BRIEF;
            if (bkBriefSub) bkBriefSub.textContent = BK_SUB_BRIEF;
            if (bkDescriptionLabel) bkDescriptionLabel.textContent = 'What should it cover?';
            if (bkDescriptionInput) {
                bkDescriptionInput.rows = 2;
                bkDescriptionInput.placeholder = 'e.g. Photosynthesis and cellular respiration, including the equations and limiting factors';
            }
            return;
        }

        bkHints[0] = BK_HINT_TOPICS;
        if (bkBriefSub) bkBriefSub.textContent = BK_SUB_TOPICS;
        // The box stays, but it stops being the brief and stops being
        // required: ticks say what to cover, this says how to pitch it.
        show(bkDescriptionGroup);
        if (bkDescriptionLabel) bkDescriptionLabel.innerHTML = 'Anything else? <span class="ws-optional">(optional)</span>';
        if (bkDescriptionInput) {
            bkDescriptionInput.rows = 2;
            bkDescriptionInput.placeholder = 'Tick topics below, or describe it – e.g. Photosynthesis, Foundation tier, heavy on the calculations';
        }
    }

    /**
     * The one warning worth showing.
     *
     * Ticking more topics than the chosen length has sections is allowed — a
     * teacher may well want two topics per section — but it is worth saying
     * out loud, because the alternative is discovering it on the contents page
     * after the plan has run.
     */
    function updateBookletTopicWarning() {
        if (!bkTopicsWarning) return;
        const n = bkPicker?.catalogue ? bkPicker.count() : 0;
        const sections = window.BookletSchema.lengthFor(bkState.length).targetSections;
        if (n > sections) {
            bkTopicsWarning.textContent = 'That is ' + n + ' topics for about ' + sections
                + ' sections – some will share a section, or be covered lightly. A longer booklet gives each one more room.';
            show(bkTopicsWarning);
        } else {
            hide(bkTopicsWarning);
        }
    }

    function loadBookletTopics() {
        bkPicker?.load(bkExamBoard?.value, bkSubject(), bkLevel?.value, $('bk-tier')?.value || '');
    }

    function bkCurriculumChanged() {
        refreshIgcseHints();
        updateBookletCoverage();
        loadBookletTopics();
    }
    bkExamBoard?.addEventListener('change', bkCurriculumChanged);
    $('bk-tier')?.addEventListener('change', () => { updateBookletCoverage(); loadBookletTopics(); });
    bkLevel?.addEventListener('change', bkCurriculumChanged);
    // The combobox commits to the HIDDEN input and fires change there, not on
    // the visible one the teacher types into (see initSubjectCombobox).
    $('bk-subject')?.addEventListener('change', bkCurriculumChanged);

    // ── Step 3: length ────────────────────────────────────

    // Tiles rather than a segmented control because each option carries a
    // sentence, and a <button role=radio> rather than the .ws-tile radios used
    // elsewhere because a locked Pro option has to open billing instead of
    // selecting. That means none of the :has(input:checked) styling applies,
    // so the selected tile is marked with .is-selected and styled from it –
    // it previously set .active, which nothing in the stylesheet matched, and
    // so the chosen length looked identical to the two beside it.
    function renderBookletLengths() {
        if (!bkLengthTiles) return;
        const isPro = state.user?.plan === 'pro';
        bkLengthTiles.innerHTML = window.BookletSchema.LENGTHS.map((l) => {
            const locked = l.proOnly && !isPro;
            const on = l.id === bkState.length && !locked;
            return '<button type="button" class="bk-length-tile'
                + (on ? ' is-selected' : '') + (locked ? ' is-locked' : '') + '"'
                + ' role="radio" aria-checked="' + on + '" data-length="' + esc(l.id) + '"'
                + (locked ? ' data-locked="true"' : '') + '>'
                + '<span class="bk-length-mark" aria-hidden="true">'
                + '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.4l2.6 2.6L9.6 3"/></svg>'
                + '</span>'
                + '<span class="bk-length-name">' + esc(l.label)
                + (locked ? '<span class="bk-pro">Pro</span>' : '') + '</span>'
                + '<span class="bk-length-count">About ' + l.targetSections + ' sections</span>'
                + '<span class="bk-length-blurb">' + esc(l.blurb) + '</span>'
                + '</button>';
        }).join('');
    }

    bkLengthTiles?.addEventListener('click', (e) => {
        const tile = e.target.closest('.bk-length-tile');
        if (!tile) return;
        if (tile.dataset.locked === 'true') { showBilling(); return; }
        bkState.length = tile.dataset.length;
        // The "more topics than sections" warning is a statement about both,
        // so changing the length has to re-evaluate it.
        updateBookletTopicWarning();
        renderBookletLengths();
        // Re-rendering throws away the element that had focus, so put it back
        // on the equivalent tile – otherwise choosing a length by keyboard
        // drops focus to the top of the document.
        bkLengthTiles.querySelector('.bk-length-tile.is-selected')?.focus();
    });

    // A radiogroup is expected to move between its options with the arrow
    // keys; Tab alone reaches every tile, which is not the same thing.
    bkLengthTiles?.addEventListener('keydown', (e) => {
        const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
            : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!step) return;
        const tiles = Array.from(bkLengthTiles.querySelectorAll('.bk-length-tile'));
        const from = tiles.indexOf(e.target.closest('.bk-length-tile'));
        if (from < 0) return;
        e.preventDefault();
        const next = tiles[(from + step + tiles.length) % tiles.length];
        // Arrowing onto the locked Pro option should show it, not throw the
        // teacher out to the billing page mid-keystroke.
        if (next.dataset.locked === 'true') next.focus();
        else next.click();
    });

    // ── The wizard ────────────────────────────────────────

    const bkWizard = initComposerWizard({
        formId: 'bk-page-form',
        nextLabels: ['Next: length and contents →'],
        hints: bkHints,
        submitId: 'btn-plan-booklet',
        isReady: () => {
            // Ticks, a description, or a file (which fills the description in):
            // any one says what to cover. The server takes a description on
            // its own even where a topic list exists, and matches it to the
            // specification during planning.
            if (!bkPicker?.count() && !bkDescriptionInput?.value.trim()) {
                setError(bkError, bkPicker?.catalogue
                    ? 'Tick topics, describe what it should cover, or add a file'
                    : 'Describe what it should cover, or add a file');
                bkDescriptionInput?.focus();
                return false;
            }
            // Unlike the other composers the curriculum is not optional here:
            // there is nothing to write a booklet against without it.
            if (!bkCurriculumReady()) {
                setError(bkError, 'Choose a subject, exam board and level – a booklet is written from that board’s specification');
                return false;
            }
            setError(bkError, '');
            return true;
        },
    });

    // ── Planning ──────────────────────────────────────────

    /**
     * Read one SSE stream to completion.
     *
     * EventSource cannot be used: these endpoints are behind the session
     * cookie and need the same credentials handling as every other call, and
     * EventSource gives no way to tell a finished stream from a dropped one.
     */
    async function bkStream(url, onEvent) {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok || !res.body) throw new Error('Could not start');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let split;
            while ((split = buffer.indexOf('\n\n')) >= 0) {
                const chunk = buffer.slice(0, split);
                buffer = buffer.slice(split + 2);
                const type = /event: (.+)/.exec(chunk);
                const data = /data: ([\s\S]+)/.exec(chunk);
                if (!type || !data) continue;
                try { onEvent(type[1], JSON.parse(data[1])); } catch { /* malformed frame */ }
            }
        }
    }

    $('btn-plan-booklet')?.addEventListener('click', async () => {
        setError(bkError, '');
        const btn = $('btn-plan-booklet');
        if (btn) { btn.disabled = true; btn.textContent = 'Planning…'; }
        try {
            const res = await apiFetch('/api/booklets', {
                method: 'POST',
                body: JSON.stringify({
                    subject: bkSubject(),
                    examBoard: bkExamBoard.value,
                    level: bkLevel.value,
                    tier: $('bk-tier')?.value || '',
                    title: bkTitleInput?.value.trim() || undefined,
                    description: bkDescriptionInput?.value.trim(),
                    sourceText: sourceTextFor('bk-source-input', 20000) || undefined,
                    // Ids, not labels — see the note on topicId in
                    // utils/specTopics.js. Omitted entirely when no list was
                    // offered, so the server takes the description as the brief.
                    topics: bkPicker?.catalogue ? bkPicker.ids() : undefined,
                    length: bkState.length,
                    homeLanguage:    $('bk-home-language')?.value || '',
                    includeGuide:    !bkIsWorkbook(),
                    includeWorkbook: !!bkMakeWorkbook?.checked,
                    includeDiagrams: !bkIsWorkbook() && !!$('bk-include-diagrams')?.checked,
                }),
            });
            const body = await res.json();
            if (!res.ok) {
                if (body.upgrade) showBilling();
                setError(bkError, body.error || 'Could not start this booklet.');
                return;
            }
            bkState.id = body.id;
            linkToTutoringSession('booklet', body.id);
            bkState.meta = {
                title: '', subject: bkSubject(),
                examBoard: bkExamBoard.value, level: bkLevel.value,
            };
            history.pushState({ bookletId: body.id }, '', '/booklets/' + body.id);
            await planBooklet();
        } catch {
            setError(bkError, 'Something went wrong. Try again.');
        } finally {
            if (btn) { btn.disabled = false; btn.textContent = 'Plan the contents'; }
        }
    });

    async function planBooklet() {
        hide(bkPageForm, bkReader);
        show(bkPlanView);
        setError(bkPlanError, '');
        if (bkPlanList) {
            bkPlanList.innerHTML = '<li class="bk-plan-loading">Reading the specification and planning the contents…</li>'
                + '<li class="bk-plan-skel" aria-hidden="true"></li>'.repeat(5);
        }
        const hint = $('bk-plan-hint');
        if (hint) hint.textContent = '';
        // Nothing to write until there is a plan; renderBookletPlan turns it
        // back on through updateBookletPlanHint.
        const go = $('btn-write-booklet');
        if (go) go.disabled = true;

        let failed = null;
        try {
            await bkStream('/api/booklets/' + bkState.id + '/plan-stream', (type, data) => {
                if (type === 'plan_done') {
                    bkState.plan = data;
                    if (bkState.meta) bkState.meta.title = data.title;
                } else if (type === 'quota_exceeded') {
                    failed = data.message || 'You have used all your booklets on this plan for now.';
                } else if (type === 'error') {
                    failed = data.message;
                }
            });
        } catch {
            failed = 'The connection dropped while planning. Try again.';
        }

        if (failed || !bkState.plan) {
            setError(bkPlanError, failed || 'Planning did not produce a contents page.');
            if (bkPlanList) bkPlanList.innerHTML = '';
            return;
        }
        renderBookletPlan();
    }

    function renderBookletPlan() {
        const plan = bkState.plan;
        const titleEl = $('bk-plan-title');
        if (titleEl) titleEl.textContent = plan.title;

        if (bkPlanList) {
            // Laid out as the contents page it will become: each chapter named
            // once, its sections numbered beneath it. The chapter still rides
            // on every row (hidden) because approval reads it from there.
            let lastChapter = null;
            bkPlanList.innerHTML = plan.sections.map((s, i) => {
                const chapter = s.chapter || '';
                const head = chapter !== lastChapter
                    ? '<li class="bk-plan-chap"' + (chapter ? '' : ' data-untitled') + '>' + esc(chapter) + '</li>'
                    : '';
                lastChapter = chapter;
                const heading = esc(s.heading).replace(/"/g, '&quot;');
                return head
                    + '<li class="bk-plan-row" data-position="' + s.position + '">'
                    + '<span class="bk-plan-chapter">' + esc(chapter) + '</span>'
                    // A textarea so a long heading wraps on a narrow screen
                    // instead of being cut off; it is still one line of text.
                    + '<textarea class="bk-plan-heading" rows="1" aria-label="Section ' + (i + 1) + ' heading">'
                    + esc(s.heading) + '</textarea>'
                    + '<button type="button" class="bk-plan-drop" aria-label="Remove section: ' + heading + '" title="Remove this section">'
                    + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'
                    + '</button>'
                    + '</li>';
            }).join('');
        }
        updateBookletPlanHint();
    }

    function updateBookletPlanHint() {
        const hint = $('bk-plan-hint');
        const n = bkPlanList ? bkPlanList.querySelectorAll('.bk-plan-row').length : 0;
        const chapters = bkPlanList ? bkPlanList.querySelectorAll('.bk-plan-chap:not([data-untitled])').length : 0;
        // Sections only. The page count that used to sit here was arithmetic on
        // a guess – how long a section runs to depends on the diagrams and
        // tables it turns out to need, which nothing knows until it is written.
        if (hint) {
            hint.textContent = n + ' section' + (n === 1 ? '' : 's')
                + (chapters > 1 ? ' in ' + chapters + ' chapters' : '') + ' to write';
        }
        const go = $('btn-write-booklet');
        if (go) go.disabled = n === 0;
    }

    // Enter finishes a heading rather than breaking it across lines.
    bkPlanList?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.classList?.contains('bk-plan-heading')) {
            e.preventDefault();
            e.target.blur();
        }
    });

    bkPlanList?.addEventListener('click', (e) => {
        if (!e.target.closest('.bk-plan-drop')) return;
        const row = e.target.closest('.bk-plan-row');
        if (!row) return;
        // A chapter with nothing left under it goes with its last section.
        const chap = row.previousElementSibling;
        const after = row.nextElementSibling;
        row.remove();
        if (chap?.classList.contains('bk-plan-chap') && !after?.classList.contains('bk-plan-row')) chap.remove();
        updateBookletPlanHint();
    });

    $('btn-plan-back')?.addEventListener('click', () => {
        hide(bkPlanView, bkReader);
        show(bkPageForm);
        bkWizard?.setStep(1);
    });

    // ── Generating ────────────────────────────────────────

    $('btn-write-booklet')?.addEventListener('click', async () => {
        setError(bkPlanError, '');
        // Only the heading and the order are the teacher's to change; the spec
        // codes come from the saved plan server-side, keyed by position.
        const sections = Array.from(bkPlanList?.querySelectorAll('.bk-plan-row') || []).map((row) => ({
            position: Number(row.dataset.position),
            heading: (row.querySelector('.bk-plan-heading')?.value || '').replace(/\s+/g, ' ').trim(),
            chapter: row.querySelector('.bk-plan-chapter')?.textContent || '',
        }));
        if (!sections.length) return;
        bkState.plannedSections = sections.length;

        const btn = $('btn-write-booklet');
        if (btn) { btn.disabled = true; btn.textContent = 'Starting…'; }
        try {
            const res = await apiFetch('/api/booklets/' + bkState.id + '/approve', {
                method: 'POST',
                body: JSON.stringify({ plan: { title: bkState.plan.title, sections } }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setError(bkPlanError, body.error || 'Could not start writing.');
                return;
            }
            await writeBooklet();
        } catch {
            setError(bkPlanError, 'Something went wrong. Try again.');
        } finally {
            if (btn) { btn.disabled = false; btn.textContent = bkWriteLabel(); }
        }
    });

    async function writeBooklet() {
        hide(bkPageForm, bkPlanView);
        show(bkReader);
        setError(bkReaderError, '');
        bkState.sections = [];
        bkState.streaming = true;
        if (bkReaderBody) bkReaderBody.innerHTML = '';
        showBookletGenerating(true);
        renderBookletProgress(0, 0);

        let started = 0, done = 0, failed = 0, fatal = null;
        try {
            await bkStream('/api/booklets/' + bkState.id + '/stream', (type, data) => {
                if (type === 'section_start') {
                    started++;
                    addBookletSkeleton(data);
                } else if (type === 'section_done') {
                    done++;
                    // The text is here; the picture is chosen for the whole
                    // document once the last section is written, so a section
                    // that asked for one shows a placeholder until it lands.
                    if (bkWantsPicture(data)) data.imagePending = true;
                    upsertBookletSection(data);
                    renderBookletProgress(done, Math.max(started, done));
                } else if (type === 'image_done') {
                    // Pictures are chosen for the whole document in one vision
                    // call once the writing is finished, so they arrive after
                    // their section did. See resolveBookletImages in
                    // routes/booklets.js for why that is worth the wait.
                    applyBookletSectionImage(data);
                } else if (type === 'section_error') {
                    failed++;
                    markBookletSectionFailed(data);
                    renderBookletProgress(done, Math.max(started, done));
                } else if (type === 'quota_exceeded') {
                    fatal = data.message || 'You have used all your booklets on this plan for now.';
                } else if (type === 'error') {
                    fatal = data.message;
                }
            });
        } catch {
            // The stream dropped. Nothing is lost: the sections that finished
            // are saved, and reopening the booklet resumes from where it got
            // to without charging again.
            fatal = 'The connection dropped. Reopen this booklet to carry on from where it stopped.';
        }
        bkState.streaming = false;
        // Nothing else is coming, so any section still showing a placeholder is
        // one the ranker rejected, or one whose image pass never ran. Either
        // way the answer is "no picture", and a placeholder left shimmering
        // after the run is a worse lie than an absent figure.
        bkSettlePendingImages();

        if (fatal) setError(bkReaderError, fatal);
        // The server has just marked it done; without mirroring that here the
        // toolbar keeps the finished booklet's own controls hidden until it is
        // closed and reopened.
        if (!fatal && done && done >= (bkState.plannedSections || 0) && bkState.meta) {
            bkState.meta.status = 'done';
            bkSyncSetLessonBtn();
            bkSyncShareBtn();
        }
        // Nothing written at all means the card has nothing to report that the
        // error above it does not already say.
        if (done || failed) finishBookletGenerating(done, failed);
        else showBookletGenerating(false);
        // The banner says how it went, so the toolbar does not say it again.
        if (bkProgress) bkProgress.textContent = '';
    }

    /**
     * The banner and the toolbar status, from one place.
     *
     * `total` is how many sections have STARTED, not how many the plan holds,
     * because the stream only says a section exists when it begins one. The
     * plan length is the honest denominator, so it is passed in separately and
     * used when it is known.
     */
    function renderBookletProgress(done, started) {
        const total = bkState.plannedSections || started;
        if (bkGenCount) bkGenCount.textContent = total ? done + ' / ' + total : '';
        if (bkGenSub) {
            bkGenSub.textContent = total
                ? 'Section ' + Math.min(done + 1, total) + ' of ' + total + ' is being written now.'
                : 'Getting the first section started…';
        }
        const pct = total ? Math.round((done / total) * 100) : 0;
        if (bkGenFill) bkGenFill.style.width = pct + '%';
        if (bkGenBar) bkGenBar.setAttribute('aria-valuenow', String(pct));
    }

    // finishBookletGenerating() rewrites the note, and the same card is reused
    // for the next booklet opened in this session, so the wording it starts
    // with is kept rather than read back out of the element.
    const BK_GEN_NOTE = $('bk-generating')?.querySelector('.bk-gen-note')?.textContent || '';

    function showBookletGenerating(on) {
        if (!bkGenerating) return;
        if (on) {
            const note = bkGenerating.querySelector('.bk-gen-note');
            if (note) note.textContent = BK_GEN_NOTE;
            bkGenerating.classList.remove('is-done');
            bkGenIcon?.setAttribute('data-state', 'a');
            show(bkGenerating);
        } else {
            hide(bkGenerating);
        }
    }

    /**
     * The end of the run, in the same card rather than in a different place.
     *
     * The spinner becomes a tick and the pulse stops, which is what the
     * presentation generator does when its own run finishes – a card that
     * simply vanished left the teacher unsure whether it had finished or the
     * page had given up.
     */
    function finishBookletGenerating(done, failed) {
        if (!bkGenerating) return;
        bkGenerating.classList.add('is-done');
        bkGenIcon?.setAttribute('data-state', 'b');
        if (bkGenFill) bkGenFill.style.width = '100%';
        if (bkGenBar) bkGenBar.setAttribute('aria-valuenow', '100');
        if (bkGenCount) bkGenCount.textContent = '';
        if (bkGenSub) {
            bkGenSub.textContent = failed
                ? done + ' of ' + (done + failed) + ' sections written — rewrite the rest below.'
                : 'Finished. ' + done + ' section' + (done === 1 ? '' : 's') + ' written.';
        }
        const note = bkGenerating.querySelector('.bk-gen-note');
        if (note) note.textContent = 'Print or download it from the button above.';
    }

    // ── The reader ────────────────────────────────────────

    function bkSectionEl(position) {
        return bkReaderBody?.querySelector('[data-position="' + position + '"]') || null;
    }

    function addBookletSkeleton(data) {
        if (!bkReaderBody || bkSectionEl(data.position)) return;
        const el = document.createElement('article');
        el.className = 'bk-r-section is-pending';
        el.dataset.position = String(data.position);
        // The skeleton bars alone read as a layout that has not loaded. The tag
        // says which of the two it is – this section is being written now.
        el.innerHTML = '<h3 class="bk-r-heading">' + esc(data.heading || '')
            + '<span class="bk-r-writing">Writing…</span></h3>'
            + '<div class="bk-r-body"><div class="t-skel bk-r-skel"></div>'
            + '<div class="t-skel bk-r-skel"></div><div class="t-skel bk-r-skel short"></div></div>';
        bkReaderBody.appendChild(el);
    }

    // ── Reading the two documents ─────────────────────────
    //
    // The reader shows one section at a time in whichever document is
    // selected: the guide's blocks, or the workbook's questions. Both come
    // from the same renderer the PDF uses, so what is on screen is what
    // prints, and both are editable in place by the same field-driven form
    // below.

    const BkSchema = window.BookletSchema;
    const BkRender = window.BookletRender;

    function bkWorkbookHtml(section) {
        const list = section.exercises || [];
        if (!list.length) {
            return '<p class="bk-r-empty">This section has no workbook questions yet.</p>';
        }
        // Grouped by tier, the same way the printed workbook groups them.
        // validateExercises has already put them in tier order.
        let html = '';
        let lastTier = null;
        let n = 0;
        for (const ex of list) {
            if (ex.tier !== lastTier) {
                if (lastTier !== null) html += '</div>';
                html += '<div class="bk-tier bk-tier-' + esc(ex.tier) + '"><h4>'
                    + esc(BkRender.TIER_LABELS[ex.tier] || ex.tier) + '</h4>';
                lastTier = ex.tier;
            }
            html += BkRender.renderExercise(ex, ++n);
        }
        if (lastTier !== null) html += '</div>';
        return html;
    }

    function bkSectionBodyHtml(section) {
        const inner = bkState.view === 'workbook'
            ? bkWorkbookHtml(section)
            : (section.blocks || []).map((b) => BkRender.renderBlock(b, section)).join('');
        return '<div class="bk-doc bk-r-rendered">' + inner + '</div>';
    }

    function upsertBookletSection(section) {
        const existing = bkState.sections.findIndex((s) => s.position === section.position);
        if (existing >= 0) bkState.sections[existing] = section;
        else bkState.sections.push(section);
        bkState.sections.sort((a, b) => a.position - b.position);

        addBookletSkeleton(section);
        const el = bkSectionEl(section.position);
        if (!el) return;
        el.classList.remove('is-pending', 'is-failed', 'is-editing');
        // Held on the element so the edit and rewrite paths can find the row to
        // write to without searching bkState again.
        if (section.id) el.dataset.sectionId = section.id;
        el.innerHTML = '<div class="bk-r-tools">'
            + '<button type="button" class="btn btn-ghost btn-sm bk-r-edit" data-position="' + section.position + '">Edit</button>'
            + '<button type="button" class="btn btn-ghost btn-sm bk-r-regen" data-position="' + section.position + '">Rewrite this section</button>'
            + '</div>'
            + bkSectionBodyHtml(section);
        const head = document.createElement('h3');
        head.className = 'bk-r-heading';
        head.textContent = section.heading;
        el.prepend(head);
    }

    /** Did the writer ask for a picture in this section? */
    function bkWantsPicture(section) {
        return (section.blocks || []).some((b) => b && b.type === 'diagram' && b.query);
    }

    /**
     * Re-render one section's body in place.
     *
     * Not upsertBookletSection: that rebuilds the section's innerHTML, which
     * would throw away an edit the teacher had open. Sections stream in with
     * live Edit buttons and the pictures land at the end of the run, so the
     * two genuinely overlap. The caller always updates state; this only
     * repaints, and declines to while the editor is open — the saved section
     * carries the picture then.
     */
    function bkRepaintSectionBody(position) {
        const i = bkState.sections.findIndex((s) => s.position === position);
        if (i < 0) return;
        const el = bkSectionEl(position);
        if (!el || el.classList.contains('is-editing')) return;
        const body = el.querySelector('.bk-r-rendered');
        if (body) body.outerHTML = bkSectionBodyHtml(bkState.sections[i]);
    }

    /** Every placeholder still on screen becomes "no picture". */
    function bkSettlePendingImages() {
        for (const section of bkState.sections) {
            if (!section.imagePending) continue;
            section.imagePending = false;
            bkRepaintSectionBody(section.position);
        }
    }

    /**
     * A picture arriving for a section that is already on screen.
     *
     * Not upsertBookletSection: that rebuilds the section's innerHTML, which
     * would throw away an edit the teacher had open. Sections stream in with
     * live Edit buttons and the images land at the end of the run, so the two
     * genuinely overlap. State is always updated; the re-render waits until
     * the editor is closed, and the saved section carries the picture then.
     */
    function applyBookletSectionImage(data) {
        const i = bkState.sections.findIndex((s) => s.position === data.position);
        if (i < 0) return;
        bkState.sections[i].imageUrl = data.imageUrl;
        bkState.sections[i].imageCredit = data.imageCredit;
        bkState.sections[i].imagePending = false;
        bkRepaintSectionBody(data.position);
    }

    function markBookletSectionFailed(data) {
        addBookletSkeleton(data);
        const el = bkSectionEl(data.position);
        if (!el) return;
        el.classList.remove('is-pending');
        el.classList.add('is-failed');
        el.innerHTML = '<h3 class="bk-r-heading">' + esc(data.heading || 'Section') + '</h3>'
            + '<p class="bk-r-failed">This section did not come back. '
            + '<button type="button" class="btn btn-ghost btn-sm bk-r-regen" data-position="' + data.position + '">Try again</button></p>';
    }

    // Switching document re-renders every card. A half-finished edit is dropped
    // rather than carried across: the two documents hold different content, and
    // a form for one cannot be saved into the other.
    document.querySelector('.bk-view-toggle')?.addEventListener('click', (e) => {
        const btn = e.target.closest('.bk-view-btn');
        if (!btn || btn.dataset.bkView === bkState.view) return;
        if (bkState.editing && !confirm('You have unsaved changes to a section. Switch anyway and lose them?')) return;
        bkState.view = btn.dataset.bkView;
        bkState.editing = null;
        document.querySelectorAll('.bk-view-btn').forEach((b) => {
            const on = b === btn;
            b.classList.toggle('active', on);
            b.setAttribute('aria-pressed', String(on));
        });
        setError(bkReaderError, '');
        if (bkReaderBody) bkReaderBody.innerHTML = '';
        bkState.sections.forEach(upsertBookletSection);
    });

    // ── Editing a section by hand ─────────────────────────
    //
    // The form is built from BLOCK_TYPES[].fields rather than from a hand
    // written form per block type, which is what that table is for: a new block
    // type gets an editor for free, and the editor cannot drift out of step
    // with what the validator will accept.
    //
    // Lists are edited as one item per line. A grid of inputs is more precise
    // and much worse to use - reordering four points should be selecting two
    // lines, not dragging four boxes.

    const BK_FIELD_LABELS = {
        title: 'Title', text: 'Text', items: 'Points', term: 'Term',
        definition: 'Definition', question: 'Question', steps: 'Steps',
        answer: 'Answer', headers: 'Column headings', rows: 'Rows',
        latex: 'Formula (LaTeX)', meaning: 'What the symbols mean',
        query: 'What the picture must show', caption: 'Caption',
        questions: 'Questions', quip: 'Quip', summary: 'Summary',
    };

    const BK_FIELD_HINTS = {
        list: 'One per line',
        longlist: 'One per line',
        rows: 'One row per line, cells separated by |',
        qa: 'One per line, as: question | answer',
    };

    const BK_FIELD_ROWS = { text: 4, definition: 3, list: 4, longlist: 6, rows: 4, qa: 3 };

    // A stored field value as the text the teacher edits.
    function bkFieldToText(kind, value) {
        if (kind === 'list' || kind === 'longlist') return (value || []).join('\n');
        if (kind === 'rows') return (value || []).map((r) => (r || []).join(' | ')).join('\n');
        if (kind === 'qa') return (value || []).map((q) => q.question + ' | ' + q.answer).join('\n');
        // A drawn diagram is not text: it rides through the form as JSON in a
        // hidden input, and the controls beside it rewrite that JSON.
        if (kind === 'curve') return value ? JSON.stringify(value) : '';
        return value == null ? '' : String(value);
    }

    // ...and back again. Split on the FIRST bar only, so an answer is free to
    // contain one.
    function bkFieldFromText(kind, text) {
        if (kind === 'curve') { try { return JSON.parse(text); } catch { return null; } }
        const lines = String(text).split('\n').map((l) => l.trim()).filter(Boolean);
        if (kind === 'list' || kind === 'longlist') return lines;
        if (kind === 'rows') return lines.map((l) => l.split('|').map((c) => c.trim()));
        if (kind === 'qa') {
            return lines.map((l) => {
                const at = l.indexOf('|');
                return at === -1
                    ? { question: l.trim(), answer: '' }
                    : { question: l.slice(0, at).trim(), answer: l.slice(at + 1).trim() };
            });
        }
        return String(text).trim();
    }

    // The diagram's controls: which diagram, then one menu per choice that
    // diagram offers. A block the model drew by hand ("custom") has no preset
    // to offer back, so its menu starts on a prompt and its picture is kept
    // until the teacher picks something else.
    function bkCurveControlsHtml(value) {
        const presets = window.CurveDiagramPresets ? window.CurveDiagramPresets.list() : [];
        const source = (value && value.source) || {};
        const current = presets.find((p) => p.id === source.preset);
        const pick = '<label class="bk-ed-f"><span>Diagram</span><select data-bk-curve-preset>'
            + (current ? '' : '<option value="">' + (value ? 'Custom diagram – choose to replace' : 'Choose a diagram') + '</option>')
            + presets.map((p) => '<option value="' + escAttr(p.id) + '"' + (current === p ? ' selected' : '') + '>' + esc(p.title) + '</option>').join('')
            + '</select></label>';
        const words = (v) => v.replace(/-/g, ' ');
        const options = current ? Object.keys(current.options).map((name) => {
            const def = current.options[name];
            const chosen = (source.options && source.options[name]) || def.default;
            return '<label class="bk-ed-f"><span>' + esc(def.description.split(/[;(]/)[0].trim()) + '</span>'
                + '<select data-bk-curve-opt="' + escAttr(name) + '">'
                + def.values.map((v) => '<option value="' + escAttr(v) + '"' + (v === chosen ? ' selected' : '') + '>' + esc(words(v)) + '</option>').join('')
                + '</select></label>';
        }).join('') : '';
        const preview = value && window.CurveDiagram ? window.CurveDiagram.render(value) : '';
        return pick + options + '<div class="bk-ed-curve-preview">' + preview + '</div>';
    }

    function bkCurveFieldHtml(name, value) {
        return '<div class="bk-ed-curve">'
            + '<input type="hidden" data-field="' + escAttr(name) + '" data-kind="curve" value="' + escAttr(bkFieldToText('curve', value)) + '">'
            + '<div class="bk-ed-curve-controls">' + bkCurveControlsHtml(value) + '</div></div>';
    }

    // A menu changed: rebuild the diagram from the menus and redraw them, since
    // a different diagram offers different choices.
    function bkCurveChanged(target) {
        const wrap = target.closest('.bk-ed-curve');
        const store = wrap && wrap.querySelector('input[data-kind="curve"]');
        const presetId = wrap && wrap.querySelector('[data-bk-curve-preset]').value;
        if (!store || !presetId || !window.CurveDiagramPresets) return;
        const options = {};
        // Only when the diagram itself did not change - another diagram's choices mean nothing to this one.
        if (!target.matches('[data-bk-curve-preset]')) {
            wrap.querySelectorAll('[data-bk-curve-opt]').forEach((el) => { options[el.dataset.bkCurveOpt] = el.value; });
        }
        const spec = window.CurveDiagramPresets.resolve({ preset: presetId, options });
        if (!spec) return;
        store.value = JSON.stringify(spec);
        wrap.querySelector('.bk-ed-curve-controls').innerHTML = bkCurveControlsHtml(spec);
    }

    function bkFieldHtml(name, kind, value) {
        if (kind === 'curve') return bkCurveFieldHtml(name, value);
        const label = esc(BK_FIELD_LABELS[name] || name);
        const hint = BK_FIELD_HINTS[kind] ? '<small>' + esc(BK_FIELD_HINTS[kind]) + '</small>' : '';
        const attrs = 'data-field="' + escAttr(name) + '" data-kind="' + escAttr(kind) + '"';
        if (kind === 'short' || kind === 'term') {
            return '<label class="bk-ed-f"><span>' + label + '</span>'
                + '<input type="text" ' + attrs + ' value="' + escAttr(bkFieldToText(kind, value)) + '"></label>';
        }
        return '<label class="bk-ed-f"><span>' + label + hint + '</span>'
            + '<textarea rows="' + (BK_FIELD_ROWS[kind] || 3) + '" ' + attrs + '>'
            + esc(bkFieldToText(kind, value)) + '</textarea></label>';
    }

    // `table` is the one block the validator handles specially rather than
    // through its fields map, so the editor spells it out the same way.
    function bkBlockFieldsHtml(block) {
        const def = BkSchema.BLOCK_TYPES.find((b) => b.id === block.type);
        if (!def) return '';
        if (def.id === 'table') {
            return bkFieldHtml('title', 'short', block.title)
                + bkFieldHtml('headers', 'list', block.headers)
                + bkFieldHtml('rows', 'rows', block.rows);
        }
        return Object.keys(def.fields)
            .map((name) => bkFieldHtml(name, def.fields[name], block[name]))
            .join('');
    }

    // Every box grows to fit what is in it. A one-word answer in a four-row
    // textarea and a paragraph in a two-row one are the same mistake made in
    // opposite directions, and a form full of both is what makes an editor
    // look like a database screen.
    function bkAutosize(el) {
        if (!el || el.offsetParent === null) return;   // hidden: scrollHeight is 0
        // Measured from zero, not from auto. At auto a textarea falls back to
        // its rows attribute, so scrollHeight never reports less than two lines
        // and every one-line answer keeps a spare line under it.
        el.style.height = '0px';
        el.style.height = (el.scrollHeight + 2) + 'px';
    }

    function bkAutosizeAll(root) {
        root?.querySelectorAll('.bk-ed textarea').forEach(bkAutosize);
    }

    // The number is drawn by a CSS counter rather than written in, so adding,
    // removing and reordering keep it right without a re-render. It is what
    // the "Block 3 is missing something" message points at.
    function bkItemHeadHtml(label, moves) {
        return '<div class="bk-ed-head">'
            + '<span class="bk-ed-num" aria-hidden="true"></span>'
            + label
            + '<span class="bk-ed-acts">'
            + (moves
                ? '<button type="button" class="bk-ed-btn" data-bk-move="up" title="Move up" aria-label="Move up">&#8593;</button>'
                + '<button type="button" class="bk-ed-btn" data-bk-move="down" title="Move down" aria-label="Move down">&#8595;</button>'
                : '')
            + '<button type="button" class="bk-ed-btn bk-ed-del" data-bk-remove title="Remove" aria-label="Remove">&#10005;</button>'
            + '</span></div>';
    }

    function bkBlockItemHtml(block) {
        const def = BkSchema.BLOCK_TYPES.find((b) => b.id === block.type);
        return '<div class="bk-ed-item" data-type="' + escAttr(block.type) + '">'
            + bkItemHeadHtml('<span class="bk-ed-kind">' + esc(def ? def.label : block.type) + '</span>', true)
            + bkBlockFieldsHtml(block)
            + '</div>';
    }

    function bkPartHtml(part) {
        return '<div class="bk-ed-part">'
            + '<span class="bk-ed-part-letter" aria-hidden="true"></span>'
            + '<div class="bk-ed-part-body">'
            + '<div class="bk-ed-row">'
            + '<label class="bk-ed-f bk-ed-grow"><span>Part question</span>'
            + '<textarea rows="2" data-field="question">' + esc(part.question || '') + '</textarea></label>'
            + '<label class="bk-ed-f bk-ed-marks"><span>Marks</span>'
            + '<input type="number" min="1" max="30" data-field="marks" value="' + escAttr(part.marks || '') + '"></label>'
            + '</div>'
            + '<label class="bk-ed-f"><span>Answer</span>'
            + '<textarea rows="2" data-field="answer">' + esc(part.answer || '') + '</textarea></label>'
            + '</div>'
            + '<button type="button" class="bk-ed-btn bk-ed-del" data-bk-removepart title="Remove part" aria-label="Remove part">&#10005;</button>'
            + '</div>';
    }

    function bkExerciseItemHtml(ex) {
        const tiers = BkSchema.EXERCISE_TIERS.map((t) =>
            '<option value="' + escAttr(t.id) + '"' + (t.id === ex.tier ? ' selected' : '') + '>'
            + esc(t.label) + '</option>').join('');
        const parts = (ex.parts || []).map(bkPartHtml).join('');
        return '<div class="bk-ed-item bk-ed-ex" data-parted="' + (parts ? '1' : '0') + '">'
            + bkItemHeadHtml('<label class="bk-ed-tier"><span class="sr-only">Tier</span>'
                + '<select data-field="tier">' + tiers + '</select></label>', false)
            // Marks rides alongside the question rather than taking a row of
            // its own: it is two characters, and a full-width row for it put
            // more space between a question and its answer than between one
            // question and the next.
            + '<div class="bk-ed-row">'
            + '<label class="bk-ed-f bk-ed-grow"><span>Question</span>'
            + '<textarea rows="2" data-field="question">' + esc(ex.question || '') + '</textarea></label>'
            + '<label class="bk-ed-f bk-ed-marks"><span>Marks</span>'
            + '<input type="number" min="1" max="30" data-field="marks" value="' + escAttr(ex.marks || '') + '"></label>'
            + '</div>'
            + '<label class="bk-ed-f bk-ed-answer"><span>Answer</span>'
            + '<textarea rows="2" data-field="answer">' + esc(ex.answer || '') + '</textarea></label>'
            + '<div class="bk-ed-parts">' + parts + '</div>'
            + '<button type="button" class="bk-ed-addpart" data-bk-addpart>+ Add a part</button>'
            + '</div>';
    }

    /**
     * The picture panel inside the section editor.
     *
     * Only for the guide view, and only where the section is one that has or
     * wants a picture — a section with no diagram block never had a figure to
     * swap, and offering one there would put a picture on a page whose text
     * makes no room for it.
     *
     * The search box is prefilled with the writer's own `query`, because nine
     * times in ten the teacher wants a better version of what was asked for
     * rather than something different, and retyping it is the tax on the
     * common case.
     */
    function bkImagePanelHtml(section) {
        const diagram = (section.blocks || []).find((b) => b && b.type === 'diagram');
        if (!diagram && !section.imageUrl) return '';
        // data-current is the picture as saved; data-picked appears only once
        // the teacher pastes or removes a link, and its absence is what tells
        // the save not to send an imageUrl at all. An upload does not use it:
        // that saves itself immediately, because a file already on the CDN is
        // storage being paid for whether or not the edit is saved.
        return '<div class="bk-ed-image" data-bk-image data-current="' + escAttr(section.imageUrl || '') + '">'
            + '<div class="bk-ed-image-head"><span class="bk-ed-kind">Picture</span>'
            + '<button type="button" class="btn btn-ghost btn-sm" data-bk-imgclear'
            + (section.imageUrl ? '' : ' hidden') + '>Remove picture</button></div>'
            + '<div class="bk-ed-image-now" data-bk-imgnow>' + bkChosenImageHtml(section.imageUrl, section.imageCredit) + '</div>'
            + '<div class="bk-ed-image-ways">'
            + '<label class="bk-ed-f"><span>Link to a picture</span>'
            + '<input type="url" data-bk-imgurl placeholder="https://…" inputmode="url"></label>'
            + '<label class="bk-ed-upload">'
            + '<input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/avif" data-bk-imgfile hidden>'
            + '<span class="btn btn-ghost btn-sm">Upload a picture</span>'
            + '</label>'
            + '</div>'
            + '<p class="bk-ed-upload-note">A linked picture stays on the other site and can disappear if they '
            + 'remove it. An uploaded one is stored by us and counts towards your image storage &ndash; '
            + 'PNG or JPEG, up to 10&nbsp;MB.</p>'
            + '<p class="bk-ed-image-msg" data-bk-imgmsg hidden></p>'
            + '</div>';
    }

    /** The currently chosen picture, or the reason there isn't one. */
    function bkChosenImageHtml(url, credit) {
        if (!url) return '<p class="bk-ed-image-none">No picture on this section.</p>';
        return '<figure class="bk-ed-image-chosen"><img src="' + escAttr(url) + '" alt="">'
            + '<figcaption>' + esc(credit || '') + '</figcaption></figure>';
    }

    function bkEditFormHtml(section) {
        const workbook = bkState.view === 'workbook';
        const items = workbook
            ? (section.exercises || []).map(bkExerciseItemHtml).join('')
            : (section.blocks || []).map(bkBlockItemHtml).join('');
        const kinds = workbook
            ? BkSchema.EXERCISE_TIERS.map((t) => '<option value="' + escAttr(t.id) + '">' + esc(t.label) + '</option>').join('')
            : BkSchema.BLOCK_TYPES.map((b) => '<option value="' + escAttr(b.id) + '">' + esc(b.label) + '</option>').join('');
        return '<div class="bk-ed">'
            + '<div class="bk-ed-items">' + items + '</div>'
            + (workbook ? '' : bkImagePanelHtml(section))
            + '<div class="bk-ed-add"><select class="bk-ed-add-kind" aria-label="What to add">' + kinds + '</select>'
            + '<button type="button" class="btn btn-ghost btn-sm" data-bk-add>'
            + (workbook ? 'Add a question' : 'Add a block') + '</button></div>'
            + '<div class="bk-ed-foot">'
            + '<button type="button" class="btn btn-primary btn-sm" data-bk-save>Save changes</button>'
            + '<button type="button" class="btn btn-ghost btn-sm" data-bk-cancel>Cancel</button>'
            + '</div></div>';
    }

    // Only the fields belonging to THIS item: a parted exercise has
    // [data-field] inside its parts too, and those belong to the part.
    function bkOwnFields(itemEl) {
        return Array.from(itemEl.querySelectorAll('[data-field]'))
            .filter((el) => el.closest('.bk-ed-item') === itemEl && !el.closest('.bk-ed-part'));
    }

    function bkReadBlock(itemEl) {
        const out = { type: itemEl.dataset.type };
        bkOwnFields(itemEl).forEach((el) => {
            out[el.dataset.field] = bkFieldFromText(el.dataset.kind, el.value);
        });
        return out;
    }

    function bkReadExercise(itemEl) {
        const out = {};
        bkOwnFields(itemEl).forEach((el) => { out[el.dataset.field] = el.value.trim(); });
        const marks = Number(out.marks);
        if (isFinite(marks) && marks > 0) out.marks = marks; else delete out.marks;

        const parts = Array.from(itemEl.querySelectorAll('.bk-ed-part')).map((p) => {
            const part = {};
            p.querySelectorAll('[data-field]').forEach((el) => { part[el.dataset.field] = el.value.trim(); });
            const pm = Number(part.marks);
            if (isFinite(pm) && pm > 0) part.marks = pm; else delete part.marks;
            return part;
        });
        // Mirrors validateExercise: parts win, and a parted question carries no
        // answer of its own.
        if (parts.length) { out.parts = parts; delete out.answer; }
        return out;
    }

    /**
     * Which items the validator would throw away, before it does.
     *
     * The server runs these same validators on the way in and silently drops a
     * block that is missing a required field - which, to a teacher who has just
     * emptied a box, looks like the editor eating their work. Running the
     * identical check here turns that into a message naming the block.
     */
    function bkDropped(items, workbook) {
        const out = [];
        items.forEach((item, i) => {
            const kept = workbook ? BkSchema.validateExercises([item]) : BkSchema.validateBlocks([item]);
            if (!kept.length) out.push(i + 1);
        });
        return out;
    }

    function bkEnterEdit(el, section) {
        bkState.editing = section.id;
        el.classList.add('is-editing');
        el.querySelector('.bk-r-rendered')?.remove();
        el.insertAdjacentHTML('beforeend', bkEditFormHtml(section));
        bkAutosizeAll(el);
    }

    // Editing the workbook can give a booklet its first question or take its
    // last one away, and "Set as lesson" only means anything with questions.
    function bkSyncSetLessonBtn() {
        const btn = $('btn-bk-set-lesson');
        if (!btn) return;
        const hasWorkbook = bkState.sections.some((s) => (s.exercises || []).length > 0);
        btn.hidden = !(bkState.meta?.status === 'done' && hasWorkbook);
    }

    // Sharing a booklet is offered from its own page as well as the library
    // list: by the time you have read one through and decided it is good, the
    // list you shared it from is two clicks behind you.
    function bkSyncShareBtn() {
        const btn = $('btn-bk-share');
        if (!btn) return;
        btn.hidden = !(state.user?.org && bkState.meta?.status === 'done');
        const link = $('btn-bk-send-link');
        if (link) link.hidden = bkState.meta?.status !== 'done';
        // Same moment, same two conditions: the booklet now has an id and we
        // know whether there is a school to set it to.
        syncReadingSetWork();
    }

    $('btn-bk-share')?.addEventListener('click', () => {
        if (bkState.id) shareToOrgLibrary('booklet', bkState.id);
    });
    $('btn-bk-send-link')?.addEventListener('click', () => {
        if (bkState.meta?.status === 'done') sendAsLink('booklet', bkState.id, bkState.meta.title);
    });

    /**
     * The picture a teacher has chosen in this open editor, if any.
     *
     * Held on the panel element rather than in bkState, because it is only
     * meaningful while the form is open: cancelling an edit has to discard the
     * choice, and anything kept in state would survive it.
     *
     * Returns undefined where the section has no picture panel at all, which
     * is what the PUT needs to leave image_url alone.
     */
    function bkPickedImage(card) {
        const panel = card.querySelector('[data-bk-image]');
        if (!panel) return undefined;
        if (!('picked' in panel.dataset)) return undefined;   // untouched
        return panel.dataset.picked || null;                  // '' means removed
    }

    function bkSetPickedImage(panel, url, credit) {
        panel.dataset.picked = url || '';
        const now = panel.querySelector('[data-bk-imgnow]');
        if (now) now.innerHTML = bkChosenImageHtml(url, credit);
        const clear = panel.querySelector('[data-bk-imgclear]');
        if (clear) clear.hidden = !url;
    }

    function bkImageMsg(panel, text, bad) {
        const el = panel.querySelector('[data-bk-imgmsg]');
        if (!el) return;
        el.textContent = text || '';
        el.hidden = !text;
        el.classList.toggle('is-bad', !!bad);
    }

    /**
     * A pasted link, applied when the teacher leaves the box.
     *
     * Nothing is fetched here: the address is shown as the chosen picture and
     * travels with Save, where the server decides whether it is one we are
     * willing to print. Validating it in the browser would only duplicate a
     * check that has to happen on the server anyway, and would disagree with
     * it the first time either list changed.
     */
    function bkApplyImageLink(panel, raw) {
        const url = (raw || '').trim();
        if (!url) return;
        if (!/^https:\/\//i.test(url)) {
            bkImageMsg(panel, 'A picture link has to start with https://', true);
            return;
        }
        bkImageMsg(panel, '');
        let credit = '';
        try { credit = new URL(url).hostname.replace(/^www\./, ''); } catch { credit = ''; }
        bkSetPickedImage(panel, url, credit);
    }

    /**
     * A file, uploaded and attached straight away.
     *
     * Deliberately not deferred to Save like the link is. The bytes are on the
     * CDN and counting against the teacher's storage the moment they land, so
     * a file attached to a section that is then cancelled would be storage
     * being paid for and impossible to find. Saving it immediately means every
     * uploaded file is attached to something a teacher can see and remove.
     */
    async function bkUploadImage(panel, card, section, file) {
        if (!file) return;
        bkImageMsg(panel, 'Uploading…');
        const body = new FormData();
        body.append('image', file);
        try {
            const res = await apiFetch(
                '/api/booklets/' + bkState.id + '/sections/' + section.id + '/image',
                { method: 'POST', body }
            );
            const out = await res.json();
            if (!res.ok) { bkImageMsg(panel, out.error || 'Could not upload that picture.', true); return; }
            bkImageMsg(panel, 'Uploaded.');
            // The section now differs from what the open form was built from,
            // so state is updated in place; the form itself is left alone so
            // an edit in progress survives.
            const i = bkState.sections.findIndex((x) => x.position === section.position);
            if (i >= 0) { bkState.sections[i].imageUrl = out.imageUrl; bkState.sections[i].imageCredit = out.imageCredit; }
            panel.dataset.current = out.imageUrl || '';
            delete panel.dataset.picked;     // saved already; nothing for Save to send
            const now = panel.querySelector('[data-bk-imgnow]');
            if (now) now.innerHTML = bkChosenImageHtml(out.imageUrl, out.imageCredit);
            const clear = panel.querySelector('[data-bk-imgclear]');
            if (clear) clear.hidden = !out.imageUrl;
        } catch {
            bkImageMsg(panel, 'Could not upload that picture.', true);
        }
    }

    async function bkSaveSection(card, section) {
        const items = Array.from(card.querySelectorAll('.bk-ed-items > .bk-ed-item'));
        const workbook = bkState.view === 'workbook';
        const parsed = items.map(workbook ? bkReadExercise : bkReadBlock);

        const bad = bkDropped(parsed, workbook);
        if (bad.length) {
            setError(bkReaderError, (workbook ? 'Question ' : 'Block ') + bad.join(', ')
                + (bad.length > 1 ? ' are' : ' is')
                + ' missing something. Fill the empty boxes in, or remove them.');
            return;
        }
        if (!workbook && !parsed.length) {
            setError(bkReaderError, 'A section needs at least one block. Add one, or rewrite the section instead.');
            return;
        }
        // renderDiagram prints a figure only where a diagram block asks for
        // one, so a picture on a section with no diagram block is stored and
        // never seen. Saying so beats saving it into a hole.
        const picked = workbook ? undefined : bkPickedImage(card);
        if (picked && !parsed.some((b) => b && b.type === 'diagram')) {
            setError(bkReaderError, 'This section has a picture but no Diagram block to put it in. '
                + 'Add a Diagram block, or remove the picture.');
            return;
        }

        const saveBtn = card.querySelector('[data-bk-save]');
        if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving…'; }
        try {
            const payload = workbook ? { exercises: parsed } : { blocks: parsed };
            // Only sent when the teacher actually touched the picture panel, so
            // saving a text edit never disturbs the picture the image pass
            // chose.
            if (picked !== undefined) payload.imageUrl = picked;
            const res = await apiFetch('/api/booklets/' + bkState.id + '/sections/' + section.id, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            const body = await res.json();
            if (!res.ok) { setError(bkReaderError, body.error || 'Could not save those changes.'); return; }
            setError(bkReaderError, '');
            bkState.editing = null;
            upsertBookletSection(body);
            bkSyncSetLessonBtn();
        } catch {
            setError(bkReaderError, 'Could not save those changes.');
        } finally {
            if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save changes'; }
        }
    }

    bkReaderBody?.addEventListener('input', (e) => {
        if (e.target.matches('.bk-ed textarea')) bkAutosize(e.target);
    });

    // Enter in the link box applies it rather than submitting the editor, which
    // is what a lone <input> in a form-less panel would otherwise invite.
    bkReaderBody?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !e.target.matches('[data-bk-imgurl]')) return;
        e.preventDefault();
        const panel = e.target.closest('[data-bk-image]');
        if (panel) bkApplyImageLink(panel, e.target.value);
    });

    // ...and applied on the way out too, so a pasted link that is never
    // followed by Enter is not silently dropped when Save is clicked.
    bkReaderBody?.addEventListener('focusout', (e) => {
        if (!e.target.matches('[data-bk-imgurl]')) return;
        const panel = e.target.closest('[data-bk-image]');
        if (panel) bkApplyImageLink(panel, e.target.value);
    });

    // A file picker fires change, not click.
    bkReaderBody?.addEventListener('change', (e) => {
        if (e.target.closest('[data-bk-curve-preset], [data-bk-curve-opt]')) { bkCurveChanged(e.target); return; }
        if (!e.target.matches('[data-bk-imgfile]')) return;
        const panel = e.target.closest('[data-bk-image]');
        const card = e.target.closest('.bk-r-section');
        const section = bkState.sections.find((x) => x.position === Number(card?.dataset.position));
        if (panel && section) bkUploadImage(panel, card, section, e.target.files && e.target.files[0]);
        e.target.value = '';   // so the same file can be chosen twice
    });

    bkReaderBody?.addEventListener('click', async (e) => {
        const item = e.target.closest('.bk-ed-item');
        const card = e.target.closest('.bk-r-section');
        const position = card ? Number(card.dataset.position) : null;
        const section = bkState.sections.find((s) => s.position === position);

        // ── the picture panel ──
        const imgPanel = e.target.closest('[data-bk-image]');
        if (imgPanel && e.target.closest('[data-bk-imgclear]')) {
            bkSetPickedImage(imgPanel, '', '');
            const link = imgPanel.querySelector('[data-bk-imgurl]');
            if (link) link.value = '';
            bkImageMsg(imgPanel, '');
            return;
        }

        // ── inside an open form ──
        if (e.target.closest('[data-bk-remove]')) { item?.remove(); return; }
        if (e.target.closest('[data-bk-removepart]')) {
            const part = e.target.closest('.bk-ed-part');
            const owner = part?.closest('.bk-ed-item');
            part?.remove();
            if (owner) {
                owner.dataset.parted = owner.querySelector('.bk-ed-part') ? '1' : '0';
                bkAutosizeAll(owner);
            }
            return;
        }
        if (e.target.closest('[data-bk-addpart]')) {
            item?.querySelector('.bk-ed-parts')?.insertAdjacentHTML('beforeend', bkPartHtml({}));
            if (item) { item.dataset.parted = '1'; bkAutosizeAll(item); }
            return;
        }
        const move = e.target.closest('[data-bk-move]');
        if (move && item) {
            const up = move.dataset.bkMove === 'up';
            const sib = up ? item.previousElementSibling : item.nextElementSibling;
            if (sib) item.parentElement.insertBefore(up ? item : sib, up ? sib : item);
            return;
        }
        if (e.target.closest('[data-bk-add]')) {
            const wrap = card?.querySelector('.bk-ed-items');
            const kind = card?.querySelector('.bk-ed-add-kind')?.value;
            if (!wrap || !kind) return;
            wrap.insertAdjacentHTML('beforeend', bkState.view === 'workbook'
                ? bkExerciseItemHtml({ tier: kind })
                : bkBlockItemHtml({ type: kind }));
            bkAutosizeAll(wrap.lastElementChild);
            wrap.lastElementChild?.querySelector('textarea, input')?.focus();
            return;
        }
        if (e.target.closest('[data-bk-cancel]')) {
            bkState.editing = null;
            if (section) upsertBookletSection(section);
            return;
        }
        if (e.target.closest('[data-bk-save]')) {
            if (section && card) await bkSaveSection(card, section);
            return;
        }

        // ── the card's own tools ──
        if (e.target.closest('.bk-r-edit')) {
            if (!section?.id) { setError(bkReaderError, 'Reopen this booklet before editing a section.'); return; }
            if (bkState.editing && bkState.editing !== section.id) {
                const open = bkState.sections.find((s) => s.id === bkState.editing);
                if (open) upsertBookletSection(open);   // one form open at a time
            }
            if (card) bkEnterEdit(card, section);
            return;
        }

        const btn = e.target.closest('.bk-r-regen');
        if (!btn) return;
        const id = section?.id || card?.dataset.sectionId;
        if (!id) { setError(bkReaderError, 'Reopen this booklet before rewriting a section.'); return; }
        btn.disabled = true;
        btn.textContent = 'Rewriting…';
        try {
            const res = await apiFetch('/api/booklets/' + bkState.id + '/sections/' + id + '/regenerate', { method: 'POST' });
            const body = await res.json();
            if (!res.ok) { setError(bkReaderError, body.error || 'Could not rewrite that section.'); return; }
            upsertBookletSection(Object.assign({}, section, body, { position, heading: section?.heading || '' }));
        } catch {
            setError(bkReaderError, 'Could not rewrite that section.');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Rewrite this section';
        }
    });

    // ── Export ────────────────────────────────────────────

    // Which document the menu's toggle picks: 'booklet' (the guide),
    // 'workbook' (the student copy) or 'answers' (the workbook with its key).
    const bkExportWhat = () =>
        document.querySelector('#bk-reader input[name="bk-export-what"]:checked')?.value || 'booklet';

    document.querySelector('#bk-reader .export-menu')?.addEventListener('click', (e) => {
        const item = e.target.closest('[data-bk-export]');
        if (!item || !bkState.id) return;
        const what = bkExportWhat();
        const base = '/api/booklets/' + bkState.id + '/';
        // Word has none of the PDF's print options, only the answers choice;
        // see utils/bookletDocx.js.
        if (item.dataset.bkExport === 'docx') {
            triggerDownload(base + (what === 'booklet' ? 'docx' : 'workbook.docx')
                + (what === 'workbook' ? '?answers=0' : ''));
            return;
        }
        const query = [];
        const a11y = $('bk-a11y-select')?.value || '';
        const tint = $('bk-tint-select')?.value || '';
        if (a11y) query.push('a11y=' + encodeURIComponent(a11y));
        if (tint) query.push('tint=' + encodeURIComponent(tint));
        if (what === 'workbook') query.push('answers=0');
        const path = what === 'booklet' ? 'pdf' : 'workbook.pdf';
        // Print opens the same PDF in a tab, where the browser's own viewer
        // prints it as laid out; there is no HTML print page to fall back on.
        if (item.dataset.bkExport === 'print') {
            query.push('inline=1');
            window.open(base + path + '?' + query.join('&'), '_blank', 'noopener');
            return;
        }
        triggerDownload(base + path + (query.length ? '?' + query.join('&') : ''));
    });

    $('btn-bk-back')?.addEventListener('click', () => showPresList());

    $('btn-bk-delete')?.addEventListener('click', async () => {
        if (!bkState.id) return;
        const title = bkState.meta?.title || 'This booklet';
        // Deleting mid-generation is allowed – it is the natural thing to do
        // with a booklet that is coming out wrong – but it has to say that is
        // what it will do, because the sections still arriving look like work
        // in hand rather than work about to be thrown away.
        const message = (bkIsWorkbook()
            ? `“${title}” will be permanently deleted. This can’t be undone.`
            : `“${title}” and its workbook will be permanently deleted. This can’t be undone.`)
            + (bkState.streaming ? ' Writing will stop.' : '');
        const ok = await showConfirm({
            title: bkIsWorkbook() ? 'Delete workbook?' : 'Delete booklet?',
            message,
            confirmLabel: 'Delete',
            danger: true,
        });
        if (!ok) return;
        const id = bkState.id;
        try {
            const r = await apiFetch('/api/booklets/' + id, { method: 'DELETE' });
            if (!r.ok) { setError(bkReaderError, 'Delete failed'); return; }
            // Drop it from the list's cache too, so going back does not show a
            // booklet that no longer exists until the next reload.
            bookletData = bookletData.filter((x) => x.id !== id);
            renderBooklets();
            showPresList();
        } catch { setError(bkReaderError, 'Network error'); }
    });

    // ── Entry points ──────────────────────────────────────

    // Booklets are Pro-only for now (free.limits.booklet = 0 in utils/stripe.js).
    // Checked here rather than at the end of the composer: a teacher who fills
    // in three steps and is then told they cannot have it has been wasted,
    // and the server refuses the create anyway.
    function bookletsAvailable() {
        return state.user?.plan === 'pro';
    }

    function showBookletPage() {
        if (!bookletsAvailable()) { showBilling(); return; }
        // Every new one starts with both documents ticked, as a booklet always
        // came.
        setBookletParts(true, true);
        clearLanding();
        hide(authView, presListView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, markingView, orgView, joinView, studentProfileView,
             studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(bookletView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        show(bkPageForm);
        hide(bkPlanView, bkReader);
        showBookletGenerating(false);
        setError(bkError, '');
        bkState.id = null;
        bkState.plan = null;
        bkState.plannedSections = 0;
        bkState.sections = [];
        if (bkTitleInput) bkTitleInput.value = '';
        if (bkDescriptionInput) bkDescriptionInput.value = '';
        const bkSourceInput = $('bk-source-input');
        if (bkSourceInput) bkSourceInput.value = '';
        clearSourceFiles('bk-source-input');
        // The title and description were cleared here but the curriculum was
        // not, so a new booklet opened holding the last one's board and level.
        resetCurriculumFields('bk-exam-board', 'bk-level', bkSubjectCombobox);
        renderBookletLengths();
        updateBookletCoverage();
        bkWizard?.setStep(1, { focus: false });
        if (window.location.pathname !== '/booklets') history.pushState({ isNewBooklet: true }, '', '/booklets');
    }

    async function openBooklet(id) {
        try {
            const res = await apiFetch('/api/booklets/' + id);
            if (!res.ok) { showPresList(); return; }
            const b = await res.json();

            bkState.id = b.id;
            bkState.plan = b.plan;
            bkState.length = b.length;
            bkState.sections = b.sections.filter((s) => s.status === 'done');
            bkState.plannedSections = b.plan?.sections?.length || 0;
            bkState.meta = { title: b.title, subject: b.subject, examBoard: b.examBoard, level: b.level, status: b.status };
            setBookletParts(b.includeGuide !== false, !!b.includeWorkbook);

            clearLanding();
            hide(authView, presListView, generatorView, worksheetView, quizView, quizResultsView,
                 billingView, markingView, orgView, joinView, studentProfileView,
                 studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
            show(bookletView, btnLogout, navCreateWrap, navStudentsBtn);
            if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
            show(navUserEmail);
            hide(bkPageForm);
            if (window.location.pathname !== '/booklets/' + id) {
                history.pushState({ bookletId: id }, '', '/booklets/' + id);
            }

            // A booklet still waiting for approval goes back to the plan
            // screen; anything further along opens in the reader, which
            // resumes the stream if there is still work outstanding.
            if (b.status === 'planned' && b.plan) {
                hide(bkReader);
                showBookletGenerating(false);
                show(bkPlanView);
                renderBookletPlan();
                return;
            }
            hide(bkPlanView);
            show(bkReader);
            // A booklet always opens on the guide, whichever document was last
            // being read in the one before it; a workbook has only the one.
            // applyBookletKind above has already set the view to match.
            bkState.editing = null;
            if (bkReaderBody) bkReaderBody.innerHTML = '';
            bkState.sections.forEach(upsertBookletSection);
            setError(bkReaderError, '');

            // Only a finished booklet with a workbook has questions to set.
            // The server refuses either way; this keeps the button from
            // offering something that cannot work.
            bkSyncSetLessonBtn();
            bkSyncShareBtn();

            const outstanding = (b.plan?.sections?.length || 0) - bkState.sections.length;
            if (b.status === 'approved' || (b.status === 'generating' && outstanding > 0)) {
                await writeBooklet();
            } else {
                showBookletGenerating(false);
                if (bkProgress) bkProgress.textContent = '';
            }
        } catch {
            showPresList();
        }
    }

    function showWorksheetPage(topic, returnTo, examBoard, subject, level, sourcePresId, context) {
        worksheetReturnTo    = returnTo || 'list';
        currentWorksheetData = null;
        currentWorksheetId   = null;
        currentWorksheetPair = null;
        syncReadingSetWork();
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, markingView, orgView, joinView, studentProfileView,
             studentsView, studentMarkingView, quizView, quizResultsView, billingView, lessonsView, classesView, tutoringView);
        show(worksheetView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        show(wsPageForm);
        hide(wsPagePreview);
        setError(worksheetError, '');
        if (worksheetTopicEl) worksheetTopicEl.value = topic || '';
        if (wsContext) wsContext.value = context || '';
        if (wsDescribeEl) { wsDescribeEl.value = ''; syncWsMode(); }
        setSourcePanel('ws-context', 'layout', false);
        clearSourceFiles('ws-context');
        setWsContextOpen(!!context);
        if (wsExamBoard) wsExamBoard.value = examBoard || '';
        wsSubjectCombobox.set?.(subject || '');
        if (wsLevel)     wsLevel.value     = level     || '';
        populateSourcePresPicker(wsSourcePres, sourcePresId);
        syncAllWsTiles();
        refreshIgcseHints();
        // The curriculum arrives set in code here – from the presentation this
        // sheet is being built from, say – and setting a value fires no change
        // event, so the picker has to be loaded by hand.
        refreshWsTopics();
        wsWizard?.setStep(1, { focus: false });
        setSourcePanel('ws-context', 'pres', !!sourcePresId);
        if (topic) worksheetTopicEl?.focus();
        if (window.location.pathname !== '/worksheets') history.pushState({ isNewWorksheet: true }, '', '/worksheets');
    }

    async function openWorksheet(id) {
        try {
            // The other sheet of a pair is usually fetched already (see
            // prefetchWsPair), which is what makes switching tabs quick.
            const ahead = wsPairCache.get(id);
            wsPairCache.delete(id);
            let ws = ahead && await ahead;
            if (!ws) {
                const res = await apiFetch(`/api/worksheets/${id}`);
                if (!res.ok) { showPresList(); return; }
                ws = await res.json();
            }

            currentWorksheetId   = ws.id;
            currentWorksheetPair = ws.pair || null;
            syncReadingSetWork();
            currentWorksheetData = ws.data_json;
            worksheetReturnTo    = 'list';

            if (worksheetTopicEl) worksheetTopicEl.value = ws.topic      || '';
            if (wsContext) wsContext.value = '';
            clearSourceFiles('ws-context');
            setWsContextOpen(false);
            if (wsExamBoard)      wsExamBoard.value      = ws.exam_board || '';
            wsSubjectCombobox.set?.(ws.subject || '');
            if (wsLevel)          wsLevel.value          = ws.level      || '';
            // A main sheet with a supported version comes back with the second
            // sheet's supports ticked, so regenerating remakes the pair.
            if (currentWorksheetPair?.role === 'supported') {
                restoreSupports(currentWorksheetPair.supports, currentWorksheetPair.language, 'separate');
            } else {
                restoreSupports(currentWorksheetData?.supports, currentWorksheetData?.language);
            }
            syncAllWsTiles();
            refreshIgcseHints();
        refreshIgcseHints();
            populateSourcePresPicker(wsSourcePres, ws.source_type === 'presentation' ? ws.source_id : '');

            hide(authView, presListView, bookletView, generatorView, markingView, orgView, joinView, studentProfileView,
                 studentsView, studentMarkingView, quizView, quizResultsView, billingView, lessonsView, classesView, tutoringView);
            show(worksheetView, btnLogout, navCreateWrap, navStudentsBtn);
            if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
            show(navUserEmail);
            setError(worksheetError, '');
            hide(wsPageForm);
            show(wsPagePreview);

            if (window.location.pathname !== `/worksheets/${id}`) {
                history.pushState({ worksheetId: id }, '', `/worksheets/${id}`);
            }
            wsResetEditorState();
            const drawn = renderWsEditor(currentWorksheetData);
            renderWsPair();
            renderPackSide(
                ws.source_type === 'presentation' ? ws.source_id : null,
                { type: 'worksheet', id: ws.id }
            );
            if (currentWorksheetPair) prefetchWsPair(currentWorksheetPair.id);
            // Resolves once the sheet is on screen, for the tab switch to fade it in.
            return drawn;
        } catch {}
    }

    // Enter moves the composer on rather than firing generation from the first
    // field – the two steps after this one exist to be seen at least once.
    worksheetTopicEl?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (wsWizard && wsWizard.step < wsWizard.count) wsWizard.next();
        else btnGenerateWs?.click();
    });

    // ── Additional support picker ─────────────────────────
    //
    // Collapsed by default with a summary line, so the state is legible when
    // it is shut. Opens automatically if anything is already ticked, which
    // happens when a teacher comes back to regenerate.
    const wsSupportsToggle  = $('ws-supports-toggle');
    const wsSupportChecks   = $('ws-support-checks');
    const wsSupportsSummary = $('ws-supports-summary');

    // Filled from the shared table rather than hand-written into index.html:
    // a language added to public/js/home-languages.js has to appear in the
    // picker, in the prompt and in the renderer at once, or the three
    // disagree. Both pickers are filled by the same call for that reason.
    const wsLanguageEl = $('ws-language');
    function fillLanguagePicker(select) {
        if (!select || !window.HomeLanguages) return;
        window.HomeLanguages.LANGUAGES.forEach(lang => {
            const opt = document.createElement('option');
            opt.value = lang.code;
            // Both names: the teacher is picking from the English one, and the
            // parent or pupil they are picking for reads the other.
            opt.textContent = `${lang.name} (${lang.endonym})`;
            select.appendChild(opt);
        });
    }
    fillLanguagePicker(wsLanguageEl);
    fillLanguagePicker($('quiz-language'));
    fillLanguagePicker($('bk-home-language'));

    function selectedSupports() {
        return Array.from(
            document.querySelectorAll('#ws-support-checks .ws-support-check:not(.ws-scaffold-check) input[type="checkbox"]:checked')
        ).map(el => el.value);
    }

    /** The scaffolds, which only a supported sheet takes (utils/worksheetAdapt.js). */
    function selectedScaffolds() {
        return Array.from(
            document.querySelectorAll('#ws-scaffolds input[type="checkbox"]:checked')
        ).map(el => el.value);
    }

    /** 'separate' when the supports go on a second sheet, else 'same'. */
    function supportWhere() {
        return document.querySelector('input[name="ws-support-where"]:checked')?.value === 'separate' ? 'separate' : 'same';
    }

    function syncSupportWhere() {
        const separate = supportWhere() === 'separate';
        const scaffolds = $('ws-scaffolds');
        if (scaffolds) scaffolds.hidden = !separate;
        const same = $('ws-support-intro-same');
        if (same) same.hidden = separate;
        const sep = $('ws-support-intro-separate');
        if (sep) sep.hidden = !separate;
        syncSupportsSummary();
        renderWsPresets();
    }

    function syncSupportsSummary() {
        if (!wsSupportsSummary) return;
        const labels = Array.from(
            document.querySelectorAll('#ws-support-checks input[type="checkbox"]:checked')
        ).map(el => el.closest('.ws-support-check')?.querySelector('b')?.textContent || el.value);
        // The home language leads the summary when there is one: it is the
        // most visible thing about the sheet that comes out of this panel.
        const lang = window.HomeLanguages?.getLanguage(wsLanguageEl?.value);
        if (lang) labels.unshift(lang.name);
        // Scaffolds are only ticked-and-hidden when the panel is set to "on
        // this sheet", where they do nothing, so they are not counted then.
        const separate = supportWhere() === 'separate';
        if (!separate) {
            const scaffoldNames = new Set(Array.from(document.querySelectorAll('#ws-scaffolds input:checked'))
                .map(el => el.closest('.ws-support-check')?.querySelector('b')?.textContent));
            for (let i = labels.length - 1; i >= 0; i--) if (scaffoldNames.has(labels[i])) labels.splice(i, 1);
        }
        if (separate && labels.length) {
            wsSupportsSummary.textContent = `Second sheet: ${labels.length === 1 ? labels[0] : `${labels[0]} + ${labels.length - 1} more`}`;
            return;
        }
        // Names one, counts the rest: "Chunked instructions" is more use than
        // "1 selected", and four names is longer than the button.
        wsSupportsSummary.textContent = !labels.length
            ? 'None'
            : labels.length === 1
                ? labels[0]
                : `${labels[0]} + ${labels.length - 1} more`;
    }

    /**
     * Put the picker back to what an existing worksheet was built with.
     *
     * Matters on the "Edit topic → regenerate" path: without it, reworking a
     * differentiated sheet quietly produces an undifferentiated one, and the
     * only sign is the summary line reading "None".
     */
    function restoreSupports(supports, language, where = 'same') {
        const list = Array.isArray(supports) ? supports : [];
        document.querySelectorAll('#ws-support-checks input[type="checkbox"]')
            .forEach(el => { el.checked = list.includes(el.value); });
        if (wsLanguageEl) wsLanguageEl.value = language || '';
        const whereEl = document.querySelector(`input[name="ws-support-where"][value="${where}"]`);
        if (whereEl) whereEl.checked = true;
        syncSupportWhere();
        // Opened when something is on, so the detail is visible rather than
        // folded away behind a summary the teacher has to think to expand.
        if (wsSupportChecks && wsSupportsToggle) {
            const anything = list.length > 0 || !!language;
            wsSupportChecks.hidden = !anything;
            wsSupportsToggle.setAttribute('aria-expanded', String(anything));
        }
    }

    if (wsSupportsToggle && wsSupportChecks) {
        wsSupportsToggle.addEventListener('click', () => {
            const open = wsSupportChecks.hidden;
            wsSupportChecks.hidden = !open;
            wsSupportsToggle.setAttribute('aria-expanded', String(open));
        });
        wsSupportChecks.addEventListener('change', syncSupportWhere);
        syncSupportWhere();
    }

    // ── Support presets ───────────────────────────────────
    //
    // A named set of everything in the panel above, plus the print layout and
    // paper, saved on the account (prefs.senPresets, utils/userPrefs.js). Not
    // attached to a student: a preset can be one pupil or a whole group, and
    // the teacher names it whichever way they think about it.
    //
    // Everything here looks elements up by id rather than through the consts
    // above, because refreshA11yPickers calls renderWsPresets at boot, before
    // this part of the file has run.

    function sameList(a, b) {
        return a.length === b.length && a.every(k => b.includes(k));
    }

    /** What the panel says now, in the shape a preset is stored in. */
    function currentSupportPick() {
        return {
            supports: selectedSupports(),
            // Only counted on a second sheet, which is the only place they do anything.
            scaffolds: supportWhere() === 'separate' ? selectedScaffolds() : [],
            where: supportWhere(),
            language: $('ws-language')?.value || '',
        };
    }

    /** The saved preset the panel currently matches, if any. */
    function matchingPreset() {
        const now = currentSupportPick();
        return (state.prefs?.senPresets || []).find(p =>
            p.where === now.where && p.language === now.language
            && sameList(p.supports, now.supports)
            && sameList(p.where === 'separate' ? p.scaffolds : [], now.scaffolds)) || null;
    }

    function renderWsPresets() {
        const wrap = $('ws-presets');
        if (!wrap) return;
        const presets = state.prefs?.senPresets || [];
        wrap.hidden = !presets.length;
        const on = matchingPreset();
        wrap.innerHTML = '<span class="ws-presets-label">Presets</span>' + presets.map(p => `
            <span class="ws-preset${on?.id === p.id ? ' is-on' : ''}">
                <button type="button" class="ws-preset-apply" data-preset="${escAttr(p.id)}" aria-pressed="${on?.id === p.id}"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 6.3l2.3 2.4 4.7-5.2"/></svg>${esc(p.name)}</button>
                <button type="button" class="ws-preset-del" data-preset-del="${escAttr(p.id)}" aria-label="Delete preset ${escAttr(p.name)}" title="Delete preset"><svg viewBox="0 0 8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M1 1l6 6M7 1L1 7"/></svg></button>
            </span>`).join('');
    }

    function applyWsPreset(preset) {
        restoreSupports([...preset.supports, ...preset.scaffolds], preset.language, preset.where);
        renderWsPresets();
    }

    // The print side of a preset goes onto the editor's export pickers when a
    // sheet is generated with it, so the sheet opens already in that layout.
    // Only then: ticking a preset and generating nothing should not change how
    // the next unrelated sheet prints.
    //
    // On a "second sheet" preset the class sheet prints as normal and the
    // layout belongs to the supported one, so it is held here until the
    // editor switches to that sheet (see the wsPairSeg handler).
    let wsPresetPrint = null;

    function setWsPrintPickers(layout, tint) {
        if (wsA11ySelect) wsA11ySelect.value = layout || '';
        if (wsTintSelect) wsTintSelect.value = tint || '';
        syncWsPaperSwatches();
    }

    function applyWsPresetPrint() {
        wsPresetPrint = null;
        const preset = matchingPreset();
        if (!preset) return;
        if (preset.where === 'separate') wsPresetPrint = { layout: preset.layout, tint: preset.tint, coreId: null };
        else setWsPrintPickers(preset.layout, preset.tint);
    }

    /**
     * Save a new presets list, keeping the editor's export pickers as they
     * were: savePrefs resets every picker to the account default, which would
     * undo a preset layout on the sheet that is open.
     */
    async function saveWsPresets(list) {
        const keep = [wsA11ySelect?.value, wsTintSelect?.value];
        try {
            await savePrefs({ senPresets: list });
        } finally {
            if (wsA11ySelect) wsA11ySelect.value = keep[0] || '';
            if (wsTintSelect) wsTintSelect.value = keep[1] || '';
            syncWsPaperSwatches();
            renderWsPresets();
        }
    }

    $('ws-presets')?.addEventListener('click', async (e) => {
        const presets = state.prefs?.senPresets || [];
        const apply = e.target.closest('[data-preset]');
        if (apply) {
            const hit = presets.find(p => p.id === apply.dataset.preset);
            if (hit) applyWsPreset(hit);
            return;
        }
        const del = e.target.closest('[data-preset-del]');
        if (!del) return;
        const hit = presets.find(p => p.id === del.dataset.presetDel);
        if (!hit || !confirm(`Delete the preset "${hit.name}"? Sheets already made with it are not affected.`)) return;
        try {
            await saveWsPresets(presets.filter(p => p.id !== hit.id));
            showToast('Preset deleted');
        } catch {
            showToast('Could not delete that preset. Try again.', 4000);
        }
    });

    (function bindPresetForm() {
        const btn = $('ws-preset-save-btn');
        const form = $('ws-preset-form');
        const nameEl = $('ws-preset-name');
        const layoutEl = $('ws-preset-layout');
        const tintEl = $('ws-preset-tint');
        const msg = $('ws-preset-msg');
        if (!btn || !form || !nameEl) return;
        if (window.A11yProfiles) {
            fillA11ySelect(layoutEl, A11yProfiles.TEXT_LAYOUTS);
            fillA11ySelect(tintEl, A11yProfiles.TINTS);
        }
        const syncSwatches = bindPaperSwatches($('ws-preset-swatches'), tintEl, 'ws-preset-paper');
        const say = (text) => { if (msg) { msg.textContent = text; msg.hidden = !text; } };
        const close = () => { form.hidden = true; btn.hidden = false; say(''); };

        btn.addEventListener('click', () => {
            const pick = currentSupportPick();
            if (!pick.supports.length && !pick.scaffolds.length && !pick.language) {
                showToast('Tick some support first, then save it as a preset.', 3500);
                return;
            }
            // Starts from what the sheet would print in now, which is the
            // account default unless a preset already changed it.
            if (layoutEl) layoutEl.value = wsA11ySelect?.value || '';
            if (tintEl) tintEl.value = wsTintSelect?.value || '';
            syncSwatches();
            nameEl.value = '';
            btn.hidden = true;
            form.hidden = false;
            nameEl.focus();
        });
        $('ws-preset-cancel')?.addEventListener('click', close);

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const name = nameEl.value.replace(/\s+/g, ' ').trim();
            if (!name) { say('Give the preset a name.'); nameEl.focus(); return; }
            const presets = state.prefs?.senPresets || [];
            const existing = presets.find(p => p.name.toLowerCase() === name.toLowerCase());
            if (!existing && presets.length >= 20) { say('You have 20 presets, which is the most there can be. Delete one first.'); return; }
            if (existing && !confirm(`Replace the preset "${existing.name}"?`)) return;
            const preset = {
                id: existing?.id || (Date.now().toString(36) + Math.random().toString(36).slice(2, 6)),
                name,
                ...currentSupportPick(),
                layout: layoutEl?.value || null,
                tint: tintEl?.value || null,
            };
            const list = existing ? presets.map(p => p.id === existing.id ? preset : p) : [...presets, preset];
            form.querySelectorAll('button, input, select').forEach(el => { el.disabled = true; });
            try {
                await saveWsPresets(list);
                close();
                showToast(`Saved "${name}"`);
            } catch {
                say('Could not save that preset. Try again.');
            } finally {
                form.querySelectorAll('button, input, select').forEach(el => { el.disabled = false; });
            }
        });
    })();

    btnGenerateWs?.addEventListener('click', async () => {
        const topic = topicLineFor(worksheetTopicEl, wsTopicPicker);
        if (!topic) { setError(worksheetError, 'Please enter a topic or tick one from the list'); return; }

        const selectedTypes = WSQ_TYPES.filter((t) => wsRecipe[t] > 0);
        // Not sent on an exam-style sheet, whose shape is the board's.
        const description = wsExamStyleModeOn ? '' : wsDescription();
        if (!wsExamStyleModeOn && !description && !selectedTypes.length) {
            // The types live on step 3, so send them there to fix it.
            wsWizard?.last();
            setError(worksheetError, 'Add at least one question');
            return;
        }

        // On "a second, supported sheet" the main sheet is written without
        // the supports, and they go into the second one made from it.
        const separate = supportWhere() === 'separate';
        const secondSheet = separate ? [...selectedSupports(), ...selectedScaffolds()] : [];
        const language = wsLanguageEl?.value || '';
        if (separate && !secondSheet.length && !language) {
            setError(worksheetError, 'Tick the support the second sheet should have');
            return;
        }

        btnGenerateWs.disabled = true;
        btnGenerateWs.textContent = 'Generating…';
        setError(worksheetError, '');

        try {
            const res = await apiFetch('/api/worksheets/generate', {
                method: 'POST',
                body: JSON.stringify({
                    topic,
                    sourcePresentationId: wsSourcePres?.value || undefined,
                    context:    sourceTextFor('ws-context'),
                    types:      selectedTypes,
                    examBoard:  wsExamBoard?.value || '',
                    subject:    wsSubject?.value.trim() || '',
                    level:      wsLevel?.value || '',
                    tier:       $('ws-tier')?.value || '',
                    ...topicPartsFor(worksheetTopicEl, wsTopicPicker),
                    difficulty: document.getElementById('ws-difficulty')?.value || 'standard',
                    questionCount: wsExamStyleModeOn ? wsExamCount : wsRecipeTotal(),
                    typeCounts: wsExamStyleModeOn ? undefined : { ...wsRecipe },
                    supports:   separate ? [] : selectedSupports(),
                    language:   separate ? '' : language,
                    examStyleMode: wsExamStyleModeOn,
                    description: description || undefined,
                }),
            });
            const data = await res.json();
            if (!res.ok) { setError(worksheetError, data.error || 'Generation failed'); return; }

            // The server builds exactly the number asked for, retrying what
            // fails; this only fires when retries ran out, so say it plainly.
            if (data.supportsFailed) {
                showToast('The questions are ready, but the additional support could not be added. Use "Make supported version" to try again.', 6000);
            }
            delete data.supportsFailed;
            const madeCount = wsGeneratedCount(data);
            if (data.requestedCount && madeCount < data.requestedCount) {
                showToast(`We could only make ${madeCount} of the ${data.requestedCount} questions you asked for. Generate again, or add the rest in the editor.`, 6000);
            }
            delete data.requestedCount;
            currentWorksheetData = normalizeWorksheetData(data);
            // Keep currentWorksheetId if already saved (back → regenerate updates existing).
            // Not when the sheet open is a supported version: what is
            // generated here is a main sheet, so it is saved as a new one.
            if (currentWorksheetPair?.role === 'core') {
                currentWorksheetId = null;
                currentWorksheetPair = null;
            }
            hide(wsPageForm);
            show(wsPagePreview);
            setError(worksheetError, '');
            wsResetEditorState();
            applyWsPresetPrint();
            renderWsEditor(currentWorksheetData);
            await saveWorksheet();
            renderWsPair();
            if (wsPresetPrint) wsPresetPrint.coreId = currentWorksheetId;
            if (separate && currentWorksheetId) makeSupportedVersion(secondSheet, language);
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(worksheetError, 'Network error');
        } finally {
            btnGenerateWs.disabled = false;
            btnGenerateWs.textContent = 'Generate worksheet';
        }
    });

    btnWsBack?.addEventListener('click', () => {
        hide(wsPagePreview);
        show(wsPageForm);
        wsWizard?.setStep(1);
    });

    // ── Supported version ─────────────────────────────────
    //
    // A second sheet made from the main one for the pupils who need more
    // scaffolding: same questions, same order, same answers, so the class goes
    // through both together (utils/worksheetAdapt.js). Saved on the server as
    // its own worksheet, linked by variant_of. The teacher stays on the main
    // sheet while it is made and switches across when it is ready.

    const wsPairEl       = $('ws-pair');
    const wsPairSeg      = $('ws-pair-seg');
    const wsPairStatus   = $('ws-pair-status');
    const btnWsMakeSupp  = $('btn-ws-make-supported');
    const btnWsRemake    = $('btn-ws-remake-supported');
    let wsAdapting = false;

    // On a supported sheet the pair is its main sheet (role 'core'); one whose
    // main sheet was deleted has no pair but is still not to be adapted again.
    const wsOnSupported = () => currentWorksheetPair?.role === 'core' || currentWorksheetData?.variant === 'supported';

    function renderWsPair() {
        if (!wsPairEl) return;
        // One tab on its own until there are two sheets to switch between.
        const solo = $('ws-tab-solo');
        if (solo) {
            solo.hidden = !!(currentWorksheetId && currentWorksheetPair);
            solo.textContent = wsOnSupported() ? 'Supported' : 'Main sheet';
        }
        wsPairEl.hidden = !currentWorksheetId;
        if (!currentWorksheetId) return;
        const onSupported = wsOnSupported();
        const paired = !!currentWorksheetPair;
        if (wsPairSeg) {
            wsPairSeg.hidden = !paired;
            const here = wsPairSeg.querySelector(`input[value="${onSupported ? 'supported' : 'core'}"]`);
            if (here) here.checked = true;
        }
        if (btnWsMakeSupp) btnWsMakeSupp.hidden = paired || onSupported;
        if (btnWsRemake) btnWsRemake.hidden = !paired;
        [btnWsMakeSupp, btnWsRemake].forEach(b => { if (b) b.disabled = wsAdapting; });
    }

    /**
     * Make (or remake) the supported version of a main sheet. Not awaited by
     * the generate button: the teacher is already reading the main sheet.
     */
    async function makeSupportedVersion(list, language, coreId = currentWorksheetId) {
        if (!coreId || wsAdapting) return;
        wsAdapting = true;
        renderWsPair();
        if (wsPairStatus) wsPairStatus.textContent = 'Making the supported version…';
        try {
            const res = await apiFetch(`/api/worksheets/${coreId}/adapt`, {
                method: 'POST',
                body: JSON.stringify({ supports: list, language }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (wsPairStatus) wsPairStatus.textContent = '';
                if (res.status !== 402) showToast(d.error || 'Could not make the supported version. Try again.', 5000);
                return;
            }
            // A remade sheet is a new sheet; any copy fetched ahead is stale.
            wsPairCache.delete(d.id);
            if (currentWorksheetId === coreId) {
                currentWorksheetPair = { id: d.id, role: 'supported', supports: list, language };
                prefetchWsPair(d.id);
            } else if (currentWorksheetId === d.id) {
                // Remade while looking at it: show the new one.
                currentWorksheetData = d.data_json;
                wsResetEditorState();
                renderWsEditor(currentWorksheetData);
            }
            if (wsPairStatus) {
                wsPairStatus.textContent = 'Supported version ready';
                setTimeout(() => { if (wsPairStatus) wsPairStatus.textContent = ''; }, 3000);
            }
        } catch (err) {
            if (wsPairStatus) wsPairStatus.textContent = '';
            if (err.message !== 'Not authenticated') showToast('Network error');
        } finally {
            wsAdapting = false;
            renderWsPair();
        }
    }

    // ── Switching between the two ──
    // (The fetch-ahead it relies on is with the editor's state, by
    // wsSheetTabs: openWorksheet uses it, and can run before this point.)
    let wsSwitching = false;

    // The sheet slides out towards the side it is leaving by and the other
    // comes in from the side of its tab: Supported is to the right of Main.
    const wsSwitchMs = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 160);
    wsPairSeg?.addEventListener('change', async (e) => {
        const want = e.target.value;
        const onSupported = wsOnSupported();
        if (!currentWorksheetPair || wsSwitching || (want === 'supported') === onSupported) {
            renderWsPair();
            return;
        }
        wsSwitching = true;
        const forward = want === 'supported';
        const leaving = currentWorksheetId;
        clearTimeout(wsAutoSaveTimer);
        wsPairSaving = saveWorksheet();
        wsPairCache.delete(leaving);

        wsSheetCanvas.dataset.switching = forward ? 'out-left' : 'out-right';
        // Only said when it is slow, so a quick switch is just the slide.
        const slow = setTimeout(() => { if (wsPairStatus) wsPairStatus.textContent = 'Opening…'; }, 350);
        await Promise.all([
            new Promise(r => setTimeout(r, wsSwitchMs())),
            openWorksheet(currentWorksheetPair.id),
        ]);
        clearTimeout(slow);
        // A "second sheet" preset's layout is for the supported sheet only.
        if (wsPresetPrint && wsPresetPrint.coreId === (forward ? leaving : currentWorksheetId)) {
            const def = state.prefs?.a11y || {};
            if (forward) setWsPrintPickers(wsPresetPrint.layout, wsPresetPrint.tint);
            else setWsPrintPickers(def.layout, def.tint);
            renderWsEditor(currentWorksheetData);
        }
        if (wsPairStatus?.textContent === 'Opening…') wsPairStatus.textContent = '';

        // In from the other side: jump there unseen, then let it slide home.
        wsSheetCanvas.dataset.switching = forward ? 'in-right' : 'in-left';
        void wsSheetScaler.offsetWidth;
        delete wsSheetCanvas.dataset.switching;
        wsSwitching = false;
    });

    // The same checkboxes as the composer's panel, copied in, plus its
    // home-language picker. Resolves with what was ticked, or null.
    let wsAdaptResolve = null;
    function closeAdaptModal(result) {
        hide($('ws-adapt-modal'));
        if (wsAdaptResolve) { wsAdaptResolve(result); wsAdaptResolve = null; }
    }
    function openAdaptModal(preset) {
        const modal = $('ws-adapt-modal');
        const supportsBox = $('ws-adapt-supports');
        const scaffoldsBox = $('ws-adapt-scaffolds');
        if (!modal || !supportsBox || !scaffoldsBox) return Promise.resolve(null);
        closeAdaptModal(null);
        supportsBox.innerHTML = '';
        scaffoldsBox.innerHTML = '';
        const ticked = Array.isArray(preset?.supports) ? preset.supports : [];
        document.querySelectorAll('#ws-support-checks .ws-support-check').forEach(label => {
            const copy = label.cloneNode(true);
            const input = copy.querySelector('input');
            if (input) input.checked = ticked.includes(input.value);
            (label.classList.contains('ws-scaffold-check') ? scaffoldsBox : supportsBox).appendChild(copy);
        });
        const slot = $('ws-adapt-lang-slot');
        if (slot && wsLanguageEl) {
            const sel = wsLanguageEl.cloneNode(true);
            sel.id = 'ws-adapt-language';
            sel.value = preset?.language || '';
            slot.replaceChildren(sel);
        }
        show(modal);
        $('ws-adapt-ok')?.focus();
        return new Promise(resolve => { wsAdaptResolve = resolve; });
    }
    $('ws-adapt-modal')?.addEventListener('click', (e) => {
        if (e.target.closest('[data-adapt-cancel]')) closeAdaptModal(null);
    });
    $('ws-adapt-modal')?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAdaptModal(null);
    });
    $('ws-adapt-ok')?.addEventListener('click', () => {
        const list = Array.from(document.querySelectorAll('#ws-adapt-modal input[type="checkbox"]:checked')).map(el => el.value);
        const language = $('ws-adapt-language')?.value || '';
        if (!list.length && !language) { showToast('Tick at least one kind of support'); return; }
        closeAdaptModal({ list, language });
    });

    async function askAndAdapt() {
        if (!currentWorksheetId) await saveWorksheet();
        if (!currentWorksheetId) return;
        const onSupported = wsOnSupported();
        const coreId = onSupported ? currentWorksheetPair?.id : currentWorksheetId;
        if (!coreId) return;
        // Remaking starts from what the supported version was made with.
        const preset = onSupported
            ? { supports: [...(currentWorksheetData?.supports || []), ...(currentWorksheetData?.scaffolds || [])], language: currentWorksheetData?.language }
            : currentWorksheetPair;
        const picked = await openAdaptModal(preset);
        if (!picked) return;
        clearTimeout(wsAutoSaveTimer);
        if (!onSupported) await saveWorksheet();
        makeSupportedVersion(picked.list, picked.language, coreId);
    }
    btnWsMakeSupp?.addEventListener('click', askAndAdapt);
    btnWsRemake?.addEventListener('click', async () => {
        const ok = await showConfirm({
            title: 'Remake the supported version?',
            message: 'It is made again from the main sheet as it is now. Changes made by hand to the supported version are lost.',
            confirmLabel: 'Choose support',
        });
        if (ok) askAndAdapt();
    });

    // Two files: the layout rules, and the ~300KB of base64 OpenDyslexic that
    // only the dyslexia layout actually needs. Fetched separately so a standard
    // print does not pay for a typeface it will not use, and cached per file
    // because a teacher printing six differentiated copies should download
    // neither of them twice.
    const printCssPromises = {};
    function loadCss(href) {
        if (!printCssPromises[href]) {
            printCssPromises[href] = fetch(href).then(r => r.text());
        }
        return printCssPromises[href];
    }
    /**
     * KaTeX's stylesheet, with its font URLs made absolute.
     *
     * Needed wherever the print CSS is INLINED rather than linked, which is
     * the print popup (a document.write into about:blank) and the preview
     * modal (a srcdoc iframe) - neither has a base URL for a <link> to resolve
     * against, so they got no KaTeX rules at all. Without them .katex-mathml
     * is not clipped and every formula prints twice: once drawn, and once as
     * the MathML copy KaTeX leaves behind for screen readers.
     *
     * The server PDF solves the same problem by base64-ing the fonts into the
     * stylesheet (utils/pdf.js). On the client the files are already served at
     * /vendor/katex/fonts, so an absolute URL is enough and costs nothing.
     */
    function loadKatexCss() {
        return loadCss('/vendor/katex/katex.min.css').then(css =>
            css.replace(/url\(fonts\//g, `url(${window.location.origin}/vendor/katex/fonts/`));
    }

    function loadWsPrintCss(a11y) {
        // Order matters twice over: @font-face before the rule that names the
        // family (OpenDyslexic), and KaTeX before worksheet-print.css, whose
        // own .katex-inline rules are meant to win.
        // worksheet-fonts.css always: which theme a sheet prints in is on the
        // document, not passed here, and it is small next to OpenDyslexic.
        const wanted = a11y === 'dyslexia'
            ? ['/css/a11y-fonts.css', '/css/worksheet-fonts.css', '/css/worksheet-print.css']
            : ['/css/worksheet-fonts.css', '/css/worksheet-print.css'];
        return Promise.all([loadKatexCss(), ...wanted.map(loadCss)])
            .then(parts => parts.join('\n'));
    }

    // The QR code to the answers page, when the box in the export menu is
    // ticked. The link belongs to a saved worksheet, so one that has never
    // been saved is saved first. A sheet with no code still prints: undefined
    // on any failure, and the header simply leaves it out.
    const wsQrCheck = $('ws-qr-check');
    const wsQrHelp = $('ws-qr-help');
    function syncWsQrSwitch() {
        const sw = wsQrCheck?.closest('.t-toggle');
        if (sw) { sw.classList.add('is-init'); sw.dataset.on = String(wsQrCheck.checked); }
    }
    wsQrCheck?.addEventListener('change', syncWsQrSwitch);

    // One copy per student of a course, each with their name and a code of
    // their own (utils/worksheetAnswers.js on the server). For tutoring
    // agencies only: nobody else is asked, and the server would answer with
    // nothing if they were. The list is fetched the first time the menu opens,
    // and the whole row stays hidden for a tutor with nobody to print for. A student's copy always carries
    // its code - that is what makes it theirs - so the switch is held on.
    const wsCopiesSelect = $('ws-copies-select');
    let wsCopiesLoaded = false;
    $('btn-ws-export')?.addEventListener('click', async () => {
        if (wsCopiesLoaded || !wsCopiesSelect || !tutoringOrg()) return;
        wsCopiesLoaded = true;
        try {
            const res = await fetch('/api/worksheets/print-groups');
            const groups = res.ok ? (await res.json()).groups || [] : [];
            if (!groups.length) return;
            wsCopiesSelect.insertAdjacentHTML('beforeend', groups.map(g =>
                `<option value="${esc(g.key)}">One each: ${esc(g.label)} (${Number(g.count)})</option>`).join(''));
            document.querySelectorAll('[data-ws-copies]').forEach(el => { el.hidden = false; });
        } catch { wsCopiesLoaded = false; }
    });
    let wsQrBeforeCopies = false;
    wsCopiesSelect?.addEventListener('change', () => {
        const each = !!wsCopiesSelect.value;
        if (each && !wsQrCheck.disabled) wsQrBeforeCopies = wsQrCheck.checked;
        wsQrCheck.checked = each ? true : wsQrBeforeCopies;
        wsQrCheck.disabled = each;
        if (wsQrHelp) wsQrHelp.textContent = each ? 'Each student gets their own' : 'Parents scan it for the answers';
        syncWsQrSwitch();
    });
    /** [{ studentName, answersQr }] for the chosen class or course, or undefined. */
    async function wsStudentCopies() {
        // Topic homework is one student's sheet and always carries their code;
        // the QR switch only decides whether it opens the answers.
        if (!wsCopiesSelect?.value && currentWorksheetId && tutoringOrg()) {
            const res = await apiFetch(`/api/worksheets/${currentWorksheetId}/copies`, {
                method: 'POST', body: JSON.stringify({ homework: true, answers: !!wsQrCheck?.checked }),
            });
            if (res.ok) {
                const { copies } = await res.json();
                if (copies) return copies;
            }
        }
        if (!wsCopiesSelect?.value) return undefined;
        if (!currentWorksheetId) await saveWorksheet();
        if (!currentWorksheetId) return undefined;
        const res = await apiFetch(`/api/worksheets/${currentWorksheetId}/copies`, {
            method: 'POST', body: JSON.stringify({ group: wsCopiesSelect.value }),
        });
        if (!res.ok) throw new Error('copies');
        return (await res.json()).copies;
    }
    async function wsAnswersQr() {
        if (!wsQrCheck?.checked) return undefined;
        try {
            if (!currentWorksheetId) await saveWorksheet();
            if (!currentWorksheetId) return undefined;
            const res = await apiFetch(`/api/worksheets/${currentWorksheetId}/answers-link`, { method: 'POST' });
            return res.ok ? (await res.json()).qr : undefined;
        } catch { return undefined; }
    }

    async function openPrint(withAnswers) {
        if (!currentWorksheetData) return;
        const opts = a11yOptsFrom(wsA11ySelect, wsTintSelect);
        const meta = {
            subject:   wsSubject?.value.trim() || '',
            level:     wsLevel?.value || '',
            examBoard: wsExamBoard?.value || '',
            a11y:      opts.a11y || undefined,
            tint:      opts.tint || undefined,
        };
        // Open the window synchronously (in the same click gesture, so popup blockers
        // don't kick in), then inline the stylesheet (same as the server PDF path)
        // instead of linking it – the CSS can carry base64 web fonts and be a
        // few hundred KB, so printing against a still-loading <link> was racy.
        const win = window.open('', '_blank');
        const css = await loadWsPrintCss(opts.a11y);
        meta.mark = await loadResourceMark();
        // A pile with no names on it is worse than no pile: if the copies
        // cannot be fetched, say so instead of printing one anonymous sheet.
        let copies;
        try { copies = await wsStudentCopies(); } catch {
            win.close();
            return showToast('Could not make the students\u2019 copies');
        }
        // Topic homework is printed from the PDF, not the browser: only the
        // PDF can put the student's code on every page, which is what lets a
        // scanned page 3 say whose it is.
        if (copies && copies[0] && copies[0].homework) {
            win.location.href = worksheetPdfUrl(withAnswers, { inline: true });
            return;
        }
        if (!copies) meta.answersQr = await wsAnswersQr();
        win.document.write(WorksheetRender.buildPrintPage(currentWorksheetData, withAnswers, {
            meta, css, copies,
        }));
        win.document.close();
        win.focus();
        let printed = false;
        const doPrint = () => { if (!printed) { printed = true; win.print(); } };
        win.addEventListener('load', () => setTimeout(doPrint, 50));
        setTimeout(doPrint, 1000);
    }
    // Which copy is chosen once, in the export panel, and both ways out of it
    // (print, PDF) ask the same question.
    const wsWithAnswers = () => !!$('ws-what-answers')?.checked;
    btnWsPrint?.addEventListener('click', () => openPrint(wsWithAnswers()));

    // ── Preview as a pupil sees it ────────────────────────
    //
    // The same document the PDF is built from, in an iframe, rather than a
    // second on-screen approximation of it. The editor above shows editable
    // fields; this shows the sheet. Choosing a layout and finding out what it
    // did by printing it is the loop this closes.
    const wsPreviewModal = $('ws-preview-modal');
    const wsPreviewFrame = $('wsp-frame');
    let wsPreviewLastFocus = null;

    async function openWorksheetPreview() {
        if (!currentWorksheetData || !wsPreviewModal || !wsPreviewFrame) return;
        const opts = a11yOptsFrom(wsA11ySelect, wsTintSelect);
        const desc = $('wsp-desc');
        if (desc) desc.textContent = A11yProfiles.describe(opts);

        wsPreviewLastFocus = document.activeElement;
        show(wsPreviewModal);
        document.body.style.overflow = 'hidden';
        $('wsp-close')?.focus();

        const css = await loadWsPrintCss(opts.a11y);
        // The logo arrives as a data URI rather than a URL for the same reason
        // the stylesheet is inlined: this document has no origin to resolve a
        // subresource against.
        const mark = await loadResourceMark();
        const answersQr = await wsAnswersQr();
        // srcdoc, not document.write into the frame: the iframe is sandboxed
        // with no allow-same-origin, so there is no contentDocument to write to.
        wsPreviewFrame.srcdoc = WorksheetRender.buildPrintPage(currentWorksheetData, false, {
            meta: {
                subject:   wsSubject?.value.trim() || '',
                level:     wsLevel?.value || '',
                examBoard: wsExamBoard?.value || '',
                a11y:      opts.a11y || undefined,
                tint:      opts.tint || undefined,
                mark,
                answersQr,
            },
            css,
        });
    }

    function closeWorksheetPreview() {
        if (!wsPreviewModal) return;
        hide(wsPreviewModal);
        document.body.style.overflow = '';
        // Cleared on close so the next open cannot flash the previous layout
        // while the new one is being fetched.
        if (wsPreviewFrame) wsPreviewFrame.srcdoc = '';
        wsPreviewLastFocus?.focus?.();
    }

    $('btn-ws-preview')?.addEventListener('click', openWorksheetPreview);
    $('wsp-close')?.addEventListener('click', closeWorksheetPreview);
    wsPreviewModal?.querySelector('[data-wsp-close]')?.addEventListener('click', closeWorksheetPreview);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && wsPreviewModal && isShown(wsPreviewModal)) closeWorksheetPreview();
    });

    // ── Booklet preview: the printed file itself ───────────────────────
    //
    // Deliberately not a re-render of the document in an iframe. A booklet is
    // paginated, and where a page ends is most of what a teacher opens a
    // preview to check — but only the renderer that makes the PDF knows where
    // its own pages fall. An on-screen paginator (this used Paged.js) agrees
    // with the download right up until it doesn't, and a preview that draws
    // page boundaries the printed thing does not have is worse than no preview
    // at all.
    //
    // So the preview *is* the file: the same endpoint the download button
    // hits, asked for `inline`, handed to the browser's own PDF viewer. It
    // costs a server render per open, which is the price of the two never
    // disagreeing again.
    const bkPreviewModal  = $('bk-preview-modal');
    const bkPreviewFrame  = $('bkp-frame');
    const bkPreviewStatus = $('bkp-status');
    let bkPreviewLastFocus = null;
    let bkPreviewKind = 'booklet';

    function setBkPreviewStatus(message) {
        if (!bkPreviewStatus) return;
        bkPreviewStatus.textContent = message || '';
        if (message) show(bkPreviewStatus); else hide(bkPreviewStatus);
    }

    /**
     * Why the endpoint URL goes straight into the frame rather than a blob:
     * a blob: PDF in an iframe is not displayed by every browser (Safari in
     * particular), and this has to work on an iPad. A same-origin URL is what
     * every PDF viewer handles, and the session cookie rides along exactly as
     * it does for the download button next to this.
     *
     * The cost is that a failure response would be shown as its raw text, so
     * the two states the server refuses are checked here first — both are
     * visible in the reader's own state.
     */
    function bkPreviewUnavailable(kind) {
        const sections = bkState.sections || [];
        if (kind === 'workbook') {
            return sections.some((s) => (s.exercises || []).length)
                ? '' : 'This booklet was generated without a workbook.';
        }
        return sections.some((s) => (s.blocks || []).length)
            ? '' : 'This booklet has no finished sections yet.';
    }

    function renderBkPreview() {
        if (!bkPreviewFrame || !bkState.id) return;
        // Dropping the src first both cancels a render still in flight (a
        // teacher can switch guide→workbook before the first one lands) and
        // stops its load event from clearing the new status line: the handler
        // below only clears when there is a src to have loaded.
        bkPreviewFrame.removeAttribute('src');

        const unavailable = bkPreviewUnavailable(bkPreviewKind);
        if (unavailable) { setBkPreviewStatus(unavailable); return; }

        const opts = a11yOptsFrom($('bk-a11y-select'), $('bk-tint-select'));
        const query = ['inline=1'];
        if (opts.a11y) query.push('a11y=' + encodeURIComponent(opts.a11y));
        if (opts.tint) query.push('tint=' + encodeURIComponent(opts.tint));
        const path = bkPreviewKind === 'workbook' ? 'workbook.pdf' : 'pdf';

        // Cleared by the frame's load handler. Rendering a booklet server-side
        // takes a second or two, and an empty frame for that long reads as
        // broken rather than as busy.
        setBkPreviewStatus('Laying out the pages…');
        bkPreviewFrame.src = '/api/booklets/' + bkState.id + '/' + path + '?' + query.join('&');
    }

    bkPreviewFrame?.addEventListener('load', () => {
        if (bkPreviewFrame.getAttribute('src')) setBkPreviewStatus('');
    });

    function openBookletPreview(kind) {
        if (!bkPreviewModal || !bkPreviewFrame || !bkState.sections?.length) return;
        bkPreviewKind = bkIsWorkbook() ? 'workbook' : (kind || 'booklet');
        bkPreviewModal.querySelectorAll('[data-bkp-kind]').forEach((b) =>
            b.classList.toggle('active', b.dataset.bkpKind === bkPreviewKind));
        bkPreviewLastFocus = document.activeElement;
        show(bkPreviewModal);
        document.body.style.overflow = 'hidden';
        $('bkp-close')?.focus();
        renderBkPreview();
    }

    function closeBookletPreview() {
        if (!bkPreviewModal) return;
        hide(bkPreviewModal);
        document.body.style.overflow = '';
        // Cleared on close so the next open cannot flash the previous
        // document while the new one is still being rendered.
        bkPreviewFrame?.removeAttribute('src');
        setBkPreviewStatus('');
        bkPreviewLastFocus?.focus?.();
    }

    $('btn-bk-preview')?.addEventListener('click', () =>
        openBookletPreview(bkExportWhat() === 'booklet' ? 'booklet' : 'workbook'));
    $('bkp-close')?.addEventListener('click', closeBookletPreview);
    bkPreviewModal?.querySelector('[data-bkp-close]')?.addEventListener('click', closeBookletPreview);
    bkPreviewModal?.querySelectorAll('[data-bkp-kind]').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (btn.dataset.bkpKind === bkPreviewKind) return;
            bkPreviewKind = btn.dataset.bkpKind;
            bkPreviewModal.querySelectorAll('[data-bkp-kind]').forEach((b) =>
                b.classList.toggle('active', b === btn));
            renderBkPreview();
        });
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && bkPreviewModal && isShown(bkPreviewModal)) closeBookletPreview();
    });

    // ── Export dropdown menus ─────────────────────────────
    function closeExportMenus() {
        document.querySelectorAll('.export-menu').forEach(m => hide(m));
    }
    document.querySelectorAll('.export-menu-wrap').forEach(wrap => {
        const btn  = wrap.querySelector('.export-menu-btn');
        const menu = wrap.querySelector('.export-menu');
        if (!btn || !menu) return;
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const willOpen = !isShown(menu);
            closeExportMenus();
            if (willOpen) show(menu); else hide(menu);
        });
        menu.addEventListener('click', (e) => {
            if (e.target.closest('select, label')) { e.stopPropagation(); return; }
            hide(menu);
        });
    });
    document.addEventListener('click', closeExportMenus);
    document.addEventListener('keydown', (e) => { if (!state.presenting && e.key === 'Escape') closeExportMenus(); });

    // ── Accessible layout pickers ─────────────────────────
    //
    // The worksheet and quiz export menus both offer the same two choices, and
    // both are filled from A11yProfiles rather than from hand-written <option>
    // markup – the server validates against that same list, so a layout added
    // in one place cannot go missing in the other.
    //
    // The starting value comes from the teacher's saved default (Settings →
    // Accessibility). Changing it here is deliberately per-document: someone
    // printing one large-print copy for one pupil should not silently rewrite
    // the default they set for their whole class.
    function fillA11ySelect(select, options) {
        if (!select) return;
        select.innerHTML = options
            .map(o => `<option value="${esc(o.id || '')}">${esc(o.label)}</option>`)
            .join('');
    }

    function initA11yPickers(layoutSelect, tintSelect, noteEl) {
        if (!window.A11yProfiles) return;
        fillA11ySelect(layoutSelect, A11yProfiles.TEXT_LAYOUTS);
        fillA11ySelect(tintSelect, A11yProfiles.TINTS);

        const prefs = state.prefs?.a11y || {};
        if (layoutSelect) layoutSelect.value = prefs.layout || '';
        if (tintSelect) tintSelect.value = prefs.tint || '';

        // The blurb explains who a layout is for. Tint needs no explanation,
        // so it does not get one – but it does re-render the line, because the
        // note also has to clear when the layout goes back to Standard.
        const syncNote = () => {
            if (!noteEl) return;
            const hit = A11yProfiles.TEXT_LAYOUTS.find(l => (l.id || '') === (layoutSelect?.value || ''));
            noteEl.textContent = hit && hit.id ? hit.blurb : '';
        };
        layoutSelect?.addEventListener('change', syncNote);
        syncNote();
    }

    /** What the two pickers currently say, in the shape A11yProfiles takes. */
    function a11yOptsFrom(layoutSelect, tintSelect) {
        return { a11y: layoutSelect?.value || null, tint: tintSelect?.value || null };
    }

    // Called once at boot and again when the saved preferences land, since the
    // pickers have to be usable before the /api/settings/prefs round trip
    // finishes but should show the teacher's default once it does.
    // The worksheet's paper is chosen from swatches rather than a list of
    // colour names. The select underneath stays the value everything reads
    // (and the one initA11yPickers sets from the saved default), so the
    // swatches only ever write to it and follow it.
    // The booklet menu uses the same swatches.
    function bindPaperSwatches(swatches, tintSelect, name) {
        if (!swatches || !tintSelect) return () => {};
        const sync = () => {
            if (!window.A11yProfiles) return;
            if (!swatches.children.length) {
                swatches.innerHTML = A11yProfiles.TINTS.map(t =>
                    `<label title="${esc(t.label)}"><input type="radio" name="${name}" value="${esc(t.id || '')}" aria-label="${esc(t.label)}"><span style="background:${esc(t.hex)}"></span></label>`
                ).join('');
            }
            const value = tintSelect.value || '';
            swatches.querySelectorAll('input').forEach(i => { i.checked = i.value === value; });
            const tint = A11yProfiles.TINTS.find(t => (t.id || '') === value);
            swatches.closest('.xm')?.style.setProperty('--xm-paper', tint ? tint.hex : '#fff');
        };
        swatches.addEventListener('change', (e) => {
            tintSelect.value = e.target.value;
            tintSelect.dispatchEvent(new Event('change'));
        });
        tintSelect.addEventListener('change', sync);
        return sync;
    }
    const syncWsPaperSwatches = bindPaperSwatches($('ws-paper-swatches'), wsTintSelect, 'ws-paper');
    const syncBkPaperSwatches = bindPaperSwatches($('bk-paper-swatches'), $('bk-tint-select'), 'bk-paper');

    function refreshA11yPickers() {
        initA11yPickers(wsA11ySelect, wsTintSelect, $('ws-a11y-note'));
        syncWsPaperSwatches();
        initA11yPickers(quizA11ySelect, quizTintSelect, $('quiz-a11y-note'));
        initA11yPickers($('mk-a11y-select'), $('mk-tint-select'), $('mk-a11y-note'));
        initA11yPickers($('bk-a11y-select'), $('bk-tint-select'), null);
        syncBkPaperSwatches();
        renderWsPresets();
    }

    /**
     * The `?a11y=…&tint=…` suffix for a marking download, or ''.
     *
     * The per-student download and the whole-set download read the same two
     * pickers – a teacher who set Large print for the class should not have to
     * set it again for the one pupil they re-export.
     */
    function markingA11yQuery() {
        const qs = A11yProfiles.toQuery(a11yOptsFrom($('mk-a11y-select'), $('mk-tint-select')));
        return qs ? '?' + qs : '';
    }

    function triggerDownload(url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    function worksheetPdfUrl(withAnswers, { inline = false } = {}) {
        const parts = [];
        if (withAnswers) parts.push('answers=1');
        if (wsCopiesSelect?.value) parts.push('group=' + encodeURIComponent(wsCopiesSelect.value));
        else if (wsQrCheck?.checked) parts.push('qr=1');
        const a11yQs = A11yProfiles.toQuery(a11yOptsFrom(wsA11ySelect, wsTintSelect));
        if (a11yQs) parts.push(a11yQs);
        if (inline) parts.push('inline=1');
        const qs = parts.join('&');
        return `/api/worksheets/${currentWorksheetId}/pdf${qs ? '?' + qs : ''}`;
    }
    async function downloadWorksheetPdf(withAnswers) {
        if (!currentWorksheetId) await saveWorksheet();   // ensure it exists server-side
        if (!currentWorksheetId) return;
        triggerDownload(worksheetPdfUrl(withAnswers));
    }
    $('btn-ws-download-pdf')?.addEventListener('click', () => downloadWorksheetPdf(wsWithAnswers()));
    // Word has none of the PDF's print options (paper, layout, QR, copies),
    // only the answers choice; see utils/worksheetDocx.js.
    $('btn-ws-download-docx')?.addEventListener('click', async () => {
        if (!currentWorksheetId) await saveWorksheet();
        if (!currentWorksheetId) return;
        triggerDownload(`/api/worksheets/${currentWorksheetId}/docx${wsWithAnswers() ? '?answers=1' : ''}`);
    });

    $('btn-ws-send-link')?.addEventListener('click', async () => {
        if (!currentWorksheetId) await saveWorksheet();   // a link needs it saved
        if (!currentWorksheetId) return;
        sendAsLink('worksheet', currentWorksheetId, currentWorksheetData?.title || worksheetTopicEl?.value);
    });

    // The answers on a page a parent can open with no account. The link is the
    // only way in, so it is made on the server and is the same each time.
    $('btn-ws-answers-link')?.addEventListener('click', async () => {
        if (!currentWorksheetId) await saveWorksheet();   // ensure it exists server-side
        if (!currentWorksheetId) return;
        const res = await apiFetch(`/api/worksheets/${currentWorksheetId}/answers-link`, { method: 'POST' });
        if (!res.ok) return showToast('Could not make the answers link');
        const { url } = await res.json();
        try {
            await navigator.clipboard.writeText(url);
            showToast('Answers link copied');
        } catch {
            // Safari refuses the clipboard once a request has come between the
            // click and the copy; the link is still theirs to take.
            window.prompt('Copy the answers link', url);
        }
    });


    // ── Shared list UI bits ───────────────────────────────

    const LIST_ICONS = {
        presentation: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M7 9h7M7 12h4"/><path d="M12 17v3M9 21h6"/></svg>',
        worksheet:    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2z"/><path d="m8.5 10 1.2 1.2L12 8.9"/><path d="M14 11h3M8.5 15.5h8.5"/></svg>',
        quiz:         '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12L13 2z"/></svg>',
        marking:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h11M4 11h7"/><path d="m13.5 17.5 2.5 2.5 5.5-6"/><path d="M4 16h5"/></svg>',
        lesson:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6.5C10.5 5 8.5 4.5 6.5 4.5c-1 0-2 .15-2.5.35V18c.5-.2 1.5-.35 2.5-.35 2 0 4 .5 5.5 1.85 1.5-1.35 3.5-1.85 5.5-1.85 1 0 2 .15 2.5.35V4.85c-.5-.2-1.5-.35-2.5-.35-2 0-4 .5-5.5 2z"/><path d="M12 6.5v12.65"/></svg>',
        booklet:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2.5 2.5 0 0 1 2 1 2.5 2.5 0 0 1 2-1h4.5A1.5 1.5 0 0 1 20 5.5v12a1.5 1.5 0 0 1-1.5 1.5H14a2.5 2.5 0 0 0-2 1 2.5 2.5 0 0 0-2-1H5.5A1.5 1.5 0 0 1 4 17.5z"/><path d="M12 5v14"/></svg>',
        students:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20"/><circle cx="9" cy="7.5" r="3.5"/><path d="M22 20v-1.5a4 4 0 0 0-3-3.87"/><path d="M16.5 4.13a4 4 0 0 1 0 6.74"/></svg>',
    };

    const DOTS_ICON = '<svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" aria-hidden="true"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>';

    // ── Card overflow menu ────────────────────────────────
    // One menu is open at a time and lives on <body>, so a card's overflow or
    // stacking context can never clip it.
    let openCardMenuEl = null;
    let openCardMenuBtn = null;

    function closeCardMenu() {
        if (!openCardMenuEl) return;
        openCardMenuEl.remove();
        openCardMenuEl = null;
        document.querySelectorAll('.card-menu-btn[aria-expanded="true"]')
            .forEach(b => b.setAttribute('aria-expanded', 'false'));
        // Not every opener is a .card-menu-btn, and one left marked expanded
        // reads its next click as "close" and never opens again.
        openCardMenuBtn?.setAttribute('aria-expanded', 'false');
        openCardMenuBtn = null;
    }

    document.addEventListener('click', (e) => {
        if (!openCardMenuEl) return;
        if (e.target.closest('.card-menu') || e.target.closest('.card-menu-btn')) return;
        closeCardMenu();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeCardMenu(); });
    // Position is fixed at open time, so any scroll would detach it.
    window.addEventListener('scroll', closeCardMenu, true);
    window.addEventListener('resize', closeCardMenu);

    function openCardMenu(btn, items) {
        const wasOpen = btn.getAttribute('aria-expanded') === 'true';
        closeCardMenu();
        if (wasOpen) return; // second click on the same button closes it

        const menu = document.createElement('div');
        menu.className = 'card-menu';
        menu.setAttribute('role', 'menu');
        items.forEach(it => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'card-menu-item' + (it.danger ? ' card-menu-danger' : '');
            b.setAttribute('role', 'menuitem');
            b.innerHTML = `${it.icon || ''}<span>${esc(it.label)}</span>`;
            b.addEventListener('click', (e) => {
                e.stopPropagation();
                closeCardMenu();
                it.onClick();
            });
            menu.appendChild(b);
        });
        document.body.appendChild(menu);

        const r = btn.getBoundingClientRect();
        const left = Math.max(8, Math.min(r.left, window.innerWidth - menu.offsetWidth - 12));
        const below = r.bottom + 6;
        // Flip above the button when there isn't room underneath.
        const top = below + menu.offsetHeight + 8 > window.innerHeight
            ? Math.max(8, r.top - menu.offsetHeight - 6)
            : below;
        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;

        btn.setAttribute('aria-expanded', 'true');
        openCardMenuEl = menu;
        openCardMenuBtn = btn;
        menu.querySelector('.card-menu-item')?.focus();
    }

    const PUBLISH_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M12 16V4"/><path d="m7 9 5-5 5 5"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>';

    const TRASH_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>';

    const SHARE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 10.5 6.8-3.9M8.6 13.5l6.8 3.9"/></svg>';

    // One item shared into the caller's org library, from a worksheet, quiz,
    // presentation or booklet they own. No dialog: unlike publishing to the
    // Marketplace this never leaves the school, so there is nothing to
    // moderate or word carefully first.
    const LINK_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>';

    // One resource as a link, through the dialog Select uses for several.
    function sendAsLink(kind, id, title) {
        if (id) openBundleModal([{ kind, id: String(id), title: title || '' }]);
    }

    // The Share button on a library card. Anyone can send a resource as a
    // link; a teacher in an organisation can also put it in the school's
    // library, so for them it opens a menu of the two. A deck or booklet still
    // generating has nothing to share yet, so it gets no button.
    function shareButtonHTML(kind, item) {
        if ((kind === 'presentation' || kind === 'booklet') && item.status !== 'done') return '';
        return `<button class="pres-item-btn pres-share-btn" title="Share" aria-label="Share">${SHARE_ICON}</button>`;
    }
    function bindShareButton(el, kind, item) {
        const btn = el.querySelector('.pres-share-btn');
        if (!btn) return;
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const link = () => sendAsLink(kind, item.id, item.title || item.topic);
            if (!state.user?.org) return link();
            openCardMenu(btn, [
                { label: 'Send as a link', icon: LINK_ICON, onClick: link },
                { label: 'Share to org library', icon: SHARE_ICON, onClick: () => shareToOrgLibrary(kind, item.id) },
            ]);
        });
    }

    async function shareToOrgLibrary(itemType, itemId) {
        try {
            const r = await apiFetch('/api/org/library', {
                method: 'POST',
                body: JSON.stringify({ itemType, itemId }),
            });
            const data = await r.json();
            if (!r.ok) { showToast(data.error || 'Couldn’t share that'); return; }
            showToast('Shared – colleagues can take a copy');
            // Keeps the Shared tab's count honest without waiting for a visit.
            loadOrgLibrary(true);
        } catch { showToast('Network error'); }
    }

    function listSkeletonHTML() {
        return Array.from({ length: 3 }, (_, i) =>
            `<div class="list-skel" style="animation-delay:${i * 0.08}s"><span class="list-skel-icon"></span><span class="list-skel-lines"><i style="width:${52 - i * 9}%"></i><i style="width:${24 + i * 4}%"></i></span></div>`
        ).join('');
    }

    function renderEmptyState(container, type, title, sub, ctaLabel) {
        container.innerHTML = `
            <div class="list-empty">
                <span class="list-empty-icon le-${type}">${LIST_ICONS[type]}</span>
                <p class="list-empty-title">${title}</p>
                <p class="list-empty-sub">${sub}</p>
                <button class="btn btn-primary list-empty-cta">${ctaLabel}</button>
            </div>`;
        container.querySelector('.list-empty-cta')?.addEventListener('click', () => {
            if (type === 'presentation') showGenerator();
            else if (type === 'booklet') showBookletPage();
            else if (type === 'worksheet') showWorksheetPage('', 'list');
            else if (type === 'marking') showMarkingPage();
            else showQuizPage('', 'list');
        });
    }

    // ── Library cards (worksheets & quizzes) ──────────────
    // Both are a pile of questions, so both cards answer the same question a
    // teacher actually asks: what kind of practice is in here, and how much?

    const QTYPE_LABELS = {
        multiple_choice:   'multiple choice',
        true_false:        'true / false',
        fill_blank:        'fill in the blank',
        short_answer:      'short answer',
        matching:          'matching',
        extended_response: 'extended response',
        whiteboard:        'whiteboard',
    };
    // Fixed order, so a given type always sits in the same place in the bar.
    const QTYPE_ORDER = Object.keys(QTYPE_LABELS);

    function typeEntries(row) {
        const counts = row.type_counts || {};
        return QTYPE_ORDER
            .filter(t => counts[t] > 0)
            .map(t => ({ type: t, n: counts[t], label: QTYPE_LABELS[t] || t }));
    }

    // The bar shows proportion only – which type is which is carried by the text
    // beneath it, so identity never rests on colour alone.
    function mixHTML(row) {
        const entries = typeEntries(row);
        if (!entries.length) return '';
        const full = entries.map(e => `${e.n} ${e.label}`).join(', ');
        const bar = entries
            .map(e => `<span class="res-mix-seg" style="flex:${e.n}"></span>`)
            .join('');
        const shown = entries.slice(0, 2).map(e => `${e.n} ${e.label}`).join(' · ');
        const rest = entries.length - 2;
        return `
            <div class="res-mix" role="img" aria-label="${escAttr(full)}" title="${escAttr(full)}">${bar}</div>
            <p class="res-mix-text">${esc(shown)}${rest > 0 ? ` · +${rest} more` : ''}</p>
        `;
    }

    // Subject / level / board, only what this resource actually has.
    function facetLine(row) {
        return [row.subject, row.level, row.exam_board].filter(Boolean).join(' · ');
    }

    // Worksheets and quizzes can be generated from a deck – name that source.
    function lineageLabel(row) {
        if (row.source_type !== 'presentation' || !row.source_id) return '';
        const src = presData.find(p => p.id === row.source_id);
        return src ? `from ${src.title || src.topic}` : '';
    }

    // Same shell and body as a presentation card, so all three kinds sit on the
    // same grid at the same size – only the cover differs.
    function resourceCardHTML(kind, row, actionsHTML) {
        const count = row.question_count || 0;
        const meta = [
            new Date(row.updated_at || row.created_at).toLocaleDateString(),
            facetLine(row),
            lineageLabel(row),
        ].filter(Boolean).join(' · ');
        return `
            <div class="res-cover">
                <span class="res-kind">${LIST_ICONS[kind]}${kind === 'worksheet' ? 'Worksheet' : 'Quiz'}</span>
                <span class="res-count"><b>${count}</b> ${count === 1 ? 'question' : 'questions'}</span>
                ${mixHTML(row)}
            </div>
            <div class="pres-card-body">
                <div class="pres-item-title">${esc(row.title || row.topic)}</div>
                <div class="pres-item-meta" title="${escAttr(meta)}">${esc(meta)}</div>
                <div class="pres-card-actions">${actionsHTML}</div>
            </div>
        `;
    }

    // List mode keeps the old row, but says what the resource holds.
    function resourceRowHTML(kind, row, actionsHTML) {
        const count = row.question_count || 0;
        const bits = [
            count ? `${count} ${count === 1 ? 'question' : 'questions'}` : '',
            facetLine(row),
            new Date(row.updated_at || row.created_at).toLocaleDateString(),
        ].filter(Boolean).join(' · ');
        return `
            <span class="pres-item-icon pi-${kind}" aria-hidden="true">${LIST_ICONS[kind]}</span>
            <div style="flex:1;min-width:0">
                <div class="pres-item-title">${esc(row.title || row.topic)}</div>
                <div class="pres-item-meta">${esc(bits)}</div>
            </div>
            ${actionsHTML}
        `;
    }

    // ── Worksheets list ───────────────────────────────────

    async function loadWorksheets() {
        if (!wsListEl) return;
        wsListEl.innerHTML = listSkeletonHTML();
        setError(wsListError, '');
        try {
            const res  = await apiFetch('/api/worksheets');
            const data = await res.json();
            if (!res.ok) { setError(wsListError, data.error || 'Failed to load'); return; }

            wsData = data;
            wsLoaded = true;
            renderTimeSaved();
            renderWorksheets();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(wsListError, 'Network error');
        }
    }

    function renderWorksheets() {
        if (!wsListEl || !wsLoaded) return;
        wsListEl.classList.remove('res-grid');

        const strip = folderStripEl('worksheet');
        const inFolder = !!openFolderId.worksheet;
        if (!wsData.length && !strip && !inFolder) {
            renderEmptyState(wsListEl, 'worksheet',
                'No worksheets yet',
                'Questions for the class, answers for you – ready to print in about a minute.',
                'Make your first worksheet');
            return;
        }

        const grid = presViewMode === 'grid';
        wsListEl.classList.toggle('res-grid', grid);
        wsListEl.innerHTML = '';
        if (strip) wsListEl.appendChild(strip);
        const view = itemsInView('worksheet', wsData);
        if (inFolder && !view.length) { wsListEl.appendChild(folderEmptyEl('worksheet')); return; }

        view.forEach(ws => {
            const item = document.createElement('div');
            const actions = `${shareButtonHTML('worksheet', ws)}${moveButtonHTML()}<button class="pres-delete-btn" title="Delete worksheet">${TRASH_ICON}</button>`;
            if (grid) {
                item.className = 'pres-card-item res-card res-worksheet';
                item.innerHTML = resourceCardHTML('worksheet', ws, actions);
            } else {
                item.className = 'pres-item';
                item.innerHTML = resourceRowHTML('worksheet', ws, actions);
            }
            bindShareButton(item, 'worksheet', ws);
            item.querySelector('.pres-delete-btn').addEventListener('click', async (e) => {
                e.stopPropagation();
                const ok = await showConfirm({
                    title: 'Delete worksheet?',
                    message: `"${ws.title || ws.topic}" will be permanently deleted. This can’t be undone.`,
                    confirmLabel: 'Delete',
                    danger: true
                });
                if (!ok) return;
                try {
                    const r = await apiFetch(`/api/worksheets/${ws.id}`, { method: 'DELETE' });
                    if (r.ok) {
                        // Its supported version stays, unlinked (as the server does).
                        wsData = wsData.filter(x => x.id !== ws.id).map(x => (x.variant_of === ws.id ? { ...x, variant_of: null } : x));
                        dropFromFolderCount('worksheet', ws); renderWorksheets(); renderTimeSaved();
                    }
                    else setError(wsListError, 'Delete failed');
                } catch { setError(wsListError, 'Network error'); }
            });
            // The two sheets of a pair share a title, so the supported one says so.
            const pairTag = ws.variant_of ? 'Supported version'
                : wsData.some(x => x.variant_of === ws.id) ? '+ supported version' : '';
            if (pairTag) {
                const tag = document.createElement('span');
                tag.className = 'ws-pair-tag';
                tag.textContent = pairTag;
                item.querySelector('.pres-item-title')?.appendChild(tag);
            }
            bindMoveButton(item, 'worksheet', ws);
            makeLibDraggable(item, 'worksheet', ws);
            markSelectable(item, 'worksheet', ws);
            item.addEventListener('click', (e) => {
                if (e.target.closest('.pres-delete-btn') || e.target.closest('.pres-share-btn') || e.target.closest('.pres-folder-btn')) return;
                openWorksheet(ws.id);
            });
            wsListEl.appendChild(item);
        });
    }

    // ── Booklets list ─────────────────────────────────────
    //
    // A card of its own rather than resourceCardHTML's: that helper answers
    // "what kind of practice is in here, and how much", which is the right
    // question for a worksheet or a quiz and the wrong one for a booklet. What
    // a teacher wants to know here is how long it is, whether a workbook came
    // with it, and — because generation can be interrupted — whether it is
    // actually finished.

    const bookletListEl    = $('booklet-list');
    const bookletListError = $('booklet-list-error');
    let bookletData = [];
    let bookletsLoaded = false;

    const BOOKLET_STATUS_LABELS = {
        pending: 'Not planned yet',
        planned: 'Waiting for you to approve the contents',
        approved: 'Ready to write',
        generating: 'Unfinished — reopen to carry on',
        error: 'Did not generate',
    };

    async function loadBooklets() {
        if (!bookletListEl) return;
        bookletListEl.innerHTML = listSkeletonHTML();
        setError(bookletListError, '');
        try {
            const res = await apiFetch('/api/booklets');
            const data = await res.json();
            if (!res.ok) { setError(bookletListError, data.error || 'Failed to load'); return; }
            bookletData = data;
            bookletsLoaded = true;
            renderBooklets();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(bookletListError, 'Network error');
        }
    }

    function renderBooklets() {
        if (!bookletListEl || !bookletsLoaded) return;
        bookletListEl.classList.remove('res-grid');

        const strip = folderStripEl('booklet');
        const inFolder = !!openFolderId.booklet;
        if (!bookletData.length && !strip && !inFolder) {
            // The call to action has to match what the plan actually allows:
            // "Make your first booklet" landing on the billing page is a
            // promise the free plan cannot keep.
            renderEmptyState(bookletListEl, 'booklet',
                'No booklets yet',
                'Pick a few specification points and get a revision guide written from the board’s own words, with a workbook to match.',
                bookletsAvailable() ? 'Make your first booklet' : 'Unlock booklets with Pro');
            return;
        }

        const grid = presViewMode === 'grid';
        bookletListEl.classList.toggle('res-grid', grid);
        bookletListEl.innerHTML = '';
        if (strip) bookletListEl.appendChild(strip);
        const view = itemsInView('booklet', bookletData);
        if (!view) { bookletListEl.insertAdjacentHTML('beforeend', listSkeletonHTML()); return; }
        if (inFolder && !view.length) { bookletListEl.appendChild(folderEmptyEl('booklet')); return; }

        view.forEach((b) => {
            const item = document.createElement('div');
            // A workbook made on its own lives in the same list, told apart by
            // having no guide (include_guide = 0).
            const workbookOnly = Number(b.include_guide) === 0;
            const noun = workbookOnly ? 'workbook' : 'booklet';
            const done = Number(b.done_count) || 0;
            const total = Number(b.section_count) || 0;
            const unfinished = b.status !== 'done';
            const meta = [
                new Date(b.created_at).toLocaleDateString(),
                facetLine({ subject: b.subject, level: b.level, exam_board: b.exam_board }),
            ].filter(Boolean).join(' · ');
            const note = unfinished
                ? `<span class="bk-card-status">${esc(BOOKLET_STATUS_LABELS[b.status] || b.status)}</span>`
                : `<span class="bk-card-status is-done">${done} section${done === 1 ? '' : 's'}${workbookOnly ? ' of exam questions' : (b.include_workbook ? ' · with workbook' : '')}</span>`;
            // Only a finished booklet: a link and the shared library both carry
            // the sections that are written, and there is nothing to carry
            // until the generation is through.
            const shareBtn = unfinished ? '' : shareButtonHTML('booklet', b);
            const actions = `${shareBtn}${moveButtonHTML()}<button class="pres-delete-btn" title="Delete booklet">${TRASH_ICON}</button>`;

            item.className = grid ? 'pres-card-item res-card res-booklet' : 'pres-item';
            item.innerHTML = grid
                ? `<div class="res-cover">
                        <span class="res-kind">${LIST_ICONS.booklet}${workbookOnly ? 'Workbook' : 'Booklet'}</span>
                        <span class="res-count"><b>${total || '—'}</b> ${total === 1 ? 'section' : 'sections'}</span>
                   </div>
                   <div class="pres-card-body">
                        <div class="pres-item-title">${esc(b.title || 'Untitled ' + noun)}</div>
                        <div class="pres-item-meta" title="${escAttr(meta)}">${esc(meta)}</div>
                        ${note}
                        <div class="pres-card-actions">${actions}</div>
                   </div>`
                : `<div class="pres-item-main">
                        <div class="pres-item-title">${esc(b.title || 'Untitled ' + noun)}</div>
                        <div class="pres-item-meta">${esc(meta)}</div>
                        ${note}
                   </div>
                   ${actions}`;

            bindShareButton(item, 'booklet', b);
            item.querySelector('.pres-delete-btn')?.addEventListener('click', async (e) => {
                e.stopPropagation();
                const ok = await showConfirm({
                    title: `Delete ${noun}?`,
                    message: workbookOnly
                        ? `"${b.title || 'This workbook'}" will be permanently deleted. This can’t be undone.`
                        : `"${b.title || 'This booklet'}" and its workbook will be permanently deleted. This can’t be undone.`,
                    confirmLabel: 'Delete',
                    danger: true,
                });
                if (!ok) return;
                try {
                    const r = await apiFetch(`/api/booklets/${b.id}`, { method: 'DELETE' });
                    if (r.ok) {
                        bookletData = bookletData.filter(x => x.id !== b.id);
                        if (folderItems.booklet) folderItems.booklet = folderItems.booklet.filter(x => x.id !== b.id);
                        dropFromFolderCount('booklet', b);
                        renderBooklets();
                    }
                    else setError(bookletListError, 'Delete failed');
                } catch { setError(bookletListError, 'Network error'); }
            });
            bindMoveButton(item, 'booklet', b);
            makeLibDraggable(item, 'booklet', b);
            markSelectable(item, 'booklet', b);
            item.addEventListener('click', (e) => {
                if (e.target.closest('.pres-delete-btn') || e.target.closest('.pres-share-btn') || e.target.closest('.pres-folder-btn')) return;
                openBooklet(b.id);
            });
            bookletListEl.appendChild(item);
        });
    }

    // ── Library folders ───────────────────────────────────
    // One level of named folders inside each library tab (routes/folders.js).
    // A tab opens on its folders and its loose items; opening a folder narrows
    // it to what is filed there. Presentations and booklets arrive a page at a
    // time, so a folder of those is fetched on opening rather than filtered
    // out of the pages already loaded; worksheets, quizzes and marking sets are
    // whole lists and are filtered here. Items are filed from the Move to
    // folder button on each card, or dragged onto a folder.
    const FOLDER_KIND_OF_TAB = {
        presentations: 'presentation', worksheets: 'worksheet', booklets: 'booklet',
        quizzes: 'quiz', marking: 'marking',
    };
    const FOLDER_ROOT_LABEL = {
        presentation: 'Presentations', worksheet: 'Worksheets', booklet: 'Booklets',
        quiz: 'Quizzes', marking: 'Marking',
    };
    const FOLDER_PATH = 'M3 7.5A2.5 2.5 0 0 1 5.5 5h3.6l2.1 2.2h7.3A2.5 2.5 0 0 1 21 9.7v7.8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z';
    const FOLDER_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="${FOLDER_PATH}"/></svg>`;
    const FOLDER_PLUS_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="${FOLDER_PATH}"/><path d="M12 11v5M9.5 13.5h5"/></svg>`;
    const FOLDER_OUT_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-3"/></svg>';
    const RENAME_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>';
    // The big manila folder on a folder card: filled, inked, with a tab.
    const FOLDER_CARD_SVG = '<svg class="lib-folder-art" viewBox="0 0 48 38" aria-hidden="true" focusable="false"><path class="lib-folder-back" d="M3 7a4 4 0 0 1 4-4h11l4 4.5h19a4 4 0 0 1 4 4V31a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z"/><path class="lib-folder-front" d="M3 14.5a3.5 3.5 0 0 1 3.5-3.5h35a3.5 3.5 0 0 1 3.5 3.5V31a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z"/></svg>';

    const libFolders = {};      // kind → [{ id, name, item_count }]
    const openFolderId = {};    // kind → the open folder's id, or null
    const folderItems = {};     // kind → the open folder's items (paged kinds only)
    let libTab = 'presentations';
    const SELECTABLE_TABS = new Set(['presentations', 'worksheets', 'booklets', 'quizzes']);
    const libSelection = new Map();     // Select mode's ticks: 'kind:id' → { kind, id, title }
    let libSelecting = false;
    let creatingFolder = null;  // { kind, fileAfter } while the new-folder card is up
    let dragging = null;        // { kind, item } during a drag
    const canDrag = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const folderById = (kind, id) => (libFolders[kind] || []).find(f => f.id === id) || null;
    const isPagedKind = (kind) => kind === 'presentation' || kind === 'booklet';

    function kindData(kind) {
        if (kind === 'presentation') return presData;
        if (kind === 'worksheet') return wsData;
        if (kind === 'booklet') return bookletData;
        if (kind === 'quiz') return quizData;
        if (kind === 'marking') return markingSetsData;
        return [];
    }

    function rerenderKind(kind) {
        if (kind === 'presentation') { if (presLoaded) renderPresentations(); }
        else if (kind === 'worksheet') renderWorksheets();
        else if (kind === 'booklet') renderBooklets();
        else if (kind === 'quiz') renderQuizzes();
        else if (kind === 'marking') renderMarkingSets();
        if (FOLDER_KIND_OF_TAB[libTab] === kind) renderLibHead();
    }

    async function loadFolders(kind) {
        try {
            const r = await apiFetch(`/api/folders?kind=${kind}`);
            const d = await r.json();
            if (r.ok) libFolders[kind] = d.folders || [];
        } catch { /* the tab still works as one flat list */ }
    }

    // Once per visit to the library, so counts made elsewhere are current.
    async function loadAllFolders() {
        const kinds = Object.values(FOLDER_KIND_OF_TAB);
        await Promise.all(kinds.map(loadFolders));
        kinds.forEach(kind => {
            // A folder deleted in another tab of the browser: back to the top.
            if (openFolderId[kind] && !folderById(kind, openFolderId[kind])) openFolderId[kind] = null;
            rerenderKind(kind);
        });
    }

    /**
     * What a list should draw right now: the open folder's items, the loose
     * ones at the top of the tab, or null while a folder is still loading.
     */
    function itemsInView(kind, all) {
        const id = openFolderId[kind];
        if (!id) return all.filter(x => !x.folder_id);
        if (isPagedKind(kind)) return folderItems[kind];
        return all.filter(x => x.folder_id === id);
    }

    async function openFolder(kind, id) {
        openFolderId[kind] = id || null;
        creatingFolder = null;
        if (id && isPagedKind(kind)) {
            folderItems[kind] = null;
            rerenderKind(kind);
            const url = kind === 'presentation'
                ? `/api/presentations?limit=100&folder=${encodeURIComponent(id)}`
                : `/api/booklets?limit=100&folder=${encodeURIComponent(id)}`;
            try {
                const r = await apiFetch(url);
                folderItems[kind] = r.ok ? await r.json() : [];
            } catch { folderItems[kind] = []; }
            if (openFolderId[kind] !== id) return;   // they moved on while it loaded
        }
        rerenderKind(kind);
    }

    /**
     * The start of a list when a tab is open at its top: its folders, plus the
     * new-folder card while one is being named. Null when there is nothing to
     * show, or inside a folder.
     */
    function folderStripEl(kind) {
        if (openFolderId[kind]) return null;
        const folders = libFolders[kind] || [];
        const naming = creatingFolder && creatingFolder.kind === kind;
        if (!folders.length && !naming) return null;
        const strip = document.createElement('div');
        strip.className = 'lib-folders';
        if (naming) strip.appendChild(newFolderCard(kind));
        folders.forEach(f => strip.appendChild(folderCard(kind, f)));
        return strip;
    }

    function folderCard(kind, f) {
        const n = Number(f.item_count) || 0;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lib-folder';
        b.innerHTML = `${FOLDER_CARD_SVG}
            <span class="lib-folder-text">
                <span class="lib-folder-name">${esc(f.name)}</span>
                <span class="lib-folder-count">${n ? `${n} item${n === 1 ? '' : 's'}` : 'Empty'}</span>
            </span>`;
        b.addEventListener('click', () => openFolder(kind, f.id));
        makeDropTarget(b, kind, f.id);
        return b;
    }

    // Named in place rather than in a dialog: the card appears where the
    // folder will be, with the cursor in it. Enter or clicking away keeps it,
    // Escape (or leaving it blank) doesn't.
    function newFolderCard(kind) {
        const form = document.createElement('form');
        form.className = 'lib-folder lib-folder-new';
        form.innerHTML = `${FOLDER_CARD_SVG}
            <span class="lib-folder-text">
                <input class="lib-folder-input" maxlength="60" placeholder="Name this folder" aria-label="New folder name" autocomplete="off">
                <span class="lib-folder-count">Enter to save · Esc to cancel</span>
            </span>`;
        const input = form.querySelector('input');
        let settled = false;
        const settle = async (keep) => {
            if (settled) return;
            settled = true;
            const fileAfter = creatingFolder && creatingFolder.fileAfter;
            creatingFolder = null;
            const name = input.value.trim();
            if (!keep || !name) { rerenderKind(kind); return; }
            const folder = await createFolder(kind, name);
            if (folder && fileAfter) await moveItem(kind, fileAfter, folder.id);
        };
        form.addEventListener('submit', (e) => { e.preventDefault(); settle(true); });
        input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); settle(false); } });
        input.addEventListener('blur', () => settle(true));
        requestAnimationFrame(() => input.focus());
        return form;
    }

    // `fileAfter`: an item to file into the folder once it exists – the "New
    // folder…" at the bottom of the Move menu.
    function startNewFolder(kind, fileAfter) {
        creatingFolder = { kind, fileAfter: fileAfter || null };
        openFolderId[kind] = null;
        rerenderKind(kind);
    }

    async function createFolder(kind, name) {
        try {
            const r = await apiFetch('/api/folders', { method: 'POST', body: JSON.stringify({ kind, name }) });
            const d = await r.json();
            if (!r.ok) { showToast(d.error || 'Couldn’t make that folder'); rerenderKind(kind); return null; }
            libFolders[kind] = [...(libFolders[kind] || []), d.folder]
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
            rerenderKind(kind);
            return d.folder;
        } catch {
            showToast('Network error');
            rerenderKind(kind);
            return null;
        }
    }

    async function moveItem(kind, item, folderId) {
        const from = item.folder_id || null;
        if (from === (folderId || null)) return;
        try {
            const r = await apiFetch('/api/folders/move', {
                method: 'POST',
                body: JSON.stringify({ kind, itemId: item.id, folderId: folderId || null }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                showToast(d.error || 'Couldn’t move that');
                return;
            }
        } catch { showToast('Network error'); return; }

        // Every copy held of it: the full list, and an open folder's page.
        kindData(kind).forEach(x => { if (x.id === item.id) x.folder_id = folderId || null; });
        item.folder_id = folderId || null;
        if (folderItems[kind]) folderItems[kind] = folderItems[kind].filter(x => x.id !== item.id);
        const src = folderById(kind, from);
        const dest = folderById(kind, folderId);
        if (src) src.item_count = Math.max(0, (Number(src.item_count) || 0) - 1);
        if (dest) dest.item_count = (Number(dest.item_count) || 0) + 1;
        showToast(dest ? `Moved to ${dest.name}` : `Moved back to ${FOLDER_ROOT_LABEL[kind]}`);
        rerenderKind(kind);
    }

    // A deleted item no longer counts towards its folder.
    function dropFromFolderCount(kind, item) {
        const f = folderById(kind, item.folder_id);
        if (f) f.item_count = Math.max(0, (Number(f.item_count) || 0) - 1);
    }

    // The Move to folder menu for one item: every other folder of its kind,
    // out of the folder it is in, or a new folder to put it in.
    function openMoveMenu(anchor, kind, item) {
        const entries = (libFolders[kind] || [])
            .filter(f => f.id !== item.folder_id)
            .map(f => ({ label: f.name, icon: FOLDER_ICON, onClick: () => moveItem(kind, item, f.id) }));
        if (item.folder_id) {
            entries.unshift({ label: `Back to ${FOLDER_ROOT_LABEL[kind]}`, icon: FOLDER_OUT_ICON, onClick: () => moveItem(kind, item, null) });
        }
        entries.push({ label: 'New folder…', icon: FOLDER_PLUS_ICON, onClick: () => startNewFolder(kind, item) });
        openCardMenu(anchor, entries);
    }

    // The small folder button in a card's or row's own action buttons.
    function moveButtonHTML() {
        return `<button class="pres-item-btn pres-folder-btn" title="Move to folder" aria-label="Move to folder" aria-haspopup="menu" aria-expanded="false">${FOLDER_ICON}</button>`;
    }
    function bindMoveButton(el, kind, item) {
        const btn = el.querySelector('.pres-folder-btn');
        if (!btn) return;
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            openMoveMenu(btn, kind, item);
        });
    }

    function makeLibDraggable(el, kind, item) {
        if (!canDrag) return;
        el.draggable = true;
        el.addEventListener('dragstart', (e) => {
            dragging = { kind, item };
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', item.title || item.id);
            el.classList.add('is-dragging');
            document.body.classList.add('lib-dragging');
        });
        el.addEventListener('dragend', () => {
            dragging = null;
            el.classList.remove('is-dragging');
            document.body.classList.remove('lib-dragging');
            document.querySelectorAll('.is-drop').forEach(t => t.classList.remove('is-drop'));
        });
    }

    // ── Select mode: several things in one link ───────────
    // Select turns a click on a card into a tick instead of opening it, on
    // every tab that holds something to send. What is ticked is kept across
    // tabs and folders, so a deck, its worksheet and a quiz can go in one link
    // (routes/bundles.js). Each list tags its cards with markSelectable; one
    // capture-phase listener on the panel does the rest, so the cards' own
    // click handlers never run while selecting.

    // SELECTABLE_TABS, libSelection and libSelecting are declared with libTab
    // above: renderLibHead reads them, and can run before this point.

    function markSelectable(el, kind, item) {
        // A deck or booklet still generating has nothing in it to send yet.
        if ((kind === 'presentation' || kind === 'booklet') && item.status !== 'done') {
            el.dataset.selBlocked = '1';
            return;
        }
        el.dataset.selKind = kind;
        el.dataset.selId = item.id;
        el.dataset.selTitle = item.title || item.topic || '';
        const on = libSelection.has(`${kind}:${item.id}`);
        el.classList.toggle('is-selected', on);
        if (libSelecting) el.setAttribute('aria-pressed', String(on));
    }

    function paintLibSelection() {
        const n = libSelection.size;
        if (!$('lib-select-bar')) return;
        $('lib-select-count').textContent = n ? `${n} selected` : 'Pick what to send';
        $('lib-select-send').disabled = !n;
        document.querySelectorAll('#lib-panel [data-sel-kind]').forEach(el => {
            const on = libSelection.has(`${el.dataset.selKind}:${el.dataset.selId}`);
            el.classList.toggle('is-selected', on);
            if (libSelecting) el.setAttribute('aria-pressed', String(on));
            else el.removeAttribute('aria-pressed');
        });
    }

    function setLibSelecting(on) {
        libSelecting = on;
        if (!on) libSelection.clear();
        $('lib-panel')?.classList.toggle('lib-selecting', on);
        syncLibSelectBtn();
        paintLibSelection();
    }

    // Called from renderLibHead, which runs on every tab change.
    function syncLibSelectBtn() {
        const btn = $('lib-select');
        // While selecting, the bar's Cancel is the way out.
        if (btn) btn.hidden = !SELECTABLE_TABS.has(libTab) || libSelecting;
        const links = $('lib-links');
        if (links) links.hidden = !SELECTABLE_TABS.has(libTab);
        const bar = $('lib-select-bar');
        if (bar) bar.hidden = !libSelecting || !SELECTABLE_TABS.has(libTab);
    }

    $('lib-select')?.addEventListener('click', () => setLibSelecting(!libSelecting));
    $('lib-select-cancel')?.addEventListener('click', () => setLibSelecting(false));
    $('lib-select-send')?.addEventListener('click', () => openBundleModal());

    $('lib-panel')?.addEventListener('click', (e) => {
        if (!libSelecting) return;
        const card = e.target.closest('[data-sel-kind], [data-sel-blocked]');
        if (!card) return;     // folders and crumbs still navigate
        e.preventDefault();
        e.stopPropagation();
        if (card.dataset.selBlocked) { showToast('That is still being made'); return; }
        const key = `${card.dataset.selKind}:${card.dataset.selId}`;
        if (libSelection.has(key)) libSelection.delete(key);
        else libSelection.set(key, { kind: card.dataset.selKind, id: card.dataset.selId, title: card.dataset.selTitle });
        paintLibSelection();
    }, true);

    // ── The link dialog ──
    const bundleModal = $('bundle-modal');
    let madeBundle = null;     // { id, url } once the link exists
    let bundleItems = [];      // what this link will carry
    let bundleFromSelection = false;

    const BUNDLE_KIND_LABEL = { presentation: 'Slides', worksheet: 'Worksheet', booklet: 'Booklet', quiz: 'Quiz' };
    const BUNDLE_KIND_PLURAL = { presentation: 'slides', worksheet: 'worksheets', booklet: 'booklets', quiz: 'quizzes' };

    // "Slides, 2 worksheets and a quiz": what is on a link, in a few words.
    function bundleSummary(items) {
        const counts = {};
        items.forEach(i => { counts[i.kind] = (counts[i.kind] || 0) + 1; });
        const parts = ['presentation', 'worksheet', 'booklet', 'quiz'].filter(k => counts[k]).map(k =>
            counts[k] === 1 ? (k === 'presentation' ? 'slides' : `a ${k}`) : `${counts[k]} ${BUNDLE_KIND_PLURAL[k]}`);
        const text = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : (parts[0] || '');
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    function bundleItemHTML(i) {
        return `<li><span class="bundle-icon bi-${i.kind}" aria-hidden="true">${LIST_ICONS[i.kind]}</span>`
            + `<span class="bundle-item-text"><span class="bundle-item-title">${esc(i.title || 'Untitled')}</span>`
            + `<span class="bundle-kind">${BUNDLE_KIND_LABEL[i.kind]}</span></span></li>`;
    }

    // From the Select bar with what is ticked, or from any one resource's
    // Share with just that one - the same link either way.
    function openBundleModal(items = null) {
        const picked = items || [...libSelection.values()];
        if (!picked.length || !bundleModal) return;
        madeBundle = null;
        bundleItems = picked;
        bundleFromSelection = !items;
        $('bundle-title').textContent = picked.length === 1 ? 'Send in a link' : `One link for ${picked.length} things`;
        $('bundle-items').innerHTML = picked.map(bundleItemHTML).join('');
        $('bundle-name').value = '';
        $('bundle-status').textContent = '';
        setError($('bundle-error'), '');
        show($('bundle-make'), $('bundle-create'));
        hide($('bundle-made'), $('bundle-stop'), $('bundle-done'));
        openModal('bundle-modal');
        document.addEventListener('keydown', bundleKeydown);
    }

    function closeBundleModal() {
        hide(bundleModal);
        document.removeEventListener('keydown', bundleKeydown);
        // Sent: the selection has done its job. Closed without sending: keep
        // it, so a teacher who opened this to check the list can add to it.
        if (madeBundle && bundleFromSelection) setLibSelecting(false);
    }
    function bundleKeydown(e) { if (e.key === 'Escape') closeBundleModal(); }

    bundleModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-bundle-close]')) closeBundleModal();
    });
    $('bundle-done')?.addEventListener('click', closeBundleModal);

    $('bundle-create')?.addEventListener('click', async () => {
        const btn = $('bundle-create');
        btn.disabled = true;
        btn.textContent = 'Making link…';
        setError($('bundle-error'), '');
        try {
            const res = await apiFetch('/api/bundles', {
                method: 'POST',
                body: JSON.stringify({
                    title: $('bundle-name').value,
                    items: bundleItems.map(({ kind, id }) => ({ kind, id })),
                }),
            });
            const data = await res.json();
            if (!res.ok) { setError($('bundle-error'), data.error || 'Could not make the link.'); return; }
            madeBundle = { id: data.id, url: window.location.origin + data.url };
            $('bundle-url').value = madeBundle.url;
            hide($('bundle-make'), btn);
            show($('bundle-made'), $('bundle-stop'), $('bundle-done'));
            $('bundle-copy').focus();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError($('bundle-error'), 'Network error');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Make link';
        }
    });

    $('bundle-copy')?.addEventListener('click', () => {
        if (!madeBundle) return;
        navigator.clipboard.writeText(madeBundle.url).then(() => {
            $('bundle-status').textContent = 'Link copied';
            setTimeout(() => { if ($('bundle-status').textContent === 'Link copied') $('bundle-status').textContent = ''; }, 2000);
        });
    });

    // Made by mistake, or sent to the wrong group: undone while the dialog is
    // still up, before anyone has had time to open it.
    $('bundle-stop')?.addEventListener('click', async () => {
        if (!madeBundle) return;
        try {
            const res = await apiFetch(`/api/bundles/${madeBundle.id}`, { method: 'DELETE' });
            if (!res.ok && res.status !== 404) { $('bundle-status').textContent = 'Could not stop it'; return; }
            madeBundle = null;
            hide(bundleModal);
            document.removeEventListener('keydown', bundleKeydown);
            showToast('Link stopped');
        } catch { $('bundle-status').textContent = 'Could not stop it'; }
    });

    // ── Sent links: every bundle made, and a way to stop each ──
    const linksModal = $('links-modal');

    function linksKeydown(e) { if (e.key === 'Escape') closeLinksModal(); }
    function closeLinksModal() {
        hide(linksModal);
        document.removeEventListener('keydown', linksKeydown);
    }

    async function openLinksModal() {
        if (!linksModal) return;
        const list = $('links-list');
        list.innerHTML = listSkeletonHTML();
        $('links-status').textContent = '';
        setError($('links-error'), '');
        show(linksModal);
        document.addEventListener('keydown', linksKeydown);
        try {
            const res = await apiFetch('/api/bundles');
            const data = await res.json();
            if (!res.ok) { list.innerHTML = ''; setError($('links-error'), data.error || 'Could not load your links.'); return; }
            renderLinks(data.bundles || []);
        } catch (err) {
            list.innerHTML = '';
            if (err.message !== 'Not authenticated') setError($('links-error'), 'Network error');
        }
    }

    function renderLinks(bundles) {
        const list = $('links-list');
        if (!bundles.length) {
            list.innerHTML = '<li class="links-empty">Nothing sent yet. Use <b>Share</b> on anything to send it as a link, or <b>Select</b> to put a few things in one.</li>';
            return;
        }
        list.innerHTML = '';
        bundles.forEach(b => {
            const url = window.location.origin + b.url;
            const titles = b.items.map(i => i.title || 'Untitled').join(', ');
            const made = new Date(b.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
            const what = b.items.length ? bundleSummary(b.items) : 'Everything on it has been deleted';
            const li = document.createElement('li');
            li.className = 'links-row';
            li.innerHTML = `
                <div class="links-main">
                    <a class="links-name" href="${escAttr(url)}" target="_blank" rel="noopener" title="${escAttr(titles)}">${esc(b.title || titles || 'Untitled link')}</a>
                    <div class="links-meta">${esc(what)} · ${esc(made)}</div>
                </div>
                <div class="links-actions">
                    <button type="button" class="btn btn-secondary btn-sm" data-act="copy">Copy</button>
                    <button type="button" class="btn btn-ghost btn-sm links-stop" data-act="stop">Stop</button>
                </div>`;
            li.querySelector('[data-act="copy"]').addEventListener('click', () => {
                navigator.clipboard.writeText(url).then(() => {
                    $('links-status').textContent = 'Link copied';
                    setTimeout(() => { if ($('links-status').textContent === 'Link copied') $('links-status').textContent = ''; }, 2000);
                });
            });
            li.querySelector('[data-act="stop"]').addEventListener('click', async () => {
                const ok = await showConfirm({
                    title: 'Stop this link?',
                    message: 'Anyone who opens it will be told it has been stopped. Your resources are not changed, and quiz answers already in are kept.',
                    confirmLabel: 'Stop link',
                    danger: true,
                });
                if (!ok) return;
                try {
                    const r = await apiFetch(`/api/bundles/${b.id}`, { method: 'DELETE' });
                    if (!r.ok && r.status !== 404) { $('links-status').textContent = 'Could not stop it'; return; }
                    bundles = bundles.filter(x => x.id !== b.id);
                    renderLinks(bundles);
                    $('links-status').textContent = 'Link stopped';
                } catch { $('links-status').textContent = 'Could not stop it'; }
            });
            list.appendChild(li);
        });
    }

    $('lib-links')?.addEventListener('click', openLinksModal);
    linksModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-links-close]')) closeLinksModal();
    });

    // folderId null: the tab's root crumb, which lifts an item out of a folder.
    function makeDropTarget(el, kind, folderId) {
        const accepts = () => dragging && dragging.kind === kind
            && (dragging.item.folder_id || null) !== (folderId || null);
        el.addEventListener('dragover', (e) => {
            if (!accepts()) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            el.classList.add('is-drop');
        });
        el.addEventListener('dragleave', () => el.classList.remove('is-drop'));
        el.addEventListener('drop', (e) => {
            el.classList.remove('is-drop');
            if (!accepts()) return;
            e.preventDefault();
            const { item } = dragging;
            moveItem(kind, item, folderId);
        });
    }

    // Inside a folder with nothing in it.
    function folderEmptyEl(kind) {
        const p = document.createElement('div');
        p.className = 'lib-folder-empty';
        p.innerHTML = `<b>Nothing in here yet</b>
            <span>Use the ${FOLDER_ICON} button on anything in ${esc(FOLDER_ROOT_LABEL[kind])}${canDrag ? ', or drag it onto this folder' : ''}.</span>`;
        return p;
    }

    function renderLibHead() {
        syncLibSelectBtn();
        const crumbs = $('lib-crumbs');
        const newBtn = $('lib-new-folder');
        if (!crumbs) return;
        const kind = FOLDER_KIND_OF_TAB[libTab];
        // Published and Shared open with a heading of their own.
        crumbs.hidden = !kind;
        if (newBtn) newBtn.hidden = !kind || !!openFolderId[kind];
        if (!kind) return;

        const folder = openFolderId[kind] ? folderById(kind, openFolderId[kind]) : null;
        // In a presentations folder, the one-level-down answer to New folder:
        // fill this one with a lesson series.
        const seriesBtn = $('lib-folder-series');
        if (seriesBtn) {
            seriesBtn.hidden = !(folder && kind === 'presentation');
            const label = seriesBtn.querySelector('span');
            if (label) label.textContent = folder && state.schemeByFolder?.has(folder.id) ? 'Open lesson series' : 'Plan a lesson series';
        }
        crumbs.innerHTML = '';
        const root = document.createElement(folder ? 'button' : 'span');
        root.className = 'lib-crumb' + (folder ? ' lib-crumb-link' : '');
        root.innerHTML = `<svg class="lib-crumb-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${FOLDER_PATH}"/></svg><span>${esc(FOLDER_ROOT_LABEL[kind])}</span>`;
        if (folder) {
            root.type = 'button';
            root.addEventListener('click', () => openFolder(kind, null));
            makeDropTarget(root, kind, null);
        } else {
            root.setAttribute('aria-current', 'page');
        }
        crumbs.appendChild(root);
        if (!folder) return;

        const sep = document.createElement('span');
        sep.className = 'lib-crumb-sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '›';
        crumbs.appendChild(sep);

        const here = document.createElement('button');
        here.type = 'button';
        here.className = 'lib-crumb lib-crumb-here';
        here.setAttribute('aria-current', 'page');
        here.setAttribute('aria-haspopup', 'menu');
        here.setAttribute('aria-expanded', 'false');
        here.innerHTML = `<span>${esc(folder.name)}</span><span class="lib-crumb-caret" aria-hidden="true">▾</span>`;
        here.addEventListener('click', (e) => {
            e.stopPropagation();
            openCardMenu(here, [
                { label: 'Rename folder', icon: RENAME_ICON, onClick: () => renameFolderInline(kind, folder) },
                { label: 'Delete folder', icon: TRASH_ICON, danger: true, onClick: () => deleteFolder(kind, folder) },
            ]);
        });
        crumbs.appendChild(here);
    }

    $('lib-folder-series')?.addEventListener('click', () => {
        const folder = folderById('presentation', openFolderId.presentation);
        if (!folder) return;
        if (state.schemeByFolder?.has(folder.id)) openScheme(state.schemeByFolder.get(folder.id));
        else showSeriesPage({ folder });
    });

    function renameFolderInline(kind, folder) {
        const here = document.querySelector('#lib-crumbs .lib-crumb-here');
        if (!here) return;
        const form = document.createElement('form');
        form.className = 'lib-crumb-rename';
        form.innerHTML = '<input class="lib-folder-input" maxlength="60" aria-label="Folder name" autocomplete="off">';
        const input = form.querySelector('input');
        input.value = folder.name;
        here.replaceWith(form);
        input.focus();
        input.select();
        let settled = false;
        const settle = async (keep) => {
            if (settled) return;
            settled = true;
            const name = input.value.trim();
            if (keep && name && name !== folder.name) {
                try {
                    const r = await apiFetch(`/api/folders/${folder.id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
                    const d = await r.json();
                    if (r.ok) folder.name = d.name;
                    else showToast(d.error || 'Couldn’t rename that');
                } catch { showToast('Network error'); }
            }
            renderLibHead();
        };
        form.addEventListener('submit', (e) => { e.preventDefault(); settle(true); });
        input.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); settle(false); } });
        input.addEventListener('blur', () => settle(true));
    }

    async function deleteFolder(kind, folder) {
        const n = Number(folder.item_count) || 0;
        const ok = await showConfirm({
            title: `Delete “${folder.name}”?`,
            message: n
                ? `The folder goes, and the ${n} thing${n === 1 ? '' : 's'} in it move${n === 1 ? 's' : ''} back to ${FOLDER_ROOT_LABEL[kind]}. Nothing inside is deleted.`
                : 'It’s empty, so nothing else changes.',
            confirmLabel: 'Delete folder',
            danger: true,
        });
        if (!ok) return;
        try {
            const r = await apiFetch(`/api/folders/${folder.id}`, { method: 'DELETE' });
            if (!r.ok && r.status !== 404) { showToast('Couldn’t delete that folder'); return; }
        } catch { showToast('Network error'); return; }
        kindData(kind).forEach(x => { if (x.folder_id === folder.id) x.folder_id = null; });
        libFolders[kind] = (libFolders[kind] || []).filter(f => f.id !== folder.id);
        openFolderId[kind] = null;
        showToast(`Deleted ${folder.name}`);
        rerenderKind(kind);
    }

    $('lib-new-folder')?.addEventListener('click', () => {
        const kind = FOLDER_KIND_OF_TAB[libTab];
        if (kind) startNewFolder(kind);
    });

    // ── Presentations list ────────────────────────────────
    const PRES_PAGE_SIZE = 20;
    let presData = [];
    let presOffset = 0;
    let presHasMore = false;
    let presLoaded = false;
    let wsData = [];
    let quizData = [];
    // Render only once the fetch has landed, so a view-toggle click mid-load
    // can't swap the skeleton for an empty state.
    let wsLoaded = false;
    let quizLoaded = false;
    let presViewMode = localStorage.getItem('presViewMode') || 'grid';
    let mktData = [];
    let mktLoaded = false;

    // Rough minutes saved per resource vs. building it by hand.
    // A deck by hand ~45m, a worksheet + answer key ~30-35m, a quiz ~25m;
    // the app takes a couple of minutes, so these are the net time given back.
    const SAVE_MINUTES = { presentation: 40, worksheet: 30, quiz: 25 };

    // Turn a saved-hours figure into a relatable chunk of a teacher's week.
    function humanizeSaved(mins) {
        if (mins < 45)  return 'a coffee break';
        if (mins < 90)  return 'a free period';
        if (mins < 180) return 'a couple of free periods';
        if (mins < 300) return 'a whole planning afternoon';
        if (mins < 480) return 'a Sunday afternoon';
        if (mins < 780) return 'a full working day';
        if (mins < 1440) return 'a couple of days off';
        return 'days of planning';
    }

    // Tally items by subject, most-made first, for the hover breakdowns.
    function subjectBreakdown(items) {
        const m = new Map();
        items.forEach(it => {
            const s = (it.subject || '').trim() || 'No subject set';
            m.set(s, (m.get(s) || 0) + 1);
        });
        return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    }

    function fillBreakdown(popId, label, items) {
        const pop = $(popId);
        if (!pop) return;
        const rows = subjectBreakdown(items);
        const body = rows.length
            ? `<ul class="tt-pop-list">${rows.map(([s, n]) =>
                  `<li><span>${esc(s)}</span><b>${n}</b></li>`).join('')}</ul>`
            : `<p class="tt-pop-empty">None yet</p>`;
        pop.innerHTML = `<span class="tt-pop-title">${label} by subject</span>${body}`;
    }

    // The dashboard's "Watch the guide": the whole platform in one video, cut
    // into chapters so a teacher can go straight to the part they are stuck on.
    const PRODUCTIVITY_GUIDE = {
        src: '/video/productivity-guide.mp4',
        poster: '/video/productivity-guide.jpg',
        subtitles: '/video/productivity-guide.vtt',
        length: 977.8,
        chapters: [
            [0, 'Introduction'],
            [14, 'Presentations'],
            [67, 'Worksheets'],
            [237, 'Booklets'],
            [287, 'Set a booklet as a lesson'],
            [395, 'Pupils and their work'],
            [461, 'Marking long answers'],
            [587, 'Live quizzes'],
            [657, 'Quiz results and follow-ups'],
            [717, 'The Marketplace'],
            [778, 'Sharing with colleagues'],
            [827, 'Classes and pupil logins'],
            [947, 'Inviting your team'],
            [964, 'Wrapping up'],
        ],
    };
    $('dash-guide-btn')?.addEventListener('click', () => window.GuideVideo.open(PRODUCTIVITY_GUIDE, 'Guide to BlueAcorn Productivity'));

    function renderTimeSaved() {
        const tape = $('time-saved');
        if (!tape) return;
        const main     = tape.querySelector('.tt-main');
        const perf     = tape.querySelector('.tt-perf');
        const counts   = $('ts-counts');
        const empty    = $('ts-empty');
        const figureEl = $('ts-figure');
        const unitEl   = $('ts-unit');

        const p = presData.length, w = wsData.length, q = quizData.length;
        const count = p + w + q;

        // Nothing made yet – a quiet invitation, no numbers.
        if (!count) {
            hide(main, perf, counts);
            show(empty);
            return;
        }
        show(main, perf, counts);
        hide(empty);

        const mins = p * SAVE_MINUTES.presentation
                   + w * SAVE_MINUTES.worksheet
                   + q * SAVE_MINUTES.quiz;

        let target, unit, suffix = '';
        if (mins < 90) {
            target = mins;
            unit = mins === 1 ? 'min' : 'mins';
        } else {
            const halves = Math.round(mins / 30);   // count in half-hours
            target = Math.floor(halves / 2);
            suffix = halves % 2 ? '½' : '';    // half-hour
            unit = 'hrs';
        }
        const figStr = target + suffix;

        // Honest note on the total (hover the figure): how the estimate is made.
        if (main) {
            main.title = `About ${figStr} ${unit === 'mins' ? 'minutes' : 'hours'} saved – roughly `
                       + `${humanizeSaved(mins)} back.`;
        }

        const setText = (id, v) => { const el = $(id); if (el) el.textContent = v; };
        setText('ts-c-pres', p);
        setText('ts-c-ws', w);
        setText('ts-c-quiz', q);
        if (unitEl) unitEl.textContent = unit;

        // Per-type subject breakdowns, shown on hover/focus of each count.
        fillBreakdown('ts-pop-pres', 'Presentations', presData);
        fillBreakdown('ts-pop-ws',   'Worksheets',    wsData);
        fillBreakdown('ts-pop-quiz', 'Quizzes',       quizData);

        // Tick the total up on load.
        const setFinal = () => { if (figureEl) figureEl.textContent = figStr; };
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || target <= 1 || !figureEl) { setFinal(); return; }

        const dur = 800, t0 = performance.now();
        (function step(now) {
            const k = Math.min(1, (now - t0) / dur);
            const eased = 1 - Math.pow(1 - k, 3);
            figureEl.textContent = Math.round(eased * target);
            if (k < 1) requestAnimationFrame(step);
            else setFinal();
        })(performance.now());
    }

    function updatePresViewToggle() {
        document.querySelectorAll('#pres-view-toggle .view-toggle-btn').forEach(b =>
            b.classList.toggle('active', b.dataset.view === presViewMode));
    }
    updatePresViewToggle();

    document.querySelectorAll('#pres-view-toggle .view-toggle-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            if (presViewMode === btn.dataset.view) return;
            presViewMode = btn.dataset.view;
            localStorage.setItem('presViewMode', presViewMode);
            updatePresViewToggle();
            // One preference across the library, so re-render whatever is on show.
            renderPresentations();
            renderWorksheets();
            renderQuizzes();
        });
    });

    // Slides are authored at a fixed 960x540 and positioned absolutely, so a
    // card has no real height until this runs. When the card's own container is
    // hidden (a background dashboard tab, an admin panel not yet shown) its
    // width reads as 0, and skipping silently – the old behaviour – left the
    // slide at whatever transform it last had, or none at all: full 960px
    // width clipped by the card's `overflow: hidden`, which is the "oversized,
    // zoomed-in corner" look. Only the render path is guarded here; becoming
    // visible again is handled by the ResizeObserver below, the same fix
    // applied to the landing page's Marketplace strip for the same root cause.
    function scalePresPreviews() {
        document.querySelectorAll('.pres-preview-slide').forEach(inner => {
            const w = inner.parentElement.clientWidth;
            if (w) inner.style.transform = `scale(${w / 960})`;
        });
    }
    let presScaleTimer = null;
    function schedulePresScale() {
        clearTimeout(presScaleTimer);
        presScaleTimer = setTimeout(scalePresPreviews, 120);
    }
    window.addEventListener('resize', schedulePresScale);

    // Each of these is a dashboard pane that toggles `hidden` rather than being
    // unmounted, so a list rendered while its tab was in the background never
    // got a resize event to fix it later. Observing the panes themselves is
    // what catches that: a display:none → visible transition changes an
    // element's own box size from 0×0 to real, which fires the observer just
    // as reliably as a window resize.
    const presScaleObserver = ('ResizeObserver' in window) ? new ResizeObserver(schedulePresScale) : null;
    ['pres-list', 'mkt-list'].forEach(id => {
        const el = $(id);
        if (el && presScaleObserver) presScaleObserver.observe(el);
    });

    // A deck taken from the Marketplace keeps its credit in your library. The
    // author is a snapshot taken at clone time, so it survives the listing being
    // pulled; decks you made yourself carry no source and render nothing.
    function sourceCredit(pres) {
        if (!pres.source_author) return '';
        return `<div class="pres-source-credit" title="${escAttr('From the Marketplace, by ' + pres.source_author)}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span>From ${esc(pres.source_author)}</span>
        </div>`;
    }

    function presPreviewHTML(pres) {
        if (pres.first_slide_html) {
            return `<div class="pres-preview"><div class="pres-preview-slide" data-theme="${esc(pres.theme || 'classic')}"></div></div>`;
        }
        return `<div class="pres-preview pres-preview-empty"><span aria-hidden="true">${LIST_ICONS.presentation}</span></div>`;
    }

    function fillPresPreview(item, pres) {
        const inner = item.querySelector('.pres-preview-slide');
        if (inner && pres.first_slide_html) inner.innerHTML = pres.first_slide_html;
    }

    function bindPresItem(item, pres) {
        const genWs = item.querySelector('.btn-gen-ws');
        if (genWs) genWs.addEventListener('click', (e) => {
            e.stopPropagation();
            showWorksheetPage(pres.topic || '', 'list', pres.exam_board, pres.subject, pres.level, pres.id);
        });
        const genQuiz = item.querySelector('.btn-gen-quiz');
        if (genQuiz) genQuiz.addEventListener('click', (e) => {
            e.stopPropagation();
            showQuizPage(pres.topic || '', 'list', pres.exam_board, pres.subject, pres.level, pres.id);
        });
        const publishBtn = item.querySelector('.btn-publish-mkt');
        if (publishBtn) publishBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            publishToMarketplace(pres);
        });
        bindShareButton(item, 'presentation', pres);

        async function deletePres() {
            const ok = await showConfirm({
                title: 'Delete presentation?',
                message: `"${pres.title || pres.topic}" will be permanently deleted. This can’t be undone.`,
                confirmLabel: 'Delete',
                danger: true
            });
            if (!ok) return;
            try {
                const r = await apiFetch('/api/presentations/' + pres.id, { method: 'DELETE' });
                if (r.ok) {
                    presData = presData.filter(p => p.id !== pres.id);
                    if (folderItems.presentation) folderItems.presentation = folderItems.presentation.filter(p => p.id !== pres.id);
                    const f = folderById('presentation', pres.folder_id);
                    if (f) f.item_count = Math.max(0, (Number(f.item_count) || 0) - 1);
                    renderPresentations();
                    renderTimeSaved();
                } else setError(presListError, 'Delete failed');
            } catch { setError(presListError, 'Network error'); }
        }

        // List rows have the width for inline buttons; grid cards route the same
        // actions through the overflow menu instead.
        item.querySelector('.pres-delete-btn')?.addEventListener('click', (e) => {
            e.stopPropagation();
            deletePres();
        });

        const menuBtn = item.querySelector('.card-menu-btn');
        if (menuBtn) menuBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            openCardMenu(menuBtn, [
                {
                    label: 'New worksheet', icon: LIST_ICONS.worksheet,
                    onClick: () => showWorksheetPage(pres.topic || '', 'list', pres.exam_board, pres.subject, pres.level, pres.id),
                },
                {
                    label: 'New quiz', icon: LIST_ICONS.quiz,
                    onClick: () => showQuizPage(pres.topic || '', 'list', pres.exam_board, pres.subject, pres.level, pres.id),
                },
                ...(pres.status === 'done' ? [{
                    label: 'Publish to Marketplace', icon: PUBLISH_ICON,
                    onClick: () => publishToMarketplace(pres),
                }] : []),
                ...(pres.status === 'done' ? [{
                    label: 'Send as a link', icon: LINK_ICON,
                    onClick: () => sendAsLink('presentation', pres.id, pres.title || pres.topic),
                }] : []),
                ...(pres.status === 'done' && state.user?.org ? [{
                    label: 'Share to org library', icon: SHARE_ICON,
                    onClick: () => shareToOrgLibrary('presentation', pres.id),
                }] : []),
                {
                    label: 'Move to folder…', icon: FOLDER_ICON,
                    onClick: () => openMoveMenu(menuBtn, 'presentation', pres),
                },
                { label: 'Delete', icon: TRASH_ICON, danger: true, onClick: deletePres },
            ]);
        });

        bindMoveButton(item, 'presentation', pres);
        makeLibDraggable(item, 'presentation', pres);
        markSelectable(item, 'presentation', pres);

        item.addEventListener('click', (e) => {
            if (e.target.closest('.pres-delete-btn') || e.target.closest('.card-menu-btn') || e.target.closest('.pres-share-btn') || e.target.closest('.pres-folder-btn')) return;
            openPresentation(pres.id);
        });
    }

    function renderPresentations() {
        const strip = folderStripEl('presentation');
        const inFolder = !!openFolderId.presentation;
        if (!presData.length && !strip && !inFolder) {
            presList.classList.remove('pres-grid');
            renderEmptyState(presList, 'presentation',
                'No presentations yet',
                'Type a topic like &ldquo;The French Revolution&rdquo; and get a finished deck to tweak.',
                'Make your first presentation');
            if (presLoadMoreBtn) presLoadMoreBtn.hidden = true;
            return;
        }
        // A folder is fetched whole; paging is for the top of the tab.
        if (presLoadMoreBtn) presLoadMoreBtn.hidden = !presHasMore || inFolder;

        presList.innerHTML = '';
        presList.classList.toggle('pres-grid', presViewMode === 'grid');
        if (strip) presList.appendChild(strip);
        const view = itemsInView('presentation', presData);
        if (!view) { presList.insertAdjacentHTML('beforeend', listSkeletonHTML()); return; }
        if (inFolder && !view.length) { presList.appendChild(folderEmptyEl('presentation')); return; }

        // A deck with companions is a lesson pack – the badge is how the
        // library shows that without a second row per resource.
        const packBadge = (pres) => {
            const n = Number(pres.companion_count) || 0;
            if (!n) return '';
            return `<span class="pack-badge" title="${n} resource${n === 1 ? '' : 's'} made from this deck">Pack · ${n + 1}</span>`;
        };

        view.forEach(pres => {
            const item = document.createElement('div');
            const statusBadge = pres.status !== 'done'
                ? `<span class="status-badge status-${pres.status}">${pres.status}</span>` : '';

            if (presViewMode === 'grid') {
                item.className = 'pres-card-item';
                // The menu sits over the thumbnail's corner rather than in a row
                // of its own: it was the only thing in that row, so the row was
                // mostly empty space with one small box pinned to the end of it.
                item.innerHTML = `
                    ${presPreviewHTML(pres)}
                    <button class="card-menu-btn card-menu-btn-float" title="More actions" aria-label="More actions" aria-haspopup="menu" aria-expanded="false">${DOTS_ICON}</button>
                    <div class="pres-card-body">
                        <div class="pres-item-title">${esc(pres.title || pres.topic)}</div>
                        ${sourceCredit(pres)}
                        <div class="pres-item-meta">${new Date(pres.created_at).toLocaleDateString()}${pres.slide_count ? ` · ${pres.slide_count} slide${pres.slide_count === 1 ? '' : 's'}` : ''}${statusBadge ? ' ' : ''}${statusBadge}${packBadge(pres)}</div>
                    </div>
                `;
            } else {
                item.className = 'pres-item';
                const listLead = pres.first_slide_html
                    ? `<div class="pres-preview pres-thumb"><div class="pres-preview-slide" data-theme="${esc(pres.theme || 'classic')}"></div></div>`
                    : `<span class="pres-item-icon pi-presentation" aria-hidden="true">${LIST_ICONS.presentation}</span>`;
                item.innerHTML = `
                    ${listLead}
                    <div style="flex:1;min-width:0">
                        <div class="pres-item-title">${esc(pres.title || pres.topic)}</div>
                        ${sourceCredit(pres)}
                        <div class="pres-item-meta">${new Date(pres.created_at).toLocaleDateString()}${pres.slide_count ? ` · ${pres.slide_count} slide${pres.slide_count === 1 ? '' : 's'}` : ''}${packBadge(pres)}</div>
                    </div>
                    ${statusBadge}
                    <button class="pres-item-btn btn-gen-ws" title="Create a worksheet from this presentation">+ Worksheet</button>
                    <button class="pres-item-btn btn-gen-quiz" title="Create a quiz from this presentation">+ Quiz</button>
                    ${pres.status === 'done' ? `<button class="pres-item-btn btn-publish-mkt" title="Publish to the Marketplace" aria-label="Publish to the Marketplace">${PUBLISH_ICON}</button>` : ''}
                    ${shareButtonHTML('presentation', pres)}
                    ${moveButtonHTML()}
                    <button class="pres-delete-btn" title="Delete presentation">${TRASH_ICON}</button>
                `;
            }
            fillPresPreview(item, pres);
            bindPresItem(item, pres);
            presList.appendChild(item);
        });
        requestAnimationFrame(scalePresPreviews);
    }

    async function loadPresentations() {
        presList.classList.remove('pres-grid');
        presList.innerHTML = listSkeletonHTML();
        setError(presListError, '');
        try {
            const res = await apiFetch(`/api/presentations?limit=${PRES_PAGE_SIZE}`);
            const data = await res.json();
            if (!res.ok) return setError(presListError, data.error || 'Failed to load');
            presData = data;
            presOffset = data.length;
            presHasMore = data.length === PRES_PAGE_SIZE;
            presLoaded = true;
            renderPresentations();
            renderTimeSaved();
            // Worksheet/quiz cards name their source deck, which needs this list.
            renderWorksheets();
            renderQuizzes();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(presListError, 'Network error');
        }
    }

    // Presentations page in blocks of PRES_PAGE_SIZE; older decks stay a click
    // away rather than piling everything into one request.
    async function loadMorePresentations() {
        if (!presLoadMoreBtn || presLoadMoreBtn.disabled) return;
        presLoadMoreBtn.disabled = true;
        presLoadMoreBtn.textContent = 'Loading…';
        try {
            const res = await apiFetch(`/api/presentations?limit=${PRES_PAGE_SIZE}&offset=${presOffset}`);
            const data = await res.json();
            if (!res.ok) return setError(presListError, data.error || 'Failed to load');
            presData = presData.concat(data);
            presOffset += data.length;
            presHasMore = data.length === PRES_PAGE_SIZE;
            renderPresentations();
        } catch {
            setError(presListError, 'Network error');
        } finally {
            presLoadMoreBtn.disabled = false;
            presLoadMoreBtn.textContent = 'Load more';
        }
    }

    presLoadMoreBtn?.addEventListener('click', loadMorePresentations);

    // ── Your publications ─────────────────────────────────
    // Browsing the Marketplace is not in here any more. It is a place with a
    // URL – /marketplace – server-rendered and the same page whether you are
    // signed in or not, so a link a teacher sends to the person next to them
    // opens the thing they were looking at. This pane keeps the half that is
    // genuinely personal: what you have published, what state each listing is
    // in, and the controls for updating or taking one down.
    const mktListEl   = $('mkt-list');
    const mktListError = $('mkt-list-error');
    const mktListSuccess = $('mkt-list-success');

    // ── Publish dialog ────────────────────────────────────
    // Publishing used to POST nothing but an id, which is why no listing has a
    // description and search had only auto-generated titles to match. The same
    // dialog serves the first publish and every later update, so a listing's
    // wording is editable rather than frozen at the moment it went up.
    const mktpubModal = $('mkt-publish-modal');
    const mktpubEls = {
        deckTitle: $('mktpub-deck-title'),
        description: $('mktpub-description'),
        count: $('mktpub-count'),
        subject: $('mktpub-subject'),
        level: $('mktpub-level'),
        board: $('mktpub-board'),
        confirm: $('mktpub-confirm'),
        error: $('mktpub-error'),
        submit: $('mktpub-submit'),
        nameRow: $('mktpub-name-row'),
        first: $('mktpub-first'),
        last: $('mktpub-last'),
    };
    let mktpubContext = null;   // { mode: 'publish'|'update', presentationId, resourceId, title }
    let mktpubLastFocus = null;

    // True when this account has no name on it. Marketplace sign-up does not ask
    // for one, so the first time it matters is the first time they publish.
    const needsName = () => !(state.user?.firstName && state.user?.lastName);

    // Both halves, or neither – a credit line reading "By Sam" is worse than
    // "By a teacher". Matches normaliseName on the server, which is what
    // actually enforces it.
    function mktpubNameGiven() {
        return !!(mktpubEls.first?.value.trim() && mktpubEls.last?.value.trim());
    }

    // The description is optional, so the only things standing between the
    // author and publishing are the confirmation they are required to make and,
    // for an account that has not got one, a name to credit the lesson to.
    function syncMktpubSubmit() {
        if (!mktpubEls.submit) return;
        const nameOk = mktpubEls.nameRow?.hidden !== false || mktpubNameGiven();
        mktpubEls.submit.disabled = !mktpubEls.confirm?.checked || !nameOk;
        if (mktpubEls.count) {
            const len = mktpubEls.description?.value.trim().length || 0;
            // Only appears once there is something to count, rather than
            // greeting an empty field with "0 / 2000".
            mktpubEls.count.textContent = len ? `${len} / 2000` : '';
        }
    }

    function hidePublishDialog() {
        if (!mktpubModal) return;
        // show()/hide() rather than the hidden attribute: the card is a
        // .t-modal, which sits at opacity 0 and pointer-events none until these
        // helpers add .is-open.
        hide(mktpubModal);
        document.body.style.overflow = '';
        document.removeEventListener('keydown', mktpubKeydown);
        mktpubContext = null;
        mktpubLastFocus?.focus?.();
    }

    function mktpubKeydown(e) {
        if (e.key === 'Escape') hidePublishDialog();
    }

    // `source` is a presentation row when publishing and a listing row when
    // updating; the listing's own wording wins so an update never silently
    // reverts to whatever the deck happens to say.
    function openPublishDialog(context, source = {}) {
        if (!mktpubModal) return;
        mktpubContext = context;
        mktpubLastFocus = document.activeElement;
        setError(mktpubEls.error, '');
        if (mktpubEls.deckTitle) mktpubEls.deckTitle.textContent = context.title || 'Untitled';
        if (mktpubEls.description) mktpubEls.description.value = source.description || '';
        // Through the combobox rather than straight at the hidden input, so the
        // visible field and the stored value cannot disagree.
        mktpubSubjectCombobox.set?.(source.subject || '');
        // A listing published before these were lists can hold a value that is
        // not an option any more. Selecting it would silently fall back to
        // "Not specified" and an author reopening the dialog to fix a typo
        // would clear their level without being told – so the old value is
        // added back as an option, marked, and kept unless they change it.
        setFacetSelect(mktpubEls.level, source.level, MKT_LEVELS, 'Not specified');
        setFacetSelect(mktpubEls.board, source.exam_board, MKT_BOARDS, 'Not specified');
        // Re-ticked every time: it is a statement about this version of the
        // deck, and an update can have changed what is on the slides.
        if (mktpubEls.confirm) mktpubEls.confirm.checked = false;
        // Asked for here, not at sign-up: the public Marketplace sign-up form
        // is deliberately email and password only, and this is the first point
        // at which not having a name would actually cost the author something.
        if (mktpubEls.nameRow) {
            mktpubEls.nameRow.hidden = !needsName();
            if (mktpubEls.first) mktpubEls.first.value = '';
            if (mktpubEls.last) mktpubEls.last.value = '';
        }
        if (mktpubEls.submit) mktpubEls.submit.textContent = context.mode === 'update' ? 'Update listing' : 'Publish';
        syncMktpubSubmit();
        show(mktpubModal);
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', mktpubKeydown);
        mktpubEls.description?.focus();
    }

    function mktpubPayload() {
        return {
            description: mktpubEls.description?.value.trim() || '',
            subject: mktpubEls.subject?.value.trim() || '',
            level: mktpubEls.level?.value.trim() || '',
            examBoard: mktpubEls.board?.value.trim() || '',
        };
    }

    // One message table for both paths. A rejection stays deliberately generic:
    // the check's own wording is derived from user-supplied content, so echoing
    // it back would let a user place arbitrary text in the UI as if it were ours.
    function mktStatusMessage(status, name, mode) {
        if (status === 'published') {
            return mode === 'update'
                ? { ok: true, text: `${name} now shows your latest version.` }
                : { ok: true, text: `${name} is now live on the Marketplace.` };
        }
        if (status === 'rejected') {
            return mode === 'update'
                ? { ok: false, text: `Your changes to ${name} didn’t pass our content check, so the listing has been taken down.` }
                : { ok: false, text: `${name} can’t be published: it didn’t pass our content check.` };
        }
        return { ok: false, text: `${name} is held for a quick review before it appears publicly.` };
    }

    async function submitPublishDialog() {
        if (!mktpubContext) return;
        const { mode, presentationId, resourceId, title } = mktpubContext;
        const btn = mktpubEls.submit;
        const label = btn?.textContent;
        if (btn) { btn.disabled = true; btn.textContent = mode === 'update' ? 'Updating…' : 'Publishing…'; }
        setError(mktpubEls.error, '');
        try {
            // Saved before the listing, so a failure here stops the publish
            // rather than putting a deck on the public web credited to nobody.
            // The same endpoint Settings uses, so there is one place that
            // decides what a valid name is.
            if (mktpubEls.nameRow && !mktpubEls.nameRow.hidden) {
                const firstName = mktpubEls.first.value.trim();
                const lastName = mktpubEls.last.value.trim();
                const nameRes = await apiFetch('/api/settings/name', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ firstName, lastName }),
                });
                const nameData = await nameRes.json().catch(() => ({}));
                if (!nameRes.ok) {
                    setError(mktpubEls.error, nameData.error || 'Could not save your name');
                    return;
                }
                if (state.user) { state.user.firstName = firstName; state.user.lastName = lastName; }
                mktpubEls.nameRow.hidden = true;
            }

            const url = mode === 'update' ? `/api/resources/${resourceId}/refresh` : '/api/resources';
            const res = await apiFetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(
                    mode === 'update' ? mktpubPayload() : { presentationId, ...mktpubPayload() }
                ),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(mktpubEls.error, data.error || 'Failed to publish');
                return;
            }
            hidePublishDialog();
            const name = `“${title}”`;
            const msg = mktStatusMessage(data.status, name, mode);
            // Publishing happens from the Presentations tab and updating from
            // the Published tab, so each reports where the user is looking.
            const okEl = mode === 'update' ? mktListSuccess : presListSuccessEl;
            const errEl = mode === 'update' ? mktListError : presListError;
            setError(msg.ok ? okEl : errEl, msg.text);
            // Either way there is a new or changed row in "Published".
            if (mktLoaded) loadMyPublications();
            // The deck open in the editor is now on the Marketplace, so neither
            // the prompt nor the Share menu should offer to put it there again.
            if (state.presentationRow && state.presentationRow.id === presentationId) {
                state.presentationRow.marketplace_listed = true;
            }
        } catch {
            setError(mktpubEls.error, 'Network error');
        } finally {
            if (btn) { btn.disabled = false; btn.textContent = label; syncMktpubSubmit(); }
        }
    }

    mktpubEls.description?.addEventListener('input', syncMktpubSubmit);
    mktpubEls.confirm?.addEventListener('change', syncMktpubSubmit);
    mktpubEls.first?.addEventListener('input', syncMktpubSubmit);
    mktpubEls.last?.addEventListener('input', syncMktpubSubmit);
    mktpubEls.submit?.addEventListener('click', submitPublishDialog);
    mktpubModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-mktpub-close]')) hidePublishDialog();
    });

    function publishToMarketplace(pres) {
        setError(presListError, '');
        setError(presListSuccessEl, '');
        openPublishDialog(
            { mode: 'publish', presentationId: pres.id, title: pres.title || pres.topic || 'Untitled' },
            pres
        );
    }

    // ── Asking at the right moment ────────────────────────
    // Publishing used to be an icon on a card in a tab nobody opens, which meant
    // you had to already want to publish in order to find out you could. The ask
    // belongs where a lesson is finished: just after presenting it, or after
    // taking it away to teach.
    //
    // Everything below exists to keep it a prompt rather than a nag. It is
    // raised at most once per deck, never for a deck that has been on the
    // Marketplace, never for one taken from another teacher, and it gives up on
    // the whole idea after being turned down three times.
    const shareNudgeEl = $('share-nudge');
    const NUDGE_DECLINE_KEY = 'mkt.nudge.declines';
    const NUDGE_DECLINE_LIMIT = 3;
    const NUDGE_TIMEOUT_MS = 15000;
    let shareNudgeTimer = null;
    let shareNudgeShownFor = null;

    // Private browsing and blocked storage throw on access rather than
    // returning null, and a prompt is not worth breaking the editor over.
    function nudgeStore(key, value) {
        try {
            if (value === undefined) return window.localStorage.getItem(key);
            window.localStorage.setItem(key, value);
        } catch { /* no memory of past dismissals; it will ask once more */ }
        return null;
    }

    function nudgeDismissedFor(presId) {
        return nudgeStore(`mkt.nudge.dismissed.${presId}`) === '1';
    }

    function shouldShowShareNudge() {
        const row = state.presentationRow;
        if (!shareNudgeEl || !row || !state.presentationId) return false;
        // Only a finished deck with slides in it is worth anyone else's time.
        if (row.status !== 'done' || !state.slides.size) return false;
        // Already on the Marketplace, or already taken off it – either way the
        // author has decided about this deck.
        if (row.marketplace_listed) return false;
        // Taken from another teacher. Offering to publish someone else's lesson
        // under your own name is the one version of this prompt that would be
        // actively wrong.
        if (row.source_resource_id) return false;
        if (nudgeDismissedFor(row.id)) return false;
        if (shareNudgeShownFor === row.id) return false;
        if (Number(nudgeStore(NUDGE_DECLINE_KEY) || 0) >= NUDGE_DECLINE_LIMIT) return false;
        return true;
    }

    function hideShareNudge() {
        if (!shareNudgeEl) return;
        clearTimeout(shareNudgeTimer);
        shareNudgeEl.classList.remove('is-open');
        const done = () => { shareNudgeEl.hidden = true; };
        // Matches the CSS close duration so it is gone once it has faded, and
        // still gets there if the transition never fires (reduced motion).
        shareNudgeTimer = setTimeout(done, 400);
    }

    // Turning it down is a real answer: it silences this deck for good, and
    // three of them silence the prompt entirely.
    function declineShareNudge() {
        const row = state.presentationRow;
        if (row) nudgeStore(`mkt.nudge.dismissed.${row.id}`, '1');
        nudgeStore(NUDGE_DECLINE_KEY, String(Number(nudgeStore(NUDGE_DECLINE_KEY) || 0) + 1));
        hideShareNudge();
    }

    function showShareNudge({ title, sub }) {
        if (!shouldShowShareNudge()) return;
        shareNudgeShownFor = state.presentationRow.id;
        $('share-nudge-title').textContent = title;
        shareNudgeEl.querySelector('.share-nudge-sub').textContent = sub;
        clearTimeout(shareNudgeTimer);
        shareNudgeEl.hidden = false;
        void shareNudgeEl.offsetWidth;   // paint the resting state before it rises
        shareNudgeEl.classList.add('is-open');
        // Times out rather than sitting over the deck forever. Not answering is
        // not the same as saying no, so this does not count as a decline.
        shareNudgeTimer = setTimeout(hideShareNudge, NUDGE_TIMEOUT_MS);
    }

    $('share-nudge-close')?.addEventListener('click', declineShareNudge);
    $('share-nudge-no')?.addEventListener('click', declineShareNudge);
    $('share-nudge-go')?.addEventListener('click', () => {
        const row = state.presentationRow;
        hideShareNudge();
        // Someone who publishes is not someone who wants the prompt gone.
        nudgeStore(NUDGE_DECLINE_KEY, '0');
        if (row) publishToMarketplace(row);
    });

    // The same link the library card's Share makes, from where the lesson is
    // finished.
    // The row is from when the deck was opened, so it can be behind a deck
    // generated in this session; the server says if it is not finished yet.
    $('ppt-btn-send-link')?.addEventListener('click', () => {
        const row = state.presentationRow?.id === state.presentationId ? state.presentationRow : null;
        sendAsLink('presentation', state.presentationId, row?.title || row?.topic);
    });

    // Publishing from inside the editor, which is where you are when a lesson is
    // actually finished. Same dialog the library card opens.
    $('ppt-btn-publish')?.addEventListener('click', () => {
        const row = state.presentationRow;
        if (!row) return;
        hideShareNudge();
        if (row.marketplace_listed) {
            showToast('This deck is already on the Marketplace');
            return;
        }
        if (row.source_resource_id) {
            showToast('This came from another teacher – publish your own decks');
            return;
        }
        publishToMarketplace(row);
    });

    // Your own list is short, unpaginated and always newest-first. There is no
    // search, filter or sort in here: there was never enough in it to need them,
    // and they belong to the catalogue at /marketplace, which has them.
    async function loadMyPublications() {
        if (!mktListEl) return;
        mktListEl.classList.add('res-grid');
        mktListEl.innerHTML = mktSkeletonHTML();
        setError(mktListError, '');
        try {
            const res = await apiFetch('/api/resources/mine');
            const data = await res.json();
            if (!res.ok) { setError(mktListError, data.error || 'Failed to load'); return; }
            mktData = data.items || [];
            mktLoaded = true;
            renderMktTotals(data.marketplace);
            renderMyPublications();
            // After the listings, not beside them: the series picker is drawn
            // from mktData, and the nudge counts what is in it.
            loadMySeries();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(mktListError, 'Network error');
        }
    }

    // Card-shaped placeholders that sit in the same grid the results will, so
    // the pane never changes shape between loading and loaded.
    function mktSkeletonHTML() {
        return Array.from({ length: 6 }, (_, i) =>
            `<div class="mkt-skel" style="animation-delay:${i * 0.06}s">
                <span class="mkt-skel-thumb"></span>
                <span class="mkt-skel-lines">
                    <i style="width:${78 - (i % 3) * 12}%"></i>
                    <i style="width:40%"></i>
                    <i style="width:${62 - (i % 2) * 10}%"></i>
                </span>
            </div>`
        ).join('');
    }

    // Matches the wording on the public card, so a listing does not describe
    // itself one way to its author and another way to everyone else.
    function usesLabel(n) {
        if (!n) return 'Not used yet';
        return `Used by ${n} teacher${n === 1 ? '' : 's'}`;
    }

    const MKT_STATUS_LABELS = {
        published: 'live',
        flagged_for_review: 'in review',
        rejected: 'rejected',
    };

    // How the sharing is going, above the listings. Without it the tab is a list
    // of rows with per-listing counts and no total, which is the one number an
    // author actually wants – and the only place the thank you is visible to
    // someone who has not thought to open their billing page.
    function renderMktTotals(m) {
        const el = $('mkt-totals');
        if (!el) return;
        if (!m || (!m.uses && !m.creditsEarned)) { el.hidden = true; return; }
        const given = m.creditsEarned + m.creditsPending;
        // "1 time a teacher has taken one of your lessons" is what assembling
        // words instead of writing sentences gets you. Each case is written out.
        const taken = m.uses === 0
            ? 'Not taken yet'
            : m.uses === 1
                ? 'Taken <b>once</b> by another teacher'
                : `Taken <b>${m.uses.toLocaleString()} times</b> by other teachers`;
        // The same note as the publish dialog, so this reads as the answer to
        // what that one promised – and having a block rather than a strip means
        // it gets a sentence instead of three fragments and a pill.
        const tail = !given
            ? `${m.usesToNext.toLocaleString()} more and we&rsquo;ll take &pound;1 off your next bill as a thank you.`
            // Credit with a count of zero happens when the listings behind it
            // were deleted. "Not taken yet, that's £2 off so far" contradicts
            // itself; the credit is still real, so say only that.
            : m.uses === 0
                ? `<b>&pound;${given}</b> is still off your next bill.`
                : `That&rsquo;s <b>&pound;${given}</b> off your bill so far &ndash; ${m.usesToNext.toLocaleString()} more for the next &pound;1.`;
        el.innerHTML = `
            <span class="thanks-note-mark" aria-hidden="true">&#9733;</span>
            <span>${taken}. ${tail}</span>`;
        el.hidden = false;
    }

    // Every card here is your own, so there is no author to name, nothing to
    // save and nothing to take a copy of – only what state the listing is in and
    // what you can do about it.
    function renderMyPublications() {
        if (!mktListEl || !mktLoaded) return;
        mktListEl.classList.add('res-grid');

        if (!mktData.length) {
            mktListEl.classList.remove('res-grid');
            mktListEl.innerHTML = `
                <div class="list-empty">
                    <span class="list-empty-icon le-presentation">${LIST_ICONS.presentation}</span>
                    <p class="list-empty-title">You haven’t published anything yet</p>
                    <p class="list-empty-sub">Publish a finished presentation from the Presentations tab and it will show up here.</p>
                    <a class="btn btn-ghost btn-sm" href="/marketplace">Browse the Marketplace</a>
                </div>`;
            return;
        }

        mktListEl.innerHTML = '';
        mktData.forEach((row, index) => {
            const item = document.createElement('div');
            item.className = 'mkt-card pres-card-item';
            const facets = facetLine(row);
            // The slide count and the "Preview" overlay both answer questions the
            // old card left to a click: how big is this, and does anything
            // happen if I press it.
            const overlay = `<span class="mkt-preview-hint" aria-hidden="true">Preview</span>`
                + (row.slide_count ? `<span class="mkt-slide-count">${row.slide_count} slide${row.slide_count === 1 ? '' : 's'}</span>` : '');
            item.innerHTML = `
                ${row.thumbnail_html
                    ? `<div class="pres-preview"><div class="pres-preview-slide" data-theme="${escAttr(row.theme || 'classic')}"></div>${overlay}</div>`
                    : `<div class="pres-preview pres-preview-empty"><span aria-hidden="true">${LIST_ICONS.presentation}</span>${overlay}</div>`}
                <div class="pres-card-body">
                    <div class="pres-item-title" title="${escAttr(row.title)}">${esc(row.title)}</div>
                    ${facets ? `<div class="pres-item-meta" title="${escAttr(facets)}">${esc(facets)}</div>` : ''}
                    ${row.description ? `<p class="mkt-desc">${esc(row.description)}</p>` : ''}
                    ${row.status !== 'published'
                    ? `<p class="mkt-note">${row.status === 'rejected'
                        ? 'Didn’t pass our content check.'
                        : 'Held for review before it appears publicly.'}</p>` : ''}
                    ${row.status === 'published'
                    ? `<p class="mkt-card-uses">${esc(usesLabel(row.clone_count || 0))}</p>` : ''}
                    ${row.status === 'published'
                    ? `<a class="mkt-visibility" href="/marketplace/${escAttr(row.id)}" target="_blank" rel="noopener"
                              title="Open the public page for this listing">
                            <span class="mkt-visibility-dot on" aria-hidden="true"></span>
                            View public page
                           </a>` : ''}
                    <div class="mkt-foot">
                        <span class="mkt-status mkt-status-${row.status}">${MKT_STATUS_LABELS[row.status] || row.status}</span>
                        <div class="pres-card-actions">
                            <button class="pres-item-btn btn-mkt-refresh" title="Push your latest edits to this listing">Update</button>
                            <button class="pres-item-btn btn-mkt-unpublish">Unpublish</button>
                        </div>
                    </div>
                </div>
            `;
            // Capped so a long page doesn't end with cards visibly trickling in.
            if (index < 10) item.style.setProperty('--mkt-stagger', `${index * 40}ms`);
            item.classList.add('mkt-card-enter');

            // Opens the frozen snapshot – what other teachers are looking at,
            // which stops being your live deck the moment you edit it. Buttons
            // inside the card stopPropagation so they never open it by accident.
            item.classList.add('mkt-card-clickable');
            item.tabIndex = 0;
            item.setAttribute('role', 'button');
            item.setAttribute('aria-label', `Preview ${row.title}`);
            const openPreview = () => showListingPreview(row);
            item.addEventListener('click', openPreview);
            item.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPreview(); }
            });
            const slideEl = item.querySelector('.pres-preview-slide');
            if (slideEl && row.thumbnail_html) slideEl.innerHTML = row.thumbnail_html;

            // A link out of the card, so the click must not also open the
            // preview modal behind it.
            item.querySelector('.mkt-visibility')?.addEventListener('click', (e) => e.stopPropagation());

            // Opens the publish dialog prefilled from the listing rather than
            // firing a bare refresh: an update re-runs moderation on the new
            // slides, so it is also the moment to revisit the wording.
            item.querySelector('.btn-mkt-refresh')?.addEventListener('click', (e) => {
                e.stopPropagation();
                setError(mktListError, '');
                setError(mktListSuccess, '');
                openPublishDialog(
                    { mode: 'update', resourceId: row.id, title: row.title || 'Untitled' },
                    row
                );
            });

            item.querySelector('.btn-mkt-unpublish')?.addEventListener('click', async (e) => {
                e.stopPropagation();
                const ok = await showConfirm({
                    title: 'Unpublish this?',
                    message: `"${row.title}" will be removed from the Marketplace. Your own copy is kept, and teachers who already used it keep theirs.`,
                    confirmLabel: 'Unpublish',
                    danger: true,
                });
                if (!ok) return;
                try {
                    const res = await apiFetch(`/api/resources/${row.id}`, { method: 'DELETE' });
                    if (!res.ok) { setError(mktListError, 'Could not unpublish'); return; }
                    mktData = mktData.filter(r => r.id !== row.id);
                    renderMyPublications();
                } catch {
                    setError(mktListError, 'Network error');
                }
            });

            mktListEl.appendChild(item);
        });
        requestAnimationFrame(scalePresPreviews);
    }

    // ── Series ────────────────────────────────────────────
    // An ordered run of your own published lessons. The catalogue could only
    // ever offer one lesson at a time, so a teacher who had written a whole unit
    // had no way to say so – and a visitor who found part three of it had no way
    // to reach the other seven.
    //
    // It lives in this tab and not one of its own, because a series is built out
    // of the listings already here.
    const mktSeriesListEl = $('mkt-series-list');
    const mktSeriesError = $('mkt-series-error');
    const mktSeriesNudge = $('mkt-series-nudge');
    const mktSeriesModal = $('mkt-series-modal');
    const mktseriesEls = {
        title: $('mktseries-title'),
        name: $('mktseries-name'),
        description: $('mktseries-description'),
        picked: $('mktseries-picked'),
        pool: $('mktseries-pool'),
        error: $('mktseries-error'),
        submit: $('mktseries-submit'),
    };
    let seriesData = [];
    let seriesLoaded = false;
    let mktseriesContext = null;   // { mode: 'create'|'edit', seriesId, title, description }
    let mktseriesOrder = [];       // resource ids, in the order the author has put them
    let mktseriesLastFocus = null;

    // Below this a series is not a series – the public page 404s and no lesson in
    // it gets a badge. Same number the server holds in SERIES_MIN_ITEMS; the
    // author is told rather than left to find out from the public page.
    const SERIES_MIN_ITEMS = 2;
    const SERIES_MAX_ITEMS = 30;

    const SERIES_STATUS_LABELS = MKT_STATUS_LABELS;

    // Called at the end of loadMyPublications rather than from setDashTab: the
    // picker is drawn from mktData, so the listings have to be in hand first.
    async function loadMySeries() {
        if (!mktSeriesListEl) return;
        try {
            const res = await apiFetch('/api/series/mine');
            const data = await res.json();
            if (!res.ok) { setError(mktSeriesError, data.error || 'Could not load your series'); return; }
            seriesData = data.items || [];
            seriesLoaded = true;
            renderMySeries();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(mktSeriesError, 'Network error');
        }
    }

    function renderMySeries() {
        if (!mktSeriesListEl || !seriesLoaded) return;
        mktSeriesListEl.innerHTML = '';
        mktSeriesListEl.classList.toggle('res-grid', seriesData.length > 0);
        renderSeriesNudge();

        seriesData.forEach((row) => {
            const lessons = row.lessons || [];
            const cover = lessons[0];
            const item = document.createElement('div');
            item.className = 'mkt-card mkt-series-card pres-card-item';
            const count = Number(row.lesson_count) || 0;
            item.innerHTML = `
                ${cover?.thumbnail_html
                    ? `<div class="pres-preview"><div class="pres-preview-slide" data-theme="${escAttr(cover.theme || 'classic')}"></div></div>`
                    : `<div class="pres-preview pres-preview-empty"><span aria-hidden="true">${LIST_ICONS.presentation}</span></div>`}
                <div class="pres-card-body">
                    <div class="pres-item-title" title="${escAttr(row.title)}">${esc(row.title)}</div>
                    <div class="pres-item-meta">${count} lesson${count === 1 ? '' : 's'}</div>
                    ${row.description ? `<p class="mkt-desc">${esc(row.description)}</p>` : ''}
                    ${row.status !== 'published'
                    ? `<p class="mkt-note">${row.status === 'rejected'
                        ? 'Didn’t pass our content check.'
                        : 'Held for review before it appears publicly.'}</p>`
                    // A series of one is a real state an author lands in – they
                    // have started it – and saying so is better than a public
                    // page that 404s with no explanation anywhere.
                    : count < SERIES_MIN_ITEMS
                        ? `<p class="mkt-note">Add another lesson and this goes live. A series of one is not shown publicly.</p>` : ''}
                    ${row.status === 'published' && count >= SERIES_MIN_ITEMS
                    ? `<a class="mkt-visibility" href="/marketplace/series/${escAttr(row.id)}" target="_blank" rel="noopener"
                              title="Open the public page for this series">
                            <span class="mkt-visibility-dot on" aria-hidden="true"></span>
                            View public page
                           </a>` : ''}
                    <div class="mkt-foot">
                        <span class="mkt-status mkt-status-${row.status}">${SERIES_STATUS_LABELS[row.status] || row.status}</span>
                        <div class="pres-card-actions">
                            <button class="pres-item-btn btn-series-edit">Edit</button>
                            <button class="pres-item-btn btn-series-delete">Delete</button>
                        </div>
                    </div>
                </div>
            `;
            const slideEl = item.querySelector('.pres-preview-slide');
            if (slideEl && cover?.thumbnail_html) slideEl.innerHTML = cover.thumbnail_html;

            item.querySelector('.btn-series-edit')?.addEventListener('click', () => {
                setError(mktSeriesError, '');
                openSeriesDialog({
                    mode: 'edit', seriesId: row.id, title: row.title, description: row.description || '',
                }, lessons.map(l => l.id));
            });

            item.querySelector('.btn-series-delete')?.addEventListener('click', () => deleteSeries(row));

            mktSeriesListEl.appendChild(item);
        });
        requestAnimationFrame(scalePresPreviews);
    }

    // Asked at the moment it starts to be worth something: enough published
    // lessons to make a run out of, and none made yet. Same reasoning as the
    // share nudge – a prompt where the work already is, not a feature waiting to
    // be discovered in a menu.
    function renderSeriesNudge() {
        if (!mktSeriesNudge) return;
        const publishable = mktData.filter(r => r.status === 'published').length;
        if (seriesData.length || publishable < SERIES_MIN_ITEMS) { mktSeriesNudge.hidden = true; return; }
        mktSeriesNudge.innerHTML = `
            <span class="thanks-note-mark" aria-hidden="true">&#9733;</span>
            <span>You have ${publishable} published lessons. Teachers take more when they can take a whole
                unit at once &ndash; put the ones that belong together in order.</span>`;
        mktSeriesNudge.hidden = false;
    }

    async function deleteSeries(row) {
        const ok = await showConfirm({
            title: 'Delete this series?',
            message: `"${row.title}" will be removed from the Marketplace. The lessons in it stay published on their own.`,
            confirmLabel: 'Delete',
            danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/series/${row.id}`, { method: 'DELETE' });
            if (!res.ok) { setError(mktSeriesError, 'Could not delete that series'); return; }
            seriesData = seriesData.filter(s => s.id !== row.id);
            // The lessons are free again, so the picker must stop treating them
            // as spoken for.
            mktData = mktData.map(r => (r.series_id === row.id ? { ...r, series_id: null } : r));
            renderMySeries();
        } catch {
            setError(mktSeriesError, 'Network error');
        }
    }

    function hideSeriesDialog() {
        if (!mktSeriesModal) return;
        hide(mktSeriesModal);
        document.body.style.overflow = '';
        document.removeEventListener('keydown', mktseriesKeydown);
        mktseriesContext = null;
        mktseriesLastFocus?.focus?.();
    }

    function mktseriesKeydown(e) {
        if (e.key === 'Escape') hideSeriesDialog();
    }

    function openSeriesDialog(context, order = []) {
        if (!mktSeriesModal) return;
        mktseriesContext = context;
        mktseriesOrder = [...order];
        mktseriesLastFocus = document.activeElement;
        setError(mktseriesEls.error, '');
        if (mktseriesEls.title) mktseriesEls.title.textContent = context.mode === 'edit' ? 'Edit series' : 'New series';
        if (mktseriesEls.name) mktseriesEls.name.value = context.title || '';
        if (mktseriesEls.description) mktseriesEls.description.value = context.description || '';
        renderSeriesPicker();
        show(mktSeriesModal);
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', mktseriesKeydown);
        mktseriesEls.name?.focus();
    }

    // Both halves of the picker, redrawn together because every action changes
    // both: adding a lesson takes it out of the pool, removing one puts it back.
    function renderSeriesPicker() {
        if (!mktseriesEls.picked || !mktseriesEls.pool) return;
        const byId = new Map(mktData.map(r => [r.id, r]));

        mktseriesEls.picked.innerHTML = mktseriesOrder.length
            ? mktseriesOrder.map((id, i) => {
                const row = byId.get(id);
                if (!row) return '';
                return `
                <li class="mktseries-item" data-id="${escAttr(id)}">
                    <span class="mktseries-num">${i + 1}</span>
                    <span class="mktseries-item-title" title="${escAttr(row.title)}">${esc(row.title)}</span>
                    <span class="mktseries-item-acts">
                        <button type="button" class="pres-item-btn" data-series-move="up" ${i === 0 ? 'disabled' : ''}
                                aria-label="Move ${escAttr(row.title)} earlier">&#9650;</button>
                        <button type="button" class="pres-item-btn" data-series-move="down" ${i === mktseriesOrder.length - 1 ? 'disabled' : ''}
                                aria-label="Move ${escAttr(row.title)} later">&#9660;</button>
                        ${/* A cross, not the word "Remove". The title is what
                              you read to put the lessons in order and the
                              buttons are chrome, but spelled out the chrome won
                              the row and every lesson showed as "Collision …".
                              The label carries the meaning for anyone who
                              cannot see the glyph. */''}
                        <button type="button" class="pres-item-btn mktseries-x" data-series-remove
                                title="Take out of the series"
                                aria-label="Take ${escAttr(row.title)} out of the series">&times;</button>
                    </span>
                </li>`;
            }).join('')
            : `<li class="mktseries-empty">Nothing in it yet. Add lessons from the right, in the order you teach them.</li>`;

        // Your own published lessons that are not already in another series.
        // A lesson can be in one series only, which is what makes "Lesson 3 of
        // 8" and the arrows answerable at all.
        const available = mktData.filter(r => r.status === 'published'
            && !mktseriesOrder.includes(r.id)
            && (!r.series_id || r.series_id === mktseriesContext?.seriesId));
        const spokenFor = mktData.filter(r => r.status === 'published'
            && r.series_id && r.series_id !== mktseriesContext?.seriesId).length;

        mktseriesEls.pool.innerHTML = (available.length
            ? available.map(row => `
                <div class="mktseries-item" data-id="${escAttr(row.id)}">
                    <span class="mktseries-item-title" title="${escAttr(row.title)}">${esc(row.title)}</span>
                    <button type="button" class="pres-item-btn" data-series-add>Add</button>
                </div>`).join('')
            : `<p class="mktseries-empty">No other published lessons to add.</p>`)
            + (spokenFor
                ? `<p class="mktseries-note">${spokenFor} lesson${spokenFor === 1 ? ' is' : 's are'} in another series.
                    A lesson can only be in one.</p>`
                : '');
    }

    mktseriesEls.picked?.addEventListener('click', (e) => {
        const li = e.target.closest('[data-id]');
        if (!li) return;
        const at = mktseriesOrder.indexOf(li.getAttribute('data-id'));
        if (at < 0) return;
        if (e.target.closest('[data-series-remove]')) {
            mktseriesOrder.splice(at, 1);
        } else {
            // Adjacent swaps rather than drag and drop: the same payload, a
            // tenth of the code, and it works on a touchscreen.
            const dir = e.target.closest('[data-series-move]')?.getAttribute('data-series-move');
            if (!dir) return;
            const to = dir === 'up' ? at - 1 : at + 1;
            if (to < 0 || to >= mktseriesOrder.length) return;
            [mktseriesOrder[at], mktseriesOrder[to]] = [mktseriesOrder[to], mktseriesOrder[at]];
        }
        renderSeriesPicker();
    });

    mktseriesEls.pool?.addEventListener('click', (e) => {
        if (!e.target.closest('[data-series-add]')) return;
        const id = e.target.closest('[data-id]')?.getAttribute('data-id');
        if (!id || mktseriesOrder.includes(id)) return;
        if (mktseriesOrder.length >= SERIES_MAX_ITEMS) {
            setError(mktseriesEls.error, `A series holds up to ${SERIES_MAX_ITEMS} lessons.`);
            return;
        }
        mktseriesOrder.push(id);
        setError(mktseriesEls.error, '');
        renderSeriesPicker();
    });

    // Same table as a listing's, and generic about a rejection for the same
    // reason: the check's wording is derived from what the author submitted, so
    // echoing it hands them a way to put their own text in our voice.
    function seriesStatusMessage(status, name) {
        if (status === 'published') return { ok: true, text: `${name} is saved.` };
        if (status === 'rejected') return { ok: false, text: `${name} didn’t pass our content check, so it is not shown publicly.` };
        return { ok: false, text: `${name} is held for a quick review before it appears publicly.` };
    }

    async function submitSeriesDialog() {
        if (!mktseriesContext) return;
        const { mode, seriesId } = mktseriesContext;
        const title = mktseriesEls.name?.value.trim() || '';
        const description = mktseriesEls.description?.value.trim() || '';
        if (!title) { setError(mktseriesEls.error, 'Give the series a name'); return; }

        const btn = mktseriesEls.submit;
        const label = btn?.textContent;
        if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
        setError(mktseriesEls.error, '');
        try {
            let id = seriesId;
            let status;
            if (mode === 'create') {
                const res = await apiFetch('/api/series', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title, description }),
                });
                const data = await res.json();
                if (!res.ok) { setError(mktseriesEls.error, data.error || 'Could not save that'); return; }
                id = data.id;
                status = data.status;
            } else {
                const res = await apiFetch(`/api/series/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ title, description }),
                });
                const data = await res.json();
                if (!res.ok) { setError(mktseriesEls.error, data.error || 'Could not save that'); return; }
                status = data.status;
            }

            // Membership second, so a series that fails to save its name never
            // silently takes lessons out of another one.
            const itemsRes = await apiFetch(`/api/series/${id}/items`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order: mktseriesOrder }),
            });
            const itemsData = await itemsRes.json().catch(() => ({}));
            if (!itemsRes.ok) { setError(mktseriesEls.error, itemsData.error || 'Could not save the lessons'); return; }

            hideSeriesDialog();
            const msg = seriesStatusMessage(status, `“${title}”`);
            setError(msg.ok ? mktListSuccess : mktSeriesError, msg.text);
            // Membership moved, so the listings are stale too – both lists are
            // reloaded from the one call.
            loadMyPublications();
        } catch {
            setError(mktseriesEls.error, 'Network error');
        } finally {
            if (btn) { btn.disabled = false; btn.textContent = label; }
        }
    }

    mktseriesEls.submit?.addEventListener('click', submitSeriesDialog);
    mktSeriesModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-mktseries-close]')) hideSeriesDialog();
    });
    $('mkt-series-new')?.addEventListener('click', () => {
        setError(mktSeriesError, '');
        openSeriesDialog({ mode: 'create', title: '', description: '' }, []);
    });

    // ── Preview your own listing ──────────────────────────
    // Reading someone else's listing happens at /marketplace/:id, which is a
    // page with a URL you can send to somebody. This modal survives for the one
    // case that has no public page: your own listing while it is in review, or
    // after it was rejected. It shows the frozen snapshot rather than your live
    // deck, which is the thing an author is usually trying to check.
    const mktpModal = $('mkt-preview-modal');
    const mktpSlideEl = $('mktp-slide');
    const mktpCounter = $('mktp-counter');
    let mktpSlides = [];
    let mktpIndex = 0;
    let mktpTheme = 'classic';
    let mktpLastFocus = null;

    function scaleMktpSlide() {
        const stage = mktpSlideEl?.parentElement;
        if (!stage || !mktpSlideEl) return;
        const w = stage.clientWidth;
        if (w) {
            mktpSlideEl.style.transform = `scale(${w / 960})`;
            stage.style.height = `${(w / 960) * 540}px`;
        }
    }

    function renderMktpSlide() {
        if (!mktpSlideEl) return;
        mktpSlideEl.dataset.theme = mktpTheme;
        mktpSlideEl.innerHTML = mktpSlides[mktpIndex] || '';
        SlideRenderer.autoFitSlides(mktpSlideEl);
        if (mktpCounter) mktpCounter.textContent = mktpSlides.length
            ? `Slide ${mktpIndex + 1} of ${mktpSlides.length}` : 'No slides';
        $('mktp-prev').disabled = mktpIndex <= 0;
        $('mktp-next').disabled = mktpIndex >= mktpSlides.length - 1;
        syncMktpFilmstrip();
        requestAnimationFrame(scaleMktpSlide);
    }

    // Numbered rather than thumbnailed: rendering N scaled copies of a deck's
    // slide HTML into the strip costs more than it is worth for navigation.
    function renderMktpFilmstrip() {
        const strip = $('mktp-filmstrip');
        if (!strip) return;
        if (mktpSlides.length < 2) { strip.hidden = true; strip.innerHTML = ''; return; }
        strip.innerHTML = mktpSlides.map((_, i) =>
            `<button class="mktp-film" type="button" role="tab" data-slide="${i}"
                     aria-label="Slide ${i + 1}">${i + 1}</button>`
        ).join('');
        strip.hidden = false;
        syncMktpFilmstrip();
    }

    function syncMktpFilmstrip() {
        const strip = $('mktp-filmstrip');
        if (!strip || strip.hidden) return;
        strip.querySelectorAll('.mktp-film').forEach(btn => {
            const on = Number(btn.dataset.slide) === mktpIndex;
            btn.classList.toggle('active', on);
            btn.setAttribute('aria-selected', String(on));
        });
        strip.querySelector('.mktp-film.active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    $('mktp-filmstrip')?.addEventListener('click', (e) => {
        const btn = e.target.closest('.mktp-film');
        if (!btn) return;
        mktpIndex = Number(btn.dataset.slide);
        renderMktpSlide();
    });

    function stepMktp(delta) {
        const next = mktpIndex + delta;
        if (next < 0 || next >= mktpSlides.length) return;
        mktpIndex = next;
        renderMktpSlide();
    }

    function hideListingPreview() {
        if (!mktpModal) return;
        mktpModal.hidden = true;
        document.body.style.overflow = '';
        document.removeEventListener('keydown', mktpKeydown);
        mktpLastFocus?.focus?.();
    }

    function mktpKeydown(e) {
        if (e.key === 'Escape') return hideListingPreview();
        if (e.key === 'ArrowLeft') return stepMktp(-1);
        if (e.key === 'ArrowRight') return stepMktp(1);
    }

    async function showListingPreview(row) {
        if (!mktpModal) return;
        mktpLastFocus = document.activeElement;
        mktpSlides = [];
        mktpIndex = 0;
        // The row we already have covers the header, so it paints immediately and
        // only the slides wait on the request.
        mktpTheme = row.theme || 'classic';
        $('mktp-title').textContent = row.title || 'Untitled';
        $('mktp-meta').textContent = facetLine(row);
        // The description is the author's own account of the deck and the main
        // thing anyone reading the listing is deciding on – it belongs here, not
        // only on the card.
        const descEl = $('mktp-desc');
        if (descEl) {
            descEl.textContent = row.description || '';
            descEl.hidden = !row.description;
        }
        // Only a live listing has a public page to go and look at; one still in
        // review has nowhere to send you yet.
        const publicLink = $('mktp-public');
        if (publicLink) {
            publicLink.hidden = row.status !== 'published';
            publicLink.href = `/marketplace/${row.id}`;
        }
        if (mktpSlideEl) mktpSlideEl.innerHTML = '';
        if (mktpCounter) mktpCounter.textContent = 'Loading…';
        mktpModal.hidden = false;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', mktpKeydown);
        $('mktp-close')?.focus();

        try {
            const res = await apiFetch(`/api/resources/${row.id}`);
            const data = await res.json();
            if (!res.ok) {
                if (mktpCounter) mktpCounter.textContent = data.error || 'Could not load this listing';
                return;
            }
            mktpSlides = data.slides || [];
            mktpTheme = data.theme || mktpTheme;
            renderMktpFilmstrip();
            renderMktpSlide();
        } catch {
            if (mktpCounter) mktpCounter.textContent = 'Network error';
        }
    }

    // Reporting a listing is done on its public page, alongside the listing –
    // it was duplicated in here for the in-app browse, which is gone. So are
    // Save, Copy link and Use this: none of them is something you do to your
    // own listing.
    $('mktp-prev')?.addEventListener('click', () => stepMktp(-1));
    $('mktp-next')?.addEventListener('click', () => stepMktp(1));
    mktpModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-mktp-close]')) hideListingPreview();
    });
    window.addEventListener('resize', () => {
        if (mktpModal && !mktpModal.hidden) scaleMktpSlide();
    });

    // Fire-and-forget: wakes the (possibly scale-to-zero-stopped) server and
    // starts the slow Chromium launch so an eventual Export is near-instant.
    function warmExportPipeline() {
        apiFetch('/api/export/warm', { method: 'POST' }).catch(() => {});
    }

    /** @returns {Promise<boolean>} whether the deck opened */
    // A deck that was planned but never generated – the tab closed on its
    // plan, or a series lesson was left at that point – opens on its plan,
    // ready to generate, rather than as an empty editor. One still waiting
    // for its plan is planned again.
    function resumePlanning(pres) {
        hide(authView, presListView, bookletView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(generatorView);
        show(btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        resetGeneratorView();
        hide(generateCard);
        state.presentationId = pres.id;
        state.resumedPres = pres;
        state.presentationTopic = pres.topic || '';
        state.selectedTheme = pres.theme || 'classic';
        // What was ticked under "Also make" went with the tab that planned it.
        state.pendingCompanions = { worksheet: false, quiz: false };
        if (window.location.pathname !== `/p/${pres.id}`) history.pushState({ presId: pres.id }, '', `/p/${pres.id}`);

        const plan = parseJson(pres.plan_json);
        if (pres.status === 'planned' && plan && Array.isArray(plan.slides) && plan.slides.length) {
            showPlanApproval(plan);
        } else {
            showGenLoading('Planning your presentation', pres.topic || '');
            startPlanStream(pres.id);
        }
        return true;
    }

    async function openPresentation(presId) {
        window.ImageBankPanel?.close();   // it was aimed at the deck being left
        try {
            const res = await apiFetch('/api/presentations/' + presId);
            const pres = await res.json();
            if (!res.ok) return false;
            if (pres.status === 'planned' || pres.status === 'pending') return resumePlanning(pres);

            // Show generator view but hide the generate form – editor only
            hide(authView, presListView, bookletView, worksheetView, quizView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
            show(generatorView);
            hide(generateCard);
            show(btnLogout, navCreateWrap, navStudentsBtn);
            if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
            show(navUserEmail);

            state.presentationId = presId;
            // A draft belongs to the deck it was asked for in.
            try { closeSeam(); } catch { /* not set up yet */ }
            state.presentationTopic     = pres.topic      || '';
            state.presentationExamBoard = pres.exam_board || '';
            state.presentationSubject   = pres.subject    || '';
            state.presentationLevel     = pres.level      || '';
            state.selectedTheme = pres.theme || 'classic';
            // What the Share menu and the finished-lesson prompt both need, kept
            // as the row rather than as four loose flags so publishing from the
            // editor prefills its dialog from the same fields the library card
            // would have passed.
            state.presentationRow = {
                id: presId,
                title: pres.title,
                topic: pres.topic,
                description: pres.description,
                subject: pres.subject,
                level: pres.level,
                exam_board: pres.exam_board,
                status: pres.status,
                marketplace_listed: !!pres.marketplace_listed,
                source_resource_id: pres.source_resource_id || null,
            };
            hideShareNudge();
            state.slides.clear();
            if (slidePanel) slidePanel.innerHTML = '';
            if (stageSlideInner) stageSlideInner.innerHTML = '';
            hide(progressBar, planView);
            applyTheme(state.selectedTheme);
            syncThemePickers(state.selectedTheme);

            const sorted = (pres.slides || []).slice().sort((a, b) => a.position - b.position);
            slideNotes.clear();
            sorted.forEach(slide => {
                rememberNotes(slide.id, slide.speaker_notes);
                state.slides.set(slide.position, {
                    id: slide.id,
                    html: slide.html_content,
                    contents_json: parseJson(slide.contents_json),
                    elements_json: parseJson(slide.elements_json),
                    json_schema_version: slide.json_schema_version,
                });
                updateThumb(slide.position, slide.id, slide.html_content);
            });

            show(pptEditor);
            if (sorted.length) {
                const first = sorted[0];
                selectSlide(first.id, first.html_content, first.position);
            }

            if (pptChromeTitle) pptChromeTitle.textContent = pres.title || pres.topic || 'Untitled presentation';
            renderPackSide(presId, { type: 'presentation' });
            switchPptMode('direct');

            // Update URL so refresh returns to this presentation
            if (window.location.pathname !== `/p/${presId}`) {
                history.pushState({ presId }, '', `/p/${presId}`);
            }
            // Once the deck is on screen, not before: this starts headless
            // Chrome on the server, which is the last thing wanted while the
            // same server is still fetching the slides.
            warmExportPipeline();
            return true;
        } catch (err) {
            if (err.message !== 'Not authenticated') console.error(err);
            return false;
        }
    }

    // Handle browser back/forward
    window.addEventListener('popstate', (e) => {
        if (!state.user) return;
        const { presId, worksheetId, isNewWorksheet, quizId, isNewQuiz, bookletId, isNewBooklet } = e.state || {};
        const pathname = window.location.pathname;
        if (presId) {
            openPresentation(presId);
        } else if (bookletId) {
            openBooklet(bookletId);
        } else if (worksheetId) {
            openWorksheet(worksheetId);
        } else if (quizId) {
            openQuiz(quizId);
        } else if (/^\/series\/[^/]+$/.test(pathname)) {
            openScheme(decodeURIComponent(pathname.split('/')[2]), { push: false });
        } else if (isNewBooklet || pathname === '/booklets') {
            showBookletPage();
        } else if (isNewWorksheet || pathname === '/worksheets') {
            showWorksheetPage('', 'list');
        } else if (isNewQuiz || pathname === '/quizzes') {
            showQuizPage('', 'list');
        } else if (pathname === '/tutoring' || pathname.startsWith('/tutoring/')) {
            showTutoringPage(pathname, false);
        } else if (e.state?.billing || pathname === '/billing') {
            showBilling();
        } else if (e.state?.org || pathname === '/org') {
            if (state.user?.org) showOrg();
            else showPresList();
        } else if (/^\/lessons\/[^/]+$/.test(pathname)) {
            // Pupils and what opens from it. A class or a lesson is a click
            // away from that page and Back is how people return to it, which
            // used to land them in their library instead.
            openLessonDetail(decodeURIComponent(pathname.split('/')[2]), false);
        } else if (/^\/classes\/[^/]+$/.test(pathname)) {
            openClass(decodeURIComponent(pathname.split('/')[2]), false);
        } else if (pathname === '/students') {
            showStudentsPage(false);
        } else {
            showPresList();
        }
    });

    // ── Resize observer ───────────────────────────────────
    if (typeof ResizeObserver !== 'undefined' && stageCanvas) {
        new ResizeObserver(scaleStage).observe(stageCanvas);
    }

    // ── Dashboard tabs ────────────────────────────────────
    // All five are panes of your library and stay at /. Browsing the Marketplace
    // used to be a sixth that was also a URL, which is why this had a special
    // case; it is a page of its own now, reached by a plain link.
    function setDashTab(tab) {
        const pList   = $('pres-list');
        const wList   = $('ws-list');
        const qList   = $('quiz-list');
        const mList   = $('marking-list');
        const mktPane = $('mkt-pane');
        const olibPane = $('orglib-pane');
        const pErr    = $('pres-list-error');
        const wErr    = $('ws-list-error');
        const qErr    = $('quiz-list-error');
        const mErr    = $('marking-list-error');

        // Somebody who has left their organisation still has the tab's name in
        // a bookmark or in this session's history, so it falls back rather than
        // showing an empty pane behind a tab that is no longer there.
        if (tab === 'shared' && !state.user?.org) tab = 'presentations';
        libTab = tab;
        renderLibHead();

        const bList = $('booklet-list');
        const bErr = $('booklet-list-error');
        hide(pList, wList, bList, qList, mList, mktPane, olibPane);
        if (tab !== 'presentations') hide(presListSuccessEl);
        if (presLoadMoreBtn) presLoadMoreBtn.hidden = tab !== 'presentations' || !presHasMore;
        if (tab === 'worksheets') {
            show(wList);
            hide(pErr, qErr, mErr, bErr);
        } else if (tab === 'booklets') {
            show(bList);
            hide(pErr, wErr, qErr, mErr);
            // Loaded on visit rather than at sign-in: a booklet left half
            // written should show as unfinished the moment you come back.
            loadBooklets();
        } else if (tab === 'quizzes') {
            show(qList);
            hide(pErr, wErr, mErr);
        } else if (tab === 'marking') {
            show(mList);
            hide(pErr, wErr, qErr);
            loadMarkingSets();
        } else if (tab === 'published') {
            show(mktPane);
            hide(pErr, wErr, qErr, mErr);
            loadMyPublications();
        } else if (tab === 'shared') {
            show(olibPane);
            hide(pErr, wErr, qErr, mErr);
            // Re-fetched on every visit: the whole point of the shelf is that
            // colleagues are putting things on it while you are logged in.
            loadOrgLibrary(true);
        } else {
            show(pList);
            hide(wErr, qErr, mErr);
        }

        document.querySelectorAll('.dash-tab').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.tab === tab);
        });
    }

    document.querySelectorAll('.dash-tab').forEach(btn => {
        btn.addEventListener('click', () => setDashTab(btn.dataset.tab));
    });

    // ── Quiz page ─────────────────────────────────────────
    const quizTopicEl      = $('quiz-topic');
    const quizContext       = $('quiz-context');
    const quizExamBoard    = $('quiz-exam-board');
    const quizSourcePres   = $('quiz-source-pres');
    const quizSubject      = $('quiz-subject');
    const quizLevel        = $('quiz-level');
    const quizError        = $('quiz-error');
    const quizPageForm     = $('quiz-page-form');
    const quizPagePreview  = $('quiz-page-preview');
    const quizEditorEl     = $('quiz-editor');
    const quizTitleInput   = $('quiz-title-input');
    const quizSaveStatus   = $('quiz-save-status');
    const btnGenerateQuiz  = $('btn-generate-quiz');
    const btnQuizBack      = $('btn-quiz-back');
    const btnQuizShare     = $('btn-quiz-share');

    /**
     * Shows "Set to a class" once the quiz is saved and if there is a school.
     *
     * Both conditions matter: an unsaved quiz has no id to set, and outside an
     * organisation there is no class to set it to.
     */
    /**
     * "Set to a class" on the worksheet and the booklet.
     *
     * The same two conditions syncQuizSetWork applies, for the same reasons:
     * something unsaved has no id to set, and outside an organisation there is
     * no class to set it to. Called wherever the id or the title can change.
     */
    function syncReadingSetWork() {
        const ws = $('btn-ws-set-class');
        if (ws) {
            ws.hidden = !state.user?.org || tutoringOrg() || !currentWorksheetId;
            ws.onclick = () => openSetWork({
                what: {
                    kind: 'worksheet',
                    refId: currentWorksheetId,
                    title: (currentWorksheetData?.title || '').trim() || 'this worksheet',
                },
            });
        }

        const bk = $('btn-bk-set-class');
        if (bk) {
            // A workbook has no guide to read – it is set as a lesson instead.
            bk.hidden = !state.user?.org || tutoringOrg() || !bkState.id || bkIsWorkbook();
            bk.onclick = () => openSetWork({
                what: {
                    kind: 'booklet',
                    refId: bkState.id,
                    title: (bkState.meta && bkState.meta.title) || 'this booklet',
                },
            });
        }
    }

    function syncQuizSetWork() {
        const btn = $('btn-quiz-setwork');
        if (!btn) return;
        btn.hidden = !state.user?.org || tutoringOrg() || !currentQuizId;
        btn.onclick = () => openSetWork({
            what: {
                kind: 'quiz',
                refId: currentQuizId,
                title: ($('quiz-title')?.value || '').trim() || 'this quiz',
            },
        });
    }
    const btnQuizSendBA    = $('btn-quiz-send-ba');
    const btnQuizExport    = $('btn-quiz-export');
    const quizA11ySelect   = $('quiz-a11y-select');
    const quizTintSelect   = $('quiz-tint-select');

    // Safe here and not earlier: the worksheet pickers are declared further up
    // this scope but the quiz ones are these two lines, so anything sooner
    // would hit the temporal dead zone.
    refreshA11yPickers();

    // The panel's height is driven by the shared .t-acc grid track, which reads
    // data-open – a class alone leaves it shut.
    function setQuizContextOpen(open) { setSourcePanel('quiz-context', 'notes', open); }

    // ── Composer steps ────────────────────────────────────
    const quizWizard = initComposerWizard({
        formId: 'quiz-page-form',
        submitId: 'btn-generate-quiz',
        nextLabels: ['Next: questions →'],
        hints: [
            'A topic, your own files, or both.',
            'Generating uses one of your monthly quiz allowances.',
        ],
        isReady: () => {
            // Lessons ticked for a starter activity are a subject in themselves.
            if (!topicLineFor(quizTopicEl, quizTopicPicker) && !quizStarterSources().length) {
                setError(quizError, 'Type a topic, add a file, or tick topics from the specification');
                quizTopicEl?.focus();
                return false;
            }
            setError(quizError, '');
            return true;
        },
    });

    async function downloadQuizPdf(withAnswers) {
        if (!currentQuizId) return;
        const parts = [];
        if (withAnswers) parts.push('answers=1');
        const a11yQs = A11yProfiles.toQuery(a11yOptsFrom(quizA11ySelect, quizTintSelect));
        if (a11yQs) parts.push(a11yQs);
        const qs = parts.join('&');
        triggerDownload('/api/quizzes/' + currentQuizId + '/pdf' + (qs ? '?' + qs : ''));
    }
    $('btn-quiz-download-pdf')?.addEventListener('click', () => downloadQuizPdf(false));
    $('btn-quiz-download-answers')?.addEventListener('click', () => downloadQuizPdf(true));
    // Taking a deck away is the other moment worth asking at – less certain than
    // having presented it, but it is still someone leaving with a finished
    // lesson. Delayed past the point the download actually starts, so the
    // browser's own bar arrives first and the prompt is not competing with it.
    function nudgeAfterDownload() {
        setTimeout(() => showShareNudge({
            title: 'Teaching this one?',
            sub: 'Share it so the next teacher doesn’t have to build it.',
        }), 2500);
    }

    $('ppt-btn-download-pdf')?.addEventListener('click', () => {
        if (!state.presentationId) return;
        triggerDownload('/api/presentations/' + state.presentationId + '/pdf');
        nudgeAfterDownload();
    });
    $('ppt-btn-download-pptx')?.addEventListener('click', () => {
        if (!state.presentationId) return;
        triggerDownload('/api/presentations/' + state.presentationId + '/pptx');
        nudgeAfterDownload();
    });
    const quizShareResult  = $('quiz-share-result');
    const quizShareUrl     = $('quiz-share-url');
    const btnQuizCopyLink  = $('btn-quiz-copy-link');
    const btnQuizAddSect   = $('btn-quiz-add-section');
    const quizSectionPicker= $('quiz-section-picker');
    const quizListEl       = $('quiz-list');
    const quizListError    = $('quiz-list-error');
    const resultsBody      = $('results-body');
    const resultsBtnBack   = $('btn-results-back');
    const resultsTitle     = $('results-quiz-title');
    const resultsCount     = $('results-count');
    const resultsSearch     = $('results-search');

    let currentQuizData      = null;
    let currentQuizId        = null;
    let currentResultsQuizId = null;
    // The quiz those responses answer – its topic and curriculum are what a
    // follow-up resource is set to.
    let currentResultsQuiz = null;
    // Where Back goes from a responses page. Arriving from a student's record
    // and being returned to the quizzes tab loses your place in the thing you
    // were actually doing.
    let resultsReturnTo = 'quizzes';
    let markingReturnTo = 'marking';

    /**
     * Scrolls to the response that was clicked and marks it, so landing in a
     * list of thirty does not mean hunting for the one you asked for.
     */
    function focusResponseCard(selector, responseId) {
        const el = document.querySelector(`${selector}[data-response-id="${responseId}"]`);
        if (!el) return;
        document.querySelectorAll('.is-focused-response').forEach(n => n.classList.remove('is-focused-response'));
        el.classList.add('is-focused-response');
        // rAF so the row is laid out before we scroll to where it will be.
        requestAnimationFrame(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
    let currentResponsesData = [];
    let currentQuestionsMap  = {};
    let quizReturnTo      = 'list';
    let quizAutoSaveTimer = null;

    function scheduleQuizSave() {
        clearTimeout(quizAutoSaveTimer);
        if (quizSaveStatus) quizSaveStatus.textContent = '';
        quizAutoSaveTimer = setTimeout(saveQuiz, 1000);
    }

    async function saveQuiz() {
        if (!currentQuizData) return;
        const body = {
            title:     currentQuizData.title || '',
            topic:     topicLineFor(quizTopicEl, quizTopicPicker),
            examBoard: quizExamBoard?.value || '',
            subject:   quizSubject?.value.trim() || '',
            level:     quizLevel?.value || '',
            sourceType: quizSourcePres?.value ? 'presentation' : undefined,
            sourceId:   quizSourcePres?.value || undefined,
            data_json: currentQuizData,
        };
        try {
            if (quizSaveStatus) quizSaveStatus.textContent = 'Saving…';
            let res;
            if (currentQuizId) {
                res = await apiFetch(`/api/quizzes/${currentQuizId}`, {
                    method: 'PUT', body: JSON.stringify(body),
                });
            } else {
                res = await apiFetch('/api/quizzes', {
                    method: 'POST', body: JSON.stringify(body),
                });
                if (res.ok) {
                    const d = await res.json();
                    currentQuizId = d.id;
                    linkToTutoringSession('quiz', d.id);
                    if (window.location.pathname !== `/quizzes/${d.id}`) {
                        history.replaceState({ quizId: d.id }, '', `/quizzes/${d.id}`);
                    }
                    if (btnQuizShare) btnQuizShare.disabled = false;
                    if (btnQuizSendBA) btnQuizSendBA.disabled = false;
                    if (btnQuizExport) btnQuizExport.disabled = false;
                    syncQuizSetWork();
                }
            }
            if (quizSaveStatus) {
                quizSaveStatus.textContent = res.ok ? 'Saved' : 'Save failed';
                setTimeout(() => { if (quizSaveStatus) quizSaveStatus.textContent = ''; }, 2000);
            }
        } catch {
            if (quizSaveStatus) quizSaveStatus.textContent = '';
        }
    }

    function renderQuizEditor(data) {
        if (!quizEditorEl) return;
        watchMathPreviews(quizEditorEl);
        quizEditorEl.innerHTML = '';
        data.sections.forEach(section => quizEditorEl.appendChild(buildSectionCard(section, currentQuizData, scheduleQuizSave)));
    }

    function syncQuizSectionOrder() {
        if (!currentQuizData || !quizEditorEl) return;
        currentQuizData.sections =
            Array.from(quizEditorEl.querySelectorAll('.ws-edit-card')).map(c => c._section).filter(Boolean);
    }

    // Quiz editor drag-to-reorder
    let quizDragCard = null;
    quizEditorEl?.addEventListener('mousedown', (e) => {
        if (e.target.closest('.ws-drag-handle')) {
            const card = e.target.closest('.ws-edit-card');
            if (card) card.setAttribute('draggable', 'true');
        }
    });
    quizEditorEl?.addEventListener('dragstart', (e) => {
        const card = e.target.closest('.ws-edit-card[draggable]');
        if (!card) return;
        quizDragCard = card;
        e.dataTransfer.effectAllowed = 'move';
        setTimeout(() => card.classList.add('ws-drag-active'), 0);
    });
    quizEditorEl?.addEventListener('dragend', () => {
        if (quizDragCard) {
            quizDragCard.classList.remove('ws-drag-active');
            quizDragCard.removeAttribute('draggable');
        }
        quizEditorEl.querySelectorAll('.ws-drag-over').forEach(el => el.classList.remove('ws-drag-over'));
        quizDragCard = null;
        syncQuizSectionOrder();
        scheduleQuizSave();
    });
    quizEditorEl?.addEventListener('dragover', (e) => {
        if (!quizDragCard) return;
        e.preventDefault();
        const target = e.target.closest('.ws-edit-card');
        if (!target || target === quizDragCard) return;
        const rect = target.getBoundingClientRect();
        if (e.clientY < rect.top + rect.height / 2) {
            quizEditorEl.insertBefore(quizDragCard, target);
        } else {
            quizEditorEl.insertBefore(quizDragCard, target.nextSibling);
        }
    });

    // Quiz section picker
    btnQuizAddSect?.addEventListener('click', (e) => {
        e.stopPropagation();
        quizSectionPicker?.hasAttribute('hidden') ? show(quizSectionPicker) : hide(quizSectionPicker);
    });
    quizSectionPicker?.addEventListener('click', (e) => {
        const btn = e.target.closest('.quiz-section-pick-btn');
        if (!btn || !currentQuizData) return;
        hide(quizSectionPicker);
        const newSection = { type: btn.dataset.type, questions: [makeEmptyQuestion(btn.dataset.type)] };
        currentQuizData.sections.push(newSection);
        const card = buildSectionCard(newSection, currentQuizData, scheduleQuizSave);
        quizEditorEl?.appendChild(card);
        scheduleQuizSave();
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        card.querySelector('input')?.focus();
    });
    document.addEventListener('click', (e) => {
        if (quizSectionPicker && !quizSectionPicker.hasAttribute('hidden') &&
            !btnQuizAddSect?.contains(e.target) && !quizSectionPicker.contains(e.target)) {
            hide(quizSectionPicker);
        }
    });

    // Quiz title input
    quizTitleInput?.addEventListener('input', () => {
        if (currentQuizData) {
            currentQuizData.title = quizTitleInput.value;
            scheduleQuizSave();
        }
    });

    function showQuizPage(topic, returnTo, examBoard, subject, level, sourcePresId, context) {
        quizReturnTo    = returnTo || 'list';
        currentQuizData = null;
        currentQuizId   = null;
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(quizView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        show(quizPageForm);
        hide(quizPagePreview);
        setError(quizError, '');
        if (quizTopicEl)   quizTopicEl.value   = topic     || '';
        if (quizContext) quizContext.value = context || '';
        clearSourceFiles('quiz-context');
        setQuizContextOpen(!!context);
        if (quizExamBoard) quizExamBoard.value  = examBoard || '';
        quizSubjectCombobox.set?.(subject || '');
        if (quizLevel)     quizLevel.value      = level     || '';
        populateSourcePresPicker(quizSourcePres, sourcePresId);
        // A new quiz starts as an ordinary one: the starter activity panel shut, and
        // emptied so it reloads against whatever has been made since.
        setSourcePanel('quiz-context', 'donow', false);
        $('quiz-donow-list')?.replaceChildren();
        syncAllWsTiles();
        refreshIgcseHints();
        // Set in code, so no change event fired – see showWorksheetPage.
        refreshQuizTopics();
        quizWizard?.setStep(1, { focus: false });
        if (topic) quizTopicEl?.focus();
        if (btnQuizShare)  btnQuizShare.disabled = true;
        if (btnQuizSendBA) btnQuizSendBA.disabled = true;
        if (btnQuizExport) btnQuizExport.disabled = true;
        hide(quizShareResult);
        if (window.location.pathname !== '/quizzes') history.pushState({ isNewQuiz: true }, '', '/quizzes');
    }

    async function openQuiz(id) {
        try {
            const res = await apiFetch(`/api/quizzes/${id}`);
            if (!res.ok) { showPresList('quizzes'); return; }
            const qz = await res.json();

            currentQuizId   = qz.id;
            currentQuizData = qz.data_json;
            quizReturnTo    = 'list';
            syncQuizSetWork();

            if (quizTopicEl)   quizTopicEl.value   = qz.topic      || '';
            if (quizContext) quizContext.value = '';
            clearSourceFiles('quiz-context');
            setQuizContextOpen(false);
            if (quizExamBoard) quizExamBoard.value  = qz.exam_board || '';
            quizSubjectCombobox.set?.(qz.subject || '');
            if (quizLevel)     quizLevel.value      = qz.level      || '';
            syncAllWsTiles();
            refreshIgcseHints();
        refreshIgcseHints();
            populateSourcePresPicker(quizSourcePres, qz.source_type === 'presentation' ? qz.source_id : '');

            hide(authView, presListView, bookletView, generatorView, worksheetView, quizResultsView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
            show(quizView, btnLogout, navCreateWrap, navStudentsBtn);
            if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
            show(navUserEmail);
            setError(quizError, '');
            hide(quizPageForm);
            show(quizPagePreview);
            if (btnQuizShare)  btnQuizShare.disabled = false;
            if (btnQuizSendBA) btnQuizSendBA.disabled = false;
            if (btnQuizExport) btnQuizExport.disabled = false;
            hide(quizShareResult);

            if (window.location.pathname !== `/quizzes/${id}`) {
                history.pushState({ quizId: id }, '', `/quizzes/${id}`);
            }
            if (quizTitleInput) quizTitleInput.value = currentQuizData?.title || '';
            renderQuizEditor(currentQuizData);
            renderPackSide(
                qz.source_type === 'presentation' ? qz.source_id : null,
                { type: 'quiz', id: qz.id }
            );
        } catch {}
    }

    // Enter moves the composer on rather than firing generation from the first
    // field – the two steps after this one exist to be seen at least once.
    quizTopicEl?.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (quizWizard && quizWizard.step < quizWizard.count) quizWizard.next();
        else btnGenerateQuiz?.click();
    });

    // The starter activity panel: its lessons load the first time it opens, and it only
    // counts while it is open - closing it is how a teacher changes their mind.
    const quizDoNowList = $('quiz-donow-list');
    $('quiz-donow-toggle')?.addEventListener('click', () => {
        if (quizDoNowList && !quizDoNowList.children.length) {
            renderRecallList(quizDoNowList, { subject: quizSubject?.value.trim() || '' });
        }
    });

    function quizStarterSources() {
        const panel = quizDoNowList?.closest('[data-src-panel]');
        return panel && !panel.hidden ? recallTicked(quizDoNowList) : [];
    }

    btnGenerateQuiz?.addEventListener('click', async () => {
        const starter = quizStarterSources();
        // A starter needs no topic of its own: it is about the lessons ticked.
        const topic = topicLineFor(quizTopicEl, quizTopicPicker)
            || (starter.length ? ('Starter activity: ' + starter.map(src => src.title).join(', ')).slice(0, TOPIC_LINE_MAX) : '');
        if (!topic) { setError(quizError, 'Please enter a topic or tick one from the list'); return; }

        const selectedTypes = Array.from(
            document.querySelectorAll('#quiz-type-checks input[type="checkbox"]:checked')
        ).map(el => el.value);
        if (!selectedTypes.length) {
            // The types live on the last step, so send them there to fix it.
            quizWizard?.last();
            setError(quizError, 'Select at least one question type');
            return;
        }

        btnGenerateQuiz.disabled = true;
        btnGenerateQuiz.textContent = 'Generating…';
        setError(quizError, '');

        try {
            const res = await apiFetch('/api/quizzes/generate', {
                method: 'POST',
                body: JSON.stringify({
                    topic,
                    sourcePresentationId: quizSourcePres?.value || undefined,
                    starterSourceIds: starter.length ? starter.map(src => src.id) : undefined,
                    context:   sourceTextFor('quiz-context'),
                    types:     selectedTypes,
                    examBoard: quizExamBoard?.value || '',
                    subject:   quizSubject?.value.trim() || '',
                    level:     quizLevel?.value || '',
                    tier:      $('quiz-tier')?.value || '',
                    ...topicPartsFor(quizTopicEl, quizTopicPicker),
                    language:  $('quiz-language')?.value || '',
                }),
            });
            const data = await res.json();
            if (!res.ok) { setError(quizError, data.error || 'Generation failed'); return; }

            currentQuizData = normalizeWorksheetData(data);
            hide(quizPageForm);
            show(quizPagePreview);
            setError(quizError, '');
            if (quizTitleInput) quizTitleInput.value = currentQuizData.title || '';
            renderQuizEditor(currentQuizData);
            if (btnQuizShare)  btnQuizShare.disabled = true;
            if (btnQuizSendBA) btnQuizSendBA.disabled = true;
            hide(quizShareResult);
            await saveQuiz();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(quizError, 'Network error');
        } finally {
            btnGenerateQuiz.disabled = false;
            btnGenerateQuiz.textContent = 'Generate quiz';
        }
    });

    btnQuizBack?.addEventListener('click', () => {
        hide(quizPagePreview);
        show(quizPageForm);
        quizWizard?.setStep(1);
    });

    btnQuizSendBA?.addEventListener('click', showQuizBaeduModal);

    btnQuizShare?.addEventListener('click', async () => {
        if (!currentQuizId) return;
        btnQuizShare.disabled = true;
        btnQuizShare.textContent = 'Getting link…';
        try {
            const res = await apiFetch(`/api/quizzes/${currentQuizId}/share`, { method: 'POST' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed');
            const fullUrl = window.location.origin + data.url;
            if (quizShareUrl) quizShareUrl.textContent = fullUrl;
            show(quizShareResult);
            await openShareModal(fullUrl, data.rosterMode, data.rosterClass);
        } catch (err) {
            alert('Could not create share link. Please try again.');
        } finally {
            btnQuizShare.disabled = false;
            btnQuizShare.textContent = 'Share with Students';
        }
    });

    btnQuizCopyLink?.addEventListener('click', () => {
        const url = quizShareUrl?.textContent;
        if (!url) return;
        navigator.clipboard.writeText(url).then(() => showToast('Quiz link copied'));
    });

    // ── Share dialog ──────────────────────────────────────
    // Who a quiz link is for. A dialog rather than controls wedged into the
    // toolbar strip: the choice carries a privacy consequence and has to have
    // room to say so.

    const shareModal = $('share-modal');

    function closeShareModal() {
        hide(shareModal);
        document.removeEventListener('keydown', shareKeydown);
    }
    function shareKeydown(e) {
        if (e.key === 'Escape') closeShareModal();
    }

    /**
     * Opens the dialog for the current quiz, filled in from whatever the link
     * is already set to.
     */
    async function openShareModal(url, mode, className) {
        if (!shareModal) return;
        $('share-url').value = url;
        $('share-status').textContent = '';

        const roster = await getRoster();
        const classes = [...new Set(roster.map(s => s.classLabel).filter(Boolean))].sort();

        // With no roster there is nobody to pick from, so the option is not
        // offered at all rather than sitting there inert.
        const rosterOpt = $('share-opt-roster');
        rosterOpt.hidden = !roster.length;
        $('share-no-roster').hidden = !!roster.length;

        const sel = $('share-class');
        sel.innerHTML = '<option value="all">Everyone on my roster</option>'
            + classes.map(c => `<option value="${esc(c)}">${esc(c)}</option>`).join('');

        const usingRoster = (mode === 'all' || mode === 'class') && roster.length;
        shareModal.querySelectorAll('[name="share-audience"]').forEach(r => {
            r.checked = usingRoster ? r.value === 'roster' : r.value === 'none';
        });
        sel.value = mode === 'class' && className && classes.includes(className) ? className : 'all';
        paintShareOptions();

        show(shareModal);
        document.addEventListener('keydown', shareKeydown);
        $('share-copy')?.focus();
    }

    // Saved as it is chosen – there is no Save button, so leaving the dialog
    // can never mean losing the setting.
    async function saveShareAudience() {
        if (!currentQuizId) return;
        const usingRoster = shareModal.querySelector('[name="share-audience"]:checked')?.value === 'roster';
        const cls = $('share-class').value;
        const body = !usingRoster ? { rosterMode: 'none' }
            : cls === 'all' ? { rosterMode: 'all' }
            : { rosterMode: 'class', rosterClass: cls };

        const status = $('share-status');
        try {
            const res = await apiFetch(`/api/quizzes/${currentQuizId}/share/roster`, {
                method: 'PUT', body: JSON.stringify(body),
            });
            if (!res.ok) { status.textContent = 'Could not save that'; return; }
            status.textContent = 'Saved';
            setTimeout(() => { if (status.textContent === 'Saved') status.textContent = ''; }, 2000);
        } catch { status.textContent = 'Could not save that'; }
    }

    // Which option is chosen, as a class on the row – see the CSS note.
    function paintShareOptions() {
        shareModal?.querySelectorAll('.share-opt').forEach(opt => {
            opt.classList.toggle('is-selected', !!opt.querySelector('input')?.checked);
        });
    }

    shareModal?.addEventListener('change', (e) => {
        if (e.target.name !== 'share-audience' && e.target.id !== 'share-class') return;
        paintShareOptions();
        saveShareAudience();
    });
    shareModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-share-close]')) closeShareModal();
    });
    $('share-close')?.addEventListener('click', closeShareModal);
    $('share-done')?.addEventListener('click', closeShareModal);
    $('share-copy')?.addEventListener('click', () => {
        const url = $('share-url').value;
        if (!url) return;
        navigator.clipboard.writeText(url).then(() => {
            $('share-status').textContent = 'Link copied';
            setTimeout(() => { if ($('share-status').textContent === 'Link copied') $('share-status').textContent = ''; }, 2000);
        });
    });

    // Reopening from the strip, to change who it is for after the fact.
    $('btn-quiz-share-settings')?.addEventListener('click', async () => {
        if (!currentQuizId) return;
        try {
            const res = await apiFetch(`/api/quizzes/${currentQuizId}/share`, { method: 'POST' });
            const data = await res.json();
            if (!res.ok) return;
            openShareModal(window.location.origin + data.url, data.rosterMode, data.rosterClass);
        } catch { /* the strip still shows the link */ }
    });

    // ── Quiz results ──────────────────────────────────────

    function buildQuestionsMap(dataJson) {
        const map = {};
        if (!dataJson) return map;

        if (Array.isArray(dataJson.sections)) {
            dataJson.sections.forEach(section => {
                if (!Array.isArray(section.questions)) return;
                section.questions.forEach((q, idx) => {
                    const key = section.type + '_' + idx;
                    map[key] = q;
                });
            });
        }

        ['multiple_choice', 'true_false', 'fill_blank', 'short_answer', 'whiteboard'].forEach(type => {
            if (Array.isArray(dataJson[type])) {
                dataJson[type].forEach((q, idx) => {
                    const key = type + '_' + idx;
                    map[key] = q;
                });
            }
        });

        return map;
    }

    function checkAnswer(answer, question) {
        if (answer.type === 'whiteboard') return null; // marked by eye, never auto-graded
        if (!question || question.answer === undefined || question.answer === null) return null;
        if (answer.type === 'fill_blank') {
            return String(answer.value).toLowerCase().trim() === String(question.answer).toLowerCase().trim();
        }
        return answer.value === question.answer;
    }

    function formatAnswerValue(val) {
        if (typeof val === 'boolean') return val ? 'True' : 'False';
        return String(val ?? '–');
    }

    /**
     * A multiple-choice letter, written out as the option it stands for.
     *
     * The letter is kept alongside the text rather than replaced by it: the
     * pupil ticked a box on a lettered list, and a teacher reading down a page
     * of answers is comparing letters.
     */
    function optionText(q, value) {
        const key = String(value ?? '').toLowerCase();
        const text = key && q ? q[key] : undefined;
        return text === undefined || text === null
            ? formatAnswerValue(value)
            : key.toUpperCase() + ' — ' + text;
    }

    /**
     * One response's score, counting the teacher's marks alongside the
     * automatic ones. Mirrors scoreResponse in utils/quizScoring.js – the
     * server stores the authoritative figure, this just avoids a round trip
     * to redraw a card the teacher is looking at.
     */
    function quizResponseScore(answers, manual = {}, questionsMap = currentQuestionsMap) {
        let correct = 0, total = 0, needsMarking = 0;
        answers.forEach(a => {
            const key = a.type + '_' + a.idx;
            const override = manual[key];
            if (override !== undefined && override !== null) {
                total++;
                if (Number(override)) correct++;
                return;
            }
            // The map is a parameter because this is also called from the
            // markbook's work dialog, which opens over a class page where the
            // quiz results - and so currentQuestionsMap - were never loaded.
            // Defaulting to it scored every question as unmarkable and reported
            // a finished paper as "Not marked".
            const auto = checkAnswer(a, questionsMap[key]);
            if (auto === null) { needsMarking++; return; }
            total++;
            if (auto) correct++;
        });
        return { correct, total, needsMarking };
    }

    /**
     * The line telling the teacher this set is not finished.
     *
     * Without it, a quiz containing whiteboard questions just quietly reports
     * lower totals than it was worth, and nothing anywhere says why.
     */
    function refreshQuizMarkingBanner() {
        const el = $('quiz-results-marking');
        if (!el) return;
        const outstanding = (currentResponsesData || []).reduce((n, r) => {
            const manual = (typeof r.manual_marks_json === 'string'
                ? JSON.parse(r.manual_marks_json || '{}')
                : r.manual_marks_json) || {};
            return n + quizResponseScore(Array.isArray(r.answers_json) ? r.answers_json : [], manual).needsMarking;
        }, 0);

        if (!outstanding) { el.hidden = true; return; }
        const people = (currentResponsesData || []).filter(r => {
            const manual = (typeof r.manual_marks_json === 'string'
                ? JSON.parse(r.manual_marks_json || '{}')
                : r.manual_marks_json) || {};
            return quizResponseScore(Array.isArray(r.answers_json) ? r.answers_json : [], manual).needsMarking > 0;
        }).length;
        el.innerHTML = `<b>${outstanding} answer${outstanding === 1 ? '' : 's'}</b> can&rsquo;t be marked automatically`
            + ` &ndash; drawings and written answers with no set answer. Award them below and they count towards`
            + ` ${people === 1 ? 'that student&rsquo;s' : 'each student&rsquo;s'} score.`;
        el.hidden = false;
    }

    /**
     * One response's questions, as HTML.
     *
     * Pulled out of the results list so a single pupil's paper can be shown on
     * its own - in the markbook's work dialog - without a second, drifting copy
     * of "what did they answer and was it right". The questions map is passed
     * in rather than read from currentQuestionsMap, because the dialog opens
     * over a class page where no quiz results are loaded.
     */
    function quizQuestionsHTML(answers, manual, questionsMap) {
        // Two buttons per question. Shown on every question, not only the
        // ones that need marking: the same panel is where a teacher
        // notices that "Paris." was marked wrong, and being unable to fix
        // it there would be the more annoying half of the problem.
        const awardHtml = (key, state, needed) =>
            '<div class="result-q-award' + (needed ? ' is-needed' : '') + '" data-award-key="' + esc(key) + '">' +
                (needed ? '<span class="result-q-award-label">Needs marking</span>' : '') +
                '<button type="button" class="award-btn award-yes' + (state === true ? ' is-on' : '') +
                    '" data-award="1" aria-pressed="' + (state === true) + '" title="Award the mark">\u2713</button>' +
                '<button type="button" class="award-btn award-no' + (state === false ? ' is-on' : '') +
                    '" data-award="0" aria-pressed="' + (state === false) + '" title="No mark">\u2717</button>' +
            '</div>';

        let qNum = 1;
        return answers.map((a) => {
            const key = a.type + '_' + a.idx;
            const q = questionsMap[key];
            const auto = checkAnswer(a, q);
            const override = manual[key];
            const hasOverride = override !== undefined && override !== null;
            // The teacher's mark is the one that counts.
            const correct = hasOverride ? !!Number(override) : auto;
            // A multiple-choice answer is stored as the letter that was ticked,
            // which is what the marker compares and is not what anybody should
            // be asked to read. "Answer: b" makes a teacher go back to the quiz
            // to find out what b was.
            const val = a.type === 'multiple_choice'
                ? optionText(q, a.value)
                : formatAnswerValue(a.value);

            let qText = '';
            if (q) {
                if (a.type === 'true_false') qText = q.statement || '';
                else if (a.type === 'fill_blank') qText = q.sentence || '';
                else qText = q.question || '';
            }

            if (a.type === 'whiteboard') {
                // Only render values that are verifiably an image data URL –
                // anything else can't be trusted inside a src attribute.
                const isImg = typeof a.value === 'string' &&
                    /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(a.value);
                const answerHtml = isImg
                    ? '<img class="result-q-img" src="' + a.value + '" alt="Whiteboard answer" title="Click to enlarge">'
                    : '<span class="result-q-value result-q-blank">No drawing submitted</span>';
                const indicativeHtml = q && q.answer
                    ? '<span class="result-q-correct">Indicative: ' + esc(formatAnswerValue(q.answer)) + '</span>'
                    : '';
                // A drawing can only ever be marked by a person, so the
                // award control is the point of this block rather than an
                // afterthought on it.
                return '<div class="result-q">' +
                    '<div class="result-q-text">' + esc(String(qNum++)) + '. ' + esc(qText) + '</div>' +
                    answerHtml +
                    indicativeHtml +
                    awardHtml(key, correct, !hasOverride) +
                '</div>';
            }

            let badgeHtml = '';
            let valueClass = 'result-q-value';
            let correctAnswerHtml = '';
            if (correct === true) {
                badgeHtml = '<span class="result-q-badge correct">\u2713</span>';
                valueClass += ' correct';
            } else if (correct === false) {
                badgeHtml = '<span class="result-q-badge incorrect">\u2717</span>';
                valueClass += ' incorrect';
                if (q && q.answer !== undefined && q.answer !== null) {
                    const correctVal = a.type === 'multiple_choice'
                        ? optionText(q, q.answer)
                        : formatAnswerValue(q.answer);
                    correctAnswerHtml = '<span class="result-q-correct">Correct: ' + esc(correctVal) + '</span>';
                }
            }

            return '<div class="result-q">' +
                '<div class="result-q-text">' + esc(String(qNum++)) + '. ' + esc(qText) + '</div>' +
                '<div class="result-q-row">' +
                    '<span class="result-q-label">Answer:</span>' +
                    '<span class="' + valueClass + '">' + esc(val) + '</span>' +
                    badgeHtml +
                '</div>' +
                correctAnswerHtml +
                (hasOverride ? '<span class="result-q-bymarker">Marked by you</span>' : '') +
                awardHtml(key, correct, auto === null && !hasOverride) +
            '</div>';
        }).join('');
    }

    /** The teacher's marks on a response, as the server keys them. */
    function manualMarksOf(resp) {
        return (typeof resp.manual_marks_json === 'string'
            ? JSON.parse(resp.manual_marks_json || '{}')
            : resp.manual_marks_json) || {};
    }

    /**
     * What one response got wrong, as text.
     *
     * The same verdict quizQuestionsHTML draws: the teacher's mark where there
     * is one, the automatic result otherwise. A question nobody has marked is
     * counted apart rather than called wrong – it may well be right.
     */
    function missedQuestions(answers, manual, questionsMap) {
        const missed = [];
        let unmarked = 0;
        answers.forEach((a) => {
            const key = a.type + '_' + a.idx;
            const q = questionsMap[key];
            const override = manual[key];
            const hasOverride = override !== undefined && override !== null;
            const correct = hasOverride ? !!Number(override) : checkAnswer(a, q);
            if (correct === null) { unmarked++; return; }
            if (correct || !q) return;

            const multi = a.type === 'multiple_choice';
            const hasKey = q.answer !== undefined && q.answer !== null;
            missed.push({
                type: a.type,
                question: a.type === 'true_false' ? q.statement
                    : a.type === 'fill_blank' ? q.sentence
                    : q.question,
                given: a.type === 'whiteboard' ? ''
                    : multi ? optionText(q, a.value) : formatAnswerValue(a.value),
                correct: !hasKey ? ''
                    : multi ? optionText(q, q.answer) : formatAnswerValue(q.answer),
            });
        });
        return { missed, unmarked };
    }

    /**
     * "Create follow-up" on a result: pick a kind of resource, and its composer
     * opens set to the quiz's topic and curriculum with the questions this
     * pupil got wrong in the notes box. The teacher reads it and generates –
     * nothing is made, or charged for, by pressing this.
     */
    function openFollowUpMenu(btn, { quiz, missed, unmarked, beforeOpen }) {
        if (!missed.length) {
            return showToast(unmarked
                ? `No wrong answers to build from yet – ${unmarked} still to mark`
                : 'No wrong answers to build from');
        }
        const { context } = window.QuizFollowUp.brief(missed, { title: quiz?.title });
        const open = (kind) => () => {
            beforeOpen?.();
            prepareFromSession(kind, {
                topic: quiz?.topic || quiz?.title || '',
                examBoard: quiz?.exam_board || '',
                subject: quiz?.subject || '',
                level: quiz?.level || '',
                context,
            });
        };
        openCardMenu(btn, [
            { label: 'Presentation', icon: LIST_ICONS.presentation, onClick: open('presentation') },
            { label: 'Worksheet', icon: LIST_ICONS.worksheet, onClick: open('worksheet') },
            { label: 'Quiz', icon: LIST_ICONS.quiz, onClick: open('quiz') },
            ...(bookletsAvailable()
                ? [{ label: 'Booklet', icon: LIST_ICONS.booklet, onClick: open('booklet') }] : []),
        ]);
        // The markbook's work dialog sits far above where card menus live.
        if (btn.closest('.cls-picker-modal')) openCardMenuEl?.classList.add('card-menu-over-modal');
    }

    function followUpButton(getArgs) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn-followup';
        btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4"/><path d="m12 8 1.4 2.6L16 12l-2.6 1.4L12 16l-1.4-2.6L8 12l2.6-1.4z"/></svg>'
            + '<span>Create follow-up</span>'
            + '<svg class="btn-followup-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
        btn.title = 'Make a resource from what they got wrong';
        btn.setAttribute('aria-haspopup', 'menu');
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            openFollowUpMenu(btn, getArgs());
        });
        return btn;
    }

    function renderResponseCards(responses) {
        resultsBody.innerHTML = '';
        responses.forEach((resp) => {
            const card = document.createElement('div');
            card.className = 'result-card t-acc';
            card.dataset.open = 'true';
            card.dataset.responseId = resp.id;

            const submittedAt = new Date(resp.submitted_at).toLocaleString();
            const answers = Array.isArray(resp.answers_json) ? resp.answers_json : [];

            // Marks the teacher has awarded by hand, keyed the same way the
            // server keys them (see utils/quizScoring.js).
            const manual = manualMarksOf(resp);

            const questionsHtml = quizQuestionsHTML(answers, manual, currentQuestionsMap);

            const score = quizResponseScore(answers, manual, currentQuestionsMap);

            card.innerHTML =
                '<div class="result-header t-acc-head">' +
                    '<span class="result-name">' + esc(resp.student_name) + '</span>' +
                    '<span class="result-score' + (score.needsMarking ? ' is-partial' : '') + '">' +
                        (score.total ? score.correct + ' / ' + score.total : 'Not marked') +
                        (score.needsMarking ? ' <em>+' + score.needsMarking + ' to mark</em>' : '') +
                    '</span>' +
                    '<span class="result-time">' + submittedAt + '</span>' +
                    '<button class="btn-icon btn-delete-response" title="Delete response">&times;</button>' +
                    '<span class="collapse-icon t-acc-chevron">&#9660;</span>' +
                '</div>' +
                '<div class="result-questions t-acc-panel"><div class="t-acc-panel-inner">' +
                    questionsHtml +
                '</div></div>';

            // A signed-in pupil's answers are already theirs; nothing to link.
            if (!resp.account_id) {
                const linkBtn = buildLinkButton(resp.student_id, async (studentId) => {
                    await apiFetch(`/api/quizzes/${currentResultsQuizId}/responses/${resp.id}/link`, {
                        method: 'PUT',
                        body: JSON.stringify({ studentId }),
                    });
                    resp.student_id = studentId;
                });
                card.querySelector('.result-header').insertBefore(linkBtn, card.querySelector('.btn-delete-response'));
            }

            // Read at the click, not here: marks awarded on this card since it
            // was drawn change what counts as wrong.
            card.querySelector('.result-header').insertBefore(followUpButton(() => ({
                quiz: currentResultsQuiz,
                ...missedQuestions(answers, manualMarksOf(resp), currentQuestionsMap),
            })), card.querySelector('.btn-delete-response'));

            // Awarding a mark. Saved immediately – there is no Save button on
            // this page and adding one just to hold two states would be a way
            // to lose marks by navigating away.
            card.querySelectorAll('.award-btn').forEach(btn => {
                btn.addEventListener('click', async (e) => {
                    e.stopPropagation();
                    const wrap = btn.closest('.result-q-award');
                    const key = wrap.dataset.awardKey;
                    const value = btn.dataset.award === '1' ? 1 : 0;
                    // Pressing the mark it already has takes it back off,
                    // returning the question to the automatic result.
                    const next = manual[key] === value ? null : value;

                    try {
                        const res = await apiFetch(
                            `/api/quizzes/${currentResultsQuizId}/responses/${resp.id}/marks`,
                            { method: 'PUT', body: JSON.stringify({ marks: { [key]: next } }) }
                        );
                        if (!res.ok) return showToast('Could not save that mark');
                        const data = await res.json();
                        resp.manual_marks_json = data.marks;
                        renderResponseCards(currentResponsesData);
                        refreshQuizMarkingBanner();
                    } catch { showToast('Could not save that mark'); }
                });
            });

            resultsBody.appendChild(card);
        });

        if (resultsCount) {
            if (!currentResponsesData.length) {
                resultsCount.textContent = '';
            } else if (responses.length === currentResponsesData.length) {
                resultsCount.textContent = responses.length + ' response' + (responses.length !== 1 ? 's' : '');
            } else {
                resultsCount.textContent = responses.length + ' of ' + currentResponsesData.length + ' response' + (currentResponsesData.length !== 1 ? 's' : '');
            }
        }
    }

    function filterResponses() {
        const query = (resultsSearch?.value || '').toLowerCase().trim();
        if (!query) {
            renderResponseCards(currentResponsesData);
            return;
        }
        const filtered = currentResponsesData.filter(resp => {
            if ((resp.student_name || '').toLowerCase().includes(query)) return true;
            const answers = Array.isArray(resp.answers_json) ? resp.answers_json : [];
            return answers.some(a => a.type !== 'whiteboard' && String(a.value ?? '').toLowerCase().includes(query));
        });
        renderResponseCards(filtered);
    }

    async function deleteResponse(responseId, cardEl) {
        try {
            const res = await apiFetch('/api/quizzes/' + currentResultsQuizId + '/responses/' + responseId, {
                method: 'DELETE',
            });
            if (!res.ok) { alert('Failed to delete response.'); return; }

            cardEl.remove();
            currentResponsesData = currentResponsesData.filter(r => r.id !== responseId);

            if (!currentResponsesData.length) {
                resultsBody.innerHTML = '<p style="color:var(--text-muted);">No responses yet. Share the quiz link with your students.</p>';
            }

            if (resultsCount) {
                resultsCount.textContent = currentResponsesData.length
                    ? currentResponsesData.length + ' response' + (currentResponsesData.length !== 1 ? 's' : '')
                    : '';
            }
        } catch (err) {
            alert('Network error deleting response.');
        }
    }

    /**
     * The responses for one quiz.
     *
     * `focusResponseId` is the response that was clicked to get here – coming
     * from a student's record you want that student's attempt, not the top of
     * a list of thirty. `returnTo` remembers where Back should go, so arriving
     * from Students does not strand you in the quizzes tab.
     */
    async function showQuizResults(quizId, { focusResponseId = null, returnTo = 'quizzes', push = true } = {}) {
        if (!resultsBody) return;
        currentResultsQuizId = quizId;
        resultsReturnTo = returnTo;
        // The label has to match where it actually goes, or Back becomes a
        // button you have to test rather than read.
        if (resultsBtnBack) {
            resultsBtnBack.innerHTML = returnTo === 'students' ? '&larr; Students' : '&larr; Back to Quizzes';
        }
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, billingView, markingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(quizResultsView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        resultsBody.innerHTML = '<p style="color:var(--text-muted);font-size:0.9rem;">Loading responses…</p>';
        const wantPath = `/quizzes/${quizId}/responses`;
        if (push && window.location.pathname !== wantPath) history.pushState({}, '', wantPath);

        try {
            const res  = await apiFetch('/api/quizzes/' + quizId + '/responses');
            const data = await res.json();
            if (!res.ok) { resultsBody.innerHTML = '<p>Failed to load responses.</p>'; return; }

            if (resultsTitle) resultsTitle.textContent = data.quiz.title || 'Quiz Responses';

            currentQuestionsMap = buildQuestionsMap(data.quiz.data_json);
            currentResultsQuiz = data.quiz;
            currentResponsesData = data.responses;

            if (!data.responses.length) {
                resultsBody.innerHTML = '<p style="color:var(--text-muted);">No responses yet. Share the quiz link with your students.</p>';
                if (resultsCount) resultsCount.textContent = '';
                const banner = $('quiz-results-marking');
                if (banner) banner.hidden = true;
                return;
            }

            if (resultsSearch) resultsSearch.value = '';
            renderResponseCards(data.responses);
            refreshQuizMarkingBanner();
            if (focusResponseId) focusResponseCard('.result-card', focusResponseId);
        } catch (err) {
            resultsBody.innerHTML = '<p>Network error loading responses.</p>';
        }
    }

    resultsBtnBack?.addEventListener('click', () => {
        if (resultsReturnTo === 'students') return showStudentsPage();
        showPresList('quizzes');
    });

    resultsSearch?.addEventListener('input', filterResponses);

    resultsBody?.addEventListener('click', (e) => {
        const deleteBtn = e.target.closest('.btn-delete-response');
        if (deleteBtn) {
            e.stopPropagation();
            const card = deleteBtn.closest('.result-card');
            const responseId = card.dataset.responseId;
            showConfirm({
                title: 'Delete this response?',
                message: 'The student’s answers and score will be permanently removed.',
                confirmLabel: 'Delete',
                danger: true
            }).then((ok) => { if (ok) deleteResponse(responseId, card); });
            return;
        }
        const img = e.target.closest('.result-q-img');
        if (img) {
            img.classList.toggle('expanded');
            return;
        }
        const header = e.target.closest('.result-header');
        if (header) {
            const rc = header.closest('.result-card');
            rc.classList.toggle('collapsed');
            rc.dataset.open = String(!rc.classList.contains('collapsed'));
        }
    });

    // ── Quiz list ─────────────────────────────────────────

    async function loadQuizzes() {
        if (!quizListEl) return;
        quizListEl.innerHTML = listSkeletonHTML();
        setError(quizListError, '');
        try {
            const res  = await apiFetch('/api/quizzes');
            const data = await res.json();
            if (!res.ok) { setError(quizListError, data.error || 'Failed to load'); return; }

            quizData = data;
            quizLoaded = true;
            renderTimeSaved();
            renderQuizzes();
        } catch (err) {
            if (err.message !== 'Not authenticated') setError(quizListError, 'Network error');
        }
    }

    function renderQuizzes() {
        if (!quizListEl || !quizLoaded) return;
        quizListEl.classList.remove('res-grid');

        const strip = folderStripEl('quiz');
        const inFolder = !!openFolderId.quiz;
        if (!quizData.length && !strip && !inFolder) {
            renderEmptyState(quizListEl, 'quiz',
                'No quizzes yet',
                'One link, whole class – watch answers arrive live. Students never need an account.',
                'Make your first quiz');
            return;
        }

        const grid = presViewMode === 'grid';
        quizListEl.classList.toggle('res-grid', grid);
        quizListEl.innerHTML = '';
        if (strip) quizListEl.appendChild(strip);
        const view = itemsInView('quiz', quizData);
        if (inFolder && !view.length) { quizListEl.appendChild(folderEmptyEl('quiz')); return; }

        view.forEach(qz => {
            const item = document.createElement('div');
            const actions = `
                <button class="pres-item-btn btn-responses" title="View responses">Responses</button>
                ${shareButtonHTML('quiz', qz)}
                ${moveButtonHTML()}
                <button class="pres-delete-btn" title="Delete quiz">${TRASH_ICON}</button>`;
            if (grid) {
                item.className = 'pres-card-item res-card res-quiz';
                item.innerHTML = resourceCardHTML('quiz', qz, actions);
            } else {
                item.className = 'pres-item';
                item.innerHTML = resourceRowHTML('quiz', qz, actions);
            }

            item.querySelector('.btn-responses').addEventListener('click', async (e) => {
                e.stopPropagation();
                await showQuizResults(qz.id);
            });
            bindShareButton(item, 'quiz', qz);
            item.querySelector('.pres-delete-btn').addEventListener('click', async (e) => {
                e.stopPropagation();
                const ok = await showConfirm({
                    title: 'Delete quiz?',
                    message: `"${qz.title || qz.topic}" will be permanently deleted, along with its student responses. This can’t be undone.`,
                    confirmLabel: 'Delete',
                    danger: true
                });
                if (!ok) return;
                try {
                    const r = await apiFetch(`/api/quizzes/${qz.id}`, { method: 'DELETE' });
                    if (r.ok) { quizData = quizData.filter(x => x.id !== qz.id); dropFromFolderCount('quiz', qz); renderQuizzes(); renderTimeSaved(); }
                    else setError(quizListError, 'Delete failed');
                } catch { setError(quizListError, 'Network error'); }
            });
            bindMoveButton(item, 'quiz', qz);
            makeLibDraggable(item, 'quiz', qz);
            markSelectable(item, 'quiz', qz);
            item.addEventListener('click', (e) => {
                if (e.target.closest('.pres-delete-btn') || e.target.closest('.btn-responses') || e.target.closest('.pres-share-btn') || e.target.closest('.pres-folder-btn')) return;
                openQuiz(qz.id);
            });
            quizListEl.appendChild(item);
        });
    }

    // ── Stripe.js (lazy-loaded, only needed for 3DS) ─────
    let stripeJsPromise = null;
    let stripeInstance = null;
    function loadStripeJs(publishableKey) {
        if (window.Stripe) {
            if (!stripeInstance) stripeInstance = window.Stripe(publishableKey);
            return Promise.resolve(stripeInstance);
        }
        if (!stripeJsPromise) {
            stripeJsPromise = new Promise((resolve, reject) => {
                const s = document.createElement('script');
                s.src = 'https://js.stripe.com/v3/';
                s.onload = () => resolve();
                s.onerror = () => { stripeJsPromise = null; reject(new Error('Couldn\'t load payment verification. Check your connection and try again.')); };
                document.head.appendChild(s);
            });
        }
        return stripeJsPromise.then(() => {
            if (!stripeInstance) stripeInstance = window.Stripe(publishableKey);
            return stripeInstance;
        });
    }

    // ── Confirm dialog (custom window.confirm) ───────────
    let confirmResolve = null;
    let confirmWired = false;

    function closeConfirm(result) {
        const modal = $('confirm-modal');
        if (!isShown(modal)) return;
        hide(modal);
        document.removeEventListener('keydown', confirmKeydown);
        const resolve = confirmResolve;
        confirmResolve = null;
        if (resolve) resolve(result);
    }

    function confirmKeydown(e) {
        if (e.key === 'Escape') { e.preventDefault(); closeConfirm(false); }
    }

    function showConfirm(opts = {}) {
        const modal = $('confirm-modal');
        if (!modal) return Promise.resolve(window.confirm(opts.message || 'Are you sure?'));
        if (!confirmWired) {
            confirmWired = true;
            $('confirm-ok-btn').addEventListener('click', () => closeConfirm(true));
            modal.addEventListener('click', (e) => {
                if (e.target.closest('[data-confirm-cancel]')) closeConfirm(false);
            });
        }
        closeConfirm(false); // resolve any pending dialog as cancelled
        $('confirm-title').textContent = opts.title || 'Are you sure?';
        $('confirm-message').textContent = opts.message || '';
        const okBtn = $('confirm-ok-btn');
        okBtn.textContent = opts.confirmLabel || 'Confirm';
        $('confirm-cancel-btn').textContent = opts.cancelLabel || 'Cancel';
        modal.classList.toggle('confirm-danger', !!opts.danger);
        show(modal);
        document.addEventListener('keydown', confirmKeydown);
        (opts.danger ? $('confirm-cancel-btn') : okBtn).focus();
        return new Promise((resolve) => { confirmResolve = resolve; });
    }

    // ── Students ──────────────────────────────────────────
    // The roster, the queue of work that arrived under a name nobody
    // recognises, and one student's record. Lives at /students rather than in
    // the library tabs: the library is what the AI made, and a class list is
    // not one of those.

    let rosterCache = null;

    async function getRoster() {
        if (rosterCache) return rosterCache;
        try {
            const res = await apiFetch('/api/students');
            const data = res.ok ? await res.json() : null;
            rosterCache = data?.students || [];
        } catch { rosterCache = []; }
        return rosterCache;
    }
    function invalidateRoster() { rosterCache = null; }

    // Names that look like one pupil typing two ways. Shown in the nav for the
    // reason the unmatched count used to be: it is quiet data loss. A child
    // split across two spellings has two half-records and two wrong averages,
    // and nothing else on screen would ever say so.
    function setUnmatchedBadge(n) {
        const badge = $('nav-students-badge');
        if (!badge) return;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.hidden = !n;
        badge.title = `${n} name${n === 1 ? '' : 's'} that might be the same pupil twice`;
    }

    async function refreshUnmatchedBadge() {
        try {
            const res = await apiFetch('/api/students');
            if (!res.ok) return;
            const data = await res.json();
            setUnmatchedBadge((data.suggestions || []).length);
        } catch { /* a badge is not worth an error message */ }
    }

    /**
     * A sparkline: which way is this going, at a glance. No axes and no
     * labels on purpose – the numbers are one click away, and a 90px chart
     * with tick marks is just a smaller unreadable chart.
     */
    function sparklineSVG(values, w = 88, h = 24) {
        if (!Array.isArray(values) || values.length < 2) return '';
        const min = Math.min(...values);
        const max = Math.max(...values);
        const span = max - min;
        const pad = 3;
        const pts = values.map((v, i) => {
            const x = pad + (i / (values.length - 1)) * (w - pad * 2);
            // A flat run sits on the centre line rather than the floor, which
            // would read as a collapse to zero.
            const y = span === 0 ? h / 2 : h - pad - ((v - min) / span) * (h - pad * 2);
            return [x, y];
        });
        const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
        const [lx, ly] = pts[pts.length - 1];
        const area = `${pad},${h} ${line} ${(w - pad).toFixed(1)},${h}`;
        return `<svg class="stu-spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="Recent scores: ${values.join(', ')} percent">
            <polygon class="stu-spark-area" points="${area}"/>
            <polyline class="stu-spark-line" points="${line}"/>
            <circle class="stu-spark-dot" cx="${lx.toFixed(1)}" cy="${ly.toFixed(1)}" r="2.6"/>
        </svg>`;
    }

    // ── Searchable student picker ─────────────────────────
    // Used from a quiz or marking response to attach it to somebody. A
    // popover with a search box rather than a <select>: a roster runs to
    // hundreds of names, and you cannot type into a native dropdown.

    let openPickerEl = null;

    function closeStudentPicker() {
        if (!openPickerEl) return;
        openPickerEl.remove();
        openPickerEl = null;
        document.querySelectorAll('.stu-link-btn[aria-expanded="true"]')
            .forEach(b => b.setAttribute('aria-expanded', 'false'));
    }

    document.addEventListener('click', (e) => {
        if (!openPickerEl) return;
        if (e.target.closest('.stu-picker') || e.target.closest('.stu-link-btn')) return;
        closeStudentPicker();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeStudentPicker(); });
    window.addEventListener('scroll', closeStudentPicker, true);
    window.addEventListener('resize', closeStudentPicker);

    async function openStudentPicker(btn, currentId, onPick) {
        const wasOpen = btn.getAttribute('aria-expanded') === 'true';
        closeStudentPicker();
        if (wasOpen) return;

        const roster = await getRoster();
        const pop = document.createElement('div');
        pop.className = 'stu-picker';
        pop.innerHTML = `
            <input type="search" class="stu-picker-search" placeholder="Search students" aria-label="Search students">
            <div class="stu-picker-list"></div>`;
        document.body.appendChild(pop);

        const listEl = pop.querySelector('.stu-picker-list');
        const searchEl = pop.querySelector('.stu-picker-search');

        const renderList = (q) => {
            const query = q.trim().toLowerCase();
            const matches = roster.filter(s => !query || s.name.toLowerCase().includes(query));
            listEl.innerHTML = '';

            if (currentId) {
                const clear = document.createElement('button');
                clear.type = 'button';
                clear.className = 'stu-picker-item is-clear';
                clear.textContent = 'Not linked';
                clear.addEventListener('click', () => { closeStudentPicker(); onPick(null); });
                listEl.appendChild(clear);
            }

            if (!matches.length) {
                const empty = document.createElement('p');
                empty.className = 'stu-picker-empty';
                empty.textContent = roster.length ? 'No match' : 'No students on your roster yet';
                listEl.appendChild(empty);
                return;
            }

            matches.slice(0, 60).forEach(s => {
                const item = document.createElement('button');
                item.type = 'button';
                item.className = 'stu-picker-item';
                item.setAttribute('aria-selected', String(s.id === currentId));
                item.innerHTML = `<span>${esc(s.name)}</span>${s.classLabel ? `<span class="stu-class-chip">${esc(s.classLabel)}</span>` : ''}`;
                item.addEventListener('click', () => { closeStudentPicker(); onPick(s.id); });
                listEl.appendChild(item);
            });
        };

        renderList('');
        searchEl.addEventListener('input', () => renderList(searchEl.value));

        // Positioned like the card overflow menu: fixed to the viewport and
        // flipped above the button when there is no room beneath it.
        const r = btn.getBoundingClientRect();
        const left = Math.max(8, Math.min(r.left, window.innerWidth - pop.offsetWidth - 12));
        const below = r.bottom + 6;
        const top = below + pop.offsetHeight + 8 > window.innerHeight
            ? Math.max(8, r.top - pop.offsetHeight - 6)
            : below;
        pop.style.left = `${left}px`;
        pop.style.top = `${top}px`;

        btn.setAttribute('aria-expanded', 'true');
        openPickerEl = pop;
        searchEl.focus();
    }

    /**
     * The control on a response row saying who this work belongs to.
     * `onLink(studentId|null)` performs the save; this owns the button and
     * its label.
     *
     * Only ever for a TYPED name. Work handed in by a signed-in pupil already
     * belongs to somebody outright, and offering to file it under a roster name
     * as well would put one child in both flows at once - the thing the two
     * were separated to stop. Callers pass the row's account_id to accountRow()
     * and leave the control off when it is set.
     */
    function buildLinkButton(studentId, onLink) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'stu-link-btn';
        btn.setAttribute('aria-expanded', 'false');

        let current = studentId || null;
        const paint = async () => {
            const roster = await getRoster();
            const student = current ? roster.find(s => s.id === current) : null;
            btn.classList.toggle('is-linked', !!student);
            btn.textContent = student ? student.name : 'Link student';
            btn.title = student ? `Counted towards ${student.name}` : 'Not counted towards anyone yet';
        };
        paint();

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            openStudentPicker(btn, current, async (id) => {
                current = id;
                paint();
                await onLink(id);
                refreshUnmatchedBadge();
            });
        });
        return btn;
    }

    // ── Classes ───────────────────────────────────────────────────────
    //
    // Classes live INSIDE the Students page, as a tab beside the roster.
    //
    // They used to be their own page, with the class detail a second page and
    // the assignment dashboard a third — so answering "how is 10B doing with
    // the homework" meant three navigations through two lists of the same
    // children. They are all one question about one group of pupils, so they
    // are now one page: the tab lists the classes, opening one swaps the pane,
    // and a piece of set work expands where it sits rather than sending the
    // teacher somewhere else and back.
    //
    // Organisation only — the tab is hidden outside a school, because a class
    // holds pupil logins and outside a school there are none.

    const classesView = $('classes-view');

    let classesCache = null;
    let currentClass = null;

    // What the Set work dialog is being asked to do, from whichever side the
    // teacher started on.
    let setWorkCtx = null;

    // ── Dialog helpers ────────────────────────────────────────────────

    function openModal(id) {
        const el = $(id);
        if (!el) return;
        show(el);
        const first = el.querySelector('input:not([type=hidden]), textarea, select');
        if (first) requestAnimationFrame(() => first.focus());
    }

    function closeModal(id) {
        const el = $(id);
        if (el) hide(el);
    }

    /**
     * Counts names in a pasted block, the same way the server does.
     *
     * Shown live under the box because the one thing a teacher wants to know
     * before pressing Create is whether their paste came out as 28 pupils or as
     * one line containing 28 names.
     */
    function countPastedNames(text) {
        const seen = new Set();
        for (const line of String(text || '').split(/[\n,]/)) {
            const name = line.replace(/^\s*\d+[.)]\s*/, '').trim().replace(/\s+/g, ' ');
            if (name) seen.add(name.toLowerCase());
        }
        return seen.size;
    }

    function wireNameCounter(boxId, outId) {
        const box = $(boxId);
        const out = $(outId);
        if (!box || !out) return;
        const update = () => {
            const n = countPastedNames(box.value);
            out.textContent = n ? `${n} ${n === 1 ? 'pupil' : 'pupils'}` : '';
        };
        box.addEventListener('input', update);
        update();
    }

    // ── The class list ────────────────────────────────────────────────

    /**
     * The Classes section of the Pupils page.
     *
     * Classes is the organisation flow and exists only inside a school, so the
     * section is absent rather than shown and then refused: there is nothing a
     * lone Pro teacher could do about a 402.
     */
    function syncClassesSection() {
        const pane = $('cls-list-pane');
        if (!pane) return;
        pane.hidden = !state.user?.org || tutoringOrg();
        if (!pane.hidden) loadClasses();
    }

    async function loadClasses() {
        const listEl = $('cls-list');
        if (!listEl) return;
        setError($('cls-error'), '');
        listEl.innerHTML = listSkeletonHTML();
        try {
            const res = await apiFetch('/api/classes');
            const data = await res.json();
            if (!res.ok) { listEl.innerHTML = ''; return setError($('cls-error'), data.error || 'Failed to load'); }
            classesCache = data.classes || [];
            renderClasses();
        } catch {
            listEl.innerHTML = '';
            setError($('cls-error'), 'Could not load your classes.');
        }
    }

    function renderClasses() {
        const listEl = $('cls-list');
        listEl.innerHTML = '';
        if (!classesCache.length) { show($('cls-empty')); return; }
        hide($('cls-empty'));

        // Year order, read as a number.
        //
        // The server sorts by year_group as a STRING, which puts Year 10 above
        // Year 8. Sorted here rather than there because it is a presentation
        // question and the label is free text — "Year 10", "10", "Sixth form" —
        // so anything without a number in it goes last rather than being
        // guessed at.
        //
        // Deliberately NOT sorted by which class needs attention most. A
        // teacher learns where 10B sits on this page, and a list that rearranges
        // itself as work comes in costs them that every time. The overdue
        // marker is what surfaces a problem.
        const yearOf = (c) => {
            const n = parseInt(String(c.yearGroup || '').replace(/\D+/g, ''), 10);
            return Number.isFinite(n) ? n : Infinity;
        };
        const ordered = [...classesCache].sort((a, b) =>
            yearOf(a) - yearOf(b) || a.name.localeCompare(b.name));

        for (const klass of ordered) {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'cls-row';

            const w = klass.work || { open: 0, overdue: 0, set: 0, handedIn: 0, owing: 0 };
            const owing = w.owing || 0;
            const pct = w.set ? Math.round((w.handedIn / w.set) * 100) : 0;

            // Pupils, not pieces of work: "5 pupils" is a number a teacher can
            // act on, where "11 outstanding items" against a class of eight is
            // arithmetic they have to do first.
            let status;
            if (!w.open) status = '<span class="cls-row-quiet">Nothing set</span>';
            else if (!owing) status = '<span class="cls-row-done">Everyone is up to date</span>';
            else status = `<strong>${owing}</strong> ${owing === 1 ? 'pupil' : 'pupils'} behind`;

            // Aligned columns, so a teacher reads DOWN the page — which class
            // needs me — rather than parsing each card as its own little
            // layout. Same reasoning as the markbook it opens into.
            row.innerHTML = `
                <span class="cls-row-name">${esc(klass.name)}</span>
                <span class="cls-row-year">${esc(klass.yearGroup || '')}</span>
                <span class="cls-row-size">${klass.memberCount} ${klass.memberCount === 1 ? 'pupil' : 'pupils'}</span>
                <span class="cls-row-bar${w.open ? '' : ' is-none'}" aria-hidden="true">
                    ${w.open ? `<span class="cls-row-bar-fill" style="width:${pct}%"></span>` : ''}
                </span>
                <span class="cls-row-status">${status}</span>
                <span class="cls-row-late">${w.overdue ? `<span class="cls-card-late">${w.overdue} overdue</span>` : ''}</span>`;
            row.addEventListener('click', () => openClass(klass.id));
            listEl.appendChild(row);
        }
    }

    // ── Making a class ────────────────────────────────────────────────

    function openNewClass() {
        $('newclass-form').reset();
        setError($('newclass-error'), '');
        $('newclass-count').textContent = '';
        openModal('newclass-modal');
    }

    /**
     * One submit does the lot: the class, the roster rows, the logins and the
     * membership. Then the printable slips, immediately, because that is the
     * next thing the teacher needs.
     */
    async function submitNewClass(event) {
        event.preventDefault();
        const btn = $('newclass-submit');
        setError($('newclass-error'), '');

        const name = $('newclass-name').value.trim();
        if (!name) return setError($('newclass-error'), 'Give the class a name.');

        btn.disabled = true;
        btn.textContent = 'Creating…';
        try {
            const res = await apiFetch('/api/classes', {
                method: 'POST',
                body: JSON.stringify({
                    name,
                    yearGroup: $('newclass-year').value.trim(),
                    names: $('newclass-names').value,
                }),
            });
            const data = await res.json();
            if (!res.ok) return setError($('newclass-error'), data.error || 'Could not make that class');

            closeModal('newclass-modal');
            await openClass(data.id);
            if (data.created?.length) showLoginSheet(data.created);
            else showToast('Class created.');
        } catch {
            setError($('newclass-error'), 'Could not make that class.');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Create class';
        }
    }

    async function submitAddPupils(event) {
        event.preventDefault();
        const btn = $('addpupils-submit');
        setError($('addpupils-error'), '');
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
            const res = await apiFetch(`/api/classes/${encodeURIComponent(currentClass.class.id)}/pupils`, {
                method: 'POST',
                body: JSON.stringify({ names: $('addpupils-names').value }),
            });
            const data = await res.json();
            if (!res.ok) return setError($('addpupils-error'), data.error || 'Could not add them');

            closeModal('addpupils-modal');
            await openClass(currentClass.class.id, false);
            if (data.created?.length) showLoginSheet(data.created);
            else showToast('Everyone in that list was already here.');
        } catch {
            setError($('addpupils-error'), 'Could not add them.');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Add them';
        }
    }

    // ── One class ─────────────────────────────────────────────────────

    /**
     * Opens a class inside the Students page.
     *
     * A pane swap, not a navigation: the tab bar, the nav and the page stay put
     * so a teacher moving between two classes is not walking a corridor.
     */
    async function openClass(id, push = true) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentProfileView, studentsView,
             studentMarkingView, lessonsView, classesView, tutoringView);
        show(classesView, btnLogout, navCreateWrap, navStudentsBtn);
        show($('cls-detail-pane'));
        if (push) history.pushState({}, '', '/classes/' + id);

        setError($('cls-detail-error'), '');
        $('cls-book').innerHTML = listSkeletonHTML();

        try {
            // The class and the work set to it together, so both halves of the
            // page arrive at once rather than the register appearing and the
            // homework popping in a moment later.
            const [clsRes, asgRes] = await Promise.all([
                apiFetch('/api/classes/' + encodeURIComponent(id)),
                apiFetch('/api/assignments'),
            ]);
            const data = await clsRes.json();
            if (!clsRes.ok) {
                $('cls-book').innerHTML = '';
                return setError($('cls-detail-error'), data.error || 'Failed to load');
            }
            const asg = asgRes.ok ? await asgRes.json() : { assignments: [] };
            currentClass = data;
            currentClass.assignments = (asg.assignments || []).filter(a => a.classId === id);
            renderClassDetail();
        } catch {
            $('cls-book').innerHTML = '';
            setError($('cls-detail-error'), 'Could not load that class.');
        }
    }

    /**
     * Where pupils sign in.
     *
     * Built from the browser's own origin rather than written down anywhere:
     * this app runs on a school's own address in some deployments and on
     * localhost in development, and a hard-coded domain would be a wrong answer
     * printed onto thirty children's login slips.
     */
    const pupilPortalUrl = () => window.location.origin + '/portal';

    /** The same address without the scheme, which is how people read one out. */
    const pupilPortalLabel = () => window.location.host + '/portal';

    function renderClassDetail() {
        const { class: klass, members, teachers, assignments } = currentClass;

        $('cls-detail-name').textContent = klass.name;

        // The header answers "how is this class doing" before anything else on
        // the page is read. The year group is real but nobody opens a class to
        // find it out, so it goes last and quietly.
        const open = assignments.filter(a => a.status === 'open');
        const owing = members.filter(m => m.set > 0 && m.handedIn < m.set).length;
        const late = open.filter(a => a.dueAt && new Date(a.dueAt) < new Date()
            && (a.handedIn || 0) < (a.targetCount || 0)).length;

        const state = [`${members.length} ${members.length === 1 ? 'pupil' : 'pupils'}`];
        if (!open.length) state.push('nothing set');
        else if (!owing) state.push('everyone is up to date');
        else state.push(`${owing} behind`);
        if (late) state.push(`${late} overdue`);
        if (klass.archived) state.push('archived');
        if (klass.yearGroup) state.push(klass.yearGroup);

        $('cls-detail-meta').textContent = state.join(' · ');

        const portalUrl = $('cls-portal-url');
        if (portalUrl) portalUrl.textContent = pupilPortalLabel();

        renderMarkbook();

        const teachersEl = $('cls-teachers');
        teachersEl.innerHTML = '';
        for (const t of teachers) {
            const row = document.createElement('div');
            row.className = 'cls-member';
            row.innerHTML = `<span class="cls-member-name">${esc(t.name || t.email)}</span>`
                + `<span class="cls-member-role">${t.role === 'owner' ? 'Made this class' : 'Teacher'}</span>`;
            if (teachers.length > 1) {
                const remove = document.createElement('button');
                remove.type = 'button';
                remove.className = 'cls-link-btn cls-link-danger';
                remove.textContent = t.isMe ? 'Leave' : 'Remove';
                remove.addEventListener('click', () => removeClassTeacher(t.id));
                row.appendChild(remove);
            }
            teachersEl.appendChild(row);
        }
    }

    /**
     * The markbook: pupils down the side, work across the top.
     *
     * Everything a teacher opens a class to find out is in one grid — who has
     * not done it (a gap in a column), who is struggling (a low row), which
     * task went badly (a low column), who is drifting (a sparse row). The four
     * separate panels this replaced answered one of those each, and none of
     * them at the same time as another.
     *
     * Cells are three-valued and NOT two: marked, handed in but not marked, and
     * nothing. Collapsing the middle state would show a pupil who did the work
     * on time as a gap, which is exactly the accusation a markbook must not
     * make by accident.
     */
    function renderMarkbook() {
        const { members, assignments } = currentClass;
        const book = $('cls-book');
        const empty = $('cls-book-empty');
        if (!book) return;

        const open = assignments.filter(a => a.status !== 'draft');
        if (empty) empty.hidden = open.length > 0 || members.length === 0;

        if (!members.length) {
            book.innerHTML = '<p class="cls-hint">Nobody in this class yet. '
                + 'Use <strong>Add pupils</strong> and paste your register.</p>';
            return;
        }

        // A class with nothing set is still a register, and the markbook draws
        // it with no columns rather than showing nothing at all — which is what
        // it did, so a teacher who had just made a class could not see whether
        // anybody was in it.

        // Oldest on the left, so a row reads left to right as a term does.
        const cols = [...open].sort((a, b) =>
            String(a.dueAt || a.createdAt || '').localeCompare(String(b.dueAt || b.createdAt || '')));

        const cellFor = new Map();
        for (const c of (currentClass.cells || [])) {
            cellFor.set(c.assignmentId + ':' + c.accountId, c);
        }

        const head = cols.map(a => {
            const overdue = a.dueAt && new Date(a.dueAt) < new Date()
                && (a.handedIn || 0) < (a.targetCount || 0);
            const due = a.dueAt
                ? new Date(a.dueAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                : 'No deadline';
            return `
                <button type="button" class="mb-col" data-asg="${esc(a.id)}"
                        title="${esc(a.title)}">
                    <span class="mb-col-name">${esc(a.title)}</span>
                    <span class="mb-col-due${overdue ? ' is-late' : ''}">${esc(due)}</span>
                </button>`;
        }).join('');

        const rows = members.map(m => {
            const cells = cols.map(a => {
                const c = cellFor.get(a.id + ':' + m.id);
                if (!c || !c.state) {
                    return `<span class="mb-cell is-none" title="Not handed in">&middot;</span>`;
                }
                // Read. Not a mark and not a gap: there was never anything to
                // mark, and a gap would say they had ignored it.
                if (c.state === 'read') {
                    return `<span class="mb-cell is-read" title="Opened it">&#10003;</span>`;
                }
                if (c.state === 'in') {
                    return `<button type="button" class="mb-cell is-in" data-work="${esc(c.workId || '')}"
                                    data-kind="${esc(c.kind || '')}" data-res="${esc(c.resourceId || '')}"
                                    data-name="${esc(m.displayName)}" data-title="${esc(a.title)}"
                                    title="Handed in, not marked">in</button>`;
                }
                // Banded so a row can be read as a shape rather than as six
                // numbers to compare in your head.
                const band = c.percent >= 70 ? 'hi' : c.percent >= 40 ? 'mid' : 'lo';
                return `<button type="button" class="mb-cell is-${band}" data-work="${esc(c.workId || '')}"
                                data-kind="${esc(c.kind || '')}" data-res="${esc(c.resourceId || '')}"
                                data-name="${esc(m.displayName)}" data-title="${esc(a.title)}"
                                title="${c.percent}%">${c.percent}</button>`;
            }).join('');

            const avg = m.average === null
                ? '<span class="mb-avg is-empty">&mdash;</span>'
                : `<span class="mb-avg">${m.average}%</span>`;

            return `
                <div class="mb-row">
                    <span class="mb-name">${esc(m.displayName)}${
                        !m.lastLoginAt && flagStragglersFor(members)
                            ? '<span class="cls-flag">Not signed in</span>' : ''}</span>
                    <span class="mb-cells">${cells}</span>
                    ${avg}
                    <span class="mb-actions">
                        <button type="button" class="cls-link-btn" data-reset="${esc(m.id)}">Reset password</button>
                        <button type="button" class="cls-link-btn cls-link-danger" data-remove="${esc(m.id)}">Remove</button>
                    </span>
                </div>`;
        }).join('');

        // The foot totals each column, which is the "how did the class do on
        // this" that a teacher asks after a deadline.
        const foot = cols.map(a => {
            const done = a.handedIn || 0;
            const set = a.targetCount || 0;
            return `<span class="mb-total${done < set ? ' is-short' : ''}">${done}/${set}</span>`;
        }).join('');

        book.innerHTML = `
            <div class="mb-scroll">
                <div class="mb-grid" style="--mb-cols:${cols.length}">
                    <div class="mb-head">
                        <span class="mb-name mb-head-name">${members.length} pupils</span>
                        <span class="mb-cells">${head}</span>
                        <span class="mb-avg mb-head-avg">Average</span>
                        <span class="mb-actions"></span>
                    </div>
                    ${rows}
                    <div class="mb-foot">
                        <!-- "Done", not "Handed in": a class can now hold a
                             column of reading, where nothing is ever handed in
                             and opening it is the whole of the task. -->
                        <span class="mb-name">Done</span>
                        <span class="mb-cells">${foot}</span>
                        <span class="mb-avg"></span>
                        <span class="mb-actions"></span>
                    </div>
                </div>
            </div>`;

        // A column header opens that piece of work; a cell opens that pupil's
        // paper. Both are the obvious meaning of clicking where you clicked.
        book.querySelectorAll('.mb-col').forEach(btn =>
            btn.addEventListener('click', () => openAssignment(btn.dataset.asg)));

        book.querySelectorAll('.mb-cell[data-work]').forEach(btn => {
            // Both halves are needed to open anything: the work id says which
            // paper, the resource id says which quiz or lesson it belongs to.
            // Without the second the old code navigated to /lessons/null.
            if (!btn.dataset.work || !btn.dataset.res) return;
            btn.addEventListener('click', () => openWorkModal({
                kind: btn.dataset.kind,
                resourceId: btn.dataset.res,
                workId: btn.dataset.work,
                name: btn.dataset.name,
                title: btn.dataset.title,
            }));
        });

        book.querySelectorAll('[data-reset]').forEach(btn => {
            const m = members.find(x => x.id === btn.dataset.reset);
            btn.addEventListener('click', () => resetPupilPassword(m.id, m.displayName));
        });
        book.querySelectorAll('[data-remove]').forEach(btn => {
            const m = members.find(x => x.id === btn.dataset.remove);
            btn.addEventListener('click', () => removeClassMember(m.id, m.displayName));
        });
    }

    /**
     * Whether "not signed in" is worth marking per pupil.
     *
     * Only when it is the exception. Six of eight wearing the same badge is a
     * wall of colour that says nothing, and the header already carries the
     * count — so a third of the class is where "a few stragglers" stops being
     * a true description.
     */
    function flagStragglersFor(members) {
        const n = members.filter(m => !m.lastLoginAt).length;
        return n > 0 && n <= members.length / 3;
    }

    /**
     * One pupil's paper, opened from their cell in the markbook.
     *
     * Over the class rather than instead of it. Clicking a mark asks one
     * question - what did this child actually write? - and the lesson's own
     * results page answers a different one, by replacing the class you were
     * reading with a list of everybody in it and making you find again the name
     * you had already picked.
     *
     * The marks are editable here too. Sending the teacher to the lesson's own
     * script pane to change one number put them back on the page they were
     * trying not to open - the whole class's roll, with the pupil they had
     * already chosen somewhere in it - so the dialog does the whole job.
     */
    async function openWorkModal(cell) {
        if (cell.kind !== 'lesson' && cell.kind !== 'quiz') {
            return openSubmission(
                { id: cell.workId, type: cell.kind, resourceId: cell.resourceId }, null);
        }

        const wrap = document.createElement('div');
        wrap.className = 'cls-picker-modal work-modal';
        wrap.innerHTML = `
            <div class="share-backdrop" data-act="cancel"></div>
            <div class="share-card t-modal" role="dialog" aria-modal="true" aria-label="${esc(cell.name || 'Their work')}">
                <header class="share-head">
                    <h2 class="share-title">${esc(cell.name || 'Their work')}</h2>
                    <p class="work-sub">
                        <span>${esc(cell.title || '')}</span>
                        <span class="work-score" data-role="score"></span>
                    </p>
                </header>
                <div class="share-body" data-role="body">${listSkeletonHTML()}</div>
                <div class="work-foot">
                    <span class="work-foot-msg" data-role="msg" aria-live="polite"></span>
                    <button class="btn btn-ghost btn-sm" data-act="cancel" type="button">Close</button>
                    ${cell.kind === 'lesson'
                        ? '<button class="btn btn-primary btn-sm" data-act="save" type="button" hidden>Save marks</button>'
                        : ''}
                </div>
            </div>`;
        document.body.appendChild(wrap);
        revealModal(wrap);

        const close = () => { document.removeEventListener('keydown', onKey); wrap.remove(); };
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);
        wrap.querySelectorAll('[data-act=cancel]').forEach(el => el.addEventListener('click', close));

        const body = wrap.querySelector('[data-role=body]');
        const scoreEl = wrap.querySelector('[data-role=score]');
        const msg = wrap.querySelector('[data-role=msg]');

        if (cell.kind === 'quiz') {
            await fillQuizWork({ cell, wrap, body, scoreEl, msg });
            return;
        }

        try {
            const res = await apiFetch(`/api/lessons/${encodeURIComponent(cell.resourceId)}`
                + `/attempts/${encodeURIComponent(cell.workId)}`);
            const data = await res.json();
            if (!res.ok) {
                body.innerHTML = `<p class="cls-hint">${esc(data.error || 'Could not open that.')}</p>`;
                return;
            }

            const marked = data.attempt.marksAwarded != null;
            scoreEl.textContent = marked
                ? `${data.attempt.marksAwarded} out of ${data.attempt.marksTotal}`
                : 'Not marked yet';
            scoreEl.classList.toggle('is-unmarked', !marked);

            body.innerHTML = data.sections.map(section => {
                if (!section.questions.length) return '';
                return `<div class="lsn-script-section">
                    <h4>${esc(section.heading)}</h4>
                    ${section.questions.map(q => `
                        <div class="lsn-answer">
                            <div class="lsn-answer-head">
                                <div class="lsn-answer-q">${window.BookletRender ? window.BookletRender.questionText(q.question) : esc(q.question)}</div>
                                ${LSN_VERDICTS[q.verdict] ? `<span class="lsn-verdict lsn-verdict-${esc(q.verdict)}">${esc(LSN_VERDICTS[q.verdict])}</span>` : ''}
                            </div>
                            <div class="lsn-answer-block"><span class="lsn-answer-label">They wrote</span>
                                <p>${q.studentAnswer ? esc(q.studentAnswer) : '<em>Nothing</em>'}</p></div>
                            ${q.feedback ? `<div class="lsn-answer-block"><span class="lsn-answer-label">AI feedback</span><p>${esc(q.feedback)}</p></div>` : ''}
                            <div class="lsn-answer-block"><span class="lsn-answer-label">Model answer</span>
                                <p>${esc(q.modelAnswer || '')}</p></div>
                            <div class="work-mark">
                                <label>Mark
                                    <input type="number" class="work-mark-input" min="0" max="${q.max}"
                                           value="${q.awarded == null ? '' : q.awarded}"
                                           data-section="${esc(section.id)}"
                                           data-exercise="${q.exerciseIndex}"
                                           data-part="${q.partIndex}">
                                    <span>/ ${q.max}</span>
                                </label>
                            </div>
                        </div>`).join('')}
                </div>`;
            }).join('') || '<p class="cls-hint">Nothing was written on this paper.</p>';

            const save = wrap.querySelector('[data-act=save]');
            save.hidden = false;
            save.addEventListener('click', async () => {
                const marks = [];
                body.querySelectorAll('.work-mark-input').forEach(input => {
                    if (input.value === '') return;
                    marks.push({
                        sectionId: input.dataset.section,
                        exerciseIndex: Number(input.dataset.exercise),
                        partIndex: Number(input.dataset.part),
                        awarded: Number(input.value),
                    });
                });

                save.disabled = true;
                msg.textContent = '';
                msg.classList.remove('is-error');
                try {
                    const put = await apiFetch(`/api/lessons/${encodeURIComponent(cell.resourceId)}`
                        + `/attempts/${encodeURIComponent(cell.workId)}/marks`, {
                        method: 'PUT',
                        body: JSON.stringify({ marks }),
                    });
                    const out = await put.json();
                    if (!put.ok) {
                        msg.textContent = out.error || 'Could not save';
                        msg.classList.add('is-error');
                        return;
                    }
                    scoreEl.textContent = `${out.marksAwarded} out of ${out.marksTotal}`;
                    scoreEl.classList.remove('is-unmarked');
                    msg.textContent = 'Saved';
                    // The cell the teacher clicked is now a different number,
                    // so the grid behind the dialog is redrawn rather than left
                    // showing the mark they have just changed.
                    if (currentClass) openClass(currentClass.class.id, false);
                } catch {
                    msg.textContent = 'Could not save';
                    msg.classList.add('is-error');
                } finally {
                    save.disabled = false;
                }
            });
        } catch {
            body.innerHTML = '<p class="cls-hint">Could not open that.</p>';
        }
    }

    /**
     * The quiz half of the work dialog.
     *
     * Marks save the moment a tick or a cross is pressed, exactly as they do on
     * the quiz's own results page - so there is no Save button here and no
     * second way to mark a quiz to keep in step. What is new is only that one
     * pupil's paper can be reached without opening a page holding every
     * submission the link has ever collected.
     */
    async function fillQuizWork({ cell, wrap, body, scoreEl, msg }) {
        let resp = null;
        let questionsMap = {};

        const paint = () => {
            const answers = Array.isArray(resp.answers_json) ? resp.answers_json : [];
            const manual = manualMarksOf(resp);
            const score = quizResponseScore(answers, manual, questionsMap);

            scoreEl.textContent = score.total
                ? `${score.correct} out of ${score.total}`
                : 'Not marked';
            scoreEl.classList.toggle('is-unmarked', !score.total);
            msg.textContent = score.needsMarking
                ? `${score.needsMarking} to mark`
                : '';

            body.innerHTML = '<div class="work-quiz">'
                + quizQuestionsHTML(answers, manual, questionsMap) + '</div>';

            body.querySelectorAll('.award-btn').forEach(btn => {
                btn.addEventListener('click', async () => {
                    const key = btn.closest('.result-q-award').dataset.awardKey;
                    const value = btn.dataset.award === '1' ? 1 : 0;
                    // Pressing the mark it already has takes it back off,
                    // returning the question to the automatic result.
                    const manualNow = manualMarksOf(resp);
                    const next = manualNow[key] === value ? null : value;
                    try {
                        const res = await apiFetch(
                            `/api/quizzes/${encodeURIComponent(cell.resourceId)}/responses/${encodeURIComponent(cell.workId)}/marks`,
                            { method: 'PUT', body: JSON.stringify({ marks: { [key]: next } }) }
                        );
                        if (!res.ok) return showToast('Could not save that mark');
                        const data = await res.json();
                        resp.manual_marks_json = data.marks;
                        paint();
                        // The cell behind the dialog is now a different number.
                        if (currentClass) openClass(currentClass.class.id, false);
                    } catch { showToast('Could not save that mark'); }
                });
            });
        };

        try {
            const res = await apiFetch(`/api/quizzes/${encodeURIComponent(cell.resourceId)}`
                + `/responses/${encodeURIComponent(cell.workId)}`);
            const data = await res.json();
            if (!res.ok) {
                body.innerHTML = `<p class="cls-hint">${esc(data.error || 'Could not open that.')}</p>`;
                return;
            }
            resp = data.response;
            questionsMap = buildQuestionsMap(data.quiz.data_json);
            paint();

            const closeBtn = wrap.querySelector('.work-foot [data-act=cancel]');
            closeBtn.before(followUpButton(() => ({
                quiz: data.quiz,
                ...missedQuestions(Array.isArray(resp.answers_json) ? resp.answers_json : [],
                    manualMarksOf(resp), questionsMap),
                // The composer replaces the class page; the dialog goes with it.
                beforeOpen: () => closeBtn.click(),
            })));
        } catch {
            body.innerHTML = '<p class="cls-hint">Could not open that.</p>';
        }
    }

    /**
     * One piece of set work, opened from its column in the markbook.
     *
     * A dialog rather than a page or an expanding panel. The grid already shows
     * who has done it — that is the column the teacher just clicked — so what
     * is left is the handful of things you can DO to it, and taking the whole
     * page away to show four buttons loses the context that prompted them.
     */
    async function openAssignment(id) {
        try {
            const res = await apiFetch('/api/assignments/' + encodeURIComponent(id));
            const data = await res.json();
            if (!res.ok) return setError($('cls-detail-error'), data.error || 'Failed to load');
            showAssignmentPanel(data);
        } catch {
            setError($('cls-detail-error'), 'Could not open that.');
        }
    }

    function showAssignmentPanel(data) {
        const { assignment, summary, pupils } = data;
        const due = assignment.dueAt
            ? new Date(assignment.dueAt).toLocaleDateString(undefined,
                { weekday: 'long', day: 'numeric', month: 'long' })
            : 'No deadline';
        const overdue = assignment.dueAt && new Date(assignment.dueAt) < new Date()
            && summary.handedIn < summary.set;

        // Only offered when there is something to hand back: a primary button
        // reporting "handed back to 0 pupils" teaches people to distrust the
        // next one.
        const canRelease = assignment.kind === 'quiz' && pupils.some(p => p.marks && !p.released);
        // Reading is done when it is opened, so "still to hand in" is the wrong
        // question about a worksheet — and the answer would be "everyone".
        const readOnly = ['worksheet', 'booklet', 'presentation'].includes(assignment.kind);
        const missing = readOnly
            ? pupils.filter(p => !p.openedAt)
            : pupils.filter(p => !p.submittedAt);

        const wrap = document.createElement('div');
        wrap.className = 'cls-picker-modal';
        wrap.innerHTML = `
            <div class="share-backdrop" data-act="cancel"></div>
            <div class="share-card t-modal" role="dialog" aria-modal="true" aria-label="${esc(assignment.title)}">
                <header class="share-head">
                    <h2 class="share-title">${esc(assignment.title)}</h2>
                </header>
                <div class="share-body">
                    <p class="asg-panel-due${overdue ? ' is-late' : ''}">${overdue ? 'Was due ' : 'Due '}${esc(due)}</p>
                    <div class="asg-bar" role="img" aria-label="${summary.handedIn} of ${summary.set} ${readOnly ? 'opened' : 'handed in'}">
                        <div class="asg-bar-fill" style="width:${summary.set ? Math.round((summary.handedIn / summary.set) * 100) : 0}%"></div>
                    </div>
                    <p class="asg-bar-label"><strong>${summary.handedIn}</strong> of ${summary.set} ${readOnly ? 'have opened it' : 'handed in'}</p>
                    ${missing.length ? `
                        <p class="asg-panel-heading">${readOnly ? 'Not opened yet' : 'Still to hand in'}</p>
                        <p class="asg-panel-missing">${missing.map(p => esc(p.displayName)).join(', ')}</p>` : ''}
                    <div class="cls-picker-actions">
                        <button class="cls-link-btn cls-link-danger" data-act="unset" type="button">Unset</button>
                        <span class="cls-picker-spacer"></span>
                        <button class="btn btn-ghost btn-sm" data-act="status" type="button">${assignment.status === 'closed' ? 'Reopen' : 'Close it'}</button>
                        ${canRelease ? '<button class="btn btn-primary btn-sm" data-act="release" type="button">Hand marks back</button>' : ''}
                    </div>
                </div>
            </div>`;
        document.body.appendChild(wrap);
        revealModal(wrap);

        const close = () => { document.removeEventListener('keydown', onKey); wrap.remove(); };
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);

        wrap.querySelectorAll('[data-act=cancel]').forEach(el => el.addEventListener('click', close));
        wrap.querySelector('[data-act=release]')?.addEventListener('click', async () => {
            close();
            await releaseAssignment(assignment.id);
        });
        wrap.querySelector('[data-act=status]')?.addEventListener('click', async () => {
            close();
            await setAssignmentStatus(assignment.id, assignment.status === 'closed' ? 'open' : 'closed');
        });
        wrap.querySelector('[data-act=unset]')?.addEventListener('click', async () => {
            if (!window.confirm(`Unset ${assignment.title}? It disappears from every pupil's list. Anything already handed in is kept.`)) return;
            close();
            await apiFetch('/api/assignments/' + encodeURIComponent(assignment.id), { method: 'DELETE' });
            showToast('Unset.');
            openClass(currentClass.class.id, false);
        });
    }

    async function releaseAssignment(id) {
        if (!window.confirm('Hand these marks back? Every pupil who has been marked will see their score and their feedback.')) return;
        const res = await apiFetch('/api/assignments/' + encodeURIComponent(id) + '/release', { method: 'POST' });
        const data = await res.json();
        if (!res.ok) return setError($('cls-detail-error'), data.error);
        showToast(`Handed back to ${data.released} ${data.released === 1 ? 'pupil' : 'pupils'}.`);
        openClass(currentClass.class.id, false);
    }

    async function setAssignmentStatus(id, status) {
        await apiFetch('/api/assignments/' + encodeURIComponent(id), {
            method: 'PATCH', body: JSON.stringify({ status }),
        });
        openClass(currentClass.class.id, false);
    }

    // ── Class housekeeping ────────────────────────────────────────────

    async function removeClassMember(accountId, name) {
        if (!window.confirm(`Take ${name} out of this class? Their login and their work are not affected.`)) return;
        await apiFetch(`/api/classes/${encodeURIComponent(currentClass.class.id)}/members/${encodeURIComponent(accountId)}`,
            { method: 'DELETE' });
        openClass(currentClass.class.id, false);
    }

    /**
     * Give a colleague this class.
     *
     * A list of the people at your school, not a box to type an email into.
     * The server only accepts somebody already in the organisation, so typing
     * was a spelling test with one right answer that the browser already knew
     * - and getting it wrong told you off rather than showing you the names.
     */
    async function addClassTeacher() {
        let members = [];
        try {
            const res = await apiFetch('/api/org');
            const data = await res.json();
            members = data.members || [];
        } catch {
            return setError($('cls-detail-error'), 'Could not load the staff list.');
        }

        const already = new Set((currentClass.teachers || []).map(t => t.id));
        const options = members
            .filter(m => !already.has(m.userId))
            .map(m => ({
                id: m.userId,
                label: [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email,
                note: m.email,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        const userId = await pickColleague(options, currentClass.class.name);
        if (!userId) return;

        try {
            const add = await apiFetch(`/api/classes/${encodeURIComponent(currentClass.class.id)}/teachers`, {
                method: 'POST', body: JSON.stringify({ userId }),
            });
            const addData = await add.json();
            if (!add.ok) return setError($('cls-detail-error'), addData.error || 'Could not add them');
            openClass(currentClass.class.id, false);
        } catch {
            setError($('cls-detail-error'), 'Could not add them.');
        }
    }

    /**
     * The staff picker, and the dialog that says why it is empty.
     *
     * Both states are the same dialog: opening a menu item and having nothing
     * happen is the worst answer to "who else teaches this class?", so an empty
     * school says so in the place the list would have been.
     */
    function pickColleague(options, className) {
        return new Promise((resolve) => {
            const wrap = document.createElement('div');
            wrap.className = 'cls-picker-modal';
            const title = className ? `Who else teaches ${className}?` : 'Who else teaches this class?';

            const list = options.length
                ? `<div class="cls-picker">
                        ${options.map((o, i) => `
                            <label class="cls-picker-row">
                                <input type="radio" name="pick-colleague" value="${esc(o.id)}"${i === 0 ? ' checked' : ''}>
                                <span class="cls-picker-name">${esc(o.label)}</span>
                                <span class="cls-member-user">${esc(o.note || '')}</span>
                            </label>`).join('')}
                   </div>
                   <p class="cls-hint">They will see the markbook and can set this class work.</p>`
                : `<p class="cls-hint">Everybody at your school already teaches this class. Anyone new has to
                   join your school first, from <strong>Pro</strong> &rarr; <strong>Invite a colleague</strong>.</p>`;

            wrap.innerHTML = `
                <div class="share-backdrop" data-act="cancel"></div>
                <div class="share-card t-modal" role="dialog" aria-modal="true" aria-label="${esc(title)}">
                    <header class="share-head"><h2 class="share-title">${esc(title)}</h2></header>
                    <div class="share-body">
                        ${list}
                        <div class="cls-picker-actions">
                            <span class="cls-picker-spacer"></span>
                            <button class="btn btn-ghost btn-sm" data-act="cancel" type="button">${options.length ? 'Cancel' : 'Close'}</button>
                            ${options.length ? '<button class="btn btn-primary btn-sm" data-act="ok" type="button">Add them</button>' : ''}
                        </div>
                    </div>
                </div>`;
            document.body.appendChild(wrap);
            revealModal(wrap);

            const close = (v) => { document.removeEventListener('keydown', onKey); wrap.remove(); resolve(v); };
            const onKey = (e) => { if (e.key === 'Escape') close(null); };
            document.addEventListener('keydown', onKey);

            wrap.querySelectorAll('[data-act=cancel]').forEach(el =>
                el.addEventListener('click', () => close(null)));
            wrap.querySelector('[data-act=ok]')?.addEventListener('click', () =>
                close(wrap.querySelector('input[name=pick-colleague]:checked')?.value || null));
            wrap.querySelector('input, [data-act=cancel]')?.focus();
        });
    }

    async function removeClassTeacher(userId) {
        const res = await apiFetch(
            `/api/classes/${encodeURIComponent(currentClass.class.id)}/teachers/${encodeURIComponent(userId)}`,
            { method: 'DELETE' });
        const data = await res.json();
        if (!res.ok) return setError($('cls-detail-error'), data.error);
        if (userId === state.user?.id) backToClasses();
        else openClass(currentClass.class.id, false);
    }

    async function renameClass() {
        const name = window.prompt('New name for this class', currentClass.class.name);
        if (name === null || !name.trim()) return;
        await apiFetch('/api/classes/' + encodeURIComponent(currentClass.class.id), {
            method: 'PATCH', body: JSON.stringify({ name }),
        });
        openClass(currentClass.class.id, false);
    }

    /**
     * Delete the class and the logins that came with it.
     *
     * The confirmation says the two things a teacher needs to weigh and cannot
     * see from the menu: how many children stop being able to sign in, and that
     * their work is not going anywhere. Named after the class, so the dialog is
     * still answerable by somebody who has forgotten which one they opened.
     */
    async function deleteClass() {
        const klass = currentClass.class;
        const n = currentClass.members.length;
        const pupils = n === 1 ? 'pupil' : 'pupils';

        const ok = await showConfirm({
            title: `Delete ${klass.name}?`,
            message: n
                ? `This deletes the class, the work set to it, and the logins of the ${n} ${pupils} in it. `
                  + 'Anyone also in another class keeps their login. Work already handed in is kept, '
                  + 'and stays on the lesson or quiz it was done for.'
                : 'This deletes the class and the work set to it. There are no pupils in it.',
            confirmLabel: 'Delete class',
            danger: true,
        });
        if (!ok) return;

        try {
            const res = await apiFetch('/api/classes/' + encodeURIComponent(klass.id), { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) return setError($('cls-detail-error'), data.error || 'Could not delete that class.');
            const gone = data.accountsDeleted || 0;
            showToast(gone
                ? `${klass.name} deleted, with ${gone} ${gone === 1 ? 'login' : 'logins'}.`
                : `${klass.name} deleted.`);
            backToClasses();
        } catch {
            setError($('cls-detail-error'), 'Could not delete that class.');
        }
    }

    function backToClasses() {
        showStudentsPage();
    }

    /** Reprints the class list. Passwords are not on it — they cannot be. */
    function printClassLogins() {
        if (!currentClass?.members?.length) return;
        showLoginSheet(currentClass.members.map(m => ({
            displayName: m.displayName, username: m.username, password: null,
        })), { reprint: true });
    }

    /**
     * A link to one piece of set work.
     *
     * The dashboard is not a page any more, so this resolves which class the
     * work belongs to, opens it, and expands the row — the same state the
     * teacher would have reached by clicking, and the same URL.
     */
    async function openAssignmentByLink(id) {
        try {
            const res = await apiFetch('/api/assignments/' + encodeURIComponent(id));
            const data = await res.json();
            if (!res.ok || !data.assignment?.classId) {
                // Set to individuals rather than a class, or gone. The class
                // list is the honest place to land: there is no page for it.
                return backToClasses();
            }
            await openClass(data.assignment.classId, false);
            // The markbook shows the whole class; the panel is the piece of
            // work the link was actually about.
            showAssignmentPanel(data);
        } catch {
            backToClasses();
        }
    }

    // ── Setting work ──────────────────────────────────────────────────

    /**
     * One dialog, opened from either side.
     *
     * From a resource: `what` is fixed, the teacher picks the class.
     * From a class: `who` is fixed, the teacher picks the resource.
     *
     * It is one decision with two halves, and which half you already know
     * depends only on where you clicked.
     */
    async function openSetWork({ what, who }) {
        setWorkCtx = { what: what || null, who: who || null, due: 'none' };
        setError($('setwork-error'), '');

        // "No deadline" is the honest default: most work set here is "before
        // next lesson", and a date nobody asked for becomes an overdue flag
        // nobody meant.
        $('setwork-date').hidden = true;
        $('setwork-date').value = '';
        document.querySelectorAll('#setwork-modal .setwork-chip').forEach(c =>
            c.classList.toggle('is-on', c.dataset.due === 'none'));

        $('setwork-what-fixed').hidden = !what;
        $('setwork-what-pick').hidden = !!what;
        $('setwork-who-fixed').hidden = !who;
        $('setwork-who-pick').hidden = !!who;

        if (what) $('setwork-what-name').textContent = what.title;
        if (who) $('setwork-who-name').textContent = who.name;

        try {
            if (!who) {
                const res = await apiFetch('/api/classes');
                const data = await res.json();
                if (!res.ok) {
                    return window.alert(data.needsOrg
                        ? 'Setting work to a class is part of an organisation account.'
                        : (data.error || 'Could not load your classes.'));
                }
                // Archived and empty classes are left out: an empty one cannot
                // be set work, and offering it only produces an error after the
                // teacher has committed.
                const usable = (data.classes || []).filter(c => !c.archived && c.memberCount > 0);
                if (!usable.length) {
                    return window.alert('You have no classes with pupils in them yet. '
                        + 'Make one on the Students page — paste your register and everyone in it gets a login.');
                }
                $('setwork-classes').innerHTML = usable.map((c, i) => `
                    <label class="setwork-class">
                        <input type="radio" name="setwork-class" value="${esc(c.id)}"${i === 0 ? ' checked' : ''}>
                        <span class="setwork-class-name">${esc(c.name)}</span>
                        <span class="cls-card-meta">${c.memberCount}</span>
                    </label>`).join('');
            }

            if (!what) {
                // The library endpoints do not agree on a shape: /api/lessons
                // answers { lessons: [...] } and /api/quizzes answers a bare
                // array. Assuming the wrapper made every quiz invisible here,
                // so the dialog told a teacher with a library full of quizzes
                // that they had nothing to set.
                const listOf = (body, key) =>
                    (Array.isArray(body) ? body : (body && body[key]) || []);

                const [lessons, quizzes, worksheets, booklets, decks] = await Promise.all([
                    apiFetch('/api/lessons').then(r => r.ok ? r.json() : []).catch(() => []),
                    apiFetch('/api/quizzes').then(r => r.ok ? r.json() : []).catch(() => []),
                    apiFetch('/api/worksheets').then(r => r.ok ? r.json() : []).catch(() => []),
                    apiFetch('/api/booklets').then(r => r.ok ? r.json() : []).catch(() => []),
                    apiFetch('/api/presentations?limit=100').then(r => r.ok ? r.json() : []).catch(() => []),
                ]);
                // Grouped by what the pupil will be asked to DO, because that
                // is the difference that matters when choosing: the first two
                // are handed in and marked, the rest are read.
                const groups = [
                    ['Lessons', listOf(lessons, 'lessons').map(l => ({ kind: 'lesson', id: l.id, title: l.title || 'Untitled lesson' }))],
                    ['Quizzes', listOf(quizzes, 'quizzes').map(q => ({ kind: 'quiz', id: q.id, title: q.title || 'Untitled quiz' }))],
                    ['To read — worksheets', listOf(worksheets, 'worksheets').map(w => ({ kind: 'worksheet', id: w.id, title: w.title || 'Untitled worksheet' }))],
                    ['To read — booklets', listOf(booklets, 'booklets').map(b => ({ kind: 'booklet', id: b.id, title: b.title || 'Untitled booklet' }))],
                    // Only finished decks: one still generating has nothing to read yet.
                    ['To read — slides', listOf(decks, 'presentations').filter(p => p.status === 'done')
                        .map(p => ({ kind: 'presentation', id: p.id, title: p.title || p.topic || 'Untitled slides' }))],
                ];
                if (!groups.some(([, items]) => items.length)) {
                    return window.alert('You have nothing to set yet. Make slides, a booklet, a worksheet or a quiz first.');
                }
                $('setwork-resource').innerHTML = groups.map(([label, items]) => items.length
                    ? `<optgroup label="${label}">` + items.map(o =>
                        `<option value="${esc(o.kind)}:${esc(o.id)}">${esc(o.title)}</option>`).join('') + '</optgroup>'
                    : '').join('');
            }

            openModal('setwork-modal');
        } catch {
            window.alert('Could not open that just now.');
        }
    }

    /** The chosen due date as YYYY-MM-DD, or null. */
    function setWorkDueDate() {
        if (setWorkCtx.due === 'none') return null;
        if (setWorkCtx.due === 'custom') return $('setwork-date').value || null;
        const d = new Date();
        d.setDate(d.getDate() + Number(setWorkCtx.due));
        // Local date, not toISOString: at 11pm on a Thursday the UTC date is
        // already Friday, and "tomorrow" would quietly become today.
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    async function submitSetWork(event) {
        event.preventDefault();
        const btn = $('setwork-submit');
        setError($('setwork-error'), '');

        let kind, refId;
        if (setWorkCtx.what) {
            ({ kind, refId } = setWorkCtx.what);
        } else {
            [kind, refId] = String($('setwork-resource').value || '').split(':');
        }

        const classId = setWorkCtx.who
            ? setWorkCtx.who.id
            : document.querySelector('#setwork-classes input:checked')?.value;

        if (!kind || !refId) return setError($('setwork-error'), 'Choose what to set.');
        if (!classId) return setError($('setwork-error'), 'Choose a class.');

        btn.disabled = true;
        btn.textContent = 'Setting…';
        try {
            const res = await apiFetch('/api/assignments', {
                method: 'POST',
                body: JSON.stringify({ kind, refId, classId, dueAt: setWorkDueDate() }),
            });
            const data = await res.json();
            if (!res.ok) return setError($('setwork-error'), data.error || 'Could not set that.');

            closeModal('setwork-modal');
            showToast(`Set to ${data.targeted} ${data.targeted === 1 ? 'pupil' : 'pupils'}.`);
            // Straight to the class, so the teacher sees where the work went
            // rather than being told and left where they were. The new column
            // is on the markbook waiting for them.
            await openClass(classId);
        } catch {
            setError($('setwork-error'), 'Could not set that.');
        } finally {
            btn.disabled = false;
            btn.textContent = 'Set it';
        }
    }

    // Logins are made where a class is made, and nowhere else.
    //
    // There used to be a "Give pupils logins" button on the roster as well,
    // which was a second path to the same place and the source of most of what
    // made this confusing: a login with no class cannot be set work, so it left
    // a teacher holding thirty passwords and no way to use them. Making a class
    // does the whole thing in one go — see submitNewClass.

    /**
     * The credential sheet.
     *
     * Shown once, printable, and deliberately blunt about being the only copy.
     * A teacher who closes this without printing has to reset each pupil one at
     * a time, so the warning is above the list rather than under it.
     */
    /**
     * The credential sheet.
     *
     * Two shapes, because there are two occasions. Fresh logins carry their
     * starter passwords and this is the only moment those exist in readable
     * form — there is no endpoint that can produce them again, by design. A
     * reprint carries only names and usernames, and says so, because a password
     * that could be looked up later would not be worth hashing.
     */
    let loginSheetHasPasswords = false;

    function showLoginSheet(rows, { reprint = false } = {}) {
        const modal = $('logins-modal');
        const list = $('logins-list');
        if (!modal || !list) return;

        loginSheetHasPasswords = !reprint;
        $('logins-title').textContent = reprint ? 'Class logins' : 'New pupil logins';
        $('logins-warning').innerHTML = reprint
            ? 'Passwords cannot be shown again once they have been handed out. '
              + 'If a pupil has forgotten theirs, use <strong>Reset</strong> next to their name.'
            : 'These passwords are shown once and cannot be looked up again. Print them now '
              + '&ndash; each pupil chooses their own password the first time they sign in.';

        const where = $('logins-url');
        if (where) where.textContent = pupilPortalLabel();

        list.innerHTML = rows.map(c => `
            <div class="logins-row">
                <span class="logins-name">${esc(c.displayName)}</span>
                <span class="logins-user">${esc(c.username)}</span>
                <span class="logins-pass">${c.password ? esc(c.password) : ''}</span>
            </div>`).join('');

        show(modal);
    }

    function hideLoginSheet() {
        // Only worth asking when there is something irrecoverable on screen. A
        // reprint has nothing to lose, and a confirm that guards nothing is how
        // people learn to click through the ones that do.
        if (loginSheetHasPasswords
            && !window.confirm('Close this? These passwords cannot be shown again.')) return;
        hide($('logins-modal'));
        $('logins-list').innerHTML = '';
    }

    /**
     * A new password for one pupil, shown once.
     *
     * The whole password-recovery story for a child, and deliberately so: they
     * have no email address to send a link to, and a teacher standing in the
     * room is a better identity check than anything this app could perform.
     */
    async function resetPupilPassword(accountId, name) {
        if (!window.confirm(`Give ${name} a new password? Their old one stops working straight away.`)) return;
        try {
            const res = await apiFetch(`/api/classes/pupils/${encodeURIComponent(accountId)}/reset-password`, {
                method: 'POST',
            });
            const data = await res.json();
            if (!res.ok) return setError($('cls-detail-error'), data.error || 'Could not reset that.');
            showLoginSheet([data]);
        } catch {
            setError($('cls-detail-error'), 'Could not reset that.');
        }
    }

    /**
     * Printing.
     *
     * window.print() on the whole page, with a print stylesheet that hides
     * everything except the sheet — rather than opening a second window, which
     * pop-up blockers eat and which loses the app's own fonts.
     */
    function printLoginSheet() {
        document.body.classList.add('printing-logins');
        const cleanup = () => {
            document.body.classList.remove('printing-logins');
            window.removeEventListener('afterprint', cleanup);
        };
        window.addEventListener('afterprint', cleanup);
        window.print();
        // Safari fires afterprint unreliably; this is the belt to that braces.
        setTimeout(cleanup, 1000);
    }

    // ── The Pupils page ───────────────────────────────────
    //
    // Lessons, classes and the names work was handed in under, as three
    // sections that each load themselves. The same page for every account; a
    // section somebody has no use for is absent rather than empty.

    function showStudentsPage(push = true) {
        clearLanding();
        stopLessonLive();
        closeLessonEditModal();
        currentClass = null;
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(studentsView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        setStudentsTab('roster');
        if (push && window.location.pathname !== '/students') history.pushState({}, '', '/students');
        loadLessons();
        syncClassesSection();
        loadRoster();
    }

    // ── Results: work gathered under whoever handed it in ─────────────
    //
    // There is no roster. A name exists here because work arrived under it, and
    // a teacher's only jobs are the two that need a human: naming a pile, and
    // merging two piles that are the same child.
    //
    // `key` addresses a pile. It is either a uuid (an identity the teacher has
    // named) or '~' plus the normalised typed name (one nobody has touched).
    // Everything below treats the two the same, which is the point.

    let namesData = { students: [], suggestions: [] };
    let expandedName = null;

    async function loadRoster() {
        const listEl = $('stu-roster');
        const errEl = $('stu-roster-error');
        if (!listEl) return;
        setError(errEl, '');
        listEl.innerHTML = listSkeletonHTML();
        invalidateRoster();
        try {
            const res = await apiFetch('/api/students');
            const data = await res.json();
            if (!res.ok) { listEl.innerHTML = ''; return setError(errEl, data.error || 'Failed to load'); }

            namesData = data;
            rosterCache = data.students;
            setUnmatchedBadge((data.suggestions || []).length);
            renderSuggestions();
            renderRoster();
        } catch {
            listEl.innerHTML = '';
            setError(errEl, 'Failed to load');
        }
    }

    // ── "Same person?" ────────────────────────────────────────────────

    let suggestionsDismissed = false;

    function renderSuggestions() {
        const box = $('stu-suggest');
        const list = $('stu-suggest-list');
        if (!box || !list) return;

        const items = namesData.suggestions || [];
        box.hidden = suggestionsDismissed || !items.length;
        if (box.hidden) return;

        list.innerHTML = '';
        for (const sug of items) {
            const row = document.createElement('div');
            row.className = 'stu-suggest-row';
            // The reason is shown because the teacher is the one deciding, and
            // "initials match" is checkable at a glance where a bare
            // proposal is a coin toss they are being asked to sign.
            row.innerHTML = `
                <span class="stu-suggest-pair">
                    <strong>${esc(sug.fromName)}</strong>
                    <span class="stu-suggest-arrow" aria-hidden="true">→</span>
                    <strong>${esc(sug.intoName)}</strong>
                </span>
                <span class="stu-suggest-why">${esc(sug.reason)}</span>`;

            const yes = document.createElement('button');
            yes.type = 'button';
            yes.className = 'btn btn-secondary btn-sm';
            yes.textContent = 'Same person';
            yes.addEventListener('click', () => mergeNames(sug.fromKey, sug.intoKey, sug.intoName));
            row.appendChild(yes);

            const no = document.createElement('button');
            no.type = 'button';
            no.className = 'cls-link-btn';
            no.textContent = 'No';
            // Dismissed for this visit only. Nothing is written: a teacher who
            // says "no" today may be looking at a genuine duplicate next term,
            // and a permanent refusal is a decision they did not make.
            no.addEventListener('click', () => {
                namesData.suggestions = items.filter(x => x !== sug);
                renderSuggestions();
            });
            row.appendChild(no);

            list.appendChild(row);
        }
    }

    // ── The list of names ─────────────────────────────────────────────

    function nameRowHTML(s) {
        const bits = [`${s.resultCount} piece${s.resultCount === 1 ? '' : 's'} of work`];
        if (s.needsMarking) bits.push(`${s.needsMarking} to mark`);
        if (s.lastActivity) {
            bits.push('last ' + new Date(s.lastActivity)
                .toLocaleDateString(undefined, { day: 'numeric', month: 'short' }));
        }

        const score = s.average !== null && s.average !== undefined
            ? `<span class="stu-row-score">${s.average}%</span>`
            : '<span class="stu-row-score is-empty">Not marked</span>';

        // Other spellings arriving under this name. Shown because it is how a
        // teacher sees that a merge has already happened, or should.
        // A tutoring client's pupil, who did the work signed in. Said, because
        // they are the one row here with nothing to rename or merge, and a
        // tutor should see why.
        const also = s.account
            ? '<span class="stu-client-tag">Tutoring client</span>'
            : s.spellings.length
                ? `<span class="stu-also">also ${esc(s.spellings.slice(0, 3).join(', '))}</span>`
                : '';

        return `
            <div class="stu-row-main">
                <div class="stu-row-name${s.account ? ' has-tag' : ''}"><span>${esc(s.name)}</span>${also}</div>
                <div class="stu-row-meta">${esc(bits.join(' · '))}</div>
            </div>
            ${s.needsMarking ? `<span class="stu-needs-mark">${s.needsMarking} to review</span>` : ''}
            ${sparklineSVG(s.trend)}
            ${score}`;
    }

    function renderRoster() {
        const listEl = $('stu-roster');
        const toolbar = $('stu-toolbar');
        const emptyEl = $('stu-empty');
        if (!listEl) return;

        const all = namesData.students || [];
        if (toolbar) toolbar.hidden = all.length < 2;
        if (emptyEl) emptyEl.hidden = all.length > 0;

        if (!all.length) { listEl.innerHTML = ''; return; }

        const query = ($('stu-search')?.value || '').trim().toLowerCase();
        const filtered = query
            ? all.filter(s => s.name.toLowerCase().includes(query)
                || s.spellings.some(sp => sp.toLowerCase().includes(query)))
            : all;

        if (!filtered.length) {
            listEl.innerHTML = '<p class="mk-empty">Nobody matches that search.</p>';
            return;
        }

        listEl.innerHTML = '';
        for (const s of filtered) {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'stu-row';
            row.setAttribute('aria-expanded', String(expandedName === s.key));
            row.innerHTML = nameRowHTML(s);
            row.addEventListener('click', () => {
                expandedName = expandedName === s.key ? null : s.key;
                renderRoster();
            });
            listEl.appendChild(row);

            if (expandedName === s.key) listEl.appendChild(nameDetail(s));
        }
    }

    /**
     * One pupil's work, opened in place.
     *
     * In place rather than on its own page because the question this answers —
     * "is this pile really one child, and what is in it" — is asked WHILE
     * looking at the list, and is usually followed by renaming or merging it.
     */
    function nameDetail(s) {
        const wrap = document.createElement('div');
        wrap.className = 'stu-detail';

        const rows = s.items.map(item => {
            const when = item.at
                ? new Date(item.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
                : '';
            const mark = item.percent === null
                ? (item.needsMarking ? 'Needs marking' : 'Not marked')
                : item.percent + '%';
            // The typed spelling is shown where it differs from the display
            // name: "Amelia Clarke (came in as 'a clarke')" is the thing that
            // tells a teacher the grouping is right.
            const typed = item.typed && item.typed !== s.name
                ? `<span class="stu-detail-typed">as &ldquo;${esc(item.typed)}&rdquo;</span>` : '';
            return `
                <button type="button" class="stu-detail-row" data-open="${esc(item.id)}">
                    <span class="stu-detail-title">${esc(item.title || 'Untitled')}${typed}</span>
                    <span class="stu-detail-when">${esc(when)}</span>
                    <span class="stu-detail-mark${item.percent === null ? ' is-empty' : ''}">${esc(mark)}</span>
                </button>`;
        }).join('');

        wrap.innerHTML = `<div class="stu-detail-list">${rows}</div>`;

        // Opening one goes to the marked paper, question by question — which is
        // the thing a teacher wants when a score looks wrong and they need to
        // see what the pupil actually wrote.
        wrap.querySelectorAll('.stu-detail-row').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const item = s.items.find(i => String(i.id) === btn.dataset.open);
                if (item) openSubmission({ ...item, type: item.kind }, s.id);
            });
        });

        // A client's pupil has a login: who they are was never in doubt, so
        // there is no pile to name and nothing to merge them with.
        if (s.account) return wrap;

        const actions = document.createElement('div');
        actions.className = 'stu-detail-actions';

        const rename = document.createElement('button');
        rename.type = 'button';
        rename.className = 'btn btn-secondary btn-sm';
        rename.textContent = s.alias ? 'Rename' : 'Name this pupil';
        rename.addEventListener('click', (e) => { e.stopPropagation(); renameName(s); });
        actions.appendChild(rename);

        const merge = document.createElement('button');
        merge.type = 'button';
        merge.className = 'cls-link-btn';
        merge.textContent = 'Merge into…';
        merge.addEventListener('click', (e) => { e.stopPropagation(); openMergeInto(s); });
        actions.appendChild(merge);

        // The undo, named after whatever was actually done.
        //
        // "Undo naming" was wrong twice over: it appeared on a pile that had
        // only been merged, and it described the mechanism (an identity row)
        // rather than the effect. A pile holding several spellings is a merge
        // and splits apart; one holding a single spelling was renamed.
        if (s.alias) {
            const merged = s.spellings.length > 0;
            const undo = document.createElement('button');
            undo.type = 'button';
            undo.className = 'cls-link-btn cls-link-danger';
            undo.textContent = merged ? 'Split apart' : 'Undo rename';
            undo.title = merged
                ? 'Put each spelling back under its own name'
                : 'Go back to the name the pupil typed';
            undo.addEventListener('click', (e) => { e.stopPropagation(); unnameName(s, merged); });
            actions.appendChild(undo);
        }

        wrap.appendChild(actions);
        return wrap;
    }

    // ── The two verbs ─────────────────────────────────────────────────

    async function renameName(s) {
        const name = window.prompt('What should this pupil be called?', s.name);
        if (name === null || !name.trim()) return;
        try {
            const res = await apiFetch('/api/students/rename', {
                method: 'POST',
                body: JSON.stringify({ key: s.key, name: name.trim() }),
            });
            const data = await res.json();
            if (!res.ok) return setError($('stu-roster-error'), data.error);
            expandedName = data.id;
            showToast('Named.');
            loadRoster();
        } catch {
            setError($('stu-roster-error'), 'Could not rename that.');
        }
    }

    async function mergeNames(fromKey, intoKey, intoName) {
        try {
            const res = await apiFetch('/api/students/merge', {
                method: 'POST',
                body: JSON.stringify({ fromKey, intoKey, intoName }),
            });
            const data = await res.json();
            if (!res.ok) return setError($('stu-roster-error'), data.error);
            showToast('Merged.');
            loadRoster();
        } catch {
            setError($('stu-roster-error'), 'Could not merge those.');
        }
    }

    /** Pick who this pile really is, from the other names on the page. */
    async function openMergeInto(s) {
        // Never into a client's pupil: a typed name is not a client.
        const others = (namesData.students || []).filter(o => o.key !== s.key && !o.account);
        if (!others.length) return setError($('stu-roster-error'), 'There is nobody else to merge into yet.');

        const picked = await pickOne(
            others.map(o => ({ id: o.key, label: o.name, note: `${o.resultCount} piece${o.resultCount === 1 ? '' : 's'}` })),
            `Who is “${s.name}” really?`
        );
        if (!picked) return;
        const into = others.find(o => o.key === picked);
        mergeNames(s.key, into.key, into.name);
    }

    async function unnameName(s, merged) {
        const question = merged
            ? `Split ${s.name} apart? Each piece of work goes back under the name it was handed in with. Nothing is deleted.`
            : `Go back to the name ${s.name} typed? Nothing is deleted.`;
        if (!window.confirm(question)) return;
        const res = await apiFetch('/api/students/' + encodeURIComponent(s.id), { method: 'DELETE' });
        if (!res.ok) return setError($('stu-roster-error'), 'Could not undo that.');
        expandedName = null;
        showToast('Undone.');
        loadRoster();
    }

    /**
     * Reveals a modal this file built by hand.
     *
     * .t-modal starts at opacity 0 and scale(.96) and is revealed by adding
     * .is-open — which show() does for the modals that live in index.html. One
     * built here and appended to the body never goes through show(), so it has
     * to do the same thing itself or it never appears at all: backdrop, no card.
     */
    function revealModal(wrap) {
        const card = wrap.querySelector('.t-modal');
        if (card) requestAnimationFrame(() => card.classList.add('is-open'));
    }

    /**
     * A single-choice picker, built on the share modal's furniture.
     *
     * A list of thirty names in a window.prompt is a list nobody can read, and
     * choosing the wrong one here merges two children.
     */
    function pickOne(options, title) {
        return new Promise((resolve) => {
            const wrap = document.createElement('div');
            wrap.className = 'cls-picker-modal';
            wrap.innerHTML = `
                <div class="share-backdrop" data-act="cancel"></div>
                <div class="share-card t-modal" role="dialog" aria-modal="true" aria-label="${esc(title)}">
                    <header class="share-head"><h2 class="share-title">${esc(title)}</h2></header>
                    <div class="share-body">
                        <div class="cls-picker">
                            ${options.map((o, i) => `
                                <label class="cls-picker-row">
                                    <input type="radio" name="pick-one" value="${esc(o.id)}"${i === 0 ? ' checked' : ''}>
                                    <span class="cls-picker-name">${esc(o.label)}</span>
                                    <span class="cls-member-user">${esc(o.note || '')}</span>
                                </label>`).join('')}
                        </div>
                        <div class="cls-picker-actions">
                            <span class="cls-picker-spacer"></span>
                            <button class="btn btn-ghost btn-sm" data-act="cancel" type="button">Cancel</button>
                            <button class="btn btn-primary btn-sm" data-act="ok" type="button">Merge</button>
                        </div>
                    </div>
                </div>`;
            document.body.appendChild(wrap);
            revealModal(wrap);

            const close = (v) => { document.removeEventListener('keydown', onKey); wrap.remove(); resolve(v); };
            const onKey = (e) => { if (e.key === 'Escape') close(null); };
            document.addEventListener('keydown', onKey);

            wrap.querySelectorAll('[data-act=cancel]').forEach(el =>
                el.addEventListener('click', () => close(null)));
            wrap.querySelector('[data-act=ok]').addEventListener('click', () =>
                close(wrap.querySelector('input[name=pick-one]:checked')?.value || null));
            wrap.querySelector('input')?.focus();
        });
    }

    function setStudentsTab(tab) {
        studentsTab = tab;
        document.querySelectorAll('.stu-tab').forEach(b => {
            const on = b.dataset.stuTab === tab;
            b.classList.toggle('active', on);
            b.setAttribute('aria-selected', String(on));
        });
        hide($('stu-add-pane'), $('stu-review-pane'),
             $('stu-roster-pane'), $('stu-submissions-pane'));

        if (tab === 'submissions') {
            show($('stu-submissions-pane'));
            loadSubmissions();
        } else {
            show($('stu-roster-pane'));
        }
    }

    document.querySelectorAll('.stu-tab').forEach(btn => {
        btn.addEventListener('click', () => setStudentsTab(btn.dataset.stuTab));
    });
    $('stu-sub-filter')?.addEventListener('change', loadSubmissions);
    // Debounced: this one hits the server, unlike the roster's local filter.
    let subSearchTimer = null;
    $('stu-sub-search')?.addEventListener('input', () => {
        clearTimeout(subSearchTimer);
        subSearchTimer = setTimeout(loadSubmissions, 250);
    });

    async function loadSubmissions() {
        const listEl = $('stu-submissions');
        const errEl = $('stu-sub-error');
        if (!listEl) return;
        setError(errEl, '');
        listEl.innerHTML = listSkeletonHTML();

        const params = new URLSearchParams({
            filter: $('stu-sub-filter')?.value || 'unassigned',
            q: ($('stu-sub-search')?.value || '').trim(),
        });
        try {
            const res = await apiFetch('/api/students/submissions?' + params);
            const data = await res.json();
            if (!res.ok) { listEl.innerHTML = ''; return setError(errEl, data.error || 'Failed to load'); }

            const badge = $('stu-sub-badge');
            if (badge) {
                badge.textContent = data.unassigned > 99 ? '99+' : String(data.unassigned);
                badge.hidden = !data.unassigned;
            }

            if (!data.submissions.length) {
                const filter = $('stu-sub-filter')?.value;
                listEl.innerHTML = `<p class="mk-empty">${
                    filter === 'unassigned'
                        ? 'Nothing waiting &ndash; every submission is assigned to somebody.'
                        : 'No submissions yet. Share a quiz or add answers to a marking set and they show up here.'
                }</p>`;
                return;
            }

            listEl.innerHTML = '';
            data.submissions.forEach(sub => listEl.appendChild(submissionRow(sub)));
        } catch {
            listEl.innerHTML = '';
            setError(errEl, 'Failed to load');
        }
    }

    function submissionRow(sub) {
        const row = document.createElement('div');
        row.className = 'stu-sub' + (sub.studentId ? '' : ' is-unassigned');

        const SUB_KINDS = { quiz: 'Quiz', marking: 'Marking', lesson: 'Lesson' };
        const meta = [
            SUB_KINDS[sub.type] || 'Work',
            new Date(sub.date).toLocaleString(),
        ].join(' · ');
        // Always show what was typed, even once assigned: seeing that "a
        // clarke" became Amelia Clarke is how a teacher trusts the link.
        const typed = sub.typedName
            ? `<span class="stu-sub-typed">came in as &ldquo;${esc(sub.typedName)}&rdquo;</span>`
            : '<span class="stu-sub-typed">no name given</span>';

        row.innerHTML = `
            <span class="pres-item-icon pi-${sub.type}" aria-hidden="true">${LIST_ICONS[sub.type]}</span>
            <div class="stu-sub-main">
                <div class="stu-sub-title">${esc(sub.title)}</div>
                <div class="stu-sub-meta">${esc(meta)} · ${typed}</div>
            </div>
            <span class="stu-sub-score${sub.scored ? '' : ' is-unscored'}">${sub.scored ? sub.percent + '%' : esc(sub.detail)}</span>
            ${sub.needsMarking ? `<span class="stu-needs-mark">${sub.type === 'quiz' ? sub.needsMarking + ' to mark' : 'Needs marking'}</span>` : ''}`;

        const linkBtn = buildLinkButton(sub.studentId, async (studentId) => {
            const path = sub.type === 'quiz'
                ? `/api/quizzes/${sub.resourceId}/responses/${sub.id}/link`
                : sub.type === 'lesson'
                    ? `/api/lessons/${sub.resourceId}/attempts/${sub.id}/link`
                    : `/api/marking/${sub.resourceId}/responses/${sub.id}/link`;
            await apiFetch(path, { method: 'PUT', body: JSON.stringify({ studentId }) });
            sub.studentId = studentId;
            row.classList.toggle('is-unassigned', !studentId);
            // The counts above and in the nav both just changed.
            loadRoster();
            if ($('stu-sub-filter')?.value === 'unassigned') loadSubmissions();
        });
        row.appendChild(linkBtn);

        const openBtn = document.createElement('button');
        openBtn.type = 'button';
        openBtn.className = 'stu-sub-open';
        openBtn.textContent = 'Open';
        openBtn.title = { quiz: 'Open the quiz responses', lesson: 'Open the lesson' }[sub.type]
            || 'Open the marking set';
        openBtn.addEventListener('click', () => openSubmission(sub));
        row.appendChild(openBtn);

        return row;
    }

    // Adding students and the unmatched-work queue lived here.
    //
    // Both are gone with the roster. Nothing is added up front any more — a
    // name exists because work arrived under it — and the queue existed only to
    // file work whose typed name missed a roster that no longer exists.
    // Renaming and merging, above, are what tidying up is now.

    /**
     * Open one piece of work from anywhere in Students.
     *
     * Always the marking view for it, never the editor: you clicked a
     * student's attempt, so you get that attempt, with Back pointing at where
     * you came from.
     */
    function openSubmission(item, studentId) {
        // From inside a student's record, a piece of work opens as that
        // student's paper – question by question, with the marks – rather than
        // as the class's response list with theirs highlighted in it.
        if (item.type === 'lesson') return openLessonDetail(item.resourceId);
        if (studentId) return showResponseMarking(studentId, item.id);
        if (item.type === 'quiz') {
            showQuizResults(item.resourceId, { focusResponseId: item.id, returnTo: 'students' });
        } else {
            openMarkingSet(item.resourceId, { focusResponseId: item.id, returnTo: 'students' });
        }
    }

    function timelineItemHTML(item) {
        const kindLabels = { quiz: 'Quiz', marking: 'Marked answer', lesson: 'Lesson' };
        const meta = [
            kindLabels[item.type] || 'Work',
            [item.subject, item.examBoard, item.level].filter(Boolean).join(' · '),
            new Date(item.date).toLocaleDateString(),
        ].filter(Boolean).join(' · ');
        const score = item.scored
            ? `<span class="stu-item-score">${item.percent}%</span>`
            : `<span class="stu-item-score is-unscored">${esc(item.detail)}</span>`;
        // Says what is wrong with this row rather than just flagging it, so a
        // teacher knows before clicking that the score beside it is partial.
        const pending = item.needsMarking
            ? `<span class="stu-needs-mark">${item.type === 'quiz'
                ? `${item.needsMarking} question${item.needsMarking === 1 ? '' : 's'} to review`
                : 'Needs reviewing'}</span>`
            : '';
        return `
            <span class="pres-item-icon pi-${item.type}" aria-hidden="true">${LIST_ICONS[item.type]}</span>
            <div class="stu-item-main">
                <div class="stu-item-title">${esc(item.title)}</div>
                <div class="stu-item-meta">${esc(meta)}${item.scored ? ' · ' + esc(item.detail) : ''}</div>
            </div>
            ${pending}
            ${score}`;
    }

    // ── A tutor's notes on one pupil ──────────────────────
    //
    // Saved on blur, and once more when the page is left with a change still
    // unsaved. There is no Save button on purpose: the box is written in
    // between marking two pieces of work, and a button is the thing people
    // walk away without pressing.
    //
    // The rename endpoint is reused rather than given one of its own, which is
    // why `name` goes up with every save — PUT /:id updates notes only when
    // notes is present, so the two paths cannot blank each other.

    let notesFor = null;      // { id, name, classLabel } of the pupil on screen
    let notesSaved = '';      // what the server last accepted, to spot a no-op

    function showStudentNotes(student) {
        notesFor = student;
        notesSaved = student.notes || '';
        const box = $('student-notes');
        const wrap = $('student-notes-wrap');
        if (!box || !wrap) return;
        box.value = notesSaved;
        wrap.hidden = false;
        const status = $('student-notes-status');
        if (status) status.textContent = '';
    }

    async function saveStudentNotes() {
        const box = $('student-notes');
        if (!notesFor || !box) return;
        const value = box.value;
        if (value === notesSaved) return;          // nothing changed

        const status = $('student-notes-status');
        if (status) status.textContent = 'Saving…';
        try {
            const res = await apiFetch(`/api/students/${notesFor.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    name: notesFor.name,
                    classLabel: notesFor.classLabel || '',
                    notes: value,
                }),
            });
            if (!res.ok) {
                if (status) status.textContent = 'Not saved — try again';
                return;
            }
            notesSaved = value;
            if (status) {
                status.textContent = 'Saved';
                setTimeout(() => {
                    if (status.textContent === 'Saved') status.textContent = '';
                }, 2000);
            }
        } catch {
            if (status) status.textContent = 'Not saved — check your connection';
        }
    }

    $('student-notes')?.addEventListener('blur', saveStudentNotes);

    // Leaving the tab or closing it is the other way an unsaved note is lost.
    // Nothing awaits this — the page may be gone before it resolves — so it is
    // deliberately fire-and-forget rather than a promise anyone reads.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') saveStudentNotes();
    });

    async function showStudentProfile(id) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(studentProfileView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);

        const errEl = $('student-profile-error');
        setError(errEl, '');
        $('student-profile-name').textContent = 'Loading…';
        $('student-profile-class').hidden = true;
        $('student-profile-stats').innerHTML = '';
        $('student-profile-timeline').innerHTML = '';
        $('student-profile-trend').hidden = true;
        $('student-timeline-head').hidden = true;
        $('student-edit').hidden = true;
        $('student-needs-marking').hidden = true;
        $('student-notes-wrap').hidden = true;

        try {
            const res = await apiFetch(`/api/students/${id}`);
            const data = await res.json();
            if (!res.ok) {
                $('student-profile-name').textContent = 'Student not found';
                return setError(errEl, data.error || 'Could not load this student');
            }

            const { student, stats, timeline, series } = data;
            $('student-profile-name').textContent = student.name;
            const chip = $('student-profile-class');
            chip.textContent = student.classLabel || '';
            chip.hidden = !student.classLabel;
            $('student-edit').hidden = false;
            $('student-edit').onclick = () => editStudent(student);
            showStudentNotes(student);

            // Stated before the numbers, because it is the reason some of the
            // numbers below are incomplete.
            const firstToReview = timeline.filter(i => i.needsMarking).slice(-1)[0];
            if (stats.needsMarking && firstToReview) {
                const n = stats.needsMarking;
                const pieces = timeline.filter(i => i.needsMarking).length;
                $('student-needs-marking-copy').innerHTML =
                    `<b>${n} question${n === 1 ? '' : 's'}</b> across ${pieces} piece${pieces === 1 ? '' : 's'} of work`
                    + ` couldn&rsquo;t be marked automatically and need`
                    + `${n === 1 ? 's' : ''} reviewing.`;
                // Oldest first: the backlog is worked through in the order it
                // built up, and each page offers the next one.
                $('student-mark-now').onclick = () => showResponseMarking(student.id, firstToReview.id);
                $('student-needs-marking').hidden = false;
            }

            const tiles = [
                { value: stats.average !== null ? `${stats.average}%` : '–', label: 'Average score' },
                { value: stats.resultCount, label: 'Pieces of work' },
                { value: stats.quizCount, label: stats.quizCount === 1 ? 'Quiz' : 'Quizzes' },
                { value: stats.markingCount, label: 'Marked answers' },
                { value: stats.lessonCount || 0, label: (stats.lessonCount || 0) === 1 ? 'Lesson' : 'Lessons' },
            ];
            if (stats.direction) {
                tiles.push({
                    value: `${stats.direction.delta > 0 ? '+' : ''}${stats.direction.delta}%`,
                    label: stats.direction.label,
                    cls: `dir-${stats.direction.label}`,
                });
            }
            $('student-profile-stats').innerHTML = tiles.map(t => `
                <div class="stu-stat">
                    <div class="stu-stat-value ${t.cls || ''}">${esc(String(t.value))}</div>
                    <div class="stu-stat-label">${esc(t.label)}</div>
                </div>`).join('');

            if (series && series.length >= 2) {
                $('student-profile-trend').hidden = false;
                $('student-profile-spark').innerHTML = sparklineSVG(series, 320, 64);
            }

            const timelineEl = $('student-profile-timeline');
            if (!timeline.length) {
                timelineEl.innerHTML = `<p class="mk-empty">No work counted towards ${esc(student.name)} yet. Results appear here as quizzes come in and answers are marked.</p>`;
                return;
            }
            $('student-timeline-head').hidden = false;
            timelineEl.innerHTML = '';
            timeline.forEach(item => {
                const el = document.createElement('div');
                el.className = 'stu-item';
                el.tabIndex = 0;
                el.setAttribute('role', 'link');
                el.innerHTML = timelineItemHTML(item);
                const open = () => openSubmission(item, student.id);
                el.addEventListener('click', open);
                el.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                });
                timelineEl.appendChild(el);
            });
        } catch { setError(errEl, 'Network error'); }
    }

    // Renaming matters more than it looks: a roster name with a typo in it is
    // a name that will never match anything the students type.
    async function editStudent(student) {
        const head = $('student-profile-name');
        if (head.dataset.editing === 'true') return;
        head.dataset.editing = 'true';

        const form = document.createElement('div');
        form.className = 'stu-actions';
        form.innerHTML = `
            <input type="text" class="ws-topic-input" id="stu-edit-name" value="${esc(student.name)}" maxlength="200" aria-label="Student name">
            <input type="text" class="ws-topic-input" id="stu-edit-class" value="${esc(student.classLabel || '')}" list="stu-class-options" placeholder="Class" maxlength="100" aria-label="Class">
            <button class="btn btn-primary btn-sm" id="stu-edit-save" type="button">Save</button>
            <button class="btn btn-ghost btn-sm" id="stu-edit-cancel" type="button">Cancel</button>`;
        head.after(form);
        $('stu-edit-name').focus();

        const finish = () => { form.remove(); head.dataset.editing = 'false'; };
        $('stu-edit-cancel').addEventListener('click', finish);
        $('stu-edit-save').addEventListener('click', async () => {
            const name = $('stu-edit-name').value.trim();
            if (!name) return;
            await apiFetch(`/api/students/${student.id}`, {
                method: 'PUT',
                body: JSON.stringify({ name, classLabel: $('stu-edit-class').value.trim() }),
            });
            finish();
            invalidateRoster();
            showStudentProfile(student.id);
        });
    }

    // ── Marking one submission ────────────────────────────
    // One student's paper, question by question, with the mark on each and
    // where it came from. Every question is shown, not only the unmarked
    // ones: a mark the app got wrong is something you can only spot by
    // seeing it, and reading the paper is half of what marking is.

    let markingData = null;

    const IMG_DATA_URL = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;

    async function showResponseMarking(studentId, responseId, push = true) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentsView, studentProfileView, lessonsView, classesView, tutoringView);
        show(studentMarkingView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);

        const errEl = $('mark-error');
        setError(errEl, '');
        $('mark-questions').innerHTML = listSkeletonHTML();
        hide($('mark-review-banner'));
        $('mark-next').hidden = true;

        const path = `/students/${studentId}/responses/${responseId}`;
        if (push && window.location.pathname !== path) history.pushState({}, '', path);

        try {
            const res = await apiFetch('/api/students' + `/${studentId}/responses/${responseId}`);
            const data = await res.json();
            if (!res.ok) {
                $('mark-questions').innerHTML = '';
                return setError(errEl, data.error || 'Could not load this');
            }
            markingData = data;
            renderMarkingHeader();
            renderMarkingQuestions();
        } catch {
            $('mark-questions').innerHTML = '';
            setError(errEl, 'Network error');
        }
    }

    function renderMarkingHeader() {
        const d = markingData;
        $('mark-eyebrow').textContent = d.kind === 'quiz' ? 'Quiz' : 'Long answer';
        $('mark-title').textContent = d.resourceTitle;
        $('mark-subtitle').textContent =
            `${d.student.name} · ${new Date(d.date).toLocaleDateString()}`;

        // `next` points at their OTHER work, so marking this response cannot
        // change it – it is set once, with the rest of the page furniture.
        const next = $('mark-next');
        if (d.next) {
            next.hidden = false;
            next.textContent = `Next needing review: ${d.next.title} →`;
            next.onclick = () => showResponseMarking(d.student.id, d.next.responseId);
        } else {
            next.hidden = true;
        }

        // A long answer has no right-or-wrong questions to build from, so the
        // button belongs to quizzes only.
        const followUp = $('mark-followup');
        if (followUp) {
            followUp.innerHTML = '';
            followUp.hidden = d.kind !== 'quiz';
            if (d.kind === 'quiz') followUp.appendChild(followUpButton(markingFollowUpArgs));
        }
        renderMarkingScore();
    }

    /**
     * What this paper got wrong, from the questions as the page holds them –
     * so a mark changed a moment ago is already counted.
     */
    function markingFollowUpArgs() {
        const text = (value, type) => (type === 'true_false' && typeof value !== 'boolean'
            ? formatAnswerValue(String(value) === 'true')
            : formatAnswerValue(value));
        const questions = markingData?.questions || [];
        return {
            quiz: markingData?.quiz,
            unmarked: questions.filter(qn => qn.mark === null).length,
            missed: questions.filter(qn => qn.mark === 0).map(qn => ({
                type: qn.type,
                question: qn.text,
                given: qn.type === 'whiteboard' || qn.studentAnswer === '' ? ''
                    : (qn.studentAnswerText || text(qn.studentAnswer, qn.type)),
                correct: qn.expected === null ? '' : (qn.expectedText || text(qn.expected, qn.type)),
            })),
        };
    }

    /**
     * The running score and the review notice. Split out from the header
     * because these two are the only things a single mark changes, and
     * redrawing the page around them would throw the teacher back to the top
     * of a paper they are halfway down.
     */
    function renderMarkingScore() {
        const s = markingData.score;
        $('mark-score').textContent = s.total ? `${s.correct} / ${s.total}` : '–';
        $('mark-score-label').textContent = s.percent !== null && s.percent !== undefined
            ? `${s.percent}%` : 'not marked';

        const banner = $('mark-review-banner');
        if (s.needsMarking) {
            $('mark-review-copy').innerHTML =
                `<b>${s.needsMarking} question${s.needsMarking === 1 ? '' : 's'}</b> couldn&rsquo;t be marked automatically`
                + ` and need${s.needsMarking === 1 ? 's' : ''} reviewing. They&rsquo;re marked below.`;
            banner.hidden = false;
        } else {
            banner.hidden = true;
        }
    }

    /**
     * Swaps one question's card for a redrawn one, in place.
     *
     * Focus is carried across deliberately: the card is replaced under the
     * button that was just pressed, and without this the teacher's place in
     * the tab order vanishes on every mark.
     */
    function replaceQuestionCard(qn, focusMark) {
        const listEl = $('mark-questions');
        const old = listEl.querySelector(`.stu-mark-card[data-key="${qn.key}"]`);
        if (!old) return;
        const fresh = markingData.kind === 'quiz' ? quizQuestionCard(qn) : longAnswerCard(qn);
        old.replaceWith(fresh);
        if (focusMark !== undefined) fresh.querySelector(`[data-mark="${focusMark}"]`)?.focus();
    }

    // How a question's mark reads, and where it came from.
    const MARK_STATE = {
        teacher: { label: 'Marked by you', cls: 'is-teacher' },
        auto: { label: 'Marked automatically', cls: 'is-auto' },
        unmarked: { label: 'Needs reviewing', cls: 'is-unmarked' },
    };

    function renderMarkingQuestions() {
        const listEl = $('mark-questions');
        listEl.innerHTML = '';
        markingData.questions.forEach(qn => {
            listEl.appendChild(markingData.kind === 'quiz' ? quizQuestionCard(qn) : longAnswerCard(qn));
        });
    }

    function answerBlock(qn, studentName) {
        if (typeof qn.studentAnswer === 'string' && IMG_DATA_URL.test(qn.studentAnswer)) {
            return `<img class="stu-mark-img" src="${qn.studentAnswer}" alt="What ${esc(studentName)} drew">`;
        }
        const val = qn.studentAnswer === '' || qn.studentAnswer === null
            ? null
            : (typeof qn.studentAnswer === 'boolean' ? (qn.studentAnswer ? 'True' : 'False') : String(qn.studentAnswer));
        return `<p class="stu-mark-answer${val === null ? ' is-blank' : ''}">${val === null ? 'Left blank' : esc(val)}</p>`;
    }

    function quizQuestionCard(qn) {
        const state = MARK_STATE[qn.source];
        const card = document.createElement('div');
        card.className = `stu-mark-card ${state.cls}`;
        card.dataset.key = qn.key;

        card.innerHTML = `
            <div class="stu-mark-qhead">
                <span class="stu-mark-qnum">Q${qn.number}</span>
                <span class="stu-mark-state ${state.cls}">${state.label}</span>
            </div>
            <p class="stu-mark-q">${esc(qn.text)}</p>
            <p class="stu-eyebrow stu-mark-label">Their answer</p>
            ${answerBlock(qn, markingData.student.name)}
            ${qn.expected ? `<p class="stu-mark-indicative"><b>${
                qn.type === 'whiteboard' ? 'You were looking for' : 'Correct answer'
            }:</b> ${esc(qn.expected)}</p>` : ''}
            <div class="stu-mark-actions">
                <button type="button" class="award-btn award-yes${qn.mark === 1 ? ' is-on' : ''}" data-mark="1"
                        aria-pressed="${qn.mark === 1}" title="Award the mark">✓</button>
                <button type="button" class="award-btn award-no${qn.mark === 0 ? ' is-on' : ''}" data-mark="0"
                        aria-pressed="${qn.mark === 0}" title="No mark">✗</button>
                <span class="stu-mark-hint">${
                    qn.source === 'unmarked' ? 'Award this one' : 'Change it if you disagree'
                }</span>
            </div>`;

        card.querySelectorAll('[data-mark]').forEach(btn => {
            btn.addEventListener('click', async () => {
                const value = Number(btn.dataset.mark);
                // Pressing the mark it already has clears your decision and
                // hands the question back to the automatic result.
                const next = qn.source === 'teacher' && qn.mark === value ? null : value;
                card.querySelectorAll('[data-mark]').forEach(b => { b.disabled = true; });
                try {
                    const res = await apiFetch(
                        `/api/quizzes/${markingData.resourceId}/responses/${markingData.responseId}/marks`,
                        { method: 'PUT', body: JSON.stringify({ marks: { [qn.key]: next } }) }
                    );
                    if (!res.ok) throw new Error();
                    const data = await res.json();

                    // Updated in place. Clearing an override returns the
                    // question to whatever the app made of it, which the page
                    // already holds as autoMark – so nothing here needs to go
                    // back to the server and redraw the paper.
                    if (next === null) {
                        qn.mark = qn.autoMark;
                        qn.source = qn.autoMark === null ? 'unmarked' : 'auto';
                    } else {
                        qn.mark = next;
                        qn.source = 'teacher';
                    }
                    qn.needsReview = qn.mark === null;

                    // The score comes back from the save, so the running total
                    // is the server's, not a second implementation of it.
                    markingData.score = data.score;
                    renderMarkingScore();
                    replaceQuestionCard(qn, btn.dataset.mark);
                } catch {
                    card.querySelectorAll('[data-mark]').forEach(b => { b.disabled = false; });
                    showToast('Could not save that mark');
                }
            });
        });
        return card;
    }

    function longAnswerCard(qn) {
        const state = MARK_STATE[qn.source];
        const card = document.createElement('div');
        card.className = `stu-mark-card ${state.cls}`;
        card.dataset.key = qn.key;

        card.innerHTML = `
            <div class="stu-mark-qhead">
                <span class="stu-mark-qnum">${qn.marks} marks</span>
                <span class="stu-mark-state ${state.cls}">${state.label}</span>
            </div>
            <p class="stu-mark-q">${esc(qn.text)}</p>
            <p class="stu-eyebrow stu-mark-label">Their answer</p>
            <div class="stu-mark-longtext">${esc(qn.studentAnswer)}</div>
            ${qn.aiMark !== null && qn.aiMark !== undefined
                ? `<p class="stu-mark-note">Marked automatically as <b>${qn.aiMark} / ${qn.marks}</b>. Your mark replaces it.</p>`
                : `<p class="stu-mark-note">${qn.status === 'error'
                    ? 'Automatic marking failed on this one.'
                    : 'This one hasn’t been marked automatically.'}</p>`}
            <div class="stu-mark-actions">
                <label class="stu-mark-marklabel" for="mark-long-input">Your mark</label>
                <input class="mk-final-input" id="mark-long-input" type="number" min="0" max="${qn.marks}"
                       value="${qn.teacherMark !== null && qn.teacherMark !== undefined ? qn.teacherMark : ''}"
                       placeholder="–" aria-label="Mark out of ${qn.marks}">
                <span class="stu-mark-outof">/ ${qn.marks}</span>
                <button type="button" class="btn btn-primary btn-sm" data-save>Save</button>
                <a class="stu-mark-full" href="#" data-open-set>Open the full mark scheme</a>
            </div>`;

        const input = card.querySelector('.mk-final-input');
        const save = async () => {
            const raw = input.value.trim();
            const btn = card.querySelector('[data-save]');
            btn.disabled = true;
            try {
                const res = await apiFetch(`/api/marking/${markingData.resourceId}/responses/${markingData.responseId}/marks`, {
                    method: 'PUT', body: JSON.stringify({ marksAwarded: raw === '' ? null : raw }),
                });
                const data = await res.json();
                if (!res.ok) { btn.disabled = false; return showToast(data.error || 'Could not save that mark'); }

                // In place, same as a quiz question. Clearing the box falls
                // back to the model's mark where it produced one.
                qn.teacherMark = data.marksFinal;
                qn.mark = data.marksFinal !== null ? data.marksFinal : (qn.aiMark ?? null);
                qn.source = data.marksFinal !== null ? 'teacher' : (qn.aiMark !== null && qn.aiMark !== undefined ? 'auto' : 'unmarked');
                qn.needsReview = qn.mark === null;
                markingData.score = {
                    correct: qn.mark, total: qn.marks,
                    needsMarking: qn.mark === null ? 1 : 0,
                    percent: qn.mark !== null && qn.marks > 0 ? Math.round((qn.mark / qn.marks) * 100) : null,
                };
                renderMarkingScore();
                replaceQuestionCard(qn);
                showToast(data.marksFinal !== null ? 'Mark saved' : 'Your mark cleared');
            } catch { btn.disabled = false; showToast('Could not save that mark'); }
        };
        card.querySelector('[data-save]').addEventListener('click', save);
        input.addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); });
        card.querySelector('[data-open-set]').addEventListener('click', (e) => {
            e.preventDefault();
            openMarkingSet(markingData.resourceId, { focusResponseId: markingData.responseId, returnTo: 'students' });
        });
        return card;
    }

    const backToStudent = () => {
        const id = markingData?.student?.id;
        if (id) {
            history.pushState({}, '', '/students/' + id);
            showStudentProfile(id);
        } else showStudentsPage();
    };
    $('mark-back')?.addEventListener('click', backToStudent);
    $('mark-foot-back')?.addEventListener('click', backToStudent);

    navStudentsBtn?.addEventListener('click', () => showStudentsPage());
    navTutoringBtn?.addEventListener('click', () => showTutoringPage('/tutoring'));
    // The Productivity half of the product switch: back to the library, as the logo goes.
    $('nav-resources')?.addEventListener('click', () => { if (state.user) showPresList(); });

    // ── Tutoring ──────────────────────────────────────────────────────
    //
    // The operations side of a tutoring company lives in public/js/tutoring.js
    // and draws itself inside #tutoring-view. This file only shows the view,
    // routes /tutoring paths to it, and lends it the three things it cannot
    // own: the fetch wrapper, the toast, and opening a generator preset to a
    // session's course.

    /**
     * The nav entry follows the org's switch. /me carries it; a fresh sign-in
     * does not, so ask once rather than hide the tab until the next reload.
     *
     * Somebody with no organisation sees it too: for them it opens a page that
     * says what tutoring is and offers to start an agency (showTutoringIntro).
     * A school's staff don't, since an account can be in one organisation only.
     */
    const TUTORING_SEEN_KEY = 'tutoringOpened';
    const TUTORING_STRIP_KEY = 'tutoringStripClosed';

    /**
     * "Tutoring" in the bar says "New" beside it for anybody who has it and
     * has never opened it on this device, whether they are in an agency or
     * could start one. For somebody with no organisation the dashboard also
     * carries a line about it, until they close it.
     */
    function markTutoringOffer(offer) {
        let seen = false;
        let closed = false;
        try {
            seen = !!localStorage.getItem(TUTORING_SEEN_KEY);
            closed = !!localStorage.getItem(TUTORING_STRIP_KEY);
        } catch { /* storage unavailable */ }
        document.querySelectorAll('[data-tutoring-new]').forEach((el) => { el.hidden = seen; });
        // The line on the dashboard that announces it: theirs until they close it.
        const strip = $('tut-strip');
        if (strip) strip.hidden = !offer || closed;
    }

    $('tut-strip-go')?.addEventListener('click', () => showTutoringPage('/tutoring'));
    $('tut-strip-close')?.addEventListener('click', () => {
        try { localStorage.setItem(TUTORING_STRIP_KEY, '1'); } catch { /* storage unavailable */ }
        $('tut-strip').hidden = true;
    });

    /** They have looked: it is no longer news. */
    function tutoringOpened() {
        try { localStorage.setItem(TUTORING_SEEN_KEY, '1'); } catch { /* storage unavailable */ }
        markTutoringOffer(!!state.user && !state.user.org);
    }

    function syncTutoringNav() {
        if (!navTutoringBtn) return;
        const org = state.user?.org;
        markTutoringOffer(!!state.user && !org);
        if (!org) { navTutoringBtn.hidden = !state.user; syncStudentsNav(); return; }
        // An organisation's admin always has it: without tutoring it is where
        // they upgrade to the agency plan (showTutoringOff). Its other members
        // only see it once the organisation has it.
        const admin = org.role === 'admin';
        if (org.tutoring !== undefined) {
            navTutoringBtn.hidden = !org.tutoring && !admin;
            syncStudentsNav();
            return;
        }
        navTutoringBtn.hidden = !admin;
        apiFetch('/api/tutoring/me')
            .then((res) => {
                if (state.user?.org) state.user.org.tutoring = res.ok;
                navTutoringBtn.hidden = !res.ok && !admin;
                syncStudentsNav();
            })
            .catch(() => {});
    }

    let tutoringMounted = false;

    /**
     * Tutoring, for somebody who has no organisation: what it is, and a button
     * that makes them an agency (POST /api/agency-signup/mine). Pressing it
     * lands them in the tutoring area proper, where the new agency's
     * onboarding takes over, starting with what it is called.
     */
    function showTutoringIntro(push = true) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentProfileView, studentsView,
             studentMarkingView, lessonsView, classesView);
        show(tutoringView, btnLogout, navCreateWrap);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        if (push && window.location.pathname !== '/tutoring') history.pushState({}, '', '/tutoring');
        tutoringOpened();
        // Drawn by tutoring.js, which isn't fetched for somebody with no
        // agency until they come here.
        loadTutoring().then(() => {
            // Gone elsewhere, or made one, while it was on its way.
            if (!isShown(tutoringView) || state.user?.org) return;
            window.Tutoring.intro(tutoringView, {
                // Rejects with a message fit to show; the page shows it.
                async create() {
                    const res = await apiFetch('/api/agency-signup/mine', { method: 'POST' });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.error || 'Could not create your agency. Please try again.');
                    // They are in an organisation now: /me says which, and that it does tutoring.
                    const me = await fetch('/api/auth/me');
                    if (me.ok) state.user = await me.json();
                    syncOrgLockup();
                    tutoringView.innerHTML = '';
                    showTutoringPage('/tutoring', false);
                },
            });
        }, () => showPresList());
    }

    /** /tutoring for an organisation without it: what it is, and (for an admin) the upgrade. */
    function showTutoringOff(push = true) {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentProfileView, studentsView,
             studentMarkingView, lessonsView, classesView);
        show(tutoringView, btnLogout, navCreateWrap);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        if (push && window.location.pathname !== '/tutoring') history.pushState({}, '', '/tutoring');
        tutoringMounted = false;
        const admin = state.user.org.role === 'admin';
        // The agency intro, offered to this organisation (Tutoring.intro's
        // `upgrade`), with its own price and credit in the pricing card.
        window.Tutoring.intro(tutoringView, { upgrade: { orgName: state.user.org.name, admin } });
        if (admin) tutoringUpgrade($('tut-upgrade'));
    }

    /**
     * The way in for an organisation that isn't an agency (routes/agencyPlan.js,
     * /upgrade). Already paying by card: it converts now, at a plan chosen
     * here, with the unused part of what it pays taken off. Otherwise: the free
     * week, with the unused part of an invoice kept on its account for its
     * first bills.
     */
    async function tutoringUpgrade(box) {
        const gbp = (pence) => `£${(pence / 100).toFixed(2).replace(/\.00$/, '')}`;
        let info;
        try {
            const res = await apiFetch('/api/org/agency/upgrade');
            info = await res.json();
            if (!res.ok) throw new Error(info.error || 'Couldn’t load the upgrade.');
        } catch (err) {
            box.innerHTML = `<p class="tut-intro-note">${esc(err.message)}</p>`;
            return;
        }
        // Back from a bank or wallet that sent them away to confirm: the
        // upgrade completes now if the payment went through.
        if (new URLSearchParams(window.location.search).has('upgrade')) {
            const fin = await apiFetch('/api/org/agency/upgrade/finalize', { method: 'POST' });
            if (fin.ok) return window.location.assign('/tutoring/admin');
            history.replaceState({}, '', '/tutoring');
            box.insertAdjacentHTML('beforebegin', '<p class="tut-intro-note">The payment wasn’t completed, so nothing has changed and nothing was charged.</p>');
        }
        const c = info.credit;
        const creditLine = !c.pence ? ''
            : `<p class="tut-up-credit">−${gbp(c.pence)} for what’s left of your ${c.kind === 'card' ? `current ${c.yearly ? 'yearly ' : ''}plan` : 'invoiced access'}, ${
                c.kind === 'card' ? 'taken off today’s charge' : 'once you add a card'}</p>`;
        box.innerHTML = `
            <form id="tut-upgrade-form">
                <p class="tut-up-label">${info.live ? 'Your plan' : 'To start'}</p>
                ${info.live ? `
                <div class="tut-up-fields">
                    <label class="tut-up-field"><span>Resources <em>× 500 a month</em></span>
                        <input type="number" name="buckets" min="0" max="${info.maxBuckets}" step="1" value="${info.suggestedBuckets}"></label>
                    <label class="tut-up-field"><span>Classroom hours <em>a month</em></span>
                        <input type="number" name="hours" min="${info.minHours}" step="10" value="${info.minHours}"></label>
                </div>
                <div class="tut-up-price"><strong id="tut-up-total"></strong><span id="tut-up-parts"></span></div>`
                : `<div class="tut-up-price"><strong>${info.trialDays} days free</strong><span>Choose your plan and add a card in Admin before it ends.</span></div>`}
                ${creditLine}
                <button type="submit" class="btn btn-primary tut-up-go">${info.live ? 'Upgrade to tutoring' : 'Start my free week'}</button>
                <p class="tut-up-msg" id="tut-up-msg" role="alert"></p>
            </form>`;
        const form = $('tut-upgrade-form');
        const msg = $('tut-up-msg');
        const plan = () => (info.live ? { buckets: Number(form.elements.buckets.value), hours: Number(form.elements.hours.value) } : {});
        const price = async () => {
            const res = await apiFetch('/api/org/agency/upgrade/quote', { method: 'POST', body: JSON.stringify(plan()) });
            const q = await res.json().catch(() => ({}));
            $('tut-up-total').textContent = res.ok ? `${gbp(q.totalPence)} a month` : '';
            $('tut-up-parts').textContent = res.ok
                ? [`${gbp(q.basePence)} fee`, q.bucketsPence && `${gbp(q.bucketsPence)} resources`, q.hoursPence && `${gbp(q.hoursPence)} classroom`].filter(Boolean).join(' · ')
                : (q.error || '');
        };
        if (info.live) {
            form.elements.buckets.addEventListener('input', price);
            form.elements.hours.addEventListener('input', price);
            price();
        }
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const btn = form.querySelector('button[type="submit"]');
            if (info.live && !confirm(info.credit.yearly
                ? 'Move to the agency plan? Your yearly plan ends today and monthly billing starts, with what’s left of the year credited.'
                : 'Move to the agency plan? The rest of this month on the new plan is charged to your card today, less what’s left of your current one. After that it renews as normal.')) return;
            btn.disabled = true;
            msg.textContent = '';
            try {
                const res = await apiFetch('/api/org/agency/upgrade', { method: 'POST', body: JSON.stringify(plan()) });
                const out = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(out.error || 'The upgrade didn’t go through. Nothing has changed.');
                // The bank wants them to confirm today's charge. They are on
                // the plan already; this pays for it. Same steps as the yearly
                // switch on the billing page.
                // The bank wants them to confirm the charge. Nothing has
                // changed yet: it does once the payment is through, and a
                // check that isn't passed drops the attempt.
                const pay = out.payment;
                if (pay && pay.requires_action) {
                    btn.textContent = 'Confirming with your bank…';
                    let confirmed = false;
                    try {
                        const stripe = await loadStripeJs(pay.publishable_key);
                        if (pay.mode === 'redirect') {
                            // Comes back to /tutoring/admin?upgrade=1; only a failure reaches here.
                            const { error } = await stripe.confirmPayment({ clientSecret: pay.client_secret, confirmParams: { return_url: pay.return_url } });
                            if (!error) return;
                        } else {
                            const result = await stripe.confirmCardPayment(pay.client_secret);
                            confirmed = !!(result.paymentIntent && result.paymentIntent.status === 'succeeded');
                        }
                    } catch { /* not confirmed */ }
                    if (!confirmed) {
                        await apiFetch('/api/org/agency/upgrade/cancel', { method: 'POST' }).catch(() => {});
                        throw new Error('Your bank didn’t confirm the payment, so nothing has changed and nothing was charged.');
                    }
                    const fin = await apiFetch('/api/org/agency/upgrade/finalize', { method: 'POST' });
                    const finData = await fin.json().catch(() => ({}));
                    if (!fin.ok) throw new Error(finData.error || 'The upgrade didn’t go through.');
                }
                // A fresh load: /me now says tutoring, and the wing is fetched with it.
                window.location.assign('/tutoring/admin');
            } catch (err) {
                msg.textContent = err.message;
                btn.disabled = false;
                btn.textContent = info.live ? 'Upgrade to tutoring' : 'Start my free week';
            }
        });
    }

    function showTutoringPage(path = '/tutoring', push = true) {
        if (!tutoringView) return showPresList();
        // No organisation at all: the page that explains it and offers one.
        if (state.user && !state.user.org) return showTutoringIntro(push);
        // tutoring.js is fetched at sign-in for a tutoring company, so this is
        // the first visit by someone it was not fetched for – a fresh sign-in,
        // whose reply does not say whether the org uses it. Come back when it
        // has arrived; the checks below still decide whether they may stay.
        if (!window.Tutoring) {
            loadTutoring().then(() => showTutoringPage(path, push), () => showPresList());
            return;
        }
        // Someone whose organisation doesn't use tutoring (a school) who opens
        // /tutoring is told so, and who can switch it on, rather than being
        // dropped on their library with no idea why.
        if (state.user && state.user.org.tutoring === false) return showTutoringOff(push);
        pendingTutoringLink = null;
        tutoringOpened();
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
             billingView, orgView, joinView, markingView, studentProfileView, studentsView,
             studentMarkingView, lessonsView, classesView);
        show(tutoringView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        if (!tutoringMounted) {
            window.Tutoring.mount(tutoringView, {
                apiFetch, toast: showToast, prepare: prepareFromSession, openResource: openTutoringResource,
                orgPanel: showOrgEmbedded,
                // The logo is changed on tutoring's Branding page: the header and the mark on resources follow it.
                brandingChanged: (branding) => { forgetResourceMark(); applyBrandingChange(branding); },
                planWall: () => showQuotaModal({}),
            });
            tutoringMounted = true;
        }
        window.Tutoring.show(path, { push });
    }

    /**
     * "Prepare" on a lesson remembers the lesson, and the next resource of that
     * kind created from here is linked to it, so it shows on the session page.
     * Dropped on returning to the library or the tutoring pages, and after a
     * few hours, so a worksheet made next week for something else is not
     * quietly pinned to this lesson.
     */
    const TUTORING_LINK_TTL_MS = 3 * 60 * 60 * 1000;

    function linkToTutoringSession(kind, id) {
        const p = pendingTutoringLink;
        if (!p || p.kind !== kind || !id || Date.now() - p.at > TUTORING_LINK_TTL_MS) return;
        pendingTutoringLink = null;
        apiFetch(`/api/tutoring/sessions/${encodeURIComponent(p.sessionId)}/resources`, {
            method: 'POST', body: JSON.stringify({ kind, id }),
        }).then((res) => { if (res.ok) showToast('Saved to the lesson'); }).catch(() => {});
    }

    function openTutoringResource(kind, id) {
        if (kind === 'presentation') { showPresList(); return openPresentation(id); }
        if (kind === 'worksheet') return openWorksheet(id);
        if (kind === 'quiz') return openQuiz(id);
        if (kind === 'booklet') return openBooklet(id);
    }

    /** Opens a generator already set to a session's board, subject and level. */
    async function prepareFromSession(kind, {
        sessionId = null, topic = '', examBoard = '', subject = '', level = '', context = '',
        topicIds = [], withWorksheet = false, autoStart = false,
    } = {}) {
        pendingTutoringLink = sessionId ? { sessionId, kind, at: Date.now() } : null;
        if (kind === 'worksheet') return showWorksheetPage(topic, 'list', examBoard, subject, level, '', context);
        if (kind === 'quiz') return showQuizPage(topic, 'list', examBoard, subject, level, '', context);

        // The presentation and booklet forms have no preset arguments, so set
        // their pickers the way a click would: value, then change, which is
        // what loads coverage, hints and the topic list.
        const ids = kind === 'booklet'
            ? { board: 'bk-exam-board', level: 'bk-level', combo: bkSubjectCombobox }
            : { board: 'exam-board', level: 'level', combo: subjectCombobox };
        if (kind === 'booklet') showBookletPage(); else showGenerator();
        const fire = (el) => el && el.dispatchEvent(new Event('change', { bubbles: true }));
        const boardEl = $(ids.board);
        const levelEl = $(ids.level);
        if (boardEl && examBoard) { boardEl.value = examBoard; fire(boardEl); }
        if (subject) ids.combo.set?.(subject);
        if (levelEl && level) { levelEl.value = level; fire(levelEl); }
        syncAllWsTiles();
        if (kind === 'booklet') {
            if (bkDescriptionInput && topic) bkDescriptionInput.value = topic;
            const bkSourceInput = $('bk-source-input');
            if (bkSourceInput && context) { bkSourceInput.value = context; setSourcePanel('bk-source-input', 'notes', true); }
        }
        if (kind !== 'presentation') return;

        // "Plan next lesson": tick the topic, give the context, ask for the
        // matching worksheet, and start. The tutor still approves the plan.
        if (topicIds.length && genTopicPicker) {
            await genTopicPicker.load(examBoard, subject, level);
            genTopicPicker.select(topicIds);
        }
        // The ticks already say what to cover, and the box is only for what
        // the teacher adds to them, so the topic goes in it only without them.
        if (topicInput && topic && !genTopicPicker?.count()) topicInput.value = topic;
        clearSourceFiles('input-content');
        if (inputContent && context) { inputContent.value = context; setGenContextOpen(true); }
        const ws = $('pack-want-worksheet');
        if (ws && withWorksheet) ws.checked = true;
        if (autoStart && topicLineFor(topicInput, genTopicPicker)) {
            genWizard?.setStep(1, { focus: false });
            generateForm?.requestSubmit();
        }
    }
    $('logins-close')?.addEventListener('click', hideLoginSheet);
    $('logins-print')?.addEventListener('click', printLoginSheet);
    $('logins-modal')?.querySelector('[data-logins-close]')
        ?.addEventListener('click', hideLoginSheet);

    // Classes: the section on Pupils, and one class
    $('cls-new')?.addEventListener('click', openNewClass);
    $('cls-empty-new')?.addEventListener('click', openNewClass);
    $('cls-back')?.addEventListener('click', backToClasses);
    $('cls-add-pupils')?.addEventListener('click', () => {
        $('addpupils-form').reset();
        setError($('addpupils-error'), '');
        $('addpupils-count').textContent = '';
        openModal('addpupils-modal');
    });
    // Both routes to setting work: the header button, and the one in the empty
    // state under a class that has none. The empty state's is the more likely
    // of the two to be used — it is sitting under the sentence explaining what
    // it does — and it was the one with no handler.
    const setWorkForClass = () => openSetWork({
        who: { id: currentClass.class.id, name: currentClass.class.name },
    });
    $('cls-set-work')?.addEventListener('click', setWorkForClass);
    $('cls-book-set')?.addEventListener('click', setWorkForClass);

    // "Not now" on the Same person? panel. For this visit only, and nothing is
    // written: a teacher who does not want to think about duplicate names right
    // now has not decided that these two children are different people.
    $('stu-suggest-dismiss')?.addEventListener('click', () => {
        suggestionsDismissed = true;
        renderSuggestions();
    });


    // The class's own overflow menu. Everything that is not "set work" lives
    // behind it: a teacher opens a class to set work or to see who has done it,
    // and renaming or deleting a class are once-a-year jobs that should not
    // compete for the same space.
    $('cls-more')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const menu = $('cls-more-menu');
        const opening = menu.hidden;
        menu.hidden = !opening;
        $('cls-more').setAttribute('aria-expanded', String(opening));
    });
    document.addEventListener('click', () => {
        const menu = $('cls-more-menu');
        if (menu && !menu.hidden) {
            menu.hidden = true;
            $('cls-more')?.setAttribute('aria-expanded', 'false');
        }
    });
    $('cls-portal-copy')?.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(pupilPortalUrl());
            showToast('Address copied.');
        } catch {
            // Clipboard access can be refused outright (an insecure origin, a
            // locked-down school browser). The address is on screen either way,
            // so this says so rather than failing silently.
            showToast('Copy blocked — the address is on screen to write down.');
        }
    });

    $('cls-more-menu')?.addEventListener('click', (e) => {
        const act = e.target.closest('[data-act]')?.dataset.act;
        if (!act) return;
        ({
            logins: printClassLogins,
            colleague: addClassTeacher,
            rename: renameClass,
            delete: deleteClass,
        })[act]?.();
    });

    // Dialogs
    $('newclass-form')?.addEventListener('submit', submitNewClass);
    $('addpupils-form')?.addEventListener('submit', submitAddPupils);
    $('setwork-form')?.addEventListener('submit', submitSetWork);

    $('newclass-close')?.addEventListener('click', () => closeModal('newclass-modal'));
    $('addpupils-close')?.addEventListener('click', () => closeModal('addpupils-modal'));
    $('setwork-close')?.addEventListener('click', () => closeModal('setwork-modal'));
    document.querySelectorAll('[data-newclass-close]').forEach(el =>
        el.addEventListener('click', () => closeModal('newclass-modal')));
    document.querySelectorAll('[data-addpupils-close]').forEach(el =>
        el.addEventListener('click', () => closeModal('addpupils-modal')));
    document.querySelectorAll('[data-setwork-close]').forEach(el =>
        el.addEventListener('click', () => closeModal('setwork-modal')));

    // Escape closes whichever of these is open, as it does everywhere else.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        ['newclass-modal', 'addpupils-modal', 'setwork-modal'].forEach(id => {
            if (!$(id)?.hidden) closeModal(id);
        });
    });

    // The live "28 pupils" counter under each paste box.
    wireNameCounter('newclass-names', 'newclass-count');
    wireNameCounter('addpupils-names', 'addpupils-count');

    // Due-date chips. "Pick a date" reveals the field; the rest are arithmetic
    // the page does so the teacher does not have to.
    document.querySelectorAll('#setwork-modal .setwork-chip').forEach(chip => {
        chip.addEventListener('click', () => {
            document.querySelectorAll('#setwork-modal .setwork-chip')
                .forEach(c => c.classList.toggle('is-on', c === chip));
            setWorkCtx.due = chip.dataset.due;
            const dateEl = $('setwork-date');
            dateEl.hidden = chip.dataset.due !== 'custom';
            if (chip.dataset.due === 'custom') dateEl.focus();
        });
    });

    $('btn-menu-students')?.addEventListener('click', () => { hide(userMenu); showStudentsPage(); });
    $('btn-menu-tutoring')?.addEventListener('click', () => { hide(userMenu); showTutoringPage('/tutoring'); });
    $('student-back')?.addEventListener('click', () => showStudentsPage());

    // ── Workbook lessons ──────────────────────────────────
    //
    // A booklet's workbook, set as work for a class, sat on screen and marked
    // by AI. This is the teacher's half; the pupil's is a separate standalone
    // page (public/learn.html), not part of this application.

    const lsnState = {
        id: null,
        lesson: null,
        bookletId: null,
        liveAbort: null,
        sectionCount: 0,
    };

    const LSN_STATUS_LABELS = {
        in_progress: 'Working on it',
        submitted: 'Handed in',
        marking: 'Being marked',
        marked: 'Marked',
        error: 'Marking failed',
    };

    // The lesson's own state, as a teacher would say it. Raw draft/open/closed
    // read like database values; these read like the state of the work.
    const LSN_LESSON_LABELS = {
        draft: 'Draft',
        open: 'Open',
        closed: 'Closed',
    };

    function lessonDue(dueAt) {
        if (!dueAt) return '';
        const d = new Date(dueAt);
        if (isNaN(d.getTime())) return '';
        return 'Due ' + d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }

    /**
     * The Lessons section of the Pupils page.
     *
     * Absent until there is a lesson: somebody who has never set one is told
     * where they come from by the empty state under the names, and a heading
     * over nothing is one more thing to read past.
     */
    async function loadLessons() {
        const pane = $('lsn-list-pane');
        const listEl = $('lsn-list');
        const closedEl = $('lsn-closed-list');
        const fold = $('lsn-closed-fold');
        const errEl = $('lsn-error');
        if (!pane || !listEl || !closedEl || !fold) return;
        setError(errEl, '');

        const fail = (message) => {
            listEl.innerHTML = '';
            closedEl.innerHTML = '';
            fold.hidden = true;
            // Shown so the error has somewhere to be read.
            pane.hidden = false;
            setError(errEl, message);
        };

        try {
            const res = await apiFetch('/api/lessons');
            const data = await res.json();
            if (!res.ok) return fail(data.error || 'Failed to load');

            pane.hidden = !data.lessons.length;
            if (pane.hidden) return;

            // Live lessons first, then drafts. Within a group, soonest
            // deadline first; no deadline last.
            const rank = { open: 0, draft: 1 };
            const byDue = (a, b) =>
                (a.dueAt ? 0 : 1) - (b.dueAt ? 0 : 1)
                || String(a.dueAt || '').localeCompare(String(b.dueAt || ''))
                || String(b.createdAt || '').localeCompare(String(a.createdAt || ''));

            const rowHTML = (l) => {
                const pct = l.started ? Math.round((l.marked / l.started) * 100) : 0;
                return `
                <button type="button" class="lsn-row" data-lesson="${esc(l.id)}">
                    <span class="lsn-row-main">
                        <span class="lsn-row-title">${esc(l.title)}</span>
                        <span class="lsn-row-meta">
                            <span class="lsn-pill lsn-pill-${esc(l.status)}">${esc(LSN_LESSON_LABELS[l.status] || l.status)}</span>
                            ${l.dueAt ? `<span class="lsn-row-due">${esc(lessonDue(l.dueAt))}</span>` : ''}
                        </span>
                    </span>
                    <span class="lsn-row-progress">
                        ${l.started
                            ? `<span class="lsn-bar" aria-hidden="true"><span class="lsn-bar-fill" style="width:${pct}%"></span></span>
                               <span class="lsn-row-counts">${l.marked} of ${l.started} marked</span>`
                            : '<span class="lsn-row-counts">Nobody started yet</span>'}
                    </span>
                </button>`;
            };

            // A closed lesson is finished business. Folded away, because this
            // list shares a page now and last term's work would otherwise push
            // the classes under it off the screen.
            const closed = data.lessons.filter(l => l.status === 'closed').sort(byDue);
            const current = data.lessons.filter(l => l.status !== 'closed')
                .sort((a, b) => (rank[a.status] ?? 2) - (rank[b.status] ?? 2) || byDue(a, b));

            listEl.innerHTML = current.map(rowHTML).join('');
            closedEl.innerHTML = closed.map(rowHTML).join('');
            fold.hidden = !closed.length;
            // Nothing current to look at, so the closed ones are the list.
            fold.open = !current.length;
            const noun = tutoringOrg() ? 'workbook' : 'lesson';
            $('lsn-closed-summary').textContent =
                `${closed.length} closed ${closed.length === 1 ? noun : noun + 's'}`;

            pane.querySelectorAll('.lsn-row').forEach(row => {
                row.addEventListener('click', () => openLessonDetail(row.dataset.lesson));
            });
        } catch (e) {
            fail('Failed to load');
        }
    }

    async function openLessonDetail(id, push = true) {
        lsnState.id = id;
        showLessonsPageShell();
        if (push && window.location.pathname !== '/lessons/' + id) {
            history.pushState({}, '', '/lessons/' + id);
        }
        await refreshLessonDetail();
        startLessonLive();
    }

    /** One lesson's page. The list of them is a section of Pupils. */
    function showLessonsPageShell() {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView,
            billingView, orgView, joinView, markingView, studentProfileView, studentsView,
            studentMarkingView, classesView, tutoringView);
        show(lessonsView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        hide($('lsn-script'));
        show($('lsn-detail-pane'));
    }

    async function refreshLessonDetail() {
        const errEl = $('lsn-detail-error');
        setError(errEl, '');
        try {
            const res = await apiFetch('/api/lessons/' + lsnState.id);
            const data = await res.json();
            if (!res.ok) return setError(errEl, data.error || 'Failed to load');

            lsnState.lesson = data;
            lsnState.sectionCount = data.sections.length;

            $('lsn-detail-title').textContent = data.title;

            // Setting to a class needs a school. Shown only inside one, for the
            // same reason the Classes nav entry is: a button whose only outcome
            // is a payment wall is not worth the room it takes.
            const setClassBtn = $('lsn-set-class');
            if (setClassBtn) {
                setClassBtn.hidden = !state.user?.org || tutoringOrg();
                setClassBtn.onclick = () => openSetWork({
                    what: { kind: 'lesson', refId: data.id, title: data.title },
                });
            }

            renderLessonStatus(data);
            renderMarkPending(data);

            // The lesson's own way in. The link opens it directly; the code is
            // the same thing for a class that types rather than clicks.
            if ($('lsn-detail-url')) {
                $('lsn-detail-url').value = window.location.origin + '/learn/' + encodeURIComponent(data.joinCode || '');
            }
            renderLessonCode(data.joinCode || '');
            if ($('lsn-join-host')) {
                $('lsn-join-host').textContent = window.location.host + '/learn';
            }

            renderLessonProgress(data.students);
        } catch (e) {
            setError(errEl, 'Failed to load');
        }
    }

    /**
     * One cell per character.
     *
     * A join code is spoken as much as it is typed – a teacher reads it to a
     * room – and separate cells are what keep the characters from running
     * together into something a pupil at the back mishears.
     */
    function renderLessonCode(code) {
        const el = $('lsn-detail-code');
        if (!el) return;
        el.textContent = '';
        for (const ch of code) {
            const cell = document.createElement('span');
            cell.className = 'lsn-code-cell';
            cell.textContent = ch;
            el.appendChild(cell);
        }
    }

    /**
     * The state pill and the one button that changes it.
     *
     * Open is the working state, so open lessons get no primary action at all
     * — closing half a working lesson is not the thing a teacher arrives here
     * wanting to do, and it should not be the biggest button on the page.
     */
    function renderLessonStatus(data) {
        const pill = $('lsn-detail-pill');
        pill.hidden = false;
        pill.className = 'lsn-pill lsn-pill-' + data.status;
        pill.textContent = LSN_LESSON_LABELS[data.status] || data.status;

        const due = $('lsn-detail-due');
        if (due) {
            due.textContent = lessonDue(data.dueAt);
            due.hidden = !due.textContent;
        }

        const btn = $('lsn-toggle-open');
        btn.hidden = data.status === 'open';
        btn.textContent = data.status === 'draft' ? 'Open to the class' : 'Reopen';
    }

    /**
     * Marking appears only when there is something waiting to be marked.
     *
     * On a lesson that marks itself this is a mop-up: whatever a quota block or
     * a crash left behind, and a secondary button is the right weight for it.
     * On a lesson the teacher chose to mark themselves it is the whole point of
     * the screen, and every script in the class is sitting behind it — so it
     * becomes the primary action rather than something to be found.
     */
    function renderMarkPending(data) {
        const btn = $('lsn-mark-pending');
        const waiting = data.students.filter(s =>
            s.attemptId && ['submitted', 'error'].includes(s.status)).length;
        btn.hidden = waiting === 0;
        const manual = data.autoMark === false;
        btn.className = manual ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm';
        btn.textContent = waiting === 1
            ? 'Mark 1 handed-in script'
            : `Mark ${waiting} handed-in scripts`;
        // Said on the button rather than beside it: a teacher who left this
        // lesson unmarked on purpose needs no explanation, and one who did not
        // needs it exactly where they are about to click.
        if (manual) btn.title = 'Each script marked comes out of your allowance.';
        else btn.removeAttribute('title');
    }

    /**
     * Who has done what, most urgent first.
     *
     * A teacher opening this page mid-lesson wants the stragglers and the
     * failures at the top, not an alphabetical roll call they scroll.
     */
    const LSN_URGENCY = ['error', 'marking', 'submitted', 'in_progress', 'marked'];

    function renderLessonProgress(students) {
        const el = $('lsn-progress');
        if (!el) return;

        // Summary chips first: the shape of the class in one glance.
        const counts = {};
        for (const s of students) counts[s.status] = (counts[s.status] || 0) + 1;
        const summary = $('lsn-progress-summary');
        summary.hidden = students.length === 0;
        summary.innerHTML = LSN_URGENCY
            .filter(st => counts[st])
            .map(st => `<span class="lsn-summary-chip"><span class="lsn-summary-count">${counts[st]}</span>${LSN_STATUS_LABELS[st]}</span>`)
            .join('');

        const ordered = [...students].sort((a, b) =>
            LSN_URGENCY.indexOf(a.status) - LSN_URGENCY.indexOf(b.status) || a.name.localeCompare(b.name));

        if (!ordered.length) {
            el.innerHTML = '<p class="lsn-roll-empty">Give out the code and pupils appear here as they start.</p>';
            return;
        }

        el.innerHTML = ordered.map(s => {
            const score = (s.marksAwarded != null && s.marksTotal)
                ? `${s.marksAwarded}/${s.marksTotal}` : '';
            const clickable = s.attemptId && ['marked', 'submitted', 'marking', 'error'].includes(s.status);
            return `
                <div class="lsn-student ${clickable ? 'lsn-student-open' : ''}"
                     ${clickable ? `data-attempt="${esc(s.attemptId)}" data-name="${esc(s.name)}" role="button" tabindex="0"` : ''}>
                    <span class="lsn-student-main">
                        <span class="lsn-student-name">${esc(s.name)}</span>
                        ${s.unmatched ? '<span class="lsn-unmatched" title="This name does not match anyone on your roster. File it under Unmatched work on the Students page.">not on your roster</span>' : ''}
                    </span>
                    <span class="lsn-student-state">
                        <span class="lsn-student-score">${esc(score)}</span>
                        <span class="lsn-pill lsn-pill-${esc(s.status)}">${esc(LSN_STATUS_LABELS[s.status] || s.status)}</span>
                    </span>
                    ${clickable ? `<span class="lsn-student-go" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 5 7 7-7 7"/></svg></span>` : ''}
                </div>`;
        }).join('');

        const open = (node) => openLessonScript(node.dataset.attempt, node.dataset.name);
        el.querySelectorAll('.lsn-student-open').forEach(node => {
            node.addEventListener('click', () => open(node));
            node.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(node); }
            });
        });
    }

    /**
     * Watch the class work, live.
     *
     * The stream is a DB poll behind SSE (see utils/lessonStream.js), so this
     * is just a reader. AbortController rather than EventSource, for the same
     * reason bkStream gives: the session cookie and a clean end/drop signal.
     */
    function startLessonLive() {
        stopLessonLive();
        if (!lsnState.id) return;

        const controller = new AbortController();
        lsnState.liveAbort = controller;
        const statusEl = $('lsn-live-status');

        (async () => {
            try {
                const res = await fetch('/api/lessons/' + lsnState.id + '/live', {
                    credentials: 'same-origin', signal: controller.signal,
                });
                if (!res.ok || !res.body) return;
                const reader = res.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    let split;
                    while ((split = buffer.indexOf('\n\n')) >= 0) {
                        const chunk = buffer.slice(0, split);
                        buffer = buffer.slice(split + 2);
                        const type = /event: (.+)/.exec(chunk);
                        const data = /data: ([\s\S]+)/.exec(chunk);
                        if (!type || !data) continue;
                        if (type[1] !== 'progress') continue;
                        try {
                            const payload = JSON.parse(data[1]);
                            renderLessonProgress(payload.students);
                            if (statusEl) setLessonLiveState(statusEl, true, 'Updating live');
                        } catch { /* malformed frame */ }
                    }
                }
                // The server caps a connection at 30 minutes; say so rather
                // than leaving a stale table looking live.
                if (statusEl) setLessonLiveState(statusEl, false, 'Paused \u2014 reopen the lesson to watch again');
            } catch { /* aborted, or the connection dropped */ }
        })();
    }

    /** The dot is the live signal; the label only names what it shows. */
    function setLessonLiveState(el, live, label) {
        el.classList.toggle('is-paused', !live);
        el.innerHTML = '<span class="lsn-live-dot" aria-hidden="true"></span>';
        el.appendChild(document.createTextNode(label));
    }

    function stopLessonLive() {
        if (lsnState.liveAbort) { lsnState.liveAbort.abort(); lsnState.liveAbort = null; }
    }

    const LSN_VERDICTS = { correct: 'Correct', partial: 'Partly right', incorrect: 'Not right', blank: 'Blank' };

    async function openLessonScript(attemptId, name) {
        const errEl = $('lsn-script-error');
        setError(errEl, '');
        show($('lsn-script'));
        $('lsn-script-title').textContent = name;
        $('lsn-script-body').innerHTML = listSkeletonHTML();

        try {
            const res = await apiFetch(`/api/lessons/${lsnState.id}/attempts/${attemptId}`);
            const data = await res.json();
            if (!res.ok) { $('lsn-script-body').innerHTML = ''; return setError(errEl, data.error || 'Failed to load'); }

            const scoreEl = $('lsn-script-score');
            const marked = data.attempt.marksAwarded != null;
            scoreEl.textContent = marked
                ? `${data.attempt.marksAwarded} out of ${data.attempt.marksTotal}`
                : 'Not marked yet';
            scoreEl.classList.toggle('is-unmarked', !marked);

            $('lsn-script-body').innerHTML = data.sections.map(section => {
                if (!section.questions.length) return '';
                return `<div class="lsn-script-section">
                    <h4>${esc(section.heading)}</h4>
                    ${section.questions.map(q => `
                        <div class="lsn-answer">
                            <div class="lsn-answer-head">
                                <div class="lsn-answer-q">${window.BookletRender ? window.BookletRender.questionText(q.question) : esc(q.question)}</div>
                                ${LSN_VERDICTS[q.verdict] ? `<span class="lsn-verdict lsn-verdict-${esc(q.verdict)}">${esc(LSN_VERDICTS[q.verdict])}</span>` : ''}
                            </div>
                            <div class="lsn-answer-block"><span class="lsn-answer-label">They wrote</span>
                                <p>${q.studentAnswer ? esc(q.studentAnswer) : '<em>Nothing</em>'}</p></div>
                            ${q.feedback ? `<div class="lsn-answer-block"><span class="lsn-answer-label">AI feedback</span><p>${esc(q.feedback)}</p></div>` : ''}
                            <div class="lsn-answer-block"><span class="lsn-answer-label">Model answer</span>
                                <p>${esc(q.modelAnswer || '')}</p></div>
                            <div class="lsn-answer-mark">
                                <label>Mark
                                    <input type="number" class="lsn-mark-input" min="0" max="${q.max}"
                                           value="${q.awarded == null ? '' : q.awarded}"
                                           data-section="${esc(section.id)}"
                                           data-exercise="${q.exerciseIndex}"
                                           data-part="${q.partIndex}">
                                    <span>/ ${q.max}</span>
                                </label>
                            </div>
                        </div>`).join('')}
                </div>`;
            }).join('') + `<div class="stu-actions">
                <button id="lsn-save-marks" class="btn btn-primary btn-sm" type="button">Save my marks</button>
            </div>`;

            $('lsn-save-marks')?.addEventListener('click', () => saveLessonMarks(attemptId));
        } catch (e) {
            $('lsn-script-body').innerHTML = '';
            setError(errEl, 'Failed to load');
        }
    }

    async function saveLessonMarks(attemptId) {
        const errEl = $('lsn-script-error');
        setError(errEl, '');
        const marks = [];
        $('lsn-script-body').querySelectorAll('.lsn-mark-input').forEach(input => {
            if (input.value === '') return;
            marks.push({
                sectionId: input.dataset.section,
                exerciseIndex: Number(input.dataset.exercise),
                partIndex: Number(input.dataset.part),
                awarded: Number(input.value),
            });
        });

        try {
            const res = await apiFetch(`/api/lessons/${lsnState.id}/attempts/${attemptId}/marks`, {
                method: 'PUT',
                body: JSON.stringify({ marks }),
            });
            const data = await res.json();
            if (!res.ok) return setError(errEl, data.error || 'Could not save');
            $('lsn-script-score').textContent = `${data.marksAwarded} out of ${data.marksTotal}`;
            refreshLessonDetail();
        } catch (e) {
            setError(errEl, 'Could not save');
        }
    }

    // ── Setting a lesson ──────────────────────────────────

    async function openSetLessonModal(bookletId, title) {
        lsnState.bookletId = bookletId;
        setError($('lsn-set-error'), '');
        $('lsn-set-title-input').value = '';
        $('lsn-set-due').value = '';
        $('lsn-set-automark').checked = true;
        $('lsn-set-context').textContent = title ? `From \u201C${title}\u201D` : '';

        show($('lsn-set-modal'));
        // After the dialog is up, not before: the warning is worth waiting a
        // moment to fill in, and not worth making the dialog wait to appear.
        renderMarkNote('lsn-set-mark-note', $('lsn-set-automark').checked);
    }

    function closeSetLessonModal() { hide($('lsn-set-modal')); }

    /**
     * What leaving automatic marking on is about to cost, in this teacher's own
     * numbers.
     *
     * A class handing in is the largest single draw on an allowance this
     * product has: thirty pupils pressing Finish is thirty of a Pro month's two
     * hundred, spent in the ten minutes before the bell with nothing on screen
     * having said so. The general caution ("this may use your allowance") is
     * the one nobody reads; "30 pupils would use 30 of the 47 you have left" is
     * a decision somebody can actually make.
     *
     * Fetched rather than assumed: an organisation on a shared bucket draws
     * from a pool its colleagues are drawing from too, and a plan with no
     * marking limit has no remainder to quote at all. A failed lookup falls
     * back to the shape of the charge without a number, which is still the
     * thing worth saying.
     */
    const LSN_ALLOWANCE_CACHE = { at: 0, data: null };

    async function fetchMarkingAllowance() {
        // One lesson is often set straight after another. A minute spares the
        // second dialog a round trip, and is far too short for the number to
        // have moved in a way that would change anybody's mind.
        if (LSN_ALLOWANCE_CACHE.data && Date.now() - LSN_ALLOWANCE_CACHE.at < 60000) {
            return LSN_ALLOWANCE_CACHE.data;
        }
        try {
            const res = await apiFetch('/api/lessons/marking-allowance');
            if (!res.ok) return null;
            const data = await res.json();
            LSN_ALLOWANCE_CACHE.at = Date.now();
            LSN_ALLOWANCE_CACHE.data = data;
            return data;
        } catch {
            return null;
        }
    }

    // A full class, as the unit the warning is measured in. A teacher does not
    // think in credits; they think in "my year 9 group".
    const LSN_CLASS_SIZE = 30;

    async function renderMarkNote(elId, on) {
        const el = $(elId);
        if (!el) return;

        if (!on) {
            el.className = 'lsn-mark-note';
            el.textContent = 'You mark the scripts yourself.';
            el.hidden = false;
            return;
        }

        // The charge, said before the number arrives: the dialog must never be
        // briefly silent about the cost. Replaced, not appended to, once the
        // allowance lands — the count says everything this does and more.
        el.className = 'lsn-mark-note is-warning';
        el.textContent = 'One script marked per pupil.';
        el.hidden = false;

        const a = await fetchMarkingAllowance();
        // The box may have been unticked, or the dialog closed, in the meantime.
        if (!a || a.remaining == null || el.hidden) return;

        const left = a.remaining.toLocaleString();

        // Both halves name what is being counted. A bucket is not counted in
        // scripts — it is one shared pool that marking draws from alongside
        // everything else the organisation makes — so it keeps the word the
        // billing page uses for it rather than being flattened into scripts
        // the organisation may well spend on something other than marking.
        //
        // Measured against a class rather than against the allowance: "76%
        // used" is a statistic, "not enough for a class" is what changes what
        // a teacher does next.
        let level = '';
        let text = a.pooled
            ? `A class of ${LSN_CLASS_SIZE} uses ${LSN_CLASS_SIZE} of your organisation’s ${left} shared generations.`
            : `A class of ${LSN_CLASS_SIZE} uses ${LSN_CLASS_SIZE} of the ${left} scripts you can mark this month.`;
        if (a.remaining === 0) {
            level = ' is-out';
            text = a.pooled
                ? 'Your organisation’s pool is empty — nothing would be marked.'
                : 'No marking left this month — nothing would be marked.';
        } else if (a.remaining < LSN_CLASS_SIZE) {
            level = ' is-low';
            text += ' Not enough for a class.';
        }

        el.className = 'lsn-mark-note is-warning' + level;
        el.textContent = text;
    }

    async function createLesson() {
        const errEl = $('lsn-set-error');
        setError(errEl, '');

        const btn = $('lsn-set-save');
        btn.disabled = true;
        try {
            const res = await apiFetch('/api/lessons', {
                method: 'POST',
                body: JSON.stringify({
                    bookletId: lsnState.bookletId,
                    title: $('lsn-set-title-input').value,
                    includeGuide: $('lsn-set-guide').checked,
                    showResults: $('lsn-set-results').checked,
                    autoMark: $('lsn-set-automark').checked,
                    dueAt: $('lsn-set-due').value || null,
                }),
            });
            const data = await res.json();
            if (!res.ok) return setError(errEl, data.error || 'Could not set the lesson');

            // Set means set: a lesson nobody can open is not what the button
            // said it would do.
            await apiFetch(`/api/lessons/${data.id}/open`, { method: 'POST' });

            closeSetLessonModal();
            openLessonDetail(data.id);
        } catch (e) {
            setError(errEl, 'Could not set the lesson');
        } finally {
            btn.disabled = false;
        }
    }

    // ── Changing a lesson's settings ──────────────────────

    function openLessonEditModal() {
        const l = lsnState.lesson;
        if (!l) return;
        setError($('lsn-edit-error'), '');
        $('lsn-edit-title-input').value = l.title || '';
        $('lsn-edit-guide').checked = !!l.includeGuide;
        $('lsn-edit-results').checked = !!l.showResults;
        $('lsn-edit-automark').checked = !!l.autoMark;
        $('lsn-edit-due').value = l.dueAt ? l.dueAt.slice(0, 10) : '';
        show($('lsn-edit-modal'));
        renderMarkNote('lsn-edit-mark-note', $('lsn-edit-automark').checked);
    }

    function closeLessonEditModal() { hide($('lsn-edit-modal')); }

    async function saveLessonEdit() {
        const errEl = $('lsn-edit-error');
        setError(errEl, '');
        try {
            const res = await apiFetch('/api/lessons/' + lsnState.id, {
                method: 'PUT',
                body: JSON.stringify({
                    title: $('lsn-edit-title-input').value,
                    includeGuide: $('lsn-edit-guide').checked,
                    showResults: $('lsn-edit-results').checked,
                    autoMark: $('lsn-edit-automark').checked,
                    dueAt: $('lsn-edit-due').value || null,
                }),
            });
            const data = await res.json();
            if (!res.ok) return setError(errEl, data.error || 'Could not save');
            closeLessonEditModal();
            refreshLessonDetail();
        } catch (e) {
            setError(errEl, 'Could not save');
        }
    }

    async function deleteLesson() {
        const l = lsnState.lesson;
        if (!l) return;
        // Native confirm on purpose: deleting a live lesson with work in it is
        // rare, irreversible, and worth a real round-trip of doubt.
        if (!confirm(`Delete \u201C${l.title}\u201D? Pupils lose it from their list, and their marks go with it.`)) return;
        try {
            await apiFetch('/api/lessons/' + lsnState.id, { method: 'DELETE' });
            showStudentsPage();
        } catch { setError($('lsn-detail-error'), 'Could not delete'); }
    }


    // ── Wiring ────────────────────────────────────────────

    $('lsn-set-automark')?.addEventListener('change', (e) => renderMarkNote('lsn-set-mark-note', e.target.checked));
    $('lsn-edit-automark')?.addEventListener('change', (e) => renderMarkNote('lsn-edit-mark-note', e.target.checked));

    $('lsn-back')?.addEventListener('click', () => showStudentsPage());
    $('lsn-script-close')?.addEventListener('click', () => hide($('lsn-script')));

    $('lsn-toggle-open')?.addEventListener('click', async () => {
        const action = lsnState.lesson?.status === 'open' ? 'close' : 'open';
        await apiFetch(`/api/lessons/${lsnState.id}/${action}`, { method: 'POST' });
        refreshLessonDetail();
    });

    $('lsn-mark-pending')?.addEventListener('click', async () => {
        const btn = $('lsn-mark-pending');
        btn.disabled = true;
        btn.textContent = 'Marking…';
        try {
            await bkStream(`/api/lessons/${lsnState.id}/mark/stream`, (type) => {
                if (type === 'attempt_done' || type === 'attempt_error') refreshLessonDetail();
            });
        } catch { /* the stream keeps going server-side regardless */ }
        btn.disabled = false;
        btn.textContent = 'Finish marking';
        refreshLessonDetail();
    });

    $('lsn-edit-open')?.addEventListener('click', openLessonEditModal);
    $('lsn-edit-close')?.addEventListener('click', closeLessonEditModal);
    $('lsn-edit-cancel')?.addEventListener('click', closeLessonEditModal);
    $('lsn-edit-save')?.addEventListener('click', saveLessonEdit);
    document.querySelector('[data-lsn-edit-close]')?.addEventListener('click', closeLessonEditModal);
    $('lsn-delete')?.addEventListener('click', deleteLesson);

    /**
     * The lesson's link is its credential, exactly like a quiz share link.
     *
     * The join code in the URL is what finds the work; the pupil's typed name
     * is who it belongs to. No per-pupil links, no tokens beyond the code.
     */
    function wireCopy(inputId, buttonId, statusId) {
        $(buttonId)?.addEventListener('click', () => {
            const value = $(inputId)?.value;
            if (!value) return;
            navigator.clipboard.writeText(value).then(() => {
                const status = $(statusId);
                if (!status) return;
                status.textContent = 'Copied';
                setTimeout(() => {
                    if (status.textContent === 'Copied') status.textContent = '';
                }, 2000);
            });
        });
    }
    wireCopy('lsn-detail-url', 'lsn-detail-url-copy', 'lsn-detail-url-status');

    $('lsn-set-close')?.addEventListener('click', closeSetLessonModal);
    $('lsn-set-cancel')?.addEventListener('click', closeSetLessonModal);
    $('lsn-set-save')?.addEventListener('click', createLesson);
    document.querySelector('[data-lsn-close]')?.addEventListener('click', closeSetLessonModal);

    $('btn-bk-set-lesson')?.addEventListener('click', () => {
        if (bkState.id) openSetLessonModal(bkState.id, bkState.meta?.title || '');
    });

    // The one door out of an empty page: go and make the work.

    // ── Long-answer marking ───────────────────────────────

    const markingView   = $('marking-view');
    const markingFormCard = $('marking-form-card');
    const markingSetEl  = $('marking-set');
    let currentSet      = null;   // { set, responses, rubricWarning }
    let currentRubric   = null;
    let markEventSource = null;
    let bulkPreviewRows = [];
    let photoTranscript = null;

    // ── Composer steps ────────────────────────────────────
    // Two steps: the question, then the curriculum it sits in.
    const mkWizard = initComposerWizard({
        formId: 'marking-form',
        submitId: 'mk-create-btn',
        nextLabels: ['Next: curriculum →'],
        hints: [
            'The mark scheme comes next – you review it before anything is marked.',
            'Leave any of it blank and the scheme is drafted from the question alone.',
        ],
        isReady: () => {
            const errEl = $('marking-form-error');
            if (!$('mk-question')?.value.trim()) {
                setError(errEl, 'Enter the question you want marked');
                $('mk-question')?.focus();
                return false;
            }
            setError(errEl, '');
            return true;
        },
    });

    function showMarkingPage() {
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView, billingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(markingView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);
        show(markingFormCard);
        hide(markingSetEl);
        setError($('marking-form-error'), '');
        mkSubjectCombobox.set?.('');
        syncAllWsTiles();
        refreshIgcseHints();
        mkWizard?.setStep(1, { focus: false });
        currentSet = null;
        currentRubric = null;
        if (window.location.pathname !== '/marking') history.pushState({}, '', '/marking');
    }

    // Held rather than drawn straight from the response, so folders can
    // re-draw the list (and filter it) without fetching it again.
    let markingSetsData = [];
    let markingSetsLoaded = false;

    async function loadMarkingSets() {
        const listEl = $('marking-list');
        const errEl = $('marking-list-error');
        if (!listEl) return;
        setError(errEl, '');
        try {
            const res = await apiFetch('/api/marking');
            const data = await res.json();
            if (!res.ok) return setError(errEl, data.error || 'Failed to load');
            markingSetsData = data;
            markingSetsLoaded = true;
            renderMarkingSets();
        } catch { setError(errEl, 'Failed to load'); }
    }

    function renderMarkingSets() {
        const listEl = $('marking-list');
        if (!listEl || !markingSetsLoaded) return;
        const strip = folderStripEl('marking');
        const inFolder = !!openFolderId.marking;
        if (!markingSetsData.length && !strip && !inFolder) {
            renderEmptyState(listEl, 'marking',
                'Nothing marked yet',
                'Set a question and mark scheme once, then mark the whole class against it.',
                'Mark your first answers');
            return;
        }
        listEl.innerHTML = '';
        if (strip) listEl.appendChild(strip);
        const view = itemsInView('marking', markingSetsData);
        if (inFolder && !view.length) { listEl.appendChild(folderEmptyEl('marking')); return; }
        view.forEach(row => {
            const item = document.createElement('div');
            item.className = 'pres-item';
            const done = Number(row.marked_count) || 0;
            const total = Number(row.response_count) || 0;
            item.innerHTML = `
                <span class="pres-item-icon pi-marking" aria-hidden="true">${LIST_ICONS.marking}</span>
                <div style="flex:1;min-width:0">
                    <div class="pres-item-title">${esc(row.title || row.question)}</div>
                    <div class="pres-item-meta">${new Date(row.updated_at).toLocaleDateString()} · ${row.marks} marks · ${done} of ${total} marked</div>
                </div>
                ${moveButtonHTML()}
                <button class="pres-delete-btn" title="Delete">${TRASH_ICON}</button>`;
            item.addEventListener('click', (e) => {
                if (e.target.closest('.pres-folder-btn') || e.target.closest('.pres-delete-btn')) return;
                openMarkingSet(row.id);
            });
            bindMoveButton(item, 'marking', row);
            makeLibDraggable(item, 'marking', row);
            item.querySelector('.pres-delete-btn').addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!await showConfirm({ title: 'Delete this marking?', message: 'The question, mark scheme and every marked response go with it.', confirmLabel: 'Delete', danger: true })) return;
                await apiFetch(`/api/marking/${row.id}`, { method: 'DELETE' });
                dropFromFolderCount('marking', row);
                loadMarkingSets();
            });
            listEl.appendChild(item);
        });
    }

    $('marking-form')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const errEl = $('marking-form-error');
        setError(errEl, '');
        const question = $('mk-question')?.value.trim();
        if (!question) {
            // The question lives on step 1, so send them there to fix it.
            mkWizard?.setStep(1);
            return setError(errEl, 'Enter the question you want marked');
        }

        const btn = $('mk-create-btn');
        btn.disabled = true; btn.textContent = 'Setting up…';
        try {
            const res = await apiFetch('/api/marking', {
                method: 'POST',
                body: JSON.stringify({
                    question,
                    title: question.slice(0, 120),
                    marks: parseInt($('mk-marks')?.value, 10) || 10,
                    examBoard: $('mk-exam-board')?.value || '',
                    subject: $('mk-subject')?.value || '',
                    level: $('mk-level')?.value || '',
                }),
            });
            const data = await res.json();
            if (!res.ok) return setError(errEl, data.error || 'Could not start');
            await openMarkingSet(data.id);
        } catch {
            setError(errEl, 'Network error');
        } finally {
            btn.disabled = false; btn.textContent = 'Start marking';
        }
    });

    // `focusResponseId` / `returnTo` behave as they do for quiz responses –
    // see showQuizResults.
    async function openMarkingSet(id, { focusResponseId = null, returnTo = 'marking' } = {}) {
        markingReturnTo = returnTo;
        const backBtn = $('mk-back');
        if (backBtn) backBtn.innerHTML = returnTo === 'students' ? '&larr; Students' : '&larr; All marking';
        clearLanding();
        hide(authView, presListView, bookletView, generatorView, worksheetView, quizView, quizResultsView, billingView, orgView, joinView, studentProfileView, studentsView, studentMarkingView, lessonsView, classesView, tutoringView);
        show(markingView, btnLogout, navCreateWrap, navStudentsBtn);
        if (navUserEmail) navUserEmail.textContent = state.user?.email || '';
        show(navUserEmail);

        try {
            const res = await apiFetch(`/api/marking/${id}`);
            if (!res.ok) return showMarkingPage();
            currentSet = await res.json();
        } catch { return showMarkingPage(); }

        currentRubric = currentSet.set.rubric_json || null;
        hide(markingFormCard);
        show(markingSetEl);
        if (window.location.pathname !== `/marking/${id}`) {
            history.pushState({ markingSetId: id }, '', `/marking/${id}`);
        }

        // Badge the photo tab for free users so the gate is visible up front.
        const proBadge = $('mk-photo-pro');
        if (proBadge) proBadge.hidden = isProUser();

        $('mk-question-text').textContent = currentSet.set.question;
        $('mk-question-meta').textContent = [
            `${currentSet.set.marks} marks`,
            currentSet.set.exam_board, currentSet.set.subject, currentSet.set.level,
        ].filter(Boolean).join(' · ');

        renderRubric();
        renderResponses();
        if (focusResponseId) focusResponseCard('.mk-response', focusResponseId);
    }

    // ── Mark scheme ───────────────────────────────────────

    function renderRubric() {
        const listEl = $('mk-rubric-list');
        const emptyEl = $('mk-rubric-empty');
        const warnEl = $('mk-rubric-warning');
        listEl.innerHTML = '';

        const criteria = currentRubric?.criteria || [];
        const bands = currentRubric?.bands || [];
        const strands = currentRubric?.strands || [];
        emptyEl.hidden = criteria.length > 0 || bands.length > 0 || strands.length > 0;

        const warning = currentRubric ? currentSet.rubricWarning : null;
        if (warning) { warnEl.textContent = warning; show(warnEl); } else { hide(warnEl); }

        // Say where a published scheme came from, so the teacher knows this is
        // the board's own ladder rather than something drafted.
        const srcEl = $('mk-rubric-source');
        if (srcEl) {
            if (currentRubric?.source) {
                srcEl.textContent = `Published scheme – ${currentRubric.source}`;
                show(srcEl);
            } else hide(srcEl);
        }

        // Two-ladder schemes: each part is judged separately and the marks added.
        for (const s of strands) {
            const wrap = document.createElement('div');
            wrap.className = 'mk-strand';
            wrap.innerHTML = `<div class="mk-strand-head">
                    <span class="mk-strand-label">${esc(s.label || '')}</span>
                    ${s.ao ? `<span class="mk-ao">${esc(s.ao)}</span>` : ''}
                    <span class="mk-strand-marks">${Number(s.marks) || 0} marks</span>
                </div>`;
            const inner = document.createElement('div');
            inner.className = 'mk-strand-bands';
            [...(s.bands || [])].sort((a, b) => Number(b.min) - Number(a.min)).forEach(b => {
                const row = document.createElement('div');
                row.className = 'mk-band-static';
                row.innerHTML = `<span class="mk-band-static-label">${esc(b.label)}</span>`
                    + `<span class="mk-band-static-range">${b.min}&ndash;${b.max}</span>`
                    + `<span class="mk-band-static-desc">${esc(b.nutshell || b.descriptor || '')}</span>`;
                inner.appendChild(row);
            });
            wrap.appendChild(inner);
            listEl.appendChild(wrap);
        }

        // Assessment objectives are ceilings on the whole judgement, so they are
        // shown as context rather than as things to award.
        if ((currentRubric?.aoMaxima || []).length) {
            const ao = document.createElement('div');
            ao.className = 'mk-ao-row';
            ao.innerHTML = '<span class="mk-eyebrow">Weighting</span>'
                + currentRubric.aoMaxima.map(a =>
                    `<span class="mk-ao-chip" title="${escAttr(a.description || '')}">${esc(a.ao)} max ${Number(a.max) || 0}</span>`).join('');
            listEl.appendChild(ao);
        }

        // Highest band first – the order a marker works in.
        [...bands].sort((a, b) => Number(b.min) - Number(a.min)).forEach((b) => {
            const row = document.createElement('div');
            row.className = 'mk-band-row';
            row.innerHTML = `
                <div class="mk-band-head">
                    <input class="mk-band-label" type="text" value="${escAttr(b.label || '')}" placeholder="Level">
                    <span class="mk-band-range">
                        <input class="mk-band-min" type="number" min="0" value="${Number(b.min) || 0}">
                        <span>–</span>
                        <input class="mk-band-max" type="number" min="0" value="${Number(b.max) || 0}">
                        <span class="mk-band-unit">marks</span>
                    </span>
                    <button class="mk-crit-remove" title="Remove band">&times;</button>
                </div>
                <textarea class="mk-band-desc" rows="2" placeholder="What this band requires">${esc(b.descriptor || '')}</textarea>
                <label class="mk-nutshell-label">Nutshell &ndash; the countable test that separates this band from the one below</label>
                <input class="mk-band-nutshell" type="text" value="${escAttr(b.nutshell || '')}" placeholder="e.g. one-sided argument, two explained points">`;

            row.querySelector('.mk-band-label').addEventListener('input', (e) => { b.label = e.target.value; });
            row.querySelector('.mk-band-min').addEventListener('input', (e) => { b.min = parseInt(e.target.value, 10) || 0; });
            row.querySelector('.mk-band-max').addEventListener('input', (e) => { b.max = parseInt(e.target.value, 10) || 0; });
            row.querySelector('.mk-band-desc').addEventListener('input', (e) => { b.descriptor = e.target.value; });
            row.querySelector('.mk-band-nutshell').addEventListener('input', (e) => { b.nutshell = e.target.value; });
            row.querySelector('.mk-crit-remove').addEventListener('click', () => {
                currentRubric.bands = bands.filter(x => x !== b);
                renderRubric();
            });
            listEl.appendChild(row);
        });

        if ((currentRubric?.gating || []).length) {
            const g = document.createElement('div');
            g.className = 'mk-gating-row';
            g.innerHTML = '<span class="mk-eyebrow">Caps</span><ul>'
                + currentRubric.gating.map(x => `<li>${esc(x)}</li>`).join('') + '</ul>';
            listEl.appendChild(g);
        }

        criteria.forEach((c, i) => {
            const row = document.createElement('div');
            row.className = 'mk-criterion';
            row.innerHTML = `
                <div class="mk-crit-top">
                    <input class="mk-crit-label" type="text" value="${esc(c.label || '')}" placeholder="Criterion">
                    <input class="mk-crit-ao" type="text" value="${esc(c.ao || '')}" placeholder="AO" maxlength="8">
                    <input class="mk-crit-marks" type="number" min="0" max="100" value="${Number(c.maxMarks) || 0}">
                    <button class="mk-crit-remove" title="Remove criterion">&times;</button>
                </div>
                <textarea class="mk-crit-desc" rows="2" placeholder="What a full-mark answer does">${esc(c.descriptor || '')}</textarea>`;

            row.querySelector('.mk-crit-label').addEventListener('input', (e) => { c.label = e.target.value; });
            row.querySelector('.mk-crit-ao').addEventListener('input', (e) => { c.ao = e.target.value; });
            row.querySelector('.mk-crit-marks').addEventListener('input', (e) => { c.maxMarks = parseInt(e.target.value, 10) || 0; });
            row.querySelector('.mk-crit-desc').addEventListener('input', (e) => { c.descriptor = e.target.value; });
            row.querySelector('.mk-crit-remove').addEventListener('click', () => {
                criteria.splice(i, 1);
                renderRubric();
            });
            listEl.appendChild(row);
        });
    }

    // ── Published mark schemes ────────────────────────────

    // The catalogue is small and static, so it is fetched once and narrowed in
    // the browser. That is what lets the board dropdown grey out boards with
    // nothing, rather than only revealing it after the teacher picks one.
    let templateCatalogue = null;

    async function loadTemplates() {
        const listEl = $('mk-template-list');
        listEl.innerHTML = '<p class="mk-hint">Loading…</p>';

        if (!templateCatalogue) {
            try {
                const res = await apiFetch('/api/marking/templates');
                if (res.ok) templateCatalogue = await res.json();
            } catch { /* falls through to the empty state */ }
        }
        if (!templateCatalogue) { templateCatalogue = []; }

        // Seed the filters from the set the first time the picker is opened;
        // afterwards keep whatever the teacher last chose.
        const subjectEl = $('mk-filter-subject');
        if (!subjectEl.dataset.seeded) {
            subjectEl.dataset.seeded = '1';
            pickerSelection = {
                subject: currentSet.set.subject || '',
                board: currentSet.set.exam_board || '',
                level: currentSet.set.level || '',
            };
        }
        renderTemplateFilters();
    }

    let pickerSelection = { subject: '', board: '', level: '' };

    const optionEl = (value, text, { selected, disabled } = {}) => {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = text;
        if (selected) o.selected = true;
        if (disabled) o.disabled = true;
        return o;
    };

    function renderTemplateFilters() {
        const F = window.MarkSchemeFilter;
        const resolved = F.resolveSelection(templateCatalogue, pickerSelection);
        pickerSelection.board = resolved.board;

        const subjectEl = $('mk-filter-subject');
        subjectEl.innerHTML = '';
        subjectEl.appendChild(optionEl('', 'All subjects', { selected: !resolved.subject }));
        for (const s of F.subjectOptions(templateCatalogue, {
            level: resolved.level, ownSubject: currentSet.set.subject || null,
        })) {
            subjectEl.appendChild(optionEl(s, s, { selected: s === resolved.subject }));
        }

        // Boards keep fixed positions; an empty one is disabled, not removed.
        const boardEl = $('mk-filter-board');
        boardEl.innerHTML = '';
        boardEl.appendChild(optionEl('', 'All boards', { selected: !resolved.board }));
        for (const b of F.BOARDS) {
            const n = resolved.counts[b];
            boardEl.appendChild(optionEl(b, n ? b : `${b} (none)`, {
                selected: b === resolved.board, disabled: n === 0,
            }));
        }

        const levelEl = $('mk-filter-level');
        levelEl.innerHTML = '';
        levelEl.appendChild(optionEl('', 'All levels', { selected: !resolved.level }));
        for (const l of F.LEVELS) {
            levelEl.appendChild(optionEl(l, l, { selected: l === resolved.level }));
        }

        const noteEl = $('mk-filter-note');
        if (resolved.boardFellBack && currentSet.set.exam_board) {
            noteEl.textContent = `${currentSet.set.exam_board} has no scheme for this subject yet – showing all boards.`;
            show(noteEl);
        } else hide(noteEl);

        renderTemplateList(resolved);
    }

    function renderTemplateList(resolved) {
        const F = window.MarkSchemeFilter;
        const listEl = $('mk-template-list');
        const emptyEl = $('mk-template-empty');
        const templates = resolved.schemes;

        listEl.innerHTML = '';
        emptyEl.hidden = templates.length > 0;
        emptyEl.textContent = window.MarkSchemeFilter.emptyMessage(resolved);

        for (const t of templates) {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'mk-template';
            // Two-ladder schemes are worth calling out – they mark differently.
            const shape = t.strands?.length
                ? t.strands.map(s => `${esc(s.label)} ${s.marks}`).join(' + ')
                : `${t.bandCount} levels`;
            row.innerHTML = `
                <span class="mk-template-main">
                    <span class="mk-template-label">${esc(t.label)}</span>
                    <span class="mk-template-shape">${t.marks} marks · ${shape}</span>
                </span>
                <span class="mk-template-meta">
                    <span class="mk-template-board">${esc(t.board)} ${esc(t.level)} ${esc(F.displaySubject(t, resolved.subject))}</span>
                    <span class="mk-template-source">${esc(t.source)}</span>
                </span>`;
            // Shared schemes list every subject they serve, so a teacher can see
            // the grid is not specific to the one they filtered by.
            row.title = [t.questionShape, (t.subjects || []).length > 1 ? `Serves: ${F.allSubjects(t)}` : '']
                .filter(Boolean).join('\n');
            row.addEventListener('click', () => adoptTemplate(t));
            listEl.appendChild(row);
        }
    }

    async function adoptTemplate(t) {
        // Adopting sets the tariff too: a 12-mark ladder cannot mark a question
        // the teacher entered as worth 10, so warn before overwriting.
        if (Number(currentSet.set.marks) !== Number(t.marks)) {
            const ok = await showConfirm({
                title: `Change the question to ${t.marks} marks?`,
                message: `This scheme marks out of ${t.marks}, but you entered ${currentSet.set.marks}. Using it will set the question to ${t.marks} marks.`,
                confirmLabel: 'Use the scheme',
            });
            if (!ok) return;
        }

        try {
            const res = await apiFetch(`/api/marking/${currentSet.set.id}/rubric/template`, {
                method: 'POST',
                body: JSON.stringify({ templateId: t.id }),
            });
            const data = await res.json();
            if (!res.ok) {
                const warnEl = $('mk-rubric-warning');
                warnEl.textContent = data.error || 'Could not load that scheme';
                show(warnEl);
                return;
            }
            currentRubric = data.rubric;
            currentSet.set.marks = data.marks;
            currentSet.rubricWarning = data.warning;
            $('mk-question-meta').textContent = [
                `${data.marks} marks`,
                currentSet.set.exam_board, currentSet.set.subject, currentSet.set.level,
            ].filter(Boolean).join(' · ');
            hide($('mk-template-picker'));
            renderRubric();
            renderResponses();
        } catch { /* nothing destructive happened */ }
    }

    $('mk-rubric-pick')?.addEventListener('click', () => {
        const picker = $('mk-template-picker');
        if (picker.hidden) { show(picker); loadTemplates(); }
        else hide(picker);
    });
    $('mk-template-close')?.addEventListener('click', () => hide($('mk-template-picker')));

    for (const [id, key] of [['mk-filter-subject', 'subject'], ['mk-filter-board', 'board'], ['mk-filter-level', 'level']]) {
        $(id)?.addEventListener('change', (e) => {
            pickerSelection = { ...pickerSelection, [key]: e.target.value };
            renderTemplateFilters();
        });
    }

    $('mk-rubric-generate')?.addEventListener('click', async () => {
        const btn = $('mk-rubric-generate');
        btn.disabled = true; btn.textContent = 'Drafting…';
        try {
            const res = await apiFetch(`/api/marking/${currentSet.set.id}/rubric/generate`, { method: 'POST' });
            const data = await res.json();
            if (!res.ok) {
                const warnEl = $('mk-rubric-warning');
                warnEl.textContent = data.error || 'Could not draft a mark scheme';
                show(warnEl);
                return;
            }
            currentRubric = data.rubric;
            currentSet.rubricWarning = data.warning;
            renderRubric();
        } catch { /* surfaced by the warning row on retry */ }
        finally { btn.disabled = false; btn.textContent = 'Draft with AI'; }
    });

    // Adds whichever unit this scheme is built from – a band for levels-of-response,
    // a criterion for a points scheme.
    $('mk-rubric-add')?.addEventListener('click', () => {
        currentRubric = currentRubric || {};
        // Two-ladder schemes are shown read-only – their bands live inside the
        // strands, so a top-level band here would belong to no ladder at all.
        if ((currentRubric.strands || []).length) return;
        if (currentRubric.markingModel === 'levels' || (currentRubric.bands || []).length) {
            currentRubric.bands = currentRubric.bands || [];
            const top = currentRubric.bands.reduce((n, b) => Math.max(n, Number(b.max) || 0), 0);
            currentRubric.bands.push({
                label: `Level ${currentRubric.bands.length + 1}`,
                min: top + 1, max: top + 1, descriptor: '', nutshell: '',
            });
        } else {
            currentRubric.criteria = currentRubric.criteria || [];
            currentRubric.criteria.push({ id: `c${currentRubric.criteria.length + 1}`, label: '', maxMarks: 0, descriptor: '' });
        }
        renderRubric();
    });

    $('mk-rubric-save')?.addEventListener('click', async () => {
        if (!currentRubric?.criteria?.length && !currentRubric?.bands?.length) return;
        const btn = $('mk-rubric-save');
        btn.disabled = true; btn.textContent = 'Saving…';
        try {
            const res = await apiFetch(`/api/marking/${currentSet.set.id}/rubric`, {
                method: 'PUT',
                body: JSON.stringify({ rubric: currentRubric }),
            });
            const data = await res.json();
            currentSet.rubricWarning = data.warning || null;
            renderRubric();
        } catch { /* nothing destructive happened */ }
        finally { btn.disabled = false; btn.textContent = 'Save'; }
    });

    // ── Adding responses ──────────────────────────────────

    const isProUser = () => state.user?.plan === 'pro';

    document.querySelectorAll('.mk-add-tab').forEach(tab => {
        tab.addEventListener('click', () => {
            // Photo marking is Pro. Stop here rather than letting them choose a
            // photo, wait for the upload, and only then hit the wall.
            if (tab.dataset.add === 'photo' && !isProUser()) {
                showQuotaModal({
                    error: 'pro_required',
                    message: 'Marking from a photo is a Pro feature. On the free plan you can paste or type answers in.',
                });
                return;
            }
            document.querySelectorAll('.mk-add-tab').forEach(t => {
                const on = t === tab;
                t.classList.toggle('active', on);
                t.setAttribute('aria-pressed', String(on));
            });
            document.querySelectorAll('.mk-add-pane').forEach(p => {
                p.hidden = p.dataset.pane !== tab.dataset.add;
            });
        });
    });

    async function addResponses(list, source) {
        if (!list.length) return;
        await apiFetch(`/api/marking/${currentSet.set.id}/responses`, {
            method: 'POST',
            body: JSON.stringify({ responses: list, source }),
        });
        await refreshResponses();
    }

    async function refreshResponses() {
        const res = await apiFetch(`/api/marking/${currentSet.set.id}`);
        if (!res.ok) return;
        const data = await res.json();
        currentSet.responses = data.responses;
        renderResponses();
    }

    $('mk-typed-add')?.addEventListener('click', async () => {
        const text = $('mk-typed-text').value.trim();
        if (!text) return;
        await addResponses([{ studentName: $('mk-typed-name').value.trim(), responseText: text }], 'typed');
        $('mk-typed-name').value = '';
        $('mk-typed-text').value = '';
    });

    // Parsed server-side and shown for confirmation – a wrong separator would
    // otherwise attach answers to the wrong students silently.
    $('mk-bulk-preview')?.addEventListener('click', async () => {
        const text = $('mk-bulk-text').value;
        if (!text.trim()) return;
        const res = await apiFetch(`/api/marking/${currentSet.set.id}/bulk-preview`, {
            method: 'POST',
            body: JSON.stringify({ text, separator: $('mk-bulk-sep').value }),
        });
        if (!res.ok) return;
        const data = await res.json();
        bulkPreviewRows = data.responses;

        const listEl = $('mk-bulk-preview-list');
        listEl.innerHTML = '';
        bulkPreviewRows.forEach((r, i) => {
            const row = document.createElement('div');
            row.className = 'mk-preview-row';
            row.innerHTML = `
                <input class="mk-preview-name" type="text" value="${esc(r.studentName)}">
                <span class="mk-preview-text">${esc(r.responseText.slice(0, 160))}${r.responseText.length > 160 ? '…' : ''}</span>`;
            row.querySelector('.mk-preview-name').addEventListener('input', (e) => {
                bulkPreviewRows[i].studentName = e.target.value;
            });
            listEl.appendChild(row);
        });
        show($('mk-bulk-result'));
    });

    $('mk-bulk-confirm')?.addEventListener('click', async () => {
        await addResponses(bulkPreviewRows, 'bulk');
        bulkPreviewRows = [];
        $('mk-bulk-text').value = '';
        hide($('mk-bulk-result'));
    });

    // Photos are downscaled in the browser before upload, the same way the
    // student quiz page handles whiteboard answers – but larger, because
    // handwriting has to stay legible.
    function downscaleImage(file, maxWidth = 1500) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.onload = () => {
                    const scale = Math.min(1, maxWidth / img.width);
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.round(img.width * scale);
                    canvas.height = Math.round(img.height * scale);
                    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.onerror = () => reject(new Error('Could not read that image'));
                img.src = reader.result;
            };
            reader.onerror = () => reject(new Error('Could not read that image'));
            reader.readAsDataURL(file);
        });
    }

    $('mk-photo-pick')?.addEventListener('click', () => $('mk-photo-input').click());

    $('mk-photo-input')?.addEventListener('change', async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const statusEl = $('mk-photo-status');
        statusEl.textContent = 'Reading the handwriting…';
        hide($('mk-photo-result'));
        try {
            const dataUrl = await downscaleImage(file);
            const res = await apiFetch(`/api/marking/${currentSet.set.id}/transcribe`, {
                method: 'POST',
                body: JSON.stringify({ image: dataUrl }),
            });
            const data = await res.json();
            if (res.status === 402) {
                statusEl.textContent = '';
                showQuotaModal(data);
                return;
            }
            if (!res.ok) { statusEl.textContent = data.error || 'Could not read that image'; return; }
            photoTranscript = data.text;
            $('mk-photo-text').value = data.text;
            statusEl.textContent = 'Check the transcription below';
            show($('mk-photo-result'));
        } catch (err) {
            statusEl.textContent = err.message || 'Could not read that image';
        }
    });

    $('mk-photo-add')?.addEventListener('click', async () => {
        const text = $('mk-photo-text').value.trim();
        if (!text) return;
        await addResponses([{ studentName: $('mk-photo-name').value.trim(), responseText: text }], 'photo');
        $('mk-photo-name').value = '';
        $('mk-photo-text').value = '';
        photoTranscript = null;
        hide($('mk-photo-result'));
        $('mk-photo-status').textContent = '';
    });

    // ── Rendering marked work ─────────────────────────────

    /**
     * Wrap each anchored highlight in the student's text.
     *
     * Spans arrive already located and non-overlapping from the server, so this
     * only slices and escapes – the student's words are never rewritten, only
     * wrapped.
     */
    function highlightedResponseHTML(text, highlights) {
        const spans = Array.isArray(highlights) ? highlights : [];
        if (!spans.length) return esc(text);

        let html = '';
        let cursor = 0;
        for (const h of spans) {
            if (h.start < cursor) continue; // defensive: overlaps were filtered server-side
            html += esc(text.slice(cursor, h.start));
            // The comment rides on the element rather than in `title`: the native
            // tooltip is unstyled, slow to appear and truncates a marker's
            // sentence. markTooltip() draws it instead. tabindex makes the same
            // comment reachable without a mouse.
            html += `<mark class="mk-hl mk-hl-${h.kind}" tabindex="0"`
                + ` data-kind="${escAttr(h.kind)}" data-comment="${escAttr(h.comment || '')}">`
                + `${esc(text.slice(h.start, h.end))}</mark>`;
            cursor = h.end;
        }
        html += esc(text.slice(cursor));
        return html;
    }

    // ── Examiner comment tooltip ──────────────────────────
    // What each highlight colour means, so the comment arrives already framed.
    const HIGHLIGHT_LABELS = {
        credit: 'Earns marks',
        development: 'Develops the point',
        gap: 'Attempted, not achieved',
        'off-target': 'Not answering the question',
    };

    const markTooltip = (() => {
        let el = null;
        let anchor = null;
        let cursor = null;   // viewport point of the pointer, null when keyboard-driven

        function ensure() {
            if (el) return el;
            el = document.createElement('div');
            el.className = 'mk-tip';
            el.setAttribute('role', 'tooltip');
            document.body.appendChild(el);
            return el;
        }

        /**
         * The line the tooltip hangs off. Under the mouse that is the line the
         * pointer is actually on – a highlight wrapping three lines should be
         * explained by the one being read, not by wherever the span began.
         */
        function anchorRect() {
            const rects = [...anchor.getClientRects()];
            if (!rects.length) return anchor.getBoundingClientRect();
            if (!cursor) return rects[0];
            return rects.find(r => cursor.y >= r.top - 2 && cursor.y <= r.bottom + 2) || rects[0];
        }

        function place() {
            if (!anchor || !el) return;
            const r = anchorRect();
            const gap = 10;
            const margin = 8;
            const w = el.offsetWidth;
            const h = el.offsetHeight;

            // Below by default, flipping above when it would run off the bottom.
            // Either way it clears the line rather than sitting on the words.
            const below = r.bottom + gap + h <= window.innerHeight - margin;
            const top = below ? r.bottom + gap : r.top - gap - h;

            // Centred on the pointer, so the comment appears where you are
            // looking; falls back to the middle of the span for keyboard focus.
            const point = cursor ? cursor.x : r.left + r.width / 2;
            let left = point - w / 2;
            left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));

            // The arrow keeps pointing at that spot even after edge-clamping.
            const arrow = Math.max(14, Math.min(point - left, w - 14));
            el.style.setProperty('--tip-arrow', `${arrow}px`);
            el.dataset.side = below ? 'below' : 'above';
            el.style.left = `${Math.round(left)}px`;
            el.style.top = `${Math.round(top)}px`;
        }

        function show(mark, point) {
            const comment = mark.dataset.comment;
            if (!comment) return;
            cursor = point || null;
            if (mark !== anchor) {
                anchor = mark;
                ensure();
                el.dataset.kind = mark.dataset.kind || '';
                el.innerHTML = `<span class="mk-tip-kind">${esc(HIGHLIGHT_LABELS[mark.dataset.kind] || 'Examiner comment')}</span>`
                    + `<p class="mk-tip-comment">${esc(comment)}</p>`;
                el.classList.add('is-open');
            }
            place();
        }

        function hide() {
            anchor = null;
            cursor = null;
            if (el) el.classList.remove('is-open');
        }

        return { show, hide, place, isOpen: () => !!anchor };
    })();

    // Delegated so it covers every response card, including ones rendered later.
    // mousemove rather than mouseover: the tooltip tracks the pointer along the
    // span instead of staying where the pointer first crossed into it.
    document.addEventListener('mousemove', (e) => {
        const mark = e.target.closest?.('.mk-answer .mk-hl');
        if (mark) markTooltip.show(mark, { x: e.clientX, y: e.clientY });
        else if (markTooltip.isOpen()) markTooltip.hide();
    });
    document.addEventListener('mouseout', (e) => {
        if (e.target.closest?.('.mk-answer .mk-hl')) markTooltip.hide();
    });
    document.addEventListener('focusin', (e) => {
        const mark = e.target.closest?.('.mk-answer .mk-hl');
        if (mark) markTooltip.show(mark);
    });
    document.addEventListener('focusout', (e) => {
        if (e.target.closest?.('.mk-answer .mk-hl')) markTooltip.hide();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') markTooltip.hide(); });
    // Fixed positioning is relative to the viewport, so anything that moves the
    // page under an open tooltip has to move it too.
    window.addEventListener('scroll', () => { if (markTooltip.isOpen()) markTooltip.place(); }, true);
    window.addEventListener('resize', () => markTooltip.hide());

    function responseCard(r) {
        const card = document.createElement('div');
        card.className = 'mk-response';
        card.dataset.responseId = r.id;
        card.dataset.status = r.status;

        const result = r.result_json && typeof r.result_json === 'string'
            ? JSON.parse(r.result_json) : r.result_json;

        // The teacher's mark wins over the model's, and over marking never
        // having run at all.
        const effectiveMark = r.marks_final !== null && r.marks_final !== undefined
            ? r.marks_final
            : (r.status === 'done' ? r.marks_awarded : null);
        const scoreText = effectiveMark !== null && effectiveMark !== undefined
            ? `${effectiveMark} / ${currentSet.set.marks}`
            : { pending: 'Not marked', marking: 'Marking…', error: 'Failed' }[r.status] || '';

        const head = document.createElement('div');
        head.className = 'mk-response-head';
        head.innerHTML = `
            <button class="mk-response-toggle" type="button" aria-expanded="false">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>
            </button>
            <span class="mk-response-name">${esc(r.student_name || 'Unnamed')}</span>
            <span class="mk-response-score">${esc(scoreText)}</span>
            ${r.status === 'done' ? '<button class="mk-response-pdf" title="Download this student\'s marked work">PDF</button>' : ''}
            <button class="mk-response-del" title="Remove response">&times;</button>`;
        card.appendChild(head);

        // Who this answer counts towards. Sits on the row because that is
        // where a teacher notices a name is wrong, but the bulk of matching
        // happens in the unmatched queue on /students.
        if (!r.account_id) {
            const linkBtn = buildLinkButton(r.student_id, async (studentId) => {
                await apiFetch(`/api/marking/${currentSet.set.id}/responses/${r.id}/link`, {
                    method: 'PUT',
                    body: JSON.stringify({ studentId }),
                });
                r.student_id = studentId;
            });
            head.insertBefore(linkBtn, head.querySelector('.mk-response-del'));
        }

        const body = document.createElement('div');
        body.className = 'mk-response-body';
        body.hidden = r.status !== 'done';

        if (result) {
            const criteriaById = new Map((currentRubric?.criteria || []).map(c => [c.id, c]));
            const critRows = (result.criteria || []).map(c => {
                const def = criteriaById.get(c.id);
                return `<tr>
                    <td>${esc(def?.label || c.id)}${def?.ao ? ` <span class="mk-ao">${esc(def.ao)}</span>` : ''}</td>
                    <td class="mk-crit-score">${c.awarded}${def ? ` / ${def.maxMarks}` : ''}</td>
                    <td>${esc(c.comment || '')}</td>
                </tr>`;
            }).join('');

            // Shows the band decision the way an examiner reaches it: the band
            // that matched, the one ruled out above it, and where in the band.
            const decisionBlock = (d, heading) => `
                <div class="mk-banddec">
                    ${heading ? `<p class="mk-eyebrow">${esc(heading)}</p>` : ''}
                    <div class="mk-banddec-head">
                        <span class="mk-banddec-label">${esc(d.label)}</span>
                        <span class="mk-placement" data-p="${esc(d.placement || '')}">${esc(PLACEMENT_LABELS[d.placement] || '')}</span>
                        ${d.marks != null ? `<span class="mk-banddec-marks">${d.awarded} / ${d.marks}</span>` : ''}
                    </div>
                    <p class="mk-banddec-reason">${esc(d.reason || '')}</p>
                    ${d.rejectedAbove ? `<p class="mk-banddec-rejected">${esc(d.rejectedAbove)}</p>` : ''}
                </div>`;

            // Two-ladder schemes are judged once per part and the marks added, so
            // each part gets its own decision. They carry no top-level
            // bandDecision – without this the teacher sees no level at all.
            const strandDefs = currentRubric?.strands || [];
            const strandBlocks = (result.strandDecisions || [])
                .filter(d => d?.label)
                .map(d => {
                    const def = strandDefs.find(s => s.id === d.strandId);
                    const heading = [def?.label, def?.ao].filter(Boolean).join(' · ');
                    return decisionBlock({ ...d, marks: def ? Number(def.marks) || null : null }, heading);
                }).join('');

            const bd = result.bandDecision;
            const bandBlock = bd?.label ? decisionBlock(bd) : strandBlocks;

            const gating = (result.gatingApplied || []).length ? `
                <div class="mk-gating">
                    <p class="mk-eyebrow">Capped by the mark scheme</p>
                    <ul>${result.gatingApplied.map(g => `<li>${esc(g)}</li>`).join('')}</ul>
                </div>` : '';

            body.innerHTML = `
                <div class="mk-answer">${highlightedResponseHTML(r.response_text, result.highlights)}</div>
                <div class="mk-legend">
                    <span><i class="mk-hl mk-hl-credit"></i> earns marks</span>
                    <span><i class="mk-hl mk-hl-development"></i> develops the point</span>
                    <span><i class="mk-hl mk-hl-gap"></i> attempted, not achieved</span>
                    <span><i class="mk-hl mk-hl-off-target"></i> not answering the question</span>
                    <span class="mk-legend-note">Select a highlight for the examiner comment</span>
                </div>
                ${bandBlock}
                ${gating}
                ${critRows ? `<table class="mk-crit-table"><tbody>${critRows}</tbody></table>` : ''}
                ${result.nextBand ? `<div class="mk-next"><p class="mk-eyebrow">To go higher</p><p>${esc(result.nextBand)}</p></div>` : ''}
                ${result.summary ? `<div class="mk-summary"><p class="mk-eyebrow">Feedback</p><p>${esc(result.summary)}</p></div>` : ''}`;
        } else {
            body.innerHTML = `<div class="mk-answer mk-answer-plain">${esc(r.response_text)}</div>`;
        }

        // The teacher's own mark. Always available: the model can be wrong,
        // and a response it never got to still has to be markable.
        const final = document.createElement('div');
        final.className = 'mk-final';
        const isSet = r.marks_final !== null && r.marks_final !== undefined;
        final.innerHTML = `
            <span class="mk-final-label">Your mark</span>
            <input type="number" class="mk-final-input" min="0" max="${currentSet.set.marks}"
                   value="${isSet ? r.marks_final : ''}" placeholder="–"
                   aria-label="Your mark out of ${currentSet.set.marks}">
            <span class="mk-final-label">/ ${currentSet.set.marks}</span>
            <span class="mk-final-note${isSet ? ' is-set' : ''}">${
                isSet ? 'Overrides the AI mark' : 'Leave blank to keep the AI mark'
            }</span>`;

        const input = final.querySelector('.mk-final-input');
        input.addEventListener('click', (e) => e.stopPropagation());
        input.addEventListener('change', async () => {
            const raw = input.value.trim();
            try {
                const res = await apiFetch(`/api/marking/${currentSet.set.id}/responses/${r.id}/marks`, {
                    method: 'PUT',
                    body: JSON.stringify({ marksAwarded: raw === '' ? null : raw }),
                });
                const data = await res.json();
                if (!res.ok) { showToast(data.error || 'Could not save that mark'); return; }
                r.marks_final = data.marksFinal;
                renderResponses();
            } catch { showToast('Could not save that mark'); }
        });
        body.appendChild(final);
        card.appendChild(body);

        // The chevron carries the state for both the eye and the keyboard; the
        // rest of the row stays clickable because that is the bigger target.
        const toggleBtn = head.querySelector('.mk-response-toggle');
        const setOpen = (open) => {
            body.hidden = !open;
            card.classList.toggle('is-open', open);
            toggleBtn.setAttribute('aria-expanded', String(open));
            toggleBtn.title = open ? 'Hide this answer' : 'Show this answer';
        };
        setOpen(r.status === 'done');

        head.addEventListener('click', (e) => {
            if (e.target.closest('.mk-response-del') || e.target.closest('.mk-response-pdf')) return;
            setOpen(body.hidden);
        });
        head.querySelector('.mk-response-pdf')?.addEventListener('click', (e) => {
            e.stopPropagation();
            triggerDownload(`/api/marking/${currentSet.set.id}/responses/${r.id}/pdf${markingA11yQuery()}`);
        });
        head.querySelector('.mk-response-del').addEventListener('click', async (e) => {
            e.stopPropagation();
            await apiFetch(`/api/marking/${currentSet.set.id}/responses/${r.id}`, { method: 'DELETE' });
            await refreshResponses();
        });
        return card;
    }

    function renderResponses() {
        const listEl = $('mk-responses');
        listEl.innerHTML = '';
        const responses = currentSet.responses || [];
        $('mk-responses-empty').hidden = responses.length > 0;
        $('mk-response-count').textContent = responses.length
            ? `${responses.filter(r => r.status === 'done').length} of ${responses.length} marked`
            : '';
        responses.forEach(r => listEl.appendChild(responseCard(r)));

        // The button says how much work it will do, and goes quiet when there is
        // none. Left alone mid-run: the stream owns the button until it ends.
        const runBtn = $('mk-run');
        if (runBtn && !markEventSource) {
            const pending = responses.filter(r => r.status !== 'done').length;
            runBtn.textContent = pending ? `Mark all (${pending})` : 'Mark all';
            runBtn.disabled = !pending;
        }

        // Handing back needs a school (pupils with logins) and something
        // finished to hand back. It counts what it will send, for the same
        // reason "Mark all" does: the number is the confirmation.
        const releaseBtn = $('mk-release');
        if (releaseBtn) {
            const done = responses.filter(r => r.status === 'done' || r.marks_final !== null).length;
            releaseBtn.hidden = !state.user?.org || !done;
            releaseBtn.textContent = done > 1 ? `Hand back (${done})` : 'Hand back to pupils';
        }
    }

    // ── Running the marking ───────────────────────────────

    // A scheme is markable if it has any of the three shapes the server accepts.
    // Two-ladder schemes carry their bands inside strands and have neither
    // top-level criteria nor bands, so they must be tested for explicitly.
    const rubricIsMarkable = (r) => !!(r?.criteria?.length || r?.bands?.length || r?.strands?.length);

    // Marked work as PDFs: the answer with its highlights, each comment in the
    // margin beside the words it refers to, and the level behind the mark. One
    // file per student – they are handed back individually – zipped together
    // when there is more than one.
    $('mk-download-pdf')?.addEventListener('click', () => {
        if (!currentSet?.set?.id) return;
        const statusEl = $('mk-progress-status');
        if (!(currentSet.responses || []).some(r => r.status === 'done')) {
            if (statusEl) statusEl.textContent = 'Mark something first';
            return;
        }
        const label = 'Building PDFs…';
        if (statusEl) statusEl.textContent = label;
        triggerDownload(`/api/marking/${currentSet.set.id}/pdf${markingA11yQuery()}`);
        // The download is a plain navigation, so there is no completion event
        // to hang the status on – a class set is rendered one PDF at a time,
        // so give it room before clearing rather than leaving the label up for
        // good.
        setTimeout(() => { if (statusEl?.textContent === label) statusEl.textContent = ''; }, 15000);
    });

    /**
     * Hands this set's marked work back to the pupils it belongs to.
     *
     * Only responses linked to a pupil are handed back — an unlinked one
     * belongs to a typed name nobody has matched, so there is no child to give
     * it to. The server enforces that; this says so up front rather than
     * reporting a smaller number afterwards than the teacher expected.
     */
    $('mk-release')?.addEventListener('click', async () => {
        if (!currentSet?.set?.id) return;
        if (!window.confirm('Hand this work back? Every pupil with a login who has been marked will see their score and their feedback in their own account.')) return;

        const btn = $('mk-release');
        btn.disabled = true;
        try {
            const res = await apiFetch(`/api/marking/${encodeURIComponent(currentSet.set.id)}/release`, {
                method: 'POST',
            });
            const data = await res.json();
            if (!res.ok) return showToast(data.error || 'Could not hand that back.');
            showToast(data.released
                ? `Handed back to ${data.released} ${data.released === 1 ? 'pupil' : 'pupils'}.`
                : 'Nothing to hand back yet — link responses to pupils first.');
        } catch {
            showToast('Could not hand that back.');
        } finally {
            btn.disabled = false;
        }
    });

    $('mk-run')?.addEventListener('click', () => {
        if (!rubricIsMarkable(currentRubric)) {
            const warnEl = $('mk-rubric-warning');
            warnEl.textContent = 'Add a mark scheme before marking.';
            show(warnEl);
            return;
        }
        const pending = (currentSet.responses || []).filter(r => r.status !== 'done');
        if (!pending.length) return;

        const btn = $('mk-run');
        btn.disabled = true;
        const statusEl = $('mk-progress-status');

        const es = new EventSource(`/api/marking/${currentSet.set.id}/mark/stream`);
        markEventSource = es;
        let done = 0;

        es.addEventListener('response_start', (e) => {
            const { studentName } = JSON.parse(e.data);
            statusEl.textContent = `Marking ${studentName || 'response'}…`;
        });

        es.addEventListener('response_done', (e) => {
            const d = JSON.parse(e.data);
            done++;
            statusEl.textContent = `Marked ${done} of ${pending.length}`;
            const card = document.querySelector(`.mk-response[data-response-id="${d.responseId}"] .mk-response-score`);
            if (card) card.textContent = `${d.total} / ${d.outOf}`;
        });

        es.addEventListener('response_error', () => {
            statusEl.textContent = `Marked ${done} of ${pending.length} – one failed`;
        });

        es.addEventListener('quota_exceeded', (e) => {
            es.close(); markEventSource = null;
            btn.disabled = false;
            statusEl.textContent = '';
            refreshResponses();
            try { showQuotaModal(JSON.parse(e.data)); } catch { showQuotaModal({}); }
        });

        es.addEventListener('done', () => {
            es.close(); markEventSource = null;
            btn.disabled = false;
            statusEl.textContent = '';
            refreshResponses();
        });

        es.addEventListener('error', (e) => {
            es.close(); markEventSource = null;
            btn.disabled = false;
            try { statusEl.textContent = JSON.parse(e.data).message || ''; } catch { statusEl.textContent = ''; }
            refreshResponses();
        });
    });

    $('mk-back')?.addEventListener('click', () => {
        if (markingReturnTo === 'students') return showStudentsPage();
        showPresList('marking');
        loadMarkingSets();
    });

    // ── Checkbox stroke draw (transitions.dev checkbox-check) ─
    // The native input keeps ownership of state and keyboard behaviour; the
    // drawn box just mirrors it onto aria-checked so the recipe can draw.
    (() => {
        const boxes = [...document.querySelectorAll('.ws-type-check')];
        const syncBox = (label) => {
            const input = label.querySelector('input[type="checkbox"]');
            const box = label.querySelector('.t-check');
            if (!input || !box) return;
            box.setAttribute('aria-checked', String(input.checked));
        };
        boxes.forEach((label) => {
            const path = label.querySelector('.t-check path');
            // The dash length has to match this path, or the stroke pre-reveals.
            if (path) label.style.setProperty('--check-len', Math.ceil(path.getTotalLength()) + 1);
            label.addEventListener('change', () => syncBox(label));
            syncBox(label);
        });
        // The forms reset these boxes programmatically as well as by click.
        document.addEventListener('ws-type-checks-sync', () => boxes.forEach(syncBox));
    })();

    // ── Accordion disclosures (transitions.dev accordion) ─
    // The disclosures already flip an `.open` class from a dozen call sites;
    // mirroring it onto data-open lets the recipe drive the height without
    // touching every one of them.
    (() => {
        const syncAcc = (el) => {
            if (el.classList.contains('t-acc') && el.classList.contains('opt-content-wrap') ||
                el.classList.contains('plan-card-notes')) {
                el.setAttribute('data-open', String(el.classList.contains('open')));
            }
        };
        new MutationObserver((records) => {
            records.forEach(r => r.target instanceof Element && syncAcc(r.target));
        }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
        document.querySelectorAll('.t-acc').forEach(syncAcc);
    })();

    // ── Sliding tab pill (transitions.dev tabs-sliding) ───
    // The bars already flip an `.active` class from a dozen call sites, so
    // the pill follows that class rather than each individual handler.
    document.querySelectorAll('.t-tabs').forEach((bar) => {
        const pill = bar.querySelector('.t-tabs-pill');
        if (!pill) return;
        const tabs = () => [...bar.querySelectorAll('.t-tab')];
        const activeTab = () => tabs().find(t => t.classList.contains('active')) || tabs()[0];

        function moveTo(tab, animate) {
            if (!tab) return;
            if (!animate) {
                const prev = pill.style.transition;
                pill.style.transition = 'none';
                pill.style.transform = `translateX(${tab.offsetLeft}px)`;
                pill.style.width = `${tab.offsetWidth}px`;
                void pill.offsetWidth;
                pill.style.transition = prev;
            } else {
                pill.style.transform = `translateX(${tab.offsetLeft}px)`;
                pill.style.width = `${tab.offsetWidth}px`;
            }
        }
        // The dashboard bar scrolls sideways on narrow screens, so a tab
        // selected from anywhere but a tap on the tab itself can be sitting
        // off the visible strip along with its underline.
        function keepInView(tab, animate) {
            if (bar.scrollWidth <= bar.clientWidth) return;
            const behavior = animate ? 'smooth' : 'auto';
            const right = tab.offsetLeft + tab.offsetWidth;
            if (tab.offsetLeft < bar.scrollLeft) {
                bar.scrollTo({ left: tab.offsetLeft, behavior });
            } else if (right > bar.scrollLeft + bar.clientWidth) {
                bar.scrollTo({ left: right - bar.clientWidth, behavior });
            }
        }

        function sync(animate) {
            const tab = activeTab();
            tabs().forEach(t => t.setAttribute('aria-selected', String(t === tab)));
            // A bar inside a hidden view measures 0 – wait until it has a box.
            if (!tab || !tab.offsetWidth) return;
            moveTo(tab, animate);
            keepInView(tab, animate);
        }

        new MutationObserver(() => sync(true)).observe(bar, {
            subtree: true, attributes: true, attributeFilter: ['class'],
        });
        // Re-measure when the bar's view is revealed or the window resizes.
        new ResizeObserver(() => sync(false)).observe(bar);
        window.addEventListener('resize', () => sync(false));
        requestAnimationFrame(() => sync(false));
    });

    // ── Init ──────────────────────────────────────────────
    applyTheme('classic');
    checkAuth();
    fetch('/api/auth/signup-lock').then(r => r.json()).then(d => {
        state.signupLocked = !!d.locked;
    }).catch(() => {});
})();
